// ╔══════════════════════════════════════════════════════════════════╗
// ║  NUGGETS  —  40Geo Operations                                   ║
// ║  Keycloak SSO · RBAC · Kanban · Messenger                     ║
// ╚══════════════════════════════════════════════════════════════════╝

import { useState, useEffect, useRef, useCallback, useMemo, Fragment } from "react";
import { useAccessibility } from "./hooks/useAccessibility.js";
import PreferencesPanel      from "./components/PreferencesPanel.jsx";
import {
  ModelBadge,
  StatusChip,
  TokenCostChip,
  FableIntroBanner,
  metaForModel,
} from "./components/proposal/ProposalPrimitives.jsx";
import MyQueuePanel     from "./components/dashboard/MyQueuePanel.jsx";
import SlackImportPanel from "./components/messenger/SlackImportPanel.jsx";
import DocViewerModal   from "./components/library/DocViewerModal.jsx";
import CRMReports       from "./components/crm/CRMReports.jsx";
import { computeArr }   from "./components/crm/reportModel.js";
import {
  db,
  // Auth is Keycloak (see src/main.jsx + src/auth.js). The old
  // email/password + Google + recovery helpers are gone; the only
  // entry points left are login (handled in main.jsx) and signOut.
  signOut,
  loadUserProfile,
  touchLastSeen,
  loadOnlineUsers,
  loadNuggets,
  saveNugget   as dbSaveNugget,
  deleteNugget as dbDeleteNugget,
  loadCompanies,
  addCompany   as dbAddCompany,
  deleteCompany as dbDeleteCompany,
  loadNuggetTypes,
  addNuggetType as dbAddNuggetType,
  loadOrgUsers,
  writeAuditLog,
  loadNotificationSettings,
  saveNotificationSettings,
  sendSMS      as dbSendSMS,
  sendEmail    as dbSendEmail,
  loadOrgSettings,
  saveOrgSettings  as dbSaveOrgSettings,
  saveAiModels     as dbSaveAiModels,
  uploadOrgLogo    as dbUploadOrgLogo,
  saveUserFeatureOverrides,
  saveUserRole,
  saveUserContact  as dbSaveUserContact,
  logTokenUsage,
  loadTokenUsage,
  loadChannels,
  loadChannelMembersMap,
  createChannel,
  getOrCreateDM,
  getOrCreateGroupDM,
  loadMessages,
  sendMessage        as dbSendMessage,
  subscribeToMessages,
  broadcastTyping,
  subscribeToTyping,
  markChannelRead,
  searchMessages     as dbSearchMessages,
  editMessage        as dbEditMessage,
  deleteMessage      as dbDeleteMessage,
  subscribeToOrgMessages,
  subscribeToChannelList,
  subscribeToMyChannelMembership,
  loadUnreadCounts,
  loadChannelMemberIds,
  subscribeLaunchpadInvites,
  savePushSubscription,
  deletePushSubscription,
  uploadMessageAttachment as dbUploadAttachment,
  subscribeToNuggets      as dbSubscribeToNuggets,
  dbToNugget              as dbRowToNugget,
  loadNotes               as dbLoadNotes,
  saveNote                as dbSaveNote,
  deleteNote              as dbDeleteNote,
  // DB Health Monitor
  loadExternalDatabases,
  loadMonitoredTables,
  saveMonitoredTable,
  bulkAddMonitoredTables,
  deleteMonitoredTable,
  loadAppOwnership,
  saveAppOwnership,
  deleteAppOwnership,
  loadHealthLog,
  discoverTablesInDatabase,
  fetchColumnsForTable,
  runMonitorNow,
  // Auth + session + admin
  startUserSession,
  heartbeatSession,
  endSession,
  endSessionBeacon,
  loadUserSessionStats,
  loadAllOrgUsers,
  // Deal-room perms
  loadDealMembers,
  upsertDealMember,
  removeDealMember,
  dealEffectivePerms,
  PERM_TABS,
  saveReviewerDefaultPerms,
  adminSetPassword,
  adminCreateUser,
  adminResetPassword,
  adminResendInvite,
  adminSetActive,
  adminDeleteUser,
  adminKeycloakHealth,
  adminStorageHealth,
  // Contacts (CRM)
  loadContacts,
  saveContact,
  deleteContact          as dbDeleteContact,
  bulkInsertContacts,
  ocrBusinessCard,
  fetchGoogleContacts,
  // Deal Room
  loadDeals,
  saveDeal,
  updateDealFields,
  loadDiligenceRequests,
  saveDiligenceRequest,
  bulkInsertDiligenceRequests,
  deleteDiligenceRequest as dbDeleteDiligenceRequest,
  loadDiligenceFiles,
  loadDealFilesByRequest,
  loadDealAiMessages,
  sendDealAiMessage,
  clearDealAiMessages,
  loadDealAiThreads,
  createDealAiThread,
  renameDealAiThread,
  deleteDealAiThread,
  shareDealAiThread,
  loadOpportunities,
  createOpportunity,
  updateOpportunity,
  deleteOpportunity,
  findOpportunityByCrmDealId,
  findContactByEmail,
  loadContact,
  loadOpportunityArtifacts,
  createOpportunityArtifact,
  updateOpportunityArtifact,
  deleteOpportunityArtifact,
  uploadOpportunityArtifactFile,
  setOpportunityArtifactAiEnabled,
  seedDefaultOutboundArtifacts,
  loadProposalMembers,
  upsertProposalMember,
  removeProposalMember,
  loadProposalSections,
  createProposalSection,
  updateProposalSection,
  deleteProposalSection,
  seedDefaultProposalSections,
  loadProposalSignoffsForOpp,
  submitProposalSignoff,
  draftProposalSection,
  draftFullProposal,
  loadOpportunityAiDirectives,
  addOpportunityAiDirective,
  deleteOpportunityAiDirective,
  FONT_CHOICES,
  HEADER_STYLES,
  TEMPLATE_STYLES,
  DEFAULT_THEME,
  resolveTheme,
  uploadProposalThemeImage,
  loadProposalSectionVersions,
  loadAllSectionVersionsForOpp,
  restoreProposalSectionVersion,
  loadProposalSectionImages,
  loadProposalSectionImagesByOpp,
  getProposalImageSignedUrl,
  uploadProposalSectionImage,
  updateProposalSectionImage,
  deleteProposalSectionImage,
  scoreOpportunityWin,
  runOpportunityGapRadar,
  runOpportunityCritique,
  applyCritiqueFixToSection,
  loadOpportunityCompetitors,
  upsertOpportunityCompetitor,
  deleteOpportunityCompetitor,
  loadArtifactProfiles,
  upsertArtifactProfile,
  deleteArtifactProfile,
  seedOpportunityFromProfile,
  loadOpportunityBrief,
  loadOpportunityTokenUsage,
  bulkMergeLibraryTags,
  LIBRARY_PERMISSIONS,
  loadLibraryDocAccess,
  grantLibraryDocAccess,
  revokeLibraryDocAccess,
  bulkGrantLibraryAccess,
  loadOrgUsersForAcl,
  CLOUD_PROVIDERS,
  loadCloudConnections,
  saveCloudConnection,
  deleteCloudConnection,
  searchLibrary,
  loadSavedSearches,
  saveSavedSearch,
  deleteSavedSearch,
  referencePastProposal,
  setOpportunityReference,
  loadOpportunityOutcome,
  upsertOpportunityOutcome,
  // CRM (persistent, migrated from localStorage on 2026-07-07)
  loadCrmDeals,     upsertCrmDeal,     deleteCrmDeal,
  loadCrmProposals, upsertCrmProposal, deleteCrmProposal,
  loadCrmContracts, upsertCrmContract, deleteCrmContract,
  saveOutcomeToLibrary,
  OUTCOME_KINDS,
  THREAT_LEVELS,
  PRICING_POSTURES,
  PROPOSAL_SECTION_TYPES,
  PROPOSAL_CAPACITIES,
  loadProposalLibrary,
  loadProposalLibraryByIds,
  uploadProposalLibraryFile,
  addProposalLibraryUrl,
  updateProposalLibrary,
  deleteProposalLibrary,
  setProposalLibraryAiEnabled,
  loadOpportunityNotes,
  addOpportunityNote,
  deleteOpportunityNote,
  loadOpportunityAiThreads,
  createOpportunityAiThread,
  renameOpportunityAiThread,
  deleteOpportunityAiThread,
  loadOpportunityAiMessages,
  sendOpportunityAiMessage,
  setDiligenceFileAiEnabled,
  loadDealIntel,
  saveDealIntelProfile,
  uploadDealIntelDocument,
  deleteDealIntelDocument,
  upsertDealIntelContract,
  deleteDealIntelContract,
  addDealIntelUrl,
  deleteDealIntelUrl,
  fetchDealIntelUrl,
  setIntelDocumentAiEnabled,
  uploadDiligenceFile,
  deleteDiligenceFile,
  dealFileDownloadUrl,
  loadDiligenceComments,
  addDiligenceComment,
  setUserCompany,
} from "./db.js";
import LaunchpadBubble from "./components/LaunchpadBubble.jsx";
import {
  Crosshair, Map as MapIcon, Layers, FlaskConical, Rocket, Users, Settings, Sun, Moon,
  Send, Mail, Smartphone, CheckCircle, AlertTriangle, XCircle, Plus,
  Activity, X, RefreshCw, ChevronRight, Shield,
  Play, Eye, Edit, Trash2, LogOut, Search, MessageSquare, ChevronLeft,
  KeyRound, Brain, Cpu, GitBranch, CheckSquare,
  LifeBuoy, Inbox, Clock, TrendingUp, BarChart2, Tag, ArrowUpRight, Filter, Star, Zap, ChevronDown, MoreHorizontal,
  Mic, MicOff, Sparkles, PenLine, Copy, ArrowRight, Wand2,
  BookOpen, FileText, Download, AtSign, Paperclip,
  ShieldAlert, Radio, Lock, Wifi, Database, Briefcase, Target,
  PanelLeftOpen, PanelLeftClose
} from "lucide-react";

// Coach-whistle SVG icon used by the Proposal Coach nav item. Lucide
// doesn't ship a Whistle icon, so we inline one. Matches lucide's
// stroke API (size + currentColor) so it slots in next to the other
// nav icons without restyling.
const WhistleIcon = ({ size = 17 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {/* Whistle body (circle) */}
    <circle cx="9" cy="14" r="5" />
    {/* Mouthpiece nozzle */}
    <path d="M14 14 L21 10 L21 18 Z" />
    {/* Air slot detail on the body */}
    <line x1="9"  y1="9"  x2="9" y2="11" />
  </svg>
);

// ═══════════════════════════════════════════════════════════════════
// PERMISSION MATRIX
// ═══════════════════════════════════════════════════════════════════
const PERMISSIONS = {
  nugget_create:      { super_admin:true,  admin:true,  developer:true,  viewer:false },
  nugget_edit_own:    { super_admin:true,  admin:true,  developer:true,  viewer:false },
  nugget_edit_any:    { super_admin:true,  admin:true,  developer:false, viewer:false },
  nugget_delete:      { super_admin:true,  admin:true,  developer:false, viewer:false },
  nugget_assign:      { super_admin:true,  admin:true,  developer:false, viewer:false },
  nugget_share:       { super_admin:true,  admin:true,  developer:true,  viewer:false },
  nugget_view_all:    { super_admin:true,  admin:true,  developer:true,  viewer:false },
  nugget_view_shared: { super_admin:true,  admin:true,  developer:true,  viewer:true  },
  deploy_trigger:     { super_admin:true,  admin:true,  developer:true,  viewer:false },
  deploy_prod:        { super_admin:true,  admin:true,  developer:false, viewer:false },
  admin_users:        { super_admin:true,  admin:true,  developer:false, viewer:false },
  admin_roles:        { super_admin:true,  admin:false, developer:false, viewer:false },
  admin_audit:        { super_admin:true,  admin:true,  developer:false, viewer:false },
  slack_post:         { super_admin:true,  admin:true,  developer:true,  viewer:false },
  map_edit:           { super_admin:true,  admin:true,  developer:true,  viewer:false },
};

const can = (role, perm) => !!PERMISSIONS[perm]?.[role];

// ── Shared AI caller — logs token usage after every Claude call ───
// Per-feature model overrides — populated from orgSettings.ai_models after auth
// and updated live when the user applies recommendations in the Token Usage dashboard.
const AI_MODELS = {};
const setAiModelOverride   = (feature, model) => { AI_MODELS[feature] = model; };
const clearAiModelOverride = (feature) => { delete AI_MODELS[feature]; };
const loadAiModelOverrides = (map) => { Object.assign(AI_MODELS, map || {}); };

const callAI = async ({ systemPrompt, messages, model = 'claude-sonnet-4-6', feature = 'unknown', user = null }) => {
  const resolvedModel = AI_MODELS[feature] || model;
  const resp = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ systemPrompt, messages, model: resolvedModel }),
  });
  const data = await resp.json();
  if (!resp.ok) throw new Error(data.error || 'AI call failed');
  // Fire-and-forget token logging
  if (data.usage && user) {
    logTokenUsage({
      orgId:        user.orgId,
      companyName:  user.companyName || null,
      userId:       user.id,
      userName:     user.name,
      feature,
      model:        data.model || resolvedModel,
      inputTokens:  data.usage.input_tokens  || 0,
      outputTokens: data.usage.output_tokens || 0,
    });
  }
  return data;
};

const ROLE_META = {
  super_admin: { label:"Super Admin", color:"#EF4444",              desc:"Full system access + role management" },
  admin:       { label:"Admin",       color:"#E87722",              desc:"User management + all nugget ops" },
  developer:   { label:"Developer",   color:"#60A5FA",              desc:"Create & edit own nuggets + deploy" },
  viewer:      { label:"Viewer",      color:"rgba(255,255,255,0.4)", desc:"Read-only — shared nuggets only" },
  org_admin:   { label:"Org Admin",   color:"#A78BFA",              desc:"Company admin — full company access" },
  org_member:  { label:"Member",      color:"#34D399",              desc:"Standard company user" },
  org_viewer:  { label:"Org Viewer",  color:"#6B7280",              desc:"Read-only company access" },
};

// ═══════════════════════════════════════════════════════════════════
// DESIGN SYSTEM THEME
// navy-primary:#1B2B5B  navy-dark:#0D1B2A  orange:#E87722
// ═══════════════════════════════════════════════════════════════════
const THEME = {
  dark: {
    bg:       "#0D1B2A",
    bg2:      "#0D1B2A",
    card:     "rgba(255,255,255,0.05)",
    elevated: "rgba(255,255,255,0.08)",
    hover:    "rgba(255,255,255,0.05)",
    accent:   "#E87722",
    accentDk: "#C4611A",
    accentLt: "#F09040",
    navy:     "#1B2B5B",
    green:    "#22C55E",
    red:      "#EF4444",
    yellow:   "#E87722",
    blue:     "#60A5FA",
    text:     "#FFFFFF",
    dim:      "rgba(255,255,255,0.5)",
    faint:    "rgba(255,255,255,0.3)",
    muted:    "rgba(255,255,255,0.1)",
    border:   "rgba(255,255,255,0.08)",
    borderHi: "rgba(255,255,255,0.15)",
  },
  light: {
    bg:       "#1B2B5B",
    bg2:      "#1B2B5B",
    card:     "rgba(255,255,255,0.08)",
    elevated: "rgba(255,255,255,0.12)",
    hover:    "rgba(255,255,255,0.08)",
    accent:   "#E87722",
    accentDk: "#C4611A",
    accentLt: "#F09040",
    navy:     "#0D1B2A",
    green:    "#22C55E",
    red:      "#EF4444",
    yellow:   "#E87722",
    blue:     "#60A5FA",
    text:     "#FFFFFF",
    dim:      "rgba(255,255,255,0.6)",
    faint:    "rgba(255,255,255,0.4)",
    muted:    "rgba(255,255,255,0.15)",
    border:   "rgba(255,255,255,0.12)",
    borderHi: "rgba(255,255,255,0.22)",
  },
};

const PRIORITY = {
  critical: { label:"CRITICAL", color:"#EF4444", bg:"rgba(220,38,38,0.15)",  border:"rgba(220,38,38,0.4)"  },
  high:     { label:"HIGH",     color:"#E87722", bg:"rgba(232,119,34,0.15)", border:"rgba(232,119,34,0.4)" },
  medium:   { label:"MED",      color:"#60A5FA", bg:"rgba(59,130,246,0.15)", border:"rgba(59,130,246,0.4)" },
  low:      { label:"LOW",      color:"rgba(255,255,255,0.5)", bg:"rgba(255,255,255,0.06)", border:"rgba(255,255,255,0.15)" },
};

const VISIBILITY_META = {
  private: { label:"Private", icon:"🔒", color:"#EF4444", desc:"Only you & assignee" },
  team:    { label:"Team",    icon:"👥", color:"#E87722", desc:"All crew members" },
  shared:  { label:"Shared",  icon:"🔗", color:"#60A5FA", desc:"Specific people only" },
};

const COLS = [
  { id:"backlog",     label:"Backlog",     icon:"📋" },
  { id:"in-progress", label:"In Progress", icon:"⚡" },
  { id:"review",      label:"Review",      icon:"🔍" },
  { id:"done",        label:"Done",        icon:"✅" },
];

const QUOTES = [
  { text:"I am serious. And don't call me Shirley.", src:"Airplane! '80" },
  { text:"Life moves fast. You ship code or you miss it.", src:"Ferris Bueller '86" },
  { text:"Roads? Where we're going, we don't need roads — just git push.", src:"BTTF '85" },
  { text:"Badges? We don't need no stinking badges — we have CI/CD!", src:"Blazing Saddles '74" },
  { text:"So you're saying there's a CHANCE this PR gets approved?", src:"Dumb & Dumber '94" },
  { text:"Great Scott! The data quality gate is PASSING!", src:"BTTF '85" },
  { text:"Nobody puts Data Quality in a corner.", src:"Dirty Dancing '87" },
  { text:"I feel the need — the need for SPEED. Ship it.", src:"Top Gun '86" },
  { text:"Get busy shipping or get busy dying.", src:"Shawshank '94" },
  { text:"Hasta la vista, staging bug.", src:"T2 '91" },
];

const INIT_NUGGETS = [
  { id:"GEO-041", title:"MapLibre tile caching for offline mode",     priority:"high",     col:"backlog",     ownerId:"u2", assigneeId:"u2", tags:["geo","perf"],    age:"2d", visibility:"team",   sharedWith:[], desc:"Implement service worker caching strategy for tile layers.",         revenueValue:"high",   company:"40Geo",         nuggetType:"Feature"       },
  { id:"GEO-042", title:"Claude prototype for address parsing NLP",   priority:"critical", col:"in-progress", ownerId:"u1", assigneeId:"u1", tags:["claude","nlp"],  age:"1d", visibility:"team",   sharedWith:[], desc:"Use Claude Sonnet to parse and normalize freeform address strings.",  revenueValue:"high",   company:"40Geo",         nuggetType:"Prototype"     },
  { id:"GEO-043", title:"Data quality: polygon self-intersections",   priority:"high",     col:"in-progress", ownerId:"u3", assigneeId:"u3", tags:["data","geo"],    age:"3d", visibility:"team",   sharedWith:[], desc:"Add automated check for self-intersecting polygon geometries.",       revenueValue:"medium", company:"Geo Solutions",  nuggetType:"Data Quality"  },
  { id:"GEO-044", title:"Twilio SMS alerts on deploy failure",         priority:"medium",   col:"review",      ownerId:"u4", assigneeId:"u4", tags:["ops","sms"],     age:"5d", visibility:"shared", sharedWith:["u1","u2"], desc:"Wire Twilio to fire SMS when a pipeline stage fails.",     revenueValue:"low",    company:"40Geo",         nuggetType:"Infrastructure"},
  { id:"GEO-045", title:"Dark mode for embedded map widgets",          priority:"low",      col:"review",      ownerId:"u5", assigneeId:"u5", tags:["ui","theme"],    age:"1w", visibility:"private", sharedWith:[], desc:"Allow host apps to pass a theme prop to the map widget.",         revenueValue:"low",    company:"MapTech Inc",   nuggetType:"Feature"       },
  { id:"GEO-046", title:"Deploy geocoder v2 to production",            priority:"critical", col:"done",        ownerId:"u4", assigneeId:"u4", tags:["deploy","geo"],  age:"2w", visibility:"team",   sharedWith:[], desc:"Promote geocoder-v2 from staging after sign-off.",                  revenueValue:"high",   company:"40Geo",         nuggetType:"Infrastructure"},
  { id:"GEO-047", title:"Weekly nugget summary email digest",          priority:"medium",   col:"backlog",     ownerId:"u5", assigneeId:"u5", tags:["email","reports"],age:"4d", visibility:"team",  sharedWith:[], desc:"Auto-send Monday morning digest of open nuggets.",                  revenueValue:"ice",    company:"40Geo",         nuggetType:"Feature"       },
];

const INIT_AUDIT = [
  { ts:"2m ago",  userId:"u4", action:"Deployed feat/geocoder-v2 → prod",  type:"deploy"  },
  { ts:"14m ago", userId:"u1", action:"Moved GEO-042 → In Progress",        type:"nugget"  },
  { ts:"1h ago",  userId:"u3", action:"Data quality gate PASSED (6/7)",     type:"quality" },
  { ts:"2h ago",  userId:"u2", action:"Created nugget GEO-047",             type:"nugget"  },
  { ts:"3h ago",  userId:"u1", action:"Assigned GEO-042 to crew",           type:"admin"   },
  { ts:"5h ago",  userId:"u4", action:"Signed in via Google SSO",           type:"auth"    },
];

const SLACK_CHANNELS = [
  { id:"C01", name:"#40geo-deploys",  topic:"Auto-posts on every deployment" },
  { id:"C02", name:"#40geo-nuggets",  topic:"Nugget create/update/close events" },
  { id:"C03", name:"#40geo-alerts",   topic:"Critical priority + pipeline failures" },
  { id:"C04", name:"#40geo-general",  topic:"Manual posts from Nuggets" },
];

// Revenue value options
const REVENUE_VALUES = [
  { id:"high",   label:"High",   color:"#22C55E", bg:"rgba(34,197,94,0.15)",   border:"rgba(34,197,94,0.4)"   },
  { id:"medium", label:"Medium", color:"#60A5FA", bg:"rgba(96,165,250,0.15)",  border:"rgba(96,165,250,0.4)"  },
  { id:"low",    label:"Low",    color:"rgba(255,255,255,0.45)", bg:"rgba(255,255,255,0.06)", border:"rgba(255,255,255,0.15)" },
  { id:"ice",    label:"ICE",    color:"#E87722", bg:"rgba(232,119,34,0.15)",  border:"rgba(232,119,34,0.4)"  },
];

// Starter companies and nugget types — admins can add more at runtime
const INIT_COMPANIES = [
  "40Geo", "Acme Corp", "Geo Solutions", "MapTech Inc", "SpatialWorks",
];
const INIT_NUGGET_TYPES = [
  "Feature", "Bug Fix", "Research", "Infrastructure", "Data Quality",
  "Prototype", "Documentation", "Integration",
];

// ═══════════════════════════════════════════════════════════════════
// SHARED HELPERS
// ═══════════════════════════════════════════════════════════════════

// Eyebrow label — small orange uppercase label above sections
function Eyebrow({ children, style={} }) {
  return (
    <div style={{ fontSize:11, fontWeight:600, letterSpacing:"0.35em", textTransform:"uppercase", color:"#E87722", marginBottom:10, ...style }}>
      {children}
    </div>
  );
}

// Section header with title + sub
function SH({ C, title, sub }) {
  return (
    <div style={{ marginBottom:20 }}>
      <h1 style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:22, color:C.text, letterSpacing:"-0.01em", textTransform:"uppercase", margin:0 }}>{title}</h1>
      {sub && <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, color:C.dim, marginTop:4, fontWeight:400 }}>{sub}</p>}
    </div>
  );
}

// Standard card style
const Card = (C, extra={}) => ({
  background: C.card,
  border: `1px solid ${C.border}`,
  borderTop: `2px solid #E87722`,
  borderRadius: 8,
  padding: "16px 20px",
  ...extra,
});

// Primary orange button
const BtnPrimary = {
  background: "#E87722",
  color: "#fff",
  border: "none",
  borderRadius: 3,
  padding: "9px 22px",
  fontFamily: "'Montserrat',sans-serif",
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  cursor: "pointer",
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  gap: 6,
};

// Convert a base64url VAPID public key to a Uint8Array (required by pushManager.subscribe)
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64  = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw     = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// Outline button
const BtnOutline = (C) => ({
  background: "transparent",
  color: C.text,
  border: `1px solid rgba(255,255,255,0.2)`,
  borderRadius: 3,
  padding: "7px 16px",
  fontFamily: "'Montserrat',sans-serif",
  fontWeight: 600,
  fontSize: 11,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  cursor: "pointer",
  transition: "all 0.2s ease",
  display: "flex",
  alignItems: "center",
  gap: 5,
});

// Small icon button
const BtnSm = () => ({
  background: "rgba(255,255,255,0.05)",
  border: `1px solid rgba(255,255,255,0.1)`,
  borderRadius: 3,
  padding: "3px 7px",
  color: "rgba(255,255,255,0.5)",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  gap: 3,
  fontSize: 9,
  transition: "all 0.2s ease",
});

// Badge/pill
const Badge = (color, bg, border) => ({
  fontSize: 10,
  fontWeight: 600,
  fontFamily: "'Montserrat',sans-serif",
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  color,
  background: bg,
  border: `1px solid ${border}`,
  borderRadius: 3,
  padding: "2px 7px",
});

// Toggle switch
function Toggle({ val, onChange, disabled = false }) {
  return (
    <div onClick={disabled ? undefined : onChange}
      style={{ width:36, height:20, borderRadius:10, background: disabled ? "rgba(255,255,255,0.06)" : val ? "#E87722" : "rgba(255,255,255,0.1)", cursor: disabled ? "not-allowed" : "pointer", position:"relative", transition:"background 0.2s ease", flexShrink:0, opacity: disabled ? 0.45 : 1 }}>
      <div style={{ position:"absolute", top:3, left:val?18:3, width:14, height:14, borderRadius:"50%", background:"#fff", transition:"left 0.2s ease", boxShadow:"0 1px 4px rgba(0,0,0,0.3)" }}/>
    </div>
  );
}

// Input style
const InputStyle = () => ({
  background: "rgba(255,255,255,0.05)",
  border: `1px solid rgba(255,255,255,0.1)`,
  borderRadius: 3,
  padding: "9px 12px",
  color: "#fff",
  fontFamily: "'Montserrat',sans-serif",
  fontWeight: 400,
  fontSize: 13,
  width: "100%",
});

// ─── COMBOBOX ────────────────────────────────────────────────────────
// Autocomplete input — filters a list as you type, allows adding new items
// canAdd: admin/super_admin can open the AddItemModal
function Combobox({ value, onChange, options, placeholder, canAdd, onRequestAdd, C }) {
  const [query,  setQuery]  = useState(value || "");
  const [open,   setOpen]   = useState(false);
  const ref = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = options.filter(o => o.toLowerCase().includes(query.toLowerCase()));
  const showAdd  = canAdd && query.trim() && !options.some(o => o.toLowerCase() === query.toLowerCase());

  const select = (val) => { setQuery(val); onChange(val); setOpen(false); };

  return (
    <div ref={ref} style={{ position:"relative" }}>
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={{ ...InputStyle(C) }}
      />
      {open && (filtered.length > 0 || showAdd) && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, right:0, background:"#0D1B2A", border:"1px solid rgba(255,255,255,0.15)", borderRadius:4, zIndex:300, maxHeight:180, overflowY:"auto", boxShadow:"0 8px 24px rgba(0,0,0,0.5)" }}>
          {filtered.map(o => (
            <div key={o} onMouseDown={() => select(o)}
              style={{ padding:"9px 12px", fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#fff", cursor:"pointer", borderBottom:"1px solid rgba(255,255,255,0.05)", transition:"background 0.15s" }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(232,119,34,0.12)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              {o}
            </div>
          ))}
          {showAdd && (
            <div onMouseDown={() => { onRequestAdd(query.trim()); setOpen(false); }}
              style={{ padding:"9px 12px", fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#E87722", cursor:"pointer", fontWeight:600, display:"flex", alignItems:"center", gap:6 }}
              onMouseEnter={e=>e.currentTarget.style.background="rgba(232,119,34,0.08)"}
              onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
              <Plus size={11}/> Create "{query.trim()}"
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ADD ITEM MODAL ─────────────────────────────────────────────────
// Shown when admin clicks "Create new" in a Combobox
function AddItemModal({ title, prefill, onConfirm, onCancel, C }) {
  const [val, setVal] = useState(prefill || "");
  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.8)", zIndex:500, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onCancel}>
      <div style={{ background:"#0D1B2A", border:"1px solid rgba(255,255,255,0.15)", borderTop:"2px solid #E87722", borderRadius:8, padding:28, width:"100%", maxWidth:400, boxShadow:"0 16px 48px rgba(0,0,0,0.6)" }} onClick={e=>e.stopPropagation()}>
        <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:16, color:"#fff", textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:6 }}>{title}</div>
        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.4)", marginBottom:20 }}>This will be added to the global list for everyone.</div>
        <input
          autoFocus
          value={val}
          onChange={e=>setVal(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&val.trim()&&onConfirm(val.trim())}
          placeholder="Enter name..."
          style={{ ...InputStyle(C), marginBottom:16 }}
        />
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
          <button onClick={onCancel} style={BtnOutline(C)}>Cancel</button>
          <button onClick={()=>val.trim()&&onConfirm(val.trim())} style={BtnPrimary}><Plus size={12}/>Add</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// GLOBAL STYLES
// ═══════════════════════════════════════════════════════════════════
const GLOBAL_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: #0D1B2A; color: #fff; font-family: 'Montserrat', sans-serif; font-weight: 400; font-size: 15px; line-height: 1.6; }
  button { font-family: 'Montserrat', sans-serif; }
  textarea, input, select { font-family: 'Montserrat', sans-serif; }
  textarea:focus, input:focus, select:focus { outline: none; border-color: #E87722 !important; }
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }
  ::-webkit-scrollbar-thumb:hover { background: rgba(232,119,34,0.4); }
  select option { background: #0D1B2A; color: #fff; }
  @keyframes fadeUp   { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
  @keyframes slideIn  { from{transform:translateX(120px);opacity:0} to{transform:translateX(0);opacity:1} }
  @keyframes spin     { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
  @keyframes pulse    { 0%,100%{opacity:1} 50%{opacity:0.3} }
  @keyframes lp-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.7)} }
`;

// ═══════════════════════════════════════════════════════════════════
// BRAND LOGO  —  40Geo gold-bar mark
// ═══════════════════════════════════════════════════════════════════
function FortyGeoLogo({ variant = 'login' }) {
  const goldBar = (w = '100%', mt = 0) => ({
    width: w, height: 10, borderRadius: 3, marginTop: mt,
    background: 'linear-gradient(90deg,#4A2E00 0%,#9A6000 12%,#D4920A 28%,#FFDA44 45%,#FFE880 55%,#FFDA44 65%,#D4920A 82%,#9A6000 92%,#4A2E00 100%)',
    boxShadow: '0 2px 5px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.28), inset 0 -1px 0 rgba(0,0,0,0.25)',
  });

  if (variant === 'icon') {
    // Sidebar nav: compact stacked bars, no text
    return (
      <div style={{ display:'flex', flexDirection:'column', gap:3, width:30 }}>
        <div style={goldBar()} />
        <div style={goldBar()} />
        <div style={goldBar()} />
      </div>
    );
  }

  if (variant === 'wordmark') {
    // Header top-bar: bars + text horizontal
    return (
      <div style={{ display:'flex', alignItems:'center', gap:10, userSelect:'none' }}>
        <div style={{ display:'flex', flexDirection:'column', gap:2.5, width:22, flexShrink:0 }}>
          <div style={goldBar()} />
          <div style={goldBar()} />
          <div style={goldBar()} />
        </div>
        <div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:17, color:'#fff', letterSpacing:'-0.02em', lineHeight:1 }}>
            <span style={{ color:'#E87722' }}>40</span>GEO
          </div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:7, letterSpacing:'0.28em', color:'rgba(255,210,60,0.75)', textTransform:'uppercase', marginTop:1 }}>
            NUGGETS
          </div>
        </div>
      </div>
    );
  }

  // login / loading — large centered stacked
  const large = variant === 'login';
  return (
    <div style={{ display:'inline-flex', flexDirection:'column', alignItems:'center', userSelect:'none' }}>
      {/* Stacked gold bars */}
      <div style={{ display:'flex', flexDirection:'column', gap: large ? 5 : 3.5, width: large ? 120 : 60, marginBottom: large ? 18 : 10 }}>
        <div style={{ ...goldBar(), height: large ? 13 : 8, borderRadius: large ? 4 : 2.5 }} />
        <div style={{ ...goldBar(), height: large ? 13 : 8, borderRadius: large ? 4 : 2.5 }} />
        <div style={{ ...goldBar(), height: large ? 13 : 8, borderRadius: large ? 4 : 2.5 }} />
      </div>
      {/* Wordmark */}
      <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize: large ? 54 : 28, color:'#fff', letterSpacing:'-0.04em', lineHeight:1 }}>
        <span style={{ color:'#E87722' }}>40</span>GEO
      </div>
      <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize: large ? 11 : 7, letterSpacing: large ? '0.42em' : '0.28em', color:'rgba(255,210,60,0.8)', textTransform:'uppercase', marginTop: large ? 5 : 3 }}>
        NUGGETS
      </div>
    </div>
  );
}

// Header brand mark. Uses the org's uploaded logo when present, but falls
// back to the built-in FortyGeoLogo wordmark if that URL is missing OR
// fails to load — a stale logo_url (e.g. an old Supabase upload that no
// longer resolves) otherwise renders as a broken-image icon in the top bar.
function HeaderLogo({ orgSettings, isMobile }) {
  const [broken, setBroken] = useState(false);
  const url = orgSettings?.logo_url;
  if (url && !broken) {
    return (
      <img
        src={url}
        alt={orgSettings?.org_name || "40Geo Nuggets"}
        onError={() => setBroken(true)}
        style={{ height:isMobile?26:34, width:"auto", display:"block", filter:"drop-shadow(0 0 8px rgba(232,119,34,0.3))" }}
      />
    );
  }
  return <FortyGeoLogo variant={isMobile?"icon":"wordmark"} />;
}

// ═══════════════════════════════════════════════════════════════════
// AUTH-ERROR / RETRY SCREEN
// PATH B: sign-in itself happens on Keycloak (src/main.jsx renders the
// "Sign in" prompt → Keycloak login page). By the time this component
// mounts the user is already Keycloak-authenticated, so we only reach
// this screen if we couldn't turn that session into an app user
// (no profile row, inactive account, or a load failure). Offer a retry
// and a sign-out (which drops the Keycloak session and returns them to
// the login prompt) rather than a defunct email/password form.
// ═══════════════════════════════════════════════════════════════════
function LoginScreen({ authError }) {
  const primaryBtn = {
    background:"#E87722", border:"none", borderRadius:6, padding:"12px 24px",
    fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:12,
    color:"#fff", cursor:"pointer", letterSpacing:"0.06em", textTransform:"uppercase",
  };
  const ghostBtn = {
    background:"none", border:"1px solid rgba(255,255,255,0.18)", borderRadius:6, padding:"12px 24px",
    fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:12,
    color:"rgba(255,255,255,0.7)", cursor:"pointer", letterSpacing:"0.06em", textTransform:"uppercase",
  };

  return (
    <div style={{ minHeight:"100vh", background:"#0D1B2A", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, backgroundImage:`linear-gradient(rgba(255,255,255,0.03) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.03) 1px,transparent 1px)`, backgroundSize:"40px 40px" }} />
      <div style={{ position:"relative", zIndex:1, width:"100%", maxWidth:420, padding:24, textAlign:"center" }}>
        <div style={{ display:"inline-block", filter:"drop-shadow(0 0 32px rgba(232,119,34,0.25))", marginBottom:24 }}>
          <FortyGeoLogo variant="login" />
        </div>

        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:15, fontWeight:700, color:"#fff", marginBottom:8 }}>
          {authError ? "We couldn't open your account" : "Preparing your account…"}
        </div>
        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.5)", lineHeight:1.6, marginBottom:24 }}>
          {authError
            ? authError
            : "You're signed in, but we're still loading your profile. If this doesn't clear, try again or sign out."}
        </div>

        <div style={{ display:"flex", gap:12, justifyContent:"center" }}>
          <button onClick={() => window.location.reload()} style={primaryBtn}>Retry</button>
          <button onClick={() => signOut()} style={ghostBtn}>Sign out</button>
        </div>
      </div>
      <style>{GLOBAL_CSS}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ROOT APP — listens to auth state changes
// ═══════════════════════════════════════════════════════════════════
// AMP MODULE REGISTRY — initial state
// ═══════════════════════════════════════════════════════════════════

// Color helper for SolarSystemView sphere shading
const hexToRgb = (hex) => {
  const h = hex.replace('#','');
  return [parseInt(h.slice(0,2),16), parseInt(h.slice(2,4),16), parseInt(h.slice(4,6),16)];
};

// Configurable status list — order determines orbital ring (first = closest to AMP)
const INIT_STATUSES = [
  { id:'live',          label:'Live',          color:'#22C55E' },
  { id:'beta',          label:'Beta',          color:'#A78BFA' },
  { id:'user-testing',  label:'User Testing',  color:'#F59E0B' },
  { id:'building',      label:'Building',      color:'#E87722' },
  { id:'planned',       label:'Planned',       color:'#60A5FA' },
  { id:'paused',        label:'Paused',        color:'#6B7280' },
];

const INIT_MODULES = [
  { id:'amp-core',       name:'AMP Core',          status:'live',     type:'platform', desc:'Process Builder, Logbook, Data Adapters, Cloud Storage, Reports',
    features:[
      { id:'f-pb',    name:'Process Builder',         status:'live',     crossModules:[] },
      { id:'f-lb',    name:'Logbook',                 status:'live',     crossModules:[] },
      { id:'f-da',    name:'API Data Adapters',        status:'live',     crossModules:[] },
      { id:'f-cs',    name:'Cloud Storage (AWS/Wasabi)',status:'live',    crossModules:[] },
      { id:'f-rp',    name:'Reports & Dashboards',    status:'live',     crossModules:[] },
    ]},
  { id:'gis',            name:'GIS / Geo Map',        status:'live',     type:'platform', desc:'ESRI + MapLibre asset visualization, basemaps, sensor overlays',
    features:[
      { id:'f-esri',  name:'ESRI Integration',        status:'live',     crossModules:[] },
      { id:'f-ml',    name:'MapLibre Renderer',        status:'live',     crossModules:[] },
      { id:'f-sensor',name:'Sensor Overlay',           status:'live',     crossModules:['amp-core'] },
    ]},
  { id:'user-mgmt',      name:'User Management',      status:'live',     type:'platform', desc:'Multi-tenant org→company→user hierarchy, group-based Writer/Reader RBAC',
    features:[
      { id:'f-rbac',  name:'Group RBAC (Writer/Reader)',status:'live',    crossModules:[] },
      { id:'f-mt',    name:'Multi-tenant Isolation',  status:'live',     crossModules:[] },
      { id:'f-sso',   name:'Google SSO',               status:'live',     crossModules:[] },
    ]},
  { id:'messenger',      name:'Messenger',             status:'live',     type:'module',   desc:'In-app team messaging — channels and DMs',
    features:[
      { id:'f-ch',    name:'Channels',                 status:'live',     crossModules:[] },
      { id:'f-dm',    name:'Direct Messages',          status:'live',     crossModules:[] },
      { id:'f-mnotif',name:'Notifications',            status:'building', crossModules:['launchpad'] },
    ]},
  { id:'nuggets-kanban', name:'Nuggets Kanban',        status:'building', type:'module',   desc:'Internal task tracking — replaces Jira/Trello for 40Geo team',
    features:[
      { id:'f-kb',    name:'Kanban Board',             status:'building', crossModules:[] },
      { id:'f-ntypes',name:'Nugget Types & Tags',      status:'building', crossModules:[] },
      { id:'f-hdt',   name:'Help Desk Ticketing',      status:'planned',  crossModules:['helpdesk'] },
      { id:'f-zen',   name:'Zendesk Migration',        status:'planned',  crossModules:['helpdesk'] },
      { id:'f-aud',   name:'Audit Trail',              status:'building', crossModules:['amp-core'] },
    ]},
  { id:'launchpad',      name:'Launchpad',             status:'building', type:'module',   desc:'WebRTC video/audio calling with SSE signaling',
    features:[
      { id:'f-rtc',   name:'WebRTC P2P Video',         status:'building', crossModules:[] },
      { id:'f-ss',    name:'Screen Share',             status:'building', crossModules:[] },
      { id:'f-inv',   name:'Call Invites',             status:'building', crossModules:['messenger'] },
      { id:'f-extlp', name:'External Calling',         status:'planned',  crossModules:['launchpad-ext'] },
    ]},
  { id:'strategy-lab',   name:'Strategy Lab',          status:'building', type:'module',   desc:'AI planning and module orchestration — this view',
    features:[
      { id:'f-reg',   name:'Module Registry',          status:'building', crossModules:[] },
      { id:'f-ai',    name:'Claude Advisor',           status:'building', crossModules:[] },
      { id:'f-solar', name:'Solar System View',        status:'building', crossModules:[] },
      { id:'f-a2n',   name:'Action → Nugget',          status:'building', crossModules:['nuggets-kanban'] },
    ]},
  { id:'helpdesk',       name:'Help Desk',             status:'planned',  type:'module',   desc:'External ticketing for clients — replaces Zendesk, built on Nuggets engine',
    features:[
      { id:'f-xt',    name:'Client Ticket Submission', status:'planned',  crossModules:['nuggets-kanban'] },
      { id:'f-sla',   name:'SLA Management',           status:'planned',  crossModules:[] },
      { id:'f-kb2',   name:'Knowledge Base',           status:'planned',  crossModules:[] },
    ]},
  { id:'launchpad-ext',  name:'External Launchpad',    status:'planned',  type:'module',   desc:'Cross-org/tenant video calling — clients call 40Geo support',
    features:[
      { id:'f-co',    name:'Cross-org Calling',        status:'planned',  crossModules:['launchpad','user-mgmt'] },
      { id:'f-sq',    name:'Support Queue',            status:'planned',  crossModules:['helpdesk'] },
    ]},
  { id:'tenant-portal',  name:'Tenant Portal',         status:'planned',  type:'platform', desc:'Client-facing AMP shell with scoped module access per company',
    features:[
      { id:'f-tpa',   name:'Tenant Auth Flow',         status:'planned',  crossModules:['user-mgmt'] },
      { id:'f-tps',   name:'Scoped Module Access',     status:'planned',  crossModules:['amp-core'] },
      { id:'f-tph',   name:'Help Desk Access',         status:'planned',  crossModules:['helpdesk'] },
    ]},
];


// ─── DATA FLOW GALAXY ────────────────────────────────────────────
const INIT_DATA_ORGS = [
  { id:'ambipar-wob',    name:'Ambipar-WOB',    color:'#FF6B6B' },
  { id:'acbl',           name:'ACBL',           color:'#3CF0FF' },
  { id:'40geo-internal', name:'40Geo Internal', color:'#22C55E' },
  { id:'woodside',       name:'Woodside',       color:'#FFB830' },
];

const INIT_DATASETS = [
  { id:'ais',          name:'AIS Vessel Positions',    category:'Maritime',     source:'external',    flow:'inbound',       orgs:['*'],                       color:'#3CF0FF', desc:'Real-time vessel transponder data — crosses all orgs' },
  { id:'weather',      name:'Weather & Environmental', category:'Environmental',source:'external',    flow:'inbound',       orgs:['*'],                       color:'#60A5FA', desc:'NOAA, sea state, wind, tidal and visibility data' },
  { id:'amp-out',      name:'AMP Processed Analytics', category:'Analytics',    source:'platform',    flow:'outbound',      orgs:['*'],                       color:'#E87722', desc:'Risk scores, compliance reports and pattern analysis from AMP' },
  { id:'hazmat',       name:'HAZMAT Manifests',        category:'Compliance',   source:'client',      flow:'bidirectional', orgs:['ambipar-wob','acbl'],      color:'#F59E0B', desc:'Dangerous goods, spill inventory, MSDS records' },
  { id:'incidents',    name:'Incident Reports',        category:'Operations',   source:'client',      flow:'bidirectional', orgs:['ambipar-wob'],             color:'#FF6B6B', desc:'Spill response and environmental incident data' },
  { id:'acbl-fleet',   name:'ACBL Fleet & Voyage',     category:'Maritime',     source:'client',      flow:'bidirectional', orgs:['acbl'],                   color:'#A78BFA', desc:'Barge positions, voyage logs, condition reports' },
  { id:'satellite',    name:'SAR Satellite Imagery',   category:'Surveillance', source:'external',    flow:'inbound',       orgs:['ambipar-wob','woodside'],  color:'#22C55E', desc:'SAR passes for oil spill detection' },
  { id:'port-state',   name:'Port State Control',      category:'Compliance',   source:'regulatory',  flow:'inbound',       orgs:['acbl','woodside'],         color:'#FF3CAC', desc:'PSC inspection records, detentions, deficiencies' },
  { id:'woodside-ops', name:'Woodside Platform Ops',   category:'Energy',       source:'client',      flow:'bidirectional', orgs:['woodside'],                color:'#FFB830', desc:'Offshore platform ops, maintenance and HSE data' },
];

export default function Nuggets() {
  // PATH B — auth + backend readiness is handled in src/main.jsx
  // (Keycloak init) and per-request via the /api layer. There's no
  // longer a client library to configure, so no env gate here.
  return <NuggetsInner />;
}

function NuggetsInner() {
  const [dark, setDark]               = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [signingIn,   setSigningIn]   = useState(false);  // true while processing OAuth redirect
  const [authError,   setAuthError]   = useState(null);
  const [view, setView]               = useState("dashboard");
  // After login, if the user is a reviewer-company user (e.g. APEM) whose
  // sidebar doesn't include the current view, snap them to Deal Room so
  // they don't land on a blank Mission Control they can't see. Staff
  // (super_admin / admin) are never auto-snapped.
  useEffect(() => {
    if (!currentUser?.company) return;
    if (['super_admin', 'admin'].includes(currentUser?.role)) return;
    if (currentUser.company === (orgSettings?.org_name || '40Geo Nuggets')) return;
    if (view !== 'deal_room' && view !== 'settings') setView('deal_room');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.id, currentUser?.company, currentUser?.role]);
  const [nuggets, setNuggets]         = useState(INIT_NUGGETS);
  const [users, setUsers]             = useState([]);
  const [permissions, setPermissions] = useState(PERMISSIONS);
  const [toast, setToast]             = useState(null);
  const [quoteIdx, setQuoteIdx]       = useState(0);
  const [audit, setAudit]             = useState(INIT_AUDIT);
  const [companies, setCompanies]         = useState(INIT_COMPANIES);
  // Company configs come from DB only. The old localStorage cache was the
  // sticky source of the test-user orgId override — proactively clear it
  // on load so stale sessions heal on refresh.
  try { localStorage.removeItem('nuggets_company_configs'); } catch(_) {}
  const [companyConfigs, setCompanyConfigs] = useState({});
  const [nuggetTypes, setNuggetTypes]     = useState(INIT_NUGGET_TYPES);
  const [dbReady, setDbReady]             = useState(false);
  const [notifSettings, setNotifSettings] = useState(null);
  const [orgSettings, setOrgSettings]     = useState({ org_name:"40Geo Nuggets", logo_url:null, features:{ messenger:true, deploy_ops:true, geo_map:true, proto_lab:true, notifications:true, help_desk:true, crm:true, cyber_security:true, deal_room:true } });
  const [cyberThreats, setCyberThreats]   = useState([]);
  const [chat, setChat]               = useState([{ role:"assistant", content:"Welcome to AMP Strategy Lab. I'm your platform architect advisor.\n\nI have full context on your module registry, current tasks, and AMP's multi-tenant architecture. Ask me to help plan a module integration, sequence your roadmap, design a permission model, or think through a technical trade-off.\n\nWhat are we planning today?" }]);
  const [chatInput, setChatInput]     = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [modules, setModules]         = useState(INIT_MODULES);
  const [moduleStatuses, setModuleStatuses] = useState(INIT_STATUSES);
  const [incomingCall, setIncomingCall] = useState(null); // { sessionId, channelId, channelName, fromName }
  const [autoJoinChannelId, setAutoJoinChannelId] = useState(null);
  const inviteSubRef = useRef(null);

  // ─── PWA / Push Notifications ─────────────────────────────────
  const [pushEnabled, setPushEnabled]   = useState(false);
  const [pushLoading, setPushLoading]   = useState(false);
  const swRegRef = useRef(null);
  const lastAuthUidRef = useRef(null); // guard: prevent double-load from getSession + SIGNED_IN
  const sessionIdRef   = useRef(null); // current user_sessions.id for heartbeats / unload

  // Declared here (not below) so every useEffect in this component can
  // reference it without a TDZ crash when React evaluates the deps array.
  const showToast = useCallback((msg) => {
    const q = QUOTES[Math.floor(Math.random() * QUOTES.length)];
    setToast({ msg, q });
    setTimeout(() => setToast(null), 5500);
  }, []);

  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  // Cross-module deep-link target. Set by CRM's "Open in Proposal
  // Coach" handler before switching view, consumed by ProposalCoach
  // on mount to auto-open that opportunity's detail.
  const [pendingProposalCoachOppId, setPendingProposalCoachOppId] = useState(null);
  // Sidebar collapsed by default; clicking the chevron expands it
  // (with full module labels). Preference persisted to localStorage.
  const [sidebarExpanded, setSidebarExpanded] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem('sidebarExpanded') === 'true';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('sidebarExpanded', String(sidebarExpanded));
  }, [sidebarExpanded]);
  const [showMoreNav, setShowMoreNav] = useState(false);
  // Accessibility prefs: text size, motion, contrast, focus ring, density.
  // Persisted to localStorage and mirrored to <html data-a11y-*> attrs
  // so a runtime stylesheet can react — see src/hooks/useAccessibility.js.
  const a11y = useAccessibility();
  const [showPrefs, setShowPrefs] = useState(false);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const C = THEME[dark ? "dark" : "light"];

  useEffect(() => {
    const t = setInterval(() => setQuoteIdx(i => (i+1) % QUOTES.length), 9000);
    return () => clearInterval(t);
  }, []);

  // ─── Register service worker (PWA) ────────────────────────────
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.register('/sw.js', { scope: '/' })
      .then((reg) => {
        swRegRef.current = reg;
        console.log('[SW] registered', reg.scope);
      })
      .catch((err) => console.warn('[SW] registration failed:', err));
  }, []);

  // ─── BOOT: load the current session, then react to auth changes ──
  // PATH B: Keycloak (src/main.jsx) completes the OIDC handshake before
  // this component mounts, so there's no redirect code to process here —
  // we just read the session and load the app user.
  useEffect(() => {
    const timeout = setTimeout(() => { setAuthLoading(false); setSigningIn(false); }, 10000);

    (async () => {
      const { data: { session } } = await db.auth.getSession();
      try {
        if (session?.user) await onAuthenticated(session.user);
      } catch (err) {
        console.error("Auth error:", err);
      } finally {
        clearTimeout(timeout);
        setAuthLoading(false);
      }
    })();

    // Listen for subsequent auth changes (magic link sign-in, sign-out)
    const { data: { subscription } } = db.auth.onAuthStateChange(
      async (event, session) => {
        try {
          console.log('[auth-event]', event, 'session?', !!session);
          if (event === "SIGNED_IN") {
            setSigningIn(true);
            if (session?.user) await onAuthenticated(session.user);
            clearTimeout(timeout);
            setSigningIn(false);
            setAuthLoading(false);
          } else if (event === "SIGNED_OUT") {
            // Fire-and-forget: best effort to close any session row that
            // wasn't handled by handleLogout (token expiry, external
            // sign-out, etc.). NEVER await this — the call goes through
            // the now-unauthenticated client, RLS denies it, and an
            // un-resolved promise here used to block setCurrentUser(null)
            // → "Signed out" audit logged but the app stayed logged in.
            if (sessionIdRef.current) {
              endSession(sessionIdRef.current).catch(() => {});
              sessionIdRef.current = null;
            }
            lastAuthUidRef.current = null;
            setCurrentUser(null);
            setUsers([]);
            setNuggets(INIT_NUGGETS);
            setDbReady(false);
          }
        } catch (err) {
          console.error("[auth] EXCEPTION in onAuthStateChange:", err?.message || err, err?.stack);
          // Make sure the user isn't stranded at the spinner if something
          // upstream threw — pop them back to the login surface with a
          // visible reason instead of silently swallowing it.
          setAuthError(`Sign-in error: ${err?.message || 'unknown'}`);
          setSigningIn(false);
          setAuthLoading(false);
        }
      }
    );

    return () => { clearTimeout(timeout); subscription.unsubscribe(); };
  }, []);

  // ─── Session heartbeat (5 min) + best-effort beforeunload close ──
  useEffect(() => {
    const HEARTBEAT_MS = 5 * 60 * 1000;
    const tick = () => { if (sessionIdRef.current) heartbeatSession(sessionIdRef.current); };
    const interval = setInterval(tick, HEARTBEAT_MS);
    const onUnload = () => { if (sessionIdRef.current) endSessionBeacon(sessionIdRef.current); };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide',     onUnload);
    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide',     onUnload);
    };
  }, []);

  // ─── CALLED ONCE PER LOGIN — load profile + all org data ──────────
  //
  // Re-entrancy: previously this set lastAuthUidRef.current at the TOP
  // and never cleared it on failure. If loadUserProfile hung (e.g. a
  // stale boot-time SIGNED_IN whose query never completed), the ref
  // permanently pointed to that uid and every subsequent SIGNED_IN —
  // including the user's actual login click — was rejected as
  // "already loaded for uid", stranding currentUser at null.
  //
  // Now the ref is only set after a successful profile load, and the
  // profile load is raced against an 8s timeout so a hung query
  // surfaces as a visible error instead of an invisible loop.
  const onAuthenticated = async (authUser) => {
    console.log('[auth] onAuthenticated start', { uid: authUser?.id, email: authUser?.email, lastAuthUid: lastAuthUidRef.current });
    if (lastAuthUidRef.current === authUser.id) {
      console.log('[auth] onAuthenticated skip — already loaded for uid');
      return;
    }

    let profile = null;
    try {
      profile = await Promise.race([
        loadUserProfile(authUser),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Profile load timed out after 8s')), 8000)),
      ]);
    } catch (err) {
      console.error('[auth] loadUserProfile threw:', err?.message || err);
      // Guard: a concurrent call (e.g. boot's SIGNED_IN vs the user's
      // explicit click) may have already succeeded. Don't overwrite a
      // good login with a stale error.
      if (lastAuthUidRef.current === authUser.id) {
        console.log('[auth] another concurrent call already succeeded — suppressing stale error');
        return;
      }
      setAuthError(`Sign-in error: ${err?.message || 'profile load failed'} — try again or contact your admin.`);
      return;
    }
    console.log('[auth] loadUserProfile result:', profile
      ? { id: profile.id, email: profile.email, role: profile.role, isActive: profile.isActive, orgId: profile.orgId, company: profile.company }
      : null);
    if (lastAuthUidRef.current === authUser.id) {
      // Same guard: a sibling call already finished and set state.
      console.log('[auth] another concurrent call already completed — skipping state update');
      return;
    }
    if (!profile) {
      console.warn('[auth] BAIL: loadUserProfile returned null → setAuthError + return');
      setAuthError(`Sign-in failed for ${authUser?.email} — contact your admin.`);
      return;
    }

    // Silent inactive-user gate: deactivated accounts get a polite
    // boot rather than a stack trace.
    if (profile.isActive === false) {
      console.warn('[auth] BAIL: profile.isActive === false → signOut');
      setAuthError('Account is inactive — contact your admin.');
      await signOut();
      return;
    }

    // Profile is good — lock in the ref so concurrent SIGNED_IN events
    // (e.g. boot + fresh sign-in racing) don't double-load org data.
    lastAuthUidRef.current = authUser.id;

    touchLastSeen(profile.id);

    // Start a user_sessions row for time-in-app tracking.
    startUserSession(profile.id, profile.orgId).then(id => {
      if (id) sessionIdRef.current = id;
    });

    console.log('[auth] loading org data…');
    const [nuggetsData, usersData, companiesData, typesData, notifData, orgData] = await Promise.all([
      loadNuggets(profile.orgId),
      loadOrgUsers(profile.orgId),
      loadCompanies(profile.orgId),
      loadNuggetTypes(profile.orgId),
      loadNotificationSettings(profile.orgId),
      loadOrgSettings(profile.orgId),
    ]);

    // Keep the in-memory companyConfigs map in sync with what's in the DB
    // so the Company Manager UI shows the latest. The login-time test-user
    // override used to live here; it was removed because it silently
    // remapped orgId and caused cross-device data invisibility.
    console.log('[auth] org data loaded — setting currentUser → entering app');
    setCompanyConfigs(orgData?.company_configs || {});
    setAuthError(null);
    setCurrentUser(profile);

    if (nuggetsData)       setNuggets(nuggetsData.filter((n,i,a) => a.findIndex(x=>x.id===n.id)===i));
    if (usersData?.length) setUsers(usersData.map(u => ({ ...u, status: u.id === profile.id ? "online" : "offline" })));
    if (typesData?.length) setNuggetTypes(typesData);
    if (companiesData?.length) setCompanies(companiesData);
    if (notifData)             setNotifSettings(notifData);
    if (orgData) {
      setOrgSettings(orgData);
      // Populate per-feature model overrides from saved org settings
      loadAiModelOverrides(orgData.ai_models);
    }

    setDbReady(true);
    setAudit(p => [{
      ts:     "just now",
      userId: profile.id,
      action: `Signed in via Google · ${profile.email}`,
      type:   "auth"
    }, ...p]);
  };

  // ─── Real-time nugget sync ─────────────────────────────────────
  useEffect(() => {
    if (!currentUser?.orgId) return;
    const sub = dbSubscribeToNuggets(currentUser.orgId, (event, row) => {
      if (event === 'DELETE') {
        setNuggets(p => p.filter(n => n.id !== row.id));
      } else {
        // INSERT and UPDATE: upsert-in-place to prevent duplicates
        setNuggets(p => {
          const exists = p.some(n => n.id === row.id);
          const updated = exists
            ? p.map(n => n.id === row.id ? dbRowToNugget(row) : n)
            : [dbRowToNugget(row), ...p];
          // Final dedup pass — guards against any race between load + realtime
          return updated.filter((n, i, arr) => arr.findIndex(x => x.id === n.id) === i);
        });
      }
    });
    return () => db.removeChannel(sub);
  }, [currentUser?.orgId]);

  // ─── In-app message alerts (fires from any view) ──────────────
  // Toast pops for every incoming message — DM or channel — while
  // the user is on Nuggets, Notes, Admin, etc.  Also fires a
  // native browser Notification when the tab is hidden so the OS
  // flashes it in the tray/dock even if Nuggets isn't in the front
  // tab.  Runs alongside Messenger's own unread-counter subscription
  // — this is the cross-view attention layer.
  useEffect(() => {
    if (!currentUser?.orgId || !currentUser?.id) return;
    let dmChannelIds     = new Set();
    let channelIds       = new Set();
    let channelNameById  = new Map();

    const refreshDms = () =>
      loadChannels(currentUser.orgId, currentUser.id).then(chs => {
        const arr = chs || [];
        dmChannelIds    = new Set(arr.filter(c => c.type === 'dm').map(c => c.id));
        channelIds      = new Set(arr.filter(c => c.type !== 'dm').map(c => c.id));
        channelNameById = new Map(arr.map(c => [c.id, c.name]));
      }).catch(() => {});
    refreshDms();

    // New DM channels (e.g. someone starts a DM with me from another device)
    const listSub = subscribeToChannelList(currentUser.orgId, currentUser.id, refreshDms);

    // Every new message in the org — fire an alert.  Toast for
    // DMs (always) + channels the caller is a member of.  When the
    // browser tab isn't focused we also fire a native Notification
    // so the OS can flash it in the tray / dock / lock screen.
    const msgSub = subscribeToOrgMessages(currentUser.orgId, (payload) => {
      const msg = payload.new;
      if (!msg || msg.user_id === currentUser.id) return;
      const isDm      = dmChannelIds.has(msg.channel_id);
      const isChannel = channelIds.has(msg.channel_id);
      if (!isDm && !isChannel) return;
      const sender  = users.find(u => u.id === msg.user_id);
      const preview = (msg.content || '').replace(/\n+/g, ' ').slice(0, 90);
      const chName  = channelNameById.get(msg.channel_id) || 'a channel';
      const icon    = isDm ? '💬' : '#';
      const title   = isDm
        ? `${sender?.name || 'Someone'}`
        : `${sender?.name || 'Someone'} in #${chName}`;

      // 1) In-app toast (visible whether the tab is focused or not).
      showToast(`${icon} ${title}: ${preview}`);

      // 2) Native browser notification when the tab is hidden so the
      //    user sees something even if Nuggets isn't in the front tab.
      //    Requires the user to have granted Notification permission.
      if (document.hidden && typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        try {
          const n = new Notification(title, {
            body: preview,
            tag:  `nuggets-msg-${msg.channel_id}`,   // collapse burst of msgs into one
            icon: '/favicon.ico',
          });
          n.onclick = () => { window.focus(); n.close(); };
        } catch (err) {
          console.warn('[msg-notify] Notification() failed:', err?.message);
        }
      }
    }, 'root-toast');

    return () => { listSub?.unsubscribe(); msgSub?.unsubscribe(); };
  }, [currentUser?.orgId, currentUser?.id, users, showToast]);

  // ─── Subscribe to incoming Launchpad invites ──────────────────
  useEffect(() => {
    if (!currentUser?.id) return;
    inviteSubRef.current = subscribeLaunchpadInvites(currentUser.id, (payload) => {
      setIncomingCall(payload);
    });
    return () => {
      if (inviteSubRef.current) db.removeChannel(inviteSubRef.current);
    };
  }, [currentUser?.id]);

  const addAudit = useCallback((action, type = "system") => {
    if (!currentUser) return;
    setAudit(p => [{ ts:"just now", userId:currentUser.id, action, type }, ...p.slice(0,49)]);
    if (dbReady) {
      writeAuditLog({
        orgId:      currentUser.orgId,
        userId:     currentUser.id,
        action,
        entityType: type,
      });
    }
  }, [currentUser, dbReady]);

  const handleLogout = async () => {
    addAudit("Signed out");
    // Close the user_sessions row while we still have auth — once
    // db.auth.signOut() runs, RLS blocks any update on this row.
    if (sessionIdRef.current) {
      try { await endSession(sessionIdRef.current); } catch (_) {}
      sessionIdRef.current = null;
    }
    await signOut();
  };

  // ─── Push notification helpers ────────────────────────────────
  const enablePushNotifications = async () => {
    if (!('Notification' in window) || !('PushManager' in window)) {
      showToast('Push notifications not supported on this device');
      return;
    }
    setPushLoading(true);
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        showToast('Notification permission denied');
        setPushLoading(false);
        return;
      }
      // Get the VAPID public key from the server
      const vapidResp = await fetch('/api/vapid-public-key');
      if (!vapidResp.ok) {
        showToast('Push setup unavailable — VAPID key not configured');
        setPushLoading(false);
        return;
      }
      const { publicKey } = await vapidResp.json();
      const reg = swRegRef.current || await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await savePushSubscription(currentUser.id, subscription);
      setPushEnabled(true);
      showToast('Push notifications enabled');
    } catch (err) {
      console.error('enablePushNotifications:', err);
      showToast('Could not enable push notifications');
    }
    setPushLoading(false);
  };

  const disablePushNotifications = async () => {
    setPushLoading(true);
    try {
      const reg = swRegRef.current || await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
      await deletePushSubscription(currentUser.id);
      setPushEnabled(false);
      showToast('Push notifications disabled');
    } catch (err) {
      console.error('disablePushNotifications:', err);
    }
    setPushLoading(false);
  };

  // Send an in-app push notification to another user (or many users).
  // Server looks up subscriptions via service role — callers just pass ids.
  // `targets` may be a single userId or an array of userIds.
  const notifyUserViaPush = async (targets, title, body, data = {}) => {
    try {
      const ids = Array.isArray(targets) ? targets.filter(Boolean) : [targets].filter(Boolean);
      if (!ids.length) return;
      const { data: { session } } = await db.auth.getSession();
      if (!session) return;
      await fetch('/api/push-notify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify(
          ids.length === 1
            ? { user_id: ids[0], title, body, data, requireInteraction: false }
            : { user_ids: ids, title, body, data, requireInteraction: false }
        ),
      });
    } catch (err) {
      console.warn('notifyUserViaPush:', err.message);
    }
  };

  const canDo = (perm, nugget = null) => {
    if (!currentUser) return false;
    if (perm === "nugget_edit_own" && nugget)
      return can(currentUser.role, "nugget_edit_any") || nugget.ownerId === currentUser.id;
    return can(currentUser.role, perm);
  };

  // Deduplicate by id before any filtering (guards against race between realtime + initial load)
  const uniqueNuggets = nuggets.filter((n, i, arr) => arr.findIndex(x => x.id === n.id) === i);

  const visibleNuggets = uniqueNuggets.filter(n => {
    if (!currentUser) return false;
    if (can(currentUser.role, "nugget_view_all")) return true;
    if (n.visibility === "team")    return can(currentUser.role, "nugget_view_shared");
    if (n.visibility === "private") return n.ownerId === currentUser.id || n.assigneeId === currentUser.id;
    if (n.visibility === "shared")  return (n.sharedWith || []).includes(currentUser.id) || n.ownerId === currentUser.id;
    return false;
  });

  const moveNugget = useCallback(async (id, dir) => {
    const order = ["backlog","in-progress","review","done"];
    let updated = null;
    setNuggets(prev => prev.map(n => {
      if (n.id !== id) return n;
      const ci = order.indexOf(n.col);
      const ni = dir === "fwd" ? Math.min(ci+1,3) : Math.max(ci-1,0);
      updated = { ...n, col: order[ni] };
      addAudit(`Moved ${id} → ${COLS[ni].label}`, "nugget");
      return updated;
    }));
    if (updated) {
      const ok = await dbSaveNugget(updated, currentUser?.orgId);
      if (!ok) {
        showToast(`❌ Move not saved — DB write failed. Check RLS on nuggets table.`);
        return;
      }
    }
    showToast("Nugget moved. Get busy shipping or get busy dying.");
  }, [addAudit, showToast, currentUser]);

  const saveCompanyConfig = (name, config) => {
    // Strip testUsers + companyOrgId from any incoming config — those fields
    // were part of the removed login-time override feature.
    // eslint-disable-next-line no-unused-vars
    const { testUsers, companyOrgId, ...clean } = config;
    const updated = { ...companyConfigs, [name]: clean };
    setCompanyConfigs(updated);
    // Persist to org_settings so company feature flags are visible from any device
    const newOrgSettings = { ...orgSettings, company_configs: updated };
    setOrgSettings(newOrgSettings);
    if (currentUser?.orgId) dbSaveOrgSettings(currentUser.orgId, newOrgSettings);
  };

  const saveNuggetLocal = async (nugget) => {
    // Capture the pre-save state so we can diff for push-notify decisions.
    const prior = nuggets.find(n => n.id === nugget.id) || null;
    const isNew = !prior;

    // upsert: add if new, replace if existing
    setNuggets(p => p.some(n => n.id === nugget.id) ? p.map(n => n.id === nugget.id ? nugget : n) : [nugget, ...p]);
    addAudit(`Saved ${nugget.id}`, "nugget");
    if (dbReady) {
      const ok = await dbSaveNugget(nugget, currentUser?.orgId);
      if (!ok) {
        showToast(`❌ Nugget save failed for ${currentUser?.name} — role: ${currentUser?.role}, orgId: ${currentUser?.orgId}. Check console + RLS.`);
        return;
      }
    }

    // Push-notify interested users. Fire-and-forget; never blocks the save path.
    // Scope:
    //   • new nugget with an assignee other than self       → notify assignee
    //   • assignee changed                                   → notify new assignee
    //   • column moved or any field edited (existing row)    → notify owner + assignee (minus self)
    notifyNuggetChange(prior, nugget, isNew).catch(() => {});
  };

  const notifyNuggetChange = async (prior, next, isNew) => {
    const targets = new Set();
    const addIfOther = (uid) => { if (uid && uid !== currentUser.id) targets.add(uid); };

    let verb = 'updated';
    if (isNew) {
      verb = 'assigned to you';
      addIfOther(next.assigneeId);
    } else {
      if (prior?.assigneeId !== next.assigneeId) {
        verb = 'assigned to you';
        addIfOther(next.assigneeId);
      } else if (prior?.col !== next.col) {
        verb = `moved to ${next.col}`;
        addIfOther(next.ownerId);
        addIfOther(next.assigneeId);
      } else {
        // field edit — title/desc/priority/etc
        addIfOther(next.ownerId);
        addIfOther(next.assigneeId);
      }
    }
    if (!targets.size) return;

    const title = `Nugget ${verb}`;
    const body  = `${currentUser.name || 'Someone'}: ${(next.title || '').slice(0, 100)}`;
    await notifyUserViaPush([...targets], title, body, {
      kind: 'nugget',
      nugget_id: next.id,
      url: `/?nugget=${encodeURIComponent(next.id)}`,
    });
  };

  const deleteNuggetLocal = async (id) => {
    setNuggets(p => p.filter(n => n.id !== id));
    addAudit(`Deleted ${id}`, "nugget");
    if (dbReady) await dbDeleteNugget(id);
  };

  const addCompanyLocal = async (name) => {
    if (!companies.includes(name)) setCompanies(p => [...p, name].sort());
    addAudit(`Added company: ${name}`, "admin");
    if (dbReady) await dbAddCompany(name, currentUser?.orgId);
  };

  const deleteCompanyLocal = async (name) => {
    // Remove from local state (both the company list and any companyConfigs entry).
    setCompanies(p => p.filter(c => c !== name));
    setCompanyConfigs(p => { const n = { ...p }; delete n[name]; return n; });
    // Mirror the config removal in org_settings so other devices stay in sync.
    if (currentUser?.orgId) {
      const newConfigs = { ...companyConfigs };
      delete newConfigs[name];
      const newOrgSettings = { ...orgSettings, company_configs: newConfigs };
      setOrgSettings(newOrgSettings);
      dbSaveOrgSettings(currentUser.orgId, newOrgSettings);
    }
    addAudit(`Deleted company: ${name}`, "admin");
    if (dbReady && currentUser?.orgId) await dbDeleteCompany(name, currentUser.orgId);
  };

  const addNuggetTypeLocal = async (name) => {
    if (!nuggetTypes.includes(name)) setNuggetTypes(p => [...p, name].sort());
    addAudit(`Added nugget type: ${name}`, "admin");
    if (dbReady) await dbAddNuggetType(name, currentUser?.orgId);
  };

  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return;
    const userMsg = chatInput.trim();
    setChatInput("");
    const newHistory = [...chat, { role:"user", content:userMsg }];
    setChat(newHistory);
    setChatLoading(true);
    try {
      const moduleContext = modules
        .map(m => `  • ${m.name} [${moduleStatuses.find(s=>s.id===m.status)?.label || m.status}] (${m.type}): ${m.desc}`)
        .join('\n');
      const nuggetContext = nuggets.slice(0, 15)
        .map(n => `  • [${n.col}] ${n.title}${n.priority === 'critical' ? ' ⚠️' : ''}`)
        .join('\n');

      const systemPrompt = `You are the AMP Strategy Advisor — an expert platform architect for 40Geo's Asset Monitoring Platform (AMP).

## Your Role
Help the 40Geo engineering team plan, sequence, and execute the integration of modules into AMP. You reason about dependencies, permission mapping, RLS policies, multi-tenant data isolation, and technical sequencing. You are direct, concise, and technically precise.

## AMP Architecture
Production stack: PostgreSQL on AWS RDS, Keycloak SSO, React frontend (amp.40geo.com). Admin portal: geo-admin.amp.40geo.com.

Multi-tenant hierarchy:
  Org (40Geo) → Company/Tenant (e.g. Ambipar-WOB, ACBL, Woodside) → Users
  Users start with zero access. Permissions granted via Groups (Writer/Reader) scoped to Location + Business Unit.

## Current Module Registry
${moduleContext}

## Current Task Backlog (top 15)
${nuggetContext || '  (empty)'}

## Active User
${currentUser.name} | ${currentUser.email} | Role: ${currentUser.role}

## Response Format
- Be concise but thorough on technical points
- When recommending specific actions, prefix each with "ACTION:" on its own line
- When discussing sequencing, use a numbered list
- When discussing permission design, reference AMP's Group/Writer/Reader model
- No fluff, no preamble`;

      const data  = await callAI({ systemPrompt, messages: newHistory.map(m=>({role:m.role,content:m.content})), feature:'strategy_lab', user:currentUser });
      const reply = data.content?.[0]?.text || "No response from Claude.";
      setChat([...newHistory, { role:"assistant", content:reply }]);
    } catch(e) {
      setChat([...newHistory, { role:"assistant", content:`Error: ${e.message}` }]);
    } finally {
      setChatLoading(false);
    }
  };

  // ─── LOADING SPLASH (checking session on boot) ─────────────────────
  if (authLoading) {
    return (
      <div style={{ minHeight:"100vh", background:"#0D1B2A", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Montserrat',sans-serif" }}>
        <div style={{ textAlign:"center" }}>
          <div style={{ marginBottom:24, opacity:0.85, display:'inline-block' }}><FortyGeoLogo variant="loading" /></div>
          <div style={{ display:"flex", gap:8, justifyContent:"center" }}>
            {[0,0.2,0.4].map((d,i) => (
              <div key={i} style={{ width:8, height:8, borderRadius:"50%", background:"#E87722", animation:`pulse 0.8s ease ${d}s infinite` }}/>
            ))}
          </div>
        </div>
        <style>{GLOBAL_CSS}</style>
      </div>
    );
  }

  // ─── SIGNING IN via OAuth redirect — show spinner, not login form ──
  if (signingIn || (!currentUser && authLoading)) {
    return (
      <div style={{ minHeight:"100vh", background:"#0D1B2A", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Montserrat',sans-serif" }}>
        <div style={{ textAlign:"center" }}>
          <div style={{ marginBottom:24, opacity:0.85, display:"inline-block" }}><FortyGeoLogo variant="loading" /></div>
          <div style={{ display:"flex", gap:8, justifyContent:"center", marginBottom:16 }}>
            {[0,0.2,0.4].map((d,i) => (
              <div key={i} style={{ width:8, height:8, borderRadius:"50%", background:"#E87722", animation:`pulse 0.8s ease ${d}s infinite` }}/>
            ))}
          </div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.35)", letterSpacing:"0.1em", textTransform:"uppercase" }}>Signing you in…</div>
        </div>
        <style>{GLOBAL_CSS}</style>
      </div>
    );
  }

  // ─── Keycloak session couldn't be turned into an app user ────────
  if (!currentUser) return <LoginScreen authError={authError} />;

  const orgF  = orgSettings?.features || {};
  const userF = currentUser?.featureOverrides || {};
  // If the user is tagged with a company in the org's company manager, that
  // company's feature flags clamp what they can see. Staff (super_admin /
  // admin) are NEVER company-clamped — they always see the full app even
  // if they happen to be tagged with a company name like "40Geo".
  const STAFF_ROLES_FEAT = ['super_admin', 'admin'];
  const isStaffForFeat   = STAFF_ROLES_FEAT.includes(currentUser?.role);
  const companyF = (!isStaffForFeat && currentUser?.company)
    ? (companyConfigs?.[currentUser.company]?.features || {})
    : null;
  // For company-tagged (reviewer) users: DENY by default — features must be
  // explicitly true in their company config. For internal staff: allow by
  // default, deny only when explicitly false.
  const featOn = (key) => {
    if (orgF[key] === false) return false;
    if (userF[key] === false) return false;
    if (companyF) return companyF[key] === true;
    return true;
  };

  // Reviewer-company users (e.g. APEM Group during the M&A diligence) get
  // a stripped-down sidebar — only the items explicitly granted by their
  // company config plus Settings (so they can manage their own profile).
  // Staff roles (super_admin / admin) are NEVER treated as reviewers, even
  // if they happen to be tagged with a company name like "40Geo" — staff
  // always see the full app.
  const STAFF_ROLES = ['super_admin', 'admin'];
  const isReviewerUser = !!currentUser?.company
    && !STAFF_ROLES.includes(currentUser?.role)
    && currentUser.company !== (orgSettings?.org_name || '40Geo Nuggets');

  // Sidebar order — daily-driver modules first, admin/back-office at
  // the bottom.  Order agreed 2026-07-07 with Miles.  When adding a
  // new module, drop it in the right band rather than at the end.
  const NAV = [
    // ── Daily drivers ─────────────────────────────────────────
    { id:"dashboard",      label:"Mission Control", Icon:Activity,                             external:false },
    { id:"slack",          label:"Messenger",       Icon:MessageSquare, feature:"messenger",  external:true  },
    { id:"nuggets",        label:"Nuggets",         Icon:Layers,                              external:false },
    { id:"notes",          label:"Notes",           Icon:PenLine,                             external:false },
    // ── Sales / delivery lifecycle ───────────────────────────
    { id:"crm",            label:"CRM",             Icon:TrendingUp,    feature:"crm",         external:false },
    { id:"proposal_coach", label:"Proposal Coach",  Icon:WhistleIcon,   feature:"proposal_coach", external:false },
    { id:"library",        label:"Library",         Icon:BookOpen,      feature:"library",     external:false },
    { id:"deal_room",      label:"Deal Room",       Icon:Briefcase,     feature:"deal_room",   external:true  },
    // ── Ops + platform back-office ───────────────────────────
    { id:"cyber",          label:"Cyber Security",  Icon:ShieldAlert,   feature:"cyber_security", external:false },
    ...(canDo("admin_users") ? [{ id:"db_health", label:"DB Health", Icon:Database, external:false }] : []),
    { id:"lab",            label:"Strategy Lab",    Icon:Brain,         feature:"proto_lab",   external:false },
    { id:"deploy",         label:"Deploy Ops",      Icon:Rocket,        feature:"deploy_ops",  external:false },
    // Dev Notebook — 40Geo internal only, super_admin only
    ...(currentUser.role === 'super_admin'
      ? [{ id:"devnotebook", label:"Dev Notebook", Icon:BookOpen, external:false }] : []),
    { id:"map",            label:"Geo Map",         Icon:MapIcon,       feature:"geo_map",     external:false },
    // ── Admin ────────────────────────────────────────────────
    { id:"settings",       label:"Settings",        Icon:Settings,                            external:true  },
    ...(canDo("admin_users") ? [{ id:"admin", label:"Admin", Icon:Shield, external:false }] : []),
    // Help Desk kept off the visible sidebar (was mid-list before;
    // not part of the new ordering).  Un-comment if it comes back:
    // { id:"helpdesk", label:"Help Desk", Icon:LifeBuoy, feature:"help_desk", external:false },
  ].filter(item => {
    // External (reviewer-company) users only see items flagged for them.
    if (isReviewerUser && !item.external) return false;
    if (item.feature && !featOn(item.feature)) {
      // Exception: reviewer-company users ALWAYS need Deal Room in
      // their sidebar — that's the entire reason they were invited.
      // Per-deal access is governed by deal_members + tab_perms; the
      // company-level deal_room flag isn't the right gate for them.
      // Without this bypass, a reviewer can land on Deal Room via the
      // post-login snap-to but then has no way back from Settings.
      if (isReviewerUser && item.id === 'deal_room') return true;
      return false;
    }
    return true;
  });

  // ── Mobile: primary 5 tabs + overflow drawer ───────────────────
  const MOB_PRIMARY = ["dashboard","nuggets","notes","slack","__more__"];
  const mobNavItems = [
    ...NAV.filter(n => ["dashboard","nuggets","notes","slack"].includes(n.id)),
    { id:"__more__", label:"More", Icon:MoreHorizontal },
  ];
  const mobOverflowItems = NAV.filter(n => !["dashboard","nuggets","notes","slack"].includes(n.id));

  return (
    <div style={{ display:"flex", height:"100dvh", overflow:"hidden", background:C.bg, color:C.text, fontFamily:"'Montserrat',sans-serif" }}>

      {/* Accessibility preferences overlay — mounted at app root so it
          sits above every module. `showPrefs` is toggled from the
          Settings icon in the sidebar. */}
      {showPrefs && (
        <PreferencesPanel
          prefs={a11y.prefs}
          update={a11y.update}
          reset={a11y.reset}
          onClose={() => setShowPrefs(false)}
        />
      )}

      {/* ── DESKTOP SIDEBAR (hidden on mobile) ────────────────── */}
      {!isMobile && (
      <div style={{ width: sidebarExpanded ? 208 : 58, background:C.bg2, borderRight:`1px solid ${C.border}`, display:"flex", flexDirection:"column", alignItems: sidebarExpanded ? 'stretch' : 'center', padding: sidebarExpanded ? '10px 8px' : '12px 0', gap:2, flexShrink:0, transition:'width 0.18s ease' }}>
        {/* Expand / collapse toggle */}
        <button
          onClick={() => setSidebarExpanded(v => !v)}
          title={sidebarExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
          style={{
            width: sidebarExpanded ? '100%' : 42,
            height: 36,
            borderRadius: 6,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarExpanded ? 'flex-end' : 'center',
            cursor: 'pointer',
            padding: sidebarExpanded ? '0 8px' : 0,
            marginBottom: 6,
          }}>
          {sidebarExpanded
            ? <PanelLeftClose size={16} />
            : <PanelLeftOpen size={16} />}
        </button>

        {NAV.map(({ id, label, Icon }) => (
          <button key={id} onClick={() => setView(id)} title={sidebarExpanded ? undefined : label}
            style={{
              width: sidebarExpanded ? '100%' : 42,
              height: 42,
              borderRadius: 6,
              background: view === id ? "rgba(232,119,34,0.12)" : "transparent",
              borderLeft: view === id ? "2px solid #E87722" : "2px solid transparent",
              borderTop: "none", borderRight: "none", borderBottom: "none",
              color: view === id ? "#E87722" : "rgba(255,255,255,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: sidebarExpanded ? 'flex-start' : 'center',
              gap: sidebarExpanded ? 12 : 0,
              padding: sidebarExpanded ? '0 12px' : 0,
              cursor: "pointer",
              transition: "all 0.2s ease",
              fontFamily: "'Montserrat',sans-serif",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: '0.02em',
              textAlign: 'left',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
            }}>
            <Icon size={17} />
            {sidebarExpanded && <span>{label}</span>}
          </button>
        ))}

        <div style={{ flex:1 }} />

        {/* User avatar */}
        {currentUser.picture
          ? <img src={currentUser.picture} alt="" style={{ width:32, height:32, borderRadius:"50%", border:`2px solid #E87722`, marginBottom:6 }} />
          : <div style={{ width:32, height:32, borderRadius:"50%", background:"#1B2B5B", border:"2px solid #E87722", display:"flex", alignItems:"center", justifyContent:"center", fontSize:10, fontWeight:800, color:"#E87722", marginBottom:6, fontFamily:"'Montserrat',sans-serif" }}>{currentUser.initials}</div>
        }
        {/* Preferences (font size, motion, contrast).  Styled to
            match NAV items so it's discoverable — a 14-px gear at
            30% opacity was too easy to miss. */}
        <button
          onClick={() => setShowPrefs(true)}
          title={sidebarExpanded ? undefined : 'Display & Text Size'}
          aria-label="Open display and accessibility preferences"
          style={{
            width: sidebarExpanded ? '100%' : 42,
            height: 38,
            borderRadius: 6,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarExpanded ? 'flex-start' : 'center',
            gap: sidebarExpanded ? 12 : 0,
            padding: sidebarExpanded ? '0 12px' : 0,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: "'Montserrat',sans-serif",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.02em',
            textAlign: 'left',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            marginBottom: 2,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#E87722'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
        >
          <Settings size={17} />
          {sidebarExpanded && <span>Display &amp; Text</span>}
        </button>
        <button
          onClick={handleLogout}
          title={sidebarExpanded ? undefined : 'Sign out'}
          aria-label="Sign out"
          style={{
            width: sidebarExpanded ? '100%' : 42,
            height: 38,
            borderRadius: 6,
            background: 'transparent',
            border: 'none',
            color: 'rgba(255,255,255,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: sidebarExpanded ? 'flex-start' : 'center',
            gap: sidebarExpanded ? 12 : 0,
            padding: sidebarExpanded ? '0 12px' : 0,
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            fontFamily: "'Montserrat',sans-serif",
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.02em',
            textAlign: 'left',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = '#E87722'; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.55)'; }}
        >
          <LogOut size={17} />
          {sidebarExpanded && <span>Sign out</span>}
        </button>
      </div>
      )}

      {/* ── MAIN ─────────────────────────────────────────────── */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", overflow:"hidden" }}>

        {/* TOP BAR */}
        <div style={{ height:isMobile?48:54, background:C.bg2, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", padding:isMobile?"0 12px":"0 20px", gap:isMobile?8:14, flexShrink:0 }}>
          {/* Wordmark / Logo */}
          <HeaderLogo orgSettings={orgSettings} isMobile={isMobile} />
          {!isMobile && orgSettings?.org_name && orgSettings.org_name !== "40Geo Nuggets" && (
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:13, color:"#fff", letterSpacing:"0.05em", whiteSpace:"nowrap" }}>{orgSettings.org_name}</span>
          )}
          {!isMobile && <div style={{ width:1, height:28, background:"rgba(255,255,255,0.08)" }} />}

          {/* Quote ticker — desktop only */}
          {!isMobile && (
            <div style={{ flex:1, overflow:"hidden" }}>
              <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)", fontStyle:"italic" }}>"{QUOTES[quoteIdx].text}" </span>
              <span style={{ fontSize:10, color:"#E87722", fontWeight:600 }}>— {QUOTES[quoteIdx].src}</span>
            </div>
          )}
          {isMobile && <div style={{ flex:1 }} />}

          {/* DB status */}
          <div title={dbReady ? "DB connected" : "Local mode"}
            style={{ display:"flex", alignItems:"center", gap:4, fontSize:9, color:dbReady?"#22C55E":"rgba(255,255,255,0.3)", fontFamily:"'Montserrat',sans-serif", fontWeight:600 }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:dbReady?"#22C55E":"rgba(255,255,255,0.2)", boxShadow:dbReady?"0 0 5px #22C55E":"none" }}/>
            {!isMobile && (dbReady ? "DB" : "LOCAL")}
          </div>

          {/* Role badge — desktop only */}
          {!isMobile && (
            <div style={{ ...Badge(ROLE_META[currentUser.role]?.color||"#E87722", `${ROLE_META[currentUser.role]?.color||"#E87722"}18`, `${ROLE_META[currentUser.role]?.color||"#E87722"}44`) }}>
              {ROLE_META[currentUser.role]?.label || currentUser.role}
            </div>
          )}

          {/* Name — desktop only */}
          {!isMobile && <div style={{ fontSize:12, color:"rgba(255,255,255,0.5)", fontWeight:600 }}>{currentUser.name}</div>}

          {/* Theme toggle */}
          <button onClick={() => setDark(d=>!d)}
            style={{ ...BtnOutline(C), padding:isMobile?"5px 8px":"5px 12px", fontSize:10, display:"flex", alignItems:"center", gap:4 }}>
            {dark ? <Sun size={12}/> : <Moon size={12}/>}
            {!isMobile && (dark ? "LIGHT" : "DARK")}
          </button>

          {/* Build badge — the deployed commit SHA, so you can confirm a
              deploy landed. Matches the `nuggets-<sha>` deployment image. */}
          {!isMobile && (
            <span
              title={`Deployed build: ${__COMMIT_SHA__}`}
              style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.08em", color:"rgba(255,255,255,0.4)", background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:4, padding:"3px 7px", whiteSpace:"nowrap" }}
            >
              {__COMMIT_SHA__}
            </span>
          )}

          {/* Mobile: user avatar + logout */}
          {isMobile && (
            <>
              {currentUser.picture
                ? <img src={currentUser.picture} alt="" style={{ width:28, height:28, borderRadius:"50%", border:`1.5px solid #E87722` }} />
                : <div style={{ width:28, height:28, borderRadius:"50%", background:"#1B2B5B", border:"1.5px solid #E87722", display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:800, color:"#E87722", fontFamily:"'Montserrat',sans-serif" }}>{currentUser.initials}</div>
              }
              <button onClick={handleLogout} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", display:"flex", alignItems:"center", padding:0 }}>
                <LogOut size={16} />
              </button>
            </>
          )}
        </div>

        {/* CONTENT */}
        <div style={{ flex:1, overflow:"auto", padding:isMobile?"12px 10px":"20px", paddingBottom:isMobile?72:20 }}>
          {view==="dashboard" && <Dashboard C={C} nuggets={visibleNuggets} users={users} setView={setView} audit={audit} currentUser={currentUser} showToast={showToast} onOpenOpportunity={(id) => setPendingProposalCoachOppId(id)} />}
          {view==="map"       && <GeoMap C={C} dark={dark} />}
          {view==="nuggets"   && <NuggetBoard C={C} nuggets={visibleNuggets} setNuggets={setNuggets} users={users} moveNugget={moveNugget} currentUser={currentUser} canDo={canDo} showToast={showToast} addAudit={addAudit} companies={companies} setCompanies={setCompanies} nuggetTypes={nuggetTypes} setNuggetTypes={setNuggetTypes} dbReady={dbReady} saveNugget={saveNuggetLocal} deleteNuggetDb={deleteNuggetLocal} saveCompany={addCompanyLocal} saveNuggetType={addNuggetTypeLocal} />}
          {view==="lab"       && <StrategyLab C={C} chat={chat} chatInput={chatInput} setChatInput={setChatInput} sendChat={sendChat} chatLoading={chatLoading} setChat={setChat} currentUser={currentUser} modules={modules} setModules={setModules} moduleStatuses={moduleStatuses} nuggets={visibleNuggets} saveNugget={saveNuggetLocal} showToast={showToast} cyberThreats={cyberThreats} />}
          {view==="deploy"    && <DeployOps C={C} showToast={showToast} currentUser={currentUser} addAudit={addAudit} />}
          {view==="slack"     && <Messenger C={C} currentUser={currentUser} users={users}
            showToast={showToast} autoJoinChannelId={autoJoinChannelId} onAutoJoinHandled={() => setAutoJoinChannelId(null)} saveNugget={saveNuggetLocal} modules={modules} notifyUserViaPush={notifyUserViaPush} />}
          {view==="helpdesk"  && <HelpDesk C={C} currentUser={currentUser} users={users} showToast={showToast} saveNugget={saveNuggetLocal} modules={modules} />}
          {view==="crm"   && <CRMModule
            C={C} currentUser={currentUser} showToast={showToast} nuggets={nuggets} orgSettings={orgSettings} users={users}
            onOpenInProposalCoach={(oppId) => {
              setPendingProposalCoachOppId(oppId);
              setView("proposal_coach");
            }} />}
          {view==="cyber" && <CyberDashboard C={C} currentUser={currentUser} orgSettings={orgSettings} showToast={showToast} users={users} threats={cyberThreats} setThreats={setCyberThreats} />}
          {view==="proposal_coach" && <ProposalCoach
            C={C}
            currentUser={currentUser}
            users={users}
            showToast={showToast}
            initialOpportunityId={pendingProposalCoachOppId}
            onConsumedInitial={() => setPendingProposalCoachOppId(null)} />}
          {view==="library" && <LibraryModule
            C={C}
            currentUser={currentUser}
            showToast={showToast} />}
          {view==="deal_room" && <DealRoom C={C} currentUser={currentUser} users={users} companies={companies} companyConfigs={companyConfigs} showToast={showToast} />}
          {view==="notes" && <NotesModule C={C} currentUser={currentUser} showToast={showToast} saveNugget={saveNuggetLocal} setChatInput={setChatInput} setMainView={setView} users={users} />}
          {view==="admin"     && canDo("admin_users") && <AdminPanel C={C} users={users} setUsers={setUsers} permissions={permissions} setPermissions={setPermissions} audit={audit} currentUser={currentUser} showToast={showToast} addAudit={addAudit}
              notifSettings={notifSettings} setNotifSettings={setNotifSettings}
              saveNotifSettings={async (s) => {
                const orgId = currentUser?.orgId;
                if (!orgId) { console.error("saveNotifSettings: currentUser.orgId is null/undefined", currentUser); return false; }
                setNotifSettings(s);
                return await saveNotificationSettings(orgId, s);
              }}
              orgSettings={orgSettings}
              saveOrgSettings={async (s) => {
                const orgId = currentUser?.orgId;
                if (!orgId) return false;
                setOrgSettings(s);
                return await dbSaveOrgSettings(orgId, s);
              }}
              uploadOrgLogo={(file) => dbUploadOrgLogo(currentUser.orgId, file)}
              updateUserFeatures={async (userId, overrides) => {
                const ok = await saveUserFeatureOverrides(userId, overrides);
                if (ok) setUsers(p => p.map(u => u.id === userId ? { ...u, featureOverrides: overrides } : u));
              }}
              moduleStatuses={moduleStatuses} setModuleStatuses={setModuleStatuses}
              companies={companies} addCompany={addCompanyLocal} deleteCompany={deleteCompanyLocal}
              companyConfigs={companyConfigs} saveCompanyConfig={saveCompanyConfig}
            />}
          {view==="settings"  && <AppSettings C={C} dark={dark} setDark={setDark} currentUser={currentUser} pushEnabled={pushEnabled} pushLoading={pushLoading} onEnablePush={enablePushNotifications} onDisablePush={disablePushNotifications} />}
          {view==="devnotebook" && currentUser.role==='super_admin' && <DevNotebook C={C} currentUser={currentUser} users={users} showToast={showToast} nuggets={visibleNuggets} audit={audit} />}
          {view==="db_health" && canDo("admin_users") && <DBHealth C={C} currentUser={currentUser} users={users} showToast={showToast} />}
        </div>
      </div>

      {/* ── MOBILE BOTTOM TAB BAR ─────────────────────────────── */}
      {isMobile && (
        <div style={{ position:"fixed", bottom:0, left:0, right:0, zIndex:9000, height:60, background:C.bg2, borderTop:`1px solid ${C.border}`, display:"flex", alignItems:"stretch", paddingBottom:"env(safe-area-inset-bottom)" }}>
          {mobNavItems.map(({ id, label, Icon }) => {
            const isActive = id === "__more__" ? showMoreNav : (view === id && !showMoreNav);
            return (
              <button key={id} onClick={() => {
                if (id === "__more__") { setShowMoreNav(v => !v); }
                else { setView(id); setShowMoreNav(false); }
              }} style={{ flex:1, background:"none", border:"none", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:3, cursor:"pointer", color:isActive?"#E87722":"rgba(255,255,255,0.4)", transition:"color 0.2s" }}>
                <Icon size={20} />
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.05em" }}>{label.toUpperCase()}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── MOBILE MORE DRAWER ────────────────────────────────── */}
      {isMobile && showMoreNav && (
        <div style={{ position:"fixed", bottom:60, left:0, right:0, zIndex:8999, background:C.bg2, borderTop:`1px solid ${C.border}`, padding:"12px 10px 8px", display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:2 }}
          onClick={() => setShowMoreNav(false)}>
          {mobOverflowItems.map(({ id, label, Icon }) => (
            <button key={id} onClick={() => { setView(id); setShowMoreNav(false); }}
              style={{ background:view===id?"rgba(232,119,34,0.12)":"none", border:"none", borderRadius:8, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:4, cursor:"pointer", color:view===id?"#E87722":"rgba(255,255,255,0.55)", padding:"10px 4px", transition:"all 0.15s" }}>
              <Icon size={18} />
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.04em", textAlign:"center" }}>{label}</span>
            </button>
          ))}
        </div>
      )}

      {/* TOAST */}
      {toast && (
        <div style={{ position:"fixed", bottom:isMobile?72:20, right:20, zIndex:10001, background:"#1B2B5B", border:"1px solid rgba(255,255,255,0.1)", borderLeft:"3px solid #E87722", borderRadius:6, padding:"14px 18px", maxWidth:380, boxShadow:"0 12px 40px rgba(0,0,0,0.5)", animation:"slideIn 0.3s ease" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
            <div>
              <div style={{ color:"#fff", fontWeight:700, fontSize:12, marginBottom:5, fontFamily:"'Montserrat',sans-serif" }}>{toast.msg}</div>
              <div style={{ color:"rgba(255,255,255,0.4)", fontSize:10, fontStyle:"italic", fontFamily:"'Montserrat',sans-serif" }}>"{toast.q.text}" <span style={{ color:"#E87722" }}>— {toast.q.src}</span></div>
            </div>
            <button onClick={() => setToast(null)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer" }}><X size={13}/></button>
          </div>
        </div>
      )}

      {/* ── INCOMING CALL BANNER ── */}
      {incomingCall && (
        <IncomingCallBanner
          call={incomingCall}
          onAccept={() => {
            setView("slack");
            setAutoJoinChannelId(incomingCall.channelId);
            setIncomingCall(null);
          }}
          onDecline={() => setIncomingCall(null)}
        />
      )}

      <style>{GLOBAL_CSS}</style>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// INCOMING CALL BANNER
// ═══════════════════════════════════════════════════════════════════
function IncomingCallBanner({ call, onAccept, onDecline }) {
  const [countdown, setCountdown] = useState(30);

  useEffect(() => {
    if (countdown <= 0) { onDecline(); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, onDecline]);

  return (
    <div style={{
      position: 'fixed', top: 20, left: '50%', transform: 'translateX(-50%)',
      zIndex: 99999,
      background: 'rgba(13,27,42,0.97)',
      border: '1px solid rgba(232,119,34,0.4)',
      borderRadius: 14,
      padding: '14px 20px',
      display: 'flex', alignItems: 'center', gap: 16,
      boxShadow: '0 8px 40px rgba(0,0,0,0.7), 0 0 0 1px rgba(232,119,34,0.15)',
      backdropFilter: 'blur(20px)',
      fontFamily: "'Montserrat',sans-serif",
      minWidth: 320,
      animation: 'fadeUp 0.3s ease',
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: '50%',
        background: 'linear-gradient(135deg, rgba(232,119,34,0.25), rgba(27,43,91,0.5))',
        border: '1.5px solid rgba(232,119,34,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 20, flexShrink: 0,
        animation: 'lp-pulse 1.5s ease-in-out infinite',
      }}>
        🚀
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: '#E87722', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 2 }}>
          Incoming Launchpad
        </div>
        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <strong>{call.fromName}</strong> is calling{call.channelName ? ` · ${call.channelName}` : ''}
        </div>
      </div>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', fontWeight: 700, minWidth: 20, textAlign: 'center' }}>
        {countdown}s
      </div>
      <button onClick={onAccept} style={{
        padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
        background: 'linear-gradient(135deg, #22C55E, #16A34A)',
        border: 'none', color: '#fff',
        fontSize: 10, fontWeight: 800, letterSpacing: '0.1em',
        fontFamily: "'Montserrat',sans-serif", flexShrink: 0,
      }}>JOIN</button>
      <button onClick={onDecline} style={{
        padding: '7px 14px', borderRadius: 8, cursor: 'pointer',
        background: 'rgba(239,68,68,0.15)',
        border: '1px solid rgba(239,68,68,0.3)', color: '#EF4444',
        fontSize: 10, fontWeight: 800, letterSpacing: '0.1em',
        fontFamily: "'Montserrat',sans-serif", flexShrink: 0,
      }}>DECLINE</button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DASHBOARD
// ═══════════════════════════════════════════════════════════════════
function Dashboard({ C, nuggets, users, setView, audit, currentUser, showToast, onOpenOpportunity }) {
  const active   = nuggets.filter(n => n.col !== "done").length;
  const critical = nuggets.filter(n => n.priority === "critical" && n.col !== "done").length;
  const online   = users.filter(u => u.status === "online").length;

  const stats = [
    { label:"Visible Nuggets", value:nuggets.length, sub:`${active} active`,        color:"#E87722" },
    { label:"Critical",        value:critical,        sub:"Need attention now",      color:"#EF4444" },
    { label:"Crew Online",     value:`${online}/${users.length}`, sub:"Standing by",color:"#22C55E" },
    { label:"Your Role",       value:(ROLE_META[currentUser.role] || ROLE_META.viewer).label, sub:"Google SSO", color:(ROLE_META[currentUser.role] || ROLE_META.viewer).color },
  ];

  return (
    <div style={{ animation:"fadeUp 0.5s ease" }}>
      <SH C={C} title="Mission Control" sub={`${currentUser.name} · ${(ROLE_META[currentUser.role] || ROLE_META.viewer).label} · Google SSO`} />

      {/* KPI CARDS */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:20 }}>
        {stats.map(s => (
          <div key={s.label} style={{ ...Card(C), borderTopColor:s.color }}>
            <Eyebrow style={{ color:s.color }}>{s.label}</Eyebrow>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:28, color:"#fff", lineHeight:1 }}>{s.value}</div>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:5 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* My Queue — action items waiting on the current user.  Placed
          above the fold so pending approvals, mentions, etc. get seen
          on every visit.  v1 sources: proposal-section signoffs. */}
      <div style={{ marginBottom: 14 }}>
        <MyQueuePanel
          currentUser={currentUser}
          setView={setView}
          onOpenOpportunity={onOpenOpportunity}
          showToast={showToast}
        />
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"1fr 320px", gap:14 }}>
        {/* Mini kanban */}
        <div style={Card(C)}>
          <Eyebrow>Nugget Status</Eyebrow>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:8, marginBottom:14 }}>
            {COLS.map(col => {
              const items = nuggets.filter(n => n.col === col.id);
              return (
                <div key={col.id} style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:6, padding:"10px 12px" }}>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.4)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:8 }}>{col.icon} {col.label}</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:24, color:"#fff", marginBottom:4 }}>{items.length}</div>
                  {items.slice(0,2).map(n => <div key={n.id} style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.3)", padding:"2px 0", borderTop:"1px solid rgba(255,255,255,0.06)" }}>{n.id}</div>)}
                </div>
              );
            })}
          </div>
          <button onClick={() => setView("nuggets")} style={{ ...BtnPrimary, width:"100%", justifyContent:"center" }}>
            Open Nugget Board <ChevronRight size={13}/>
          </button>
        </div>

        {/* Audit feed */}
        <div style={Card(C)}>
          <Eyebrow>Audit Log</Eyebrow>
          {audit.slice(0,8).map((a,i) => {
            const typeColor = { deploy:"#E87722", nugget:"#60A5FA", quality:"#22C55E", admin:"#E87722", auth:"#60A5FA", system:"rgba(255,255,255,0.4)" }[a.type] || "rgba(255,255,255,0.4)";
            return (
              <div key={i} style={{ display:"flex", gap:10, padding:"7px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ width:7, height:7, borderRadius:"50%", background:typeColor, flexShrink:0, marginTop:5 }} />
                <div style={{ flex:1 }}>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"#fff", lineHeight:1.4 }}>{a.action}</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:typeColor, fontWeight:600, letterSpacing:"0.1em", textTransform:"uppercase", marginTop:2 }}>{a.type} · {a.ts}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// NUGGET BOARD
// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// GANTT VIEW
// ═══════════════════════════════════════════════════════════════════
function GanttView({ C, nuggets, users }) {
  const WINDOW_DAYS = 28;
  const today = new Date(); today.setHours(0,0,0,0);
  const windowStart = new Date(today); windowStart.setDate(today.getDate() - 7);
  const windowEnd   = new Date(today); windowEnd.setDate(today.getDate() + 21);
  const totalMs = windowEnd - windowStart;

  // Day tick labels (every 7 days)
  const weekTicks = [];
  for (let i = 0; i <= 4; i++) {
    const d = new Date(windowStart); d.setDate(windowStart.getDate() + i * 7);
    weekTicks.push({ label: d.toLocaleDateString([],{month:'short',day:'numeric'}), pct: (i * 7 / WINDOW_DAYS) * 100 });
  }

  // Today line pct
  const todayPct = ((today - windowStart) / totalMs) * 100;

  // Per-assignee rows
  const assigneeIds = [...new Set(nuggets.filter(n=>n.col!=='done').map(n=>n.assigneeId).filter(Boolean))];
  const rows = assigneeIds.map(uid => {
    const user = users.find(u=>u.id===uid) || { name:'Unknown', initials:'?', accent:'#666' };
    const bars = nuggets
      .filter(n => n.assigneeId===uid && n.col!=='done')
      .map(n => {
        const rawStart = n.createdAt ? new Date(n.createdAt).getTime() : Date.now();
        const durMs = n.estimatedHours ? (n.estimatedHours/8)*86400000 : 3*86400000;
        const rawEnd = n.requestedCompletionDate
          ? new Date(n.requestedCompletionDate).getTime()
          : rawStart + durMs;
        const clampStart = Math.max(rawStart, windowStart.getTime());
        const clampEnd   = Math.min(rawEnd,   windowEnd.getTime());
        if (clampStart >= clampEnd) return null;
        const leftPct  = ((clampStart - windowStart.getTime()) / totalMs) * 100;
        const widthPct = Math.max(0.5, ((clampEnd - clampStart) / totalMs) * 100);
        const p = PRIORITY[n.priority] || PRIORITY.medium;
        return { nugget:n, leftPct, widthPct, color:p.color };
      }).filter(Boolean);
    return { user, bars };
  });

  const empty = rows.every(r=>r.bars.length===0);

  return (
    <div style={{ ...Card(C) }}>
      <Eyebrow>Gantt Chart — 1 week prior · today · 3 weeks ahead</Eyebrow>

      {empty ? (
        <div style={{ padding:'40px 0', textAlign:'center', color:'rgba(255,255,255,0.25)', fontFamily:"'Montserrat',sans-serif", fontSize:12, lineHeight:1.8 }}>
          No scheduled items. Set <strong style={{color:'rgba(255,255,255,0.5)'}}>Target Completion Date</strong> or <strong style={{color:'rgba(255,255,255,0.5)'}}>Est. Hours</strong> on any nugget to see it here.
        </div>
      ) : (
        <>
          {/* Header: week ticks */}
          <div style={{ display:'flex', marginLeft:150, position:'relative', height:22, marginBottom:4 }}>
            {weekTicks.map((t,i) => (
              <div key={i} style={{ position:'absolute', left:`${t.pct}%`, transform:'translateX(-50%)', fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.35)', letterSpacing:'0.04em', whiteSpace:'nowrap' }}>{t.label}</div>
            ))}
          </div>

          {/* Rows */}
          <div style={{ position:'relative' }}>
            {/* Grid lines */}
            <div style={{ position:'absolute', top:0, left:150, right:0, bottom:0, display:'flex', pointerEvents:'none', zIndex:0 }}>
              {weekTicks.map((_,i) => <div key={i} style={{ position:'absolute', left:`${(_ .pct)}%`, top:0, bottom:0, width:1, background:'rgba(255,255,255,0.05)' }} />)}
              {/* Today marker */}
              <div style={{ position:'absolute', left:`${todayPct}%`, top:0, bottom:0, width:2, background:'rgba(232,119,34,0.55)', zIndex:2 }}/>
            </div>

            {rows.map(({ user, bars }) => (
              <div key={user.id} style={{ display:'flex', alignItems:'center', borderBottom:'1px solid rgba(255,255,255,0.04)', minHeight:44 }}>
                {/* User label */}
                <div style={{ width:150, flexShrink:0, display:'flex', alignItems:'center', gap:7, paddingRight:10, zIndex:1 }}>
                  <div style={{ width:24, height:24, borderRadius:'50%', background:user.accent||'#E87722', display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:800, color:'#fff', flexShrink:0 }}>{user.initials}</div>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.75)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.name.split(' ')[0]}</span>
                </div>
                {/* Bars */}
                <div style={{ flex:1, position:'relative', height:36 }}>
                  {bars.map(({ nugget:n, leftPct, widthPct, color }) => (
                    <div key={n.id} title={`${n.title}${n.estimatedHours?` · ${n.estimatedHours}h`:''}${n.requestedCompletionDate?` · due ${new Date(n.requestedCompletionDate).toLocaleDateString([],{month:'short',day:'numeric'})}`:''}`}
                      style={{ position:'absolute', left:`${leftPct}%`, width:`${widthPct}%`, minWidth:4, height:24, top:6, borderRadius:4, background:`${color}CC`, border:`1px solid ${color}`, display:'flex', alignItems:'center', padding:'0 5px', overflow:'hidden', cursor:'default', transition:'opacity 0.15s', zIndex:1 }}
                      onMouseEnter={e=>e.currentTarget.style.opacity='0.75'}
                      onMouseLeave={e=>e.currentTarget.style.opacity='1'}>
                      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:8, fontWeight:800, color:'#fff', overflow:'hidden', whiteSpace:'nowrap', textOverflow:'ellipsis', letterSpacing:'0.05em' }}>{n.id}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Legend */}
          <div style={{ display:'flex', gap:14, marginTop:14, flexWrap:'wrap', alignItems:'center' }}>
            {Object.entries(PRIORITY).map(([k,p]) => (
              <div key={k} style={{ display:'flex', alignItems:'center', gap:4 }}>
                <div style={{ width:12, height:12, borderRadius:2, background:p.color }}/>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.45)', textTransform:'uppercase', letterSpacing:'0.08em' }}>{p.label}</span>
              </div>
            ))}
            <div style={{ display:'flex', alignItems:'center', gap:4 }}>
              <div style={{ width:2, height:12, background:'rgba(232,119,34,0.7)' }}/>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.4)' }}>Today</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// RESOURCES VIEW — Availability management
// ═══════════════════════════════════════════════════════════════════
const WEEKDAY_LABELS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
const RES_KEY = uid => `nuggets_resources_${uid}`;
const RES_DEFAULTS = { workStart:'08:00', workEnd:'17:00', blockedDates:[], recurringBlocks:[] };

function ResourcesView({ C, users, currentUser, showToast }) {
  const [selUid, setSelUid]         = useState(currentUser.id);
  const [configs, setConfigs]       = useState(() => {
    const r = {};
    users.forEach(u => {
      try { r[u.id] = JSON.parse(localStorage.getItem(RES_KEY(u.id)) || 'null') || {...RES_DEFAULTS}; }
      catch { r[u.id] = {...RES_DEFAULTS}; }
    });
    return r;
  });
  const [blockDate,   setBlockDate]   = useState('');
  const [blockNote,   setBlockNote]   = useState('');
  const [recurDays,   setRecurDays]   = useState([]);
  const [recurStart,  setRecurStart]  = useState('08:00');
  const [recurEnd,    setRecurEnd]    = useState('09:00');
  const [recurNote,   setRecurNote]   = useState('');

  const canEdit = selUid === currentUser.id ||
    currentUser.role === 'super_admin' || currentUser.role === 'admin';
  const cfg  = configs[selUid] || {...RES_DEFAULTS};
  const selUser = users.find(u=>u.id===selUid);

  const persist = (uid, data) => {
    setConfigs(prev => ({ ...prev, [uid]: data }));
    try { localStorage.setItem(RES_KEY(uid), JSON.stringify(data)); } catch(_) {}
    showToast('✓ Availability saved');
  };

  const updField = (field, val) => persist(selUid, { ...cfg, [field]: val });

  const addBlocked = () => {
    if (!blockDate) return;
    persist(selUid, { ...cfg, blockedDates: [...(cfg.blockedDates||[]).filter(b=>b.date!==blockDate), { date:blockDate, note:blockNote }] });
    setBlockDate(''); setBlockNote('');
  };
  const removeBlocked = date => persist(selUid, { ...cfg, blockedDates:(cfg.blockedDates||[]).filter(b=>b.date!==date) });

  const addRecurring = () => {
    if (!recurDays.length) { showToast('Select at least one day'); return; }
    const block = { id:crypto.randomUUID(), days:recurDays, startTime:recurStart, endTime:recurEnd, note:recurNote };
    persist(selUid, { ...cfg, recurringBlocks:[...(cfg.recurringBlocks||[]), block] });
    setRecurDays([]); setRecurStart('08:00'); setRecurEnd('09:00'); setRecurNote('');
  };
  const removeRecurring = id => persist(selUid, { ...cfg, recurringBlocks:(cfg.recurringBlocks||[]).filter(b=>b.id!==id) });

  // Build 28-day calendar
  const today = new Date(); today.setHours(0,0,0,0);
  const calDays = Array.from({length:28}, (_,i) => {
    const d = new Date(today); d.setDate(today.getDate()+i);
    const dateStr = d.toISOString().slice(0,10);
    const isWknd = d.getDay()===0||d.getDay()===6;
    const blocked = (cfg.blockedDates||[]).find(b=>b.date===dateStr);
    const recurHits = (cfg.recurringBlocks||[]).filter(b=>b.days.includes(d.getDay()));
    return { d, dateStr, isWknd, blocked, recurHits, isToday: dateStr===today.toISOString().slice(0,10) };
  });

  return (
    <div style={{ ...Card(C) }}>
      <Eyebrow>Resource Availability</Eyebrow>

      {/* User selector */}
      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:18 }}>
        {users.map(u => (
          <button key={u.id} onClick={()=>setSelUid(u.id)}
            style={{ display:'flex', alignItems:'center', gap:6, padding:'5px 12px', borderRadius:20, border:`1px solid ${selUid===u.id?'#E87722':'rgba(255,255,255,0.12)'}`, background:selUid===u.id?'rgba(232,119,34,0.12)':'transparent', color:selUid===u.id?'#E87722':'rgba(255,255,255,0.5)', fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:600, cursor:'pointer', transition:'all 0.15s' }}>
            <div style={{ width:20, height:20, borderRadius:'50%', background:u.accent||'#E87722', display:'flex', alignItems:'center', justifyContent:'center', fontSize:7, fontWeight:800, color:'#fff' }}>{u.initials}</div>
            {u.name.split(' ')[0]}
          </button>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:20 }}>
        {/* ── Left: settings ────────────────────────────── */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

          {/* Work hours */}
          <div>
            <Eyebrow>Work Hours — {selUser?.name?.split(' ')[0]}</Eyebrow>
            <div style={{ display:'flex', alignItems:'center', gap:8 }}>
              <input type="time" value={cfg.workStart} onChange={e=>canEdit&&updField('workStart',e.target.value)} disabled={!canEdit} style={{ ...InputStyle(C), flex:1, padding:'5px 8px', fontSize:11 }}/>
              <span style={{ color:'rgba(255,255,255,0.3)', fontSize:12 }}>→</span>
              <input type="time" value={cfg.workEnd}   onChange={e=>canEdit&&updField('workEnd',e.target.value)}   disabled={!canEdit} style={{ ...InputStyle(C), flex:1, padding:'5px 8px', fontSize:11 }}/>
            </div>
          </div>

          {canEdit && <>
            {/* Block off date */}
            <div>
              <Eyebrow>Block Off Date</Eyebrow>
              <div style={{ display:'flex', gap:6, marginBottom:6 }}>
                <input type="date" value={blockDate} onChange={e=>setBlockDate(e.target.value)} style={{ ...InputStyle(C), flex:1, padding:'5px 8px', fontSize:11 }}/>
                <input value={blockNote} onChange={e=>setBlockNote(e.target.value)} placeholder="Note (optional)" style={{ ...InputStyle(C), flex:1, padding:'5px 8px', fontSize:11 }}/>
                <button onClick={addBlocked} style={{ ...BtnPrimary, padding:'5px 10px', flexShrink:0 }} title="Add"><Plus size={12}/></button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {(cfg.blockedDates||[]).sort((a,b)=>a.date.localeCompare(b.date)).map(b=>(
                  <div key={b.date} style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.2)', borderRadius:5, padding:'5px 9px' }}>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(239,68,68,0.85)', flex:1 }}>🚫 {b.date}{b.note?` — ${b.note}`:''}</span>
                    <button onClick={()=>removeBlocked(b.date)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.25)', cursor:'pointer', padding:0, display:'flex' }}><X size={10}/></button>
                  </div>
                ))}
              </div>
            </div>

            {/* Recurring block */}
            <div>
              <Eyebrow>Recurring Time Block</Eyebrow>
              <div style={{ display:'flex', gap:4, marginBottom:7, flexWrap:'wrap' }}>
                {WEEKDAY_LABELS.map((d,i)=>(
                  <button key={i} onClick={()=>setRecurDays(p=>p.includes(i)?p.filter(x=>x!==i):[...p,i])}
                    style={{ padding:'3px 8px', borderRadius:4, border:`1px solid ${recurDays.includes(i)?'#E87722':'rgba(255,255,255,0.15)'}`, background:recurDays.includes(i)?'rgba(232,119,34,0.14)':'transparent', color:recurDays.includes(i)?'#E87722':'rgba(255,255,255,0.4)', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, cursor:'pointer', transition:'all 0.1s' }}>{d}</button>
                ))}
              </div>
              <div style={{ display:'flex', gap:6, marginBottom:6 }}>
                <input type="time" value={recurStart} onChange={e=>setRecurStart(e.target.value)} style={{ ...InputStyle(C), flex:1, padding:'5px 8px', fontSize:11 }}/>
                <span style={{ color:'rgba(255,255,255,0.3)', fontSize:12, alignSelf:'center' }}>–</span>
                <input type="time" value={recurEnd}   onChange={e=>setRecurEnd(e.target.value)}   style={{ ...InputStyle(C), flex:1, padding:'5px 8px', fontSize:11 }}/>
              </div>
              <div style={{ display:'flex', gap:6, marginBottom:8 }}>
                <input value={recurNote} onChange={e=>setRecurNote(e.target.value)} placeholder="e.g. Morning standup" style={{ ...InputStyle(C), flex:1, padding:'5px 8px', fontSize:11 }}/>
                <button onClick={addRecurring} style={{ ...BtnPrimary, padding:'5px 10px', flexShrink:0 }}><Plus size={12}/> Add</button>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                {(cfg.recurringBlocks||[]).map(b=>(
                  <div key={b.id} style={{ display:'flex', alignItems:'center', gap:6, background:'rgba(232,119,34,0.06)', border:'1px solid rgba(232,119,34,0.18)', borderRadius:5, padding:'5px 9px' }}>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(232,119,34,0.85)', flex:1 }}>
                      🔁 {b.days.map(d=>WEEKDAY_LABELS[d]).join(', ')} · {b.startTime}–{b.endTime}{b.note?` · ${b.note}`:''}
                    </span>
                    <button onClick={()=>removeRecurring(b.id)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.25)', cursor:'pointer', padding:0, display:'flex' }}><X size={10}/></button>
                  </div>
                ))}
              </div>
            </div>
          </>}

          {!canEdit && (
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.3)', fontStyle:'italic', padding:'10px 0' }}>
              You can only edit your own availability. Ask an admin to adjust others.
            </div>
          )}
        </div>

        {/* ── Right: 4-week preview calendar ────────────── */}
        <div>
          <Eyebrow>Availability Preview — Next 4 Weeks</Eyebrow>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3, marginBottom:10 }}>
            {WEEKDAY_LABELS.map(d=>(
              <div key={d} style={{ fontFamily:"'Montserrat',sans-serif", fontSize:8, fontWeight:800, color:'rgba(255,255,255,0.3)', textAlign:'center', padding:'2px 0', letterSpacing:'0.05em' }}>{d.slice(0,2)}</div>
            ))}
            {/* Pad start to correct weekday */}
            {Array.from({length: calDays[0].d.getDay()}, (_,i) => <div key={`pad-${i}`}/>)}
            {calDays.map(({ d, dateStr, isWknd, blocked, recurHits, isToday }) => {
              const bg = blocked ? 'rgba(239,68,68,0.22)' : isWknd ? 'rgba(255,255,255,0.03)' : recurHits.length ? 'rgba(232,119,34,0.14)' : 'rgba(34,197,94,0.1)';
              const border = isToday ? '1px solid #E87722' : blocked ? '1px solid rgba(239,68,68,0.3)' : isWknd ? '1px solid rgba(255,255,255,0.05)' : recurHits.length ? '1px solid rgba(232,119,34,0.25)' : '1px solid rgba(34,197,94,0.2)';
              const dotColor = blocked ? '#EF4444' : isWknd ? 'rgba(255,255,255,0.15)' : recurHits.length ? '#E87722' : '#22C55E';
              const tip = blocked ? `Blocked: ${blocked.note||dateStr}` : recurHits.length ? recurHits.map(b=>`${b.startTime}-${b.endTime}${b.note?` ${b.note}`:''}`).join(', ') : isWknd ? 'Weekend' : 'Available';
              return (
                <div key={dateStr} title={tip} style={{ background:bg, border, borderRadius:4, padding:'4px 2px', textAlign:'center', cursor:'default' }}>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:isToday?800:400, color:isToday?'#E87722':isWknd?'rgba(255,255,255,0.2)':'rgba(255,255,255,0.65)' }}>{d.getDate()}</div>
                  <div style={{ width:5, height:5, borderRadius:'50%', background:dotColor, margin:'1px auto 0' }}/>
                </div>
              );
            })}
          </div>
          {/* Legend */}
          <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
            {[['#22C55E','Available'],['#E87722','Partial block'],['#EF4444','Blocked / Off'],['rgba(255,255,255,0.2)','Weekend']].map(([c,l])=>(
              <div key={l} style={{ display:'flex', alignItems:'center', gap:4 }}>
                <div style={{ width:8, height:8, borderRadius:'50%', background:c }}/>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.4)' }}>{l}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// NUGGET BOARD
// ═══════════════════════════════════════════════════════════════════
function NuggetBoard({ C, nuggets, setNuggets, users, moveNugget, currentUser, canDo, showToast, addAudit, companies, setCompanies, nuggetTypes, setNuggetTypes, saveNugget, deleteNuggetDb, saveCompany, saveNuggetType }) {
  const [newTitle,  setNewTitle]  = useState("");
  const [filter,    setFilter]    = useState("all");
  const [search,    setSearch]    = useState("");
  const [detailId,  setDetailId]  = useState(null);
  const [boardTab,  setBoardTab]  = useState("kanban");

  const filtered = nuggets
    .filter(n => filter === "all" || n.priority === filter)
    .filter(n => !search || n.title.toLowerCase().includes(search.toLowerCase()) || n.id.toLowerCase().includes(search.toLowerCase()));

  const addNugget = async () => {
    if (!newTitle.trim() || !canDo("nugget_create")) return;
    const id = `GEO-0${50+nuggets.length}`;
    const fresh = { id, title:newTitle.trim(), priority:"medium", col:"backlog", ownerId:currentUser.id, assigneeId:currentUser.id, tags:["new"], age:"now", visibility:"team", sharedWith:[], desc:"", revenueValue:"medium", company:"", nuggetType:"Feature" };
    setNuggets(p => [...p, fresh]);
    addAudit(`Created ${id}`, "nugget");
    setNewTitle("");
    showToast(`✨ ${id} created! "To infinity and beyond!"`);
    await saveNugget(fresh);
  };

  const detailNugget = nuggets.find(n => n.id === detailId);

  return (
    <div style={{ animation:"fadeUp 0.5s ease" }}>
      <SH C={C} title="Nugget Board" sub="Kanban with RBAC · visibility controls · SMS & Email notifications" />

      {/* Tab bar */}
      <div style={{ display:"flex", gap:4, marginBottom:18 }}>
        {[{id:"kanban",label:"⬜ Kanban"},{id:"gantt",label:"📊 Gantt"},{id:"resources",label:"👥 Resources"}].map(t => (
          <button key={t.id} onClick={() => setBoardTab(t.id)}
            style={{ ...BtnOutline(C), padding:"7px 16px", fontSize:11,
              ...(boardTab===t.id ? {background:"rgba(232,119,34,0.15)",borderColor:"rgba(232,119,34,0.6)",color:"#E87722",fontWeight:700} : {}) }}>
            {t.label}
          </button>
        ))}
      </div>

      {boardTab === "kanban" && (<>
        {/* Controls */}
        <div style={{ display:"flex", gap:8, marginBottom:16, alignItems:"center", flexWrap:"wrap" }}>
          {canDo("nugget_create") && (
            <>
              <input value={newTitle} onChange={e=>setNewTitle(e.target.value)} onKeyDown={e=>e.key==="Enter"&&addNugget()} placeholder="New nugget title... press Enter to add" style={{ ...InputStyle(C), flex:1, minWidth:200 }} />
              <button onClick={addNugget} style={BtnPrimary}><Plus size={13}/> Add</button>
            </>
          )}
          <div style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:3, padding:"6px 12px" }}>
            <Search size={12} color="rgba(255,255,255,0.4)"/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Search..." style={{ background:"none", border:"none", color:"#fff", fontSize:12, width:110, fontFamily:"'Montserrat',sans-serif" }}/>
          </div>
          {["all","critical","high","medium","low"].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ ...BtnOutline(C), ...(filter===f ? {background:"rgba(232,119,34,0.12)",borderColor:"rgba(232,119,34,0.5)",color:"#E87722"} : {}), padding:"6px 12px" }}>
              {f}
            </button>
          ))}
        </div>

        {/* Board */}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
          {COLS.map(col => {
            const cards = filtered.filter(n => n.col === col.id);
            return (
              <div key={col.id} style={{ background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderTop:"2px solid rgba(232,119,34,0.3)", borderRadius:8, padding:12, minHeight:300 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.4)", letterSpacing:"0.2em", textTransform:"uppercase" }}>{col.icon} {col.label}</div>
                  <div style={{ ...Badge("#E87722","rgba(232,119,34,0.12)","rgba(232,119,34,0.3)") }}>{cards.length}</div>
                </div>
                {cards.map(n => (
                  <NuggetCard key={n.id} C={C} nugget={n} users={users} moveNugget={moveNugget} currentUser={currentUser} canDo={canDo} onDetail={() => setDetailId(n.id)} showToast={showToast} addAudit={addAudit} setNuggets={setNuggets} />
                ))}
              </div>
            );
          })}
        </div>
      </>)}

      {boardTab === "gantt"     && <GanttView     C={C} nuggets={nuggets} users={users} />}
      {boardTab === "resources" && <ResourcesView C={C} users={users} currentUser={currentUser} showToast={showToast} />}

      {detailNugget && <NuggetDetail C={C} nugget={detailNugget} users={users} currentUser={currentUser} canDo={canDo} setNuggets={setNuggets} onClose={() => setDetailId(null)} showToast={showToast} addAudit={addAudit} companies={companies} setCompanies={setCompanies} nuggetTypes={nuggetTypes} setNuggetTypes={setNuggetTypes} saveNugget={saveNugget} deleteNuggetDb={deleteNuggetDb} saveCompany={saveCompany} saveNuggetType={saveNuggetType} />}
    </div>
  );
}

function NuggetCard({ C, nugget, users, moveNugget, canDo, onDetail, showToast, addAudit }) {
  const assignee = users.find(u => u.id === nugget.assigneeId);
  const p   = PRIORITY[nugget.priority];
  const vm  = VISIBILITY_META[nugget.visibility];
  const rv  = REVENUE_VALUES.find(r => r.id === nugget.revenueValue) || REVENUE_VALUES[1];
  const order = ["backlog","in-progress","review","done"];
  const ci  = order.indexOf(nugget.col);
  const canEdit = canDo("nugget_edit_own", nugget);

  return (
    <div style={{ background:"rgba(255,255,255,0.04)", border:`1px solid rgba(255,255,255,0.08)`, borderRadius:6, padding:"10px 12px", marginBottom:8, borderLeft:`3px solid ${p.color}`, transition:"all 0.2s ease" }}>
      {/* ID + priority + visibility */}
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
        <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.3)", letterSpacing:"0.1em" }}>{nugget.id}</span>
        <div style={{ display:"flex", gap:4 }}>
          <span style={{ ...Badge(p.color, p.bg, p.border) }}>{p.label}</span>
          <span style={{ fontSize:11 }} title={vm.desc}>{vm.icon}</span>
        </div>
      </div>

      {/* Title */}
      <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:600, color:"#fff", lineHeight:1.4, marginBottom:8, cursor:"pointer" }} onClick={onDetail}>{nugget.title}</div>

      {/* Revenue + Type + date/hours row */}
      <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:6 }}>
        {nugget.revenueValue && (
          <span style={{ ...Badge(rv.color, rv.bg, rv.border), fontSize:9 }}>💰 {rv.label}</span>
        )}
        {nugget.nuggetType && (
          <span style={{ ...Badge("rgba(255,255,255,0.6)","rgba(255,255,255,0.06)","rgba(255,255,255,0.15)"), fontSize:9 }}>{nugget.nuggetType}</span>
        )}
        {nugget.requestedCompletionDate && (
          <span style={{ ...Badge('#34D399','rgba(52,211,153,0.1)','rgba(52,211,153,0.3)'), fontSize:9 }}>
            📅 {new Date(nugget.requestedCompletionDate).toLocaleDateString([],{month:'short',day:'numeric'})}
          </span>
        )}
        {nugget.estimatedHours && (
          <span style={{ ...Badge('#60A5FA','rgba(96,165,250,0.1)','rgba(96,165,250,0.3)'), fontSize:9 }}>
            ⏱ {nugget.estimatedHours}h
          </span>
        )}
      </div>

      {/* Company */}
      {nugget.company && (
        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.4)", marginBottom:6, display:"flex", alignItems:"center", gap:4 }}>
          <span style={{ color:"#E87722" }}>🏢</span> {nugget.company}
        </div>
      )}

      {/* Tags */}
      <div style={{ display:"flex", gap:4, flexWrap:"wrap", marginBottom:8 }}>
        {nugget.tags.map(t => <span key={t} style={{ ...Badge("#E87722","rgba(232,119,34,0.1)","rgba(232,119,34,0.25)"), fontSize:9 }}>{t}</span>)}
      </div>

      {/* Assignee */}
      {assignee && (
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
          <div style={{ width:18, height:18, borderRadius:"50%", background:"#1B2B5B", border:`1px solid #E87722`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:7, fontWeight:800, color:"#E87722", fontFamily:"'Montserrat',sans-serif" }}>{assignee.initials}</div>
          <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.4)" }}>{assignee.name.split(" ")[0]}</span>
        </div>
      )}

      {/* Action row */}
      <div style={{ display:"flex", gap:4, paddingTop:8, borderTop:"1px solid rgba(255,255,255,0.06)" }}>
        {canEdit && ci > 0 && <button onClick={() => moveNugget(nugget.id,"back")} style={BtnSm(C)}><ChevronLeft size={9}/></button>}
        {canEdit && ci < 3 && <button onClick={() => moveNugget(nugget.id,"fwd")}  style={BtnSm(C)}><ChevronRight size={9}/></button>}
        <button onClick={onDetail} style={BtnSm(C)}><Eye size={9}/></button>
        <div style={{ flex:1 }}/>
        <button onClick={() => { addAudit(`SMS re ${nugget.id}`,"system"); showToast(`📱 SMS → ${assignee?.name||"assignee"}`); }} style={{ ...BtnSm(C), color:"#22C55E" }}><Smartphone size={9}/></button>
        <button onClick={() => { addAudit(`Email re ${nugget.id}`,"system"); showToast(`📧 Email → ${assignee?.name||"assignee"}`); }} style={{ ...BtnSm(C), color:"#E87722" }}><Mail size={9}/></button>
      </div>
    </div>
  );
}

function NuggetDetail({ C, nugget, users, canDo, setNuggets, onClose, showToast, addAudit, companies, setCompanies, nuggetTypes, setNuggetTypes, saveNugget, deleteNuggetDb, saveCompany, saveNuggetType }) {
  const [editing, setEditing]         = useState(false);
  const [draft,   setDraft]           = useState({ ...nugget });
  const [companyModal,  setCompanyModal]  = useState(null);
  const [typeModal,     setTypeModal]     = useState(null);
  const canEdit   = canDo("nugget_edit_own", nugget);
  const canDelete = canDo("nugget_delete");
  const canAssign = canDo("nugget_assign");
  const canManage = canDo("admin_users");

  const save = async () => {
    setNuggets(p => p.map(n => n.id === nugget.id ? draft : n));
    addAudit(`Updated ${nugget.id}`, "nugget");
    showToast("Saved. 'Nobody puts Data Quality in a corner.'");
    setEditing(false);
    await saveNugget(draft);
  };

  const del = async () => {
    setNuggets(p => p.filter(n => n.id !== nugget.id));
    addAudit(`Deleted ${nugget.id}`, "nugget");
    showToast("Deleted. 'Hasta la vista, baby.'");
    await deleteNuggetDb(nugget.id);
    onClose();
  };

  const addCompany = async (name) => {
    if (!companies.includes(name)) setCompanies(p => [...p, name].sort());
    setDraft(p => ({ ...p, company:name }));
    setCompanyModal(null);
    addAudit(`Added company: ${name}`, "admin");
    showToast(`🏢 "${name}" added to company list.`);
    await saveCompany(name);
  };

  const addType = async (name) => {
    if (!nuggetTypes.includes(name)) setNuggetTypes(p => [...p, name].sort());
    setDraft(p => ({ ...p, nuggetType:name }));
    setTypeModal(null);
    addAudit(`Added nugget type: ${name}`, "admin");
    showToast(`🏷️ "${name}" added to type list.`);
    await saveNuggetType(name);
  };

  const rv = REVENUE_VALUES.find(r => r.id === nugget.revenueValue) || REVENUE_VALUES[1];

  return (
    <>
      <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.75)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center" }} onClick={onClose}>
        <div style={{ ...Card(C), width:"100%", maxWidth:600, maxHeight:"88vh", overflow:"auto", padding:24, borderRadius:8 }} onClick={e => e.stopPropagation()}>

          {/* Header */}
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:18 }}>
            <div style={{ flex:1, marginRight:16 }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:6 }}>{nugget.id}</div>
              {editing
                ? <input value={draft.title} onChange={e => setDraft(p=>({...p,title:e.target.value}))} style={{ ...InputStyle(C), fontSize:16, fontWeight:700 }}/>
                : <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:16, fontWeight:700, color:"#fff" }}>{nugget.title}</div>}
            </div>
            <button onClick={onClose} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer" }}><X size={16}/></button>
          </div>

          {/* Description */}
          <div style={{ marginBottom:16 }}>
            <Eyebrow>Description</Eyebrow>
            {editing
              ? <textarea value={draft.desc} onChange={e => setDraft(p=>({...p,desc:e.target.value}))} rows={3} style={{ ...InputStyle(C), resize:"none" }}/>
              : <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, color:"rgba(255,255,255,0.5)", lineHeight:1.6 }}>{nugget.desc || "No description yet."}</div>}
          </div>

          {/* Row 1: Priority + Assignee */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
            <div>
              <Eyebrow>Priority</Eyebrow>
              {editing
                ? <select value={draft.priority} onChange={e => setDraft(p=>({...p,priority:e.target.value}))} style={InputStyle(C)}>
                    {Object.keys(PRIORITY).map(k => <option key={k} value={k}>{PRIORITY[k].label}</option>)}
                  </select>
                : (() => {
                    // Guard against nugget rows whose priority isn't in the map
                    // (e.g. legacy data, or a value added via SQL outside the picker).
                    const pri = PRIORITY[nugget.priority] || PRIORITY.medium;
                    return <span style={{ ...Badge(pri.color, pri.bg, pri.border) }}>{pri.label}</span>;
                  })()}
            </div>
            <div>
              <Eyebrow>Assignee</Eyebrow>
              {editing && canAssign
                ? <select value={draft.assigneeId} onChange={e => setDraft(p=>({...p,assigneeId:e.target.value}))} style={InputStyle(C)}>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                : <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, color:"rgba(255,255,255,0.7)", fontWeight:600 }}>{users.find(u=>u.id===nugget.assigneeId)?.name||"Unassigned"}</div>}
            </div>
          </div>

          {/* Row 2: Revenue Value + Nugget Type */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
            {/* Revenue Value */}
            <div>
              <Eyebrow>Revenue Value</Eyebrow>
              {editing
                ? (
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {REVENUE_VALUES.map(r => (
                      <button key={r.id} onClick={() => setDraft(p=>({...p,revenueValue:r.id}))}
                        style={{ ...BtnOutline(C), padding:"5px 10px", fontSize:10, ...(draft.revenueValue===r.id ? {background:r.bg, borderColor:r.border, color:r.color} : {}) }}>
                        {r.label}
                      </button>
                    ))}
                  </div>
                )
                : nugget.revenueValue
                  ? <span style={{ ...Badge(rv.color, rv.bg, rv.border) }}>💰 {rv.label}</span>
                  : <span style={{ fontSize:12, color:"rgba(255,255,255,0.3)" }}>Not set</span>}
            </div>

            {/* Nugget Type */}
            <div>
              <Eyebrow>Nugget Type</Eyebrow>
              {editing
                ? <Combobox
                    value={draft.nuggetType || ""}
                    onChange={v => setDraft(p=>({...p,nuggetType:v}))}
                    options={nuggetTypes}
                    placeholder="Select or create type..."
                    canAdd={canManage}
                    onRequestAdd={prefill => setTypeModal(prefill)}
                    C={C}
                  />
                : nugget.nuggetType
                  ? <span style={{ ...Badge("rgba(255,255,255,0.7)","rgba(255,255,255,0.06)","rgba(255,255,255,0.15)") }}>{nugget.nuggetType}</span>
                  : <span style={{ fontSize:12, color:"rgba(255,255,255,0.3)" }}>Not set</span>}
            </div>
          </div>

          {/* Row 3: Completion Date + Estimated Hours */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14, marginBottom:16 }}>
            <div>
              <Eyebrow>Target Completion</Eyebrow>
              {editing
                ? <input type="date" value={draft.requestedCompletionDate?.slice(0,10)||''} onChange={e=>setDraft(p=>({...p,requestedCompletionDate:e.target.value}))} style={InputStyle(C)}/>
                : nugget.requestedCompletionDate
                  ? <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, color:'#34D399', fontWeight:600 }}>📅 {new Date(nugget.requestedCompletionDate).toLocaleDateString([],{month:'short',day:'numeric',year:'numeric'})}</div>
                  : <span style={{ fontSize:12, color:"rgba(255,255,255,0.3)" }}>Not set</span>}
            </div>
            <div>
              <Eyebrow>Est. Hours</Eyebrow>
              {editing
                ? <input type="number" min="0" step="0.5" value={draft.estimatedHours||''} onChange={e=>setDraft(p=>({...p,estimatedHours:e.target.value?parseFloat(e.target.value):''}))} placeholder="e.g. 8" style={InputStyle(C)}/>
                : nugget.estimatedHours
                  ? <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, color:'#60A5FA', fontWeight:600 }}>⏱ {nugget.estimatedHours}h</div>
                  : <span style={{ fontSize:12, color:"rgba(255,255,255,0.3)" }}>Not set</span>}
            </div>
          </div>

          {/* Module / Platform assignment (conditional on nugget type) */}
          {(draft.nuggetType === 'Feature' || draft.nuggetType === 'Module') && (
            <div style={{ marginBottom:16 }}>
              <Eyebrow>{draft.nuggetType === 'Feature' ? 'Assign to Module' : 'Assign to Platform'}</Eyebrow>
              {editing
                ? <select value={draft.nuggetType==='Feature' ? (draft.moduleLink||'') : (draft.platformLink||'')}
                    onChange={e => setDraft(p => draft.nuggetType==='Feature' ? {...p,moduleLink:e.target.value} : {...p,platformLink:e.target.value})}
                    style={InputStyle(C)}>
                    <option value="">— None —</option>
                    {INIT_MODULES.filter(m => draft.nuggetType==='Feature' ? m.type==='module' : m.type==='platform').map(m =>
                      <option key={m.id} value={m.id}>{m.name}</option>
                    )}
                  </select>
                : (() => {
                    const linked = draft.nuggetType==='Feature' ? draft.moduleLink : draft.platformLink;
                    const meta = INIT_MODULES.find(m => m.id === linked);
                    return linked
                      ? <span style={{ ...Badge('rgba(167,139,250,0.9)','rgba(167,139,250,0.1)','rgba(167,139,250,0.3)') }}>🔗 {meta?.name || linked}</span>
                      : <span style={{ fontSize:12, color:"rgba(255,255,255,0.3)" }}>Not assigned</span>;
                  })()}
            </div>
          )}

          {/* Row 4: Company (full width) */}
          <div style={{ marginBottom:16 }}>
            <Eyebrow>Company</Eyebrow>
            {editing
              ? <Combobox
                  value={draft.company || ""}
                  onChange={v => setDraft(p=>({...p,company:v}))}
                  options={companies}
                  placeholder="Select or create company..."
                  canAdd={canManage}
                  onRequestAdd={prefill => setCompanyModal(prefill)}
                  C={C}
                />
              : nugget.company
                ? <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, color:"rgba(255,255,255,0.7)", fontWeight:600, display:"flex", alignItems:"center", gap:6 }}>🏢 {nugget.company}</div>
                : <span style={{ fontSize:12, color:"rgba(255,255,255,0.3)" }}>Not set</span>}
          </div>

          {/* Visibility */}
          <div style={{ marginBottom:18 }}>
            <Eyebrow>Visibility</Eyebrow>
            <div style={{ display:"flex", gap:8 }}>
              {Object.entries(VISIBILITY_META).map(([k,vm]) => (
                <button key={k} onClick={() => editing && setDraft(p=>({...p,visibility:k}))}
                  style={{ flex:1, background:draft.visibility===k?`${vm.color}18`:"rgba(255,255,255,0.04)", border:`1px solid ${draft.visibility===k?vm.color+"66":"rgba(255,255,255,0.08)"}`, borderRadius:6, padding:"10px 6px", cursor:editing?"pointer":"default", textAlign:"center", transition:"all 0.2s ease" }}>
                  <div style={{ fontSize:16 }}>{vm.icon}</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:draft.visibility===k?vm.color:"rgba(255,255,255,0.4)", marginTop:4, textTransform:"uppercase", letterSpacing:"0.1em" }}>{vm.label}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Footer actions */}
          <div style={{ display:"flex", gap:8, paddingTop:14, borderTop:"1px solid rgba(255,255,255,0.08)" }}>
            {canEdit && !editing && <button onClick={() => setEditing(true)} style={{ ...BtnOutline(C) }}><Edit size={12}/> Edit</button>}
            {editing && <button onClick={save} style={BtnPrimary}><CheckCircle size={12}/> Save</button>}
            {editing && <button onClick={() => {setDraft({...nugget});setEditing(false);}} style={BtnOutline(C)}>Cancel</button>}
            <div style={{ flex:1 }}/>
            {canDelete && <button onClick={del} style={{ ...BtnOutline(C), borderColor:"rgba(239,68,68,0.4)", color:"#EF4444" }}><Trash2 size={12}/> Delete</button>}
          </div>
        </div>
      </div>

      {/* Add Company modal */}
      {companyModal !== null && (
        <AddItemModal title="Add New Company" prefill={companyModal} onConfirm={addCompany} onCancel={() => setCompanyModal(null)} C={C} />
      )}
      {/* Add Type modal */}
      {typeModal !== null && (
        <AddItemModal title="Add New Nugget Type" prefill={typeModal} onConfirm={addType} onCancel={() => setTypeModal(null)} C={C} />
      )}
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ADMIN PANEL
// ═══════════════════════════════════════════════════════════════════
function AdminPanel({ C, users, setUsers, permissions, setPermissions, audit, currentUser, showToast, addAudit, notifSettings, saveNotifSettings, orgSettings, saveOrgSettings, updateUserFeatures, moduleStatuses, setModuleStatuses, companies, addCompany, deleteCompany, companyConfigs, saveCompanyConfig }) {
  const [tab, setTab]         = useState("users");
  const isSuperAdmin          = currentUser.role === "super_admin";
  const isAdmin               = isSuperAdmin || currentUser.role === "admin";

  const changeRole = async (uid, newRole) => {
    if (!isSuperAdmin) { showToast("Only Super Admin can change roles."); return; }
    const u = users.find(x => x.id === uid);
    const ok = await saveUserRole(uid, newRole);
    if (!ok) { showToast(`❌ Role update failed — check RLS on users table`); return; }
    setUsers(p => p.map(u => u.id === uid ? { ...u, role: newRole, accent: ROLE_META[newRole]?.color || '#E87722' } : u));
    addAudit(`Role changed: ${u?.name} → ${ROLE_META[newRole].label}`, "admin");
    showToast(`✓ ${u?.name} is now ${ROLE_META[newRole].label}`);
  };

  const togglePerm = (perm, role) => {
    if (!isSuperAdmin) { showToast("Only Super Admin can edit permissions."); return; }
    setPermissions(p => ({ ...p, [perm]:{ ...p[perm], [role]:!p[perm][role] } }));
    addAudit(`Permission ${perm}·${role} toggled`, "admin");
  };

  const tabs = [
    ["users",    "Users & Roles",       true],
    ["notifs",   "Notifications",       isAdmin],
    ["perms",    "Permission Matrix",   isSuperAdmin],
    ["platform", "Platform Config",     isSuperAdmin],
    ["companies","Company Manager",     isSuperAdmin],
    ["tokens",   "Token Usage",         isSuperAdmin],
    ["import",   "Import Data",         isAdmin],
    ["audit",    "Audit Log",           isAdmin],
  ].filter(t => t[2]);

  return (
    <div style={{ animation:"fadeUp 0.5s ease" }}>
      <SH C={C} title="Admin Panel" sub="Users · Notifications · RBAC · Audit trail" />

      {/* Tab bar */}
      <div style={{ display:"flex", borderBottom:"1px solid rgba(255,255,255,0.08)", marginBottom:20, overflowX:"auto", flexWrap:"wrap", gap:"0 2px" }}>
        {tabs.map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:600, fontSize:12, letterSpacing:"0.08em", textTransform:"uppercase", background:"none", border:"none", borderBottom:tab===id?"2px solid #E87722":"2px solid transparent", color:tab===id?"#fff":"rgba(255,255,255,0.4)", padding:"10px 20px", cursor:"pointer", transition:"all 0.2s ease", marginBottom:"-1px" }}>
            {label}
          </button>
        ))}
      </div>

      {tab==="users"    && <AdminUsers  C={C} users={users} setUsers={setUsers} currentUser={currentUser} isSuperAdmin={isSuperAdmin} isAdmin={isAdmin} changeRole={changeRole} orgSettings={orgSettings} updateUserFeatures={updateUserFeatures} showToast={showToast} addAudit={addAudit} companies={companies} />}
      {tab==="notifs"   && <AdminNotifications C={C} notifSettings={notifSettings} saveNotifSettings={saveNotifSettings} showToast={showToast} currentUser={currentUser} />}
      {tab==="perms"    && isSuperAdmin && <AdminPerms C={C} permissions={permissions} togglePerm={togglePerm} />}
      {tab==="platform"  && isSuperAdmin && <PlatformConfig C={C} moduleStatuses={moduleStatuses} setModuleStatuses={setModuleStatuses} showToast={showToast} />}
      {tab==="companies" && isSuperAdmin && <AdminCompanies C={C} companies={companies} addCompany={addCompany} deleteCompany={deleteCompany} companyConfigs={companyConfigs} saveCompanyConfig={saveCompanyConfig} showToast={showToast} currentUser={currentUser} orgSettings={orgSettings} />}
      {tab==="tokens"    && isSuperAdmin && <TokenUsageDashboard C={C} currentUser={currentUser} orgSettings={orgSettings} saveOrgSettings={saveOrgSettings} showToast={showToast} />}
      {tab==="import"    && isAdmin && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <SlackImportPanel />
        </div>
      )}
      {tab==="audit"     && <AdminAudit  C={C} audit={audit} />}
    </div>
  );
}

// ── ADMIN SUB-COMPONENTS ─────────────────────────────────────────

const USER_FEATURES = [
  ["geo_map",         "Geo Map"],
  ["proto_lab",       "Proto Lab"],
  ["deploy_ops",      "Deploy Ops"],
  ["messenger",       "Messenger"],
  ["cyber_security",  "Cyber Security"],
];

function AdminUsers({ C, users, setUsers, currentUser, isSuperAdmin, isAdmin, changeRole, orgSettings, updateUserFeatures, showToast, addAudit, companies }) {
  const [expanded,      setExpanded]      = useState(null);
  const [expandTab,     setExpandTab]     = useState("contact"); // "contact" | "features"
  const [editContact,   setEditContact]   = useState({});        // { [userId]: { name, email, phone } }
  const [savingContact, setSavingContact] = useState(null);
  const [showAddForm,   setShowAddForm]   = useState(false);
  const [addForm,       setAddForm]       = useState({ name:"", email:"", phone:"", role:"viewer", company:"", initial_password:"" });
  const [addSaving,     setAddSaving]     = useState(false);
  const orgF = orgSettings?.features || {};

  // Augment the live users list with inactive ones + session stats.
  const [allUsers,  setAllUsers]  = useState([]);    // active + inactive
  const [stats,     setStats]     = useState({});    // { [user_id]: { last_login, seconds_30d, session_count } }
  const [showInactive, setShowInactive] = useState(false);
  const [pwUser,    setPwUser]    = useState(null);  // { id, name } for the Set Password modal
  const [pwValue,   setPwValue]   = useState('');
  const [pwBusy,    setPwBusy]    = useState(false);
  const [resetBusy, setResetBusy] = useState(null);  // user id while sending reset
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [kcHealth,  setKcHealth]  = useState(null);  // last Keycloak self-check result
  const [kcBusy,    setKcBusy]    = useState(false);
  const [s3Health,  setS3Health]  = useState(null);  // last S3 storage self-check result
  const [s3Busy,    setS3Busy]    = useState(false);

  const onTestKeycloak = async () => {
    setKcBusy(true);
    const r = await adminKeycloakHealth();
    setKcBusy(false);
    if (r.error && r.configured === undefined) {
      // Transport/auth error before the self-check could run.
      setKcHealth({ ok:false, error:r.error, hint:'Could not reach the diagnostic endpoint — check you are signed in as an admin.' });
    } else {
      setKcHealth(r);
    }
  };

  const onTestStorage = async () => {
    setS3Busy(true);
    const r = await adminStorageHealth();
    setS3Busy(false);
    if (r.error && r.config === undefined) {
      setS3Health({ ok:false, error:r.error, hint:'Could not reach the diagnostic endpoint — check you are signed in as an admin.', buckets:{} });
    } else {
      setS3Health(r);
    }
  };

  const refreshUsers = useCallback(async () => {
    if (!currentUser?.orgId) return;
    const all = await loadAllOrgUsers(currentUser.orgId);
    setAllUsers(all);
    setStats(await loadUserSessionStats(currentUser.orgId, 30));
  }, [currentUser?.orgId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refreshUsers(); }, [currentUser?.orgId]);

  // ── Action handlers (new endpoints) ─────────────────────────────
  const onSetActive = async (u, active) => {
    if (u.id === currentUser.id) { showToast("You can't deactivate yourself."); return; }
    const action = active ? 'reactivate' : 'deactivate';
    if (!window.confirm(`${active ? 'Reactivate' : 'Deactivate'} ${u.name}?`)) return;
    const r = await adminSetActive(u.id, active);
    if (r.error) { showToast(`❌ ${action} failed: ${r.error}`); return; }
    showToast(`✓ ${u.name} ${active ? 'reactivated' : 'deactivated'}`);
    addAudit?.(`User ${action}d: ${u.name}`, 'admin');
    refreshUsers();
  };

  const onResetPassword = async (u) => {
    setResetBusy(u.id);
    const r = await adminResetPassword(u.id);
    setResetBusy(null);
    if (r.error) { showToast(`❌ ${r.error}`); return; }
    if (r.recovery_link) {
      try { await navigator.clipboard.writeText(r.recovery_link); } catch(_) {}
      showToast(`✓ Recovery link copied to clipboard for ${u.email}`);
    } else {
      showToast(`✓ Recovery flow started for ${u.email}`);
    }
    addAudit?.(`Password-reset link generated for ${u.email}`, 'admin');
  };

  const onResendInvite = async (u) => {
    setResetBusy(u.id);
    const r = await adminResendInvite(u.id);
    setResetBusy(null);
    if (r.error) { showToast(`❌ ${r.error}`); return; }
    showToast(`✓ Invite email resent to ${u.email}`);
    addAudit?.(`Invite resent to ${u.email}`, 'admin');
  };

  const submitSetPassword = async () => {
    if (!pwUser || pwValue.length < 10) { showToast('Password must be at least 10 characters'); return; }
    setPwBusy(true);
    const r = await adminSetPassword(pwUser.id, pwValue);
    setPwBusy(false);
    if (r.error) { showToast(`❌ ${r.error}`); return; }
    showToast(`✓ Password set for ${pwUser.name}`);
    addAudit?.(`Password set directly for ${pwUser.email || pwUser.name}`, 'admin');
    setPwUser(null); setPwValue('');
    refreshUsers();
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const r = await adminDeleteUser(deleteTarget.id);
    if (r.error) { showToast(`❌ ${r.error}`); setDeleteTarget(null); return; }
    showToast(`✓ ${deleteTarget.name} deleted`);
    addAudit?.(`User deleted: ${deleteTarget.email || deleteTarget.name}`, 'admin');
    setDeleteTarget(null);
    refreshUsers();
  };

  // Visible roster — augment from allUsers (admin source of truth) once it loads.
  const rosterSource = allUsers.length ? allUsers : users;
  const roster = rosterSource.filter(u => showInactive || u.isActive !== false);

  const fmtAgo = (iso) => {
    if (!iso) return 'never';
    const d = Date.now() - new Date(iso).getTime();
    const m = Math.floor(d/60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m/60); if (h < 24) return `${h}h ago`;
    return `${Math.floor(h/24)}d ago`;
  };
  const fmtDuration = (sec) => {
    if (!sec) return '—';
    const h = Math.floor(sec/3600), m = Math.floor((sec%3600)/60);
    if (h >= 1) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const toggleExpand = (uid) => {
    if (expanded === uid) { setExpanded(null); return; }
    const u = users.find(x => x.id === uid);
    setExpanded(uid);
    setExpandTab("contact");
    setEditContact(prev => ({ ...prev, [uid]: { name: u.name, email: u.email, phone: u.phone || "" } }));
  };

  const handleSaveContact = async (uid) => {
    const draft = editContact[uid];
    if (!draft) return;
    setSavingContact(uid);
    const ok = await dbSaveUserContact(uid, draft);
    setSavingContact(null);
    if (!ok) { showToast("❌ Failed to save contact info"); return; }
    setUsers(p => p.map(u => u.id === uid ? {
      ...u,
      name:     draft.name || u.name,
      email:    draft.email || u.email,
      phone:    draft.phone,
      initials: (draft.name || draft.email || u.email).split(" ").map(n=>n[0]).join("").toUpperCase().slice(0,2),
    } : u));
    showToast("✓ Contact info saved");
    addAudit?.(`Contact updated: ${draft.name}`, "admin");
  };

  const handleAddUser = async () => {
    if (!addForm.email.trim() || !addForm.name.trim()) { showToast("Name and email are required"); return; }
    if (addForm.initial_password && addForm.initial_password.length < 10) {
      showToast("Password must be at least 10 characters (or leave blank to send invite)");
      return;
    }
    setAddSaving(true);
    const r = await adminCreateUser({
      email:    addForm.email,
      name:     addForm.name,
      phone:    addForm.phone || null,
      role:     addForm.role,
      company:  addForm.company || null,
      initial_password: addForm.initial_password || undefined,
    });
    setAddSaving(false);
    if (r.error) { showToast(`❌ ${r.error}`); return; }
    if (r.invited) {
      showToast(`✓ ${addForm.name} created — Keycloak invite email sent to ${addForm.email}`);
    } else if (addForm.initial_password) {
      showToast(`✓ ${addForm.name} created — they can sign in now with the temp password`);
    } else if (r.invite_error) {
      // KC row exists but the mail couldn't leave the realm.  Most
      // often SMTP isn't configured yet.
      showToast(`⚠ ${addForm.name} created but invite email failed. Realm SMTP not configured?`);
    } else {
      showToast(`✓ ${addForm.name} created`);
    }
    setAddForm({ name:"", email:"", phone:"", role:"viewer", company:"", initial_password:"" });
    setShowAddForm(false);
    addAudit?.(`User created: ${r.user?.email || addForm.email}`, "admin");
    refreshUsers();
  };

  const FLabel = { fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.4)", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:4 };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>

      {/* Header row */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:8 }}>
        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.35)", letterSpacing:"0.12em", textTransform:"uppercase" }}>
          Crew Roster — {roster.length} member{roster.length !== 1 ? "s" : ""}
          {allUsers.length > roster.length && (
            <span style={{ color:"rgba(255,255,255,0.25)", marginLeft:8 }}>· {allUsers.length - roster.length} inactive hidden</span>
          )}
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <label style={{ display:"flex", alignItems:"center", gap:6, fontSize:10, color:"rgba(255,255,255,0.5)", fontFamily:"'Montserrat',sans-serif", cursor:"pointer", textTransform:"uppercase", letterSpacing:"0.08em" }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          {isAdmin && (
            <button onClick={onTestKeycloak} disabled={kcBusy}
              title="Verify the Keycloak admin connection before onboarding"
              style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:5, padding:"6px 12px", color:"rgba(255,255,255,0.55)", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:kcBusy?"default":"pointer", letterSpacing:"0.08em", opacity:kcBusy?0.6:1 }}>
              {kcBusy ? "Testing…" : "Test Keycloak"}
            </button>
          )}
          {isAdmin && (
            <button onClick={onTestStorage} disabled={s3Busy}
              title="Verify the S3 storage configuration and whether existing library files are present"
              style={{ background:"rgba(255,255,255,0.04)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:5, padding:"6px 12px", color:"rgba(255,255,255,0.55)", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:s3Busy?"default":"pointer", letterSpacing:"0.08em", opacity:s3Busy?0.6:1 }}>
              {s3Busy ? "Testing…" : "Test Storage"}
            </button>
          )}
          {isAdmin && (
            <button onClick={() => setShowAddForm(p => !p)}
              style={{ background:showAddForm?"rgba(232,119,34,0.2)":"rgba(255,255,255,0.06)", border:`1px solid ${showAddForm?"rgba(232,119,34,0.5)":"rgba(255,255,255,0.12)"}`, borderRadius:5, padding:"6px 14px", color:showAddForm?"#E87722":"rgba(255,255,255,0.7)", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer", letterSpacing:"0.08em" }}>
              {showAddForm ? "✕ Cancel" : "+ Add User"}
            </button>
          )}
        </div>
      </div>

      {/* Keycloak connection self-check result */}
      {kcHealth && (
        <div style={{ background: kcHealth.ok ? "rgba(52,199,89,0.08)" : "rgba(255,86,48,0.08)", border:`1px solid ${kcHealth.ok ? "rgba(52,199,89,0.35)" : "rgba(255,86,48,0.35)"}`, borderRadius:8, padding:"12px 16px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700, color: kcHealth.ok ? "#34C759" : "#FF5630", letterSpacing:"0.06em" }}>
              {kcHealth.ok ? "✓ Keycloak connected — onboarding is ready" : "✕ Keycloak not connected"}
            </div>
            <button onClick={() => setKcHealth(null)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:14, lineHeight:1 }}>✕</button>
          </div>
          <div style={{ display:"flex", gap:14, marginTop:8, flexWrap:"wrap" }}>
            {[["Env configured", kcHealth.configured],["Service-account token", kcHealth.tokenOk],["Can read users", kcHealth.usersReadOk]].map(([label, ok]) => (
              <span key={label} style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.6)" }}>
                <span style={{ color: ok ? "#34C759" : "rgba(255,255,255,0.3)" }}>{ok ? "●" : "○"}</span> {label}
              </span>
            ))}
          </div>
          {!kcHealth.ok && kcHealth.error && (
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.65)", marginTop:8, lineHeight:1.5 }}>
              <strong style={{ color:"rgba(255,255,255,0.85)" }}>Error:</strong> {kcHealth.error}
            </div>
          )}
          {!kcHealth.ok && kcHealth.hint && (
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(232,119,34,0.9)", marginTop:6, lineHeight:1.5 }}>
              → {kcHealth.hint}
            </div>
          )}
        </div>
      )}

      {/* S3 storage self-check result */}
      {s3Health && (
        <div style={{ background: s3Health.ok ? "rgba(52,199,89,0.08)" : "rgba(255,86,48,0.08)", border:`1px solid ${s3Health.ok ? "rgba(52,199,89,0.35)" : "rgba(255,86,48,0.35)"}`, borderRadius:8, padding:"12px 16px" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700, color: s3Health.ok ? "#34C759" : "#FF5630", letterSpacing:"0.06em" }}>
              {s3Health.ok ? "✓ S3 storage connected — uploads will work" : "✕ S3 storage not ready"}
            </div>
            <button onClick={() => setS3Health(null)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", fontSize:14, lineHeight:1 }}>✕</button>
          </div>
          {s3Health.config && (
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:6 }}>
              Region {s3Health.config.region} · Endpoint {s3Health.config.endpoint || "native AWS S3"} · Credentials: {s3Health.config.credentials}
            </div>
          )}
          <div style={{ display:"flex", gap:14, marginTop:8, flexWrap:"wrap" }}>
            {Object.entries(s3Health.buckets || {}).map(([name, b]) => (
              <span key={name} title={b.error || b.bucket || ""} style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.6)" }}>
                <span style={{ color: b.ok ? "#34C759" : "rgba(255,255,255,0.3)" }}>{b.ok ? "●" : "○"}</span> {name}{b.bucket ? ` → ${b.bucket}` : ""}
              </span>
            ))}
            {s3Health.library && (
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.6)" }}>
                <span style={{ color: s3Health.library.missing === 0 ? "#34C759" : "#E87722" }}>●</span> Library files present: {s3Health.library.present}/{s3Health.library.total}
              </span>
            )}
          </div>
          {s3Health.error && (
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.65)", marginTop:8, lineHeight:1.5 }}>
              <strong style={{ color:"rgba(255,255,255,0.85)" }}>Error:</strong> {s3Health.error}
            </div>
          )}
          {s3Health.hint && (
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(232,119,34,0.9)", marginTop:6, lineHeight:1.5 }}>
              → {s3Health.hint}
            </div>
          )}
          {s3Health.library && s3Health.library.missing > 0 && (
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.5)", marginTop:6, lineHeight:1.6 }}>
              Missing: {s3Health.library.sample.filter(s => !s.present).map(s => s.title).join(" · ")}
            </div>
          )}
        </div>
      )}

      {/* Add User form */}
      {showAddForm && isAdmin && (
        <div style={{ background:"rgba(232,119,34,0.07)", border:"1px solid rgba(232,119,34,0.25)", borderRadius:8, padding:"16px 18px" }}>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color:"#E87722", letterSpacing:"0.1em", textTransform:"uppercase", marginBottom:12 }}>Pre-Register New User</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"10px 16px", marginBottom:12 }}>
            <div>
              <div style={FLabel}>Full Name</div>
              <input value={addForm.name} onChange={e => setAddForm(p=>({...p,name:e.target.value}))} placeholder="Jane Smith"
                style={{ ...InputStyle(C), fontSize:12 }} />
            </div>
            <div>
              <div style={FLabel}>Email *</div>
              <input value={addForm.email} onChange={e => setAddForm(p=>({...p,email:e.target.value}))} placeholder="jane@company.com" type="email"
                style={{ ...InputStyle(C), fontSize:12 }} />
            </div>
            <div>
              <div style={FLabel}>Phone</div>
              <input value={addForm.phone} onChange={e => setAddForm(p=>({...p,phone:e.target.value}))} placeholder="+1 555 000 0000"
                style={{ ...InputStyle(C), fontSize:12 }} />
            </div>
            <div>
              <div style={FLabel}>Role</div>
              <select value={addForm.role} onChange={e => setAddForm(p=>({...p,role:e.target.value}))}
                style={{ ...InputStyle(C), fontSize:12 }}>
                {Object.entries(ROLE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <div style={FLabel}>Company (optional)</div>
              <select value={addForm.company} onChange={e => setAddForm(p=>({...p,company:e.target.value}))}
                style={{ ...InputStyle(C), fontSize:12 }}>
                <option value="">— none (internal staff) —</option>
                {(companies || []).map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <div style={FLabel}>Temp password (optional — leave blank to send invite)</div>
              <input value={addForm.initial_password} onChange={e => setAddForm(p=>({...p,initial_password:e.target.value}))}
                placeholder="min 10 chars · communicate out-of-band"
                style={{ ...InputStyle(C), fontSize:12 }} />
            </div>
          </div>
          <div style={{ display:"flex", gap:10, alignItems:"center" }}>
            <button onClick={handleAddUser} disabled={addSaving}
              style={{ background:"#E87722", border:"none", borderRadius:5, padding:"7px 18px", color:"#fff", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer", opacity:addSaving?0.6:1 }}>
              {addSaving ? "Creating…" : "Create User"}
            </button>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.4)", lineHeight:1.5 }}>
              {addForm.initial_password
                ? "Temp password set — user signs in immediately. Make sure you've shared it securely."
                : "Without a temp password, Keycloak emails an invite with set-password + verify-email actions."}
            </div>
          </div>
        </div>
      )}

      {/* User list */}
      <div style={Card(C)}>
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          {roster.map(u => {
            const rm        = ROLE_META[u.role] || ROLE_META.viewer;
            const isMe      = u.id === currentUser.id;
            const isOpen    = expanded === u.id;
            const overrides = u.featureOverrides || {};
            const contact   = editContact[u.id] || { name: u.name, email: u.email, phone: u.phone || "" };
            const st        = stats[u.id] || {};
            const inactive  = u.isActive === false;
            return (
              <div key={u.id} style={{ background:"rgba(255,255,255,0.04)", border:`1px solid ${isMe?"rgba(232,119,34,0.4)":"rgba(255,255,255,0.08)"}`, borderRadius:6, overflow:"hidden", transition:"all 0.2s ease", opacity: inactive ? 0.55 : 1 }}>
                <div style={{ padding:"12px 16px", display:"flex", alignItems:"center", gap:14 }}>
                  <div style={{ width:38, height:38, borderRadius:"50%", background:"#1B2B5B", border:`2px solid ${inactive ? '#6B7280' : '#E87722'}`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:11, fontWeight:800, color: inactive ? '#6B7280' : '#E87722', flexShrink:0, fontFamily:"'Montserrat',sans-serif" }}>{u.initials}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, fontWeight:700, color:"#fff", display:'flex', alignItems:'center', gap:6 }}>
                      <span>{u.name}</span>
                      {isMe && <span style={{ fontSize:9, color:"#E87722", fontWeight:600 }}>← YOU</span>}
                      {inactive && <span style={{ fontSize:9, color:"#FF6B6B", border:"1px solid rgba(255,107,107,0.4)", borderRadius:3, padding:"1px 6px", textTransform:"uppercase", letterSpacing:"0.06em" }}>Inactive</span>}
                    </div>
                    <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.4)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.email}{u.phone ? <span style={{ color:"rgba(255,255,255,0.25)", marginLeft:8 }}>{u.phone}</span> : null}</div>
                  </div>
                  {/* Session columns */}
                  <div style={{ textAlign:'right', flexShrink:0, fontFamily:"'Montserrat',sans-serif", minWidth: 110 }}>
                    <div style={{ fontSize:9, color:'rgba(255,255,255,0.35)', letterSpacing:'0.08em', textTransform:'uppercase' }}>Last login</div>
                    <div style={{ fontSize:11, color:'#fff' }}>{fmtAgo(st.last_login)}</div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0, fontFamily:"'Montserrat',sans-serif", minWidth: 80 }}>
                    <div style={{ fontSize:9, color:'rgba(255,255,255,0.35)', letterSpacing:'0.08em', textTransform:'uppercase' }}>Time (30d)</div>
                    <div style={{ fontSize:11, color:'#fff' }}>{fmtDuration(st.seconds_30d)}</div>
                  </div>
                  <select value={u.role} onChange={e => changeRole(u.id, e.target.value)} disabled={!isSuperAdmin}
                    style={{ background:"rgba(255,255,255,0.05)", border:`1px solid ${rm.color}44`, borderRadius:3, padding:"5px 10px", color:rm.color, fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:600, cursor:isSuperAdmin?"pointer":"default", flexShrink:0 }}>
                    {Object.entries(ROLE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
                  </select>
                  {isSuperAdmin && (
                    <button onClick={() => toggleExpand(u.id)}
                      style={{ background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:4, padding:"4px 10px", color:"rgba(255,255,255,0.5)", fontSize:10, fontFamily:"'Montserrat',sans-serif", fontWeight:600, cursor:"pointer", letterSpacing:"0.08em", textTransform:"uppercase", flexShrink:0 }}>
                      Edit {isOpen ? "▲" : "▼"}
                    </button>
                  )}
                </div>

                {isOpen && (
                  <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)", background:"rgba(0,0,0,0.15)" }}>
                    {/* Sub-tabs */}
                    <div style={{ display:"flex", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                      {[["contact","Contact Info"],["features","Features"]].map(([t,lbl]) => (
                        <button key={t} onClick={() => setExpandTab(t)}
                          style={{ background:"none", border:"none", borderBottom:`2px solid ${expandTab===t?"#E87722":"transparent"}`, padding:"8px 16px", color:expandTab===t?"#fff":"rgba(255,255,255,0.35)", fontSize:10, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer", letterSpacing:"0.08em", textTransform:"uppercase" }}>
                          {lbl}
                        </button>
                      ))}
                    </div>

                    {expandTab === "contact" && (
                      <div style={{ padding:"12px 16px 14px" }}>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:"10px 16px", marginBottom:12 }}>
                          <div>
                            <div style={FLabel}>Name</div>
                            <input value={contact.name} onChange={e => setEditContact(p=>({...p,[u.id]:{...contact,name:e.target.value}}))}
                              style={{ ...InputStyle(C), fontSize:12 }} />
                          </div>
                          <div>
                            <div style={FLabel}>Email</div>
                            <input value={contact.email} onChange={e => setEditContact(p=>({...p,[u.id]:{...contact,email:e.target.value}}))}
                              type="email" style={{ ...InputStyle(C), fontSize:12 }} />
                          </div>
                          <div>
                            <div style={FLabel}>Phone (SMS)</div>
                            <input value={contact.phone} onChange={e => setEditContact(p=>({...p,[u.id]:{...contact,phone:e.target.value}}))}
                              placeholder="+1 555 000 0000" style={{ ...InputStyle(C), fontSize:12 }} />
                          </div>
                        </div>
                        {/* Company assignment — drives feature gating + Deal Room access */}
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:"10px 16px", marginBottom:12, alignItems:"end" }}>
                          <div>
                            <div style={FLabel}>Company</div>
                            <select
                              value={u.company || ''}
                              onChange={async (e) => {
                                const value = e.target.value || null;
                                const ok = await setUserCompany(u.id, value);
                                if (!ok) { showToast('❌ Failed to save company'); return; }
                                setUsers(p => p.map(x => x.id === u.id ? { ...x, company: value } : x));
                                addAudit?.(`Assigned ${u.name} to ${value || 'no company'}`, 'admin');
                                showToast(`✓ ${u.name} → ${value || '— none —'}`);
                              }}
                              disabled={!isSuperAdmin}
                              style={{ ...InputStyle(C), fontSize:12 }}>
                              <option value="">— none (internal staff) —</option>
                              {(companies || []).map(c => <option key={c} value={c}>{c}</option>)}
                            </select>
                          </div>
                          <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', lineHeight:1.5, paddingBottom:6 }}>
                            Setting a company turns this user into a <strong>reviewer</strong>. Their sidebar collapses to whatever modules
                            that company is granted in <strong>Admin → Company Manager</strong>, and they gain access to deals where they
                            are the reviewer.
                          </div>
                        </div>
                        <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
                          <button onClick={() => handleSaveContact(u.id)} disabled={savingContact===u.id}
                            style={{ background:"#E87722", border:"none", borderRadius:5, padding:"6px 16px", color:"#fff", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer", opacity:savingContact===u.id?0.6:1 }}>
                            {savingContact===u.id ? "Saving…" : "Save Contact"}
                          </button>
                          <button onClick={() => { setPwUser(u); setPwValue(''); }}
                            style={{ background:"rgba(96,165,250,0.12)", border:"1px solid rgba(96,165,250,0.35)", borderRadius:5, padding:"6px 12px", color:"#60A5FA", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer" }}>
                            Set Password
                          </button>
                          <button onClick={() => onResetPassword(u)} disabled={resetBusy === u.id}
                            style={{ background:"transparent", border:"1px solid rgba(255,255,255,0.15)", borderRadius:5, padding:"6px 12px", color:"rgba(255,255,255,0.7)", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer" }}>
                            {resetBusy === u.id ? '…' : 'Email Reset Link'}
                          </button>
                          {/* Resend the Keycloak invite (UPDATE_PASSWORD + VERIFY_EMAIL).
                              Only meaningful for users who haven't finished onboarding
                              yet — the stats row is missing until they first log in. */}
                          {!st.last_login && !isMe && (
                            <button onClick={() => onResendInvite(u)} disabled={resetBusy === u.id}
                              style={{ background:"rgba(232,119,34,0.12)", border:"1px solid rgba(232,119,34,0.35)", borderRadius:5, padding:"6px 12px", color:"#E87722", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer" }}>
                              {resetBusy === u.id ? '…' : 'Resend Invite'}
                            </button>
                          )}
                          {!isMe && (
                            <>
                              <button onClick={() => onSetActive(u, !u.isActive)}
                                style={{ background: inactive ? "rgba(52,211,153,0.12)" : "rgba(255,184,48,0.12)", border:`1px solid ${inactive ? 'rgba(52,211,153,0.35)' : 'rgba(255,184,48,0.35)'}`, borderRadius:5, padding:"6px 12px", color: inactive ? '#34D399' : '#FFB830', fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer" }}>
                                {inactive ? 'Reactivate' : 'Deactivate'}
                              </button>
                              <button onClick={() => setDeleteTarget(u)}
                                style={{ background:"rgba(239,68,68,0.12)", border:"1px solid rgba(239,68,68,0.3)", borderRadius:5, padding:"6px 12px", color:"#EF4444", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:"pointer" }}>
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {expandTab === "features" && (
                      <div style={{ padding:"10px 16px 14px" }}>
                        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"6px 20px" }}>
                          {USER_FEATURES.map(([key, label]) => {
                            const orgAllows   = orgF[key] !== false;
                            const userEnabled = overrides[key] !== false;
                            return (
                              <div key={key} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"5px 0" }}>
                                <div>
                                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:600, color: orgAllows ? "#fff" : "rgba(255,255,255,0.3)" }}>{label}</div>
                                  {!orgAllows && <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,100,100,0.7)" }}>disabled org-wide</div>}
                                </div>
                                <Toggle val={orgAllows && userEnabled} disabled={!orgAllows} onChange={() => updateUserFeatures(u.id, { ...overrides, [key]: !userEnabled })} C={C} />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {!isSuperAdmin && (
          <div style={{ marginTop:14, background:"rgba(232,119,34,0.1)", borderLeft:"3px solid #E87722", borderRadius:4, padding:"10px 14px", fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"#E87722", fontWeight:600 }}>
            ⚠ Only Super Admin can edit users.
          </div>
        )}
      </div>

      {/* Set Password modal */}
      {pwUser && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:10200, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => !pwBusy && setPwUser(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#0d1b2a', border:`1px solid ${C.border}`, borderRadius:12, padding:22, maxWidth:440, width:'100%' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#fff', marginBottom:6, fontFamily:"'Montserrat',sans-serif" }}>Set password for {pwUser.name}</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginBottom:14 }}>Sets the password directly. Communicate it to the user out-of-band (chat, SMS) — don't email plain-text passwords.</div>
            <input type="password" autoFocus value={pwValue} onChange={e => setPwValue(e.target.value)}
              placeholder="At least 10 characters"
              style={{ ...InputStyle(C), fontSize:12, width:'100%', boxSizing:'border-box', marginBottom:6 }} />
            <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginBottom:14 }}>Min 10 chars. The user can change it later from their own session.</div>
            <div style={{ display:'flex', gap:8 }}>
              <div style={{ flex:1 }} />
              <button onClick={() => !pwBusy && (setPwUser(null), setPwValue(''))}
                style={{ background:'transparent', border:`1px solid ${C.border}`, color:'rgba(255,255,255,0.6)', borderRadius:6, padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Montserrat',sans-serif" }}>Cancel</button>
              <button onClick={submitSetPassword} disabled={pwBusy || pwValue.length < 10}
                style={{ background:'#60A5FA', border:'none', color:'#04231a', borderRadius:6, padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Montserrat',sans-serif", opacity: (pwBusy || pwValue.length < 10) ? 0.5 : 1 }}>
                {pwBusy ? 'Setting…' : 'Set password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete user modal */}
      {deleteTarget && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:10200, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setDeleteTarget(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#0d1b2a', border:`1px solid ${C.border}`, borderRadius:12, padding:22, maxWidth:440, width:'100%' }}>
            <div style={{ fontSize:15, fontWeight:700, color:'#fff', marginBottom:6, fontFamily:"'Montserrat',sans-serif" }}>Delete user?</div>
            <div style={{ fontSize:12, color:'rgba(255,255,255,0.55)', marginBottom:14, lineHeight:1.55 }}>
              This permanently removes <strong style={{ color:'#fff' }}>{deleteTarget.name}</strong> ({deleteTarget.email}) — both their auth identity and the application profile. Audit log entries that reference them stay but lose the back-link.
            </div>
            <div style={{ fontSize:11, color:'#FFB830', background:'rgba(255,184,48,0.08)', border:'1px solid rgba(255,184,48,0.3)', borderRadius:5, padding:'8px 10px', marginBottom:14 }}>
              If you just want to revoke access without losing history, use <strong>Deactivate</strong> instead.
            </div>
            <div style={{ display:'flex', gap:8 }}>
              <div style={{ flex:1 }} />
              <button onClick={() => setDeleteTarget(null)}
                style={{ background:'transparent', border:`1px solid ${C.border}`, color:'rgba(255,255,255,0.6)', borderRadius:6, padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Montserrat',sans-serif" }}>Cancel</button>
              <button onClick={confirmDelete}
                style={{ background:'#EF4444', border:'none', color:'#fff', borderRadius:6, padding:'8px 14px', fontSize:12, fontWeight:700, cursor:'pointer', fontFamily:"'Montserrat',sans-serif" }}>Delete permanently</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const ORG_FEATURES = [
  ["geo_map",       "🗺 Geo Map",       "Geographic visualization"],
  ["proto_lab",     "🧪 Proto Lab",     "AI prototype workspace"],
  ["deploy_ops",    "🚀 Deploy Ops",    "Deployment pipeline"],
  ["messenger",     "💬 Messenger",     "In-app team messaging"],
  ["help_desk",     "🎫 Help Desk",     "ARIA-powered ticket management"],
  ["notifications", "🔔 Notifications", "SMS & email alerts"],
];

function OrgSettings({ C, orgSettings, saveOrgSettings, uploadOrgLogo, showToast }) {
  const [draft,     setDraft]     = useState(orgSettings || { org_name:"40Geo Nuggets", features:{} });
  const [uploading, setUploading] = useState(false);
  const [saved,     setSaved]     = useState(false);
  const fileRef = useRef(null);

  const update        = (field, val) => setDraft(p => ({ ...p, [field]: val }));
  const updateFeature = (key, val)   => setDraft(p => ({ ...p, features: { ...p.features, [key]: val } }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const url = await uploadOrgLogo(file);
    if (url) update("logo_url", url);
    else showToast("❌ Logo upload failed — check console.");
    setUploading(false);
  };

  const handleSave = async () => {
    const ok = await saveOrgSettings(draft);
    if (!ok) { showToast("❌ Save failed — check console."); return; }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    showToast("Org settings saved.");
  };

  const LabelStyle = { fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.4)", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:5 };

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>

      {/* Org Profile */}
      <div style={Card(C)}>
        <Eyebrow>Org Profile</Eyebrow>

        <div style={{ marginBottom:16 }}>
          <div style={LabelStyle}>Org Name</div>
          <input value={draft.org_name || ""} onChange={e => update("org_name", e.target.value)}
            placeholder="40Geo Nuggets" style={{ ...InputStyle(C) }} />
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.25)", marginTop:4 }}>Appears in the top banner bar</div>
        </div>

        <div>
          <div style={LabelStyle}>Logo</div>
          {draft.logo_url && (
            <div style={{ marginBottom:10, padding:8, background:"rgba(255,255,255,0.04)", borderRadius:6, display:"inline-block" }}>
              <img src={draft.logo_url} alt="Logo preview" style={{ height:36, display:"block" }} />
            </div>
          )}
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              style={{ ...BtnOutline(C), padding:"6px 14px", fontSize:11, opacity:uploading?0.5:1 }}>
              {uploading ? "Uploading..." : draft.logo_url ? "Change Logo" : "Upload Logo"}
            </button>
            {draft.logo_url && (
              <button onClick={() => update("logo_url", null)}
                style={{ background:"none", border:"none", color:"#EF4444", cursor:"pointer", fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:600 }}>
                Remove
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoUpload} style={{ display:"none" }} />
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.25)", marginTop:6 }}>PNG, SVG, or JPEG · replaces the default icon in the top bar</div>
        </div>
      </div>

      {/* Feature Toggles */}
      <div style={Card(C)}>
        <Eyebrow>Org-Wide Feature Access</Eyebrow>
        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.35)", marginBottom:12 }}>
          Disabling a feature hides it from all users. Per-user overrides are in Users & Roles.
        </div>
        {ORG_FEATURES.map(([key, label, desc]) => (
          <div key={key} style={{ padding:"10px 0", borderBottom:"1px solid rgba(255,255,255,0.06)", display:"flex", alignItems:"center", gap:12 }}>
            <div style={{ flex:1 }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:600, color:"#fff" }}>{label}</div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.35)" }}>{desc}</div>
            </div>
            <Toggle val={draft.features?.[key] !== false} onChange={() => updateFeature(key, !(draft.features?.[key] !== false))} C={C} />
          </div>
        ))}
      </div>

      {/* Save */}
      <div style={{ gridColumn:"1 / -1", display:"flex", justifyContent:"flex-end" }}>
        <button onClick={handleSave} style={{ ...BtnPrimary, ...(saved ? { background:"#22C55E" } : {}) }}>
          <CheckCircle size={13}/> {saved ? "Saved!" : "Save Org Settings"}
        </button>
      </div>
    </div>
  );
}

function NotifField({ label, field, type="text", placeholder="", draft, update, C }) {
  const [show, setShow] = useState(false);
  const isPassword = type === "password";
  return (
    <div style={{ marginBottom:12 }}>
      <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:600, color:"rgba(255,255,255,0.4)", letterSpacing:"0.15em", textTransform:"uppercase", marginBottom:5 }}>{label}</div>
      <div style={{ position:"relative" }}>
        <input
          type={isPassword && !show ? "password" : "text"}
          value={draft[field] || ""}
          onChange={e => update(field, e.target.value)}
          placeholder={placeholder}
          style={{ ...InputStyle(C), paddingRight: isPassword ? 36 : undefined }}
        />
        {isPassword && (
          <button onClick={() => setShow(v => !v)}
            title={show ? "Hide" : "Show"}
            style={{ position:"absolute", right:8, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", color:"rgba(255,255,255,0.35)", cursor:"pointer", padding:2, fontSize:13, lineHeight:1 }}>
            {show ? "🙈" : "👁"}
          </button>
        )}
      </div>
    </div>
  );
}

function AdminNotifications({ C, notifSettings, saveNotifSettings, showToast, currentUser }) {
  const [draft,     setDraft]     = useState(notifSettings || {});
  const [testing,   setTesting]   = useState(null); // "sms" | "email" | null
  const [testPhone, setTestPhone] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [saved,     setSaved]     = useState(false);

  const update = (field, value) => setDraft(p => ({ ...p, [field]: value }));

  const handleSave = async () => {
    const ok = await saveNotifSettings(draft);
    if (!ok) {
      showToast("❌ Save failed — check console for details.");
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    showToast("Notification settings saved. 'Great Scott — it works!'");
  };

  const withTimeout = (promise, ms = 15000) =>
    Promise.race([promise, new Promise((_, rej) => setTimeout(() => rej(new Error("Request timed out")), ms))]);

  const handleTestSMS = async () => {
    if (!testPhone) { showToast("Enter a phone number to test"); return; }
    setTesting("sms");
    try {
      const result = await withTimeout(dbSendSMS({ to: testPhone, message: "✅ Test SMS from 40Geo Nuggets — notifications are working!", orgId: currentUser.orgId }));
      showToast(result.success ? "📱 Test SMS sent!" : `❌ SMS failed: ${result.error}`);
    } catch (err) {
      showToast(`❌ SMS failed: ${err.message}`);
    } finally {
      setTesting(null);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) { showToast("Enter an email to test"); return; }
    setTesting("email");
    try {
      const result = await withTimeout(dbSendEmail({ to: testEmail, subject: "✅ Test email from 40Geo Nuggets", html: "<p>Notifications are working! 🎉</p>", orgId: currentUser.orgId }));
      showToast(result.success ? "📧 Test email sent!" : `❌ Email failed: ${result.error}`);
    } catch (err) {
      showToast(`❌ Email failed: ${err.message}`);
    } finally {
      setTesting(null);
    }
  };


  const RULE_LABELS = {
    nugget_created:      "Nugget Created",
    nugget_moved_review: "Nugget Moved to Review",
    nugget_critical:     "Critical Priority Assigned",
    nugget_done:         "Nugget Marked Done",
    deploy_failed:       "Deploy Failed",
    deploy_success:      "Deploy Succeeded",
  };

  const rules = draft.rules || {};

  return (
    <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>

      {/* ── SMS SETTINGS ── */}
      <div style={Card(C)}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <Eyebrow style={{ margin:0 }}>📱 SMS via Twilio</Eyebrow>
          <Toggle val={!!draft.sms_enabled} onChange={() => update("sms_enabled", !draft.sms_enabled)} C={C} />
        </div>

        {draft.sms_enabled ? (
          <>
            <NotifField label="Account SID"  field="twilio_account_sid"  placeholder="ACxxxxxxxxxxxxxxxx"   draft={draft} update={update} C={C} />
            <NotifField label="Auth Token"   field="twilio_auth_token"   type="password" placeholder="••••••••••••" draft={draft} update={update} C={C} />
            <NotifField label="From Number"  field="twilio_from_number"  placeholder="+15550001234"          draft={draft} update={update} C={C} />

            <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:12, marginTop:4 }}>
              <Eyebrow>Test SMS</Eyebrow>
              <div style={{ display:"flex", gap:8 }}>
                <input value={testPhone} onChange={e => setTestPhone(e.target.value)} placeholder="+15550001234"
                  style={{ ...InputStyle(C), flex:1 }} />
                <button onClick={handleTestSMS} disabled={testing==="sms"}
                  style={{ ...BtnPrimary, opacity:testing==="sms"?0.5:1, whiteSpace:"nowrap" }}>
                  {testing==="sms" ? "Sending..." : "Send Test"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.3)", fontStyle:"italic", padding:"20px 0", textAlign:"center" }}>
            Enable SMS to configure Twilio credentials
          </div>
        )}
      </div>

      {/* ── EMAIL SETTINGS ── */}
      <div style={Card(C)}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
          <Eyebrow style={{ margin:0 }}>📧 Email via Gmail</Eyebrow>
          <Toggle val={!!draft.email_enabled} onChange={() => update("email_enabled", !draft.email_enabled)} C={C} />
        </div>

        {draft.email_enabled ? (
          <>
            <NotifField label="Gmail Address"   field="gmail_user"  placeholder="you@gmail.com"       draft={draft} update={update} C={C} />
            <NotifField label="App Password"   field="gmail_pass"  type="password" placeholder="xxxx xxxx xxxx xxxx" draft={draft} update={update} C={C} />
            <NotifField label="From Name"      field="email_from_name" placeholder="40Geo Nuggets"        draft={draft} update={update} C={C} />

            <div style={{ borderTop:"1px solid rgba(255,255,255,0.08)", paddingTop:12, marginTop:4 }}>
              <Eyebrow>Test Email</Eyebrow>
              <div style={{ display:"flex", gap:8 }}>
                <input value={testEmail} onChange={e => setTestEmail(e.target.value)} placeholder="you@40geo.com"
                  style={{ ...InputStyle(C), flex:1 }} />
                <button onClick={handleTestEmail} disabled={testing==="email"}
                  style={{ ...BtnPrimary, opacity:testing==="email"?0.5:1, whiteSpace:"nowrap" }}>
                  {testing==="email" ? "Sending..." : "Send Test"}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.3)", fontStyle:"italic", padding:"20px 0", textAlign:"center" }}>
            Enable Email to configure SMTP credentials
          </div>
        )}
      </div>

      {/* ── NOTIFICATION RULES ── */}
      <div style={{ ...Card(C), gridColumn:"1 / -1" }}>
        <Eyebrow>Notification Rules</Eyebrow>
        <div style={{ display:"grid", gridTemplateColumns:"2fr 80px 80px 1fr", gap:0 }}>
          {/* Header */}
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>Event</div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", padding:"6px 0", textAlign:"center", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>SMS</div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", padding:"6px 0", textAlign:"center", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>Email</div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.3)", letterSpacing:"0.2em", textTransform:"uppercase", padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>Notify</div>

          {/* Rows */}
          {Object.entries(RULE_LABELS).map(([key, label]) => {
            const rule = rules[key] || { sms:false, email:false, notify:"assignee" };
            const updateRule = (field, val) => {
              setDraft(p => ({ ...p, rules: { ...p.rules, [key]: { ...rule, [field]: val } } }));
            };
            return (
              <>
                <div key={key+"l"} style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#fff", padding:"10px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>{label}</div>
                <div key={key+"s"} style={{ display:"flex", justifyContent:"center", alignItems:"center", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <Toggle val={rule.sms} onChange={() => updateRule("sms", !rule.sms)} C={C} />
                </div>
                <div key={key+"e"} style={{ display:"flex", justifyContent:"center", alignItems:"center", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <Toggle val={rule.email} onChange={() => updateRule("email", !rule.email)} C={C} />
                </div>
                <div key={key+"n"} style={{ padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <select value={rule.notify} onChange={e => updateRule("notify", e.target.value)}
                    style={{ ...InputStyle(C), padding:"5px 8px", fontSize:11 }}>
                    <option value="assignee">Assignee</option>
                    <option value="owner">Owner</option>
                    <option value="reporter">Reporter</option>
                    <option value="all_admins">All Admins</option>
                    <option value="all_internal">All Internal</option>
                  </select>
                </div>
              </>
            );
          })}
        </div>

        {/* Save button */}
        <div style={{ display:"flex", justifyContent:"flex-end", marginTop:16 }}>
          <button onClick={handleSave} style={{ ...BtnPrimary, ...(saved ? {background:"#22C55E"} : {}) }}>
            <CheckCircle size={13}/> {saved ? "Saved!" : "Save Settings"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AdminPerms({ C, permissions, togglePerm }) {
  return (
    <div style={{ ...Card(C), overflowX:"auto" }}>
      <Eyebrow>Permission Matrix — click to toggle</Eyebrow>
      <table style={{ width:"100%", borderCollapse:"collapse" }}>
        <thead>
          <tr>
            <th style={{ textAlign:"left", padding:"8px 12px", fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, color:"rgba(255,255,255,0.4)", letterSpacing:"0.2em", textTransform:"uppercase", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>Permission</th>
            {["super_admin","admin","developer","viewer"].map(r => (
              <th key={r} style={{ textAlign:"center", padding:"8px 16px", fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, color:ROLE_META[r]?.color||"#fff", letterSpacing:"0.2em", textTransform:"uppercase", borderBottom:"1px solid rgba(255,255,255,0.08)" }}>{ROLE_META[r]?.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Object.entries(permissions).map(([perm,roles]) => (
            <tr key={perm} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
              <td style={{ padding:"8px 12px", fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.5)", fontWeight:400 }}>{perm.replace(/_/g," ")}</td>
              {["super_admin","admin","developer","viewer"].map(r => (
                <td key={r} style={{ textAlign:"center", padding:"8px 16px" }}>
                  <div onClick={() => togglePerm(perm,r)}
                    style={{ width:22, height:22, borderRadius:"50%", background:roles[r]?"rgba(34,197,94,0.15)":"rgba(239,68,68,0.1)", border:`1px solid ${roles[r]?"rgba(34,197,94,0.5)":"rgba(239,68,68,0.3)"}`, display:"inline-flex", alignItems:"center", justifyContent:"center", cursor:"pointer", transition:"all 0.2s ease" }}>
                    {roles[r] ? <CheckCircle size={12} color="#22C55E"/> : <XCircle size={12} color="#EF4444"/>}
                  </div>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Token Usage Dashboard ────────────────────────────────────────
const TOKEN_RATES_UI = {
  // Anthropic — the gateway in /api/chat is Anthropic-only today, so
  // these are the models the manual picker actually routes to.
  'claude-opus-4-8':           { label:'Claude Opus 4.8',     vendor:'Anthropic', input:15.00, output:75.00, tier:'premium'  },
  'claude-opus-4-7':           { label:'Claude Opus 4.7',     vendor:'Anthropic', input:15.00, output:75.00, tier:'premium'  },
  'claude-sonnet-4-6':         { label:'Claude Sonnet 4.6',   vendor:'Anthropic', input:3.00,  output:15.00, tier:'premium'  },
  'claude-opus-4-6':           { label:'Claude Opus 4.6',     vendor:'Anthropic', input:15.00, output:75.00, tier:'premium'  },
  'claude-haiku-4-5-20251001': { label:'Claude Haiku 4.5',    vendor:'Anthropic', input:0.80,  output:4.00,  tier:'economy'  },
  'claude-fable-5':            { label:'Claude Fable 5',      vendor:'Anthropic', input:0.00,  output:0.00,  tier:'standard' },
  // Other vendors kept for cost-reference only — /api/chat won't
  // route to them today, so the manual picker won't list them.
  'gpt-4o':                    { label:'GPT-4o',              vendor:'OpenAI',    input:2.50,  output:10.00, tier:'premium'  },
  'gpt-4o-mini':               { label:'GPT-4o Mini',         vendor:'OpenAI',    input:0.15,  output:0.60,  tier:'economy'  },
  'gemini-1.5-flash':          { label:'Gemini 1.5 Flash',    vendor:'Google',    input:0.075, output:0.30,  tier:'economy'  },
  'gemini-1.5-pro':            { label:'Gemini 1.5 Pro',      vendor:'Google',    input:1.25,  output:5.00,  tier:'standard' },
  'llama-3.1-local':           { label:'Llama 3.1 (Local)',   vendor:'Meta/Self', input:0,     output:0,     tier:'free'     },
};

// Models exposed in the manual per-feature picker. Order = picker order.
// Restricted to Anthropic until we wire multi-vendor routing into
// /api/chat.
const SELECTABLE_MODELS = [
  'claude-opus-4-8',
  'claude-opus-4-7',
  'claude-sonnet-4-6',
  'claude-haiku-4-5-20251001',
  'claude-fable-5',
];

// Per-feature default model fallback — what /api/chat uses when no
// override exists. Should match ANTHROPIC_CHAT_MODEL env in Vercel.
const DEFAULT_CHAT_MODEL = 'claude-sonnet-4-6';

const FEATURE_LABELS = {
  strategy_lab:        { label:'Strategy Lab',              icon:'🧠', desc:'Multi-turn planning & architecture' },
  helpdesk_aria:       { label:'Help Desk — ARIA',          icon:'🎧', desc:'Ticket analysis & response drafting' },
  messenger_detect:    { label:'Messenger — Detect',        icon:'⚡', desc:'Auto-detect actionable items in chat' },
  messenger_ai:        { label:'Messenger — AI Chat',       icon:'💬', desc:'Direct AI Q&A in team channels' },
  messenger_summary:   { label:'Messenger — Summary',       icon:'📋', desc:'Catch-me-up channel summaries' },
  cyber_assessment:    { label:'Cyber — Threat Assessment', icon:'🔒', desc:'AI executive risk summary from active threats' },
  cyber_report:        { label:'Cyber — Report Generation', icon:'🛡️', desc:'Scheduled & on-demand security reports' },
  devnotebook:         { label:'Dev Notebook',              icon:'📓', desc:'Auto-populate daily dev log from activity' },
  deal_ai_chat:        { label:'Deal Room — AI Chat',       icon:'🤝', desc:'Per-deal AI Q&A over diligence docs + merger thesis' },
  proposal_coach:      { label:'Proposal Coach',             icon:'🪈', desc:'Per-opportunity AI advisor — win prob, gaps, next actions' },
};

const MODEL_RECOMMENDATIONS = [
  { feature:'messenger_detect',  current:'claude-sonnet-4-6', recommended:'claude-haiku-4-5-20251001', reason:'High-volume, simple JSON extraction. Haiku handles this well at 94% lower cost.', savingsPct:85 },
  { feature:'messenger_summary', current:'claude-sonnet-4-6', recommended:'claude-haiku-4-5-20251001', reason:'Summarization is well within Haiku\'s capability. No quality loss expected.', savingsPct:85 },
  { feature:'helpdesk_aria',     current:'claude-sonnet-4-6', recommended:'claude-haiku-4-5-20251001', reason:'Structured JSON responses with clear schema. Haiku is reliable for this pattern.', savingsPct:80 },
  { feature:'messenger_ai',      current:'claude-sonnet-4-6', recommended:'gpt-4o-mini',               reason:'Concise Q&A under 180 words. GPT-4o Mini is fast and cost-effective here.', savingsPct:90 },
  { feature:'cyber_assessment',  current:'claude-sonnet-4-6', recommended:'claude-haiku-4-5-20251001', reason:'Threat summarization follows a fixed template — Haiku handles this pattern reliably at 85% lower cost.', savingsPct:85 },
  { feature:'cyber_report',      current:'claude-sonnet-4-6', recommended:'claude-haiku-4-5-20251001', reason:'Structured report generation with defined output. No complex reasoning required.', savingsPct:85 },
  { feature:'devnotebook',       current:'claude-sonnet-4-6', recommended:'claude-haiku-4-5-20251001', reason:'Categorization and summarization tasks. Haiku performs well and runs daily — savings compound.', savingsPct:80 },
  { feature:'strategy_lab',      current:'claude-sonnet-4-6', recommended:'claude-sonnet-4-6',          reason:'Complex multi-turn reasoning, architecture planning — keep on Sonnet. Quality matters here.', savingsPct:0 },
  { feature:'deal_ai_chat',      current:'claude-sonnet-4-6', recommended:'claude-sonnet-4-6',          reason:'Deal-room conversational Q&A over PDFs + merger thesis — keep on Sonnet so document analysis and persuasive writing stay sharp. Switch to Haiku for cost only if usage spikes.', savingsPct:0 },
];

function TokenUsageDashboard({ currentUser, orgSettings, saveOrgSettings, showToast }) {
  const [logs,       setLogs]     = useState([]);
  const [loading,    setLoading]  = useState(true);
  const [view,       setView]     = useState('overview'); // overview | by_feature | by_user | recommendations
  const [days,       setDays]     = useState(30);
  const [applying,   setApplying] = useState(null); // feature key being saved

  const appliedModels = orgSettings?.ai_models || {};

  const applyRecommendation = async (rec) => {
    setApplying(rec.feature);
    const updated = { ...appliedModels, [rec.feature]: rec.recommended };
    // Use targeted save — only touches ai_models column
    const ok = await dbSaveAiModels(currentUser.orgId, updated);
    if (ok) {
      // Mirror into full orgSettings so the rest of the UI stays in sync
      saveOrgSettings({ ...orgSettings, ai_models: updated });
      setAiModelOverride(rec.feature, rec.recommended);
      showToast?.(`✓ ${FEATURE_LABELS[rec.feature]?.label || rec.feature} switched to ${TOKEN_RATES_UI[rec.recommended]?.label || rec.recommended}`);
    } else {
      showToast?.(`❌ Save failed — check console. Does the org_settings table have an ai_models column?`);
    }
    setApplying(null);
  };

  // Manual per-feature model override. Called from the dropdown in
  // the By Feature view. Pass model='' (empty) to clear the override
  // and fall back to the env default in /api/chat.
  const setManualModel = async (featureKey, modelId) => {
    setApplying(featureKey);
    const updated = { ...appliedModels };
    if (modelId) updated[featureKey] = modelId;
    else delete updated[featureKey];
    const ok = await dbSaveAiModels(currentUser.orgId, updated);
    if (ok) {
      saveOrgSettings({ ...orgSettings, ai_models: updated });
      if (modelId) setAiModelOverride(featureKey,   modelId);
      else         clearAiModelOverride(featureKey);
      const label = modelId
        ? (TOKEN_RATES_UI[modelId]?.label || modelId)
        : 'default';
      showToast?.(`✓ ${FEATURE_LABELS[featureKey]?.label || featureKey} → ${label}`);
    } else {
      showToast?.(`❌ Save failed`);
    }
    setApplying(null);
  };

  const revertRecommendation = async (rec) => {
    setApplying(rec.feature);
    const updated = { ...appliedModels };
    delete updated[rec.feature];
    const ok = await dbSaveAiModels(currentUser.orgId, updated);
    if (ok) {
      saveOrgSettings({ ...orgSettings, ai_models: updated });
      clearAiModelOverride(rec.feature);
      showToast?.(`✓ Reverted ${FEATURE_LABELS[rec.feature]?.label || rec.feature} to default`);
    } else {
      showToast?.(`❌ Save failed — check console`);
    }
    setApplying(null);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const data = await loadTokenUsage(currentUser.orgId, 1000);
      setLogs(data);
      setLoading(false);
    })();
  }, [currentUser.orgId]);

  // eslint-disable-next-line react-hooks/purity -- rolling-window cutoff; recomputed on re-render is intended
  const cutoff   = new Date(Date.now() - days * 86400000);
  const filtered = logs.filter(l => new Date(l.created_at) >= cutoff);

  const totalIn    = filtered.reduce((s,l) => s + (l.input_tokens||0), 0);
  const totalOut   = filtered.reduce((s,l) => s + (l.output_tokens||0), 0);
  const totalTok   = totalIn + totalOut;
  const totalCost  = filtered.reduce((s,l) => s + (parseFloat(l.cost_usd)||0), 0);
  const callCount  = filtered.length;

  // By feature
  const byFeature = Object.entries(
    filtered.reduce((acc, l) => {
      const k = l.feature || 'unknown';
      if (!acc[k]) acc[k] = { tokens:0, cost:0, calls:0 };
      acc[k].tokens += (l.total_tokens||0);
      acc[k].cost   += parseFloat(l.cost_usd||0);
      acc[k].calls  += 1;
      return acc;
    }, {})
  ).map(([feature, d]) => ({ feature, ...d })).sort((a,b) => b.cost - a.cost);

  // By user
  const byUser = Object.entries(
    filtered.reduce((acc, l) => {
      const k = l.user_name || l.user_id || 'Unknown';
      if (!acc[k]) acc[k] = { tokens:0, cost:0, calls:0, company: l.company_name };
      acc[k].tokens += (l.total_tokens||0);
      acc[k].cost   += parseFloat(l.cost_usd||0);
      acc[k].calls  += 1;
      return acc;
    }, {})
  ).map(([name, d]) => ({ name, ...d })).sort((a,b) => b.cost - a.cost);

  // By model
  const byModel = Object.entries(
    filtered.reduce((acc, l) => {
      const k = l.model || 'claude-sonnet-4-6';
      if (!acc[k]) acc[k] = { tokens:0, cost:0, calls:0 };
      acc[k].tokens += (l.total_tokens||0);
      acc[k].cost   += parseFloat(l.cost_usd||0);
      acc[k].calls  += 1;
      return acc;
    }, {})
  ).map(([model, d]) => ({ model, ...d })).sort((a,b) => b.cost - a.cost);

  // Projected monthly
  const projMonthly = days > 0 ? (totalCost / days) * 30 : 0;


  // Always render in dollars to avoid the prior "$0.869¢" trap where
  // mixing $ and ¢ made fractional-cent amounts look like dollars.
  // Small amounts get extra decimal places so they stay readable.
  const fmtCost = (v) => {
    const n = Number(v) || 0;
    if (n === 0)       return '$0.0000';
    if (n < 0.0001)    return '<$0.0001';
    if (n < 0.01)      return `$${n.toFixed(4)}`;
    if (n < 1)         return `$${n.toFixed(4)}`;
    return `$${n.toFixed(2)}`;
  };
  const fmtTokenK = v => v >= 1000000 ? `${(v/1000000).toFixed(2)}M` : v >= 1000 ? `${(v/1000).toFixed(1)}K` : `${v}`;

  const SUBNAV = [{id:'overview',label:'Overview'},{id:'by_feature',label:'By Feature'},{id:'by_user',label:'By User'},{id:'recommendations',label:'Recommendations'}];

  return (
    <div style={{padding:'4px 0'}}>
      {/* Controls */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
        <div style={{display:'flex',gap:4,background:'rgba(255,255,255,0.03)',padding:4,borderRadius:8}}>
          {SUBNAV.map(n=>(
            <button key={n.id} onClick={()=>setView(n.id)} style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.04em',padding:'5px 12px',borderRadius:6,border:'none',cursor:'pointer',background:view===n.id?'#E87722':'transparent',color:view===n.id?'#fff':'rgba(255,255,255,0.4)',transition:'all 0.15s'}}>{n.label}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:6}}>
          {[7,30,90].map(d=>(
            <button key={d} onClick={()=>setDays(d)} style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,padding:'4px 10px',borderRadius:5,border:`1px solid ${days===d?'#E87722':'rgba(255,255,255,0.1)'}`,background:days===d?'rgba(232,119,34,0.15)':'transparent',color:days===d?'#E87722':'rgba(255,255,255,0.4)',cursor:'pointer'}}>{d}d</button>
          ))}
        </div>
      </div>

      {loading && <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,color:'rgba(255,255,255,0.4)',textAlign:'center',padding:40}}>Loading usage data…</div>}

      {!loading && filtered.length === 0 && view !== 'recommendations' && (
        <div style={{background:'rgba(255,255,255,0.03)',border:'1px dashed rgba(255,255,255,0.1)',borderRadius:10,padding:40,textAlign:'center'}}>
          <div style={{fontSize:32,marginBottom:12}}>📊</div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:700,color:'rgba(255,255,255,0.6)',marginBottom:6}}>No usage data yet</div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.3)'}}>Token usage will appear here after AI features are used.</div>
        </div>
      )}

      {/* Overview */}
      {!loading && view==='overview' && filtered.length > 0 && (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {/* KPI row */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12}}>
            {[
              {label:'Total Tokens',    value:fmtTokenK(totalTok),    sub:`${fmtTokenK(totalIn)} in · ${fmtTokenK(totalOut)} out`, color:'#60A5FA'},
              {label:'Total Cost',      value:`$${totalCost.toFixed(4)}`, sub:`${days}-day period`,  color:'#E87722'},
              {label:'Proj. Monthly',   value:`$${projMonthly.toFixed(2)}`,  sub:'at current pace',    color:'#F59E0B'},
              {label:'AI Calls',        value:callCount,               sub:'total requests',      color:'#A78BFA'},
              {label:'Potential Savings',value:`$${(projMonthly*0.7).toFixed(2)}/mo`, sub:'with recommendations', color:'#22C55E'},
            ].map(k=>(
              <div key={k.label} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:8,padding:'14px 16px'}}>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.1em',textTransform:'uppercase',color:'rgba(255,255,255,0.35)',marginBottom:6}}>{k.label}</div>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontWeight:900,fontSize:20,color:k.color,lineHeight:1}}>{k.value}</div>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.25)',marginTop:4}}>{k.sub}</div>
              </div>
            ))}
          </div>

          {/* Model breakdown */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
            <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'18px 20px'}}>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:14}}>By Model</div>
              {byModel.length === 0 && <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.25)'}}>No data</div>}
              {byModel.map(m => {
                const meta = TOKEN_RATES_UI[m.model] || { label: m.model, vendor:'Unknown', tier:'standard' };
                const tierColor = {premium:'#E87722',standard:'#60A5FA',economy:'#22C55E',free:'#A78BFA'}[meta.tier]||'#6B7280';
                return (
                  <div key={m.model} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
                    <div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:700,color:'#fff'}}>{meta.label}</div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.35)'}}>{meta.vendor} · <span style={{color:tierColor}}>{meta.tier}</span> · {m.calls} calls</div>
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:700,color:'#E87722'}}>{fmtCost(m.cost)}</div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.3)'}}>{fmtTokenK(m.tokens)} tok</div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recent log */}
            <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'18px 20px'}}>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:14}}>Recent Calls</div>
              <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:240,overflowY:'auto'}}>
                {filtered.slice(0,12).map((l,i)=>{
                  const feat = FEATURE_LABELS[l.feature] || { label: l.feature, icon:'🤖' };
                  return (
                    <div key={i} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'5px 0',borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                      <div style={{display:'flex',gap:8,alignItems:'center'}}>
                        <span style={{fontSize:14}}>{feat.icon}</span>
                        <div>
                          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.7)'}}>{feat.label}</div>
                          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)'}}>{l.user_name} · {new Date(l.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'#E87722',fontWeight:700}}>{fmtCost(parseFloat(l.cost_usd||0))}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* By Feature */}
      {!loading && view==='by_feature' && (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {byFeature.length===0 && <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,color:'rgba(255,255,255,0.3)',textAlign:'center',padding:40}}>No data for this period</div>}
          {byFeature.map(f=>{
            const meta = FEATURE_LABELS[f.feature] || { label:f.feature, icon:'🤖', desc:'' };
            const rec  = MODEL_RECOMMENDATIONS.find(r=>r.feature===f.feature);
            const maxCost = byFeature[0]?.cost || 1;
            const currentModel = appliedModels[f.feature] || '';
            const effectiveModel = currentModel || DEFAULT_CHAT_MODEL;
            const isOverridden = !!currentModel;
            const isSaving = applying === f.feature;
            return (
              <div key={f.feature} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'16px 20px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                  <div style={{display:'flex',gap:10,alignItems:'center'}}>
                    <span style={{fontSize:22}}>{meta.icon}</span>
                    <div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:'#fff'}}>{meta.label}</div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.35)'}}>{meta.desc}</div>
                    </div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:18,fontWeight:900,color:'#E87722'}}>{fmtCost(f.cost)}</div>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.3)'}}>{f.calls} calls · {fmtTokenK(f.tokens)} tokens</div>
                  </div>
                </div>
                <div style={{height:4,background:'rgba(255,255,255,0.06)',borderRadius:2,marginBottom:10}}>
                  <div style={{height:'100%',width:`${(f.cost/maxCost)*100}%`,background:'linear-gradient(90deg,#E87722,#f59840)',borderRadius:2}}/>
                </div>
                {/* Manual model picker — pick any Anthropic model or
                    revert to the env default. Overrides persist in
                    org_settings.ai_models and apply on the next call. */}
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom: (rec && rec.savingsPct > 0) ? 10 : 0}}>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.45)',letterSpacing:'0.08em',textTransform:'uppercase',fontWeight:700,whiteSpace:'nowrap'}}>Model</span>
                  <select
                    value={currentModel}
                    disabled={isSaving}
                    onChange={e => setManualModel(f.feature, e.target.value)}
                    style={{background:'rgba(255,255,255,0.06)',border:`1px solid ${isOverridden ? 'rgba(167,139,250,0.5)' : 'rgba(255,255,255,0.12)'}`,color:'#fff',borderRadius:6,padding:'6px 10px',fontSize:11,fontFamily:"'Montserrat',sans-serif",flex:1,cursor: isSaving ? 'wait' : 'pointer'}}>
                    <option value="">— Default ({TOKEN_RATES_UI[DEFAULT_CHAT_MODEL]?.label || DEFAULT_CHAT_MODEL}) —</option>
                    {SELECTABLE_MODELS.map(id => {
                      const m = TOKEN_RATES_UI[id] || { label: id };
                      const priceTag = (m.input != null && m.output != null && (m.input || m.output))
                        ? ` · $${m.input}/$${m.output} per MTok`
                        : '';
                      return <option key={id} value={id}>{m.label}{priceTag}</option>;
                    })}
                  </select>
                  {isOverridden && (
                    <button onClick={() => setManualModel(f.feature, '')} disabled={isSaving}
                      title="Clear override — use environment default"
                      style={{background:'transparent',border:'1px solid rgba(255,255,255,0.15)',color:'rgba(255,255,255,0.6)',borderRadius:5,padding:'5px 10px',fontSize:10,fontWeight:700,cursor: isSaving ? 'wait' : 'pointer',fontFamily:"'Montserrat',sans-serif",letterSpacing:'0.05em',textTransform:'uppercase'}}>
                      Reset
                    </button>
                  )}
                  {isSaving && (
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'#FFB830'}}>Saving…</span>
                  )}
                </div>
                {rec && rec.savingsPct > 0 && (
                  <div style={{display:'flex',alignItems:'center',gap:8,background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.15)',borderRadius:6,padding:'7px 10px'}}>
                    <span style={{fontSize:14}}>💡</span>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.6)',flex:1}}>{rec.reason}</div>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#22C55E',whiteSpace:'nowrap'}}>~{rec.savingsPct}% cheaper</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* By User */}
      {!loading && view==='by_user' && (
        <div style={{display:'flex',flexDirection:'column',gap:8}}>
          {byUser.length===0 && <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,color:'rgba(255,255,255,0.3)',textAlign:'center',padding:40}}>No data for this period</div>}
          {byUser.map((u,i)=>{
            const maxCost = byUser[0]?.cost||1;
            return (
              <div key={u.name} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:9,padding:'14px 18px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <div style={{display:'flex',gap:10,alignItems:'center'}}>
                    <div style={{width:32,height:32,borderRadius:'50%',background:`hsl(${(i*67)%360},60%,40%)`,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:700,color:'#fff',flexShrink:0}}>{u.name?.[0]?.toUpperCase()||'?'}</div>
                    <div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:'#fff'}}>{u.name}</div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.35)'}}>{u.company||'40Geo'} · {u.calls} calls</div>
                    </div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:16,fontWeight:900,color:'#E87722'}}>{fmtCost(u.cost)}</div>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.3)'}}>{fmtTokenK(u.tokens)} tokens</div>
                  </div>
                </div>
                <div style={{height:3,background:'rgba(255,255,255,0.06)',borderRadius:2}}>
                  <div style={{height:'100%',width:`${(u.cost/maxCost)*100}%`,background:'#60A5FA',borderRadius:2,opacity:0.7}}/>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Model Recommendations */}
      {view==='recommendations' && (
        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          <div style={{background:'rgba(34,197,94,0.07)',border:'1px solid rgba(34,197,94,0.15)',borderRadius:10,padding:'16px 20px'}}>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(34,197,94,0.8)',marginBottom:6}}>Optimization Summary</div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,color:'rgba(255,255,255,0.7)',lineHeight:1.6}}>
              Applying the recommendations below could reduce your monthly AI spend by <strong style={{color:'#22C55E'}}>~70–85%</strong> on high-volume, low-complexity features — while keeping premium models where quality matters most (Strategy Lab).
            </div>
          </div>

          {/* Model comparison table */}
          <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'18px 20px',marginBottom:4}}>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:14}}>Model Cost Reference (per 1M tokens)</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 80px 80px 80px',gap:0}}>
              {['Model','Input','Output','Tier'].map(h=><div key={h} style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.3)',padding:'0 0 8px'}}>{h}</div>)}
              {Object.entries(TOKEN_RATES_UI).map(([id,m])=>{
                const tierColor={premium:'#E87722',standard:'#60A5FA',economy:'#22C55E',free:'#A78BFA'}[m.tier]||'#6B7280';
                return [
                  <div key={id+'n'} style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'#fff',padding:'6px 0',borderTop:'1px solid rgba(255,255,255,0.05)'}}>{m.label}<span style={{fontSize:10,color:'rgba(255,255,255,0.3)',marginLeft:6}}>{m.vendor}</span></div>,
                  <div key={id+'i'} style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.7)',padding:'6px 0',borderTop:'1px solid rgba(255,255,255,0.05)'}}>${m.input}</div>,
                  <div key={id+'o'} style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.7)',padding:'6px 0',borderTop:'1px solid rgba(255,255,255,0.05)'}}>${m.output}</div>,
                  <div key={id+'t'} style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:tierColor,padding:'6px 0',borderTop:'1px solid rgba(255,255,255,0.05)',textTransform:'uppercase'}}>{m.tier}</div>,
                ];
              })}
            </div>
          </div>

          {/* Per-feature recommendations */}
          {MODEL_RECOMMENDATIONS.map(rec=>{
            const feat     = FEATURE_LABELS[rec.feature] || { label:rec.feature, icon:'🤖' };
            const curMeta  = TOKEN_RATES_UI[rec.current]     || { label:rec.current };
            const recMeta  = TOKEN_RATES_UI[rec.recommended] || { label:rec.recommended };
            const noChange = rec.recommended === rec.current;
            const isActive = !noChange && appliedModels[rec.feature] === rec.recommended;
            const isSaving = applying === rec.feature;
            const activeModel = appliedModels[rec.feature];
            const activeMeta  = activeModel ? (TOKEN_RATES_UI[activeModel] || { label:activeModel }) : null;
            return (
              <div key={rec.feature} style={{background:'rgba(255,255,255,0.04)',border:`1px solid ${isActive?'rgba(232,119,34,0.3)':noChange?'rgba(255,255,255,0.07)':'rgba(34,197,94,0.15)'}`,borderRadius:10,padding:'16px 20px'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                  <div style={{display:'flex',gap:10,alignItems:'center'}}>
                    <span style={{fontSize:20}}>{feat.icon}</span>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:'#fff'}}>{feat.label}</div>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    {isActive && <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:'#E87722',background:'rgba(232,119,34,0.12)',padding:'3px 8px',borderRadius:3}}>ACTIVE</span>}
                    {noChange
                      ? <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:'rgba(255,255,255,0.4)',background:'rgba(255,255,255,0.06)',padding:'3px 8px',borderRadius:3}}>KEEP AS-IS</span>
                      : <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:'#22C55E',background:'rgba(34,197,94,0.1)',padding:'3px 8px',borderRadius:3}}>SAVE ~{rec.savingsPct}%</span>}
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:10}}>
                  <div style={{background:'rgba(255,255,255,0.06)',borderRadius:6,padding:'6px 12px',fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.6)'}}>
                    {activeMeta ? <><span style={{color:'rgba(255,255,255,0.35)',textDecoration:'line-through',marginRight:6}}>{curMeta.label}</span>{activeMeta.label}</> : curMeta.label}
                  </div>
                  {!noChange && <>
                    <span style={{color:'rgba(255,255,255,0.3)',fontSize:16}}>→</span>
                    <div style={{background:'rgba(34,197,94,0.1)',border:'1px solid rgba(34,197,94,0.2)',borderRadius:6,padding:'6px 12px',fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#22C55E'}}>{recMeta.label}</div>
                  </>}
                </div>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.5)',lineHeight:1.6,marginBottom:noChange?0:12}}>{rec.reason}</div>
                {!noChange && (
                  <div style={{display:'flex',gap:8}}>
                    {!isActive
                      ? <button disabled={isSaving} onClick={()=>applyRecommendation(rec)}
                          style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',background:'#22C55E',color:'#000',border:'none',borderRadius:6,padding:'6px 14px',cursor:'pointer',opacity:isSaving?0.6:1}}>
                          {isSaving ? 'Applying…' : 'Apply'}
                        </button>
                      : <button disabled={isSaving} onClick={()=>revertRecommendation(rec)}
                          style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',background:'rgba(255,255,255,0.08)',color:'rgba(255,255,255,0.6)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:6,padding:'6px 14px',cursor:'pointer',opacity:isSaving?0.6:1}}>
                          {isSaving ? 'Reverting…' : 'Revert to Default'}
                        </button>}
                  </div>
                )}
              </div>
            );
          })}

          <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'16px 20px'}}>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:10}}>Local Model Option</div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.55)',lineHeight:1.7}}>
              For the highest-volume, lowest-stakes tasks (message classification, intent detection), a self-hosted model via <strong style={{color:'#fff'}}>Ollama + Llama 3.1 8B</strong> or <strong style={{color:'#fff'}}>Mistral 7B</strong> runs at <strong style={{color:'#22C55E'}}>$0 per token</strong> with acceptable quality. Infrastructure cost is a small VPS (~$20–40/mo). Best suited for <em style={{color:'rgba(255,255,255,0.5)'}}>messenger_detect</em> at scale.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DB HEALTH — external Postgres monitor (GEO_DB, AMP, …)
// ═══════════════════════════════════════════════════════════════════
function DBHealth({ C, currentUser, users, showToast }) {
  const [databases,   setDatabases]   = useState([]);
  const [monitored,   setMonitored]   = useState([]);
  const [ownerships,  setOwnerships]  = useState([]);
  const [logs,        setLogs]        = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [activeDbId,  setActiveDbId]  = useState(null);

  // "Discover tables" modal
  const [discoverOpen,    setDiscoverOpen]    = useState(false);
  const [discoverRows,    setDiscoverRows]    = useState([]);
  const [collapsedSchemas, setCollapsedSchemas] = useState(new Set());
  const [discoverLoading, setDiscoverLoading] = useState(false);
  const [discoverError,   setDiscoverError]   = useState(null);
  const [discoverFilter,  setDiscoverFilter]  = useState('');
  const [selectedForAdd,  setSelectedForAdd]  = useState(new Set());

  // Editing monitored table
  const [editingId,  setEditingId]  = useState(null);
  const [editDraft,  setEditDraft]  = useState(null);
  const [editCols,   setEditCols]   = useState([]);
  const [savingEdit, setSavingEdit] = useState(false);

  // Run now
  const [running, setRunning] = useState(false);

  // Log filter
  const [logStatusFilter, setLogStatusFilter] = useState('all');

  // ── Load everything ─────────────────────────────────────────────
  const refresh = useCallback(async () => {
    setLoading(true);
    const [dbs, mts, owners, log] = await Promise.all([
      loadExternalDatabases(currentUser.orgId),
      loadMonitoredTables(currentUser.orgId),
      loadAppOwnership(currentUser.orgId),
      loadHealthLog(currentUser.orgId, { limit: 200 }),
    ]);
    setDatabases(dbs);
    setMonitored(mts);
    setOwnerships(owners);
    setLogs(log);
    if (!activeDbId && dbs.length) setActiveDbId(dbs[0].id);
    setLoading(false);
  }, [currentUser.orgId, activeDbId]);

  // Intentionally only re-run on org change; `refresh` is stable for the
  // lifetime of this panel.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); }, [currentUser.orgId]);

  const activeDb = databases.find(d => d.id === activeDbId) || null;
  const tablesForActive = monitored.filter(m => m.database_id === activeDbId);

  // ── Discover ────────────────────────────────────────────────────
  const openDiscover = async () => {
    if (!activeDb) return;
    setDiscoverOpen(true);
    setDiscoverLoading(true);
    setDiscoverError(null);
    setSelectedForAdd(new Set());
    const r = await discoverTablesInDatabase(activeDb.id);
    setDiscoverLoading(false);
    if (r.error) { setDiscoverError(r.error); return; }
    setDiscoverRows(r.tables || []);
    // Start with every non-public schema collapsed. With dozens of
    // schemas (acbl, basf, ...) this keeps the initial view compact;
    // public gets expanded because it's almost always the primary.
    const schemas = new Set((r.tables || []).map(t => t.schema_name));
    schemas.delete('public');
    setCollapsedSchemas(schemas);
  };

  const saveDiscoverSelections = async () => {
    if (!activeDb || !selectedForAdd.size) { setDiscoverOpen(false); return; }
    const picks = discoverRows.filter(r => selectedForAdd.has(`${r.schema_name}.${r.table_name}`));
    const ok = await bulkAddMonitoredTables(currentUser.orgId, activeDb.id, picks);
    if (!ok) { showToast('❌ Failed to save table picks'); return; }
    showToast(`✓ Added ${picks.length} table${picks.length === 1 ? '' : 's'} to monitoring`);
    setDiscoverOpen(false);
    await refresh();
  };

  // ── Edit monitored table ────────────────────────────────────────
  const openEdit = async (row) => {
    setEditingId(row.id);
    setEditDraft({ ...row });
    setEditCols([]);
    const r = await fetchColumnsForTable(row.database_id, row.schema_name, row.table_name);
    if (!r.error) setEditCols(r.columns || []);
  };

  const saveEdit = async () => {
    if (!editDraft) return;
    setSavingEdit(true);
    const ok = await saveMonitoredTable(editDraft);
    setSavingEdit(false);
    if (!ok) { showToast('❌ Save failed'); return; }
    setEditingId(null);
    setEditDraft(null);
    await refresh();
    showToast('✓ Saved');
  };

  const removeTable = async (id) => {
    if (!confirm('Stop monitoring this table?')) return;
    const ok = await deleteMonitoredTable(id);
    if (!ok) { showToast('❌ Delete failed'); return; }
    await refresh();
  };

  // ── Run now ─────────────────────────────────────────────────────
  const runNow = async () => {
    setRunning(true);
    const r = await runMonitorNow();
    setRunning(false);
    if (r.error) { showToast(`❌ ${r.error}`); return; }
    showToast(`✓ Checked ${r.checked}; ${r.failures} fail, ${r.alerted} alerted`);
    await refresh();
  };

  // ── Ownership ───────────────────────────────────────────────────
  const upsertOwner = async (row) => {
    const ok = await saveAppOwnership({ ...row, org_id: currentUser.orgId });
    if (!ok) { showToast('❌ Ownership save failed'); return; }
    await refresh();
  };
  const removeOwner = async (id) => {
    const ok = await deleteAppOwnership(id);
    if (!ok) { showToast('❌ Delete failed'); return; }
    await refresh();
  };

  // ── Styles ──────────────────────────────────────────────────────
  const card   = { background: C.cardBg, border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 16 };
  const btn    = { background: 'rgba(232,119,34,0.15)', border: '1px solid rgba(232,119,34,0.4)', color: '#E87722', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer' };
  const btnGho = { ...btn, background: 'transparent', color: 'rgba(255,255,255,0.6)', border: `1px solid ${C.border}` };
  const input  = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif" };
  const STATUS_COLOR = { ok: '#34D399', warn: '#FFB830', fail: '#FF6B6B', error: '#FF3B30' };

  const fmtAge = (iso) => {
    const d = Date.now() - new Date(iso).getTime(), m = Math.floor(d/60000);
    if (m < 1) return 'just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m/60); if (h < 24) return `${h}h ago`;
    return `${Math.floor(h/24)}d ago`;
  };

  if (loading) return <div style={{ padding: 24, color: 'rgba(255,255,255,0.5)' }}>Loading…</div>;

  if (!databases.length) {
    return (
      <div style={{ padding: 24, color: 'rgba(255,255,255,0.6)' }}>
        No external databases configured yet. Run the migration
        <code style={{ background:'rgba(255,255,255,0.05)', padding:'2px 6px', borderRadius:4, margin:'0 4px' }}>20260424_db_monitor.sql</code>
        or insert rows into <code>external_databases</code>.
      </div>
    );
  }

  const filteredLogs = logs.filter(l => {
    if (logStatusFilter !== 'all' && l.status !== logStatusFilter) return false;
    return !activeDbId || tablesForActive.some(t => t.id === l.monitored_table_id);
  });

  const filteredDiscoverRows = discoverRows.filter(r =>
    !discoverFilter.trim()
      ? true
      : `${r.schema_name}.${r.table_name}`.toLowerCase().includes(discoverFilter.toLowerCase())
  );

  // Group filtered rows by schema for the picker UI.
  // Schemas with matches are sorted alphabetically (public first as a
  // convenience since it's almost always the primary schema).
  const discoverGroups = (() => {
    const m = new Map();
    for (const r of filteredDiscoverRows) {
      if (!m.has(r.schema_name)) m.set(r.schema_name, []);
      m.get(r.schema_name).push(r);
    }
    return [...m.entries()]
      .sort(([a], [b]) => {
        if (a === 'public') return -1;
        if (b === 'public') return 1;
        return a.localeCompare(b);
      })
      .map(([schema, rows]) => ({ schema, rows }));
  })();

  // When the user types a filter, auto-expand any schema with matches so
  // they don't have to click around to find them.
  const effectivelyCollapsed = (schema) =>
    !discoverFilter.trim() && collapsedSchemas.has(schema);

  const toggleSchemaCollapsed = (schema) => {
    const next = new Set(collapsedSchemas);
    if (next.has(schema)) next.delete(schema); else next.add(schema);
    setCollapsedSchemas(next);
  };

  // Schema-level tri-state: none / some / all of its selectable rows are picked.
  const schemaSelectionState = (rows) => {
    const selectable = rows.filter(r => !r.already_monitored);
    if (!selectable.length) return 'none';
    const picked = selectable.filter(r => selectedForAdd.has(`${r.schema_name}.${r.table_name}`)).length;
    if (picked === 0) return 'none';
    if (picked === selectable.length) return 'all';
    return 'some';
  };

  const toggleSchemaAll = (rows) => {
    const selectable = rows.filter(r => !r.already_monitored);
    if (!selectable.length) return;
    const state = schemaSelectionState(rows);
    const next = new Set(selectedForAdd);
    const keyOf = r => `${r.schema_name}.${r.table_name}`;
    if (state === 'all') {
      selectable.forEach(r => next.delete(keyOf(r)));
    } else {
      selectable.forEach(r => next.add(keyOf(r)));
    }
    setSelectedForAdd(next);
  };

  return (
    <div style={{ animation: 'fadeUp 0.4s ease' }}>
      {/* ── Top bar ───────────────────────────────────────────── */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:16, flexWrap:'wrap' }}>
        <select value={activeDbId || ''} onChange={e => setActiveDbId(e.target.value)} style={{ ...input, padding:'8px 12px' }}>
          {databases.map(d => (
            <option key={d.id} value={d.id}>{d.name} ({d.env_prefix})</option>
          ))}
        </select>
        <button style={btn} onClick={openDiscover} disabled={!activeDb}>+ Discover tables</button>
        <button style={btnGho} onClick={runNow} disabled={running}>
          {running ? 'Running…' : 'Run checks now'}
        </button>
        <div style={{ flex:1 }} />
        <span style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>
          {tablesForActive.length} monitored · {logs.length} recent checks
        </span>
      </div>

      {/* ── Monitored tables ─────────────────────────────────── */}
      <div style={card}>
        <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.15em', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', marginBottom:12 }}>Monitored tables — {activeDb?.name}</div>
        {!tablesForActive.length && (
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', padding:'8px 0' }}>
            Nothing yet. Click <strong>+ Discover tables</strong> to pick what to watch.
          </div>
        )}
        {tablesForActive.map(t => {
          const owns = ownerships.filter(o => o.monitored_table_id === t.id);
          const latest = logs.find(l => l.monitored_table_id === t.id);
          const c = t.check_types || {};
          return (
            <div key={t.id} style={{ display:'grid', gridTemplateColumns:'1fr auto auto auto', gap:12, alignItems:'center', padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize:13, fontWeight:600, color:'#fff' }}>{t.schema_name}.{t.table_name}</div>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.4)', marginTop:2 }}>
                  {[c.connectivity && 'connectivity', c.row_count && 'row_count', c.freshness && 'freshness'].filter(Boolean).join(' · ') || 'no checks'}
                  {!!owns.length && ` · ${owns.length} owner${owns.length===1?'':'s'}`}
                  {t.consecutive_failures > 0 && ` · ${t.consecutive_failures} consecutive fail${t.consecutive_failures===1?'':'s'}`}
                </div>
              </div>
              <div style={{ fontSize:10, textAlign:'right' }}>
                {latest ? (
                  <>
                    <div style={{ color: STATUS_COLOR[latest.status], fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em' }}>{latest.status}</div>
                    <div style={{ color:'rgba(255,255,255,0.35)', marginTop:2 }}>{fmtAge(latest.checked_at)}</div>
                  </>
                ) : <span style={{ color:'rgba(255,255,255,0.25)' }}>never checked</span>}
              </div>
              <button style={btnGho} onClick={() => openEdit(t)}>Edit</button>
              <button style={{ ...btnGho, color:'#FF6B6B', borderColor:'rgba(255,107,107,0.3)' }} onClick={() => removeTable(t.id)}>Remove</button>
            </div>
          );
        })}
      </div>

      {/* ── Health log ───────────────────────────────────────── */}
      <div style={card}>
        <div style={{ display:'flex', alignItems:'center', marginBottom:12 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.15em', color:'rgba(255,255,255,0.4)', textTransform:'uppercase' }}>Health log</div>
          <div style={{ flex:1 }} />
          <select value={logStatusFilter} onChange={e => setLogStatusFilter(e.target.value)} style={input}>
            <option value="all">All statuses</option>
            <option value="ok">ok</option>
            <option value="warn">warn</option>
            <option value="fail">fail</option>
            <option value="error">error</option>
          </select>
        </div>
        <div style={{ maxHeight:360, overflowY:'auto' }}>
          {!filteredLogs.length && <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', padding:'8px 0' }}>No log rows match.</div>}
          {filteredLogs.map(l => {
            const mt = monitored.find(m => m.id === l.monitored_table_id);
            return (
              <div key={l.id} style={{ display:'grid', gridTemplateColumns:'110px 1fr auto', gap:12, padding:'7px 0', borderBottom:`1px solid ${C.border}`, fontSize:12 }}>
                <span style={{ color: STATUS_COLOR[l.status], fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', fontSize:10 }}>{l.status}</span>
                <span style={{ color:'#fff' }}>
                  {mt ? `${mt.schema_name}.${mt.table_name}` : l.monitored_table_id.slice(0,8)}
                  {l.latency_ms != null && <span style={{ color:'rgba(255,255,255,0.4)', marginLeft:8 }}>{l.latency_ms}ms</span>}
                  {l.row_count != null && <span style={{ color:'rgba(255,255,255,0.4)', marginLeft:8 }}>{l.row_count} rows</span>}
                  {l.freshness_seconds != null && <span style={{ color:'rgba(255,255,255,0.4)', marginLeft:8 }}>{l.freshness_seconds}s old</span>}
                  {l.error && <span style={{ color:'#FF6B6B', marginLeft:8 }}>{l.error}</span>}
                </span>
                <span style={{ color:'rgba(255,255,255,0.35)' }}>{fmtAge(l.checked_at)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Discover modal ───────────────────────────────────── */}
      {discoverOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setDiscoverOpen(false)}>
          <div onClick={e => e.stopPropagation()} style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, padding:24, maxWidth:720, width:'100%', maxHeight:'85vh', display:'flex', flexDirection:'column' }}>
            <div style={{ display:'flex', alignItems:'center', marginBottom:16 }}>
              <div>
                <div style={{ fontSize:16, fontWeight:700, color:'#fff' }}>Discover tables — {activeDb?.name}</div>
                <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:4 }}>Pick which tables to monitor.</div>
              </div>
              <div style={{ flex:1 }} />
              <button style={btnGho} onClick={() => setDiscoverOpen(false)}>Cancel</button>
            </div>
            {discoverLoading && <div style={{ color:'rgba(255,255,255,0.5)' }}>Scanning…</div>}
            {discoverError && <div style={{ color:'#FF6B6B', fontSize:12 }}>{discoverError}</div>}
            {!discoverLoading && !discoverError && (
              <>
                <input autoFocus placeholder="Filter…" value={discoverFilter} onChange={e => setDiscoverFilter(e.target.value)} style={{ ...input, marginBottom:12, width:'100%' }} />
                <div style={{ flex:1, overflowY:'auto', border:`1px solid ${C.border}`, borderRadius:8 }}>
                  {discoverGroups.map(({ schema, rows }) => {
                    const state = schemaSelectionState(rows);
                    const collapsed = effectivelyCollapsed(schema);
                    const selectable = rows.filter(r => !r.already_monitored).length;
                    const alreadyOn  = rows.length - selectable;
                    return (
                      <div key={schema}>
                        {/* Schema folder header */}
                        <div
                          style={{
                            display:'grid',
                            gridTemplateColumns:'18px auto 1fr auto',
                            gap:10,
                            alignItems:'center',
                            padding:'10px 12px',
                            background:'rgba(255,255,255,0.04)',
                            borderBottom:`1px solid ${C.border}`,
                            cursor:'pointer',
                            userSelect:'none',
                          }}
                          onClick={() => toggleSchemaCollapsed(schema)}
                        >
                          <span style={{ color:'rgba(255,255,255,0.5)', fontSize:10, textAlign:'center' }}>
                            {collapsed ? '▸' : '▾'}
                          </span>
                          <input
                            type="checkbox"
                            // Indeterminate style when "some" are selected.
                            ref={el => { if (el) el.indeterminate = state === 'some'; }}
                            checked={state === 'all'}
                            onChange={(e) => { e.stopPropagation(); toggleSchemaAll(rows); }}
                            onClick={(e) => e.stopPropagation()}
                            disabled={!selectable}
                          />
                          <span style={{ color:'#fff', fontWeight:700, fontSize:13, fontFamily:"'Montserrat',sans-serif", letterSpacing:'0.04em' }}>
                            {schema}
                            <span style={{ color:'rgba(255,255,255,0.35)', fontWeight:400, marginLeft:8, fontSize:11 }}>
                              {rows.length} table{rows.length===1?'':'s'}
                              {alreadyOn > 0 && ` · ${alreadyOn} on`}
                            </span>
                          </span>
                          <span style={{ color:'rgba(255,255,255,0.4)', fontSize:11 }}>
                            {state === 'all'  && 'all selected'}
                            {state === 'some' && 'some selected'}
                            {state === 'none' && selectable === 0 && 'all monitored'}
                          </span>
                        </div>

                        {/* Schema rows */}
                        {!collapsed && rows.map(r => {
                          const key = `${r.schema_name}.${r.table_name}`;
                          const checked  = selectedForAdd.has(key);
                          const disabled = r.already_monitored;
                          return (
                            <label
                              key={key}
                              style={{
                                display:'grid',
                                gridTemplateColumns:'18px auto 1fr auto',
                                gap:10,
                                alignItems:'center',
                                padding:'7px 12px 7px 12px',
                                paddingLeft:34, // indent under folder
                                borderBottom:`1px solid ${C.border}`,
                                cursor: disabled ? 'not-allowed' : 'pointer',
                                opacity: disabled ? 0.45 : 1,
                              }}
                            >
                              <span />
                              <input
                                type="checkbox"
                                disabled={disabled}
                                checked={checked || disabled}
                                onChange={(e) => {
                                  const next = new Set(selectedForAdd);
                                  if (e.target.checked) next.add(key); else next.delete(key);
                                  setSelectedForAdd(next);
                                }}
                              />
                              <span style={{ color:'#fff', fontSize:13 }}>{r.table_name}</span>
                              <span style={{ color:'rgba(255,255,255,0.4)', fontSize:11 }}>
                                {disabled ? 'already monitored' : `~${r.approx_rows.toLocaleString()} rows`}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                    );
                  })}
                  {!discoverGroups.length && (
                    <div style={{ padding:16, color:'rgba(255,255,255,0.4)', fontSize:12 }}>No tables match.</div>
                  )}
                </div>
                <div style={{ display:'flex', alignItems:'center', marginTop:16 }}>
                  <span style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{selectedForAdd.size} selected</span>
                  <div style={{ flex:1 }} />
                  <button style={btn} onClick={saveDiscoverSelections} disabled={!selectedForAdd.size}>Add to monitoring</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── Edit modal ───────────────────────────────────────── */}
      {editingId && editDraft && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:10000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }} onClick={() => setEditingId(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background:C.bg2, border:`1px solid ${C.border}`, borderRadius:12, padding:24, maxWidth:640, width:'100%', maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, color:'#fff', marginBottom:4 }}>{editDraft.schema_name}.{editDraft.table_name}</div>
            <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginBottom:20 }}>{activeDb?.name}</div>

            {/* Checks */}
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.15em', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', marginBottom:10 }}>Active checks</div>
            {['connectivity', 'row_count', 'freshness'].map(k => (
              <label key={k} style={{ display:'flex', alignItems:'center', gap:10, padding:'6px 0', fontSize:13, color:'#fff', cursor:'pointer' }}>
                <input type="checkbox"
                  checked={!!editDraft.check_types?.[k]}
                  onChange={e => setEditDraft({ ...editDraft, check_types: { ...editDraft.check_types, [k]: e.target.checked } })} />
                {k.replace('_', ' ')}
              </label>
            ))}

            {/* Row-count thresholds */}
            {editDraft.check_types?.row_count && (
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', marginBottom:6 }}>Row-count thresholds (optional)</div>
                <div style={{ display:'flex', gap:8 }}>
                  <input type="number" placeholder="min" value={editDraft.min_rows ?? ''} onChange={e => setEditDraft({ ...editDraft, min_rows: e.target.value === '' ? null : Number(e.target.value) })} style={input} />
                  <input type="number" placeholder="max" value={editDraft.max_rows ?? ''} onChange={e => setEditDraft({ ...editDraft, max_rows: e.target.value === '' ? null : Number(e.target.value) })} style={input} />
                </div>
              </div>
            )}

            {/* Freshness */}
            {editDraft.check_types?.freshness && (
              <div style={{ marginTop:14 }}>
                <div style={{ fontSize:10, color:'rgba(255,255,255,0.5)', marginBottom:6 }}>Freshness column & max age (seconds)</div>
                <div style={{ display:'flex', gap:8 }}>
                  <select value={editDraft.freshness_column || ''} onChange={e => setEditDraft({ ...editDraft, freshness_column: e.target.value || null })} style={{ ...input, flex:1 }}>
                    <option value="">— pick a timestamp column —</option>
                    {editCols.map(c => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
                  </select>
                  <input type="number" placeholder="max seconds" value={editDraft.freshness_max_seconds ?? ''} onChange={e => setEditDraft({ ...editDraft, freshness_max_seconds: e.target.value === '' ? null : Number(e.target.value) })} style={{ ...input, width:140 }} />
                </div>
              </div>
            )}

            {/* Ownerships */}
            <div style={{ fontSize:11, fontWeight:700, letterSpacing:'0.15em', color:'rgba(255,255,255,0.4)', textTransform:'uppercase', margin:'22px 0 10px' }}>Owners / alert recipients</div>
            {ownerships.filter(o => o.monitored_table_id === editingId).map(o => (
              <div key={o.id} style={{ display:'grid', gridTemplateColumns:'1fr 1fr auto auto', gap:6, padding:'6px 0', fontSize:12 }}>
                <span style={{ color:'#fff' }}>{o.app_name}</span>
                <span style={{ color:'rgba(255,255,255,0.5)' }}>{[o.contact_email, o.contact_phone].filter(Boolean).join(' · ') || '—'}</span>
                <span style={{ color:'rgba(255,255,255,0.5)', fontSize:10, textTransform:'uppercase' }}>{o.impact_level}</span>
                <button style={{ ...btnGho, padding:'2px 8px', fontSize:10 }} onClick={() => removeOwner(o.id)}>×</button>
              </div>
            ))}
            <OwnerForm btn={btn} input={input} users={users} onAdd={(row) => upsertOwner({ ...row, monitored_table_id: editingId })} />

            <div style={{ display:'flex', gap:8, marginTop:22 }}>
              <button style={btnGho} onClick={() => setEditingId(null)}>Cancel</button>
              <div style={{ flex:1 }} />
              <button style={btn} onClick={saveEdit} disabled={savingEdit}>{savingEdit ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OwnerForm({ btn, input, users, onAdd }) {
  const [app,    setApp]    = useState('');
  const [email,  setEmail]  = useState('');
  const [phone,  setPhone]  = useState('');
  const [impact, setImpact] = useState('medium');
  const [ownerId, setOwnerId] = useState('');

  const submit = () => {
    if (!app.trim()) return;
    onAdd({
      app_name: app.trim(),
      contact_email: email.trim() || null,
      contact_phone: phone.trim() || null,
      owner_user_id: ownerId || null,
      impact_level: impact,
    });
    setApp(''); setEmail(''); setPhone(''); setImpact('medium'); setOwnerId('');
  };

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr auto', gap:6, marginTop:8 }}>
      <input placeholder="App name" value={app} onChange={e => setApp(e.target.value)} style={input} />
      <input placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} style={input} />
      <input placeholder="Phone" value={phone} onChange={e => setPhone(e.target.value)} style={input} />
      <select value={impact} onChange={e => setImpact(e.target.value)} style={input}>
        <option value="critical">critical</option>
        <option value="high">high</option>
        <option value="medium">medium</option>
        <option value="low">low</option>
      </select>
      <select value={ownerId} onChange={e => setOwnerId(e.target.value)} style={{ ...input, gridColumn: '1 / 4' }}>
        <option value="">— link a Nuggets user (optional) —</option>
        {(users || []).map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
      </select>
      <button onClick={submit} style={btn} disabled={!app.trim()}>Add</button>
    </div>
  );
}

function AdminAudit({ C, audit }) {
  return (
    <div style={Card(C)}>
      <Eyebrow>Audit Log — {audit.length} entries</Eyebrow>
      {audit.map((a,i) => {
        const typeColor = { deploy:"#E87722", nugget:"#60A5FA", quality:"#22C55E", admin:"#E87722", auth:"#60A5FA", system:"rgba(255,255,255,0.4)" }[a.type] || "rgba(255,255,255,0.4)";
        return (
          <div key={i} style={{ display:"flex", gap:12, padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
            <div style={{ width:7, height:7, borderRadius:"50%", background:typeColor, flexShrink:0, marginTop:5 }}/>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.3)", width:80, flexShrink:0 }}>{a.ts}</div>
            <div style={{ flex:1, fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#fff" }}>{a.action}</div>
            <span style={{ ...Badge(typeColor,"transparent","transparent"), padding:0, fontSize:9 }}>{a.type?.toUpperCase()}</span>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ADMIN — COMPANY MANAGER
// ═══════════════════════════════════════════════════════════════════
const ALL_FEATURES = [
  ["messenger",     "💬 Messenger",     "In-app team messaging & DMs"],
  ["help_desk",     "🎫 Help Desk",     "ARIA-powered ticket management"],
  ["geo_map",       "🗺 Geo Map",       "Geographic visualization"],
  ["proto_lab",     "🧪 Strategy Lab",  "AI planning & module registry"],
  ["deploy_ops",    "🚀 Deploy Ops",    "Deployment pipeline"],
  ["notifications", "🔔 Notifications", "SMS & email alerts"],
  ["crm",           "📈 CRM",           "Pipeline · contacts · proposals"],
  ["deal_room",     "💼 Deal Room",     "M&A diligence + earn-out modelling"],
];

const DEFAULT_COMPANY_FEATURES = { messenger:true, help_desk:true, geo_map:false, proto_lab:false, deploy_ops:false, notifications:false, crm:false, deal_room:false };

function AdminCompanies({ C, companies, addCompany, deleteCompany, companyConfigs, saveCompanyConfig, showToast, orgSettings }) {
  const [selected,   setSelected]   = useState(null);
  const [newCoName,  setNewCoName]  = useState("");
  const [newCoNotes, setNewCoNotes] = useState("");
  const [showAdd,    setShowAdd]    = useState(false);

  const getConfig = (name) => companyConfigs[name] || { features: { ...DEFAULT_COMPANY_FEATURES }, notes: '' };

  const createCompany = () => {
    if (!newCoName.trim()) return;
    const name = newCoName.trim();
    addCompany(name);
    saveCompanyConfig(name, { features: { ...DEFAULT_COMPANY_FEATURES }, notes: newCoNotes.trim(), createdAt: new Date().toISOString(), org: orgSettings?.org_name || '40Geo' });
    setSelected(name); setShowAdd(false); setNewCoName(""); setNewCoNotes("");
    showToast(`Company "${name}" created`);
  };

  const toggleFeature = (coName, feat) => {
    const cfg = getConfig(coName);
    saveCompanyConfig(coName, { ...cfg, features: { ...cfg.features, [feat]: !cfg.features[feat] } });
  };

  const removeCompany = async (coName) => {
    if (!confirm(`Delete the "${coName}" company?\n\nThis removes its feature flags, notes, and any user assignments to this company (users stay; their company field is cleared). It does NOT delete users or nuggets.\n\nThis is not undoable.`)) return;
    if (deleteCompany) await deleteCompany(coName);
    if (selected === coName) setSelected(null);
    showToast?.(`Deleted "${coName}"`);
  };

  const IB = InputStyle(C);
  const selCfg = selected ? getConfig(selected) : null;

  return (
    <div>
      {/* TEST MODE BANNER */}
      <div style={{ padding:'10px 16px', borderRadius:6, background:'rgba(232,119,34,0.08)', border:'1px solid rgba(232,119,34,0.25)', marginBottom:18, display:'flex', alignItems:'center', gap:10 }}>
        <span style={{ fontSize:16 }}>🧪</span>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color:C.accent }}>TEST MODE — Follows AMP's Org → Company → User Hierarchy</div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.5)', marginTop:2 }}>Company configs and test users are stored locally. When integrating with AMP production, this data migrates to the <code style={{color:'rgba(255,255,255,0.7)'}}>companies</code> and <code style={{color:'rgba(255,255,255,0.7)'}}>company_users</code> tables. Use personal email addresses to test each company's feature scope without affecting real org users.</div>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'240px 1fr', gap:16 }}>
        {/* Company list */}
        <div>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.4)', letterSpacing:'0.1em', textTransform:'uppercase', flex:1 }}>Companies ({companies.length})</span>
            <button onClick={()=>setShowAdd(v=>!v)} style={{ ...BtnPrimary, padding:'4px 8px', fontSize:10 }}><Plus size={11}/></button>
          </div>
          {showAdd && (
            <div style={{ ...Card(C), padding:12, marginBottom:10 }}>
              <input value={newCoName} onChange={e=>setNewCoName(e.target.value)} placeholder="Company name (e.g. ACBL)" style={{ ...IB, marginBottom:8 }} onKeyDown={e=>e.key==='Enter'&&createCompany()}/>
              <textarea value={newCoNotes} onChange={e=>setNewCoNotes(e.target.value)} placeholder="Notes (optional)" rows={2} style={{ ...IB, resize:'none', marginBottom:8 }}/>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={createCompany} disabled={!newCoName.trim()} style={{ ...BtnPrimary, padding:'4px 10px', fontSize:10, flex:1, opacity:newCoName.trim()?1:0.4 }}>Create</button>
                <button onClick={()=>setShowAdd(false)} style={{ ...BtnOutline(C), padding:'4px 8px', fontSize:10 }}>✕</button>
              </div>
            </div>
          )}
          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
            {companies.map(co => {
              const cfg = getConfig(co);
              const isActive = selected === co;
              const featureCount = Object.values(cfg.features).filter(Boolean).length;
              return (
                <div key={co} onClick={()=>setSelected(co)}
                  style={{ padding:'9px 12px', borderRadius:6, cursor:'pointer', background:isActive?'rgba(232,119,34,0.1)':'rgba(255,255,255,0.03)', border:`1px solid ${isActive?C.accent:C.border}`, transition:'all 0.15s' }}
                  onMouseEnter={e=>!isActive&&(e.currentTarget.style.background='rgba(255,255,255,0.05)')}
                  onMouseLeave={e=>!isActive&&(e.currentTarget.style.background='rgba(255,255,255,0.03)')}>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700, color:isActive?C.accent:'rgba(255,255,255,0.85)' }}>{co}</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.35)', marginTop:2 }}>
                    {featureCount} feature{featureCount!==1?'s':''}
                  </div>
                </div>
              );
            })}
            {companies.length===0&&<div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.25)',textAlign:'center',padding:'20px 0'}}>No companies yet</div>}
          </div>
        </div>

        {/* Company detail */}
        {selected && selCfg ? (
          <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
            {/* Header */}
            <div style={{ ...Card(C), padding:'14px 18px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:selCfg.notes?8:0 }}>
                <div style={{ width:36, height:36, borderRadius:8, background:`${C.accent}22`, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'Montserrat',sans-serif", fontSize:16, fontWeight:800, color:C.accent }}>
                  {selected[0].toUpperCase()}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:14, fontWeight:800, color:'#fff' }}>{selected}</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.4)' }}>Under org: {selCfg.org || orgSettings?.org_name || '40Geo'} · Created {selCfg.createdAt ? new Date(selCfg.createdAt).toLocaleDateString() : 'N/A'}</div>
                </div>
                <button onClick={()=>removeCompany(selected)}
                  style={{ background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', color:'#EF4444', borderRadius:5, padding:'5px 12px', fontSize:11, fontFamily:"'Montserrat',sans-serif", fontWeight:700, cursor:'pointer', letterSpacing:'0.06em' }}>
                  Delete company
                </button>
              </div>
              {selCfg.notes && <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.5)', fontStyle:'italic' }}>{selCfg.notes}</div>}
            </div>

            {/* Feature flags */}
            <div style={Card(C)}>
              <Eyebrow>Feature Access</Eyebrow>
              <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.4)', marginBottom:14 }}>
                Control which modules users at <strong>{selected}</strong> can access. Changes take effect immediately for any user assigned to this company.
              </p>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 }}>
                {ALL_FEATURES.map(([key, label, desc]) => {
                  const on = selCfg.features[key] ?? false;
                  return (
                    <div key={key} onClick={()=>toggleFeature(selected,key)}
                      style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 12px', borderRadius:7, cursor:'pointer', background:on?'rgba(232,119,34,0.08)':'rgba(255,255,255,0.03)', border:`1px solid ${on?C.accent:C.border}`, transition:'all 0.2s' }}>
                      <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${on?C.accent:'rgba(255,255,255,0.2)'}`, background:on?C.accent:'transparent', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, transition:'all 0.2s' }}>
                        {on && <CheckCircle size={11} color="#fff"/>}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color:on?'#fff':'rgba(255,255,255,0.6)' }}>{label}</div>
                        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.3)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{desc}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* AMP Integration note */}
            <div style={{ padding:'12px 16px', borderRadius:6, background:'rgba(96,165,250,0.06)', border:'1px solid rgba(96,165,250,0.15)' }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:'rgba(96,165,250,0.8)', marginBottom:4 }}>🔗 AMP Integration Path</div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.4)', lineHeight:1.6 }}>
                When connecting to AMP production: (1) map <strong>{selected}</strong> → AMP <code>companies</code> table entry, (2) link test users to <code>company_users</code> with their Keycloak auth IDs, (3) replace <code>localStorage</code> feature flags with <code>company_settings</code> table rows, (4) comment out this TEST MODE banner. The feature key names (<code>messenger</code>, <code>help_desk</code>, etc.) are designed to match AMP's <code>org_settings.features</code> schema.
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:200, fontFamily:"'Montserrat',sans-serif", fontSize:12, color:'rgba(255,255,255,0.2)' }}>
            Select a company to configure it
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CRM MODULE — Pipeline · Proposals · Contracts · Market Intel
// ═══════════════════════════════════════════════════════════════════

const CRM_STAGES = [
  { id:'lead',        label:'Lead',          color:'#6B7280', prob:10  },
  { id:'qualified',   label:'Qualified',     color:'#60A5FA', prob:25  },
  { id:'proposal',    label:'Proposal Out',  color:'#F59E0B', prob:50  },
  { id:'negotiation', label:'Negotiation',   color:'#E87722', prob:75  },
  { id:'closed_won',  label:'Closed Won',    color:'#22C55E', prob:100 },
  { id:'closed_lost', label:'Closed Lost',   color:'#EF4444', prob:0   },
];
const STAGE_NEXT = { lead:'qualified', qualified:'proposal', proposal:'negotiation', negotiation:'closed_won' };
const fmtDollars = v => v >= 1000000 ? `$${(v/1000000).toFixed(1)}M` : v >= 1000 ? `$${(v/1000).toFixed(0)}K` : `$${v}`;
const daysSince  = d => Math.max(0, Math.floor((Date.now() - new Date(d).getTime()) / 86400000));

const PROPOSAL_SECTION_TEMPLATES = [
  { id:'exec',     title:'Executive Summary',      defaultContent:'We are pleased to present this proposal to [Client]. 40Geo\'s AMP platform delivers operational visibility and response coordination, enabling [key outcome].' },
  { id:'overview', title:'Solution Overview',       defaultContent:'The AMP platform provides the following capabilities tailored to your requirements:\n\n• Real-time monitoring and alerting\n• Incident command and coordination\n• GIS and mapping integration\n• Reporting and compliance dashboards' },
  { id:'tech',     title:'Technical Architecture',  defaultContent:'Built on a cloud-native architecture:\n\n• AWS infrastructure with 99.9% uptime SLA\n• Real-time data processing\n• Secure REST and WebSocket APIs\n• Mobile-responsive web interface\n• Role-based access control' },
  { id:'timeline', title:'Implementation Timeline', defaultContent:'Phase 1 — Foundation (Weeks 1–4): Infrastructure setup, data migration planning\nPhase 2 — Integration (Weeks 5–10): Module configuration, API connections, UAT\nPhase 3 — Launch (Weeks 11–16): User training, go-live, hypercare support' },
  { id:'pricing',  title:'Pricing & ROI',           defaultContent:'Implementation Fee: $[amount]\nAnnual Subscription: $[ARR]\n\nEstimated ROI:\n• Operational efficiency gain: [X]%\n• Incident response time reduction: [X]%\n• Estimated 3-year ROI: [X]%' },
  { id:'sla',      title:'Support & SLA',           defaultContent:'• 99.9% platform uptime guarantee\n• 24/7 critical incident response\n• Business hours support (Mon–Fri 8am–6pm CT)\n• Dedicated Customer Success Manager\n• Quarterly business reviews included' },
  { id:'terms',    title:'Terms & Conditions',      defaultContent:'Payment: Net 30 from contract execution\nTerm: [X] year agreement\nRenewal: Auto-renews unless 60-day written notice provided\nData: Client retains all data rights; 30-day export window on termination' },
];

const INIT_CRM_DEALS = [];
const INIT_CRM_PROPOSALS = [];
const INIT_CRM_CONTRACTS = [];

// IDs that were seeded as demo data — strip them on load
const SEEDED_DEAL_IDS     = new Set(['crm-001','crm-002','crm-003','crm-004','crm-005','crm-006','crm-007','crm-008','crm-009','crm-010']);
const SEEDED_PROPOSAL_IDS = new Set(['prop-001','prop-002','prop-003','prop-004','prop-005','prop-006','prop-007']);
const SEEDED_CONTRACT_IDS = new Set(['con-001','con-002']);

function CRMModule({ currentUser, showToast, onOpenInProposalCoach, orgSettings, users }) {
  // CRM used to persist to `nuggets_crm_<orgId>` in localStorage.
  // It now lives in the DB (crm_deals / crm_proposals / crm_contracts).
  // On mount we:
  //   1) fetch all three tables from the DB → state
  //   2) if the DB is empty AND localStorage has legacy rows, upsert
  //      each row up to the DB with a freshly-issued UUID, then wipe
  //      the localStorage cache.  Idempotent — re-runs are no-ops
  //      because the DB fetch on step (1) will then be non-empty.
  const CRM_KEY = `nuggets_crm_${currentUser?.orgId || 'default'}`;
  const orgId   = currentUser?.orgId;

  const [view,         setView]         = useState('pipeline');
  const [deals,        setDeals]        = useState(INIT_CRM_DEALS);
  const [proposals,    setProposals]    = useState(INIT_CRM_PROPOSALS);
  const [contracts,    setContracts]    = useState(INIT_CRM_CONTRACTS);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showNewDeal,  setShowNewDeal]  = useState(false);
  const [editProposal, setEditProposal] = useState(null);
  const [loadingCrm,   setLoadingCrm]   = useState(true);

  // Fetch → hydrate → maybe migrate localStorage.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      const [dbDeals, dbProps, dbCons] = await Promise.all([
        loadCrmDeals(orgId), loadCrmProposals(orgId), loadCrmContracts(orgId),
      ]);
      if (cancelled) return;

      const anyDb = (dbDeals?.length || 0) + (dbProps?.length || 0) + (dbCons?.length || 0);
      let d = dbDeals || [], p = dbProps || [], c = dbCons || [];

      // One-shot migration from the legacy localStorage cache.  Runs
      // when the DB is completely empty for this org and localStorage
      // has some (non-seed) rows to promote.  New UUIDs are issued so
      // legacy `crm-<timestamp>` strings don't leak into the DB.
      if (anyDb === 0) {
        let legacy = null;
        try { legacy = JSON.parse(localStorage.getItem(CRM_KEY)); } catch {}
        const lDeals = (legacy?.deals     || []).filter(x => !SEEDED_DEAL_IDS.has(x.id));
        const lProps = (legacy?.proposals || []).filter(x => !SEEDED_PROPOSAL_IDS.has(x.id));
        const lCons  = (legacy?.contracts || []).filter(x => !SEEDED_CONTRACT_IDS.has(x.id));
        if (lDeals.length + lProps.length + lCons.length > 0) {
          console.log('[crm] migrating localStorage → DB', { deals: lDeals.length, proposals: lProps.length, contracts: lCons.length });
          const idMap = new Map();  // old-id → new-uuid so FKs re-point
          const now = new Date().toISOString();
          const uuid = () => (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

          // PASS 1: insert deals with FK columns (proposal_id,
          // opportunity_id) FORCED TO NULL.  Legacy localStorage rows
          // often carry `proposalId: 'prop-<timestamp>'` — those are
          // not valid UUIDs and cause the whole INSERT to fail (invalid
          // input syntax for type uuid), silently swallowing the row.
          // Same for legacy opportunityId links.  We re-link in pass 4
          // once proposal UUIDs have been issued.
          let okD = 0;
          for (const old of lDeals) {
            const newId = uuid();
            idMap.set(old.id, newId);
            const scrubbed = { ...old, id: newId, proposalId: null, opportunityId: null, createdAt: old.createdAt || now };
            const upserted = await upsertCrmDeal(scrubbed, orgId);
            if (upserted.row) { d = [upserted.row, ...d]; okD += 1; }
            else console.warn('[crm] deal insert failed', old.title, upserted.error);
          }
          // PASS 2: proposals.  dealId can be looked up in idMap now.
          let okP = 0;
          for (const old of lProps) {
            const newId = uuid();
            idMap.set(old.id, newId);
            const dealId = (old.dealId && idMap.get(old.dealId)) || null;
            const scrubbed = { ...old, id: newId, dealId, createdAt: old.createdAt || now };
            const upserted = await upsertCrmProposal(scrubbed, orgId);
            if (upserted.row) { p = [upserted.row, ...p]; okP += 1; }
            else console.warn('[crm] proposal insert failed', old.title, upserted.error);
          }
          // PASS 3: contracts.  Both dealId + proposalId are mappable.
          let okC = 0;
          for (const old of lCons) {
            const newId = uuid();
            idMap.set(old.id, newId);
            const dealId     = (old.dealId     && idMap.get(old.dealId))     || null;
            const proposalId = (old.proposalId && idMap.get(old.proposalId)) || null;
            const scrubbed = { ...old, id: newId, dealId, proposalId, createdAt: old.createdAt || now };
            const upserted = await upsertCrmContract(scrubbed, orgId);
            if (upserted.row) { c = [upserted.row, ...c]; okC += 1; }
            else console.warn('[crm] contract insert failed', old.title, upserted.error);
          }
          // PASS 4: re-link deal.proposalId now that both sides exist.
          for (const old of lDeals) {
            if (!old.proposalId) continue;
            const newDealId  = idMap.get(old.id);
            const newPropId  = idMap.get(old.proposalId);
            if (!newDealId || !newPropId) continue;
            const stored = d.find(x => x.id === newDealId);
            if (!stored) continue;
            const r = await upsertCrmDeal({ ...stored, proposalId: newPropId }, orgId);
            if (r.row) d = d.map(x => x.id === newDealId ? r.row : x);
          }

          // Wipe the cache — but ONLY if every row landed.  Partial
          // migrations leave the cache in place so the user can retry
          // (delete the DB rows OR ship a fix, then reload).
          const allOk = okD === lDeals.length && okP === lProps.length && okC === lCons.length;
          if (allOk) try { localStorage.removeItem(CRM_KEY); } catch {}
          showToast?.(
            allOk
              ? `✓ CRM restored — ${okD} deals, ${okP} proposals, ${okC} contracts`
              : `⚠ Partial CRM restore — ${okD}/${lDeals.length} deals, ${okP}/${lProps.length} proposals, ${okC}/${lCons.length} contracts.  Check console for details; legacy state kept for retry.`
          );
        }
      }
      setDeals(d);
      setProposals(p);
      setContracts(c);
      setLoadingCrm(false);
    })();
    return () => { cancelled = true; };
  }, [orgId]);

  // ── Persist wrappers ─────────────────────────────────────────
  // Fire-and-forget the DB write so the UI stays snappy; on error
  // we log + toast but leave the optimistic state as-is (the next
  // full reload will re-hydrate from the DB, revealing any drift).
  const setDealsP = (fn) => setDeals(prev => {
    const next = typeof fn === 'function' ? fn(prev) : fn;
    // Diff to find who changed vs who's new (both need upsert).
    const prevById = new Map(prev.map(x => [x.id, x]));
    const changed = next.filter(x => JSON.stringify(x) !== JSON.stringify(prevById.get(x.id)));
    for (const d of changed) {
      upsertCrmDeal(d, orgId).then(r => { if (r.error) showToast?.(`⚠ save failed: ${r.error}`); });
    }
    // Any row that was in prev but not in next got deleted.
    const nextIds = new Set(next.map(x => x.id));
    for (const old of prev) if (!nextIds.has(old.id)) deleteCrmDeal(old.id);
    return next;
  });
  const setProposalsP = (fn) => setProposals(prev => {
    const next = typeof fn === 'function' ? fn(prev) : fn;
    const prevById = new Map(prev.map(x => [x.id, x]));
    const changed = next.filter(x => JSON.stringify(x) !== JSON.stringify(prevById.get(x.id)));
    for (const p of changed) {
      upsertCrmProposal(p, orgId).then(r => { if (r.error) showToast?.(`⚠ save failed: ${r.error}`); });
    }
    const nextIds = new Set(next.map(x => x.id));
    for (const old of prev) if (!nextIds.has(old.id)) deleteCrmProposal(old.id);
    return next;
  });
  const setContractsP = (fn) => setContracts(prev => {
    const next = typeof fn === 'function' ? fn(prev) : fn;
    const prevById = new Map(prev.map(x => [x.id, x]));
    const changed = next.filter(x => JSON.stringify(x) !== JSON.stringify(prevById.get(x.id)));
    for (const c of changed) {
      upsertCrmContract(c, orgId).then(r => { if (r.error) showToast?.(`⚠ save failed: ${r.error}`); });
    }
    const nextIds = new Set(next.map(x => x.id));
    for (const old of prev) if (!nextIds.has(old.id)) deleteCrmContract(old.id);
    return next;
  });

  const uuid = () => (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  const wonDeals      = deals.filter(d => d.stage==='closed_won');
  const lostDeals     = deals.filter(d => d.stage==='closed_lost');
  const openDeals     = deals.filter(d => !['closed_won','closed_lost'].includes(d.stage));
  const pipelineValue = openDeals.reduce((s,d) => s + d.value*(d.probability/100), 0);
  // ARR: one shared rule (components/crm/reportModel.js computeArr) — a won
  // deal's contract if it has one (while in force), else the deal's own ARR.
  const totalArr      = computeArr(deals, contracts).total;
  const winRate       = (wonDeals.length+lostDeals.length)>0 ? Math.round(wonDeals.length/(wonDeals.length+lostDeals.length)*100) : 0;

  const advanceStage = (dealId) => {
    setDealsP(p => p.map(d => {
      if (d.id!==dealId) return d;
      const next = STAGE_NEXT[d.stage]; if (!next) return d;
      return { ...d, stage:next, probability:CRM_STAGES.find(s=>s.id===next)?.prob??d.probability, activities:[...(d.activities||[]),{date:new Date().toISOString().split('T')[0],type:'stage',note:`Advanced to ${CRM_STAGES.find(s=>s.id===next)?.label}`}] };
    }));
    showToast('Deal advanced');
  };

  const addDeal = (deal) => {
    setDealsP(p => [{...deal, id:uuid(), stage:'lead', probability:10, activities:[], createdAt:new Date().toISOString()}, ...p]);
    setShowNewDeal(false); showToast('Deal added to pipeline');
  };

  const addNote = (dealId, note) => {
    setDealsP(p => p.map(d => d.id!==dealId ? d : {...d, activities:[...(d.activities||[]),{date:new Date().toISOString().split('T')[0],type:'note',note}]}));
  };

  const createProposal = (deal) => {
    const prop = { id:uuid(), dealId:deal.id, title:deal.title, client:deal.company, status:'draft', createdAt:new Date().toISOString(), sentAt:null, totalValue:deal.value, sections:PROPOSAL_SECTION_TEMPLATES.map(t=>({...t,enabled:true,content:t.defaultContent.replace('[Client]',deal.company)})) };
    setProposalsP(p => [prop,...p]);
    setDealsP(p => p.map(d => d.id===deal.id ? {...d,proposalId:prop.id} : d));
    setEditProposal(prop); setView('proposals'); showToast('Proposal created');
  };

  // CRM stage → Opportunity stage. CRM uses different vocabulary
  // (lead/qualified/proposal/negotiation/closed_*) so map deliberately.
  const CRM_TO_OPP_STAGE = {
    lead:          'opportunity',
    qualified:     'qualified',
    proposal:      'proposal',
    negotiation:   'proposal',
    closed_won:    'won',
    closed_lost:   'lost',
  };

  // CRM deal → Proposal Coach handoff. Idempotent: a second click on
  // the same deal finds the existing opportunity instead of cloning.
  const handleOpenInProposalCoach = async (deal) => {
    try {
      const existing = await findOpportunityByCrmDealId(currentUser.orgId, deal.id);
      if (existing) {
        onOpenInProposalCoach?.(existing.id);
        showToast?.(`Opened "${existing.name}" in Proposal Coach`);
        return;
      }
      // No need to clone activity notes into opportunity_notes — the
      // Proposal Coach reads the CRM deal's activity log live via
      // the crm_deal_id link, so the coach sees fresh data even as
      // new touchpoints are logged in the CRM.

      // Auto-link the primary contact: if the CRM deal has a
      // contactEmail, look up the matching contact row (rich
      // fields — title, phone, mobile, address) and link it as
      // primary_contact_id on the new opportunity. Saves the user
      // a manual picker step and means the AI sees the full
      // contact record from the first draft.
      let autoContactId = null;
      let autoContactName = null;
      if (deal.contactEmail) {
        const matched = await findContactByEmail(currentUser.orgId, deal.contactEmail);
        if (matched) {
          autoContactId   = matched.id;
          autoContactName = matched.name || [matched.first_name, matched.last_name].filter(Boolean).join(' ') || matched.email;
        }
      }

      // Build a rich scope_summary from every CRM field the coach's
      // AI can actually use — the more concrete context we hand off,
      // the better the first section drafts read.  Blank fields are
      // filtered out; contact info gets appended only if there was no
      // matched contact row (rich matched contacts already surface in
      // the opportunity's PrimaryContactCard).
      const scopeParts = [];
      if (deal.notes)     scopeParts.push(deal.notes);
      if (deal.industry)  scopeParts.push(`Industry: ${deal.industry}`);
      if (deal.source)    scopeParts.push(`Source: ${deal.source}`);
      if (deal.arr)       scopeParts.push(`Target ARR: $${Number(deal.arr).toLocaleString()} ${deal.currency || 'USD'}`);
      // If we didn't find a matched contact row, at least drop the CRM
      // contact details into scope_summary so the AI sees them.
      if (!autoContactId && (deal.contact || deal.contactEmail)) {
        const c = [deal.contact, deal.contactEmail].filter(Boolean).join(' · ');
        if (c) scopeParts.push(`Contact: ${c}`);
      }
      // Latest activity note (if any) — gives the AI the most recent
      // account context without pulling the whole activities array.
      const latestNote = (deal.activities || [])
        .filter(a => a?.note)
        .slice(-1)[0];
      if (latestNote) scopeParts.push(`Latest touchpoint (${latestNote.date}): ${latestNote.note}`);

      const result = await createOpportunity(currentUser.orgId, currentUser.id, {
        name:            deal.title || `${deal.company || 'New'} opportunity`,
        client_name:     deal.company || null,
        stage:           CRM_TO_OPP_STAGE[deal.stage] || 'opportunity',
        source:          'existing_client',
        estimated_value: deal.value || null,
        currency:        deal.currency || 'USD',
        scope_summary:   scopeParts.join('\n\n') || null,
        crm_deal_id:     deal.id,
        primary_contact_id: autoContactId,
      });
      if (result.error || !result.row) {
        showToast?.(`❌ Could not open in Proposal Coach: ${result.error || 'unknown error'}`);
        return;
      }
      seedDefaultOutboundArtifacts(result.row.id);
      seedDefaultProposalSections(result.row.id);

      // Backfill the CRM deal with the new opportunity_id so the
      // deal card can show "Open opportunity" next time.  Fire and
      // forget — the coach handoff shouldn't wait on this write.
      setDealsP(prev => prev.map(x => x.id === deal.id
        ? { ...x, opportunityId: result.row.id }
        : x));

      onOpenInProposalCoach?.(result.row.id);
      const contactNote = autoContactName ? ` · linked contact: ${autoContactName}` : '';
      showToast?.(`✓ Created "${result.row.name}" in Proposal Coach${contactNote}`);
    } catch (err) {
      console.error('[crm → proposal coach] failed:', err);
      showToast?.(`❌ Handoff failed: ${err.message || err}`);
    }
  };

  // Contract mirrored from a won deal. Term starts on the won date (not
  // "today") so backdated wins get the right start / renewal dates.
  const buildContract = (deal) => {
    const start = deal.wonDate || new Date().toISOString().split('T')[0];
    const t0 = new Date(start).getTime();
    return { id:uuid(), dealId:deal.id, proposalId:deal.proposalId||null, title:deal.title, client:deal.company, contact:deal.contact, value:deal.value, arr:deal.arr||0, startDate:start, endDate:new Date(t0+2*365*86400000).toISOString().split('T')[0], status:'active', renewalDate:new Date(t0+18*30*86400000).toISOString().split('T')[0], healthScore:85, supportTickets:0, supportHoursUsed:0, notes:'' };
  };

  const markWon = (dealId) => {
    const deal = deals.find(d=>d.id===dealId); if (!deal) return;
    const today = new Date().toISOString().split('T')[0];
    // Keep the expected close date as forecast history; the actual close
    // goes in wonDate. Both are editable afterwards from the deal panel.
    const won = { ...deal, stage:'closed_won', probability:100, wonDate:today, closeDate:deal.closeDate||today };
    setDealsP(p => p.map(d => d.id!==dealId ? d : {...won,activities:[...(d.activities||[]),{date:today,type:'won',note:'Marked Closed Won'}]}));
    if (!contracts.some(c => c.dealId === dealId)) setContractsP(p => [buildContract(won),...p]);
    setSelectedDeal(null); showToast('🎉 Deal won! Contract created.');
  };

  // Edit any deal field from the panel. For won deals the linked contract
  // is kept in step (value, ARR, term start = won date) and created if the
  // deal was imported as closed-won without one — that is what makes the
  // ARR KPI reflect wins that never went through "Mark Won" here.
  const updateDeal = (dealId, patch) => {
    const deal = deals.find(d=>d.id===dealId); if (!deal) return;
    const today  = new Date().toISOString().split('T')[0];
    const merged = { ...deal, ...patch };
    const changed = [];
    if ('wonDate'   in patch && patch.wonDate   !== deal.wonDate)   changed.push(`won date → ${patch.wonDate || '—'}`);
    if ('closeDate' in patch && patch.closeDate !== deal.closeDate) changed.push(`expected close → ${patch.closeDate || '—'}`);
    if ('value'     in patch && patch.value     !== deal.value)     changed.push(`value → ${fmtDollars(patch.value)}`);
    if ('arr'       in patch && patch.arr       !== deal.arr)       changed.push(`ARR → ${fmtDollars(patch.arr)}/yr`);
    if (changed.length) merged.activities = [...(deal.activities||[]), { date:today, type:'note', note:`Updated: ${changed.join(', ')}` }];
    setDealsP(p => p.map(d => d.id!==dealId ? d : merged));
    if (merged.stage === 'closed_won') {
      const existing = contracts.find(c => c.dealId === dealId);
      if (existing) {
        setContractsP(p => p.map(c => c.id!==existing.id ? c : { ...c, title:merged.title, client:merged.company, contact:merged.contact, value:merged.value, arr:merged.arr||0, startDate:merged.wonDate||c.startDate }));
      } else {
        setContractsP(p => [buildContract(merged),...p]);
      }
    }
    showToast('Deal updated');
  };

  const markLost = (dealId, reason) => {
    setDealsP(p => p.map(d => d.id!==dealId ? d : {...d,stage:'closed_lost',probability:0,lostReason:reason,activities:[...(d.activities||[]),{date:new Date().toISOString().split('T')[0],type:'lost',note:`Lost: ${reason}`}]}));
    setSelectedDeal(null); showToast('Deal marked as lost');
  };

  const CRM_NAV = [{id:'pipeline',label:'Pipeline'},{id:'proposals',label:'Proposals'},{id:'contracts',label:'Contracts'},{id:'contacts',label:'Contacts'},{id:'intel',label:'Market Intel'},{id:'analytics',label:'Analytics'},{id:'reports',label:'Reports'}];

  return (
    <div style={{padding:'24px 28px',minHeight:'100vh'}}>
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontFamily:"'Montserrat',sans-serif",fontWeight:900,fontSize:26,color:'#fff',margin:0,letterSpacing:'-0.02em'}}>CRM</h1>
          <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.35)',margin:'4px 0 0',letterSpacing:'0.04em'}}>Pipeline · Proposals · Contracts · Market Intelligence</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            title="Paste a legacy CRM localStorage JSON (from the old Vercel site) to import into the DB."
            onClick={async () => {
              const raw = prompt(
                'Paste the CRM localStorage JSON (from the old site).\n\n' +
                'DevTools → Console → run:\n' +
                "  copy(localStorage.getItem('nuggets_crm_" + (orgId || '<orgId>') + "'))\n" +
                'then paste here.'
              );
              if (!raw) return;
              let parsed;
              try { parsed = JSON.parse(raw); } catch { showToast('❌ Not valid JSON'); return; }
              const inDeals = (parsed?.deals || []).filter(x => !SEEDED_DEAL_IDS.has(x.id));
              const inProps = (parsed?.proposals || []).filter(x => !SEEDED_PROPOSAL_IDS.has(x.id));
              const inCons  = (parsed?.contracts || []).filter(x => !SEEDED_CONTRACT_IDS.has(x.id));
              if (!inDeals.length && !inProps.length && !inCons.length) { showToast('Nothing to import'); return; }
              const uuid = () => (crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);
              const now  = new Date().toISOString();
              const idMap = new Map();
              let okD = 0, okP = 0, okC = 0;
              let addedDeals = [], addedProps = [], addedCons = [];
              // Pass 1: deals, FK columns nulled.
              for (const old of inDeals) {
                const newId = uuid();
                idMap.set(old.id, newId);
                const r = await upsertCrmDeal({ ...old, id: newId, proposalId: null, opportunityId: null, createdAt: old.createdAt || now }, orgId);
                if (r.row) { addedDeals.push(r.row); okD += 1; }
                else console.warn('[crm-import] deal failed', old.title, r.error);
              }
              // Pass 2: proposals with mapped dealId.
              for (const old of inProps) {
                const newId = uuid();
                idMap.set(old.id, newId);
                const dealId = (old.dealId && idMap.get(old.dealId)) || null;
                const r = await upsertCrmProposal({ ...old, id: newId, dealId, createdAt: old.createdAt || now }, orgId);
                if (r.row) { addedProps.push(r.row); okP += 1; }
                else console.warn('[crm-import] proposal failed', old.title, r.error);
              }
              // Pass 3: contracts with both mapped.
              for (const old of inCons) {
                const newId = uuid();
                idMap.set(old.id, newId);
                const dealId     = (old.dealId     && idMap.get(old.dealId))     || null;
                const proposalId = (old.proposalId && idMap.get(old.proposalId)) || null;
                const r = await upsertCrmContract({ ...old, id: newId, dealId, proposalId, createdAt: old.createdAt || now }, orgId);
                if (r.row) { addedCons.push(r.row); okC += 1; }
                else console.warn('[crm-import] contract failed', old.title, r.error);
              }
              // Pass 4: back-link deal.proposalId.
              for (const old of inDeals) {
                if (!old.proposalId) continue;
                const newDealId = idMap.get(old.id);
                const newPropId = idMap.get(old.proposalId);
                if (!newDealId || !newPropId) continue;
                const stored = addedDeals.find(x => x.id === newDealId);
                if (!stored) continue;
                const r = await upsertCrmDeal({ ...stored, proposalId: newPropId }, orgId);
                if (r.row) addedDeals = addedDeals.map(x => x.id === newDealId ? r.row : x);
              }
              // Merge into state without wiping what's already there.
              setDeals(prev => [...addedDeals, ...prev.filter(x => !addedDeals.some(y => y.id === x.id))]);
              setProposals(prev => [...addedProps, ...prev.filter(x => !addedProps.some(y => y.id === x.id))]);
              setContracts(prev => [...addedCons, ...prev.filter(x => !addedCons.some(y => y.id === x.id))]);
              showToast?.(`Import complete — ${okD}/${inDeals.length} deals · ${okP}/${inProps.length} proposals · ${okC}/${inCons.length} contracts. Check console for any failures.`);
            }}
            style={{
              background: 'transparent', color: 'rgba(255,255,255,0.6)', border: '1px solid rgba(255,255,255,0.14)',
              padding: '9px 12px', borderRadius: 6, cursor: 'pointer',
              fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
            Import legacy
          </button>
          <button onClick={()=>setShowNewDeal(true)} style={{...BtnPrimary,display:'flex',alignItems:'center',gap:6,padding:'9px 16px',fontSize:12}}><Plus size={14}/> New Deal</button>
        </div>
      </div>

      {/* KPI strip */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:24}}>
        {[
          {label:'Pipeline Value', value:fmtDollars(pipelineValue), sub:'weighted forecast'},
          {label:'Total ARR',      value:fmtDollars(totalArr),       sub:'active contracts'},
          {label:'Open Deals',     value:openDeals.length,           sub:'in progress'},
          {label:'Win Rate',       value:`${winRate}%`,              sub:'closed deals'},
          {label:'Avg Deal Size',  value:wonDeals.length ? fmtDollars(Math.round(wonDeals.reduce((s,d)=>s+d.value,0)/wonDeals.length)) : '—', sub:'closed won'},
        ].map(k=>(
          <div key={k.label} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:8,padding:'14px 16px'}}>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.1em',textTransform:'uppercase',color:'rgba(255,255,255,0.35)',marginBottom:6}}>{k.label}</div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontWeight:900,fontSize:22,color:'#E87722',lineHeight:1}}>{k.value}</div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.25)',marginTop:4}}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* Sub-nav */}
      <div style={{display:'flex',gap:4,marginBottom:24,background:'rgba(255,255,255,0.03)',padding:4,borderRadius:8,width:'fit-content'}}>
        {CRM_NAV.map(n=>(
          <button key={n.id} onClick={()=>setView(n.id)} style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.04em',padding:'6px 14px',borderRadius:6,border:'none',cursor:'pointer',background:view===n.id?'#E87722':'transparent',color:view===n.id?'#fff':'rgba(255,255,255,0.4)',transition:'all 0.15s'}}>
            {n.label}
          </button>
        ))}
      </div>

      {view==='pipeline'  && <CRMPipeline  deals={deals} onSelect={setSelectedDeal} onAdvance={advanceStage} />}
      {view==='proposals' && <CRMProposals proposals={proposals} deals={deals} onEdit={p=>{setEditProposal(p);}} editProposal={editProposal} onUpdate={p=>setProposalsP(prev=>prev.map(q=>q.id===p.id?p:q))} onClose={()=>setEditProposal(null)} showToast={showToast} />}
      {view==='contracts' && <CRMContracts contracts={contracts} deals={deals} showToast={showToast} />}
      {view==='contacts'  && <CRMContacts  currentUser={currentUser} showToast={showToast} />}
      {view==='intel'     && <CRMIntel     deals={deals} />}
      {view==='analytics' && <CRMAnalytics deals={deals} contracts={contracts} />}
      {view==='reports'   && <CRMReports   deals={deals} contracts={contracts} stages={CRM_STAGES} currentUser={currentUser} orgSettings={orgSettings} users={users} showToast={showToast} />}

      {selectedDeal && <CRMDealPanel deal={deals.find(d=>d.id===selectedDeal.id)||selectedDeal} proposals={proposals} contracts={contracts} users={users||[]} onClose={()=>setSelectedDeal(null)} onAdvance={advanceStage} onWon={markWon} onLost={markLost} onAddNote={addNote} onUpdate={updateDeal} onCreateProposal={createProposal} onOpenInProposalCoach={handleOpenInProposalCoach} showToast={showToast} />}
      {showNewDeal  && <CRMNewDealModal onAdd={addDeal} onClose={()=>setShowNewDeal(false)} />}
    </div>
  );
}

function CRMPipeline({ deals, onSelect, onAdvance }) {
  const pipelineStages = CRM_STAGES.filter(s=>s.id!=='closed_lost');
  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,alignItems:'start'}}>
      {pipelineStages.map(stage=>{
        const sd = deals.filter(d=>d.stage===stage.id);
        const sv = sd.reduce((s,d)=>s+d.value,0);
        return (
          <div key={stage.id}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10,padding:'8px 10px',background:`${stage.color}14`,borderRadius:6,border:`1px solid ${stage.color}22`}}>
              <div>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:stage.color}}>{stage.label}</div>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.4)',marginTop:2}}>{fmtDollars(sv)}</div>
              </div>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:700,color:'rgba(255,255,255,0.4)',background:'rgba(255,255,255,0.06)',borderRadius:'50%',width:22,height:22,display:'flex',alignItems:'center',justifyContent:'center'}}>{sd.length}</span>
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {sd.map(deal=>(
                <div key={deal.id} onClick={()=>onSelect(deal)}
                  style={{background:'rgba(255,255,255,0.04)',border:`1px solid rgba(255,255,255,0.07)`,borderLeft:`3px solid ${stage.color}`,borderRadius:7,padding:'12px 14px',cursor:'pointer',transition:'all 0.15s'}}
                  onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.07)';e.currentTarget.style.transform='translateY(-1px)';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.04)';e.currentTarget.style.transform='none';}}>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:'#fff',marginBottom:2,lineHeight:1.3}}>{deal.company}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.38)',marginBottom:8,lineHeight:1.4}}>{deal.title}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:18,fontWeight:900,color:'#E87722',marginBottom:8}}>{fmtDollars(deal.value)}</div>
                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)'}}>{deal.contact}</span>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.25)',background:'rgba(255,255,255,0.05)',padding:'2px 6px',borderRadius:3}}>{daysSince(deal.createdAt)}d</span>
                  </div>
                  {STAGE_NEXT[deal.stage] && (
                    <button onClick={e=>{e.stopPropagation();onAdvance(deal.id);}} style={{marginTop:10,width:'100%',background:`${stage.color}18`,border:`1px solid ${stage.color}33`,borderRadius:4,padding:'5px 0',fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:stage.color,cursor:'pointer',letterSpacing:'0.04em',textTransform:'uppercase'}}>
                      Advance →
                    </button>
                  )}
                  {deal.stage==='closed_won' && <div style={{marginTop:8,fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'#22C55E',textAlign:'center'}}>✓ Won {deal.wonDate || deal.closeDate || ''}{deal.arr>0 ? ` · ${fmtDollars(deal.arr)}/yr` : ''}</div>}
                </div>
              ))}
              {sd.length===0 && <div style={{background:'rgba(255,255,255,0.02)',border:'1px dashed rgba(255,255,255,0.06)',borderRadius:7,padding:'20px 14px',textAlign:'center',fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.15)'}}>No deals</div>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CRMDealPanel({ deal, contracts, users = [], onClose, onAdvance, onWon, onLost, onAddNote, onUpdate, onCreateProposal, onOpenInProposalCoach, showToast }) {
  const [noteText,  setNoteText]  = useState('');
  const [lostReason,setLostReason]= useState('');
  const [showLost,  setShowLost]  = useState(false);
  const [editing,   setEditing]   = useState(false);
  const [form,      setForm]      = useState(null);
  const IB = { background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'8px 12px', color:'#fff', fontFamily:"'Montserrat',sans-serif", fontSize:13, outline:'none', boxSizing:'border-box' };
  const EL = { fontFamily:"'Montserrat',sans-serif", fontSize:9, letterSpacing:'0.08em', textTransform:'uppercase', color:'rgba(255,255,255,0.35)', display:'block', marginBottom:4 };
  const stage    = CRM_STAGES.find(s=>s.id===deal.stage);
  const contract = contracts.find(c=>c.dealId===deal.id) || contracts.find(c=>c.id===deal.contractId);
  const isOpen   = !['closed_won','closed_lost'].includes(deal.stage);
  const isWon    = deal.stage==='closed_won';
  const ACT_ICON = {call:'📞',email:'📧',demo:'🖥',proposal:'📄',won:'🏆',lost:'❌',note:'📝',stage:'→'};
  const startEdit = () => {
    setForm({
      title:deal.title||'', company:deal.company||'', contact:deal.contact||'', contactEmail:deal.contactEmail||'',
      value:deal.value??'', arr:deal.arr??'', probability:deal.probability??10,
      closeDate:deal.closeDate||'', wonDate:deal.wonDate||deal.closeDate||'',
      ownerUserId:deal.ownerUserId||'', industry:deal.industry||'', source:deal.source||'', notes:deal.notes||'',
    });
    setEditing(true);
  };
  const setF = (k,v) => setForm(p=>({...p,[k]:v}));
  const saveEdit = () => {
    if (!form.title.trim() || !form.company.trim()) { showToast('Title and company are required'); return; }
    const patch = {
      title:form.title.trim(), company:form.company.trim(), contact:form.contact.trim(), contactEmail:form.contactEmail.trim(),
      value:parseFloat(form.value)||0, arr:parseFloat(form.arr)||0,
      closeDate:form.closeDate||null,
      ownerUserId:form.ownerUserId||null, industry:form.industry.trim(), source:form.source.trim(), notes:form.notes,
    };
    if (isOpen) patch.probability = Math.min(100, Math.max(0, Math.round(Number(form.probability)||0)));
    if (isWon)  patch.wonDate = form.wonDate || null;
    onUpdate?.(deal.id, patch);
    setEditing(false);
  };
  return (
    <div style={{position:'fixed',inset:0,zIndex:200,display:'flex'}}>
      <div onClick={onClose} style={{flex:1,background:'rgba(0,0,0,0.5)'}}/>
      <div style={{width:480,background:'#0D1B2A',borderLeft:'1px solid rgba(255,255,255,0.1)',overflowY:'auto',display:'flex',flexDirection:'column'}}>
        <div style={{padding:'20px 24px',borderBottom:'1px solid rgba(255,255,255,0.08)',background:'rgba(255,255,255,0.02)'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',color:stage?.color,background:`${stage?.color}18`,padding:'3px 10px',borderRadius:4}}>{stage?.label}</span>
            <div style={{display:'flex',gap:10,alignItems:'center'}}>
              {onUpdate && !editing && (
                <button onClick={startEdit} title="Edit deal details, dates, value and ARR"
                  style={{background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:5,padding:'4px 10px',color:'rgba(255,255,255,0.7)',cursor:'pointer',fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,letterSpacing:'0.06em'}}>✎ Edit</button>
              )}
              <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',cursor:'pointer',fontSize:20,lineHeight:1}}>×</button>
            </div>
          </div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:18,fontWeight:900,color:'#fff',marginBottom:3}}>{deal.company}</div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.4)',marginBottom:10}}>{deal.title}</div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:28,fontWeight:900,color:'#E87722'}}>{fmtDollars(deal.value)}</div>
          {deal.arr>0 && <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.35)',marginTop:2}}>{fmtDollars(deal.arr)}/yr ARR</div>}
        </div>

        <div style={{padding:'20px 24px',flex:1,overflowY:'auto'}}>
          <div style={{background:'rgba(255,255,255,0.03)',borderRadius:8,padding:'12px 14px',marginBottom:14}}>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.1em',textTransform:'uppercase',color:'rgba(255,255,255,0.3)',marginBottom:6}}>Contact</div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:'#fff'}}>{deal.contact}</div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.4)',marginTop:2}}>{deal.contactEmail}</div>
          </div>

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:14}}>
            {[
              {label:'Industry',value:deal.industry||'—'},{label:'Source',value:deal.source||'—'},
              {label:'Probability',value:`${deal.probability}%`},
              {label:'Owner',value:users.find(u=>u.id===deal.ownerUserId)?.name||'Unassigned'},
              {label:'Expected Close',value:deal.closeDate||'—'},
              ...(isWon ? [{label:'Won Date',value:deal.wonDate||deal.closeDate||'—',color:'#22C55E'}] : []),
            ].map(m=>(
              <div key={m.label} style={{background:'rgba(255,255,255,0.03)',borderRadius:6,padding:'10px 12px'}}>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.3)',marginBottom:3}}>{m.label}</div>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:600,color:m.color||'rgba(255,255,255,0.8)'}}>{m.value}</div>
              </div>
            ))}
          </div>

          {editing && form && (
            <div style={{background:'rgba(232,119,34,0.06)',border:'1px solid rgba(232,119,34,0.25)',borderRadius:8,padding:'14px 16px',marginBottom:14}}>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.1em',textTransform:'uppercase',color:'#E87722',marginBottom:10,fontWeight:700}}>Edit deal</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                <div style={{gridColumn:'span 2'}}><label style={EL}>Deal title</label><input value={form.title} onChange={e=>setF('title',e.target.value)} style={{...IB,width:'100%',fontSize:12}}/></div>
                <div><label style={EL}>Company</label><input value={form.company} onChange={e=>setF('company',e.target.value)} style={{...IB,width:'100%',fontSize:12}}/></div>
                <div><label style={EL}>Owner</label>
                  <select value={form.ownerUserId} onChange={e=>setF('ownerUserId',e.target.value)} style={{...IB,width:'100%',fontSize:12,color:'rgba(255,255,255,0.85)'}}>
                    <option value="" style={{color:'#000'}}>Unassigned</option>
                    {users.map(u=><option key={u.id} value={u.id} style={{color:'#000'}}>{u.name}</option>)}
                  </select></div>
                <div><label style={EL}>Contact</label><input value={form.contact} onChange={e=>setF('contact',e.target.value)} style={{...IB,width:'100%',fontSize:12}}/></div>
                <div><label style={EL}>Contact email</label><input value={form.contactEmail} onChange={e=>setF('contactEmail',e.target.value)} style={{...IB,width:'100%',fontSize:12}}/></div>
                <div><label style={EL}>Deal value ($)</label><input type="number" min={0} value={form.value} onChange={e=>setF('value',e.target.value)} style={{...IB,width:'100%',fontSize:12}}/></div>
                <div><label style={EL}>ARR ($ / yr)</label><input type="number" min={0} value={form.arr} onChange={e=>setF('arr',e.target.value)} placeholder="Recurring per year" style={{...IB,width:'100%',fontSize:12}}/></div>
                <div><label style={EL}>Expected close</label><input type="date" value={form.closeDate} onChange={e=>setF('closeDate',e.target.value)} style={{...IB,width:'100%',fontSize:12,colorScheme:'dark'}}/></div>
                {isWon
                  ? <div><label style={{...EL,color:'#22C55E'}}>Won date</label><input type="date" value={form.wonDate} onChange={e=>setF('wonDate',e.target.value)} style={{...IB,width:'100%',fontSize:12,colorScheme:'dark',borderColor:'rgba(34,197,94,0.4)'}}/></div>
                  : isOpen
                    ? <div><label style={EL}>Probability (%)</label><input type="number" min={0} max={100} value={form.probability} onChange={e=>setF('probability',e.target.value)} style={{...IB,width:'100%',fontSize:12}}/></div>
                    : <div/>}
                <div><label style={EL}>Industry</label><input value={form.industry} onChange={e=>setF('industry',e.target.value)} style={{...IB,width:'100%',fontSize:12}}/></div>
                <div><label style={EL}>Source</label><input value={form.source} onChange={e=>setF('source',e.target.value)} style={{...IB,width:'100%',fontSize:12}}/></div>
                <div style={{gridColumn:'span 2'}}><label style={EL}>Notes</label><textarea rows={3} value={form.notes} onChange={e=>setF('notes',e.target.value)} style={{...IB,width:'100%',fontSize:12,resize:'vertical',lineHeight:1.5}}/></div>
              </div>
              {isWon && <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.4)',marginTop:10,lineHeight:1.5}}>Saving a won deal also updates its contract: value, ARR and the term start (= won date). If no contract exists yet, one is created so ARR counts.</div>}
              <div style={{display:'flex',gap:8,marginTop:12}}>
                <button onClick={saveEdit} style={{...BtnPrimary,fontSize:11,padding:'7px 16px'}}>Save changes</button>
                <button onClick={()=>setEditing(false)} style={{background:'none',border:'1px solid rgba(255,255,255,0.12)',borderRadius:6,padding:'7px 14px',color:'rgba(255,255,255,0.5)',cursor:'pointer',fontFamily:"'Montserrat',sans-serif",fontSize:11}}>Cancel</button>
              </div>
            </div>
          )}

          {deal.notes && <div style={{background:'rgba(232,119,34,0.06)',border:'1px solid rgba(232,119,34,0.15)',borderRadius:8,padding:'12px 14px',marginBottom:14,fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.6)',lineHeight:1.6}}>{deal.notes}</div>}
          {deal.lostReason && <div style={{background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:8,padding:'12px 14px',marginBottom:14}}><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(239,68,68,0.7)',marginBottom:4}}>Lost Reason</div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.6)'}}>{deal.lostReason}</div></div>}
          {contract && <div style={{background:'rgba(34,197,94,0.08)',border:'1px solid rgba(34,197,94,0.2)',borderRadius:8,padding:'12px 14px',marginBottom:14}}><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(34,197,94,0.7)',marginBottom:4}}>Active Contract</div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:700,color:'#fff'}}>{fmtDollars(contract.arr)}/yr · Expires {contract.endDate}</div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.4)',marginTop:2}}>Health {contract.healthScore}/100 · {contract.supportTickets} support tickets</div></div>}

          {isOpen && (
            <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
              {STAGE_NEXT[deal.stage] && <button onClick={()=>onAdvance(deal.id)} style={{...BtnPrimary,fontSize:11,padding:'7px 14px'}}>Advance → {CRM_STAGES.find(s=>s.id===STAGE_NEXT[deal.stage])?.label}</button>}
              {onOpenInProposalCoach && (
                <button onClick={() => onOpenInProposalCoach(deal)}
                  title="Create or open the matching Proposal Coach opportunity for this deal"
                  style={{background:'rgba(167,139,250,0.15)',border:'1px solid rgba(167,139,250,0.4)',borderRadius:6,padding:'7px 14px',fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#A78BFA',cursor:'pointer'}}>
                  🪈 Open in Proposal Coach
                </button>
              )}
              {!deal.proposalId && <button onClick={()=>onCreateProposal(deal)} style={{background:'rgba(245,158,11,0.15)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:6,padding:'7px 14px',fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#F59E0B',cursor:'pointer'}}>📄 Create Proposal</button>}
              <button onClick={()=>onWon(deal.id)} style={{background:'rgba(34,197,94,0.15)',border:'1px solid rgba(34,197,94,0.3)',borderRadius:6,padding:'7px 14px',fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#22C55E',cursor:'pointer'}}>🏆 Mark Won</button>
              {!showLost
                ? <button onClick={()=>setShowLost(true)} style={{background:'rgba(239,68,68,0.1)',border:'1px solid rgba(239,68,68,0.2)',borderRadius:6,padding:'7px 14px',fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#EF4444',cursor:'pointer'}}>Mark Lost</button>
                : <div style={{width:'100%',display:'flex',gap:6}}><input value={lostReason} onChange={e=>setLostReason(e.target.value)} placeholder="Reason for loss..." style={{...IB,flex:1,fontSize:11}}/><button onClick={()=>{onLost(deal.id,lostReason);setShowLost(false);}} style={{background:'rgba(239,68,68,0.2)',border:'1px solid rgba(239,68,68,0.3)',borderRadius:6,padding:'7px 12px',fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'#EF4444',cursor:'pointer',fontWeight:700}}>Confirm</button></div>}
            </div>
          )}

          <div style={{display:'flex',gap:8,marginBottom:20}}>
            <input value={noteText} onChange={e=>setNoteText(e.target.value)} placeholder="Add a note or call log..." style={{...IB,flex:1,fontSize:11}} onKeyDown={e=>{if(e.key==='Enter'&&noteText.trim()){onAddNote(deal.id,noteText.trim());setNoteText('');showToast('Note added');}}}/>
            <button onClick={()=>{if(noteText.trim()){onAddNote(deal.id,noteText.trim());setNoteText('');showToast('Note added');}}} style={{background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:6,padding:'7px 12px',color:'rgba(255,255,255,0.6)',cursor:'pointer',fontSize:11}}>Add</button>
          </div>

          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.1em',textTransform:'uppercase',color:'rgba(255,255,255,0.3)',marginBottom:10}}>Activity</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {[...(deal.activities||[])].reverse().map((a,i)=>(
              <div key={i} style={{display:'flex',gap:10,alignItems:'flex-start'}}>
                <span style={{fontSize:14,lineHeight:1.4,flexShrink:0}}>{ACT_ICON[a.type]||'•'}</span>
                <div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.7)',lineHeight:1.4}}>{a.note}</div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.25)',marginTop:2}}>{a.date}</div></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CRMNewDealModal({ onAdd, onClose }) {
  const [form, setForm] = useState({title:'',company:'',contact:'',contactEmail:'',value:'',arr:'',industry:'Maritime',source:'Inbound',notes:''});
  const set = (k,v) => setForm(p=>({...p,[k]:v}));
  const IB = { width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'8px 12px', color:'#fff', fontFamily:"'Montserrat',sans-serif", fontSize:13, outline:'none', boxSizing:'border-box' };
  const valid = form.title.trim() && form.company.trim() && form.value;
  const INDS = ['Maritime','Environmental','Energy','Government','Industrial','Municipal','Healthcare','Technology'];
  const SRCS = ['Inbound','Outbound','Referral','Conference','Partner','Renewal'];
  return (
    <div style={{position:'fixed',inset:0,zIndex:300,display:'flex',alignItems:'center',justifyContent:'center',background:'rgba(0,0,0,0.6)'}}>
      <div style={{background:'#0D1B2A',border:'1px solid rgba(255,255,255,0.1)',borderRadius:12,padding:28,width:480,maxHeight:'85vh',overflowY:'auto'}}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:20}}>
          <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:900,color:'#fff'}}>New Deal</span>
          <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',cursor:'pointer',fontSize:20}}>×</button>
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          {[['title','Deal Title','e.g. AIS Tracking Platform'],['company','Company','e.g. Port of Houston'],['contact','Contact Name','e.g. Sarah Mitchell'],['contactEmail','Contact Email','e.g. sarah@port.com']].map(([k,label,ph])=>(
            <div key={k}>
              <label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.35)',display:'block',marginBottom:4}}>{label}</label>
              <input value={form[k]} onChange={e=>set(k,e.target.value)} placeholder={ph} style={IB}/>
            </div>
          ))}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.35)',display:'block',marginBottom:4}}>Deal Value ($)</label><input type="number" value={form.value} onChange={e=>set('value',e.target.value)} placeholder="285000" style={IB}/></div>
            <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.35)',display:'block',marginBottom:4}}>ARR ($/yr)</label><input type="number" value={form.arr} onChange={e=>set('arr',e.target.value)} placeholder="95000" style={IB}/></div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
            <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.35)',display:'block',marginBottom:4}}>Industry</label><select value={form.industry} onChange={e=>set('industry',e.target.value)} style={{...IB,color:'rgba(255,255,255,0.8)'}}>{INDS.map(i=><option key={i}>{i}</option>)}</select></div>
            <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.35)',display:'block',marginBottom:4}}>Source</label><select value={form.source} onChange={e=>set('source',e.target.value)} style={{...IB,color:'rgba(255,255,255,0.8)'}}>{SRCS.map(s=><option key={s}>{s}</option>)}</select></div>
          </div>
          <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.08em',textTransform:'uppercase',color:'rgba(255,255,255,0.35)',display:'block',marginBottom:4}}>Notes</label><textarea value={form.notes} onChange={e=>set('notes',e.target.value)} placeholder="Initial notes about this opportunity..." rows={3} style={{...IB,resize:'vertical',lineHeight:1.5}}/></div>
          <button onClick={()=>onAdd({...form,value:parseFloat(form.value)||0,arr:parseFloat(form.arr)||0})} disabled={!valid} style={{...BtnPrimary,opacity:valid?1:0.4,marginTop:4}}>Add Deal</button>
        </div>
      </div>
    </div>
  );
}

function CRMProposals({ proposals, onEdit, editProposal, onUpdate, onClose, showToast }) {
  const [activeSection, setActiveSection] = useState(null);
  const STATUS_COLOR = {draft:'#6B7280',sent:'#F59E0B',accepted:'#22C55E',rejected:'#EF4444'};
  const IB = { width:'100%', background:'rgba(255,255,255,0.06)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'8px 12px', color:'#fff', fontFamily:"'Montserrat',sans-serif", fontSize:13, outline:'none', boxSizing:'border-box' };

  if (editProposal) {
    return (
      <div style={{display:'grid',gridTemplateColumns:'220px 1fr',gap:16,height:'72vh'}}>
        <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:14,overflowY:'auto',display:'flex',flexDirection:'column'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)'}}>Sections</span>
            <button onClick={onClose} style={{background:'none',border:'none',color:'rgba(255,255,255,0.3)',cursor:'pointer',fontSize:16}}>×</button>
          </div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:700,color:'#fff',marginBottom:2}}>{editProposal.client}</div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.35)',marginBottom:14}}>{editProposal.title}</div>
          {editProposal.sections.map(sec=>(
            <div key={sec.id} onClick={()=>setActiveSection(sec.id)} style={{display:'flex',alignItems:'center',gap:8,padding:'7px 8px',marginBottom:3,borderRadius:6,cursor:'pointer',background:activeSection===sec.id?'rgba(232,119,34,0.15)':'transparent',border:activeSection===sec.id?'1px solid rgba(232,119,34,0.3)':'1px solid transparent'}}>
              <input type="checkbox" checked={sec.enabled} onClick={e=>e.stopPropagation()} onChange={e=>onUpdate({...editProposal,sections:editProposal.sections.map(s=>s.id===sec.id?{...s,enabled:e.target.checked}:s)})}/>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:sec.enabled?'rgba(255,255,255,0.8)':'rgba(255,255,255,0.3)'}}>{sec.title}</span>
            </div>
          ))}
          <div style={{marginTop:'auto',paddingTop:16,borderTop:'1px solid rgba(255,255,255,0.07)'}}>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.3)',marginBottom:4}}>Total Value</div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:20,fontWeight:900,color:'#E87722',marginBottom:12}}>{fmtDollars(editProposal.totalValue)}</div>
            <button onClick={()=>{onUpdate({...editProposal,status:'sent',sentAt:new Date().toISOString().split('T')[0]});showToast('Marked as sent');}} style={{...BtnPrimary,width:'100%',fontSize:11,padding:'8px 0'}}>Mark as Sent ✓</button>
          </div>
        </div>

        <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:20,overflowY:'auto'}}>
          {activeSection ? (()=>{
            const sec = editProposal.sections.find(s=>s.id===activeSection);
            return sec ? (
              <div>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:900,color:'#fff',marginBottom:12}}>{sec.title}</div>
                <textarea value={sec.content} onChange={e=>onUpdate({...editProposal,sections:editProposal.sections.map(s=>s.id===activeSection?{...s,content:e.target.value}:s)})} rows={22} style={{...IB,width:'100%',resize:'vertical',lineHeight:1.8,fontSize:13,boxSizing:'border-box'}}/>
              </div>
            ) : null;
          })() : (
            <div>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontWeight:900,fontSize:20,color:'#fff',marginBottom:4}}>{editProposal.title}</div>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.4)',marginBottom:24}}>Prepared for {editProposal.client} · {editProposal.createdAt}</div>
              {editProposal.sections.filter(s=>s.enabled).map(s=>(
                <div key={s.id} style={{marginBottom:20,paddingBottom:20,borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:'#E87722',marginBottom:8}}>{s.title}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.6)',lineHeight:1.8,whiteSpace:'pre-wrap'}}>{s.content}</div>
                </div>
              ))}
              <div style={{marginTop:16,fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.25)',textAlign:'center'}}>← Click a section on the left to edit</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
      {proposals.map(prop=>(
        <div key={prop.id} onClick={()=>{onEdit(prop);}} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'18px 20px',cursor:'pointer',transition:'all 0.15s'}}
          onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.07)'}
          onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',color:STATUS_COLOR[prop.status],background:`${STATUS_COLOR[prop.status]}18`,padding:'3px 8px',borderRadius:3}}>{prop.status}</span>
            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:18,fontWeight:900,color:'#E87722'}}>{fmtDollars(prop.totalValue)}</span>
          </div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:'#fff',marginBottom:3}}>{prop.client}</div>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.4)',marginBottom:12}}>{prop.title}</div>
          <div style={{display:'flex',justifyContent:'space-between',fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.25)'}}>
            <span>Created {prop.createdAt}</span>
            <span>{prop.sections.filter(s=>s.enabled).length} sections</span>
          </div>
        </div>
      ))}
      {proposals.length===0 && <div style={{background:'rgba(255,255,255,0.02)',border:'1px dashed rgba(255,255,255,0.08)',borderRadius:10,padding:40,textAlign:'center',fontFamily:"'Montserrat',sans-serif",fontSize:13,color:'rgba(255,255,255,0.25)'}}>No proposals yet. Advance a deal to Proposal stage to get started.</div>}
    </div>
  );
}

function CRMContracts({ contracts, showToast }) {
  const [selected, setSelected] = useState(null);
  const HOURLY = 185;
  return (
    <div style={{display:'grid',gridTemplateColumns:selected?'1fr 380px':'1fr',gap:16}}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14,alignContent:'start'}}>
        {contracts.map(con=>{
          // eslint-disable-next-line react-hooks/purity -- "days until renewal" display; recomputed per render is intended
          const renewalDays= Math.floor((new Date(con.renewalDate).getTime()-Date.now())/86400000);
          const supportCost= con.supportHoursUsed*HOURLY;
          const margin     = con.arr>0 ? Math.round(((con.arr-supportCost)/con.arr)*100) : 100;
          const hColor     = con.healthScore>=85?'#22C55E':con.healthScore>=70?'#F59E0B':'#EF4444';
          const expiring   = renewalDays<=90;
          return (
            <div key={con.id} onClick={()=>setSelected(selected?.id===con.id?null:con)}
              style={{background:'rgba(255,255,255,0.04)',border:`1px solid ${expiring?'rgba(245,158,11,0.3)':'rgba(255,255,255,0.07)'}`,borderRadius:10,padding:'18px 20px',cursor:'pointer',transition:'all 0.15s',position:'relative'}}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.07)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.04)'}>
              {expiring && <div style={{position:'absolute',top:12,right:12,fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'#F59E0B',background:'rgba(245,158,11,0.15)',padding:'2px 8px',borderRadius:3}}>RENEW IN {renewalDays}D</div>}
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:900,color:'#fff',marginBottom:2}}>{con.client}</div>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.4)',marginBottom:14}}>{con.title}</div>
              <div style={{display:'flex',gap:20,marginBottom:14}}>
                <div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)',marginBottom:2}}>CONTRACT</div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:18,fontWeight:900,color:'#E87722'}}>{fmtDollars(con.value)}</div></div>
                <div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)',marginBottom:2}}>ARR</div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:18,fontWeight:900,color:'#60A5FA'}}>{fmtDollars(con.arr)}</div></div>
              </div>
              <div style={{marginBottom:8}}>
                <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}><span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)'}}>Health</span><span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:hColor}}>{con.healthScore}/100</span></div>
                <div style={{height:4,background:'rgba(255,255,255,0.08)',borderRadius:2}}><div style={{height:'100%',width:`${con.healthScore}%`,background:hColor,borderRadius:2}}/></div>
              </div>
              <div style={{display:'flex',justifyContent:'space-between',fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.3)'}}>
                <span>{con.supportTickets} tickets · {con.supportHoursUsed}h support</span>
                <span style={{color:margin>=80?'#22C55E':margin>=60?'#F59E0B':'#EF4444'}}>~{margin}% margin</span>
              </div>
            </div>
          );
        })}
        {contracts.length===0 && <div style={{background:'rgba(255,255,255,0.02)',border:'1px dashed rgba(255,255,255,0.08)',borderRadius:10,padding:40,textAlign:'center',fontFamily:"'Montserrat',sans-serif",fontSize:13,color:'rgba(255,255,255,0.25)'}}>No active contracts yet.</div>}
      </div>

      {selected && (()=>{
        const con=selected, sc=con.supportHoursUsed*HOURLY, tm=con.arr>0?Math.round(((con.arr-sc)/con.arr)*100):100, pu=Math.min(100,Math.round((sc/Math.max(con.arr,1))*100));
        return (
          <div style={{background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:10,padding:22,position:'sticky',top:0,maxHeight:'75vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:900,color:'#fff'}}>Contract Detail</span>
              <button onClick={()=>setSelected(null)} style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',cursor:'pointer',fontSize:18}}>×</button>
            </div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:700,color:'#fff',marginBottom:2}}>{con.client}</div>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.4)',marginBottom:18}}>{con.startDate} → {con.endDate}</div>
            <div style={{background:'rgba(232,119,34,0.07)',border:'1px solid rgba(232,119,34,0.15)',borderRadius:8,padding:'14px 16px',marginBottom:16}}>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,letterSpacing:'0.1em',textTransform:'uppercase',color:'rgba(232,119,34,0.6)',marginBottom:10}}>True Cost of Service</div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
                {[{l:'Contract ARR',v:fmtDollars(con.arr),c:'#60A5FA'},{l:'Support Cost',v:fmtDollars(sc),c:'#F59E0B'},{l:'Tickets',v:con.supportTickets,c:'#A78BFA'},{l:'Hours Used',v:`${con.supportHoursUsed}h`,c:'#F59E0B'}].map(m=>(
                  <div key={m.l} style={{background:'rgba(0,0,0,0.2)',borderRadius:6,padding:'8px 10px'}}>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)',marginBottom:2}}>{m.l}</div>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:15,fontWeight:700,color:m.c}}>{m.v}</div>
                  </div>
                ))}
              </div>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.4)'}}>Support cost vs ARR</span>
                <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:pu>30?'#EF4444':'#22C55E'}}>{pu}%</span>
              </div>
              <div style={{height:6,background:'rgba(255,255,255,0.07)',borderRadius:3,marginBottom:8}}><div style={{height:'100%',width:`${pu}%`,background:pu>30?'#EF4444':pu>15?'#F59E0B':'#22C55E',borderRadius:3}}/></div>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:tm>=80?'#22C55E':tm>=60?'#F59E0B':'#EF4444',textAlign:'center'}}>Net Margin: ~{tm}%</div>
            </div>
            {con.notes && <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.5)',lineHeight:1.6,background:'rgba(255,255,255,0.03)',borderRadius:6,padding:'10px 12px',marginBottom:14}}>{con.notes}</div>}
            <button onClick={()=>showToast('Renewal workflow — coming soon')} style={{...BtnPrimary,width:'100%',fontSize:11,padding:'9px 0'}}>Start Renewal →</button>
          </div>
        );
      })()}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// CRM CONTACTS — address book with manual / business-card / Gmail import
// ═══════════════════════════════════════════════════════════════════
function CRMContacts({ currentUser, showToast }) {
  const [contacts,    setContacts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');
  const [filterTag,   setFilterTag]   = useState('all'); // all | manual | business_card | gmail
  const [editing,     setEditing]     = useState(null);   // contact obj or { __new: true }
  const [addOpen,     setAddOpen]     = useState(false);  // dropdown
  const [photoMode,   setPhotoMode]   = useState(false);  // file picker / OCR flow
  const [photoLoad,   setPhotoLoad]   = useState(false);
  const [photoErr,    setPhotoErr]    = useState(null);
  const [scanMode,    setScanMode]    = useState(false);  // live camera scanner
  const [scanProcess, setScanProcess] = useState(false);  // OCR pending after camera capture
  const [gmailMode,   setGmailMode]   = useState(false);  // google import flow
  const [gmailLoad,   setGmailLoad]   = useState(false);
  const [gmailErr,    setGmailErr]    = useState(null);
  const [gmailRows,   setGmailRows]   = useState([]);     // pending import list
  const [gmailFilter, setGmailFilter] = useState('');
  const [gmailPicks,  setGmailPicks]  = useState(new Set());

  const refresh = useCallback(async () => {
    setLoading(true);
    setContacts(await loadContacts(currentUser.orgId));
    setLoading(false);
  }, [currentUser.orgId]);

  useEffect(() => { refresh(); }, [refresh]);

  // ── Add (manual) ───────────────────────────────────────────────
  const startManual = () => { setEditing({ __new: true, source: 'manual' }); setAddOpen(false); };

  // ── Add (photo / OCR) ──────────────────────────────────────────
  const onPhotoPicked = async (file) => {
    if (!file) return;
    setPhotoErr(null);
    setPhotoLoad(true);
    try {
      // Compress: max-width 1600, JPEG 85% — keeps the body well under 1MB.
      const compressed = await compressImage(file, 1600, 0.85);
      const r = await ocrBusinessCard(compressed);
      if (r.error) { setPhotoErr(r.error); return; }
      // Pre-fill the edit form with what Claude extracted.
      setEditing({ __new: true, source: 'business_card', ...r.contact });
      setPhotoMode(false);
    } catch (e) {
      setPhotoErr(e.message);
    } finally {
      setPhotoLoad(false);
    }
  };

  // ── Add (live camera scanner) ───────────────────────────────────
  const onScanCaptured = async (imageData) => {
    setScanMode(false);
    if (!imageData) return;
    setScanProcess(true);
    try {
      const r = await ocrBusinessCard(imageData);
      if (r.error) { showToast(`❌ ${r.error}`); return; }
      setEditing({ __new: true, source: 'business_card', ...r.contact });
    } catch (e) {
      showToast(`❌ ${e.message}`);
    } finally {
      setScanProcess(false);
    }
  };

  // ── Add (Gmail) ─────────────────────────────────────────────────
  const startGmailImport = async () => {
    setAddOpen(false);
    setGmailMode(true);
    setGmailErr(null);
    setGmailRows([]);
    setGmailPicks(new Set());
    setGmailLoad(true);
    try {
      const token = await getGoogleContactsAccessToken();
      const r = await fetchGoogleContacts(token);
      if (r.error) { setGmailErr(r.error); return; }
      setGmailRows(r.contacts || []);
      // Default to all checked — most users want everything.
      setGmailPicks(new Set((r.contacts || []).map(c => c.source_id)));
    } catch (e) {
      setGmailErr(e.message || 'Could not access Google contacts');
    } finally {
      setGmailLoad(false);
    }
  };

  const importGmailPicks = async () => {
    const picks = gmailRows.filter(r => gmailPicks.has(r.source_id));
    if (!picks.length) { setGmailMode(false); return; }
    const { inserted, skipped } = await bulkInsertContacts(picks, currentUser.orgId, currentUser.id);
    showToast(`✓ Imported ${inserted}${skipped ? ` · ${skipped} already in list` : ''}`);
    setGmailMode(false);
    await refresh();
  };

  // ── Save edit ──────────────────────────────────────────────────
  const handleSave = async (draft) => {
    const row = { ...draft };
    delete row.__new;
    const result = await saveContact(row, currentUser.orgId, currentUser.id);
    if (result?.error) {
      showToast(`❌ ${result.error}`);
      return;
    }
    if (!result?.row) {
      showToast('❌ Save failed');
      return;
    }
    setEditing(null);
    await refresh();
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this contact?')) return;
    const ok = await dbDeleteContact(id);
    if (!ok) { showToast('❌ Delete failed'); return; }
    await refresh();
  };

  // ── Render ─────────────────────────────────────────────────────
  const visible = contacts
    .filter(c => filterTag === 'all' ? true : c.source === filterTag)
    .filter(c => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return [c.name, c.email, c.company, c.title, c.phone, c.mobile]
        .filter(Boolean).join(' ').toLowerCase().includes(q);
    });

  const initialsOf = (c) =>
    (c.name || c.email || '?').split(' ').map(p => p[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

  const sourceMeta = {
    manual:        { label: 'Manual',    color: '#A78BFA' },
    business_card: { label: 'Card',      color: '#34D399' },
    gmail:         { label: 'Gmail',     color: '#60A5FA' },
    import:        { label: 'Import',    color: '#FFB830' },
  };

  return (
    <div style={{ animation: 'fadeUp 0.4s ease' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          placeholder="Search contacts…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 6, padding: '8px 12px', fontSize: 13, fontFamily: "'Montserrat',sans-serif", flex: '1 1 220px', maxWidth: 360 }}
        />
        <select value={filterTag} onChange={e => setFilterTag(e.target.value)} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 6, padding: '8px 12px', fontSize: 12 }}>
          <option value="all">All sources</option>
          <option value="manual">Manual</option>
          <option value="business_card">Business cards</option>
          <option value="gmail">From Gmail</option>
        </select>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{visible.length} of {contacts.length}</span>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => setAddOpen(v => !v)}
            style={{ background: '#E87722', border: 'none', borderRadius: 6, color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", cursor: 'pointer' }}
          >
            + Add contact
          </button>
          {addOpen && (
            <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: '#0d1b2a', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8, padding: 4, zIndex: 100, minWidth: 200, boxShadow: '0 8px 24px rgba(0,0,0,0.4)' }}>
              {[
                { id: 'manual', label: 'Type it in',           onClick: startManual },
                { id: 'scan',   label: 'Scan card with camera', onClick: () => { setAddOpen(false); setScanMode(true); } },
                { id: 'photo',  label: 'Upload a card photo',   onClick: () => { setAddOpen(false); setPhotoMode(true); } },
                { id: 'gmail',  label: 'Import from Gmail',     onClick: startGmailImport },
              ].map(opt => (
                <button
                  key={opt.id}
                  onClick={opt.onClick}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', color: '#fff', padding: '8px 12px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", cursor: 'pointer', borderRadius: 4 }}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.07)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.4)', padding: 20 }}>Loading contacts…</div>
      ) : visible.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.4)', padding: 30, textAlign: 'center', fontSize: 13 }}>
          No contacts yet. Click <strong>+ Add contact</strong> to type one in, snap a business card, or pull from Gmail.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
          {visible.map(c => {
            const meta = sourceMeta[c.source] || sourceMeta.manual;
            return (
              <div key={c.id} onClick={() => setEditing(c)}
                style={{ padding: 14, borderRadius: 8, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', cursor: 'pointer', display: 'flex', gap: 12 }}
                onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}>
                {c.photo_url ? (
                  <img src={c.photo_url} alt="" style={{ width: 38, height: 38, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'rgba(232,119,34,0.12)', color: '#E87722', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 12, fontFamily: "'Montserrat',sans-serif", flexShrink: 0 }}>
                    {initialsOf(c)}
                  </div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{ fontWeight: 700, fontSize: 13, color: '#fff', fontFamily: "'Montserrat',sans-serif", overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{c.name || c.email || '(no name)'}</span>
                    <span style={{ fontSize: 9, color: meta.color, padding: '2px 6px', borderRadius: 3, background: `${meta.color}1a`, textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>{meta.label}</span>
                  </div>
                  {c.title && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>{c.title}{c.company ? ` · ${c.company}` : ''}</div>}
                  {!c.title && c.company && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>{c.company}</div>}
                  {c.email && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.email}</div>}
                  {(c.phone || c.mobile) && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)' }}>{c.phone || c.mobile}</div>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Live camera scanner */}
      {scanMode && (
        <CardScanner onCapture={onScanCaptured} onCancel={() => setScanMode(false)} />
      )}

      {/* Spinner shown while OCR runs after a camera capture */}
      {scanProcess && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 11500, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16, color: '#E87722', fontFamily: "'Montserrat',sans-serif" }}>
          <div style={{ width: 48, height: 48, border: '3px solid rgba(232,119,34,0.25)', borderTopColor: '#E87722', borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
          <span style={{ fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase' }}>Decoding card…</span>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      {/* Photo picker modal */}
      {photoMode && (
        <ContactsModal title="Snap a business card" onClose={() => setPhotoMode(false)}>
          <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginBottom: 14 }}>
            Pick a photo of the card. We'll send it to Claude Vision for OCR and prefill a contact form for your review.
          </p>
          <input
            type="file"
            accept="image/*"
            onChange={e => onPhotoPicked(e.target.files?.[0])}
            disabled={photoLoad}
            style={{ color: '#fff', fontSize: 12 }}
          />
          {photoLoad && <div style={{ marginTop: 14, color: '#E87722', fontSize: 12 }}>Reading the card…</div>}
          {photoErr && <div style={{ marginTop: 14, color: '#FF6B6B', fontSize: 12 }}>{photoErr}</div>}
        </ContactsModal>
      )}

      {/* Gmail import modal */}
      {gmailMode && (
        <ContactsModal title="Import from Gmail" onClose={() => setGmailMode(false)}>
          {gmailLoad && <div style={{ color: '#E87722', fontSize: 12 }}>Authorising and pulling your Google contacts…</div>}
          {gmailErr && <div style={{ color: '#FF6B6B', fontSize: 12 }}>{gmailErr}</div>}
          {!gmailLoad && !gmailErr && (
            <>
              <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: 12, marginBottom: 12 }}>
                {gmailRows.length} contacts with email or phone. Pick which to import — duplicates already in Nuggets are skipped silently.
              </p>
              <input
                placeholder="Filter…"
                value={gmailFilter}
                onChange={e => setGmailFilter(e.target.value)}
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 12, marginBottom: 10, width: '100%' }}
              />
              <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                <button onClick={() => setGmailPicks(new Set(gmailRows.map(r => r.source_id)))} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', borderRadius: 4, padding: '4px 10px', fontSize: 10, cursor: 'pointer' }}>Select all</button>
                <button onClick={() => setGmailPicks(new Set())}                                  style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', borderRadius: 4, padding: '4px 10px', fontSize: 10, cursor: 'pointer' }}>Select none</button>
              </div>
              <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6 }}>
                {gmailRows
                  .filter(r => !gmailFilter.trim() ? true : (`${r.name || ''} ${r.email || ''} ${r.company || ''}`).toLowerCase().includes(gmailFilter.toLowerCase()))
                  .map(r => (
                    <label key={r.source_id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10, alignItems: 'center', padding: '7px 12px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={gmailPicks.has(r.source_id)}
                        onChange={e => {
                          const next = new Set(gmailPicks);
                          if (e.target.checked) next.add(r.source_id); else next.delete(r.source_id);
                          setGmailPicks(next);
                        }}
                      />
                      <div>
                        <div style={{ fontSize: 12, color: '#fff' }}>{r.name || r.email || '(no name)'}</div>
                        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>{[r.email, r.company].filter(Boolean).join(' · ')}</div>
                      </div>
                    </label>
                  ))}
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
                <button onClick={() => setGmailMode(false)} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                <div style={{ flex: 1 }} />
                <button onClick={importGmailPicks} disabled={!gmailPicks.size} style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: gmailPicks.size ? 1 : 0.4 }}>
                  Import {gmailPicks.size}
                </button>
              </div>
            </>
          )}
        </ContactsModal>
      )}

      {/* Edit / new modal */}
      {editing && (
        <ContactEditor
          contact={editing}
          onClose={() => setEditing(null)}
          onSave={handleSave}
          onDelete={editing.id ? () => handleDelete(editing.id) : null}
        />
      )}
    </div>
  );
}

// ── Generic modal shell ──────────────────────────────────────────
function ContactsModal({ title, onClose, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#0d1b2a', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 12, padding: 24, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat',sans-serif" }}>{title}</span>
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── Contact form (manual entry / OCR review / edit existing) ─────
function ContactEditor({ contact, onClose, onSave, onDelete }) {
  const [draft, setDraft] = useState(contact);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const submit = async () => {
    setSaving(true);
    await onSave(draft);
    setSaving(false);
  };

  const inp = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%' };
  const lbl = { fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4, display: 'block', fontFamily: "'Montserrat',sans-serif" };
  // Defined as a plain function (returns JSX) rather than a component so
  // we don't get a fresh component identity per render and lose input focus.
  const field = (k, label, { type = 'text', full = false } = {}) => (
    <div key={k} style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <label style={lbl}>{label}</label>
      <input type={type} value={draft[k] ?? ''} onChange={e => set(k, e.target.value)} style={inp} />
    </div>
  );

  return (
    <ContactsModal
      title={contact.__new ? (contact.source === 'business_card' ? 'New contact (from card)' : 'New contact') : (draft.name || draft.email || 'Edit contact')}
      onClose={onClose}
    >
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
        {field('name',       'Full name', { full: true })}
        {field('first_name', 'First name')}
        {field('last_name',  'Last name')}
        {field('email',      'Email',  { type: 'email', full: true })}
        {field('phone',      'Phone',  { type: 'tel' })}
        {field('mobile',     'Mobile', { type: 'tel' })}
        {field('company',    'Company')}
        {field('title',      'Title')}
        {field('website',    'Website', { full: true })}
        {field('address',    'Address', { full: true })}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl}>Notes</label>
          <textarea value={draft.notes ?? ''} onChange={e => set('notes', e.target.value)} rows={3} style={{ ...inp, resize: 'vertical' }} />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
        {onDelete && (
          <button onClick={onDelete} style={{ background: 'transparent', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 6, padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}>Delete</button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
        <button onClick={submit} disabled={saving} style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
    </ContactsModal>
  );
}

// ── Helpers (lifted out so they can be reused / tree-shaken) ─────

// Compress an image File to a JPEG data URL with max-width capped.
async function compressImage(file, maxWidth, quality) {
  const dataUrl = await new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload  = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  const ratio = Math.min(1, maxWidth / img.width);
  const w = Math.round(img.width * ratio);
  const h = Math.round(img.height * ratio);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d').drawImage(img, 0, 0, w, h);
  return c.toDataURL('image/jpeg', quality);
}

// Loads Google Identity Services + requests a `contacts.readonly` access
// token. The Google Client ID lives in VITE_GOOGLE_CLIENT_ID (already set).
async function loadGsi() {
  if (window.google?.accounts?.oauth2) return;
  await new Promise((resolve, reject) => {
    if (document.querySelector('script[data-gsi]')) { resolve(); return; }
    const s = document.createElement('script');
    s.src   = 'https://accounts.google.com/gsi/client';
    s.async = true;
    s.defer = true;
    s.dataset.gsi = '1';
    s.onload  = resolve;
    s.onerror = () => reject(new Error('Failed to load Google Identity Services script'));
    document.head.appendChild(s);
  });
}

// ── Camera-based business card scanner ──────────────────────────
// Streams from getUserMedia (back camera preferred), shows a Tron-style
// scan overlay with synthesized chirps, captures a frame on tap, then
// hands the JPEG up to the parent for OCR.
function CardScanner({ onCapture, onCancel }) {
  const videoRef  = useRef(null);
  const audioRef  = useRef(null);   // shared AudioContext
  const streamRef = useRef(null);
  const [status,  setStatus]  = useState('starting');  // starting | scanning | captured | error
  const [error,   setError]   = useState(null);
  const [preview, setPreview] = useState(null);        // data URL after capture

  // Cue helpers — Web Audio synth, no external assets.
  const ensureAudio = () => {
    if (!audioRef.current) {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      if (Ctor) audioRef.current = new Ctor();
    }
    return audioRef.current;
  };
  const beep = (freq, dur = 0.12, type = 'sine', vol = 0.18) => {
    const ctx = ensureAudio(); if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type; osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(vol, now + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now); osc.stop(now + dur + 0.02);
  };
  const sweep = (fromHz, toHz, dur = 0.3) => {
    const ctx = ensureAudio(); if (!ctx) return;
    const now = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(fromHz, now);
    osc.frequency.exponentialRampToValueAtTime(toHz, now + dur);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.16, now + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + dur);
    osc.connect(gain).connect(ctx.destination);
    osc.start(now); osc.stop(now + dur + 0.02);
  };
  const chord = (freqs, dur = 0.18) => freqs.forEach((f, i) =>
    setTimeout(() => beep(f, dur, 'triangle', 0.14), i * 80)
  );

  // Start camera on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError('Camera not available on this device/browser.');
          setStatus('error');
          return;
        }
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setStatus('scanning');
        sweep(220, 880, 0.28); // power-on
      } catch (e) {
        setError(e?.message || 'Could not access camera. Check browser permissions.');
        setStatus('error');
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach(t => t.stop());
      streamRef.current = null;
      audioRef.current?.close?.().catch(() => {});
      audioRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const capture = () => {
    if (!videoRef.current) return;
    const v = videoRef.current;
    const w = v.videoWidth || 1280;
    const h = v.videoHeight || 720;
    // Cap the long edge for the OCR upload — keeps the body well under 1MB.
    const maxEdge = 1600;
    const ratio = Math.min(1, maxEdge / Math.max(w, h));
    const cw = Math.round(w * ratio);
    const ch = Math.round(h * ratio);
    const canvas = document.createElement('canvas');
    canvas.width = cw; canvas.height = ch;
    canvas.getContext('2d').drawImage(v, 0, 0, cw, ch);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
    setPreview(dataUrl);
    setStatus('captured');
    beep(1200, 0.06, 'square', 0.18);
    setTimeout(() => beep(1600, 0.10, 'square', 0.18), 70);
  };

  const useShot = () => {
    chord([523, 659, 784], 0.18);  // C-E-G success
    setTimeout(() => onCapture(preview), 280);
  };
  const retake = () => {
    setPreview(null);
    setStatus('scanning');
    beep(440, 0.08, 'sine', 0.12);
  };

  // ── Render ─────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes scan-line  { 0%{top:8%} 50%{top:88%} 100%{top:8%} }
        @keyframes pulse-bracket { 0%,100%{opacity:1} 50%{opacity:.55} }
        @keyframes hud-flicker { 0%,100%{opacity:.85} 50%{opacity:1} }
      `}</style>
      <div style={{ position: 'fixed', inset: 0, zIndex: 11000, background: '#000', display: 'flex', flexDirection: 'column' }}>
        {/* Top status bar */}
        <div style={{ position: 'absolute', top: 12, left: 12, right: 12, display: 'flex', alignItems: 'center', gap: 10, zIndex: 3 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: status === 'error' ? '#FF6B6B' : status === 'captured' ? '#34D399' : '#E87722', boxShadow: status === 'error' ? '0 0 12px #FF6B6B' : status === 'captured' ? '0 0 12px #34D399' : '0 0 12px #E87722' }} />
          <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)', animation: 'hud-flicker 1.6s infinite' }}>
            {status === 'starting'  && 'Initialising sensor…'}
            {status === 'scanning'  && 'Acquire target ⏐ business card'}
            {status === 'captured'  && 'Capture · review and confirm'}
            {status === 'error'     && 'Sensor offline'}
          </span>
          <div style={{ flex: 1 }} />
          <button onClick={onCancel} aria-label="Cancel" style={{ width: 36, height: 36, borderRadius: 18, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', fontSize: 16, cursor: 'pointer' }}>×</button>
        </div>

        {/* Live video / preview */}
        <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
          {!preview && (
            <video ref={videoRef} playsInline muted style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', background: '#000' }} />
          )}
          {preview && (
            <img src={preview} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
          )}

          {/* HUD overlay: corner brackets + scan line + dim mask */}
          {(status === 'scanning' || status === 'starting') && (
            <>
              {/* dim outside band */}
              <div style={{ position: 'absolute', inset: 0, boxShadow: 'inset 0 0 0 80px rgba(0,0,0,0.45)', pointerEvents: 'none' }} />
              {/* scan zone (centered card aspect) */}
              <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 'min(86%, 560px)', aspectRatio: '1.7 / 1', pointerEvents: 'none' }}>
                {/* corner brackets */}
                {[[0,0],[1,0],[0,1],[1,1]].map(([x,y]) => (
                  <div key={`${x}-${y}`} style={{
                    position: 'absolute',
                    [x ? 'right' : 'left']: -2,
                    [y ? 'bottom' : 'top']: -2,
                    width: 28, height: 28,
                    borderTop:    !y ? '3px solid #E87722' : 'none',
                    borderBottom: y ? '3px solid #E87722' : 'none',
                    borderLeft:   !x ? '3px solid #E87722' : 'none',
                    borderRight:  x ? '3px solid #E87722' : 'none',
                    boxShadow: '0 0 12px rgba(232,119,34,0.55)',
                    animation: 'pulse-bracket 1.4s infinite',
                  }} />
                ))}
                {/* scan line */}
                <div style={{
                  position: 'absolute', left: 0, right: 0,
                  height: 2, background: 'linear-gradient(90deg, transparent, #E87722, transparent)',
                  boxShadow: '0 0 14px #E87722, 0 0 28px rgba(232,119,34,0.55)',
                  animation: 'scan-line 2.2s ease-in-out infinite',
                }} />
                {/* gridlines */}
                <div style={{ position: 'absolute', inset: 0, backgroundImage: 'linear-gradient(rgba(232,119,34,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(232,119,34,0.10) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
              </div>
              {/* hint text bottom */}
              <div style={{ position: 'absolute', bottom: 110, left: 0, right: 0, textAlign: 'center', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.08em' }}>
                Centre the card inside the brackets · hold steady
              </div>
            </>
          )}

          {status === 'error' && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, color: '#FF6B6B', textAlign: 'center', fontFamily: "'Montserrat',sans-serif" }}>
              {error}
            </div>
          )}
        </div>

        {/* Bottom controls */}
        <div style={{ padding: '20px 0 calc(env(safe-area-inset-bottom, 0px) + 24px)', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 24, background: 'rgba(0,0,0,0.4)' }}>
          {status === 'scanning' && (
            <button onClick={capture} aria-label="Capture" style={{ width: 72, height: 72, borderRadius: '50%', background: '#E87722', border: '4px solid rgba(255,255,255,0.85)', boxShadow: '0 0 24px rgba(232,119,34,0.6)', cursor: 'pointer' }} />
          )}
          {status === 'captured' && (
            <>
              <button onClick={retake} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)', borderRadius: 6, padding: '12px 18px', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer' }}>RETAKE</button>
              <button onClick={useShot} style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '12px 22px', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', cursor: 'pointer', boxShadow: '0 0 18px rgba(232,119,34,0.6)' }}>USE THIS</button>
            </>
          )}
          {status === 'error' && (
            <button onClick={onCancel} style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.85)', borderRadius: 6, padding: '12px 18px', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>CLOSE</button>
          )}
        </div>
      </div>
    </>
  );
}

async function getGoogleContactsAccessToken() {
  await loadGsi();
  const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
  if (!clientId) throw new Error('VITE_GOOGLE_CLIENT_ID is not configured');
  return new Promise((resolve, reject) => {
    const tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope:     'https://www.googleapis.com/auth/contacts.readonly',
      callback:  (resp) => {
        if (resp.error) reject(new Error(resp.error_description || resp.error));
        else            resolve(resp.access_token);
      },
    });
    tokenClient.requestAccessToken({ prompt: 'consent' });
  });
}

function CRMIntel({ deals }) {
  const industries = [...new Set(deals.map(d=>d.industry))];
  const byInd = industries.map(ind=>{
    const ds=deals.filter(d=>d.industry===ind), won=ds.filter(d=>d.stage==='closed_won').length, lost=ds.filter(d=>d.stage==='closed_lost').length, open=ds.filter(d=>!['closed_won','closed_lost'].includes(d.stage)).length;
    return {ind, won, lost, open, wr:(won+lost)>0?Math.round(won/(won+lost)*100):null, pipe:ds.filter(d=>!['closed_won','closed_lost'].includes(d.stage)).reduce((s,d)=>s+d.value*(d.probability/100),0)};
  }).sort((a,b)=>(b.won+b.open)-(a.won+a.open));
  const maxPipe = Math.max(...byInd.map(i=>i.pipe),1);
  const srcs = ['Inbound','Outbound','Referral','Conference','Partner','Renewal'].map(src=>({src,count:deals.filter(d=>d.source===src).length,won:deals.filter(d=>d.source===src&&d.stage==='closed_won').length})).filter(s=>s.count>0);
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 340px',gap:20}}>
      <div>
        <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:14}}>Industry Performance</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {byInd.map(i=>(
            <div key={i.ind} style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'14px 18px'}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:i.pipe>0?10:0}}>
                <div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:700,color:'#fff'}}>{i.ind}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.35)',marginTop:2}}>
                    {i.won}W · {i.lost}L · {i.open} open
                    {i.wr!==null && <span style={{color:i.wr>=60?'#22C55E':i.wr>=40?'#F59E0B':'#EF4444',fontWeight:700,marginLeft:8}}>{i.wr}% win rate</span>}
                  </div>
                </div>
                {i.pipe>0 && <div style={{textAlign:'right'}}><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)',marginBottom:2}}>Pipeline</div><div style={{fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:700,color:'#E87722'}}>{fmtDollars(i.pipe)}</div></div>}
              </div>
              {i.pipe>0 && <div style={{height:4,background:'rgba(255,255,255,0.06)',borderRadius:2}}><div style={{height:'100%',width:`${(i.pipe/maxPipe)*100}%`,background:'linear-gradient(90deg,#E87722,#f59840)',borderRadius:2}}/></div>}
              <div style={{marginTop:8}}>
                {i.won>=1&&i.open>=1 && <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'#22C55E',background:'rgba(34,197,94,0.08)',padding:'2px 8px',borderRadius:3}}>✓ Strong market — keep investing</span>}
                {i.won===0&&i.open>=1 && <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'#F59E0B',background:'rgba(245,158,11,0.08)',padding:'2px 8px',borderRadius:3}}>⚡ First win in sight — prioritize</span>}
                {i.lost>=1&&i.won===0&&i.open===0 && <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'#EF4444',background:'rgba(239,68,68,0.08)',padding:'2px 8px',borderRadius:3}}>⚠ Loss pattern — review approach</span>}
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'16px 18px'}}>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:14}}>Deal Sources</div>
          {srcs.map(s=>(
            <div key={s.src} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.7)'}}>{s.src}</span>
              <div style={{display:'flex',gap:8,alignItems:'center'}}>
                <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.35)'}}>{s.count} deals</span>
                {s.won>0 && <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'#22C55E',background:'rgba(34,197,94,0.1)',padding:'1px 6px',borderRadius:3}}>{s.won}W</span>}
              </div>
            </div>
          ))}
        </div>
        <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'16px 18px'}}>
          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:14}}>Expansion Signals</div>
          {[
            {icon:'🔗',sig:'Referral momentum',detail:'2 deals sourced from Port of Houston alone',action:'Build a formal referral program'},
            {icon:'⚓',sig:'Maritime is your core',detail:`${deals.filter(d=>d.industry==='Maritime'&&d.stage==='closed_won').length} Maritime wins — highest win rate`,action:'Double down on ports & fleet operators'},
            {icon:'🏛',sig:'Federal pipeline brewing',detail:'$624K USCG opportunity — long cycle',action:'Start FedRAMP compliance pathway now'},
          ].map(s=>(
            <div key={s.sig} style={{marginBottom:12,paddingBottom:12,borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
              <div style={{display:'flex',gap:8,alignItems:'flex-start'}}>
                <span style={{fontSize:16}}>{s.icon}</span>
                <div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:700,color:'#fff',marginBottom:2}}>{s.sig}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.4)',marginBottom:4}}>{s.detail}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'#E87722'}}>→ {s.action}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DEAL ROOM — M&A workspace (diligence, files, earn-out modelling)
// ═══════════════════════════════════════════════════════════════════

// Rashma's request list, prefilled into every new deal so 40Geo doesn't
// have to type it out.
const DEFAULT_DILIGENCE_TEMPLATE = [
  { request: "Annual statutory accounts for the past 3 years (if produced)", category: "financials" },
  { request: "Management accounts for the past 3 years",                      category: "financials" },
  { request: "Revenue for the last 3 years split by revenue stream",          category: "financials" },
  { request: "Revenue for the last 3 years split by end sector",              category: "financials" },
  { request: "Organisation structure",                                        category: "structure" },
  { request: "Overview of growth strategy including investment requirements", category: "growth" },
  { request: "Overview of AI capabilities and transformation of current work",category: "growth" },
  { request: "Capex and R&D spend for the past 3 years",                      category: "financials" },
];

const DD_STATUS_META = {
  open:         { label: 'Open',         color: '#FFB830' },
  in_progress:  { label: 'In Progress',  color: '#60A5FA' },
  submitted:    { label: 'Submitted',    color: '#A78BFA' },
  accepted:     { label: 'Accepted',     color: '#34D399' },
};

const fmtMoney = (n, cur = 'USD') => {
  const v = Number(n || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: cur, maximumFractionDigits: 0 }).format(v);
};

// ─── Linked-context hooks: read Notes module + CRM activities ────
// Both live in localStorage today. These hooks watch the relevant
// key and emit the slice relevant to the current opportunity (auto-
// matched by client_name / opportunity name today; manual override
// comes later).
function useLinkedNotes(userId, clientName, opportunityName, opportunityId = null) {
  const [allNotes, setAllNotes] = useState([]);
  const [manualIds, setManualIds] = useState([]);
  const [excludedIds, setExcludedIds] = useState([]);
  const notesKey    = `nuggets_notes_${userId}`;
  // Per-user manual link table: {"opp-abc": ["note-1","note-2"], ...}
  const linksKey    = `nuggets_opplinks_${userId}`;
  // Per-user exclusion table (same shape) — auto-matched notes the user
  // has explicitly unlinked from an opportunity. Auto-matching is a live
  // name-substring heuristic, so without this an unwanted match would
  // just reappear on the next render; the exclusion suppresses it.
  const excludesKey = `nuggets_oppexcludes_${userId}`;

  useEffect(() => {
    const load = () => {
      try {
        const raw = localStorage.getItem(notesKey);
        setAllNotes(raw ? (JSON.parse(raw) || []) : []);
      } catch { setAllNotes([]); }
    };
    load();
    const onStorage = (e) => { if (e.key === notesKey) load(); };
    window.addEventListener('storage', onStorage);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, [notesKey]);

  // Load manual note IDs pinned to THIS opportunity.
  useEffect(() => {
    if (!opportunityId) { setManualIds([]); return; }
    try {
      const raw = localStorage.getItem(linksKey);
      const map = raw ? (JSON.parse(raw) || {}) : {};
      setManualIds(Array.isArray(map[opportunityId]) ? map[opportunityId] : []);
    } catch { setManualIds([]); }
  }, [linksKey, opportunityId]);

  // Load the exclusion list for THIS opportunity.
  useEffect(() => {
    if (!opportunityId) { setExcludedIds([]); return; }
    try {
      const raw = localStorage.getItem(excludesKey);
      const map = raw ? (JSON.parse(raw) || {}) : {};
      setExcludedIds(Array.isArray(map[opportunityId]) ? map[opportunityId] : []);
    } catch { setExcludedIds([]); }
  }, [excludesKey, opportunityId]);

  // Auto-matched by any of: crmLink / title / body / tags containing
  // client name OR opportunity name (case-insensitive substring).
  const autoMatched = useMemo(() => {
    if (!allNotes.length) return [];
    const needle1 = (clientName || '').trim().toLowerCase();
    const needle2 = (opportunityName || '').trim().toLowerCase();
    if (!needle1 && !needle2) return [];
    return allNotes.filter(n => {
      const hay = `${(n.crmLink || '').toLowerCase()} ${(n.title || '').toLowerCase()} ${(n.content || '').toLowerCase()} ${(n.tags || []).join(' ').toLowerCase()}`;
      return (needle1 && hay.includes(needle1)) || (needle2 && hay.includes(needle2));
    });
  }, [allNotes, clientName, opportunityName]);

  // Manually-linked notes: resolve IDs back to full note objects.
  const manualMatched = useMemo(() => {
    if (!manualIds.length || !allNotes.length) return [];
    const set = new Set(manualIds);
    return allNotes.filter(n => set.has(n.id));
  }, [allNotes, manualIds]);

  // Merged view: auto + manual, deduplicated by id, minus anything the
  // user has explicitly unlinked.
  const merged = useMemo(() => {
    const excluded = new Set(excludedIds);
    const seen = new Set();
    const out = [];
    for (const n of [...autoMatched, ...manualMatched]) {
      if (excluded.has(n.id) || seen.has(n.id)) continue;
      seen.add(n.id);
      out.push(n);
    }
    return out;
  }, [autoMatched, manualMatched, excludedIds]);

  // Search across ALL notes (not filtered by client). Returns up to
  // maxResults matches — used by the panel's search box so the user
  // can pin notes tagged with an abbreviation or a former name that
  // the auto-matcher doesn't catch (e.g. "DRT" for Deep Reach Tech).
  const searchNotes = (query, maxResults = 30) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return [];
    return allNotes.filter(n => {
      const hay = `${(n.crmLink || '').toLowerCase()} ${(n.title || '').toLowerCase()} ${(n.content || '').toLowerCase()} ${(n.tags || []).join(' ').toLowerCase()}`;
      return hay.includes(q);
    }).slice(0, maxResults);
  };

  // Write the updated manual-links map back to localStorage. Called
  // from linkNote / unlinkNote / linkNotes helpers.
  const persist = (nextIds) => {
    if (!opportunityId) return;
    try {
      const raw = localStorage.getItem(linksKey);
      const map = raw ? (JSON.parse(raw) || {}) : {};
      map[opportunityId] = nextIds;
      localStorage.setItem(linksKey, JSON.stringify(map));
      setManualIds(nextIds);
    } catch (e) {
      console.warn('[useLinkedNotes] persist failed:', e?.message || e);
    }
  };

  // Write the exclusion map back to localStorage.
  const persistExcludes = (nextIds) => {
    if (!opportunityId) return;
    try {
      const raw = localStorage.getItem(excludesKey);
      const map = raw ? (JSON.parse(raw) || {}) : {};
      map[opportunityId] = nextIds;
      localStorage.setItem(excludesKey, JSON.stringify(map));
      setExcludedIds(nextIds);
    } catch (e) {
      console.warn('[useLinkedNotes] persistExcludes failed:', e?.message || e);
    }
  };

  const linkNote = (noteId) => {
    // Re-linking clears any prior exclusion so the note becomes visible
    // again (whether via manual pin or the auto-matcher).
    if (excludedIds.includes(noteId)) persistExcludes(excludedIds.filter(id => id !== noteId));
    if (!manualIds.includes(noteId)) persist([...manualIds, noteId]);
  };
  const unlinkNote = (noteId) => {
    // Drop any manual pin AND record an exclusion, so the note stays
    // hidden even if it still matches the auto-linker's name heuristic.
    if (manualIds.includes(noteId)) persist(manualIds.filter(id => id !== noteId));
    if (!excludedIds.includes(noteId)) persistExcludes([...excludedIds, noteId]);
  };
  const linkNotes = (noteIds) => {
    persist(Array.from(new Set([...manualIds, ...noteIds])));
    const cleared = excludedIds.filter(id => !noteIds.includes(id));
    if (cleared.length !== excludedIds.length) persistExcludes(cleared);
  };

  // Create a new note in the user's notes store and auto-link it to
  // this opportunity. Used by the "Add note here" flow — lets users
  // paste an email / meeting transcript / .txt / .docx directly on
  // the opportunity without leaving the workspace. The note also
  // shows up in the main Notes module (single source of truth) so
  // it's searchable/editable across the app.
  const createNote = ({ title, content, tags = [], category = 'note' }) => {
    const id  = (crypto?.randomUUID?.() || `note-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const now = new Date().toISOString();
    const note = {
      id,
      title:    (title || '').trim() || 'Untitled note',
      content:  (content || '').trim(),
      tags:     Array.isArray(tags) ? tags : [],
      category,
      crmLink:  null,
      createdAt: now,
      updatedAt: now,
    };
    try {
      const raw = localStorage.getItem(notesKey);
      const arr = raw ? (JSON.parse(raw) || []) : [];
      arr.unshift(note);
      localStorage.setItem(notesKey, JSON.stringify(arr));
      setAllNotes(arr);
    } catch (e) {
      console.warn('[useLinkedNotes] createNote persist failed:', e?.message || e);
      return null;
    }
    // Auto-link it to this opportunity
    if (opportunityId) {
      const nextIds = [...manualIds, id];
      persist(nextIds);
    }
    return note;
  };

  return {
    notes:         merged,      // auto + manual, for AI + display
    autoMatched,
    manuallyLinkedIds: manualIds,
    searchNotes,
    linkNote,
    unlinkNote,
    linkNotes,
    createNote,
  };
}

function useLinkedCrmActivities(orgId, crmDealId) {
  const [activities, setActivities] = useState([]);
  const storageKey = `nuggets_crm_${orgId || 'default'}`;
  useEffect(() => {
    if (!crmDealId) { setActivities([]); return; }
    const load = () => {
      try {
        const raw = localStorage.getItem(storageKey);
        const state = raw ? JSON.parse(raw) : null;
        const deal = (state?.deals || []).find(d => d.id === crmDealId);
        setActivities(deal?.activities || []);
      } catch { setActivities([]); }
    };
    load();
    const onStorage = (e) => { if (e.key === storageKey) load(); };
    window.addEventListener('storage', onStorage);
    const onFocus = () => load();
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
    };
  }, [storageKey, crmDealId]);
  return activities;
}

function LinkedNotesPanel({ C, notes, clientName, manuallyLinkedIds = [], searchNotes, onLink, onUnlink, onLinkMany, onCreate, onJumpToNotes, showToast }) {
  const [showSearch, setShowSearch] = useState(false);
  const [showAdd, setShowAdd]       = useState(false);
  const [query, setQuery] = useState('');
  const [checkedIds, setCheckedIds] = useState([]);

  const manualSet = useMemo(() => new Set(manuallyLinkedIds), [manuallyLinkedIds]);
  // Notes currently shown in the panel (auto + manual − unlinked).
  const visibleSet = useMemo(() => new Set(notes.map(n => n.id)), [notes]);

  // Search results — exclude notes already visible in the panel so the
  // checklist only offers adds. A note that was auto-matched but then
  // unlinked is NOT visible, so it correctly reappears here to be re-linked.
  const searchResults = useMemo(() => {
    if (!query.trim() || !searchNotes) return [];
    return searchNotes(query, 30).filter(n => !visibleSet.has(n.id));
  }, [query, searchNotes, visibleSet]);

  const toggleChecked = (id) => {
    setCheckedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const linkChecked = () => {
    if (!checkedIds.length) return;
    onLinkMany?.(checkedIds);
    setCheckedIds([]);
    setQuery('');
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 12 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.12em', textTransform: 'uppercase', flex: 1 }}>
          📝 Linked Notes
        </div>
        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{notes.length} linked</span>
      </div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 10, lineHeight: 1.5 }}>
        Auto-matched from your Notes by any mention of <strong style={{ color: 'rgba(255,255,255,0.65)' }}>{clientName || '—'}</strong>. Click <strong style={{ color: 'rgba(248,113,113,0.8)' }}>×</strong> on any note to unlink one the matcher got wrong. Search below to manually link notes that use a different name / abbreviation (e.g. "DRT" for "Deep Reach Technology").
      </div>

      {/* Search + checklist + add-note buttons */}
      <div style={{ marginBottom: 12, padding: 8, border: `1px dashed ${C.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.02)' }}>
        {!showSearch ? (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => setShowSearch(true)}
              style={{
                background: 'transparent', color: '#A78BFA', border: '1px solid rgba(167,139,250,0.4)',
                borderRadius: 4, padding: '5px 12px', cursor: 'pointer',
                fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
              }}
            >🔍 Search &amp; link more notes</button>
            {onCreate && (
              <button
                onClick={() => setShowAdd(true)}
                style={{
                  background: 'transparent', color: '#22d3ee', border: '1px solid rgba(34,211,238,0.4)',
                  borderRadius: 4, padding: '5px 12px', cursor: 'pointer',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}
              >+ Add note (paste / upload)</button>
            )}
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: query ? 8 : 0 }}>
              <input
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder='Search notes by any term — e.g. "DRT", "kickoff", tag name…'
                autoFocus
                style={{
                  flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`,
                  color: '#fff', borderRadius: 4, padding: '6px 10px',
                  fontFamily: "'Inter',sans-serif", fontSize: 12, outline: 'none',
                }}
              />
              <button
                onClick={() => { setShowSearch(false); setQuery(''); setCheckedIds([]); }}
                style={{
                  background: 'transparent', color: 'rgba(255,255,255,0.5)',
                  border: `1px solid ${C.border}`, borderRadius: 4, padding: '5px 10px', cursor: 'pointer',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                }}
              >✕</button>
            </div>
            {query && searchResults.length === 0 && (
              <div style={{ padding: '10px 4px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontStyle: 'italic' }}>
                No unlinked notes match "{query}". Try a different search term or check whether it's already linked below.
              </div>
            )}
            {searchResults.length > 0 && (
              <>
                <div style={{ maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {searchResults.map(n => {
                    const isChecked = checkedIds.includes(n.id);
                    return (
                      <label key={n.id} style={{
                        display: 'flex', gap: 8, padding: 8, borderRadius: 4, cursor: 'pointer',
                        background: isChecked ? 'rgba(167,139,250,0.08)' : 'rgba(0,0,0,0.15)',
                        border: `1px solid ${isChecked ? 'rgba(167,139,250,0.4)' : C.border}`,
                      }}>
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleChecked(n.id)}
                          style={{ marginTop: 3 }}
                        />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
                            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 8, color: '#A78BFA', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
                              {n.category || 'note'}
                            </span>
                            {n.crmLink && <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 8, color: '#60A5FA', background: 'rgba(96,165,250,0.12)', padding: '1px 5px', borderRadius: 2 }}>↳ {n.crmLink}</span>}
                            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
                              {new Date(n.updatedAt || n.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff' }}>{n.title}</div>
                          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2, lineHeight: 1.4, maxHeight: 34, overflow: 'hidden' }}>
                            {n.content}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button
                    onClick={linkChecked}
                    disabled={checkedIds.length === 0}
                    style={{
                      background: checkedIds.length === 0 ? 'rgba(255,255,255,0.05)' : '#A78BFA',
                      color: checkedIds.length === 0 ? 'rgba(255,255,255,0.3)' : '#001114',
                      border: 'none', borderRadius: 4, padding: '6px 14px',
                      cursor: checkedIds.length === 0 ? 'default' : 'pointer',
                      fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
                      letterSpacing: '0.08em', textTransform: 'uppercase',
                    }}
                  >Link {checkedIds.length > 0 ? checkedIds.length : ''} selected</button>
                  {checkedIds.length > 0 && (
                    <button
                      onClick={() => setCheckedIds([])}
                      style={{
                        background: 'transparent', color: 'rgba(255,255,255,0.5)',
                        border: `1px solid ${C.border}`, borderRadius: 4, padding: '6px 12px', cursor: 'pointer',
                        fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
                      }}
                    >Clear</button>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {notes.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontStyle: 'italic', padding: '8px 0' }}>
          No notes link to this opportunity yet. Capture them in the Notes module (auto-match) or use the search above to pin ones with a different naming convention.
        </div>
      ) : notes.map(n => {
        const isManual = manualSet.has(n.id);
        return (
          <div key={n.id} style={{ padding: '8px 0', borderTop: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: '#A78BFA', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>{n.category || 'note'}</span>
              {n.crmLink && <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: '#60A5FA', background: 'rgba(96,165,250,0.12)', padding: '1px 6px', borderRadius: 3 }}>↳ {n.crmLink}</span>}
              {isManual ? (
                <span title="Manually linked — click × to remove" style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: '#A78BFA', background: 'rgba(167,139,250,0.12)', padding: '1px 6px', borderRadius: 3 }}>📌 pinned</span>
              ) : (
                <span title="Auto-matched by name — click × to unlink" style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.06)', padding: '1px 6px', borderRadius: 3 }}>auto</span>
              )}
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{new Date(n.updatedAt || n.createdAt).toLocaleDateString()}</span>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => { onUnlink?.(n.id); showToast?.(`Unlinked "${n.title || 'note'}"`); }}
                title={isManual ? 'Unlink this note from the opportunity' : 'Unlink — hides this auto-matched note from the opportunity'}
                style={{
                  background: 'transparent', color: 'rgba(248,113,113,0.7)', border: 'none',
                  cursor: 'pointer', padding: '2px 6px',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, lineHeight: 1,
                }}
              >×</button>
            </div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff', marginTop: 3 }}>{n.title}</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 3, lineHeight: 1.45, whiteSpace: 'pre-wrap', maxHeight: 100, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {n.content}
            </div>
          </div>
        );
      })}

      {showAdd && onCreate && (
        <AddNoteModal
          C={C}
          onCancel={() => setShowAdd(false)}
          onSave={(payload) => {
            const created = onCreate(payload);
            setShowAdd(false);
            if (created) {
              showToast?.(`✓ Note "${created.title}" saved and linked`);
            } else {
              showToast?.('❌ Failed to save note');
            }
          }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function AddNoteModal({ C, onCancel, onSave, showToast }) {
  const [title, setTitle]     = useState('');
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const name = file.name || 'uploaded';
    const ext  = (name.split('.').pop() || '').toLowerCase();
    if (!['txt', 'md', 'docx'].includes(ext)) {
      showToast?.('❌ Only .txt, .md, or .docx files are supported.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showToast?.('❌ File is larger than 5MB.');
      return;
    }
    setLoading(true);
    try {
      if (ext === 'docx') {
        // Dynamic import so mammoth doesn't bloat the main bundle
        const mammoth = (await import('mammoth')).default;
        const buf = await file.arrayBuffer();
        const result = await mammoth.extractRawText({ arrayBuffer: buf });
        const text = (result?.value || '').trim();
        if (!text) {
          showToast?.('❌ No readable text found in this .docx.');
          return;
        }
        // Append to any existing pasted content — user can prefix
        // their own thoughts before uploading a doc.
        setContent(prev => prev ? `${prev}\n\n${text}` : text);
        // Auto-fill title from file name if user hasn't set one
        if (!title.trim()) setTitle(name.replace(/\.docx$/i, ''));
      } else {
        // .txt / .md — read as text
        const text = await file.text();
        setContent(prev => prev ? `${prev}\n\n${text}` : text);
        if (!title.trim()) setTitle(name.replace(/\.(txt|md)$/i, ''));
      }
    } catch (err) {
      console.warn('[AddNoteModal] file read failed:', err);
      showToast?.(`❌ Could not read file: ${err.message || err}`);
    } finally {
      setLoading(false);
    }
  };

  const canSave = content.trim().length > 0;

  return (
    <ModalShell title="Add note to this opportunity" onCancel={onCancel}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 14 }}>
        Paste an email, meeting transcript, or free-form note. Or upload a
        <strong style={{ color: '#fff' }}> .txt / .md / .docx </strong>
        file. Saves to your Notes module and auto-links to this opportunity
        so it flows into every AI draft.
      </div>

      <Field label="Title (optional — auto-fills from filename)">
        <input
          value={title}
          onChange={e => setTitle(e.target.value)}
          placeholder="e.g. Client kickoff — 2026-07-01"
          style={modalInput(C)}
        />
      </Field>

      <div style={{ height: 10 }} />

      <Field label="Content — paste text here">
        <textarea
          value={content}
          onChange={e => setContent(e.target.value)}
          rows={14}
          placeholder="Paste email, transcript, meeting notes… anything you want the Coach to consider."
          style={{ ...modalInput(C), resize: 'vertical', fontFamily: "'Inter',sans-serif", fontSize: 12, lineHeight: 1.5 }}
        />
      </Field>

      <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".txt,.md,.docx,text/plain,text/markdown,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          onChange={onFileChosen}
          style={{ display: 'none' }}
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={loading}
          style={{
            background: 'transparent', color: '#22d3ee', border: '1px solid rgba(34,211,238,0.4)',
            borderRadius: 4, padding: '6px 12px', cursor: loading ? 'default' : 'pointer',
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase',
          }}
        >{loading ? 'Reading file…' : '📎 Upload .txt / .md / .docx'}</button>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.45)' }}>
          Text is appended to whatever's already pasted above.
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <ModalActions
          onCancel={onCancel}
          primary={{
            label: 'Save note',
            disabled: !canSave,
            onClick: () => onSave({ title, content }),
          }}
        />
      </div>
    </ModalShell>
  );
}

function CrmTouchpointsPanel({ C, activities, crmDealId, clientName }) {
  const ACT_ICON = { call: '📞', email: '📧', demo: '🖥', proposal: '📄', won: '🏆', lost: '❌', note: '📝', stage: '→' };
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 12 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.12em', textTransform: 'uppercase', flex: 1 }}>
          📞 CRM Touchpoints
        </div>
        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{activities.length}</span>
      </div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 10, lineHeight: 1.5 }}>
        {crmDealId
          ? <>Activity log from the linked CRM deal. Add new touchpoints in the CRM module.</>
          : <>This opportunity isn't linked to a CRM deal yet. Open the deal in CRM and use <strong style={{ color: 'rgba(255,255,255,0.65)' }}>🪈 Open in Proposal Coach</strong> to link them — touchpoints will then appear here automatically.</>}
      </div>
      {!crmDealId ? null : activities.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontStyle: 'italic', padding: '8px 0' }}>
          No CRM activity yet. Calls, emails, stage moves, and proposal events will show up here as you log them in the CRM module.
        </div>
      ) : [...activities].slice().reverse().map((a, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0', borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 14, lineHeight: 1.4, flexShrink: 0 }}>{ACT_ICON[a.type] || '•'}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.78)', lineHeight: 1.45 }}>{a.note}</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.35)', marginTop: 2 }}>{a.date} · {a.type}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Flow-chart banner ────────────────────────────────────────────
// Demo-friendly visualization of the full proposal-coach lifecycle.
// Each card maps to a real artifact in the system; arrows show how
// data flows downstream. APEM / CSA can read this once and grok the
// architecture without a tour guide.
function ProposalCoachFlowChart({ C, subView }) {
  const stages = [
    { icon: '📝', label: 'Notes',        sub: 'pre-CRM captures',     color: '#94A3B8', source: 'Notes module' },
    { icon: '📞', label: 'Touchpoints',  sub: 'calls / emails / mtgs', color: '#60A5FA', source: 'CRM activity' },
    { icon: '🪈', label: 'Opportunity',  sub: 'shaped + scored',       color: '#A78BFA', source: 'Coach captures' },
    { icon: '📚', label: 'Library',      sub: 'past wins + assets',    color: '#E87722', source: 'Org knowledge' },
    { icon: '📄', label: 'Proposal',     sub: 'drafted + assembled',   color: '#F59E0B', source: 'Generated' },
    { icon: '🏆', label: 'Won / Lost',   sub: 'feedback loop',          color: '#22C55E', source: 'Outcome' },
  ];

  return (
    <div style={{ background: 'linear-gradient(135deg, rgba(167,139,250,0.06), rgba(232,119,34,0.04))', border: `1px solid ${C.border}`, borderRadius: 10, padding: '14px 18px', marginBottom: 18 }}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 12 }}>
        How the Proposal Coach works
      </div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, overflowX: 'auto', paddingBottom: 4 }}>
        {stages.map((s, i) => (
          <Fragment key={s.label}>
            <div style={{
              flex: '1 1 0',
              minWidth: 110,
              background: 'rgba(13,27,42,0.6)',
              border: `1px solid ${s.color}40`,
              borderRadius: 8,
              padding: '10px 12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 16 }}>{s.icon}</span>
                <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800, color: s.color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{s.label}</span>
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: '#fff', lineHeight: 1.4 }}>{s.sub}</div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 4, fontStyle: 'italic' }}>{s.source}</div>
            </div>
            {i < stages.length - 1 && (
              <div style={{ display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 14, flexShrink: 0 }}>›</div>
            )}
          </Fragment>
        ))}
      </div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 10, lineHeight: 1.5 }}>
        The AI Coach is wired across the whole lifecycle. It reads Notes + CRM activity for context, pulls relevant precedents from the Library, scores win probability + surfaces gaps, and helps draft the final proposal. Outcomes feed back into the Library as new wins or losses.
      </div>
    </div>
  );
}

// ─── Proposal Library — org-wide knowledge base ───────────────────
// ─── LIBRARY (L1): standalone top-level module ───────────────────
// The library used to live as a sub-tab inside Proposal Coach. It's
// now its own module — same nav-level citizen as CRM, Deal Room,
// Proposal Coach. Reuses ProposalLibraryManager as the workhorse so
// there's no duplicated storage or list logic; this component adds
// the top-level page frame + a slot for the L2/L3/L4/L5 features to
// live in as they come online.
function LibraryModule({ C, currentUser, showToast }) {
  const [tab, setTab] = useState('docs'); // 'docs' | 'connections'
  const isAdmin = ['super_admin', 'admin', 'org_admin'].includes(currentUser.role);
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: '0.02em' }}>Library</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 4, maxWidth: 720, lineHeight: 1.55 }}>
            Org-wide document library — past proposals, case studies, capability decks, regs, and reference material. Search, tag, and pin to opportunities. AI drafters read anything AI-enabled here.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {[{ id: 'docs', label: '📄 Documents' }, ...(isAdmin ? [{ id: 'connections', label: '☁️ Cloud Connections' }] : [])].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
              border: `1px solid ${tab === t.id ? C.accent : C.border}`,
              background: tab === t.id ? `${C.accent}22` : 'transparent',
              color: tab === t.id ? C.accent : 'rgba(255,255,255,0.6)',
            }}>{t.label}</button>
          ))}
        </div>
      </div>
      {tab === 'docs' && <ProposalLibraryManager C={C} currentUser={currentUser} showToast={showToast} />}
      {tab === 'connections' && isAdmin && <CloudConnectionsPanel C={C} currentUser={currentUser} showToast={showToast} />}
    </div>
  );
}

function CloudConnectionsPanel({ C, currentUser, showToast }) {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [editing, setEditing]         = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setConnections(await loadCloudConnections(currentUser.orgId));
    setLoading(false);
  }, [currentUser.orgId]);
  useEffect(() => { refresh(); }, [refresh]);

  const onSave = async (fields) => {
    const result = await saveCloudConnection(currentUser.orgId, currentUser.id, fields);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setEditing(null);
    refresh();
    showToast?.('✓ Connection saved');
  };
  const onRemove = async (c) => {
    if (!confirm(`Remove connection "${c.connection_name}"? Docs already imported from it stay in the library.`)) return;
    const ok = await deleteCloudConnection(c.id);
    if (ok) refresh();
    else showToast?.('❌ Remove failed');
  };

  return (
    <div>
      <div style={{
        padding: 12, marginBottom: 14, borderRadius: 6,
        border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.06)',
        fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: '#fbbf24', lineHeight: 1.55,
      }}>
        ⚠ Cloud sync execution (OAuth flow, folder polling, file import) is scheduled but not yet wired. This panel captures connection metadata so admins can pre-configure the target folders now; automatic sync will start once the OAuth credentials are added to the deployment environment. Docs can still be uploaded directly in the Documents tab in the meantime.
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Cloud Connections
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
          {connections.length} configured
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setEditing({})} style={{
          background: '#fbbf24', color: '#001114', border: 'none', borderRadius: 6,
          padding: '6px 14px', cursor: 'pointer',
          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>+ Add connection</button>
      </div>

      {loading ? (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>Loading…</div>
      ) : connections.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11, border: `1px dashed ${C.border}`, borderRadius: 8 }}>
          No cloud connections yet. Connect a Google Drive folder or S3 bucket to pull docs from it into the library automatically.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {connections.map(c => {
            const meta = CLOUD_PROVIDERS.find(p => p.id === c.provider) || { icon: '☁️', label: c.provider };
            return (
              <div key={c.id} style={{
                border: `1px solid ${c.active ? C.border : 'rgba(255,255,255,0.06)'}`,
                borderRadius: 8, padding: 14, background: c.active ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.15)',
                opacity: c.active ? 1 : 0.6,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 18 }}>{meta.icon}</span>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: '#fff', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.connection_name}
                  </div>
                  {!c.active && (
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 6px', borderRadius: 3, background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.5)' }}>Inactive</span>
                  )}
                </div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 8, lineHeight: 1.55 }}>
                  {meta.label} · {c.sync_mode.replace(/_/g, ' ')}
                  <br />
                  {c.external_folder_name || c.external_folder_id || <em>no folder set</em>}
                </div>
                {c.last_synced_at && (
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>
                    Last synced: {new Date(c.last_synced_at).toLocaleString()}
                  </div>
                )}
                {c.last_sync_error && (
                  <div style={{ padding: 6, borderRadius: 4, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: '#f87171', marginBottom: 8 }}>
                    ⚠ {c.last_sync_error}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setEditing(c)} style={{
                    background: 'transparent', color: 'rgba(255,255,255,0.7)',
                    border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 10px',
                    cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}>Edit</button>
                  <button onClick={() => onRemove(c)} style={{
                    background: 'transparent', color: 'rgba(248,113,113,0.7)',
                    border: '1px solid rgba(248,113,113,0.4)', borderRadius: 4, padding: '4px 10px',
                    cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}>Remove</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <CloudConnectionModal
          C={C}
          existing={editing.id ? editing : null}
          onCancel={() => setEditing(null)}
          onSubmit={onSave}
        />
      )}
    </div>
  );
}

function CloudConnectionModal({ C, existing, onCancel, onSubmit }) {
  const [provider, setProvider]                   = useState(existing?.provider || 'google_drive');
  const [connectionName, setConnectionName]       = useState(existing?.connection_name || '');
  const [externalFolderId, setExternalFolderId]   = useState(existing?.external_folder_id || '');
  const [externalFolderName, setExternalFolderName] = useState(existing?.external_folder_name || '');
  const [syncMode, setSyncMode]                   = useState(existing?.sync_mode || 'pull_on_demand');
  const [autoImport, setAutoImport]               = useState(existing?.auto_import_metadata !== false);
  const [active, setActive]                       = useState(existing?.active !== false);
  const [busy, setBusy]                           = useState(false);

  const submit = async () => {
    setBusy(true);
    await onSubmit({
      id: existing?.id,
      provider,
      connection_name:      connectionName,
      external_folder_id:   externalFolderId,
      external_folder_name: externalFolderName,
      sync_mode:            syncMode,
      auto_import_metadata: autoImport,
      active,
    });
    setBusy(false);
  };

  return (
    <ModalShell title={existing ? `Edit connection · ${existing.connection_name}` : 'Add cloud connection'} onCancel={onCancel}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 14 }}>
        Configure the target folder here. Actual OAuth authorization + sync execution runs when the provider credentials are configured in the deployment environment (see docs/library-cloud-setup for the env vars needed).
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Provider" required>
          <select value={provider} onChange={e => setProvider(e.target.value)} style={modalInput(C)}>
            {CLOUD_PROVIDERS.map(p => <option key={p.id} value={p.id}>{p.icon} {p.label}</option>)}
          </select>
        </Field>
        <Field label="Connection name" required>
          <input value={connectionName} onChange={e => setConnectionName(e.target.value)} placeholder="e.g. 40Geo Proposal Archive" style={modalInput(C)} />
        </Field>
      </div>
      <div style={{ height: 10 }} />
      <Field label="External folder id (Drive fileId / S3 prefix / etc.)">
        <input value={externalFolderId} onChange={e => setExternalFolderId(e.target.value)} placeholder="e.g. 1a2b3c… for a Drive folder" style={modalInput(C)} />
      </Field>
      <div style={{ height: 10 }} />
      <Field label="Folder display name (optional)">
        <input value={externalFolderName} onChange={e => setExternalFolderName(e.target.value)} placeholder='e.g. "/Proposals/2026"' style={modalInput(C)} />
      </Field>
      <div style={{ height: 10 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Sync mode">
          <select value={syncMode} onChange={e => setSyncMode(e.target.value)} style={modalInput(C)}>
            <option value="pull_on_demand">Pull on demand</option>
            <option value="mirror">Mirror (auto-import all)</option>
            <option value="bidirectional">Bidirectional</option>
          </select>
        </Field>
        <Field label=" ">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>
            <input type="checkbox" checked={autoImport} onChange={e => setAutoImport(e.target.checked)} />
            Auto-import metadata (title, mime, size)
          </label>
        </Field>
      </div>
      <div style={{ height: 10 }} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>
        <input type="checkbox" checked={active} onChange={e => setActive(e.target.checked)} />
        Active — set to inactive to pause sync without deleting the config
      </label>

      <div style={{ marginTop: 14 }}>
        <ModalActions
          onCancel={onCancel}
          primary={{
            label: busy ? 'Saving…' : existing ? 'Save' : 'Create connection',
            disabled: busy || !connectionName.trim(),
            onClick: submit,
          }}
        />
      </div>
    </ModalShell>
  );
}

// Shared style for L2 bulk-action buttons — matches height of the
// select-all label so the toolbar row stays visually aligned.
const bulkBtn = (C, tint) => ({
  background: 'transparent', color: tint,
  border: `1px solid ${tint}55`, borderRadius: 4,
  padding: '4px 10px', cursor: 'pointer',
  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
  letterSpacing: '0.06em', textTransform: 'uppercase',
});

function BulkRetagModal({ C, count, existingSectors = [], existingClients = [], onCancel, onSubmit }) {
  const [sectorsInput, setSectorsInput]  = useState('');
  const [clientsInput, setClientsInput]  = useState('');
  const [tagsInput, setTagsInput]        = useState('');
  const [docType, setDocType]            = useState('');
  const [busy, setBusy]                  = useState(false);

  const submit = async () => {
    setBusy(true);
    await onSubmit({
      addSectors: sectorsInput.split(',').map(s => s.trim()).filter(Boolean),
      addClients: clientsInput.split(',').map(s => s.trim()).filter(Boolean),
      addTags:    tagsInput.split(',').map(s => s.trim()).filter(Boolean),
      setDocType: docType || undefined,
    });
    setBusy(false);
  };

  const DOC_TYPES = [
    { id: '', label: '(leave doc type as-is)' },
    { id: 'past_proposal',     label: 'Past proposal' },
    { id: 'case_study',        label: 'Case study' },
    { id: 'capability_deck',   label: 'Capability deck' },
    { id: 'pricing_sheet',     label: 'Pricing sheet' },
    { id: 'sow_template',      label: 'SOW template' },
    { id: 'rfp_response',      label: 'RFP response' },
    { id: 'whitepaper',        label: 'Whitepaper' },
    { id: 'reference_letter',  label: 'Reference letter' },
    { id: 'gov_regulation',    label: 'Gov regulation' },
    { id: 'insurance_cert',    label: 'Insurance cert' },
    { id: 'schedule_of_rates', label: 'Schedule of rates' },
    { id: 'other',             label: 'Other' },
  ];

  return (
    <ModalShell title={`Retag ${count} document${count === 1 ? '' : 's'}`} onCancel={onCancel}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 14 }}>
        Additive — existing tags stay in place. Comma-separated. Blank = no change to that field.
      </div>
      <Field label="Add sectors (comma-separated)">
        <input value={sectorsInput} onChange={e => setSectorsInput(e.target.value)}
          placeholder="e.g. NOAA, fisheries, oil & gas"
          style={modalInput(C)} />
        {existingSectors.length > 0 && (
          <div style={{ marginTop: 4, fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
            Existing in library: {existingSectors.slice(0, 8).join(', ')}{existingSectors.length > 8 ? '…' : ''}
          </div>
        )}
      </Field>
      <div style={{ height: 10 }} />
      <Field label="Add clients (comma-separated)">
        <input value={clientsInput} onChange={e => setClientsInput(e.target.value)}
          placeholder="e.g. OXEA, Occidental Petroleum"
          style={modalInput(C)} />
        {existingClients.length > 0 && (
          <div style={{ marginTop: 4, fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
            Existing in library: {existingClients.slice(0, 8).join(', ')}{existingClients.length > 8 ? '…' : ''}
          </div>
        )}
      </Field>
      <div style={{ height: 10 }} />
      <Field label="Add tags (comma-separated)">
        <input value={tagsInput} onChange={e => setTagsInput(e.target.value)}
          placeholder="e.g. compliance, tropical-response, gulf"
          style={modalInput(C)} />
      </Field>
      <div style={{ height: 10 }} />
      <Field label="Set doc type (optional)">
        <select value={docType} onChange={e => setDocType(e.target.value)} style={modalInput(C)}>
          {DOC_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
      </Field>
      <div style={{ marginTop: 14 }}>
        <ModalActions
          onCancel={onCancel}
          primary={{
            label: busy ? 'Applying…' : `Apply to ${count}`,
            disabled: busy || (!sectorsInput.trim() && !clientsInput.trim() && !tagsInput.trim() && !docType),
            onClick: submit,
          }}
        />
      </div>
    </ModalShell>
  );
}

function BulkGrantAccessModal({ C, count, currentUser, onCancel, onSubmit }) {
  const [principalType, setPrincipalType] = useState('user');
  const [users, setUsers]                 = useState([]);
  const [pickedUserId, setPickedUserId]   = useState('');
  const [companyName, setCompanyName]     = useState('');
  const [permission, setPermission]       = useState('ai_reference');
  const [busy, setBusy]                   = useState(false);

  useEffect(() => {
    if (principalType !== 'user') return;
    (async () => setUsers(await loadOrgUsersForAcl(currentUser.orgId)))();
  }, [principalType, currentUser.orgId]);

  const canSubmit = principalType === 'user'
    ? !!pickedUserId
    : principalType === 'company'
      ? !!companyName.trim()
      : false;

  const submit = async () => {
    setBusy(true);
    const principal = principalType === 'user'
      ? { type: 'user', id: pickedUserId }
      : { type: 'company', name: companyName.trim() };
    await onSubmit({ principal, permission });
    setBusy(false);
  };

  return (
    <ModalShell title={`Grant access to ${count} document${count === 1 ? '' : 's'}`} onCancel={onCancel}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 14 }}>
        Grants apply to every selected doc at once — the "search NOAA → select all → grant reviewer access" flow. Existing grants for the same principal + permission are left as-is (no duplicate rows).
      </div>

      <Field label="Grant to">
        <div style={{ display: 'flex', gap: 6 }}>
          {[
            { id: 'user',    label: '👤 A specific user' },
            { id: 'company', label: '🏢 A company (all users in that company)' },
          ].map(opt => {
            const on = principalType === opt.id;
            return (
              <button key={opt.id} type="button" onClick={() => setPrincipalType(opt.id)} style={{
                flex: 1, padding: '8px 10px', borderRadius: 6, cursor: 'pointer',
                background: on ? 'rgba(251,191,36,0.12)' : 'transparent',
                border: `1px solid ${on ? '#fbbf24' : 'rgba(255,255,255,0.18)'}`,
                color: on ? '#fbbf24' : 'rgba(255,255,255,0.6)',
                fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.05em',
              }}>{opt.label}</button>
            );
          })}
        </div>
      </Field>
      <div style={{ height: 10 }} />

      {principalType === 'user' && (
        <Field label="Pick user" required>
          <select value={pickedUserId} onChange={e => setPickedUserId(e.target.value)} style={modalInput(C)}>
            <option value="">— Choose a user —</option>
            {users.filter(u => u.id !== currentUser.id).map(u => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}{u.company ? ` · ${u.company}` : ''}{u.role ? ` · ${u.role}` : ''}
              </option>
            ))}
          </select>
        </Field>
      )}
      {principalType === 'company' && (
        <Field label="Company name" required>
          <input
            value={companyName}
            onChange={e => setCompanyName(e.target.value)}
            placeholder="Exact company name as set on users.company"
            style={modalInput(C)}
          />
          <div style={{ marginTop: 4, fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>
            Matches any user whose <code style={{ background: 'rgba(255,255,255,0.06)', padding: '1px 5px', borderRadius: 2 }}>company</code> field equals this string.
          </div>
        </Field>
      )}
      <div style={{ height: 10 }} />

      <Field label="Permission">
        <select value={permission} onChange={e => setPermission(e.target.value)} style={modalInput(C)}>
          {LIBRARY_PERMISSIONS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </Field>

      <div style={{ marginTop: 14 }}>
        <ModalActions
          onCancel={onCancel}
          primary={{
            label: busy ? 'Granting…' : `Grant on ${count} doc${count === 1 ? '' : 's'}`,
            disabled: busy || !canSubmit,
            onClick: submit,
          }}
        />
      </div>
    </ModalShell>
  );
}

function ProposalLibraryManager({ C, currentUser, showToast }) {
  const [docs, setDocs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filterDocType, setFilterDocType] = useState('');
  const [filterSector, setFilterSector]   = useState('');
  const [filterClient, setFilterClient]   = useState('');
  const [searchQ, setSearchQ]   = useState('');
  const [uploadProgress, setUploadProgress] = useState(null);
  const [showUploadMeta, setShowUploadMeta] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadMeta, setUploadMeta] = useState({ document_type: 'past_proposal', sectors: [], clients: [], geo_scope: [], tags: '' });
  const [preparingId, setPreparingId] = useState(null);
  const [bulkPrepping, setBulkPrepping] = useState(false);

  // L2 bulk-ops selection. Set of doc ids the user has ticked.
  // "Select all matching filter" writes every filtered id into this
  // Set, so filter-then-act (search NOAA → select all → action)
  // is two clicks.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [showBulkRetag, setShowBulkRetag] = useState(false);
  const [showBulkGrant, setShowBulkGrant] = useState(false);
  const [bulkWorking, setBulkWorking]     = useState(false);

  // L5: saved searches (per-user)
  const [savedSearches, setSavedSearches] = useState([]);
  useEffect(() => {
    (async () => setSavedSearches(await loadSavedSearches(currentUser.id)))();
  }, [currentUser.id]);
  const currentFilterSet = useMemo(() => ({
    query:           searchQ,
    filter_doc_type: filterDocType || null,
    filter_sectors:  filterSector ? [filterSector] : [],
    filter_clients:  filterClient ? [filterClient] : [],
    filter_tags:     [],
    filter_ai_only:  false,
  }), [searchQ, filterDocType, filterSector, filterClient]);
  const saveCurrentAsSearch = async () => {
    const name = prompt('Name this search (e.g. "NOAA fisheries — active"):');
    if (!name?.trim()) return;
    const result = await saveSavedSearch(currentUser.id, currentUser.orgId, { ...currentFilterSet, name });
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setSavedSearches(prev => [result.row, ...prev.filter(s => s.id !== result.row.id)]);
    showToast?.(`✓ Saved "${name}"`);
  };
  const applySavedSearch = (s) => {
    setSearchQ(s.query || '');
    setFilterDocType(s.filter_doc_type || '');
    setFilterSector(s.filter_sectors?.[0] || '');
    setFilterClient(s.filter_clients?.[0] || '');
  };
  const removeSavedSearch = async (s) => {
    if (!confirm(`Delete saved search "${s.name}"?`)) return;
    const ok = await deleteSavedSearch(s.id);
    if (ok) setSavedSearches(prev => prev.filter(x => x.id !== s.id));
  };
  const toggleSelected = (id) => setSelectedIds(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const refresh = useCallback(async () => {
    setLoading(true);
    setDocs(await loadProposalLibrary(currentUser.orgId));
    setLoading(false);
  }, [currentUser.orgId]);
  useEffect(() => { refresh(); }, [refresh]);

  // Doc currently open in the viewer modal — null when closed.
  const [viewingDoc, setViewingDoc] = useState(null);

  // Derived: unique sectors / clients present in the library, for
  // the filter dropdowns. Saves the user typing.
  const allSectors = useMemo(() => [...new Set(docs.flatMap(d => d.sectors || []))].sort(), [docs]);
  const allClients = useMemo(() => [...new Set(docs.flatMap(d => d.clients || []))].sort(), [docs]);

  const filtered = useMemo(() => {
    return docs.filter(d => {
      if (filterDocType && d.document_type !== filterDocType) return false;
      if (filterSector && !(d.sectors || []).includes(filterSector)) return false;
      if (filterClient && !(d.clients || []).includes(filterClient)) return false;
      if (searchQ) {
        const hay = `${d.title} ${d.extracted_title || ''} ${d.extracted_summary || ''} ${d.description || ''}`.toLowerCase();
        if (!hay.includes(searchQ.toLowerCase())) return false;
      }
      return true;
    });
  }, [docs, filterDocType, filterSector, filterClient, searchQ]);

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    e.target.value = '';
    if (!list.length) return;
    setPendingFiles(list);
    setShowUploadMeta(true);
  };

  const submitUpload = async () => {
    const files = pendingFiles;
    const meta = {
      document_type: uploadMeta.document_type,
      sectors:       uploadMeta.sectors,
      clients:       uploadMeta.clients,
      geo_scope:     uploadMeta.geo_scope,
      tags:          uploadMeta.tags ? uploadMeta.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    };
    setShowUploadMeta(false);
    setPendingFiles([]);
    setUploadProgress({ done: 0, total: files.length, current: files[0]?.name });
    let done = 0, failed = 0, firstError = null;
    for (const f of files) {
      setUploadProgress({ done, total: files.length, current: f.name });
      const result = await uploadProposalLibraryFile(currentUser.orgId, currentUser.id, f, meta);
      if (result.error) { failed++; firstError ??= result.error; }
      done++;
    }
    setUploadProgress(null);
    if (failed) showToast?.(`⚠ Uploaded ${files.length - failed}/${files.length} — ${failed} failed${firstError ? `: ${firstError}` : ''}`);
    else        showToast?.(`✓ Uploaded ${files.length} doc${files.length === 1 ? '' : 's'} to library`);
    setUploadMeta({ document_type: 'past_proposal', sectors: [], clients: [], geo_scope: [], tags: '' });
    refresh();
  };

  const toggleAi = async (doc) => {
    const next = !doc.ai_enabled;
    setPreparingId(doc.id);
    const result = await setProposalLibraryAiEnabled(doc.id, next);
    setPreparingId(null);
    if (result.error) showToast?.(`❌ AI prep: ${result.error}`);
    else showToast?.(next ? `✓ "${doc.title}" is AI-ready` : '↩ AI prep cleared');
    refresh();
  };

  const enableAiAll = async () => {
    const pending = filtered.filter(d => d.storage_path && !(d.ai_enabled && (d.anthropic_file_id || d.extracted_content)));
    if (!pending.length) { showToast?.('Nothing to prep — all visible docs are AI-ready.'); return; }
    if (!confirm(`Enable AI on ${pending.length} document${pending.length === 1 ? '' : 's'}? PDFs → Files API; DOCX/PPTX → text extract. May take a minute.`)) return;
    setBulkPrepping(true);
    let ok = 0, failed = 0;
    for (const d of pending) {
      setPreparingId(d.id);
      const result = await setProposalLibraryAiEnabled(d.id, true);
      if (result.error) failed++; else ok++;
    }
    setPreparingId(null);
    setBulkPrepping(false);
    showToast?.(failed ? `⚠ Prepped ${ok}/${pending.length} — ${failed} failed` : `✓ ${ok} doc${ok === 1 ? '' : 's'} AI-ready`);
    refresh();
  };

  const remove = async (d) => {
    if (!confirm(`Remove "${d.title}" from library?`)) return;
    await deleteProposalLibrary(d);
    refresh();
  };

  // ── L2 bulk operations ────────────────────────────────────────
  // All bulk actions iterate the currently-selected doc list. The
  // "select all matching filter" affordance in the header populates
  // selectedIds from the filtered list, so the filter-then-act flow
  // is: apply filter → click select-all → click bulk action.
  const bulkEnableAi = async () => {
    const target = docs.filter(d => selectedIds.has(d.id) && d.storage_path && !(d.ai_enabled && (d.anthropic_file_id || d.extracted_content)));
    if (!target.length) { showToast?.('No selected docs need AI-prep.'); return; }
    if (!confirm(`Enable AI on ${target.length} selected doc${target.length === 1 ? '' : 's'}? PDFs → Files API; DOCX/PPTX → text extract.`)) return;
    setBulkWorking(true);
    let ok = 0, failed = 0;
    for (const d of target) {
      setPreparingId(d.id);
      const r = await setProposalLibraryAiEnabled(d.id, true);
      if (r.error) failed++; else ok++;
    }
    setPreparingId(null);
    setBulkWorking(false);
    showToast?.(failed ? `⚠ Prepped ${ok}/${target.length} — ${failed} failed` : `✓ ${ok} doc${ok === 1 ? '' : 's'} AI-ready`);
    refresh();
  };
  const bulkDisableAi = async () => {
    const target = docs.filter(d => selectedIds.has(d.id) && d.ai_enabled);
    if (!target.length) { showToast?.('No selected docs have AI enabled.'); return; }
    if (!confirm(`Disable AI access for ${target.length} selected doc${target.length === 1 ? '' : 's'}? They'll stop appearing in Coach context.`)) return;
    setBulkWorking(true);
    for (const d of target) await setProposalLibraryAiEnabled(d.id, false);
    setBulkWorking(false);
    showToast?.(`✓ AI disabled on ${target.length} doc${target.length === 1 ? '' : 's'}`);
    refresh();
  };
  const bulkDelete = async () => {
    const target = docs.filter(d => selectedIds.has(d.id));
    if (!target.length) return;
    if (!confirm(`DELETE ${target.length} selected doc${target.length === 1 ? '' : 's'} from the library? This removes files from storage. Cannot be undone.`)) return;
    setBulkWorking(true);
    for (const d of target) await deleteProposalLibrary(d);
    setBulkWorking(false);
    showToast?.(`✓ Deleted ${target.length} doc${target.length === 1 ? '' : 's'}`);
    setSelectedIds(new Set());
    refresh();
  };
  const bulkRetag = async (patch) => {
    const target = docs.filter(d => selectedIds.has(d.id));
    if (!target.length) return;
    setBulkWorking(true);
    const { ok, failed } = await bulkMergeLibraryTags(target, patch);
    setBulkWorking(false);
    setShowBulkRetag(false);
    showToast?.(failed ? `⚠ Updated ${ok}/${target.length} — ${failed} failed` : `✓ Retagged ${ok} doc${ok === 1 ? '' : 's'}`);
    refresh();
  };
  const bulkGrant = async ({ principal, permission }) => {
    const targetIds = Array.from(selectedIds);
    if (!targetIds.length) return;
    setBulkWorking(true);
    const { ok, existed, failed } = await bulkGrantLibraryAccess(targetIds, principal, permission, currentUser.id);
    setBulkWorking(false);
    setShowBulkGrant(false);
    const parts = [];
    if (ok > 0)      parts.push(`granted on ${ok}`);
    if (existed > 0) parts.push(`${existed} already granted`);
    if (failed > 0)  parts.push(`${failed} failed`);
    showToast?.(`✓ Bulk grant — ${parts.join(' · ')}`);
  };

  const DOC_TYPES = [
    { id: 'past_proposal',     label: 'Past proposal' },
    { id: 'case_study',        label: 'Case study' },
    { id: 'capability_deck',   label: 'Capability deck' },
    { id: 'pricing_sheet',     label: 'Pricing sheet' },
    { id: 'sow_template',      label: 'SOW template' },
    { id: 'rfp_response',      label: 'RFP response' },
    { id: 'whitepaper',        label: 'Whitepaper' },
    { id: 'reference_letter',  label: 'Reference letter' },
    { id: 'gov_regulation',    label: 'Gov regulation' },
    { id: 'insurance_cert',    label: 'Insurance cert' },
    { id: 'schedule_of_rates', label: 'Schedule of rates' },
    { id: 'other',             label: 'Other' },
  ];

  const inp = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: "'Montserrat',sans-serif" };

  return (
    <div>
      {/* Filter / search / upload toolbar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search title / summary / body / tags"
          style={{ ...inp, width: 260 }} />
        {(searchQ.trim() || filterDocType || filterSector || filterClient) && (
          <button onClick={saveCurrentAsSearch} title="Save current search + filters as a shortcut" style={{
            background: 'transparent', color: '#22d3ee',
            border: '1px solid rgba(34,211,238,0.4)', borderRadius: 4, padding: '4px 10px', cursor: 'pointer',
            fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>💾 Save search</button>
        )}
        {savedSearches.length > 0 && (
          <select
            value=""
            onChange={e => {
              const s = savedSearches.find(x => x.id === e.target.value);
              if (s) applySavedSearch(s);
              else if (e.target.value === '__clear__') {
                setSearchQ(''); setFilterDocType(''); setFilterSector(''); setFilterClient('');
              }
            }}
            style={{ ...inp, width: 200 }}
            title="Apply a saved search"
          >
            <option value="">📌 Saved searches…</option>
            {savedSearches.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
            <option value="__clear__">— Clear filters —</option>
          </select>
        )}
        <select value={filterDocType} onChange={e => setFilterDocType(e.target.value)} style={inp}>
          <option value="">Any type</option>
          {DOC_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <select value={filterSector} onChange={e => setFilterSector(e.target.value)} style={inp}>
          <option value="">Any sector</option>
          {allSectors.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={inp}>
          <option value="">Any client</option>
          {allClients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        <button onClick={enableAiAll} disabled={bulkPrepping || !!uploadProgress}
          style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.45)', color: '#A78BFA', borderRadius: 6, padding: '6px 12px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {bulkPrepping ? 'Prepping…' : '🤖 Enable AI on visible'}
        </button>
        <label style={{ background: 'rgba(232,119,34,0.15)', border: '1px solid rgba(232,119,34,0.4)', color: '#E87722', borderRadius: 6, padding: '6px 12px', fontSize: 10, fontWeight: 700, cursor: uploadProgress ? 'wait' : 'pointer', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {uploadProgress
            ? `Uploading ${uploadProgress.done + 1}/${uploadProgress.total}…`
            : '+ Upload docs'}
          <input type="file" multiple disabled={!!uploadProgress}
            accept=".pdf,.docx,.pptx,.txt,.md,.csv"
            onChange={onPickFiles} style={{ display: 'none' }} />
        </label>
      </div>

      {/* L2 Bulk-selection bar: header (visible when ≥1 doc filtered) +
          action bar (visible when ≥1 doc selected). filter-then-act:
          click "Select all filtered" then any bulk action. */}
      {filtered.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, padding: '6px 10px',
          border: `1px solid ${selectedIds.size > 0 ? '#A78BFA55' : C.border}`,
          background: selectedIds.size > 0 ? 'rgba(167,139,250,0.08)' : 'rgba(255,255,255,0.02)',
          borderRadius: 6, flexWrap: 'wrap',
        }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.75)', fontWeight: 700, letterSpacing: '0.06em' }}>
            <input
              type="checkbox"
              checked={filtered.length > 0 && filtered.every(d => selectedIds.has(d.id))}
              onChange={e => {
                if (e.target.checked) setSelectedIds(new Set(filtered.map(d => d.id)));
                else setSelectedIds(new Set());
              }}
              style={{ accentColor: '#A78BFA' }}
            />
            {selectedIds.size === 0
              ? `Select all ${filtered.length} filtered`
              : `${selectedIds.size} selected${filtered.length !== docs.length ? ' (from filter)' : ''}`}
          </label>
          {selectedIds.size > 0 && (
            <>
              <button onClick={bulkEnableAi} disabled={bulkWorking} style={bulkBtn(C, '#A78BFA')}>🤖 Enable AI</button>
              <button onClick={bulkDisableAi} disabled={bulkWorking} style={bulkBtn(C, 'rgba(255,255,255,0.6)')}>🚫 Disable AI</button>
              <button onClick={() => setShowBulkRetag(true)} disabled={bulkWorking} style={bulkBtn(C, '#22d3ee')}>🏷 Retag</button>
              <button onClick={() => setShowBulkGrant(true)} disabled={bulkWorking} style={bulkBtn(C, '#fbbf24')}>🔒 Grant access</button>
              <button onClick={bulkDelete} disabled={bulkWorking} style={bulkBtn(C, '#f87171')}>🗑 Delete</button>
              <div style={{ flex: 1 }} />
              <button onClick={() => setSelectedIds(new Set())} disabled={bulkWorking} style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>Clear</button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.4)', padding: 40, textAlign: 'center' }}>Loading library…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 12, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
          {docs.length === 0
            ? 'No library documents yet. Upload past proposals, case studies, capability decks — anything you want the Coach to draw from.'
            : 'No documents match the current filter.'}
        </div>
      ) : (
        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10 }}>
          {filtered.map(d => {
            const aiReady   = d.ai_enabled && (!!d.anthropic_file_id || !!d.extracted_content);
            const aiPending = preparingId === d.id;
            const aiError   = d.ai_enabled && !aiReady && !aiPending && d.ai_prep_error;
            return (
              <div key={d.id} style={{
                display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto auto', gap: 12, alignItems: 'center', padding: '12px 16px', borderTop: `1px solid ${C.border}`,
                background: selectedIds.has(d.id) ? 'rgba(167,139,250,0.06)' : 'transparent',
              }}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(d.id)}
                  onChange={() => toggleSelected(d.id)}
                  style={{ accentColor: '#A78BFA', cursor: 'pointer' }}
                  title="Select for bulk action"
                />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.extracted_title || d.title}
                    {aiReady   && <span style={{ marginLeft: 8, fontSize: 9, color: '#A78BFA', fontWeight: 700 }}>🤖 AI-ready</span>}
                    {aiPending && <span style={{ marginLeft: 8, fontSize: 9, color: '#FFB830', fontWeight: 700 }}>🤖 preparing…</span>}
                    {aiError   && <span title={d.ai_prep_error} style={{ marginLeft: 8, fontSize: 9, color: '#EF4444', fontWeight: 700 }}>🤖 prep failed</span>}
                  </div>
                  {d.extracted_summary && (
                    <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 3, lineHeight: 1.4 }}>{d.extracted_summary}</div>
                  )}
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 5 }}>
                    {d.document_type && <LibraryTag color="#94A3B8" text={DOC_TYPES.find(t => t.id === d.document_type)?.label || d.document_type} />}
                    {(d.sectors || []).map(s => <LibraryTag key={`s-${s}`} color="#60A5FA" text={s} />)}
                    {(d.clients || []).map(c => <LibraryTag key={`c-${c}`} color="#A78BFA" text={c} />)}
                    {(d.tags || []).map(t => <LibraryTag key={`t-${t}`} color="rgba(255,255,255,0.3)" text={`#${t}`} />)}
                  </div>
                </div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>
                  {d.size_bytes ? `${Math.round(d.size_bytes / 1024).toLocaleString()} KB` : (d.url ? 'URL' : '—')}
                </div>
                {(d.storage_path || d.url) && (
                  <button
                    onClick={() => d.storage_path ? setViewingDoc(d) : window.open(d.url, '_blank', 'noopener')}
                    title={d.storage_path ? 'View file inline' : 'Open URL in new tab'}
                    style={{
                      background: 'rgba(96,165,250,0.1)', border: '1px solid rgba(96,165,250,0.35)',
                      color: '#60A5FA', borderRadius: 5, padding: '4px 10px',
                      fontSize: 10, fontWeight: 700, cursor: 'pointer',
                      fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.04em',
                    }}
                  >
                    👁 View
                  </button>
                )}
                <label title={d.ai_enabled ? 'Disable AI access' : 'Prep this doc for the AI Coach to reference'}
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', border: `1px solid ${d.ai_enabled ? 'rgba(167,139,250,0.5)' : C.border}`, background: d.ai_enabled ? 'rgba(167,139,250,0.12)' : 'transparent', borderRadius: 5, cursor: aiPending ? 'wait' : 'pointer', fontSize: 10, fontWeight: 700, color: d.ai_enabled ? '#A78BFA' : 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif" }}>
                  <input type="checkbox" checked={!!d.ai_enabled} disabled={aiPending}
                    onChange={() => toggleAi(d)} style={{ accentColor: '#A78BFA' }} />
                  🤖 AI
                </label>
                <button onClick={() => remove(d)}
                  style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Inline viewer for the currently-open document. */}
      {viewingDoc && (
        <DocViewerModal
          doc={viewingDoc}
          bucket="proposal-library"
          onClose={() => setViewingDoc(null)}
        />
      )}

      {/* Upload metadata modal */}
      {showBulkRetag && (
        <BulkRetagModal
          C={C}
          count={selectedIds.size}
          existingSectors={allSectors}
          existingClients={allClients}
          onCancel={() => setShowBulkRetag(false)}
          onSubmit={bulkRetag}
        />
      )}
      {showBulkGrant && (
        <BulkGrantAccessModal
          C={C}
          count={selectedIds.size}
          currentUser={currentUser}
          onCancel={() => setShowBulkGrant(false)}
          onSubmit={bulkGrant}
        />
      )}
      {showUploadMeta && (
        <ModalShell title={`Tag ${pendingFiles.length} document${pendingFiles.length === 1 ? '' : 's'}`} onCancel={() => { setShowUploadMeta(false); setPendingFiles([]); }}>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 12 }}>
            These tags apply to all {pendingFiles.length} file{pendingFiles.length === 1 ? '' : 's'} in this batch. You can edit each one individually after upload.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <Field label="Document type">
              <select value={uploadMeta.document_type} onChange={e => setUploadMeta({ ...uploadMeta, document_type: e.target.value })} style={modalInput(C)}>
                {DOC_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </Field>
            <Field label="Tags (comma-separated)">
              <input value={uploadMeta.tags} onChange={e => setUploadMeta({ ...uploadMeta, tags: e.target.value })} placeholder="federal, marine, q3-2024" style={modalInput(C)} />
            </Field>
            <Field label="Sectors (comma-separated)">
              <input value={uploadMeta.sectors.join(', ')} onChange={e => setUploadMeta({ ...uploadMeta, sectors: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="government, environmental" style={modalInput(C)} />
            </Field>
            <Field label="Clients (comma-separated)">
              <input value={uploadMeta.clients.join(', ')} onChange={e => setUploadMeta({ ...uploadMeta, clients: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })}
                placeholder="NOAA, UK EPA" style={modalInput(C)} />
            </Field>
          </div>
          <div style={{ marginTop: 14 }}>
            <ModalActions onCancel={() => { setShowUploadMeta(false); setPendingFiles([]); }} primary={{
              label: `Upload ${pendingFiles.length} file${pendingFiles.length === 1 ? '' : 's'}`,
              disabled: false,
              onClick: submitUpload,
            }} />
          </div>
        </ModalShell>
      )}
    </div>
  );
}

function LibraryTag({ color, text }) {
  return (
    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, padding: '2px 7px', borderRadius: 999, background: `${color}1f`, color, border: `1px solid ${color}44`, letterSpacing: '0.04em', fontWeight: 700 }}>
      {text}
    </span>
  );
}

// ─── Reference Material panel ─────────────────────────────────────
// Per-opportunity pinned library docs. Lives in opportunity.pinned_
// library_ids (UUID[]). These docs are the "gold-standard precedents"
// for THIS opportunity — always rides along with each AI Coach turn
// ahead of any scope-filtered library docs.
function ArtifactProfilesManager({ C, currentUser, showToast }) {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState(null); // profile row OR {} for add

  const refresh = useCallback(async () => {
    setLoading(true);
    setProfiles(await loadArtifactProfiles(currentUser.orgId));
    setLoading(false);
  }, [currentUser.orgId]);
  useEffect(() => { refresh(); }, [refresh]);

  const onSubmit = async (fields) => {
    const result = await upsertArtifactProfile(currentUser.orgId, fields, currentUser.id);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setEditing(null);
    refresh();
    showToast?.('✓ Profile saved');
  };
  const onRemove = async (p) => {
    if (!confirm(`Delete profile "${p.name}"? Opportunities already created from it will keep their seeded sections, but no new opportunities can use it.`)) return;
    const ok = await deleteArtifactProfile(p.id);
    if (ok) setProfiles(prev => prev.filter(x => x.id !== p.id));
    else showToast?.('❌ Delete failed');
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 22 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 800, color: '#fff', letterSpacing: '0.04em' }}>Artifact profiles</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.5)', marginTop: 4, lineHeight: 1.5, maxWidth: 720 }}>
            Org-level templates that drive what gets seeded when a new opportunity is created. Define profiles for the RFP categories you bid on most (Federal, State DOT, Enterprise SaaS, Academic Grant) so the deliverables checklist + proposal sections match the format expected.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setEditing({})} style={{
          background: C.accent, color: '#fff', border: 'none', borderRadius: 6,
          padding: '8px 16px', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
          fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase',
        }}>+ New profile</button>
      </div>

      {loading ? (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>Loading…</div>
      ) : profiles.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
          No profiles yet. New opportunities will be seeded with the generic defaults until you add one.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
          {profiles.map(p => (
            <div key={p.id} style={{
              border: `1px solid ${p.is_default ? C.accent + '55' : C.border}`,
              borderRadius: 8, padding: 14, background: p.is_default ? `${C.accent}08` : 'rgba(255,255,255,0.02)',
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: '#fff', flex: 1 }}>{p.name}</div>
                {p.is_default && (
                  <span style={{
                    fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    padding: '2px 6px', borderRadius: 3,
                    background: `${C.accent}22`, color: C.accent, border: `1px solid ${C.accent}55`,
                  }}>Default</span>
                )}
              </div>
              {p.description && (
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 10 }}>
                  {p.description}
                </div>
              )}
              <div style={{ display: 'flex', gap: 14, fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 12 }}>
                <span>📤 {(p.default_outbound_artifacts || []).length} deliverables</span>
                <span>📝 {(p.default_proposal_sections || []).length} sections</span>
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => setEditing(p)} style={{
                  background: 'transparent', color: 'rgba(255,255,255,0.7)',
                  border: `1px solid ${C.border}`, borderRadius: 4, padding: '4px 10px',
                  cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>Edit</button>
                <button onClick={() => onRemove(p)} style={{
                  background: 'transparent', color: 'rgba(248,113,113,0.7)',
                  border: '1px solid rgba(248,113,113,0.4)', borderRadius: 4, padding: '4px 10px',
                  cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                  fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                }}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ArtifactProfileModal C={C} profile={editing.id ? editing : null} onCancel={() => setEditing(null)} onSubmit={onSubmit} />
      )}
    </div>
  );
}

function ArtifactProfileModal({ C, profile, onCancel, onSubmit }) {
  const [name, setName]               = useState(profile?.name || '');
  const [description, setDescription] = useState(profile?.description || '');
  const [isDefault, setIsDefault]     = useState(!!profile?.is_default);
  const [outboundJson, setOutboundJson] = useState(JSON.stringify(profile?.default_outbound_artifacts || [{ artifact_type: 'cover_letter', name: 'Cover letter' }], null, 2));
  const [sectionsJson, setSectionsJson] = useState(JSON.stringify(profile?.default_proposal_sections || [{ section_type: 'exec_summary', title: 'Executive summary', sequence_idx: 10 }], null, 2));
  const [error, setError] = useState('');

  const handleSubmit = () => {
    setError('');
    let outbound, sections;
    try { outbound = JSON.parse(outboundJson); } catch (e) { setError(`Outbound JSON: ${e.message}`); return; }
    try { sections = JSON.parse(sectionsJson); } catch (e) { setError(`Sections JSON: ${e.message}`); return; }
    if (!Array.isArray(outbound)) { setError('Outbound must be a JSON array'); return; }
    if (!Array.isArray(sections)) { setError('Sections must be a JSON array'); return; }
    onSubmit({
      id: profile?.id,
      name, description, is_default: isDefault,
      default_outbound_artifacts: outbound,
      default_proposal_sections:  sections,
    });
  };

  return (
    <ModalShell title={profile ? `Edit · ${profile.name}` : 'New artifact profile'} onCancel={onCancel}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <Field label="Profile name" required>
          <input value={name} onChange={e => setName(e.target.value)} style={modalInput(C)} placeholder="e.g. Federal RFP" />
        </Field>
        <Field label="Make default for new opportunities">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: 8, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.75)' }}>
            <input type="checkbox" checked={isDefault} onChange={e => setIsDefault(e.target.checked)} />
            Default
          </label>
        </Field>
      </div>
      <div style={{ height: 10 }} />
      <Field label="Description (what RFP category this is for)">
        <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }} />
      </Field>
      <div style={{ height: 12 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Default outbound deliverables (JSON)">
          <textarea rows={8} value={outboundJson} onChange={e => setOutboundJson(e.target.value)}
            style={{ ...modalInput(C), resize: 'vertical', fontFamily: 'Menlo, monospace', fontSize: 10 }} />
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Each item: {`{ "artifact_type": "<id>", "name": "<label>" }`}
          </div>
        </Field>
        <Field label="Default proposal sections (JSON)">
          <textarea rows={8} value={sectionsJson} onChange={e => setSectionsJson(e.target.value)}
            style={{ ...modalInput(C), resize: 'vertical', fontFamily: 'Menlo, monospace', fontSize: 10 }} />
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Each item: {`{ "section_type": "<id>", "title": "<label>", "sequence_idx": <number> }`}
          </div>
        </Field>
      </div>
      {error && (
        <div style={{ marginTop: 10, padding: 10, borderRadius: 4, background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: '#f87171' }}>
          {error}
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: profile ? 'Save' : 'Create profile',
          disabled: !name.trim(),
          onClick: handleSubmit,
        }} />
      </div>
    </ModalShell>
  );
}

function ReferenceMaterialPanel({ C, opp, currentUser, onUpdated, showToast }) {
  const [pinnedDocs, setPinnedDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);

  const pinnedIds = useMemo(
    () => Array.isArray(opp.pinned_library_ids) ? opp.pinned_library_ids : [],
    [opp.pinned_library_ids]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const list = pinnedIds.length
        ? await loadProposalLibraryByIds(currentUser.orgId, pinnedIds)
        : [];
      if (!cancelled) { setPinnedDocs(list); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [currentUser.orgId, pinnedIds.join('|')]);  // re-run when set changes

  const persist = async (newIds) => {
    const ok = await updateOpportunity(opp.id, { pinned_library_ids: newIds });
    if (!ok) { showToast?.('❌ Pin update failed'); return; }
    onUpdated?.({ pinned_library_ids: newIds });
  };

  const unpin = async (docId) => {
    await persist(pinnedIds.filter(id => id !== docId));
    showToast?.('Reference removed');
  };

  return (
    <div style={{ background: 'rgba(167,139,250,0.04)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 12 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, color: '#A78BFA', letterSpacing: '0.12em', textTransform: 'uppercase', flex: 1 }}>
          📌 Reference Material
        </div>
        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>{pinnedIds.length}</span>
      </div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 10, lineHeight: 1.5 }}>
        Library docs pinned as <strong style={{ color: 'rgba(255,255,255,0.75)' }}>definitive precedents</strong> for this opportunity. The AI Coach always pulls these in, even when the library scope filter is off.
      </div>

      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, padding: '6px 0' }}>Loading references…</div>
      ) : pinnedDocs.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontStyle: 'italic', padding: '8px 0' }}>
          No references pinned yet. For OXEA-style scenarios where a different client's contract is the right precedent, pin those docs here so the Coach treats them as the gold standard.
        </div>
      ) : pinnedDocs.map(d => (
        <div key={d.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, padding: '8px 0', borderTop: `1px solid ${C.border}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {d.extracted_title || d.title}
              {d.outcome && <span style={{ marginLeft: 6, fontSize: 9, color: d.outcome === 'won' ? '#22C55E' : '#EF4444', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>· {d.outcome}</span>}
            </div>
            {d.extracted_summary && (
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3, lineHeight: 1.4 }}>{d.extracted_summary}</div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
              {(d.clients || []).slice(0, 3).map(c => (
                <span key={c} style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 8, padding: '1px 6px', borderRadius: 999, background: 'rgba(167,139,250,0.18)', color: '#A78BFA', letterSpacing: '0.04em', fontWeight: 700 }}>{c}</span>
              ))}
              {(d.sectors || []).slice(0, 3).map(s => (
                <span key={s} style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 8, padding: '1px 6px', borderRadius: 999, background: 'rgba(96,165,250,0.18)', color: '#60A5FA', letterSpacing: '0.04em', fontWeight: 700 }}>{s}</span>
              ))}
            </div>
          </div>
          <button onClick={() => unpin(d.id)} title="Unpin"
            style={{ background: 'transparent', border: 'none', color: 'rgba(255,107,107,0.5)', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
        </div>
      ))}

      <button onClick={() => setShowPicker(true)}
        style={{ marginTop: 12, width: '100%', background: 'rgba(167,139,250,0.12)', border: '1px dashed rgba(167,139,250,0.45)', color: '#A78BFA', borderRadius: 6, padding: '8px 12px', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        + Pin from Library
      </button>

      {showPicker && (
        <PinFromLibraryModal
          C={C}
          orgId={currentUser.orgId}
          alreadyPinnedIds={pinnedIds}
          onCancel={() => setShowPicker(false)}
          onConfirm={async (ids) => {
            await persist([...pinnedIds, ...ids.filter(id => !pinnedIds.includes(id))]);
            setShowPicker(false);
            showToast?.(`✓ Pinned ${ids.length} reference${ids.length === 1 ? '' : 's'}`);
          }}
        />
      )}
    </div>
  );
}

function PinFromLibraryModal({ C, orgId, alreadyPinnedIds, onCancel, onConfirm }) {
  const [allDocs, setAllDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [searchQ, setSearchQ] = useState('');
  const [filterClient, setFilterClient] = useState('');
  const [filterSector, setFilterSector] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const list = await loadProposalLibrary(orgId);
      if (!cancelled) {
        // Only show docs that aren't already pinned and have some
        // form of content (extracted text OR file_id) — pinning a
        // URL-only entry would be a no-op for the Coach.
        setAllDocs(list.filter(d => !alreadyPinnedIds.includes(d.id)));
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, alreadyPinnedIds.join('|')]);

  const allClients = useMemo(() => [...new Set(allDocs.flatMap(d => d.clients || []))].sort(), [allDocs]);
  const allSectors = useMemo(() => [...new Set(allDocs.flatMap(d => d.sectors || []))].sort(), [allDocs]);

  const filtered = useMemo(() => allDocs.filter(d => {
    if (filterClient && !(d.clients || []).includes(filterClient)) return false;
    if (filterSector && !(d.sectors || []).includes(filterSector)) return false;
    if (searchQ) {
      const hay = `${d.title} ${d.extracted_title || ''} ${d.extracted_summary || ''} ${(d.tags || []).join(' ')}`.toLowerCase();
      if (!hay.includes(searchQ.toLowerCase())) return false;
    }
    return true;
  }), [allDocs, searchQ, filterClient, filterSector]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <ModalShell title="Pin from Library" onCancel={onCancel}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 12, lineHeight: 1.5 }}>
        Pick the library docs that are the right precedents for this opportunity. They'll always be attached to AI Coach turns, ahead of the library scope filter.
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        <input value={searchQ} onChange={e => setSearchQ(e.target.value)} placeholder="Search title / summary / tags"
          style={{ ...modalInput(C), flex: 1 }} />
        <select value={filterClient} onChange={e => setFilterClient(e.target.value)} style={{ ...modalInput(C), width: 140 }}>
          <option value="">Any client</option>
          {allClients.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filterSector} onChange={e => setFilterSector(e.target.value)} style={{ ...modalInput(C), width: 140 }}>
          <option value="">Any sector</option>
          {allSectors.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.02)' }}>
        {loading ? (
          <div style={{ padding: 14, color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Loading library…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 14, color: 'rgba(255,255,255,0.4)', fontSize: 11, fontStyle: 'italic' }}>
            {allDocs.length === 0 ? 'No library docs available to pin yet.' : 'No docs match the current filter.'}
          </div>
        ) : filtered.map(d => {
          const isSelected = selected.has(d.id);
          return (
            <label key={d.id}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderTop: `1px solid ${C.border}`, cursor: 'pointer', background: isSelected ? 'rgba(167,139,250,0.08)' : 'transparent' }}>
              <input type="checkbox" checked={isSelected} onChange={() => toggle(d.id)} style={{ accentColor: '#A78BFA', marginTop: 2 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {d.extracted_title || d.title}
                  {d.outcome && <span style={{ marginLeft: 6, fontSize: 9, color: d.outcome === 'won' ? '#22C55E' : '#EF4444', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>· {d.outcome}</span>}
                </div>
                {d.extracted_summary && (
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 2, lineHeight: 1.4 }}>{d.extracted_summary}</div>
                )}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                  {(d.clients || []).slice(0, 3).map(c => <LibraryTag key={`c-${c}`} color="#A78BFA" text={c} />)}
                  {(d.sectors || []).slice(0, 3).map(s => <LibraryTag key={`s-${s}`} color="#60A5FA" text={s} />)}
                  {d.document_type && <LibraryTag color="#94A3B8" text={d.document_type} />}
                </div>
              </div>
            </label>
          );
        })}
      </div>
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: `Pin ${selected.size} reference${selected.size === 1 ? '' : 's'}`,
          disabled: selected.size === 0,
          onClick: () => onConfirm(Array.from(selected)),
        }} />
      </div>
    </ModalShell>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PROPOSAL COACH — pre-CRM → opportunity → proposal pipeline
//
// Phase 1A: list / kanban view by stage, create opportunity, detail
// pane with editable fields + meeting notes. AI Coach chat lands in
// Phase 1B as a forked variant of the deal-AI chat.
// ═══════════════════════════════════════════════════════════════════
const OPP_STAGES = [
  { id: 'note',         label: 'Notes',       color: '#94A3B8', desc: 'Captured but not qualified yet' },
  { id: 'opportunity',  label: 'Opportunity', color: '#60A5FA', desc: 'Real lead, not yet committed' },
  { id: 'qualified',    label: 'Qualified',   color: '#A78BFA', desc: 'Decision-maker engaged, scope discussed' },
  { id: 'proposal',     label: 'Proposal',    color: '#E87722', desc: 'Drafting / submitting' },
  { id: 'submitted',    label: 'Submitted',   color: '#FFB830', desc: 'Awaiting client decision' },
  { id: 'won',          label: 'Won',         color: '#22C55E', desc: 'Closed-won' },
  { id: 'lost',         label: 'Lost',        color: '#EF4444', desc: 'Closed-lost' },
];
const OPP_SOURCES = [
  { id: 'referral',          label: 'Referral' },
  { id: 'inbound',           label: 'Inbound' },
  { id: 'cold_outreach',     label: 'Cold outreach' },
  { id: 'existing_client',   label: 'Existing client' },
  { id: 'partner',           label: 'Partner / channel' },
];

function ProposalCoach({ C, currentUser, users, showToast, initialOpportunityId = null, onConsumedInitial = null }) {
  const [subView, setSubView]     = useState('pipeline'); // 'pipeline' | 'library'
  const [opps, setOpps]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setOpps(await loadOpportunities(currentUser.orgId));
    setLoading(false);
  }, [currentUser.orgId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Consume a cross-module deep link (e.g. CRM "Open in Proposal
  // Coach" set pendingProposalCoachOppId at the root before switching
  // view). Fires once when both an id is set AND the opportunity is
  // already in the list.
  useEffect(() => {
    if (!initialOpportunityId) return;
    if (loading) return;
    const exists = opps.find(o => o.id === initialOpportunityId);
    if (exists) {
      setSelectedId(initialOpportunityId);
      onConsumedInitial?.();
    }
  }, [initialOpportunityId, loading, opps, onConsumedInitial]);

  const selected = opps.find(o => o.id === selectedId) || null;
  const onCreated = (row) => {
    setOpps(prev => [row, ...prev]);
    setShowCreate(false);
    setSelectedId(row.id);
  };
  const onUpdated = (id, patch) => {
    setOpps(prev => prev.map(o => o.id === id ? { ...o, ...patch } : o));
  };
  const onDeleted = (id) => {
    setOpps(prev => prev.filter(o => o.id !== id));
    if (selectedId === id) setSelectedId(null);
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 24, fontWeight: 900, color: '#fff', letterSpacing: '0.02em' }}>Proposal Coach</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.45)', marginTop: 4 }}>
            Pre-CRM notes → opportunity → proposal pipeline. Drop the coach what you know, get win-probability + gap radar back.
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {subView === 'pipeline' && !selected && (
          <button onClick={() => setShowCreate(true)}
            style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '8px 16px', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            + New opportunity
          </button>
        )}
      </div>

      {/* One-time "Powered by Fable 5" hero — dismissible, persists to
          localStorage.  Only appears on Pipeline (not Library / Profiles). */}
      {subView === 'pipeline' && <FableIntroBanner />}

      {/* Demo-friendly flow chart banner — read this and you understand
          where each artifact comes from and where the AI plugs in. */}
      <ProposalCoachFlowChart C={C} subView={subView} />

      {/* Sub-nav: Pipeline (per-opportunity work) vs Library (org-wide
          reusable knowledge base). Always visible — including when
          you're inside an opportunity detail — so you can jump to
          Library from anywhere without losing your selection. */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'rgba(255,255,255,0.03)', padding: 4, borderRadius: 8, width: 'fit-content' }}>
        {[
          { id: 'pipeline', label: selected ? `Pipeline · ${selected.name.slice(0, 28)}${selected.name.length > 28 ? '…' : ''}` : 'Pipeline' },
          { id: 'library',  label: 'Library' },
          { id: 'profiles', label: 'Profiles' },
        ].map(t => (
          <button key={t.id} onClick={() => setSubView(t.id)}
            style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', background: subView === t.id ? '#E87722' : 'transparent', color: subView === t.id ? '#fff' : 'rgba(255,255,255,0.55)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {t.label}
          </button>
        ))}
      </div>

      {subView === 'library' && (
        <ProposalLibraryManager C={C} currentUser={currentUser} showToast={showToast} />
      )}

      {subView === 'profiles' && (
        <ArtifactProfilesManager C={C} currentUser={currentUser} showToast={showToast} />
      )}

      {subView === 'pipeline' && selected && (
        <OpportunityDetail
          C={C}
          opp={selected}
          currentUser={currentUser}
          users={users}
          onBack={() => setSelectedId(null)}
          onUpdated={(patch) => onUpdated(selected.id, patch)}
          onDeleted={() => onDeleted(selected.id)}
          showToast={showToast}
        />
      )}

      {subView === 'pipeline' && !selected && (loading ? (
        <div style={{ color: 'rgba(255,255,255,0.4)', padding: 40, textAlign: 'center' }}>Loading…</div>
      ) : opps.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13, border: `1px dashed ${C.border}`, borderRadius: 10 }}>
          No opportunities yet. Click + New opportunity to start with a quick note from a meeting or phone call.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${OPP_STAGES.length}, minmax(0, 1fr))`, gap: 10, overflowX: 'auto' }}>
          {OPP_STAGES.map(stage => {
            const items = opps.filter(o => o.stage === stage.id && o.status !== 'archived');
            return (
              <div key={stage.id} style={{ background: 'rgba(255,255,255,0.025)', border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, minHeight: 200 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: stage.color }} />
                  <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{stage.label}</span>
                  <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginLeft: 'auto' }}>{items.length}</span>
                </div>
                {items.length === 0 ? (
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', fontStyle: 'italic' }}>{stage.desc}</div>
                ) : items.map(o => (
                  <OpportunityCard key={o.id} opp={o} onClick={() => setSelectedId(o.id)} />
                ))}
              </div>
            );
          })}
        </div>
      ))}

      {showCreate && (
        <OpportunityCreateModal
          C={C}
          currentUser={currentUser}
          onCancel={() => setShowCreate(false)}
          onCreated={onCreated}
          showToast={showToast}
        />
      )}
    </div>
  );
}

function OpportunityCreateModal({ C, currentUser, onCancel, onCreated, showToast }) {
  const [name, setName]               = useState('');
  const [clientName, setClientName]   = useState('');
  const [source, setSource]           = useState('inbound');
  const [stage, setStage]             = useState('note');
  const [estimatedValue, setEstimatedValue] = useState('');
  const [scope, setScope]             = useState('');
  const [profileId, setProfileId]     = useState('');
  const [profiles, setProfiles]       = useState([]);
  const [busy, setBusy]               = useState(false);

  useEffect(() => {
    (async () => {
      const list = await loadArtifactProfiles(currentUser.orgId);
      setProfiles(list);
      const def = list.find(p => p.is_default);
      if (def) setProfileId(def.id);
    })();
  }, [currentUser.orgId]);

  const submit = async () => {
    if (!name.trim()) { showToast?.('Name is required.'); return; }
    setBusy(true);
    const result = await createOpportunity(currentUser.orgId, currentUser.id, {
      name,
      client_name:     clientName,
      source,
      stage,
      estimated_value: estimatedValue ? Number(estimatedValue) : null,
      scope_summary:   scope,
      artifact_profile_id: profileId || null,
    });
    setBusy(false);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    // Seed from the chosen profile if one was selected, otherwise
    // fall back to the hard-coded defaults. seedOpportunityFromProfile
    // handles both outbound artifacts AND proposal sections in one
    // pass so we don't need the older two-call pattern here.
    const chosen = profiles.find(p => p.id === profileId) || null;
    seedOpportunityFromProfile(result.row.id, chosen);
    showToast?.(`✓ Created "${result.row.name}"`);
    onCreated(result.row);
  };

  return (
    <ModalShell title="New opportunity" onCancel={onCancel}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Opportunity name" required>
          <input value={name} onChange={e => setName(e.target.value)} autoFocus
            placeholder="e.g. Acme Corp — Q3 environmental modelling"
            style={modalInput(C)} />
        </Field>
        <Field label="Client name">
          <input value={clientName} onChange={e => setClientName(e.target.value)}
            placeholder="Acme Corp" style={modalInput(C)} />
        </Field>
        <Field label="Source">
          <select value={source} onChange={e => setSource(e.target.value)} style={modalInput(C)}>
            {OPP_SOURCES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Stage">
          <select value={stage} onChange={e => setStage(e.target.value)} style={modalInput(C)}>
            {OPP_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </Field>
        <Field label="Estimated value (USD)">
          <input type="number" value={estimatedValue} onChange={e => setEstimatedValue(e.target.value)}
            placeholder="50000" style={modalInput(C)} />
        </Field>
        <Field label="Artifact profile (template)">
          <select value={profileId} onChange={e => setProfileId(e.target.value)} style={modalInput(C)}>
            <option value="">— Generic defaults —</option>
            {profiles.map(p => <option key={p.id} value={p.id}>{p.name}{p.is_default ? ' (default)' : ''}</option>)}
          </select>
        </Field>
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Scope / what they need">
            <textarea rows={3} value={scope} onChange={e => setScope(e.target.value)}
              placeholder="Brief scope summary — what's the work, what's the timeline, what would success look like."
              style={{ ...modalInput(C), resize: 'vertical' }} />
          </Field>
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: busy ? 'Creating…' : 'Create opportunity',
          disabled: busy || !name.trim(),
          onClick: submit,
        }} />
      </div>
    </ModalShell>
  );
}

function ToneFormalitySlider({ C, value, onChange }) {
  const v = value || 0; // 0 = not set
  const bucket = v === 0 ? null : v <= 3 ? 'casual' : v <= 6 ? 'business' : v <= 9 ? 'formal' : 'regulatory';
  const bucketMeta = {
    casual:     { label: 'Casual / friendly',     color: '#34d399', desc: 'Contractions OK · short sentences · warm · peer-to-peer.' },
    business:   { label: 'Business standard',     color: '#22d3ee', desc: 'Polished but approachable · active voice · outcome-led.' },
    formal:     { label: 'Formal / legal-adjacent', color: '#a78bfa', desc: 'No contractions · precise phrasing · cite standards/certs by name · risk-conscious.' },
    regulatory: { label: 'Regulatory / litigation-grade', color: '#f87171', desc: 'Maximally formal · every claim must be defensible · no superlatives · prefer passive when active attributes risk.' },
  };
  const m = bucket ? bucketMeta[bucket] : { label: 'Not set (audience default)', color: 'rgba(255,255,255,0.4)', desc: 'Falls back to whatever the audience (Federal / Enterprise / etc.) implies.' };
  return (
    <div style={{
      padding: 12, borderRadius: 6,
      border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.02)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>Friendly</span>
        <input
          type="range" min={0} max={10} step={1} value={v}
          onChange={e => {
            const n = Number(e.target.value);
            onChange(n === 0 ? null : n);
          }}
          style={{
            flex: 1, accentColor: m.color,
          }}
        />
        <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>Strict</span>
        <span style={{
          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
          padding: '3px 8px', borderRadius: 3,
          background: `${m.color}22`, color: m.color, border: `1px solid ${m.color}55`,
          minWidth: 24, textAlign: 'center',
        }}>{v === 0 ? '—' : `${v}/10`}</span>
      </div>
      <div style={{ marginTop: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: m.color, fontWeight: 700 }}>
        {m.label}
      </div>
      <div style={{ marginTop: 2, fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
        {m.desc}
      </div>
    </div>
  );
}

function PrimaryContactCard({ C, contact }) {
  if (!contact) return null;
  const name = contact.name || [contact.first_name, contact.last_name].filter(Boolean).join(' ') || '(unnamed)';
  const rows = [
    contact.title    && ['Title',   contact.title],
    contact.company  && ['Company', contact.company],
    contact.email    && ['Email',   contact.email],
    contact.phone    && ['Phone',   contact.phone],
    contact.mobile   && ['Mobile',  contact.mobile],
    contact.website  && ['Website', contact.website],
    contact.address  && ['Address', contact.address],
  ].filter(Boolean);
  // Surface gaps so the user knows what the AI will treat as [CONFIRM:]
  const missing = ['title', 'phone', 'mobile', 'company', 'address'].filter(k => !contact[k]);

  return (
    <div style={{
      marginTop: 8, padding: 10, border: `1px solid ${C.border}`, borderRadius: 6,
      background: 'rgba(255,255,255,0.02)',
    }}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 6 }}>
        {name}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
        {rows.map(([k, v]) => (
          <div key={k} style={{ fontFamily: "'Inter',sans-serif", fontSize: 11 }}>
            <span style={{ color: 'rgba(255,255,255,0.45)' }}>{k}: </span>
            <span style={{ color: 'rgba(255,255,255,0.85)' }}>{v}</span>
          </div>
        ))}
      </div>
      {missing.length > 0 && (
        <div style={{ marginTop: 8, padding: 6, borderRadius: 4, background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.3)', fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: '#fbbf24' }}>
          ⚠ Missing fields the AI will flag as [CONFIRM:] in drafts: {missing.join(', ')}. Edit in the CRM module to fill them.
        </div>
      )}
    </div>
  );
}

function OpportunityDetail({ C, opp, currentUser, users, onBack, onUpdated, onDeleted, showToast }) {
  const [draft, setDraft] = useState(opp);
  useEffect(() => { setDraft(opp); }, [opp.id]);

  // Pull linked context from the canonical sources: Notes module
  // (localStorage per-user) and CRM (localStorage per-org). The
  // Proposal Coach doesn't store its own meeting notes anymore —
  // it references the originals so there's a single source of truth.
  const notesLink = useLinkedNotes(currentUser.id, opp.client_name, opp.name, opp.id);
  const linkedNotes = notesLink.notes;
  const crmActivities = useLinkedCrmActivities(currentUser.orgId, opp.crm_deal_id);

  // Load the org contact directory once (used by the primary-contact
  // picker and to surface the picked contact's fields to the AI).
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    (async () => setContacts(await loadContacts(currentUser.orgId)))();
  }, [currentUser.orgId]);

  // Resolve the picked primary contact's full row so the UI can show
  // title/phone/etc inline and the AI can read the same. Lookup is
  // local-only (we already loaded the directory).
  const primaryContact = useMemo(
    () => (draft.primary_contact_id ? contacts.find(c => c.id === draft.primary_contact_id) : null) || null,
    [draft.primary_contact_id, contacts],
  );

  // Build a compact serializable bundle the AI Coach can read on
  // every chat turn (passed in the request body — Notes + CRM live
  // in localStorage and aren't visible server-side).
  const linkedContext = useMemo(() => ({
    notes: linkedNotes.map(n => ({
      source:  'notes',
      title:   n.title,
      content: n.content,
      tags:    n.tags,
      date:    n.updatedAt || n.createdAt,
      crmLink: n.crmLink,
    })),
    primary_contact: primaryContact ? {
      source:       'contact',
      name:         primaryContact.name || [primaryContact.first_name, primaryContact.last_name].filter(Boolean).join(' '),
      first_name:   primaryContact.first_name || null,
      last_name:    primaryContact.last_name  || null,
      title:        primaryContact.title      || null,
      company:      primaryContact.company    || null,
      email:        primaryContact.email      || null,
      phone:        primaryContact.phone      || null,
      mobile:       primaryContact.mobile     || null,
      website:      primaryContact.website    || null,
      address:      primaryContact.address    || null,
      notes:        primaryContact.notes      || null,
    } : null,
    crm_activities: crmActivities.map(a => ({
      source:  'crm',
      type:    a.type,
      content: a.note,
      date:    a.date,
    })),
  }), [linkedNotes, crmActivities]);

  const dirty = draft.name !== opp.name
             || (draft.client_name || '') !== (opp.client_name || '')
             || draft.stage !== opp.stage
             || (draft.source || '') !== (opp.source || '')
             || Number(draft.estimated_value || 0) !== Number(opp.estimated_value || 0)
             || (draft.win_probability ?? null) !== (opp.win_probability ?? null)
             || (draft.scope_summary || '') !== (opp.scope_summary || '')
             || (draft.client_priorities || '') !== (opp.client_priorities || '')
             || (draft.proposal_tone || '') !== (opp.proposal_tone || '')
             || (draft.proposal_tone_custom || '') !== (opp.proposal_tone_custom || '')
             || (draft.tone_formality ?? null) !== (opp.tone_formality ?? null)
             || (draft.primary_contact_id || null) !== (opp.primary_contact_id || null);

  const saveOpp = async () => {
    const ok = await updateOpportunity(opp.id, {
      name:             draft.name,
      client_name:      draft.client_name || null,
      stage:            draft.stage,
      source:           draft.source || null,
      estimated_value:  draft.estimated_value ? Number(draft.estimated_value) : null,
      win_probability:  draft.win_probability ?? null,
      scope_summary:    draft.scope_summary || null,
      client_priorities: draft.client_priorities || null,
      proposal_tone:    draft.proposal_tone || null,
      proposal_tone_custom: draft.proposal_tone_custom || null,
      tone_formality:     draft.tone_formality ?? null,
      primary_contact_id: draft.primary_contact_id || null,
    });
    if (ok) {
      onUpdated(draft);
      showToast?.('✓ Saved');
    } else {
      showToast?.('❌ Save failed');
    }
  };

  const removeOpp = async () => {
    if (!confirm(`Delete "${opp.name}"? This permanently removes the opportunity.`)) return;
    const ok = await deleteOpportunity(opp.id);
    if (ok) { onDeleted(); onBack(); }
    else    showToast?.('❌ Delete failed');
  };

  const lbl = { fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, display: 'block', fontFamily: "'Montserrat',sans-serif" };
  const inp = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%' };
  const stageMeta = OPP_STAGES.find(s => s.id === opp.stage) || OPP_STAGES[0];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <button onClick={onBack}
          style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.7)', borderRadius: 6, padding: '6px 12px', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer', marginRight: 12 }}>
          ← Pipeline
        </button>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 20, fontWeight: 900, color: '#fff' }}>{opp.name}</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
            {opp.client_name || '—'} · <span style={{ color: stageMeta.color }}>{stageMeta.label}</span>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={removeOpp}
          style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 6, padding: '6px 12px', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
          Delete
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        {/* Left: opportunity fields */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', marginBottom: 14 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Opportunity</div>
            <div style={{ flex: 1 }} />
            {dirty && (
              <button onClick={saveOpp}
                style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 5, padding: '5px 12px', fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                Save
              </button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={lbl}>Name</label>
              <input value={draft.name || ''} onChange={e => setDraft({ ...draft, name: e.target.value })} style={inp} />
            </div>
            <div>
              <label style={lbl}>Client</label>
              <input value={draft.client_name || ''} onChange={e => setDraft({ ...draft, client_name: e.target.value })} style={inp} />
            </div>
            <div>
              <label style={lbl}>Stage</label>
              <select value={draft.stage} onChange={e => setDraft({ ...draft, stage: e.target.value })} style={inp}>
                {OPP_STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Source</label>
              <select value={draft.source || ''} onChange={e => setDraft({ ...draft, source: e.target.value })} style={inp}>
                <option value="">—</option>
                {OPP_SOURCES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label style={lbl}>Estimated value</label>
              <input type="number" step="1000" min="0"
                value={draft.estimated_value ?? ''}
                onChange={e => setDraft({ ...draft, estimated_value: e.target.value })}
                style={inp} />
            </div>
            <div>
              <label style={lbl}>Win probability (%)</label>
              <input type="number" min="0" max="100" step="1"
                value={draft.win_probability ?? ''}
                onChange={e => {
                  const v = e.target.value;
                  if (v === '') { setDraft({ ...draft, win_probability: null }); return; }
                  const n = Math.max(0, Math.min(100, Math.round(Number(v))));
                  setDraft({ ...draft, win_probability: Number.isFinite(n) ? n : null });
                }}
                placeholder="0–100"
                style={inp} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Scope / what they need</label>
              <textarea rows={3} value={draft.scope_summary || ''} onChange={e => setDraft({ ...draft, scope_summary: e.target.value })}
                style={{ ...inp, resize: 'vertical' }} />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Client priorities / what they care about</label>
              <textarea rows={3} value={draft.client_priorities || ''} onChange={e => setDraft({ ...draft, client_priorities: e.target.value })}
                style={{ ...inp, resize: 'vertical' }} />
            </div>
            <div>
              <label style={lbl}>Proposal tone (buyer audience)</label>
              <select value={draft.proposal_tone || ''} onChange={e => setDraft({ ...draft, proposal_tone: e.target.value || null })} style={inp}>
                <option value="">— Not set (default voice) —</option>
                <option value="gov_federal">Federal government</option>
                <option value="gov_state_local">State / local government</option>
                <option value="enterprise">Large enterprise</option>
                <option value="small_operator">Small operator / owner-operator</option>
                <option value="academic">Academic / research</option>
                <option value="custom">Custom (specify below)</option>
              </select>
            </div>
            {draft.proposal_tone === 'custom' && (
              <div>
                <label style={lbl}>Custom tone hint</label>
                <input value={draft.proposal_tone_custom || ''} onChange={e => setDraft({ ...draft, proposal_tone_custom: e.target.value })}
                  placeholder="e.g. Plain-spoken, no jargon, peer-to-peer voice" style={inp} />
              </div>
            )}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Formality (how strict the voice should be)</label>
              <ToneFormalitySlider
                C={C}
                value={draft.tone_formality}
                onChange={(v) => setDraft({ ...draft, tone_formality: v })}
              />
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={lbl}>Primary contact (used by AI cover letters + signature blocks)</label>
              <select value={draft.primary_contact_id || ''} onChange={e => setDraft({ ...draft, primary_contact_id: e.target.value || null })} style={inp}>
                <option value="">— None linked —</option>
                {contacts.map(c => {
                  const name = c.name || [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email || '(unnamed)';
                  const tag  = c.title ? ` · ${c.title}` : '';
                  const co   = c.company ? ` (${c.company})` : '';
                  return <option key={c.id} value={c.id}>{name}{tag}{co}</option>;
                })}
              </select>
              {primaryContact && (
                <PrimaryContactCard C={C} contact={primaryContact} />
              )}
              {!primaryContact && contacts.length === 0 && (
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
                  No contacts in your CRM yet. Add one in the CRM module and they'll show up here.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: LINKED context from canonical sources. No notes
            are stored on the opportunity itself — Notes module +
            CRM are the single source of truth. */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <LinkedNotesPanel
            C={C}
            notes={linkedNotes}
            clientName={opp.client_name}
            manuallyLinkedIds={notesLink.manuallyLinkedIds}
            searchNotes={notesLink.searchNotes}
            onLink={notesLink.linkNote}
            onUnlink={notesLink.unlinkNote}
            onLinkMany={notesLink.linkNotes}
            onCreate={notesLink.createNote}
            showToast={showToast}
            onJumpToNotes={() => showToast?.('Tip: open Notes from the sidebar to edit')}
          />
          <CrmTouchpointsPanel C={C} activities={crmActivities} crmDealId={opp.crm_deal_id} clientName={opp.client_name} />
          <ReferenceMaterialPanel C={C} opp={opp} currentUser={currentUser} onUpdated={onUpdated} showToast={showToast} />
        </div>
      </div>

      {/* Phase 3: RFP intake + outbound deliverables checklist */}
      <div style={{ marginTop: 18 }}>
        <OpportunityArtifactsPanel
          C={C}
          opp={opp}
          currentUser={currentUser}
          users={users}
          showToast={showToast}
        />
      </div>

      {/* Phase 4: proposal workspace — members + sections + sign-offs */}
      <div style={{ marginTop: 18 }}>
        <ProposalWorkspacePanel
          C={C}
          opp={opp}
          currentUser={currentUser}
          users={users}
          linkedContext={linkedContext}
          showToast={showToast}
        />
      </div>

      {/* Phase 7: AI-scored win probability */}
      <div style={{ marginTop: 18 }}>
        <WinProbabilityPanel
          C={C}
          opp={opp}
          currentUser={currentUser}
          linkedContext={linkedContext}
          onUpdated={onUpdated}
          showToast={showToast}
        />
      </div>

      {/* Phase 8: Gap radar */}
      <div style={{ marginTop: 18 }}>
        <GapRadarPanel
          C={C}
          opp={opp}
          onUpdated={onUpdated}
          showToast={showToast}
        />
      </div>

      {/* Phase 9: Critique mode */}
      <div style={{ marginTop: 18 }}>
        <CritiquePanel
          C={C}
          opp={opp}
          onUpdated={onUpdated}
          showToast={showToast}
        />
      </div>

      {/* Phase 10: Competition view */}
      <div style={{ marginTop: 18 }}>
        <CompetitionPanel
          C={C}
          opp={opp}
          currentUser={currentUser}
          showToast={showToast}
        />
      </div>

      {/* Phase 12: Outcome + feedback loop */}
      <div style={{ marginTop: 18 }}>
        <OutcomePanel
          C={C}
          opp={opp}
          currentUser={currentUser}
          onUpdated={onUpdated}
          showToast={showToast}
        />
      </div>

      {/* Reference a past proposal as template */}
      <div style={{ marginTop: 18 }}>
        <ReferencePastProposalPanel
          C={C}
          opp={opp}
          currentUser={currentUser}
          onUpdated={onUpdated}
          showToast={showToast}
        />
      </div>

      {/* Proposal theme (logo, color, font) */}
      <div style={{ marginTop: 18 }}>
        <ProposalThemePanel
          C={C}
          opp={opp}
          currentUser={currentUser}
          onUpdated={onUpdated}
          showToast={showToast}
        />
      </div>

      {/* AI Coach — Phase 1B */}
      <div style={{ marginTop: 18 }}>
        <OpportunityAiCoach
          C={C}
          opp={opp}
          currentUser={currentUser}
          linkedContext={linkedContext}
          showToast={showToast}
        />
      </div>
    </div>
  );
}

// Compact opportunity card with a deliverables-progress badge.
// Loads artifact counts lazily so the kanban stays fast.
function OpportunityCard({ opp, onClick }) {
  const [counts, setCounts] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const arts = await loadOpportunityArtifacts(opp.id);
      if (cancelled) return;
      const outbound = arts.filter(a => a.direction === 'outbound');
      const applicable = outbound.filter(a => a.status !== 'na');
      const ready = applicable.filter(a => a.status === 'ready' || a.status === 'submitted');
      setCounts({ ready: ready.length, total: applicable.length });
    })();
    return () => { cancelled = true; };
  }, [opp.id]);
  const pct = counts && counts.total > 0 ? Math.round((counts.ready / counts.total) * 100) : 0;
  return (
    <div onClick={onClick}
      style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 6, padding: '8px 10px', marginBottom: 6, cursor: 'pointer' }}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{opp.name}</div>
      {opp.client_name && <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{opp.client_name}</div>}
      {opp.estimated_value > 0 && (
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: '#E87722', marginTop: 4, fontWeight: 700 }}>
          {fmtMoney(opp.estimated_value, opp.currency || 'USD')}
        </div>
      )}
      {counts && counts.total > 0 && (
        <div style={{ marginTop: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 2 }}>
            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Deliverables</span>
            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: counts.ready === counts.total ? '#22C55E' : 'rgba(255,255,255,0.6)', fontWeight: 700 }}>{counts.ready}/{counts.total}</span>
          </div>
          <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2 }}>
            <div style={{ height: '100%', width: `${pct}%`, background: pct === 100 ? '#22C55E' : '#A78BFA', borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Artifacts catalog ────────────────────────────────────────────
const ARTIFACT_TYPES = {
  // Inbound — what the client sent us
  rfp:                 { label: 'RFP / Solicitation',    icon: '📥', direction: 'inbound' },
  spec:                { label: 'Spec / SOW',            icon: '📥', direction: 'inbound' },
  regulation:          { label: 'Regulation',            icon: '📥', direction: 'inbound' },
  addendum:            { label: 'Addendum / Amendment',  icon: '📥', direction: 'inbound' },
  qa:                  { label: 'Q&A / Clarification',   icon: '📥', direction: 'inbound' },
  // Outbound — what we produce for them
  cover_letter:        { label: 'Cover letter',          icon: '📄', direction: 'outbound' },
  exec_summary:        { label: 'Executive summary',     icon: '📄', direction: 'outbound' },
  technical_approach:  { label: 'Technical approach',    icon: '📄', direction: 'outbound' },
  pricing:             { label: 'Pricing sheet',         icon: '💰', direction: 'outbound' },
  org_chart:           { label: 'Org chart',             icon: '🏢', direction: 'outbound' },
  resume:              { label: 'Staff resumes',         icon: '👤', direction: 'outbound' },
  past_performance:    { label: 'Past performance',      icon: '📊', direction: 'outbound' },
  capability_statement:{ label: 'Capability statement',  icon: '📋', direction: 'outbound' },
  schedule_of_rates:   { label: 'Schedule of rates',     icon: '💰', direction: 'outbound' },
  insurance_cert:      { label: 'Insurance certificate', icon: '🛡', direction: 'outbound' },
  reference_letter:    { label: 'Reference letter',      icon: '✉', direction: 'outbound' },
  sow:                 { label: 'Statement of Work',     icon: '📄', direction: 'outbound' },
  other:               { label: 'Other',                 icon: '📎', direction: 'outbound' },
};

const ARTIFACT_STATUS_META = {
  pending:     { label: 'Pending',     color: '#94A3B8' },
  in_progress: { label: 'In progress', color: '#60A5FA' },
  ready:       { label: 'Ready',       color: '#A78BFA' },
  submitted:   { label: 'Submitted',   color: '#22C55E' },
  na:          { label: 'N/A',         color: 'rgba(255,255,255,0.3)' },
};

function OpportunityArtifactsPanel({ C, opp, currentUser, users, showToast }) {
  const [artifacts, setArtifacts] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(null); // 'inbound' | 'outbound' | null
  const [preparingId, setPreparingId] = useState(null);
  const fileInputRef = useRef(null);
  const fileTargetRef = useRef(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setArtifacts(await loadOpportunityArtifacts(opp.id));
    setLoading(false);
  }, [opp.id]);
  useEffect(() => { refresh(); }, [refresh]);

  const inbound  = artifacts.filter(a => a.direction === 'inbound');
  const outbound = artifacts.filter(a => a.direction === 'outbound');
  const readyCount = outbound.filter(a => a.status === 'ready' || a.status === 'submitted').length;
  const totalApplicable = outbound.filter(a => a.status !== 'na').length;

  const onAddSubmit = async (fields) => {
    const result = await createOpportunityArtifact(opp.id, fields);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setShowAdd(null);
    setArtifacts(prev => [...prev, result.row]);
    showToast?.(`✓ Added "${result.row.name}"`);
  };

  const onPatchInline = async (id, patch) => {
    // Optimistic
    setArtifacts(prev => prev.map(a => a.id === id ? { ...a, ...patch } : a));
    const result = await updateOpportunityArtifact(id, patch);
    if (result.error) {
      showToast?.(`❌ Update failed: ${result.error}`);
      refresh();
    }
  };

  const onRemove = async (a) => {
    if (!confirm(`Remove "${a.name}"?`)) return;
    const ok = await deleteOpportunityArtifact(a);
    if (ok) setArtifacts(prev => prev.filter(x => x.id !== a.id));
    else showToast?.('❌ Remove failed');
  };

  const onAttachFile = (a) => {
    fileTargetRef.current = a;
    fileInputRef.current?.click();
  };
  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const target = fileTargetRef.current;
    fileTargetRef.current = null;
    if (!file || !target) return;
    const result = await uploadOpportunityArtifactFile(currentUser.orgId, opp.id, target.id, file);
    if (result.error) { showToast?.(`❌ Upload failed: ${result.error}`); return; }
    showToast?.(`✓ Attached "${file.name}"`);
    refresh();
  };

  const onToggleAi = async (a) => {
    setPreparingId(a.id);
    const next = !a.ai_enabled;
    const result = await setOpportunityArtifactAiEnabled(a.id, next);
    setPreparingId(null);
    if (result.error) showToast?.(`❌ AI prep: ${result.error}`);
    else showToast?.(next ? `✓ "${a.name}" is AI-ready` : '↩ AI prep cleared');
    refresh();
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <input ref={fileInputRef} type="file" onChange={onFileChosen} style={{ display: 'none' }}
        accept=".pdf,.docx,.pptx,.txt,.md,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation" />

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          RFP & Deliverables
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
          {readyCount}/{totalApplicable} ready
        </div>
      </div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 14, lineHeight: 1.5 }}>
        Inbound RFPs and outbound deliverables for this opportunity. AI-prep inbound docs so the Coach reads them as the authoritative ask, and track outbound items toward submission.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <ArtifactsColumn
          C={C}
          title="📥 Inbound (from client)"
          direction="inbound"
          artifacts={inbound}
          users={users}
          loading={loading}
          preparingId={preparingId}
          onAddClick={() => setShowAdd('inbound')}
          onPatch={onPatchInline}
          onRemove={onRemove}
          onAttachFile={onAttachFile}
          onToggleAi={onToggleAi}
          emptyHint="No RFP / spec docs uploaded yet. Add the RFP here and AI-enable it so the Coach reads it as source-of-truth."
        />
        <ArtifactsColumn
          C={C}
          title="📤 Outbound (we deliver)"
          direction="outbound"
          artifacts={outbound}
          users={users}
          loading={loading}
          preparingId={preparingId}
          onAddClick={() => setShowAdd('outbound')}
          onPatch={onPatchInline}
          onRemove={onRemove}
          onAttachFile={onAttachFile}
          onToggleAi={onToggleAi}
          emptyHint="New opportunities are seeded with a default deliverables checklist. Mark items N/A or delete what doesn't apply."
        />
      </div>

      {showAdd && (
        <AddArtifactModal
          C={C}
          direction={showAdd}
          users={users}
          onCancel={() => setShowAdd(null)}
          onSubmit={onAddSubmit}
        />
      )}
    </div>
  );
}

function ArtifactsColumn({ C, title, direction, artifacts, users, loading, preparingId, onAddClick, onPatch, onRemove, onAttachFile, onToggleAi, emptyHint }) {
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.65)', letterSpacing: '0.08em', textTransform: 'uppercase', flex: 1 }}>
          {title}
        </div>
        <button onClick={onAddClick}
          style={{ background: 'rgba(232,119,34,0.15)', border: '1px solid rgba(232,119,34,0.4)', color: '#E87722', borderRadius: 5, padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          + Add
        </button>
      </div>
      {loading ? (
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 11, padding: '6px 0' }}>Loading…</div>
      ) : artifacts.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontStyle: 'italic', padding: '12px 0' }}>{emptyHint}</div>
      ) : artifacts.map(a => (
        <ArtifactRow
          key={a.id}
          C={C}
          artifact={a}
          users={users}
          preparingId={preparingId}
          onPatch={onPatch}
          onRemove={onRemove}
          onAttachFile={onAttachFile}
          onToggleAi={onToggleAi}
        />
      ))}
    </div>
  );
}

function ArtifactRow({ C, artifact, users, preparingId, onPatch, onRemove, onAttachFile, onToggleAi }) {
  const a = artifact;
  const meta = ARTIFACT_TYPES[a.artifact_type] || { label: a.artifact_type, icon: '📎' };
  const statusMeta = ARTIFACT_STATUS_META[a.status] || ARTIFACT_STATUS_META.pending;
  const isInbound = a.direction === 'inbound';
  const aiReady   = a.ai_enabled && (!!a.anthropic_file_id || !!a.extracted_content);
  const aiPending = preparingId === a.id;
  const aiError   = a.ai_enabled && !aiReady && !aiPending && a.ai_prep_error;
  const hasFile   = !!a.storage_path;

  return (
    <div style={{ padding: '10px 0', borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <span style={{ fontSize: 14 }}>{meta.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {a.extracted_title || a.name}
            {aiReady   && <span style={{ marginLeft: 6, fontSize: 9, color: '#A78BFA', fontWeight: 700 }}>🤖 AI-ready</span>}
            {aiPending && <span style={{ marginLeft: 6, fontSize: 9, color: '#FFB830', fontWeight: 700 }}>🤖 preparing…</span>}
            {aiError   && <span title={a.ai_prep_error} style={{ marginLeft: 6, fontSize: 9, color: '#EF4444', fontWeight: 700 }}>🤖 prep failed</span>}
          </div>
          {a.extracted_summary && (
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 2, lineHeight: 1.4 }}>{a.extracted_summary}</div>
          )}
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>
            {meta.label}
            {a.filename && <span> · {a.filename} ({Math.round((a.size_bytes || 0) / 1024).toLocaleString()} KB)</span>}
          </div>
        </div>
        <button onClick={() => onRemove(a)}
          title="Remove" style={{ background: 'transparent', border: 'none', color: 'rgba(255,107,107,0.5)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
        {!isInbound && (
          <select value={a.status} onChange={e => onPatch(a.id, { status: e.target.value })}
            style={{ background: `${statusMeta.color}1f`, border: `1px solid ${statusMeta.color}66`, color: statusMeta.color, borderRadius: 4, padding: '3px 8px', fontSize: 10, fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, cursor: 'pointer' }}>
            {Object.entries(ARTIFACT_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        )}
        {!isInbound && (
          <select value={a.assignee_user_id || ''} onChange={e => onPatch(a.id, { assignee_user_id: e.target.value || null })}
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 4, padding: '3px 8px', fontSize: 10, fontFamily: "'Montserrat',sans-serif", maxWidth: 140 }}>
            <option value="">— Assignee —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        )}
        {!isInbound && (
          <input type="date" value={a.due_date || ''} onChange={e => onPatch(a.id, { due_date: e.target.value || null })}
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 4, padding: '3px 8px', fontSize: 10, fontFamily: "'Montserrat',sans-serif" }} />
        )}
        {!hasFile ? (
          <button onClick={() => onAttachFile(a)}
            style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.65)', borderRadius: 4, padding: '3px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
            📎 Attach file
          </button>
        ) : (
          <label title={a.ai_enabled ? 'AI access enabled' : 'AI-prep this file (PDF / DOCX / PPTX → Coach can read it)'}
            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', border: `1px solid ${a.ai_enabled ? 'rgba(167,139,250,0.5)' : C.border}`, background: a.ai_enabled ? 'rgba(167,139,250,0.12)' : 'transparent', borderRadius: 4, cursor: aiPending ? 'wait' : 'pointer', fontSize: 10, fontWeight: 700, color: a.ai_enabled ? '#A78BFA' : 'rgba(255,255,255,0.55)', fontFamily: "'Montserrat',sans-serif" }}>
            <input type="checkbox" checked={!!a.ai_enabled} disabled={aiPending} onChange={() => onToggleAi(a)} style={{ accentColor: '#A78BFA' }} />
            🤖 AI
          </label>
        )}
      </div>
    </div>
  );
}

function AddArtifactModal({ C, direction, users, onCancel, onSubmit }) {
  const choices = useMemo(() => Object.entries(ARTIFACT_TYPES).filter(([_, m]) => m.direction === direction), [direction]);
  const [type, setType]       = useState(choices[0]?.[0] || 'other');
  const [name, setName]       = useState(choices[0]?.[1]?.label || '');
  const [description, setDescription] = useState('');
  const [status, setStatus]   = useState('pending');
  const [dueDate, setDueDate] = useState('');
  const [assigneeId, setAssigneeId] = useState('');

  useEffect(() => {
    // When the user picks a type, pre-fill the name with the type's
    // default label so they don't have to retype.
    const m = ARTIFACT_TYPES[type];
    if (m && !name) setName(m.label);
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ModalShell title={direction === 'inbound' ? 'Add inbound document' : 'Add deliverable'} onCancel={onCancel}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Type">
          <select value={type} onChange={e => { setType(e.target.value); setName(ARTIFACT_TYPES[e.target.value]?.label || ''); }} style={modalInput(C)}>
            {choices.map(([id, m]) => <option key={id} value={id}>{m.label}</option>)}
          </select>
        </Field>
        <Field label="Name" required>
          <input value={name} onChange={e => setName(e.target.value)} style={modalInput(C)} />
        </Field>
        {direction === 'outbound' && (
          <>
            <Field label="Status">
              <select value={status} onChange={e => setStatus(e.target.value)} style={modalInput(C)}>
                {Object.entries(ARTIFACT_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </Field>
            <Field label="Due date">
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={modalInput(C)} />
            </Field>
            <Field label="Assignee">
              <select value={assigneeId} onChange={e => setAssigneeId(e.target.value)} style={modalInput(C)}>
                <option value="">— None —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </Field>
            <Field label=" " />
          </>
        )}
        <div style={{ gridColumn: '1 / -1' }}>
          <Field label="Notes (optional)">
            <textarea rows={2} value={description} onChange={e => setDescription(e.target.value)}
              style={{ ...modalInput(C), resize: 'vertical' }} />
          </Field>
        </div>
      </div>
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: 'Add',
          disabled: !name.trim(),
          onClick: () => onSubmit({
            direction, artifact_type: type, name, description,
            status: direction === 'outbound' ? status : 'pending',
            due_date: dueDate || null,
            assignee_user_id: assigneeId || null,
          }),
        }} />
      </div>
    </ModalShell>
  );
}

// ─── Proposal theme (per-opportunity branding) ───────────────────
function ReferencePastProposalPanel({ C, opp, currentUser, onUpdated, showToast }) {
  const [showPicker, setShowPicker] = useState(false);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading]       = useState(false);
  const [query, setQuery]           = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [applyTheme, setApplyTheme]         = useState(true);
  const [applyDirectives, setApplyDirectives] = useState(true);
  const [applyReference, setApplyReference]   = useState(true);
  const [applying, setApplying]     = useState(false);

  // Look up the currently-referenced opportunity (if any) so we can
  // show its name in the panel.
  const [refName, setRefName] = useState(null);
  useEffect(() => {
    if (!opp.reference_opportunity_id) { setRefName(null); return; }
    (async () => {
      const data = await loadOpportunityBrief(opp.reference_opportunity_id);
      setRefName(data ? `${data.name}${data.client_name ? ` · ${data.client_name}` : ''}` : null);
    })();
  }, [opp.reference_opportunity_id]);

  const openPicker = async () => {
    setShowPicker(true);
    setLoading(true);
    // Load all opportunities in the org, excluding the current one.
    // The user picks any past proposal to clone from.
    const all = await loadOpportunities(currentUser.orgId);
    setCandidates(all.filter(o => o.id !== opp.id));
    setLoading(false);
  };

  const filteredCandidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return candidates;
    return candidates.filter(o =>
      (o.name || '').toLowerCase().includes(q) ||
      (o.client_name || '').toLowerCase().includes(q)
    );
  }, [candidates, query]);

  const applySelection = async () => {
    if (!selectedId) return;
    const src = candidates.find(o => o.id === selectedId);
    if (!src) return;
    setApplying(true);
    const result = await referencePastProposal(
      selectedId,
      opp.id,
      { theme: applyTheme, directives: applyDirectives, reference: applyReference },
      src.name,
      currentUser.id,
    );
    setApplying(false);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    const actions = [];
    if (applyTheme) actions.push('theme copied');
    if (applyDirectives && result.directives?.appended) actions.push(`${result.directives.appended} directive${result.directives.appended === 1 ? '' : 's'} appended`);
    if (applyReference) actions.push('set as AI reference');
    showToast?.(`✓ From "${src.name}": ${actions.join(' · ')}`);
    // Trigger re-fetch of the parent's opp so theme + reference id refresh.
    onUpdated?.({ ...opp, reference_opportunity_id: applyReference ? selectedId : opp.reference_opportunity_id });
    setShowPicker(false);
    setSelectedId(null);
  };

  const clearReference = async () => {
    if (!confirm('Stop using the current past proposal as an AI reference? Theme and directives already applied stay in place.')) return;
    const result = await setOpportunityReference(opp.id, null);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    onUpdated?.({ ...opp, reference_opportunity_id: null });
    showToast?.('✓ AI reference cleared');
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          📎 Reference a Past Proposal
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
          use an earlier proposal as a template
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={openPicker} style={{
          background: 'transparent', color: '#A78BFA', border: '1px solid rgba(167,139,250,0.4)',
          borderRadius: 6, padding: '5px 12px', cursor: 'pointer',
          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
        }}>{refName ? 'Change reference' : '+ Pick a past proposal'}</button>
      </div>

      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 12, lineHeight: 1.55 }}>
        Point at an earlier proposal to (a) copy its visual theme, (b) inherit its AI directives, and/or (c) make the AI mirror its structure + voice on every draft. Different clients, same shape.
      </div>

      {refName && (
        <div style={{
          padding: 12, borderRadius: 6,
          border: '1px solid rgba(167,139,250,0.4)', background: 'rgba(167,139,250,0.08)',
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#A78BFA', padding: '3px 8px', borderRadius: 3, background: 'rgba(167,139,250,0.2)', border: '1px solid rgba(167,139,250,0.5)' }}>
            AI reference active
          </span>
          <div style={{ flex: 1, fontFamily: "'Inter',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.9)' }}>
            {refName}
          </div>
          <button onClick={clearReference} style={{
            background: 'transparent', color: 'rgba(248,113,113,0.7)',
            border: '1px solid rgba(248,113,113,0.4)', borderRadius: 4, padding: '4px 10px',
            cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
            fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
          }}>Clear</button>
        </div>
      )}

      {showPicker && (
        <ModalShell title="Reference a past proposal" onCancel={() => { setShowPicker(false); setSelectedId(null); setQuery(''); }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55, marginBottom: 14 }}>
            Pick an earlier proposal from your org. Any combination of theme / directives / AI reference can be pulled — nothing is copied that you don't check.
          </div>

          <input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search by proposal name or client…"
            autoFocus
            style={{ ...modalInput(C), marginBottom: 12 }}
          />

          {loading ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>Loading…</div>
          ) : filteredCandidates.length === 0 ? (
            <div style={{ padding: 16, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontStyle: 'italic' }}>
              {query ? `No past proposals match "${query}"` : 'No other proposals in this org yet.'}
            </div>
          ) : (
            <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14 }}>
              {filteredCandidates.map(o => {
                const isSelected = selectedId === o.id;
                return (
                  <label key={o.id} style={{
                    display: 'flex', gap: 10, padding: 10, borderRadius: 6, cursor: 'pointer',
                    background: isSelected ? 'rgba(167,139,250,0.08)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${isSelected ? '#A78BFA' : C.border}`,
                  }}>
                    <input
                      type="radio"
                      name="pick-past-proposal"
                      checked={isSelected}
                      onChange={() => setSelectedId(o.id)}
                      style={{ marginTop: 3 }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff' }}>{o.name}</div>
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
                        {o.client_name || '—'}
                        {o.stage && <span> · {o.stage}</span>}
                        {o.updated_at && <span> · updated {new Date(o.updated_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}

          {selectedId && (
            <div style={{ padding: 12, borderRadius: 6, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.02)', marginBottom: 12 }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>
                What to pull from the picked proposal
              </div>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>
                <input type="checkbox" checked={applyTheme} onChange={e => setApplyTheme(e.target.checked)} style={{ marginTop: 3 }} />
                <span><strong>Copy theme</strong> — logo, color, font, page layout, cover image, footer. Overwrites this proposal's current theme.</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.85)', marginBottom: 6 }}>
                <input type="checkbox" checked={applyDirectives} onChange={e => setApplyDirectives(e.target.checked)} style={{ marginTop: 3 }} />
                <span><strong>Copy AI directives</strong> — appends each of the past proposal's directives (with a "(from X)" prefix) to this one's list. Non-destructive.</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.85)' }}>
                <input type="checkbox" checked={applyReference} onChange={e => setApplyReference(e.target.checked)} style={{ marginTop: 3 }} />
                <span><strong>Attach as AI reference</strong> (persistent) — every future AI draft on this proposal sees the past proposal's section content as "match this style, structure, and voice." This is the big one for shape-matching.</span>
              </label>
            </div>
          )}

          <ModalActions
            onCancel={() => { setShowPicker(false); setSelectedId(null); setQuery(''); }}
            primary={{
              label: applying ? 'Applying…' : 'Apply',
              disabled: !selectedId || applying || (!applyTheme && !applyDirectives && !applyReference),
              onClick: applySelection,
            }}
          />
        </ModalShell>
      )}
    </div>
  );
}

function ProposalThemePanel({ C, opp, currentUser, onUpdated, showToast }) {
  const initial = useMemo(() => resolveTheme(opp.theme), [opp.theme]);
  const [theme, setTheme] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);

  useEffect(() => { setTheme(initial); }, [initial]);

  // Resolve signed URLs for the saved images so the preview shows
  // what's currently on the opportunity.
  useEffect(() => {
    (async () => {
      if (theme.logo_storage_path) {
        const url = await getProposalImageSignedUrl(theme.logo_storage_path);
        setLogoPreview(url);
      } else setLogoPreview(null);
      if (theme.cover_image_storage_path) {
        const url = await getProposalImageSignedUrl(theme.cover_image_storage_path);
        setCoverPreview(url);
      } else setCoverPreview(null);
    })();
  }, [theme.logo_storage_path, theme.cover_image_storage_path]);

  const dirty = JSON.stringify(theme) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    const ok = await updateOpportunity(opp.id, { theme });
    setSaving(false);
    if (!ok) { showToast?.('❌ Theme save failed'); return; }
    showToast?.('✓ Theme saved');
    onUpdated?.({ ...opp, theme });
  };

  const uploadImage = async (kind, file) => {
    setSaving(true);
    const result = await uploadProposalThemeImage(currentUser.orgId, opp.id, kind, file);
    setSaving(false);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    const field = kind === 'logo' ? 'logo_storage_path' : 'cover_image_storage_path';
    setTheme(t => ({ ...t, [field]: result.storage_path }));
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          🎨 Proposal Theme
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
          how the exported PDF will be branded
        </div>
        <div style={{ flex: 1 }} />
        {dirty && (
          <button onClick={save} disabled={saving} style={{
            background: C.accent, color: '#001114', border: 'none', borderRadius: 6,
            padding: '6px 14px', cursor: saving ? 'default' : 'pointer',
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
            letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>{saving ? 'Saving…' : 'Save theme'}</button>
        )}
      </div>

      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.45)', marginBottom: 14, lineHeight: 1.55 }}>
        Anything you don't set falls back to the 40Geo default. Want to match a specific buyer's brand? Upload their logo + pick a primary color + choose Montserrat or Inter (or stick with the default).
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <ThemeField label="Primary color">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="color"
              value={theme.primary_color}
              onChange={e => setTheme(t => ({ ...t, primary_color: e.target.value }))}
              style={{ width: 48, height: 36, borderRadius: 4, border: `1px solid ${C.border}`, background: 'transparent', cursor: 'pointer' }}
            />
            <input
              value={theme.primary_color}
              onChange={e => setTheme(t => ({ ...t, primary_color: e.target.value }))}
              style={modalInput(C)}
            />
          </div>
        </ThemeField>

        <ThemeField label="Font family">
          <select value={theme.font_family} onChange={e => setTheme(t => ({ ...t, font_family: e.target.value }))} style={modalInput(C)}>
            {FONT_CHOICES.map(f => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
        </ThemeField>

        <ThemeField label="Header style">
          <select value={theme.header_style} onChange={e => setTheme(t => ({ ...t, header_style: e.target.value }))} style={modalInput(C)}>
            {HEADER_STYLES.map(h => <option key={h.id} value={h.id}>{h.label}</option>)}
          </select>
        </ThemeField>

        <ThemeField label="Page layout / template">
          <select value={theme.template_style} onChange={e => setTheme(t => ({ ...t, template_style: e.target.value }))} style={modalInput(C)}>
            {TEMPLATE_STYLES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 4, lineHeight: 1.5 }}>
            <strong>Cover-first</strong> matches the Oxy / formal proposal look: big cover image up top, two-column "Prepared for / Prepared by" block, no meta grid, no TOC.
          </div>
        </ThemeField>

        <ThemeField label="Subtitle (small line under the proposal title)">
          <input
            value={theme.subtitle || ''}
            onChange={e => setTheme(t => ({ ...t, subtitle: e.target.value || null }))}
            placeholder='e.g. "Add-On to the AMP Operational Platform · 2026 Service Proposal"'
            style={modalInput(C)}
          />
        </ThemeField>

        <ThemeField label="Header company name (override 40Geo branding)">
          <input
            value={theme.company_name || ''}
            onChange={e => setTheme(t => ({ ...t, company_name: e.target.value || null }))}
            placeholder="40Geo"
            style={modalInput(C)}
          />
        </ThemeField>

        <ThemeField label="Logo (PNG/JPG, ≤5MB)">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {logoPreview ? (
              <img src={logoPreview} alt="Logo" style={{ width: 80, height: 50, objectFit: 'contain', borderRadius: 4, border: `1px solid ${C.border}`, background: '#fff', padding: 4 }} />
            ) : (
              <div style={{ width: 80, height: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, border: `1px dashed ${C.border}`, color: 'rgba(255,255,255,0.3)', fontFamily: "'Montserrat',sans-serif", fontSize: 9 }}>
                default
              </div>
            )}
            <label style={{
              background: 'transparent', color: C.accent, border: `1px solid ${C.accent}66`,
              borderRadius: 4, padding: '5px 10px', cursor: 'pointer',
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              {logoPreview ? 'Replace' : 'Upload'}
              <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadImage('logo', f); }} style={{ display: 'none' }} />
            </label>
            {logoPreview && (
              <button onClick={() => setTheme(t => ({ ...t, logo_storage_path: null }))} style={{
                background: 'transparent', color: 'rgba(248,113,113,0.7)', border: '1px solid rgba(248,113,113,0.4)',
                borderRadius: 4, padding: '5px 10px', cursor: 'pointer',
                fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
              }}>Remove</button>
            )}
          </div>
        </ThemeField>

        <ThemeField label="Cover image (optional — shown under title on page 1)">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {coverPreview ? (
              <img src={coverPreview} alt="Cover" style={{ width: 110, height: 64, objectFit: 'cover', borderRadius: 4, border: `1px solid ${C.border}` }} />
            ) : (
              <div style={{ width: 110, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4, border: `1px dashed ${C.border}`, color: 'rgba(255,255,255,0.3)', fontFamily: "'Montserrat',sans-serif", fontSize: 9 }}>
                none
              </div>
            )}
            <label style={{
              background: 'transparent', color: C.accent, border: `1px solid ${C.accent}66`,
              borderRadius: 4, padding: '5px 10px', cursor: 'pointer',
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
            }}>
              {coverPreview ? 'Replace' : 'Upload'}
              <input type="file" accept="image/*" onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadImage('cover', f); }} style={{ display: 'none' }} />
            </label>
            {coverPreview && (
              <button onClick={() => setTheme(t => ({ ...t, cover_image_storage_path: null }))} style={{
                background: 'transparent', color: 'rgba(248,113,113,0.7)', border: '1px solid rgba(248,113,113,0.4)',
                borderRadius: 4, padding: '5px 10px', cursor: 'pointer',
                fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
              }}>Remove</button>
            )}
          </div>
        </ThemeField>
      </div>

      <div style={{ marginTop: 14, padding: 12, border: `1px solid ${C.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.02)' }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 10 }}>Page footer (rendered on every page)</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.8)', marginBottom: 10 }}>
          <input type="checkbox" checked={theme.show_page_numbers !== false} onChange={e => setTheme(t => ({ ...t, show_page_numbers: e.target.checked }))} />
          Show "Page X of Y" at bottom of each page
        </label>
        <ThemeField label="Confidentiality / custom footer text (optional)">
          <textarea
            rows={2}
            value={theme.footer_text || ''}
            onChange={e => setTheme(t => ({ ...t, footer_text: e.target.value || null }))}
            placeholder='e.g. "Confidential — for evaluation only. Do not distribute."'
            style={{ ...modalInput(C), resize: 'vertical', fontFamily: "'Inter',sans-serif", fontSize: 11 }}
          />
        </ThemeField>
      </div>

      {theme.template_style === 'cover_first' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
          <ThemeField label="Prepared for (override — defaults to client + primary contact)">
            <textarea
              rows={5}
              value={theme.prepared_for || ''}
              onChange={e => setTheme(t => ({ ...t, prepared_for: e.target.value || null }))}
              placeholder={`Leave blank to auto-fill from the linked primary contact, e.g.:\n\nOccidental Petroleum (Oxy)\nAttn: Steve Daigle, CSM\nEmergency Preparedness & Response Manager\nOxy – GOA Business Unit\nAllison Tower, Office 16090, Houston, Texas\nCell: 713-842-0532\nsteve_daigle@oxy.com`}
              style={{ ...modalInput(C), resize: 'vertical', fontFamily: "'Inter',sans-serif", fontSize: 11 }}
            />
          </ThemeField>
          <ThemeField label="Prepared by (your side — leave blank for default)">
            <textarea
              rows={5}
              value={theme.prepared_by || ''}
              onChange={e => setTheme(t => ({ ...t, prepared_by: e.target.value || null }))}
              placeholder={`Leave blank for default, e.g.:\n\n40Geo, LLC.\nMiles Roden\n912 Heights Ave, Houston, Texas 77008\nOffice: 713-397-0584`}
              style={{ ...modalInput(C), resize: 'vertical', fontFamily: "'Inter',sans-serif", fontSize: 11 }}
            />
          </ThemeField>
        </div>
      )}

      <div style={{ marginTop: 16, padding: 10, borderRadius: 6, border: `1px solid ${theme.primary_color}55`, background: `${theme.primary_color}10` }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.primary_color, marginBottom: 4 }}>
          Preview
        </div>
        <div style={{ fontFamily: theme.font_family === 'montserrat' ? "'Montserrat',sans-serif" : theme.font_family === 'inter' ? "'Inter',sans-serif" : theme.font_family === 'serif' ? 'Georgia, serif' : '-apple-system, sans-serif', fontSize: 13, color: '#fff', fontWeight: 700 }}>
          {theme.company_name || '40Geo'} · Proposal · {opp.client_name || 'Client'}
        </div>
        <div style={{ marginTop: 4, fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
          Open the 👁 Preview button at the top of the workspace to see the full styled output.
        </div>
      </div>
    </div>
  );
}

function ThemeField({ label, children }) {
  return (
    <div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', marginBottom: 6 }}>{label}</div>
      {children}
    </div>
  );
}

// ─── Outcome + feedback loop (Phase 12) ──────────────────────────
function OutcomePanel({ C, opp, currentUser, onUpdated, showToast }) {
  const [outcome, setOutcome]         = useState(null);
  const [loading, setLoading]         = useState(true);
  const [competitors, setCompetitors] = useState([]);
  const [showCapture, setShowCapture] = useState(false);
  const [saving, setSaving]           = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [o, c] = await Promise.all([
      loadOpportunityOutcome(opp.id),
      loadOpportunityCompetitors(opp.id),
    ]);
    setOutcome(o);
    setCompetitors(c);
    setLoading(false);
  }, [opp.id]);
  useEffect(() => { refresh(); }, [refresh]);

  const onSubmitCapture = async (fields) => {
    const result = await upsertOpportunityOutcome(opp.id, fields, currentUser.id);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setOutcome(result.row);
    setShowCapture(false);
    showToast?.(`✓ Outcome recorded — ${OUTCOME_KINDS.find(k => k.id === fields.outcome)?.label}`);
    // Bump the opportunity's stage too via onUpdated.
    onUpdated?.({ ...opp, stage: ['won','lost'].includes(fields.outcome) ? fields.outcome : opp.stage });
  };

  const saveToLib = async () => {
    if (!outcome) return;
    setSaving(true);
    const result = await saveOutcomeToLibrary(opp.id, outcome.id, currentUser);
    setSaving(false);
    if (result.error) { showToast?.(`❌ Save to library failed: ${result.error}`); return; }
    setOutcome({ ...outcome, saved_to_library: true, saved_to_library_doc_id: result.libraryDoc.id });
    showToast?.('✓ Saved to library — future proposals can lean on this');
  };

  // Fall back to a neutral chip when the outcome value isn't one of
  // the known kinds (legacy row from before the enum was locked
  // down, or an ad-hoc SQL insert).  Without this fallback the panel
  // renders `meta.color` on `undefined` and crashes the whole
  // Proposal Coach view.
  const OUTCOME_FALLBACK = { id: 'unknown', label: 'Unknown', color: '#94a3b8' };
  const meta = outcome
    ? (OUTCOME_KINDS.find(k => k.id === outcome.outcome) || OUTCOME_FALLBACK)
    : null;

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Outcome
        </div>
        {outcome && meta && (
          <span style={{
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
            letterSpacing: '0.1em', textTransform: 'uppercase',
            padding: '4px 10px', borderRadius: 4,
            background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55`,
          }}>{meta.label}</span>
        )}
        {outcome?.decided_at && (
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
            decided {new Date(outcome.decided_at).toLocaleDateString()}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => setShowCapture(true)} style={{
          background: C.accent, color: '#001114',
          border: 'none', borderRadius: 6, padding: '6px 12px',
          cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
          fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>{outcome ? 'Edit outcome' : 'Record outcome'}</button>
      </div>

      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 14, lineHeight: 1.5 }}>
        Post-decision capture: did we win, lose, no-decision, or withdraw? Why? What did the client say? What did we learn? Won proposals can be saved back into the org library as precedents the next opportunity's AI can lean on.
      </div>

      {loading ? (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>Loading…</div>
      ) : !outcome ? (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
          No outcome recorded yet. Click "Record outcome" when the client decides.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          {outcome.outcome === 'won' && outcome.awarded_value && (
            <OutcomeField label="Awarded value" color="#34d399">
              ${Number(outcome.awarded_value).toLocaleString()} {outcome.awarded_currency || 'USD'}
            </OutcomeField>
          )}
          {outcome.outcome === 'lost' && outcome.winning_competitor_name && (
            <OutcomeField label="Won by" color="#f87171">
              {outcome.winning_competitor_name}
            </OutcomeField>
          )}
          {outcome.reasons_we_won && (
            <OutcomeField label="Why we won" color="#34d399" wide>
              {outcome.reasons_we_won}
            </OutcomeField>
          )}
          {outcome.reasons_we_lost && (
            <OutcomeField label="Why we lost" color="#f87171" wide>
              {outcome.reasons_we_lost}
            </OutcomeField>
          )}
          {outcome.client_feedback && (
            <OutcomeField label="Client feedback" color="#22d3ee" wide>
              {outcome.client_feedback}
            </OutcomeField>
          )}
          {outcome.lessons_learned && (
            <OutcomeField label="Lessons learned" color="#a78bfa" wide>
              {outcome.lessons_learned}
            </OutcomeField>
          )}

          {outcome.outcome === 'won' && (
            <div style={{ gridColumn: '1 / -1', marginTop: 8, padding: 14, borderRadius: 6, border: `1px dashed ${C.accent}55`, background: `${C.accent}08` }}>
              {outcome.saved_to_library ? (
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: C.accent, fontWeight: 700 }}>
                  ✓ Saved to library as a precedent
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ flex: 1, fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                    Save this won proposal to the org library so future proposals can use it as a precedent.
                  </div>
                  <button onClick={saveToLib} disabled={saving} style={{
                    background: C.accent, color: '#001114',
                    border: 'none', borderRadius: 6, padding: '6px 12px',
                    cursor: saving ? 'default' : 'pointer',
                    fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                  }}>{saving ? 'Saving…' : '→ Save to library'}</button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {showCapture && (
        <OutcomeCaptureModal
          C={C}
          opp={opp}
          existing={outcome}
          competitors={competitors}
          onCancel={() => setShowCapture(false)}
          onSubmit={onSubmitCapture}
        />
      )}
    </div>
  );
}

function OutcomeField({ label, color, children, wide = false }) {
  return (
    <div style={{ gridColumn: wide ? '1 / -1' : 'auto', padding: 10, border: '1px solid rgba(255,255,255,0.08)', borderRadius: 6, background: 'rgba(255,255,255,0.02)' }}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>{children}</div>
    </div>
  );
}

function OutcomeCaptureModal({ C, opp, existing, competitors, onCancel, onSubmit }) {
  const [outcome, setOutcome]               = useState(existing?.outcome || 'won');
  const [decidedAt, setDecidedAt]           = useState(existing?.decided_at || new Date().toISOString().slice(0, 10));
  const [awardedValue, setAwardedValue]     = useState(existing?.awarded_value || opp.estimated_value || '');
  const [awardedCurrency, setAwardedCurrency] = useState(existing?.awarded_currency || opp.currency || 'USD');
  const [winningCompetitorId, setWcId]      = useState(existing?.winning_competitor_id || '');
  const [winningCompetitorName, setWcName]  = useState(existing?.winning_competitor_name || '');
  const [reasonsWon, setReasonsWon]         = useState(existing?.reasons_we_won || '');
  const [reasonsLost, setReasonsLost]       = useState(existing?.reasons_we_lost || '');
  const [clientFeedback, setClientFeedback] = useState(existing?.client_feedback || '');
  const [lessonsLearned, setLessonsLearned] = useState(existing?.lessons_learned || '');

  // If picking a tracked competitor, copy its name into the snapshot
  // field so deleting the competitor row later doesn't lose the
  // historical fact.
  useEffect(() => {
    if (winningCompetitorId) {
      const c = competitors.find(x => x.id === winningCompetitorId);
      if (c) setWcName(c.name);
    }
  }, [winningCompetitorId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ModalShell title={`${existing ? 'Edit outcome' : 'Record outcome'} · ${opp.name}`} onCancel={onCancel}>
      <Field label="Outcome" required>
        <div style={{ display: 'flex', gap: 8 }}>
          {OUTCOME_KINDS.map(k => {
            const on = outcome === k.id;
            return (
              <button key={k.id} type="button" onClick={() => setOutcome(k.id)} style={{
                flex: 1, padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                background: on ? `${k.color}22` : 'transparent',
                border: `1px solid ${on ? k.color : 'rgba(255,255,255,0.18)'}`,
                color: on ? k.color : 'rgba(255,255,255,0.6)',
                fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
              }}>{k.label}</button>
            );
          })}
        </div>
      </Field>
      <div style={{ height: 10 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Decision date">
          <input type="date" value={decidedAt} onChange={e => setDecidedAt(e.target.value)} style={modalInput(C)} />
        </Field>
        {outcome === 'won' && (
          <>
            <Field label="Awarded value">
              <input type="number" value={awardedValue} onChange={e => setAwardedValue(e.target.value)} style={modalInput(C)} />
            </Field>
            <Field label="Currency">
              <input value={awardedCurrency} onChange={e => setAwardedCurrency(e.target.value)} style={modalInput(C)} />
            </Field>
          </>
        )}
        {outcome === 'lost' && (
          <>
            <Field label="Won by (tracked competitor)">
              <select value={winningCompetitorId} onChange={e => setWcId(e.target.value)} style={modalInput(C)}>
                <option value="">— None / unknown —</option>
                {competitors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="…or other competitor name">
              <input value={winningCompetitorName} onChange={e => setWcName(e.target.value)} style={modalInput(C)} />
            </Field>
          </>
        )}
      </div>
      <div style={{ height: 12 }} />
      {outcome === 'won' && (
        <Field label="Why we won">
          <textarea rows={3} value={reasonsWon} onChange={e => setReasonsWon(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }}
            placeholder="What tipped the decision — relationship, price, technical fit, past performance, etc." />
        </Field>
      )}
      {outcome === 'lost' && (
        <Field label="Why we lost">
          <textarea rows={3} value={reasonsLost} onChange={e => setReasonsLost(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }}
            placeholder="What pushed us out — price, fit, missing capability, relationship strength, etc." />
        </Field>
      )}
      <div style={{ height: 10 }} />
      <Field label="Client feedback (if any)">
        <textarea rows={2} value={clientFeedback} onChange={e => setClientFeedback(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }}
          placeholder="Anything the client told us directly — what they liked, what concerned them, anything we should change next time" />
      </Field>
      <div style={{ height: 10 }} />
      <Field label="Lessons learned (for the next proposal)">
        <textarea rows={3} value={lessonsLearned} onChange={e => setLessonsLearned(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }}
          placeholder="What we'd do differently. This is the most valuable field — it's what the next opportunity gets to inherit." />
      </Field>
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: existing ? 'Save outcome' : 'Record outcome',
          onClick: () => onSubmit({
            outcome,
            decided_at:        decidedAt || null,
            awarded_value:     outcome === 'won' ? awardedValue : null,
            awarded_currency:  outcome === 'won' ? awardedCurrency : null,
            winning_competitor_id:   outcome === 'lost' ? (winningCompetitorId || null) : null,
            winning_competitor_name: outcome === 'lost' ? (winningCompetitorName || null) : null,
            reasons_we_won:    outcome === 'won' ? reasonsWon : null,
            reasons_we_lost:   outcome === 'lost' ? reasonsLost : null,
            client_feedback:   clientFeedback,
            lessons_learned:   lessonsLearned,
          }),
        }} />
      </div>
    </ModalShell>
  );
}

// ─── Competition (Phase 10) ──────────────────────────────────────
function CompetitionPanel({ C, opp, currentUser, showToast }) {
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [editing, setEditing]         = useState(null); // competitor row OR {} for add

  const refresh = useCallback(async () => {
    setLoading(true);
    setCompetitors(await loadOpportunityCompetitors(opp.id));
    setLoading(false);
  }, [opp.id]);
  useEffect(() => { refresh(); }, [refresh]);

  const onSubmit = async (fields) => {
    const result = await upsertOpportunityCompetitor(opp.id, fields, currentUser.id);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setEditing(null);
    refresh();
    showToast?.('✓ Competitor saved');
  };
  const onRemove = async (c) => {
    if (!confirm(`Remove "${c.name}" from competition tracking?`)) return;
    const ok = await deleteOpportunityCompetitor(c.id);
    if (ok) setCompetitors(prev => prev.filter(x => x.id !== c.id));
    else showToast?.('❌ Remove failed');
  };

  const threatOrder = { incumbent: 0, high: 1, medium: 2, low: 3 };
  const sorted = [...competitors].sort((a, b) => (threatOrder[a.threat_level] ?? 4) - (threatOrder[b.threat_level] ?? 4));

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Competition
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
          {competitors.length} {competitors.length === 1 ? 'competitor' : 'competitors'}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={() => setEditing({})} style={{
          background: 'transparent', color: C.accent, border: `1px solid ${C.accent}66`, borderRadius: 6,
          padding: '5px 11px', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
          fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
        }}>+ Add competitor</button>
      </div>

      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 14, lineHeight: 1.5 }}>
        Who else is bidding. Capture threat level, what they're strong / weak at, and our sustainable edge so positioning stays specific (not aimed at imagined competitors).
      </div>

      {loading ? (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>Loading…</div>
      ) : sorted.length === 0 ? (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
          No competitors tracked yet.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {sorted.map(c => {
            const threat = THREAT_LEVELS.find(t => t.id === c.threat_level) || THREAT_LEVELS[1];
            return (
              <div key={c.id} style={{
                border: `1px solid ${C.border}`, borderLeft: `3px solid ${threat.color}`,
                borderRadius: 6, padding: 12, background: 'rgba(255,255,255,0.02)',
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: '#fff', flex: 1 }}>
                    {c.name}
                  </div>
                  <span style={{
                    fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    padding: '3px 8px', borderRadius: 3,
                    background: `${threat.color}22`, color: threat.color, border: `1px solid ${threat.color}55`,
                  }}>{threat.label}</span>
                  {c.pricing_posture && c.pricing_posture !== 'unknown' && (
                    <span style={{
                      fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700,
                      letterSpacing: '0.06em', color: 'rgba(255,255,255,0.55)',
                      padding: '3px 8px', borderRadius: 3, border: `1px solid ${C.border}`,
                    }}>{PRICING_POSTURES.find(p => p.id === c.pricing_posture)?.label || c.pricing_posture}</span>
                  )}
                  <button onClick={() => setEditing(c)} style={{
                    background: 'transparent', color: 'rgba(255,255,255,0.5)',
                    border: `1px solid ${C.border}`, borderRadius: 4, padding: '3px 8px',
                    cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}>Edit</button>
                  <button onClick={() => onRemove(c)} style={{
                    background: 'transparent', color: 'rgba(248,113,113,0.7)',
                    border: '1px solid rgba(248,113,113,0.4)', borderRadius: 4, padding: '3px 8px',
                    cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                    fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}>Remove</button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                  <CompetitorField label="Their strengths" value={c.strengths} color="#fbbf24" />
                  <CompetitorField label="Their weaknesses" value={c.weaknesses} color="#34d399" />
                  <CompetitorField label="Our edge vs them" value={c.our_advantage} color="#22d3ee" />
                </div>
                {(c.source || c.notes) && (
                  <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px dashed ${C.border}`, fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
                    {c.source && <div>Source: {c.source}</div>}
                    {c.notes && <div style={{ marginTop: 4 }}>{c.notes}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <CompetitorModal C={C} competitor={editing.id ? editing : null} onCancel={() => setEditing(null)} onSubmit={onSubmit} />
      )}
    </div>
  );
}

function CompetitorField({ label, value, color }) {
  return (
    <div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: value ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.3)', lineHeight: 1.5, fontStyle: value ? 'normal' : 'italic' }}>
        {value || '—'}
      </div>
    </div>
  );
}

function CompetitorModal({ C, competitor, onCancel, onSubmit }) {
  const [name, setName]                 = useState(competitor?.name || '');
  const [threatLevel, setThreatLevel]   = useState(competitor?.threat_level || 'medium');
  const [strengths, setStrengths]       = useState(competitor?.strengths || '');
  const [weaknesses, setWeaknesses]     = useState(competitor?.weaknesses || '');
  const [ourEdge, setOurEdge]           = useState(competitor?.our_advantage || '');
  const [pricingPosture, setPosture]    = useState(competitor?.pricing_posture || 'unknown');
  const [source, setSource]             = useState(competitor?.source || '');
  const [notes, setNotes]               = useState(competitor?.notes || '');

  return (
    <ModalShell title={competitor ? `Edit · ${competitor.name}` : 'Add competitor'} onCancel={onCancel}>
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
        <Field label="Name" required>
          <input value={name} onChange={e => setName(e.target.value)} style={modalInput(C)} placeholder="e.g. Acme Logistics" />
        </Field>
        <Field label="Threat level">
          <select value={threatLevel} onChange={e => setThreatLevel(e.target.value)} style={modalInput(C)}>
            {THREAT_LEVELS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Pricing posture">
          <select value={pricingPosture} onChange={e => setPosture(e.target.value)} style={modalInput(C)}>
            {PRICING_POSTURES.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </Field>
      </div>
      <div style={{ height: 10 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Field label="Their strengths">
          <textarea rows={4} value={strengths} onChange={e => setStrengths(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }} placeholder="What they do well" />
        </Field>
        <Field label="Their weaknesses">
          <textarea rows={4} value={weaknesses} onChange={e => setWeaknesses(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }} placeholder="Where they fall short" />
        </Field>
        <Field label="Our edge vs them">
          <textarea rows={4} value={ourEdge} onChange={e => setOurEdge(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }} placeholder="Our sustainable advantage" />
        </Field>
      </div>
      <div style={{ height: 10 }} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
        <Field label="Source of intel">
          <input value={source} onChange={e => setSource(e.target.value)} style={modalInput(C)} placeholder="RFP, client mention, market scan…" />
        </Field>
        <Field label="Notes (optional)">
          <input value={notes} onChange={e => setNotes(e.target.value)} style={modalInput(C)} />
        </Field>
      </div>
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: competitor ? 'Save' : 'Add competitor',
          disabled: !name.trim(),
          onClick: () => onSubmit({
            id: competitor?.id,
            name, threat_level: threatLevel, strengths, weaknesses,
            our_advantage: ourEdge, pricing_posture: pricingPosture,
            source, notes,
          }),
        }} />
      </div>
    </ModalShell>
  );
}

// ─── Critique Mode (Phase 9) ─────────────────────────────────────
const GRADE_COLOR = { A: '#34d399', B: '#22d3ee', C: '#fbbf24', D: '#fb923c', F: '#f87171' };
const CRITIQUE_CATEGORY_META = {
  weak_claim:              { label: 'Weak claim',              color: '#f87171' },
  generic_language:        { label: 'Generic language',        color: '#fbbf24' },
  missed_differentiation:  { label: 'Missed differentiation',  color: '#fb923c' },
  tone_mismatch:           { label: 'Tone mismatch',           color: '#a78bfa' },
  persuasion_gap:          { label: 'Persuasion gap',          color: '#fb923c' },
  inconsistency:           { label: 'Inconsistency',           color: '#f87171' },
  factual_concern:         { label: 'Factual concern',         color: '#dc2626' },
};

function CritiquePanel({ C, opp, onUpdated, showToast }) {
  const [running, setRunning] = useState(false);
  // Track which weakness's fix is being applied (so only one button
  // shows the spinner at a time even though many can be clicked).
  const [applyingFixKey, setApplyingFixKey] = useState(null);

  const jumpToSection = (title) => {
    if (!title) return;
    window.dispatchEvent(new CustomEvent('proposal-jump-to-section', { detail: { sectionTitle: title } }));
  };

  const applyFix = async (w, idx) => {
    if (!w.fix && !w.suggested_fix) { showToast?.('No fix to apply on this weakness.'); return; }
    // Pick the first affected section as the apply target. Multi-
    // section weaknesses (inconsistencies) need to be applied per
    // section — one click per chip.
    const targetTitle = (Array.isArray(w.sections) && w.sections[0]) || w.section;
    if (!targetTitle || targetTitle === 'overall') {
      showToast?.('This critique points at the overall proposal, not a specific section — open the relevant section manually to apply.');
      return;
    }
    // Need to resolve title → section id. Dispatch a one-shot
    // resolver via the existing jump event — but we also need the
    // section_id directly. Simpler: ask the parent workspace via a
    // resolver event.
    const key = `${idx}-${targetTitle}`;
    setApplyingFixKey(key);
    const result = await new Promise((resolve) => {
      const handler = (e) => {
        window.removeEventListener('proposal-section-resolved', handler);
        resolve(e.detail);
      };
      window.addEventListener('proposal-section-resolved', handler);
      window.dispatchEvent(new CustomEvent('proposal-resolve-section', { detail: { sectionTitle: targetTitle } }));
      // 1s safety timeout in case the workspace isn't mounted
      setTimeout(() => { window.removeEventListener('proposal-section-resolved', handler); resolve(null); }, 1000);
    });
    if (!result || !result.sectionId) {
      setApplyingFixKey(null);
      showToast?.(`Couldn't find a section matching "${targetTitle}".`);
      return;
    }
    const applyResult = await applyCritiqueFixToSection(result.sectionId, w.fix || w.suggested_fix, {
      issue:        w.issue,
      whyItMatters: w.why_it_matters,
    });
    setApplyingFixKey(null);
    if (applyResult.error) { showToast?.(`❌ Apply fix failed: ${applyResult.error}`); return; }
    showToast?.(`✓ Fix applied to "${targetTitle}" — review it (history is preserved)`);
    // Refresh the workspace's section data and jump to it so the
    // user immediately sees the new prose.
    window.dispatchEvent(new CustomEvent('proposal-refresh-sections'));
    jumpToSection(targetTitle);
  };

  const findings    = opp.critique_findings || {};
  const summary     = opp.critique_summary;
  const grade       = opp.critique_grade;
  const updatedAt   = opp.critique_updated_at;
  const strengths   = Array.isArray(findings.top_strengths) ? findings.top_strengths : [];
  const weaknesses  = Array.isArray(findings.weaknesses)    ? findings.weaknesses    : [];
  const toneGrade   = findings.tone_grade;
  const toneNote    = findings.tone_note;
  const competitor  = findings.competitor_attack;

  const sortedWeaknesses = [...weaknesses].sort((a, b) => {
    const rank = { critical: 0, high: 1, medium: 2, low: 3 };
    return (rank[a.severity] ?? 3) - (rank[b.severity] ?? 3);
  });

  const runCritique = async () => {
    setRunning(true);
    const result = await runOpportunityCritique(opp.id);
    setRunning(false);
    if (result.error) { showToast?.(`❌ Critique failed: ${result.error}`); return; }
    showToast?.(`✓ Critique: grade ${result.critique_grade || '—'}`);
    onUpdated?.({ ...opp, ...result });
  };

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Critique Mode
        </div>
        {updatedAt && (
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
            critiqued {new Date(updatedAt).toLocaleString()}{opp.critique_model ? ` · ${opp.critique_model}` : ''}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={runCritique} disabled={running} style={{
          background: running ? 'rgba(255,255,255,0.1)' : C.accent,
          color: running ? 'rgba(255,255,255,0.4)' : '#001114',
          border: 'none', borderRadius: 6, padding: '6px 12px',
          cursor: running ? 'default' : 'pointer',
          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>{running ? 'Critiquing…' : (grade ? '↻ Re-critique' : '🎯 Run critique')}</button>
      </div>

      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 14, lineHeight: 1.5 }}>
        AI puts on the buyer's hat and tears the proposal apart. Surfaces weak claims, generic language, missed differentiation, tone-mismatch, and persuasion gaps in what's PRESENT (vs. gap radar which finds what's missing).
      </div>

      {grade && (
        <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 16, marginBottom: 16, alignItems: 'center' }}>
          <div style={{
            padding: '16px 0', borderRadius: 8, textAlign: 'center',
            background: `${GRADE_COLOR[grade] || '#94a3b8'}1a`,
            border: `2px solid ${GRADE_COLOR[grade] || '#94a3b8'}55`,
          }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>Buyer's grade</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 48, fontWeight: 900, color: GRADE_COLOR[grade] || '#94a3b8', lineHeight: 1, marginTop: 4 }}>{grade}</div>
            {toneGrade && (
              <div style={{ marginTop: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)' }}>
                Tone grade: <span style={{ color: GRADE_COLOR[toneGrade] || '#94a3b8', fontWeight: 700 }}>{toneGrade}</span>
              </div>
            )}
          </div>
          <div>
            {summary && (
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, marginBottom: 8 }}>
                {summary}
              </div>
            )}
            {toneNote && (
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.6)', lineHeight: 1.5, fontStyle: 'italic' }}>
                Tone note: {toneNote}
              </div>
            )}
          </div>
        </div>
      )}

      {(strengths.length > 0 || sortedWeaknesses.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 14 }}>
          <div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#34d399', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #34d39933' }}>
              Top strengths {strengths.length > 0 && <span style={{ color: 'rgba(255,255,255,0.4)' }}>({strengths.length})</span>}
            </div>
            {strengths.length === 0 ? (
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>—</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {strengths.map((s, i) => (
                  <div key={i} style={{ padding: 8, border: `1px solid ${C.border}`, borderRadius: 4, background: 'rgba(52,211,153,0.04)' }}>
                    <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 3 }}>{s.label}</div>
                    {s.detail && <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>{s.detail}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#f87171', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid #f8717133' }}>
              Weaknesses {sortedWeaknesses.length > 0 && <span style={{ color: 'rgba(255,255,255,0.4)' }}>({sortedWeaknesses.length})</span>}
            </div>
            {sortedWeaknesses.length === 0 ? (
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>—</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {sortedWeaknesses.map((w, i) => {
                  const cat = CRITIQUE_CATEGORY_META[w.category] || { label: w.category || 'Issue', color: '#94a3b8' };
                  const sevColor = { critical: '#dc2626', high: '#f87171', medium: '#fbbf24', low: '#94a3b8' }[w.severity] || '#94a3b8';
                  return (
                    <div key={i} style={{
                      padding: 10, border: `1px solid ${C.border}`, borderRadius: 4,
                      background: 'rgba(248,113,113,0.04)', borderLeft: `3px solid ${sevColor}`,
                    }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: '#fff' }}>{w.issue}</div>
                        <span style={{
                          fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase',
                          padding: '2px 6px', borderRadius: 3,
                          background: `${cat.color}22`, color: cat.color, border: `1px solid ${cat.color}44`,
                        }}>{cat.label}</span>
                        {w.severity && (
                          <span style={{
                            fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase',
                            padding: '2px 6px', borderRadius: 3,
                            background: `${sevColor}22`, color: sevColor, border: `1px solid ${sevColor}44`,
                          }}>{w.severity}</span>
                        )}
                        <div style={{ flex: 1 }} />
                        {/* Section chips — multi-section weaknesses (inconsistencies) list ALL involved.
                            Each chip is a click target that jumps the workspace into the section editor. */}
                        {(() => {
                          const sectionsList = (Array.isArray(w.sections) && w.sections.length > 0)
                            ? w.sections
                            : (w.section ? [w.section] : []);
                          if (sectionsList.length === 0) return null;
                          return (
                            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                              {sectionsList.map((title, ix) => (
                                <button
                                  key={`${title}-${ix}`}
                                  onClick={() => jumpToSection(title)}
                                  title={`Jump to ${title} editor`}
                                  style={{
                                    background: 'rgba(34,211,238,0.1)',
                                    color: '#22d3ee',
                                    border: '1px solid rgba(34,211,238,0.4)',
                                    borderRadius: 3, padding: '2px 7px',
                                    cursor: 'pointer',
                                    fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700,
                                    letterSpacing: '0.05em',
                                  }}
                                >✎ {title}</button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      {w.why_it_matters && (
                        <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5, marginBottom: 6 }}>
                          {w.why_it_matters}
                        </div>
                      )}
                      {w.suggested_fix && (() => {
                        const fixKey = `${i}-${(Array.isArray(w.sections) && w.sections[0]) || w.section || ''}`;
                        const applying = applyingFixKey === fixKey;
                        const targetTitle = (Array.isArray(w.sections) && w.sections[0]) || w.section;
                        const targetable = targetTitle && targetTitle !== 'overall';
                        return (
                          <div style={{
                            fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#fff',
                            padding: 8, borderRadius: 4, background: 'rgba(34,211,238,0.08)',
                            border: '1px solid rgba(34,211,238,0.25)',
                            display: 'flex', alignItems: 'flex-start', gap: 8,
                          }}>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#22d3ee', marginRight: 6 }}>Fix:</span>
                              {w.suggested_fix}
                            </div>
                            {targetable && (
                              <button
                                onClick={() => applyFix({ ...w, fix: w.suggested_fix }, i)}
                                disabled={applying}
                                title={`Have the AI apply this fix to "${targetTitle}" — version history preserves the prior content`}
                                style={{
                                  background: applying ? 'rgba(255,255,255,0.1)' : '#22d3ee',
                                  color: applying ? 'rgba(255,255,255,0.5)' : '#001114',
                                  border: 'none', borderRadius: 4, padding: '5px 10px',
                                  cursor: applying ? 'default' : 'pointer',
                                  fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800,
                                  letterSpacing: '0.07em', textTransform: 'uppercase',
                                  whiteSpace: 'nowrap',
                                  alignSelf: 'flex-start',
                                }}
                              >{applying ? 'Applying…' : '✨ Apply fix'}</button>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {competitor && (
        <div style={{
          marginTop: 16, padding: 14, borderRadius: 6,
          border: '1px solid rgba(167,139,250,0.3)', background: 'rgba(167,139,250,0.06)',
        }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#a78bfa', marginBottom: 8 }}>
            🎯 How a competitor would attack
          </div>
          <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6 }}>
            {competitor}
          </div>
        </div>
      )}

      {!grade && !running && (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
          Draft at least one section, then run the critique.
        </div>
      )}
    </div>
  );
}

// ─── Gap Radar (Phase 8) ─────────────────────────────────────────
function GapRadarPanel({ C, opp, onUpdated, showToast }) {
  const [running, setRunning] = useState(false);

  const findings  = Array.isArray(opp.gap_findings) ? opp.gap_findings : [];
  const summary   = opp.gap_findings_summary;
  const updatedAt = opp.gap_findings_updated_at;

  const counts = findings.reduce((acc, f) => {
    const sev = f.severity || 'low';
    acc[sev] = (acc[sev] || 0) + 1;
    return acc;
  }, {});

  const runRadar = async () => {
    setRunning(true);
    const result = await runOpportunityGapRadar(opp.id);
    setRunning(false);
    if (result.error) { showToast?.(`❌ Gap radar failed: ${result.error}`); return; }
    showToast?.(`✓ Gap radar: ${result.gap_findings?.length || 0} findings`);
    onUpdated?.({ ...opp, ...result });
  };

  const SEVERITY_META = {
    critical: { color: '#dc2626', label: 'Critical' },
    high:     { color: '#f87171', label: 'High' },
    medium:   { color: '#fbbf24', label: 'Medium' },
    low:      { color: '#94a3b8', label: 'Low' },
  };

  // Sort findings by severity then current_state (missing > partial > covered_well)
  const order = (f) => {
    const sevRank = { critical: 0, high: 1, medium: 2, low: 3 }[f.severity || 'low'];
    const stateRank = { missing: 0, partial: 1, covered_well: 2 }[f.current_state || 'partial'];
    return (sevRank ?? 3) * 10 + (stateRank ?? 1);
  };
  const sorted = [...findings].sort((a, b) => order(a) - order(b));

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Gap Radar
        </div>
        {updatedAt && (
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
            scanned {new Date(updatedAt).toLocaleString()}{opp.gap_findings_model ? ` · ${opp.gap_findings_model}` : ''}
          </div>
        )}
        <div style={{ flex: 1 }} />
        {findings.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginRight: 8 }}>
            {['critical', 'high', 'medium', 'low'].map(sev => counts[sev] ? (
              <span key={sev} style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800,
                letterSpacing: '0.08em', textTransform: 'uppercase',
                padding: '3px 7px', borderRadius: 3,
                background: `${SEVERITY_META[sev].color}22`,
                color: SEVERITY_META[sev].color,
                border: `1px solid ${SEVERITY_META[sev].color}55`,
              }}>{counts[sev]} {SEVERITY_META[sev].label}</span>
            ) : null)}
          </div>
        )}
        <button onClick={runRadar} disabled={running} style={{
          background: running ? 'rgba(255,255,255,0.1)' : C.accent,
          color: running ? 'rgba(255,255,255,0.4)' : '#001114',
          border: 'none', borderRadius: 6, padding: '6px 12px',
          cursor: running ? 'default' : 'pointer',
          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>{running ? 'Scanning…' : (findings.length === 0 ? '✨ Run gap radar' : '↻ Re-scan')}</button>
      </div>

      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 14, lineHeight: 1.5 }}>
        AI sweep comparing the inbound RFP against your drafted proposal sections + outbound deliverables checklist. Surfaces requirements not yet addressed.
      </div>

      {summary && (
        <div style={{
          padding: 12, marginBottom: 14, borderRadius: 6,
          border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.02)',
          fontFamily: "'Inter',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.85)',
          lineHeight: 1.6,
        }}>{summary}</div>
      )}

      {sorted.length === 0 && !running && (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
          {updatedAt ? 'No gaps surfaced.' : 'Not yet scanned. Run the radar to find requirements not yet addressed in the proposal.'}
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sorted.map((f, i) => {
          const sev   = SEVERITY_META[f.severity] || SEVERITY_META.low;
          const state = f.current_state || 'partial';
          const stateColor = state === 'covered_well' ? '#34d399' : state === 'partial' ? '#fbbf24' : '#f87171';
          return (
            <div key={i} style={{
              border: `1px solid ${C.border}`, borderRadius: 6, padding: 12,
              background: 'rgba(255,255,255,0.02)',
              borderLeft: `3px solid ${sev.color}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff', flex: 1 }}>
                  {f.requirement}
                </div>
                <span style={{
                  fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  padding: '2px 7px', borderRadius: 3,
                  background: `${stateColor}22`, color: stateColor,
                  border: `1px solid ${stateColor}55`,
                }}>{state.replace('_',' ')}</span>
                <span style={{
                  fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800,
                  letterSpacing: '0.08em', textTransform: 'uppercase',
                  padding: '2px 7px', borderRadius: 3,
                  background: `${sev.color}22`, color: sev.color,
                  border: `1px solid ${sev.color}55`,
                }}>{sev.label}</span>
              </div>
              {f.where_in_rfp && f.where_in_rfp !== '(unspecified)' && (
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em', marginBottom: 6 }}>
                  RFP reference: {f.where_in_rfp}
                </div>
              )}
              {f.evidence && (
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.75)', lineHeight: 1.5, marginBottom: 6 }}>
                  {f.evidence}
                </div>
              )}
              {f.suggested_action && (
                <div style={{
                  fontFamily: "'Inter',sans-serif", fontSize: 11, color: '#fff',
                  padding: 8, borderRadius: 4, background: 'rgba(34,211,238,0.08)',
                  border: '1px solid rgba(34,211,238,0.25)', marginTop: 6,
                }}>
                  <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#22d3ee', marginRight: 6 }}>Action:</span>
                  {f.suggested_action}
                  {f.suggested_section && f.suggested_section !== 'other' && (
                    <span style={{ marginLeft: 6, fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
                      → {(PROPOSAL_SECTION_TYPES.find(t => t.id === f.suggested_section)?.label) || f.suggested_section}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Win Probability dial + AI scorer (Phase 7) ──────────────────
function WinProbabilityPanel({ C, opp, currentUser, linkedContext, onUpdated, showToast }) {
  const [scoring, setScoring]   = useState(false);
  const [expanded, setExpanded] = useState(true);

  const wp        = opp.win_probability;
  const updatedAt = opp.win_probability_updated_at;
  const rationale = opp.win_probability_rationale;
  const strengths = Array.isArray(opp.win_probability_strengths)    ? opp.win_probability_strengths    : [];
  const gaps      = Array.isArray(opp.win_probability_gaps)         ? opp.win_probability_gaps         : [];
  const nextActs  = Array.isArray(opp.win_probability_next_actions) ? opp.win_probability_next_actions : [];

  const scoreNow = async () => {
    setScoring(true);
    const result = await scoreOpportunityWin(opp.id, { linkedContext });
    setScoring(false);
    if (result.error) { showToast?.(`❌ Score failed: ${result.error}`); return; }
    showToast?.(`✓ Scored: ${result.win_probability}% win probability`);
    onUpdated?.({ ...opp, ...result });
  };

  const dialColor = wp == null ? '#94a3b8'
    : wp >= 80 ? '#34d399'
    : wp >= 60 ? '#22d3ee'
    : wp >= 40 ? '#fbbf24'
    : wp >= 20 ? '#fb923c'
    :            '#f87171';
  const dialLabel = wp == null ? '—'
    : wp >= 80 ? 'Near-certain'
    : wp >= 60 ? 'Favorable'
    : wp >= 40 ? 'Even'
    : wp >= 20 ? 'Uphill'
    :            'Long shot';

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Win Probability
        </div>
        {updatedAt && (
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
            scored {new Date(updatedAt).toLocaleString()}{opp.win_probability_model ? ` · ${opp.win_probability_model}` : ''}
          </div>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={() => setExpanded(!expanded)} style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif",
          fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
        }}>{expanded ? '▾ Collapse' : '▸ Expand'}</button>
        <button onClick={scoreNow} disabled={scoring} style={{
          background: scoring ? 'rgba(255,255,255,0.1)' : C.accent,
          color: scoring ? 'rgba(255,255,255,0.4)' : '#001114',
          border: 'none', borderRadius: 6, padding: '6px 12px',
          cursor: scoring ? 'default' : 'pointer',
          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
          letterSpacing: '0.08em', textTransform: 'uppercase',
        }}>{scoring ? 'Scoring…' : (wp == null ? '✨ Score with AI' : '↻ Re-score')}</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 24, alignItems: 'center' }}>
        <WinDial value={wp} color={dialColor} label={dialLabel} />
        <div>
          {rationale ? (
            <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.55 }}>
              {rationale}
            </div>
          ) : (
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
              {wp == null
                ? 'Not yet scored. Click "Score with AI" — Coach will read the opportunity, notes, CRM touchpoints, RFP, and proposal progress to estimate likelihood of winning.'
                : 'No rationale recorded for this score.'}
            </div>
          )}
        </div>
      </div>

      {expanded && (strengths.length > 0 || gaps.length > 0 || nextActs.length > 0) && (
        <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
          <WinList C={C} title="Strengths"    color="#34d399" items={strengths} />
          <WinList C={C} title="Gaps / risks" color="#f87171" items={gaps} severityKey />
          <WinList C={C} title="Next actions" color="#22d3ee" items={nextActs} actionKey />
        </div>
      )}
    </div>
  );
}

function WinDial({ value, color, label }) {
  const size = 160;
  const stroke = 14;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const pct = value == null ? 0 : Math.max(0, Math.min(100, value));
  const offset = circ * (1 - pct / 100);
  return (
    <div style={{ position: 'relative', width: size, height: size }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={stroke} />
        <circle
          cx={size/2} cy={size/2} r={r}
          fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 0.6s ease, stroke 0.3s' }}
        />
      </svg>
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        textAlign: 'center', pointerEvents: 'none',
      }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 34, fontWeight: 900, color, lineHeight: 1 }}>
          {value == null ? '—' : `${value}%`}
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginTop: 4 }}>
          {label}
        </div>
      </div>
    </div>
  );
}

function WinList({ C, title, color, items, severityKey = false, actionKey = false }) {
  return (
    <div>
      <div style={{
        fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
        letterSpacing: '0.08em', textTransform: 'uppercase', color,
        marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${color}33`,
      }}>{title} {items.length > 0 && <span style={{ color: 'rgba(255,255,255,0.4)' }}>({items.length})</span>}</div>
      {items.length === 0 ? (
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>—</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {items.map((it, i) => (
            <div key={i} style={{ padding: 8, border: `1px solid ${C.border}`, borderRadius: 4, background: 'rgba(255,255,255,0.02)' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: '#fff', flex: 1 }}>{it.label}</div>
                {severityKey && it.severity && (
                  <span style={{
                    fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    padding: '2px 6px', borderRadius: 3,
                    background: it.severity === 'high' ? '#f8717122' : it.severity === 'medium' ? '#fbbf2422' : '#94a3b822',
                    color: it.severity === 'high' ? '#f87171' : it.severity === 'medium' ? '#fbbf24' : '#94a3b8',
                  }}>{it.severity}</span>
                )}
                {actionKey && it.urgency && (
                  <span style={{
                    fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    padding: '2px 6px', borderRadius: 3,
                    background: it.urgency === 'this_week' ? '#f8717122' : it.urgency === 'next_two_weeks' ? '#fbbf2422' : '#94a3b822',
                    color: it.urgency === 'this_week' ? '#f87171' : it.urgency === 'next_two_weeks' ? '#fbbf24' : '#94a3b8',
                  }}>{it.urgency.replace(/_/g, ' ')}</span>
                )}
              </div>
              {it.detail && (
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.65)', lineHeight: 1.5 }}>
                  {it.detail}
                </div>
              )}
              {actionKey && it.owner && (
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 4 }}>
                  Owner: {it.owner}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Proposal Workspace (Phase 4) ────────────────────────────────
// Multi-contributor + multi-tier approval workflow for the proposal
// itself. Lives below the Artifacts panel inside OpportunityDetail.
//
// Three concerns, three sub-views:
//   • Members — who is involved (contributor / approver / both),
//     and what functional role they fill (regulatory / ops / IT /
//     legal / pricing / etc.). Free-text role so CSA can match
//     their actual org structure.
//   • Sections — the actual proposal content. Each section carries
//     a workflow status: draft → contributor_review →
//     contributor_approved → final_review → final_approved.
//     "rejected" is a terminal state.
//   • Sign-offs — append-only ledger of approve / reject /
//     changes_requested decisions per section per user. RLS pins
//     user_id = self so nobody can sign off on someone else's
//     behalf.
const SECTION_STATUS_META = {
  draft:                { label: 'Draft',                  color: '#94a3b8' },
  contributor_review:   { label: 'In contributor review',  color: '#fbbf24' },
  contributor_approved: { label: 'Contributors signed off',color: '#22d3ee' },
  final_review:         { label: 'In final review',        color: '#a78bfa' },
  final_approved:       { label: 'Final approved',         color: '#34d399' },
  rejected:             { label: 'Rejected',               color: '#f87171' },
};

function ProposalWorkspacePanel({ C, opp, currentUser, users, linkedContext, showToast }) {
  const [tab, setTab] = useState('sections'); // 'sections' | 'members'
  const [members, setMembers] = useState([]);
  const [sections, setSections] = useState([]);
  const [signoffs, setSignoffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddSection, setShowAddSection] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [signoffTarget, setSignoffTarget] = useState(null); // { section, capacity }
  const [draftingSectionId, setDraftingSectionId] = useState(null);
  const [showAssembly, setShowAssembly]           = useState(false);
  const [exporting, setExporting]                 = useState(false);
  const [sendingEmail, setSendingEmail]           = useState(false);
  const [showFullDraft, setShowFullDraft]         = useState(false);
  const [draftingFull, setDraftingFull]           = useState(false);
  const [showPreview, setShowPreview]             = useState(false);
  const [showAllVersions, setShowAllVersions]     = useState(false);
  const [directives, setDirectives]               = useState([]);
  const [directiveInput, setDirectiveInput]       = useState('');
  const [savingDirective, setSavingDirective]     = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    const [m, s, so, d] = await Promise.all([
      loadProposalMembers(opp.id),
      loadProposalSections(opp.id),
      loadProposalSignoffsForOpp(opp.id),
      loadOpportunityAiDirectives(opp.id),
    ]);
    setMembers(m); setSections(s); setSignoffs(so); setDirectives(d);
    setLoading(false);
  }, [opp.id]);
  useEffect(() => { refresh(); }, [refresh]);

  // Listen for cross-panel "jump to this section" events — e.g. when
  // the user clicks a weakness chip in Critique mode and wants to
  // edit the offending section. Match by id first, then exact title,
  // then fuzzy contains. Expand + scroll into view.
  const resolveSection = useCallback((titleOrId) => {
    if (!titleOrId) return null;
    let target = sections.find(s => s.id === titleOrId);
    if (target) return target;
    const norm = String(titleOrId).toLowerCase().trim();
    return sections.find(s => s.title.toLowerCase().trim() === norm)
        || sections.find(s => s.title.toLowerCase().includes(norm) || norm.includes(s.title.toLowerCase()))
        || null;
  }, [sections]);

  useEffect(() => {
    const onJump = (e) => {
      const { sectionTitle, sectionId } = e.detail || {};
      const target = resolveSection(sectionId || sectionTitle);
      if (!target) return;
      setEditingSectionId(target.id);
      setTab('sections');
      requestAnimationFrame(() => {
        const el = document.querySelector(`[data-section-id="${target.id}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    };
    // CritiquePanel asks us to resolve a title → id BEFORE it calls
    // the apply-fix endpoint. We answer via 'proposal-section-resolved'.
    const onResolve = (e) => {
      const { sectionTitle, sectionId } = e.detail || {};
      const target = resolveSection(sectionId || sectionTitle);
      window.dispatchEvent(new CustomEvent('proposal-section-resolved', {
        detail: target ? { sectionId: target.id, sectionTitle: target.title } : null,
      }));
    };
    // After an apply-fix, the section's content changed server-side
    // — refresh so the editor shows the new prose immediately.
    const onRefresh = () => { refresh(); };
    window.addEventListener('proposal-jump-to-section', onJump);
    window.addEventListener('proposal-resolve-section', onResolve);
    window.addEventListener('proposal-refresh-sections', onRefresh);
    return () => {
      window.removeEventListener('proposal-jump-to-section', onJump);
      window.removeEventListener('proposal-resolve-section', onResolve);
      window.removeEventListener('proposal-refresh-sections', onRefresh);
    };
  }, [resolveSection, refresh]);

  const onAddDirective = async () => {
    const text = directiveInput.trim();
    if (!text) return;
    setSavingDirective(true);
    const result = await addOpportunityAiDirective(opp.id, text, currentUser.id);
    setSavingDirective(false);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setDirectives(prev => [...prev, result.row]);
    setDirectiveInput('');
  };
  const onRemoveDirective = async (d) => {
    const ok = await deleteOpportunityAiDirective(d.id);
    if (ok) setDirectives(prev => prev.filter(x => x.id !== d.id));
  };

  // The currently-signed-in user's capacities for this opportunity.
  // Used to decide which workflow buttons to show on each section.
  const myMembership = useMemo(
    () => members.find(m => m.user_id === currentUser.id),
    [members, currentUser.id],
  );
  const myCapacities = myMembership?.capacities || [];
  // Admins/super_admins implicitly act as final_approver even if
  // they haven't been explicitly added as a member.
  const isAdmin = ['super_admin','admin'].includes(currentUser.role);
  const canApproveContributor = myCapacities.includes('contributor') || myCapacities.includes('approver') || isAdmin;
  const canApproveFinal       = myCapacities.includes('final_approver') || isAdmin;

  const sectionsByStatus = useMemo(() => {
    const counts = { draft:0, contributor_review:0, contributor_approved:0, final_review:0, final_approved:0, rejected:0 };
    sections.forEach(s => { counts[s.status] = (counts[s.status] || 0) + 1; });
    return counts;
  }, [sections]);

  const finalCount   = sectionsByStatus.final_approved;
  const totalCount   = sections.filter(s => s.status !== 'rejected').length;
  const progressPct  = totalCount ? Math.round((finalCount / totalCount) * 100) : 0;

  // ─── handlers ────────────────────────────────────────────────
  const onCreateSection = async (fields) => {
    const result = await createProposalSection(opp.id, fields);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setSections(prev => [...prev, result.row].sort((a,b) => (a.sequence_idx - b.sequence_idx) || a.created_at.localeCompare(b.created_at)));
    setShowAddSection(false);
    showToast?.(`✓ Added "${result.row.title}"`);
  };

  const onUpdateSection = async (id, patch) => {
    setSections(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));
    // Only stamp the version trigger when the user actually changed
    // text — workflow status bumps shouldn't pollute history.
    const isTextEdit = 'content' in patch || 'title' in patch;
    const result = await updateProposalSection(id, patch, isTextEdit
      ? { editorUserId: currentUser.id, source: 'edit' }
      : {});
    if (result.error) {
      showToast?.(`❌ Update failed: ${result.error}`);
      refresh();
    }
  };

  const onDeleteSection = async (s) => {
    if (!confirm(`Delete section "${s.title}"? This removes its content and all sign-offs.`)) return;
    const ok = await deleteProposalSection(s.id);
    if (ok) {
      setSections(prev => prev.filter(x => x.id !== s.id));
      setSignoffs(prev => prev.filter(x => x.section_id !== s.id));
      showToast?.('✓ Section removed');
    } else showToast?.('❌ Delete failed');
  };

  // Workflow transitions. The state machine is simple:
  //   draft → contributor_review        (author submits)
  //   contributor_review → contributor_approved  (contributor/approver signs off)
  //   contributor_review → rejected     (contributor rejects)
  //   contributor_approved → final_review   (someone bumps it up)
  //   final_review → final_approved     (final_approver signs off)
  //   final_review → rejected           (final rejects)
  //   any → draft                       (anyone can pull it back to edit; logs no signoff)
  const submitForContribReview = (s) => onUpdateSection(s.id, { status: 'contributor_review' });
  const pullBackToDraft        = (s) => onUpdateSection(s.id, { status: 'draft' });
  const bumpToFinalReview      = (s) => onUpdateSection(s.id, { status: 'final_review' });

  // Bulk-approve every section that's at the indicated workflow
  // stage. For final approvers, this lets you sign off on all
  // sections-in-final-review in one click instead of N. Each
  // section still produces its own signoff row in the audit log
  // (RLS still pins user_id = self), so the trail is preserved.
  const [bulkApproving, setBulkApproving] = useState(false);

  // Running token / cost total for THIS proposal. Refreshes when any
  // AI action completes (drafting, critique, gap radar, etc.) since
  // those all write to token_usage_log server-side.
  const [tokenUsage, setTokenUsage] = useState(null);
  const [showUsageDetail, setShowUsageDetail] = useState(false);
  const refreshTokenUsage = useCallback(async () => {
    setTokenUsage(await loadOpportunityTokenUsage(opp.id));
  }, [opp.id]);
  useEffect(() => { refreshTokenUsage(); }, [refreshTokenUsage]);
  // Poll every 30s so long AI runs (full-proposal draft can take
  // 30-60s) show updated cost when they complete.
  useEffect(() => {
    const t = setInterval(() => { refreshTokenUsage(); }, 30_000);
    return () => clearInterval(t);
  }, [refreshTokenUsage]);
  const bulkApproveSections = async (mode) => {
    // mode = 'contributor' approves every contributor_review section
    // mode = 'final'       approves every final_review section
    const targetStatus = mode === 'final' ? 'final_review' : 'contributor_review';
    const capacity     = mode === 'final' ? 'final_approver' : 'contributor';
    const candidates   = sections.filter(s => s.status === targetStatus);
    if (candidates.length === 0) {
      showToast?.(`No sections currently at "${targetStatus.replace('_',' ')}"`);
      return;
    }
    if (!confirm(`Approve all ${candidates.length} section${candidates.length === 1 ? '' : 's'} at "${targetStatus.replace('_',' ')}" as ${capacity}? Each gets its own audit-log entry.`)) return;
    setBulkApproving(true);
    let succeeded = 0;
    for (const section of candidates) {
      const result = await submitProposalSignoff(section.id, currentUser.id, {
        capacity_used: capacity,
        decision:      'approved',
        comments:      `Bulk-approved via workspace action`,
      });
      if (result.error) continue;
      setSignoffs(prev => [result.row, ...prev]);
      const nextStatus = capacity === 'final_approver' ? 'final_approved' : 'contributor_approved';
      await onUpdateSection(section.id, { status: nextStatus });
      succeeded++;
    }
    setBulkApproving(false);
    showToast?.(`✓ Bulk-approved ${succeeded} / ${candidates.length} section${candidates.length === 1 ? '' : 's'}`);
  };

  const recordSignoff = async (section, capacity, decision, comments) => {
    const result = await submitProposalSignoff(section.id, currentUser.id, {
      capacity_used: capacity,
      decision,
      comments,
    });
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setSignoffs(prev => [result.row, ...prev]);

    // Auto-advance the section's status based on decision + capacity.
    let nextStatus = section.status;
    if (decision === 'rejected') nextStatus = 'rejected';
    else if (decision === 'approved' && capacity === 'contributor')    nextStatus = 'contributor_approved';
    else if (decision === 'approved' && capacity === 'final_approver') nextStatus = 'final_approved';
    else if (decision === 'changes_requested')                          nextStatus = 'draft';
    if (nextStatus !== section.status) onUpdateSection(section.id, { status: nextStatus });
    showToast?.(`✓ ${decision === 'approved' ? 'Approved' : decision === 'rejected' ? 'Rejected' : 'Changes requested'}`);
    setSignoffTarget(null);
  };

  const buildProposalPdf = async ({ mode = 'download', includeReviewStatus = true } = {}) => {
    const generatedAt = new Date();

    // Downscale a blob through an offscreen canvas before encoding
    // as a data URL. Inlining raw multi-MB images was producing
    // ~700KB body HTML and causing html2canvas to silently fail
    // (blank PDF). Cap at 1600px on the long edge + JPEG quality
    // 0.85 — visually indistinguishable in a printed proposal but
    // 10–50× smaller in bytes.
    const downscaleBlobToDataUrl = (blob, { maxDim = 1600, quality = 0.85 } = {}) => new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => {
          const ratio = Math.min(1, maxDim / Math.max(img.width, img.height));
          const w = Math.round(img.width  * ratio);
          const h = Math.round(img.height * ratio);
          const canvas = document.createElement('canvas');
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d');
          // Paint a white background so PNGs with transparency
          // don't become solid black when re-encoded as JPEG.
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, w, h);
          ctx.drawImage(img, 0, 0, w, h);
          // Always JPEG output regardless of input MIME — keeps file
          // size predictable. The white background above handles
          // transparency loss.
          const dataUrl = canvas.toDataURL('image/jpeg', quality);
          resolve(dataUrl);
        };
        img.onerror = (err) => reject(new Error('image-decode-failed'));
        img.src = e.target.result;
      };
      reader.onerror = () => reject(new Error('blob-read-failed'));
      reader.readAsDataURL(blob);
    });

    // Inline all section images. Downscaled to keep total payload
    // manageable for html2canvas — see comment above.
    const rawImages = await loadProposalSectionImagesByOpp(opp.id);
    let totalImageBytes = 0;
    const images = await Promise.all(rawImages.map(async img => {
      try {
        const url = await getProposalImageSignedUrl(img.storage_path);
        if (!url) return img;
        const resp = await fetch(url);
        const blob = await resp.blob();
        const dataUrl = await downscaleBlobToDataUrl(blob, { maxDim: 1400, quality: 0.82 });
        totalImageBytes += dataUrl.length;
        return { ...img, dataUrl };
      } catch (e) {
        console.warn('[proposal-pdf] section image inline failed:', img.id, e?.message);
        return img;
      }
    }));

    // Theme: resolve + pre-fetch logo + cover image as data URLs.
    const theme = resolveTheme(opp.theme);
    const fetchAsDataUrl = async (path, opts) => {
      if (!path) return null;
      try {
        const url = await getProposalImageSignedUrl(path);
        if (!url) return null;
        const resp = await fetch(url);
        const blob = await resp.blob();
        return await downscaleBlobToDataUrl(blob, opts);
      } catch (e) {
        console.warn('[proposal-pdf] theme image inline failed:', path, e?.message);
        return null;
      }
    };
    // Logo can be smaller (it renders at ~140×50 max); cover image
    // is the dominant page-1 element so allow a larger long edge.
    const [logoDataUrl, coverDataUrl] = await Promise.all([
      fetchAsDataUrl(theme.logo_storage_path,        { maxDim: 400,  quality: 0.9  }),
      fetchAsDataUrl(theme.cover_image_storage_path, { maxDim: 1400, quality: 0.85 }),
    ]);
    console.log('[proposal-pdf] image bytes — sections=%dKB, logo=%dKB, cover=%dKB',
      Math.round(totalImageBytes / 1024),
      logoDataUrl ? Math.round(logoDataUrl.length / 1024) : 0,
      coverDataUrl ? Math.round(coverDataUrl.length / 1024) : 0,
    );

    // Resolve primary contact for the cover_first template
    // (Prepared-for block needs it).
    let resolvedContact = null;
    if (opp.primary_contact_id) {
      resolvedContact = await loadContact(opp.primary_contact_id);
    }

    const html = buildProposalExportHtml({
      opp, sections, members, signoffs, users, images,
      generatedAt, includeReviewStatus,
      theme, logoDataUrl, coverDataUrl,
      primaryContact: resolvedContact,
    });
    // Sanity checks — blank PDFs in the wild were caused by HTML
    // rendering to an empty body when a template variant threw.
    // Surface this in the console so future regressions are easy
    // to spot.
    const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/i);
    const bodyMatch  = html.match(/<body>([\s\S]*?)<\/body>/i);
    const bodyLen    = bodyMatch ? bodyMatch[1].trim().length : 0;
    console.log('[proposal-pdf] template=%s, html=%dchars, body=%dchars, sections=%d, hasLogo=%s, hasCover=%s',
      theme.template_style, html.length, bodyLen, sections.length, !!logoDataUrl, !!coverDataUrl);
    if (bodyLen < 200) {
      console.error('[proposal-pdf] body content suspiciously short — PDF likely to be blank. Body:', bodyMatch ? bodyMatch[1] : '(no body match)');
    }
    // CRITICAL POSITIONING for html2canvas off-screen capture:
    //   position: absolute + left: -9999px (in document flow, fully
    //   laid out, just outside viewport). Going to -99999px makes
    //   recent Chrome skip layout; opacity: 0 makes the capture come
    //   out transparent/white. Both produced blank PDFs. The canonical
    //   pattern that works: -9999px left + opacity: 1.
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.top = '0';
    wrapper.style.left = '-9999px';
    wrapper.style.width = '816px';
    wrapper.style.background = '#fff';
    wrapper.style.color = '#0D1B2A';
    // Padding mirrors what `body` CSS originally provided (since the
    // body { padding: ... } rule never applied — we extract body
    // innerHTML, not body itself).
    wrapper.style.padding = '32px 40px 56px';
    wrapper.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    wrapper.style.fontSize = '11pt';
    wrapper.style.lineHeight = '1.55';
    wrapper.style.opacity = '1';
    wrapper.style.pointerEvents = 'none';
    if (styleMatch) {
      const s = document.createElement('style');
      s.textContent = styleMatch[1];
      wrapper.appendChild(s);
    }
    const inner = document.createElement('div');
    inner.innerHTML = bodyMatch ? bodyMatch[1] : html;
    wrapper.appendChild(inner);
    document.body.appendChild(wrapper);

    // Force layout / give the browser a tick to compute styles and
    // load image data URLs before capture.
    void wrapper.offsetHeight;
    await new Promise(r => setTimeout(r, 50));
    console.log('[proposal-pdf] wrapper size: %dx%d (offsetWidth x offsetHeight)',
      wrapper.offsetWidth, wrapper.offsetHeight);
    if (wrapper.offsetHeight < 100) {
      console.error('[proposal-pdf] wrapper has near-zero height — PDF will be blank. Check that body CSS rules are not the only source of dimensions.');
    }

    try {
      const slug = (s) => String(s || '')
        .replace(/[^a-z0-9_\- ]/gi, '')
        .replace(/\s+/g, '_')
        .trim()
        .slice(0, 60) || 'proposal';
      const dateTag = generatedAt.toISOString().slice(0, 10).replace(/-/g, '');
      const filename = `${slug(opp.client_name || opp.name)}__proposal__${dateTag}.pdf`;

      // Direct html2canvas + jsPDF path — no html2pdf wrapper.
      // Previous diagnostic showed canvas dims 1325x0: html2canvas
      // was producing zero-height captures, which suggests
      // html2pdf's internal container/positioning was clashing with
      // our wrapper layout. Going direct gives explicit control
      // over both stages and removes a layer of magic.
      //
      // Picks scale dynamically to keep the final canvas height
      // under Chrome's ~16384px hard limit. For a 7600px wrapper at
      // scale 2 = 15200px — just under the limit, but with no
      // headroom for DPR rounding. Cap at 14000.
      const wrapperH = wrapper.offsetHeight || 1056;
      const MAX_CANVAS_PX = 14000;
      const dynamicScale = wrapperH * 2 > MAX_CANVAS_PX
        ? Math.max(1, MAX_CANVAS_PX / wrapperH)
        : 2;
      console.log('[proposal-pdf] dynamic scale = %s (wrapper height %dpx → canvas %dpx)',
        dynamicScale.toFixed(2), wrapperH, Math.round(wrapperH * dynamicScale));

      const { default: html2canvasFn } = await import('html2canvas');
      const { jsPDF: JsPDFCtor } = await import('jspdf');

      // NO windowWidth/windowHeight options — they were producing
      // zero-height canvases on Chrome with our absolute-positioned
      // wrapper. Default behavior: html2canvas captures the
      // element's exact bounding rect.
      // Per-block rendering: identify the cover region and each
      // proposal section, capture each to its own canvas, lay them
      // out as their own PDF pages. This makes the cover stand
      // alone on page 1 and prevents mid-paragraph breaks across
      // section boundaries (the previous "single canvas sliced
      // into pages" approach cut headings in half).
      const coverBlock = wrapper.querySelector('.cover-first')
        || wrapper.querySelector('.minimal-cover')
        || wrapper.querySelector('.cover-title')?.parentElement
        || wrapper.firstElementChild;
      const sectionBlocks = Array.from(wrapper.querySelectorAll('.prop-section'));

      // For the cover, capture everything from the wrapper TOP down
      // to where the first section begins. That includes the brand
      // header + cover content but excludes any prop-section.
      const headerEl = wrapper.querySelector('.header');
      const firstSection = sectionBlocks[0];
      const coverY = 0;
      const coverHeight = firstSection
        ? firstSection.getBoundingClientRect().top - wrapper.getBoundingClientRect().top
        : wrapper.offsetHeight;

      // Composite helper: takes an html2canvas output and
      // composites onto opaque white before returning. Avoids the
      // RGB=white + alpha=0 → JPEG=black trap.
      const opaquify = (src) => {
        const o = document.createElement('canvas');
        o.width = src.width; o.height = src.height;
        const c = o.getContext('2d');
        c.fillStyle = '#ffffff';
        c.fillRect(0, 0, o.width, o.height);
        c.drawImage(src, 0, 0);
        return o;
      };

      const captureRegion = async (yPx, hPx) => {
        const src = await html2canvasFn(wrapper, {
          scale:            dynamicScale,
          useCORS:          true,
          backgroundColor:  '#ffffff',
          letterRendering:  true,
          logging:          false,
          width:            wrapper.offsetWidth || 816,
          height:           Math.max(1, Math.ceil(hPx)),
          x:                0,
          y:                Math.max(0, Math.floor(yPx)),
        });
        if (!src || src.width === 0 || src.height === 0) return null;
        return opaquify(src);
      };

      console.log('[proposal-pdf] capturing %d block(s) (cover + %d section%s)',
        1 + sectionBlocks.length, sectionBlocks.length, sectionBlocks.length === 1 ? '' : 's');

      // Capture cover
      const coverCanvas = await captureRegion(coverY, coverHeight);
      if (!coverCanvas) throw new Error('Cover capture failed');
      console.log('[proposal-pdf] cover canvas: %dx%d', coverCanvas.width, coverCanvas.height);

      // Capture each section
      const sectionCanvases = [];
      for (let i = 0; i < sectionBlocks.length; i++) {
        const sec = sectionBlocks[i];
        const secY = sec.getBoundingClientRect().top - wrapper.getBoundingClientRect().top;
        const secH = sec.offsetHeight;
        const sCanvas = await captureRegion(secY, secH);
        if (sCanvas) sectionCanvases.push(sCanvas);
      }
      console.log('[proposal-pdf] captured %d section canvas(es)', sectionCanvases.length);

      // Assemble PDF — each canvas (cover, then each section) starts
      // on its own page. Within a canvas that's too tall to fit on
      // one page, slide the image up across additional pages.
      const pdf = new JsPDFCtor({ unit: 'in', format: 'letter', orientation: 'portrait' });
      const pageW = 8.5, pageH = 11;
      const marginX = 0.5, marginY = 0.5;
      const pageContentH = pageH - marginY * 2;
      const imgW = pageW - marginX * 2;

      const addCanvasAsPages = (cnvs, startNewPage) => {
        const imgHTotalIn = cnvs.height * (imgW / cnvs.width);
        if (startNewPage) pdf.addPage();

        // Short canvas: fits on one page, no slicing needed.
        if (imgHTotalIn <= pageContentH) {
          const imgData = cnvs.toDataURL('image/jpeg', 0.95);
          pdf.addImage(imgData, 'JPEG', marginX, marginY, imgW, imgHTotalIn);
          return;
        }

        // Tall canvas: slice into page-sized chunks and add each chunk
        // as its OWN image with bounded height. Previously we added the
        // full-height image on each page with a negative y-offset to
        // scroll — that produced bleed/overlap because jsPDF's clipping
        // isn't reliable across page boundaries. Slicing eliminates it.
        //
        // pxPerIn = source canvas px per PDF inch (canvas.width /
        // printed image width in inches).
        const pxPerIn      = cnvs.width / imgW;
        const pageSliceHPx = Math.floor(pageContentH * pxPerIn);
        let   ySrcPx       = 0;
        let   isFirst      = true;

        while (ySrcPx < cnvs.height) {
          const sliceHPx = Math.min(pageSliceHPx, cnvs.height - ySrcPx);
          const sliceHIn = sliceHPx / pxPerIn;

          const sliceCanvas = document.createElement('canvas');
          sliceCanvas.width  = cnvs.width;
          sliceCanvas.height = sliceHPx;
          const ctx = sliceCanvas.getContext('2d');
          // Solid white background under the slice — belt & braces for
          // any transparent regions in the source (already composited
          // once, but harmless to guarantee twice).
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
          // drawImage 9-arg form: crop the source at (0, ySrcPx, w, sliceHPx)
          // and paint it at (0, 0, w, sliceHPx) on the slice canvas.
          ctx.drawImage(cnvs,
            0, ySrcPx, cnvs.width, sliceHPx,
            0, 0,      cnvs.width, sliceHPx);
          const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.95);

          if (!isFirst) pdf.addPage();
          pdf.addImage(sliceData, 'JPEG', marginX, marginY, imgW, sliceHIn);

          ySrcPx += pageSliceHPx;
          isFirst = false;
        }
      };

      addCanvasAsPages(coverCanvas, false);  // page 1 = cover (alone)
      for (const sCanvas of sectionCanvases) {
        addCanvasAsPages(sCanvas, true);     // each section starts a new page
      }

      // Footer pass — page numbers + optional confidentiality on
      // every page. Drawn natively by jsPDF so the text is selectable
      // and scales sharply at any zoom.
      const totalPages = pdf.internal.getNumberOfPages();
      const showPageNumbers = theme.show_page_numbers !== false;
      const footerText = theme.footer_text || null;
      if (showPageNumbers || footerText) {
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          if (footerText) {
            pdf.setFontSize(7);
            pdf.setTextColor(140, 150, 160);
            const lines = pdf.splitTextToSize(footerText, pageW - 1.5);
            const linesH = lines.length * 0.10;
            pdf.text(lines, pageW / 2, pageH - 0.35 - linesH, { align: 'center' });
          }
          if (showPageNumbers) {
            pdf.setFontSize(8);
            pdf.setTextColor(120, 130, 140);
            pdf.text(`Page ${i} of ${totalPages}`, pageW / 2, pageH - 0.25, { align: 'center' });
          }
        }
      }

      if (mode === 'blob') {
        const blob = pdf.output('blob');
        console.log('[proposal-pdf] blob size: %d bytes', blob?.size || 0);
        if (!blob || blob.size === 0) throw new Error('PDF rendering produced an empty file');
        return { blob, filename };
      }
      pdf.save(filename);
      return { filename };
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  const exportProposalPdf = async (includeReviewStatus) => {
    if (exporting) return;
    setExporting(true);
    try {
      const result = await buildProposalPdf({ mode: 'download', includeReviewStatus });
      showToast?.(`✓ Proposal exported as ${result.filename}`);
    } catch (err) {
      showToast?.(`❌ Export failed: ${err.message || err}`);
    } finally {
      setExporting(false);
    }
  };

  const emailProposal = async ({ to, subject, body }) => {
    if (sendingEmail) return;
    setSendingEmail(true);
    try {
      // Render the clean (no internal review tags) PDF as a blob,
      // base64-encode it, and hand it to the Edge Function as an
      // attachment. Same email channel as deal-room PDF sharing.
      const pdf = await buildProposalPdf({ mode: 'blob', includeReviewStatus: false });
      const arrayBuffer = await pdf.blob.arrayBuffer();
      // Encode in chunks to avoid stack overflow on large PDFs
      const bytes = new Uint8Array(arrayBuffer);
      let binary = '';
      const chunkSize = 32768;
      for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
      }
      const base64 = btoa(binary);

      const html = `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #0D1B2A; line-height: 1.55; font-size: 14px;">
        ${body.split('\n').filter(Boolean).map(line => `<p style="margin: 0 0 12px;">${line.replace(/</g, '&lt;').replace(/>/g, '&gt;')}</p>`).join('')}
        <div style="margin-top: 28px; padding-top: 14px; border-top: 1px solid #E5EAF0; font-size: 11px; color: #899AAB;">
          Sent via 40Geo Nuggets · Proposal Coach
        </div>
      </div>`;

      const { error } = await dbSendEmail({
        to: Array.isArray(to) && to.length === 1 ? to[0] : to,
        subject,
        html,
        orgId: currentUser.orgId,
        attachments: [{
          filename:    pdf.filename,
          content:     base64,
          contentType: 'application/pdf',
        }],
      });
      if (error) throw new Error(error.message || 'send failed');
      showToast?.(`✓ Proposal sent to ${Array.isArray(to) ? to.join(', ') : to}`);
      setShowAssembly(false);
    } catch (err) {
      showToast?.(`❌ Email failed: ${err.message || err}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const runFullProposalDraft = async (mode) => {
    setDraftingFull(true);
    const result = await draftFullProposal(opp.id, { mode, linkedContext });
    setDraftingFull(false);
    if (result.error) { showToast?.(`❌ Full draft failed: ${result.error}`); return; }
    showToast?.(`✓ Drafted ${result.drafted_count} sections — review and edit any of them`);
    setShowFullDraft(false);
    refresh();
  };

  const onDraftWithAi = async (section, instructions) => {
    setDraftingSectionId(section.id);
    const result = await draftProposalSection(section.id, {
      instructions,
      linkedContext,
    });
    setDraftingSectionId(null);
    if (result.error) {
      showToast?.(`❌ AI draft failed: ${result.error}`);
      return;
    }
    setSections(prev => prev.map(s => s.id === section.id ? { ...s, ai_generated_content: result.draft } : s));
    showToast?.('✓ AI draft ready — review it below');
  };

  const onAddMember = async (userId, fields) => {
    const result = await upsertProposalMember(opp.id, userId, fields);
    if (result.error) { showToast?.(`❌ ${result.error}`); return; }
    setMembers(prev => {
      const idx = prev.findIndex(m => m.user_id === userId);
      if (idx >= 0) { const next = [...prev]; next[idx] = result.row; return next; }
      return [...prev, result.row];
    });
    setShowAddMember(false);
    showToast?.('✓ Member added');
  };
  const onRemoveMember = async (m) => {
    const u = users.find(x => x.id === m.user_id);
    if (!confirm(`Remove ${u?.name || u?.email || 'this user'} from the proposal team?`)) return;
    const ok = await removeProposalMember(m.id);
    if (ok) setMembers(prev => prev.filter(x => x.id !== m.id));
    else showToast?.('❌ Remove failed');
  };

  // ─── render ──────────────────────────────────────────────────
  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 4 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          Proposal Workspace
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
          {finalCount}/{totalCount} sections final-approved · {progressPct}%
        </div>
        {tokenUsage && tokenUsage.count > 0 && (
          <button
            onClick={() => setShowUsageDetail(true)}
            title={`${tokenUsage.count} AI call${tokenUsage.count === 1 ? '' : 's'} · ${tokenUsage.total_tokens.toLocaleString()} tokens — click for breakdown`}
            style={{
              background: 'rgba(52,211,153,0.1)', color: '#34d399',
              border: '1px solid rgba(52,211,153,0.3)', borderRadius: 3,
              padding: '3px 8px', cursor: 'pointer',
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: '0.04em',
            }}
          >💰 ${tokenUsage.total_cost.toFixed(2)}</button>
        )}
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 6 }}>
          {['sections','members'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '5px 10px', borderRadius: 6, cursor: 'pointer',
              border: `1px solid ${tab === t ? C.accent : C.border}`,
              background: tab === t ? `${C.accent}22` : 'transparent',
              color: tab === t ? C.accent : 'rgba(255,255,255,0.55)',
            }}>{t === 'sections' ? `Sections (${sections.length})` : `Team (${members.length})`}</button>
          ))}
          <button
            onClick={() => setShowFullDraft(true)}
            disabled={sections.length === 0 || draftingFull}
            title="Generate a full coherent first-draft of every section in one pass"
            style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '5px 10px', borderRadius: 6,
              cursor: (sections.length === 0 || draftingFull) ? 'default' : 'pointer',
              border: `1px solid ${draftingFull ? 'rgba(255,255,255,0.12)' : '#a78bfa'}`,
              background: draftingFull ? 'rgba(255,255,255,0.05)' : `linear-gradient(90deg, #a78bfa, #22d3ee)`,
              color: draftingFull ? 'rgba(255,255,255,0.4)' : '#001114',
            }}
          >{draftingFull ? 'Drafting…' : '✨ Generate Full Proposal'}</button>
          <button
            onClick={() => setShowAllVersions(true)}
            disabled={sections.length === 0}
            title="Browse every saved revision across all sections — restore any version"
            style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '5px 10px', borderRadius: 6,
              cursor: sections.length === 0 ? 'default' : 'pointer',
              border: `1px solid ${sections.length === 0 ? 'rgba(255,255,255,0.12)' : '#fbbf24'}`,
              background: 'transparent',
              color: sections.length === 0 ? 'rgba(255,255,255,0.3)' : '#fbbf24',
            }}
          >⟲ History</button>
          {sectionsByStatus.contributor_review > 0 && canApproveContributor && (
            <button
              onClick={() => bulkApproveSections('contributor')}
              disabled={bulkApproving}
              title={`Sign off as contributor on all ${sectionsByStatus.contributor_review} section${sectionsByStatus.contributor_review === 1 ? '' : 's'} in contributor review`}
              style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '5px 10px', borderRadius: 6,
                cursor: bulkApproving ? 'default' : 'pointer',
                border: '1px solid #34d39966',
                background: bulkApproving ? 'rgba(255,255,255,0.05)' : 'transparent',
                color: bulkApproving ? 'rgba(255,255,255,0.4)' : '#34d399',
              }}
            >{bulkApproving ? '…' : `✓ Contrib-approve all (${sectionsByStatus.contributor_review})`}</button>
          )}
          {sectionsByStatus.final_review > 0 && canApproveFinal && (
            <button
              onClick={() => bulkApproveSections('final')}
              disabled={bulkApproving}
              title={`Sign off as final approver on all ${sectionsByStatus.final_review} section${sectionsByStatus.final_review === 1 ? '' : 's'} in final review`}
              style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '5px 10px', borderRadius: 6,
                cursor: bulkApproving ? 'default' : 'pointer',
                border: 'none',
                background: bulkApproving ? 'rgba(255,255,255,0.05)' : '#34d399',
                color: bulkApproving ? 'rgba(255,255,255,0.4)' : '#001114',
              }}
            >{bulkApproving ? '…' : `✓ Final-approve all (${sectionsByStatus.final_review})`}</button>
          )}
          <button
            onClick={() => setShowPreview(true)}
            disabled={sections.filter(s => s.status !== 'rejected').length === 0}
            title="Preview the assembled proposal in-app — review before exporting"
            style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '5px 10px', borderRadius: 6,
              cursor: sections.length === 0 ? 'default' : 'pointer',
              border: `1px solid ${sections.length === 0 ? 'rgba(255,255,255,0.12)' : '#22d3ee'}`,
              background: 'transparent',
              color: sections.length === 0 ? 'rgba(255,255,255,0.3)' : '#22d3ee',
            }}
          >👁 Preview</button>
          <button
            onClick={() => setShowAssembly(true)}
            disabled={sections.filter(s => s.status !== 'rejected').length === 0}
            style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: '0.06em', textTransform: 'uppercase',
              padding: '5px 10px', borderRadius: 6,
              cursor: sections.length === 0 ? 'default' : 'pointer',
              border: `1px solid ${sections.length === 0 ? 'rgba(255,255,255,0.12)' : C.accent}`,
              background: sections.length === 0 ? 'transparent' : C.accent,
              color: sections.length === 0 ? 'rgba(255,255,255,0.3)' : '#001114',
            }}
          >Assemble / Export</button>
        </div>
      </div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginBottom: 14, lineHeight: 1.5 }}>
        Section-by-section drafting + sign-offs. Contributors edit content and approve their own sections; final approvers (budget / pricing / legal) hold the last gate.
      </div>

      {/* Progress bar */}
      <div style={{ height: 4, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden', marginBottom: 14 }}>
        <div style={{ width: `${progressPct}%`, height: '100%', background: progressPct === 100 ? '#34d399' : C.accent, transition: 'width 0.3s' }} />
      </div>

      <DirectivesPanel
        C={C}
        directives={directives}
        input={directiveInput}
        setInput={setDirectiveInput}
        saving={savingDirective}
        drafting={draftingFull}
        sectionsCount={sections.filter(s => s.status !== 'rejected').length}
        onAdd={onAddDirective}
        onRemove={onRemoveDirective}
        onRegenerate={(mode) => runFullProposalDraft(mode)}
      />

      {loading ? (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>Loading…</div>
      ) : tab === 'sections' ? (
        <SectionsList
          C={C}
          sections={sections}
          signoffs={signoffs}
          users={users}
          currentUser={currentUser}
          canApproveContributor={canApproveContributor}
          canApproveFinal={canApproveFinal}
          editingSectionId={editingSectionId}
          draftingSectionId={draftingSectionId}
          onEdit={(id) => setEditingSectionId(id)}
          onCloseEdit={() => setEditingSectionId(null)}
          onPatch={onUpdateSection}
          onDelete={onDeleteSection}
          onSubmitForReview={submitForContribReview}
          onPullBack={pullBackToDraft}
          onBumpToFinal={bumpToFinalReview}
          onRequestSignoff={(section, capacity) => setSignoffTarget({ section, capacity })}
          onAddSection={() => setShowAddSection(true)}
          onDraftWithAi={onDraftWithAi}
        />
      ) : (
        <MembersList
          C={C}
          members={members}
          users={users}
          currentUser={currentUser}
          onAddMember={() => setShowAddMember(true)}
          onRemoveMember={onRemoveMember}
          onPatchMember={async (memberRow, patch) => {
            const result = await upsertProposalMember(opp.id, memberRow.user_id, { ...memberRow, ...patch });
            if (result.error) { showToast?.(`❌ ${result.error}`); return; }
            setMembers(prev => prev.map(m => m.id === memberRow.id ? result.row : m));
          }}
        />
      )}

      {showAddSection && (
        <AddSectionModal C={C} users={users} onCancel={() => setShowAddSection(false)} onSubmit={onCreateSection} />
      )}
      {showAddMember && (
        <AddMemberModal C={C} users={users} existingMemberUserIds={members.map(m => m.user_id)}
          onCancel={() => setShowAddMember(false)} onSubmit={onAddMember} />
      )}
      {signoffTarget && (
        <SignoffModal
          C={C}
          section={signoffTarget.section}
          capacity={signoffTarget.capacity}
          onCancel={() => setSignoffTarget(null)}
          onSubmit={(decision, comments) => recordSignoff(signoffTarget.section, signoffTarget.capacity, decision, comments)}
        />
      )}
      {showUsageDetail && tokenUsage && (
        <ProposalCostBreakdownModal
          C={C}
          opp={opp}
          usage={tokenUsage}
          onClose={() => setShowUsageDetail(false)}
          onRefresh={refreshTokenUsage}
        />
      )}
      {showAllVersions && (
        <AllVersionsModal
          C={C}
          opp={opp}
          sections={sections}
          users={users}
          currentUser={currentUser}
          onClose={() => setShowAllVersions(false)}
          onRestored={() => { refresh(); showToast?.('✓ Restored'); }}
          showToast={showToast}
        />
      )}
      {showPreview && (
        <ProposalPreviewModal
          C={C}
          opp={opp}
          sections={sections}
          signoffs={signoffs}
          members={members}
          users={users}
          onClose={() => setShowPreview(false)}
          onJumpToEditor={(sectionId) => {
            setShowPreview(false);
            setEditingSectionId(sectionId);
          }}
          onExport={() => { setShowPreview(false); setShowAssembly(true); }}
        />
      )}
      {showFullDraft && (
        <FullProposalDraftModal
          C={C}
          sections={sections}
          drafting={draftingFull}
          onCancel={() => setShowFullDraft(false)}
          onSubmit={runFullProposalDraft}
        />
      )}
      {showAssembly && (
        <AssembleProposalModal
          C={C}
          opp={opp}
          sections={sections}
          signoffs={signoffs}
          members={members}
          users={users}
          currentUser={currentUser}
          exporting={exporting}
          sendingEmail={sendingEmail}
          onCancel={() => setShowAssembly(false)}
          onExport={exportProposalPdf}
          onEmail={emailProposal}
        />
      )}
    </div>
  );
}

function SectionsList({ C, sections, signoffs, users, currentUser, canApproveContributor, canApproveFinal, editingSectionId, draftingSectionId, onEdit, onCloseEdit, onPatch, onDelete, onSubmitForReview, onPullBack, onBumpToFinal, onRequestSignoff, onAddSection, onDraftWithAi }) {
  if (!sections.length) {
    return (
      <div style={{ padding: 24, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
        No sections yet.
        <div style={{ marginTop: 10 }}>
          <button onClick={onAddSection} style={{
            background: C.accent, color: '#001114', border: 'none', borderRadius: 6,
            padding: '6px 14px', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
            fontSize: 10, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase',
          }}>+ Add section</button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={onAddSection} style={{
          background: 'transparent', color: C.accent, border: `1px solid ${C.accent}66`, borderRadius: 6,
          padding: '5px 11px', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
          fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
        }}>+ Section</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {sections.map(s => (
          <SectionRow
            key={s.id}
            C={C}
            section={s}
            signoffs={signoffs.filter(so => so.section_id === s.id)}
            users={users}
            currentUser={currentUser}
            canApproveContributor={canApproveContributor}
            canApproveFinal={canApproveFinal}
            expanded={editingSectionId === s.id}
            drafting={draftingSectionId === s.id}
            onEdit={() => onEdit(s.id)}
            onCloseEdit={onCloseEdit}
            onPatch={(patch) => onPatch(s.id, patch)}
            onDelete={() => onDelete(s)}
            onSubmitForReview={() => onSubmitForReview(s)}
            onPullBack={() => onPullBack(s)}
            onBumpToFinal={() => onBumpToFinal(s)}
            onRequestSignoff={(capacity) => onRequestSignoff(s, capacity)}
            onDraftWithAi={(instructions) => onDraftWithAi(s, instructions)}
          />
        ))}
      </div>
    </div>
  );
}

function SectionRow({ C, section, signoffs, users, currentUser, canApproveContributor, canApproveFinal, expanded, drafting, onEdit, onCloseEdit, onPatch, onDelete, onSubmitForReview, onPullBack, onBumpToFinal, onRequestSignoff, onDraftWithAi }) {
  const [draftContent, setDraftContent] = useState(section.content || '');
  const [draftTitle, setDraftTitle]     = useState(section.title || '');
  const [savingDirty, setSavingDirty]   = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [showAiPrompt, setShowAiPrompt]     = useState(false);
  const [showHistory, setShowHistory]       = useState(false);
  const [versions, setVersions]             = useState([]);
  const [loadingVersions, setLoadingVersions] = useState(false);
  const [previewVersion, setPreviewVersion]   = useState(null);
  const [images, setImages]                   = useState([]);
  const [uploadingImage, setUploadingImage]   = useState(false);
  const imageFileInputRef = useRef(null);

  // Lazy-load images when the section is expanded so collapsed
  // sections don't fan out into N storage calls.
  useEffect(() => {
    if (!expanded) return;
    (async () => {
      const imgs = await loadProposalSectionImages(section.id);
      const withUrls = await Promise.all(imgs.map(async img => ({
        ...img,
        signedUrl: await getProposalImageSignedUrl(img.storage_path),
      })));
      setImages(withUrls);
    })();
  }, [expanded, section.id]);

  const onImageFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploadingImage(true);
    const result = await uploadProposalSectionImage(currentUser.orgId, section.opportunity_id, section.id, file, currentUser.id);
    setUploadingImage(false);
    if (result.error) return;
    const signedUrl = await getProposalImageSignedUrl(result.row.storage_path);
    setImages(prev => [...prev, { ...result.row, signedUrl }]);
  };
  const onImageCaptionEdit = async (img, caption) => {
    setImages(prev => prev.map(x => x.id === img.id ? { ...x, caption } : x));
    await updateProposalSectionImage(img.id, { caption });
  };
  const onImageDelete = async (img) => {
    if (!confirm(`Remove "${img.filename || 'image'}"?`)) return;
    const ok = await deleteProposalSectionImage(img);
    if (ok) setImages(prev => prev.filter(x => x.id !== img.id));
  };

  // Pricing tables — structured tables stored on the section row.
  // Each table = { id, title, columns: [labels], rows: [[cells]] }
  // Renders below the prose content in the PDF + preview.
  const [pricingTables, setPricingTables] = useState(() => Array.isArray(section.pricing_tables) ? section.pricing_tables : []);
  useEffect(() => {
    setPricingTables(Array.isArray(section.pricing_tables) ? section.pricing_tables : []);
  }, [section.id, section.pricing_tables]);

  const savePricingTables = async (next) => {
    setPricingTables(next);
    await onPatch({ pricing_tables: next });
  };
  const onAddPricingTable = () => {
    const tbl = {
      id: (crypto?.randomUUID?.() || Math.random().toString(36).slice(2)),
      title: 'Pricing Schedule',
      columns: ['Service', 'Provider', 'Rate'],
      rows: [['', '', '']],
    };
    savePricingTables([...pricingTables, tbl]);
  };
  const onUpdatePricingTable = (id, patch) => {
    savePricingTables(pricingTables.map(t => t.id === id ? { ...t, ...patch } : t));
  };
  const onRemovePricingTable = (id) => {
    if (!confirm('Remove this pricing table?')) return;
    savePricingTables(pricingTables.filter(t => t.id !== id));
  };
  const onAddTableRow = (id) => {
    onUpdatePricingTable(id, {
      rows: [...(pricingTables.find(t => t.id === id)?.rows || []), pricingTables.find(t => t.id === id).columns.map(() => '')],
    });
  };
  const onUpdateTableCell = (id, rowIdx, colIdx, value) => {
    const tbl = pricingTables.find(t => t.id === id);
    if (!tbl) return;
    const newRows = tbl.rows.map((r, i) => i === rowIdx ? r.map((c, j) => j === colIdx ? value : c) : r);
    onUpdatePricingTable(id, { rows: newRows });
  };
  const onRemoveTableRow = (id, rowIdx) => {
    const tbl = pricingTables.find(t => t.id === id);
    if (!tbl) return;
    onUpdatePricingTable(id, { rows: tbl.rows.filter((_, i) => i !== rowIdx) });
  };
  const onAddTableColumn = (id) => {
    const tbl = pricingTables.find(t => t.id === id);
    if (!tbl) return;
    const newCol = `Column ${tbl.columns.length + 1}`;
    onUpdatePricingTable(id, {
      columns: [...tbl.columns, newCol],
      rows: tbl.rows.map(r => [...r, '']),
    });
  };
  const onUpdateTableColumn = (id, colIdx, value) => {
    const tbl = pricingTables.find(t => t.id === id);
    if (!tbl) return;
    onUpdatePricingTable(id, {
      columns: tbl.columns.map((c, i) => i === colIdx ? value : c),
    });
  };
  const onRemoveTableColumn = (id, colIdx) => {
    const tbl = pricingTables.find(t => t.id === id);
    if (!tbl || tbl.columns.length <= 1) return;
    onUpdatePricingTable(id, {
      columns: tbl.columns.filter((_, i) => i !== colIdx),
      rows: tbl.rows.map(r => r.filter((_, i) => i !== colIdx)),
    });
  };

  const openHistory = async () => {
    setShowHistory(true);
    setLoadingVersions(true);
    const v = await loadProposalSectionVersions(section.id);
    setVersions(v);
    setLoadingVersions(false);
  };
  const restoreVersion = async (v) => {
    if (!confirm(`Restore the version from ${new Date(v.edited_at).toLocaleString()}? Your current content will be saved as a new version so nothing is lost.`)) return;
    const result = await restoreProposalSectionVersion(section.id, v, currentUser.id);
    if (result.error) { return; }
    setDraftContent(v.content);
    setDraftTitle(v.title);
    setShowHistory(false);
    setPreviewVersion(null);
    // Patch the local section row so workflow status etc stay coherent
    onPatch({ content: v.content, title: v.title });
  };

  useEffect(() => { setDraftContent(section.content || ''); setDraftTitle(section.title || ''); }, [section.id, section.content, section.title]);

  const dirty = draftContent !== (section.content || '') || draftTitle !== (section.title || '');
  const author = users.find(u => u.id === section.primary_author_user_id);
  const meta = SECTION_STATUS_META[section.status] || SECTION_STATUS_META.draft;
  const signoffCount = signoffs.length;
  const contribApprovals = signoffs.filter(s => s.capacity_used === 'contributor' && s.decision === 'approved').length;
  const finalApprovals   = signoffs.filter(s => s.capacity_used === 'final_approver' && s.decision === 'approved').length;

  const saveDraft = async () => {
    setSavingDirty(true);
    await onPatch({ title: draftTitle.trim() || 'Untitled', content: draftContent });
    setSavingDirty(false);
  };

  // What workflow actions does this user have available right now?
  const canSubmit       = section.status === 'draft' && (canApproveContributor || currentUser.id === section.primary_author_user_id);
  const canContribSign  = section.status === 'contributor_review' && canApproveContributor;
  const canBumpFinal    = section.status === 'contributor_approved' && (canApproveContributor || canApproveFinal);
  const canFinalSign    = section.status === 'final_review' && canApproveFinal;
  const canReopen       = (section.status !== 'draft' && section.status !== 'final_approved') || (section.status === 'final_approved' && canApproveFinal);

  return (
    <div data-section-id={section.id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: expanded ? 14 : 10, background: 'rgba(255,255,255,0.02)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={expanded ? onCloseEdit : onEdit} style={{
          background: 'transparent', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.5)',
          padding: 0, fontSize: 14, width: 16, textAlign: 'center',
        }}>{expanded ? '▾' : '▸'}</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {section.title}
          </div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em', marginTop: 2 }}>
            {(PROPOSAL_SECTION_TYPES.find(t => t.id === section.section_type)?.label) || section.section_type}
            {author && <> · {author.name || author.email}</>}
            {signoffCount > 0 && <> · {contribApprovals} contrib + {finalApprovals} final sign-offs</>}
          </div>
        </div>
        <span style={{
          fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700,
          letterSpacing: '0.07em', textTransform: 'uppercase',
          padding: '3px 8px', borderRadius: 4,
          background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55`,
        }}>{meta.label}</span>
      </div>

      {expanded && (
        <div style={{ marginTop: 12 }}>
          <Field label="Title">
            <input value={draftTitle} onChange={e => setDraftTitle(e.target.value)} style={modalInput(C)} />
          </Field>
          <div style={{ height: 8 }} />
          <Field label="Content">
            <textarea rows={10} value={draftContent} onChange={e => setDraftContent(e.target.value)}
              style={{ ...modalInput(C), resize: 'vertical', fontFamily: "'Inter',sans-serif", fontSize: 12, lineHeight: 1.55 }} />
          </Field>

          {/* Images strip — uploadable thumbnails with captions; embedded in PDF assembly */}
          <div style={{ marginTop: 12 }}>
            <input ref={imageFileInputRef} type="file" accept="image/*" onChange={onImageFileChosen} style={{ display: 'none' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>
                Images {images.length > 0 && <span style={{ color: 'rgba(255,255,255,0.4)' }}>({images.length})</span>}
              </div>
              <button onClick={() => imageFileInputRef.current?.click()} disabled={uploadingImage} style={{
                background: 'transparent', color: C.accent, border: `1px solid ${C.accent}66`,
                borderRadius: 4, padding: '3px 9px', cursor: uploadingImage ? 'default' : 'pointer',
                fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>{uploadingImage ? 'Uploading…' : '+ Add image'}</button>
            </div>
            {images.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                {images.map(img => (
                  <div key={img.id} style={{ border: `1px solid ${C.border}`, borderRadius: 6, overflow: 'hidden', background: 'rgba(0,0,0,0.2)' }}>
                    {img.signedUrl ? (
                      <img src={img.signedUrl} alt={img.alt_text || img.filename || ''} style={{ width: '100%', height: 100, objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ width: '100%', height: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.3)', fontSize: 10, fontFamily: "'Montserrat',sans-serif" }}>preview unavailable</div>
                    )}
                    <div style={{ padding: 6 }}>
                      <input
                        value={img.caption || ''}
                        onChange={e => onImageCaptionEdit(img, e.target.value)}
                        placeholder="Caption (optional)"
                        style={{
                          width: '100%', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.85)',
                          fontFamily: "'Inter',sans-serif", fontSize: 10, padding: 0, outline: 'none',
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 4 }}>
                        <div style={{ flex: 1, fontFamily: "'Montserrat',sans-serif", fontSize: 8, color: 'rgba(255,255,255,0.4)' }}>
                          {img.width && img.height ? `${img.width}×${img.height}` : ''}
                        </div>
                        <button onClick={() => onImageDelete(img)} style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'rgba(248,113,113,0.6)', fontFamily: "'Montserrat',sans-serif",
                          fontSize: 9, fontWeight: 700,
                        }}>✕</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pricing tables — structured tables that render below
              section prose in the PDF. Header bar + columns + rows. */}
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)' }}>
                Pricing Tables {pricingTables.length > 0 && <span style={{ color: 'rgba(255,255,255,0.4)' }}>({pricingTables.length})</span>}
              </div>
              <button onClick={onAddPricingTable} style={{
                background: 'transparent', color: '#22d3ee', border: '1px solid rgba(34,211,238,0.4)',
                borderRadius: 4, padding: '3px 9px', cursor: 'pointer',
                fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
              }}>+ Add table</button>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', flex: 1 }}>
                Renders below this section's prose in the PDF. Multiple tables for grouped offerings (Core / Optional / Add-ons).
              </div>
            </div>
            {pricingTables.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {pricingTables.map(tbl => (
                  <div key={tbl.id} style={{
                    border: '1px solid rgba(34,211,238,0.3)', borderRadius: 6,
                    background: 'rgba(34,211,238,0.04)', padding: 10,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <input
                        value={tbl.title || ''}
                        onChange={e => onUpdatePricingTable(tbl.id, { title: e.target.value })}
                        placeholder="Table title (e.g. Core Services) — appears as colored header bar"
                        style={{ ...modalInput(C), flex: 1, background: 'rgba(0,0,0,0.3)', fontWeight: 700 }}
                      />
                      <button onClick={() => onRemovePricingTable(tbl.id)} style={{
                        background: 'transparent', color: 'rgba(248,113,113,0.7)',
                        border: '1px solid rgba(248,113,113,0.4)', borderRadius: 4, padding: '5px 10px',
                        cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
                        fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                      }}>Delete table</button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr>
                          {tbl.columns.map((col, ci) => (
                            <th key={ci} style={{ padding: 4, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.04)' }}>
                              <input
                                value={col}
                                onChange={e => onUpdateTableColumn(tbl.id, ci, e.target.value)}
                                style={{
                                  width: '100%', background: 'transparent', border: 'none',
                                  color: '#fff', fontFamily: "'Montserrat',sans-serif",
                                  fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
                                  textTransform: 'uppercase', outline: 'none', padding: 2,
                                }}
                              />
                            </th>
                          ))}
                          <th style={{ width: 32, padding: 4, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.04)' }}>
                            <button onClick={() => onAddTableColumn(tbl.id)} title="Add column" style={{
                              background: 'transparent', border: 'none', cursor: 'pointer',
                              color: 'rgba(255,255,255,0.5)', fontSize: 14, padding: 0,
                            }}>+</button>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {tbl.rows.map((row, ri) => (
                          <tr key={ri}>
                            {row.map((cell, ci) => (
                              <td key={ci} style={{ padding: 0, border: `1px solid ${C.border}`, verticalAlign: 'top' }}>
                                <textarea
                                  value={cell}
                                  onChange={e => onUpdateTableCell(tbl.id, ri, ci, e.target.value)}
                                  rows={1}
                                  style={{
                                    width: '100%', background: 'transparent', border: 'none',
                                    color: 'rgba(255,255,255,0.9)', fontFamily: "'Inter',sans-serif",
                                    fontSize: 11, padding: 6, outline: 'none', resize: 'vertical',
                                    minHeight: 28, lineHeight: 1.4,
                                  }}
                                />
                              </td>
                            ))}
                            <td style={{ width: 32, padding: 0, border: `1px solid ${C.border}`, textAlign: 'center' }}>
                              {tbl.columns.length > 1 && (
                                <button onClick={() => onRemoveTableColumn(tbl.id, row.length - 1)} title="Remove last column" style={{
                                  display: 'none', // only show on header
                                }}>×</button>
                              )}
                              <button onClick={() => onRemoveTableRow(tbl.id, ri)} title="Remove row" style={{
                                background: 'transparent', border: 'none', cursor: 'pointer',
                                color: 'rgba(248,113,113,0.5)', fontSize: 12, padding: 4,
                              }}>×</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button onClick={() => onAddTableRow(tbl.id)} style={{
                        background: 'transparent', color: '#22d3ee', border: '1px solid rgba(34,211,238,0.4)',
                        borderRadius: 4, padding: '4px 10px', cursor: 'pointer',
                        fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                      }}>+ Add row</button>
                      {tbl.columns.length > 1 && (
                        <button onClick={() => {
                          if (confirm(`Remove the last column "${tbl.columns[tbl.columns.length-1]}"?`)) {
                            onRemoveTableColumn(tbl.id, tbl.columns.length - 1);
                          }
                        }} style={{
                          background: 'transparent', color: 'rgba(255,255,255,0.5)', border: `1px solid ${C.border}`,
                          borderRadius: 4, padding: '4px 10px', cursor: 'pointer',
                          fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
                        }}>− Remove last column</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {section.ai_generated_content && draftContent !== section.ai_generated_content && (
            <div style={{ marginTop: 8, padding: 10, border: `1px dashed ${C.accent}55`, borderRadius: 6, background: `${C.accent}08` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.accent }}>AI Draft Available</span>
                <button onClick={() => setDraftContent(section.ai_generated_content)} style={{
                  marginLeft: 'auto', background: 'transparent', color: C.accent, border: `1px solid ${C.accent}66`,
                  borderRadius: 4, padding: '3px 8px', cursor: 'pointer',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                }}>Load AI draft</button>
              </div>
              <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.6)', maxHeight: 80, overflow: 'auto' }}>
                {section.ai_generated_content.slice(0, 400)}{section.ai_generated_content.length > 400 ? '…' : ''}
              </div>
            </div>
          )}

          {/* Sign-offs history */}
          {signoffs.length > 0 && (
            <div style={{ marginTop: 12, padding: 10, border: `1px solid ${C.border}`, borderRadius: 6 }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 8 }}>Decision history</div>
              {signoffs.map(so => {
                const u = users.find(x => x.id === so.user_id);
                const color = so.decision === 'approved' ? '#34d399' : so.decision === 'rejected' ? '#f87171' : '#fbbf24';
                return (
                  <div key={so.id} style={{ fontFamily: "'Inter',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.7)', marginBottom: 6, display: 'flex', gap: 10 }}>
                    <span style={{ color, fontWeight: 700, width: 80 }}>{so.decision.replace('_',' ')}</span>
                    <span style={{ flex: 1 }}>
                      <span style={{ color: '#fff' }}>{u?.name || u?.email || 'unknown'}</span>
                      <span style={{ color: 'rgba(255,255,255,0.4)' }}> · as {so.capacity_used === 'final_approver' ? 'final approver' : 'contributor'}</span>
                      {so.comments && <div style={{ color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>"{so.comments}"</div>}
                    </span>
                    <span style={{ color: 'rgba(255,255,255,0.35)' }}>{new Date(so.decided_at).toLocaleString()}</span>
                  </div>
                );
              })}
            </div>
          )}

          {/* AI draft trigger */}
          {!showAiPrompt && (
            <div style={{ marginTop: 12 }}>
              <button onClick={() => setShowAiPrompt(true)} disabled={drafting} style={{
                background: drafting ? 'rgba(255,255,255,0.1)' : `${C.accent}22`,
                color: drafting ? 'rgba(255,255,255,0.4)' : C.accent,
                border: `1px solid ${drafting ? 'rgba(255,255,255,0.15)' : C.accent + '66'}`,
                borderRadius: 6, padding: '6px 12px', cursor: drafting ? 'default' : 'pointer',
                fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: '0.07em', textTransform: 'uppercase',
              }}>{drafting ? '✨ Drafting…' : `✨ ${section.content ? 'Refine with AI' : 'Draft with AI'}`}</button>
            </div>
          )}
          {showAiPrompt && (
            <div style={{ marginTop: 12, padding: 12, border: `1px solid ${C.accent}55`, borderRadius: 6, background: `${C.accent}08` }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: C.accent, marginBottom: 8 }}>
                Draft this section with AI
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginBottom: 8, lineHeight: 1.5 }}>
                The Coach will read the opportunity record, notes, CRM activity, RFP docs, and pinned library precedents — then produce a first draft. Result lands in the "AI Draft" panel; your content isn't overwritten.
              </div>
              <textarea
                value={aiInstructions}
                onChange={e => setAiInstructions(e.target.value)}
                rows={3}
                placeholder="Optional: any specific angle / emphasis / things to avoid"
                style={{ ...modalInput(C), resize: 'vertical', fontFamily: "'Inter',sans-serif", fontSize: 11 }}
              />
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button onClick={async () => { await onDraftWithAi(aiInstructions); setShowAiPrompt(false); setAiInstructions(''); }} disabled={drafting} style={{
                  background: C.accent, color: '#001114', border: 'none', borderRadius: 6,
                  padding: '6px 14px', cursor: drafting ? 'default' : 'pointer',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
                  letterSpacing: '0.08em', textTransform: 'uppercase', opacity: drafting ? 0.5 : 1,
                }}>{drafting ? 'Drafting…' : 'Generate draft'}</button>
                <button onClick={() => { setShowAiPrompt(false); setAiInstructions(''); }} disabled={drafting} style={{
                  background: 'transparent', color: 'rgba(255,255,255,0.5)',
                  border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px',
                  cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                  letterSpacing: '0.07em', textTransform: 'uppercase',
                }}>Cancel</button>
              </div>
            </div>
          )}

          {/* Action bar */}
          <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <button onClick={saveDraft} disabled={!dirty || savingDirty} style={{
              ...BtnPrimary, background: dirty ? C.accent : 'rgba(255,255,255,0.1)',
              color: dirty ? '#001114' : 'rgba(255,255,255,0.4)', cursor: dirty ? 'pointer' : 'default',
            }}>{savingDirty ? 'Saving…' : 'Save'}</button>

            {canSubmit && (
              <button onClick={onSubmitForReview} style={BtnOutline(C)}>Submit for contributor review</button>
            )}
            {canContribSign && (
              <>
                <button onClick={() => onRequestSignoff('contributor')} style={{ ...BtnOutline(C), borderColor: '#34d39966', color: '#34d399' }}>
                  ✓ Contributor sign-off
                </button>
              </>
            )}
            {canBumpFinal && (
              <button onClick={onBumpToFinal} style={BtnOutline(C)}>Send to final review</button>
            )}
            {canFinalSign && (
              <button onClick={() => onRequestSignoff('final_approver')} style={{ ...BtnOutline(C), borderColor: '#34d39966', color: '#34d399' }}>
                ✓ Final sign-off
              </button>
            )}
            {canReopen && (
              <button onClick={onPullBack} style={{ ...BtnOutline(C), color: 'rgba(255,255,255,0.5)' }}>↩ Reopen as draft</button>
            )}
            <button onClick={openHistory} style={{
              ...BtnOutline(C), color: 'rgba(255,255,255,0.55)', borderColor: C.border,
            }} title="See and restore previous versions of this section">⟲ History</button>
            <div style={{ flex: 1 }} />
            <button onClick={onDelete} style={{
              background: 'transparent', color: 'rgba(248,113,113,0.7)', border: '1px solid rgba(248,113,113,0.4)',
              borderRadius: 6, padding: '5px 10px', cursor: 'pointer',
              fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
            }}>Delete</button>
          </div>

          {showHistory && (
            <SectionHistoryPanel
              C={C}
              section={section}
              versions={versions}
              loading={loadingVersions}
              previewVersion={previewVersion}
              users={users}
              onClose={() => { setShowHistory(false); setPreviewVersion(null); }}
              onPreview={setPreviewVersion}
              onRestore={restoreVersion}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SectionHistoryPanel({ C, section, versions, loading, previewVersion, users, onClose, onPreview, onRestore }) {
  const SOURCE_META = {
    edit:     { label: 'Human edit', color: '#22d3ee' },
    ai_draft: { label: 'AI draft',   color: '#a78bfa' },
    restore:  { label: 'Restored',   color: '#fbbf24' },
  };
  return (
    <div style={{
      marginTop: 14, padding: 14, border: `1px solid ${C.border}`, borderRadius: 6,
      background: 'rgba(255,255,255,0.02)',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 10 }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Version history
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
          {loading ? 'loading…' : `${versions.length} revision${versions.length === 1 ? '' : 's'}`}
        </div>
        <div style={{ flex: 1 }} />
        <button onClick={onClose} style={{
          background: 'transparent', border: 'none', cursor: 'pointer',
          color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif",
          fontSize: 11, fontWeight: 700,
        }}>✕ Close</button>
      </div>

      {!loading && versions.length === 0 && (
        <div style={{ padding: 12, color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
          No prior revisions yet. The first edit you make to this section will be tracked here.
        </div>
      )}

      {versions.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 14, alignItems: 'flex-start' }}>
          <div style={{ maxHeight: 360, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {/* Current (top) */}
            <div style={{
              padding: 8, borderRadius: 4, border: `1px solid ${C.border}`,
              background: previewVersion ? 'transparent' : `${C.accent}10`,
              cursor: 'pointer',
            }} onClick={() => onPreview(null)}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: previewVersion ? 'rgba(255,255,255,0.6)' : C.accent }}>
                Current version
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
                {section.content?.length ? `${section.content.length.toLocaleString()} chars` : 'empty'}
              </div>
            </div>
            {versions.map(v => {
              const sel    = previewVersion?.id === v.id;
              const editor = users.find(u => u.id === v.edited_by_user_id);
              const meta   = SOURCE_META[v.source] || SOURCE_META.edit;
              return (
                <div key={v.id} style={{
                  padding: 8, borderRadius: 4, border: `1px solid ${sel ? meta.color : C.border}`,
                  background: sel ? `${meta.color}10` : 'transparent', cursor: 'pointer',
                }} onClick={() => onPreview(v)}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                    <span style={{
                      fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800,
                      letterSpacing: '0.07em', textTransform: 'uppercase',
                      padding: '1px 5px', borderRadius: 2,
                      background: `${meta.color}22`, color: meta.color,
                    }}>{meta.label}</span>
                    <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>
                      {new Date(v.edited_at).toLocaleString()}
                    </div>
                  </div>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)', marginTop: 3 }}>
                    {editor ? (editor.name || editor.email) : '—'} · {v.content?.length || 0} chars
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ minHeight: 240, maxHeight: 360, overflow: 'auto', padding: 12, border: `1px solid ${C.border}`, borderRadius: 6, background: 'rgba(0,0,0,0.2)' }}>
            {previewVersion ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: '#fff', flex: 1 }}>
                    {previewVersion.title}
                  </div>
                  <button onClick={() => onRestore(previewVersion)} style={{
                    background: C.accent, color: '#001114', border: 'none', borderRadius: 4,
                    padding: '4px 10px', cursor: 'pointer',
                    fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
                    letterSpacing: '0.06em', textTransform: 'uppercase',
                  }}>↺ Restore this version</button>
                </div>
                <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.85)', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                  {previewVersion.content || '(empty)'}
                </div>
              </>
            ) : (
              <div style={{ color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11, textAlign: 'center', paddingTop: 40 }}>
                ← Pick a revision on the left to preview and optionally restore it
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function MembersList({ C, members, users, currentUser, onAddMember, onRemoveMember, onPatchMember }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        <button onClick={onAddMember} style={{
          background: 'transparent', color: C.accent, border: `1px solid ${C.accent}66`, borderRadius: 6,
          padding: '5px 11px', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif",
          fontSize: 10, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase',
        }}>+ Add member</button>
      </div>
      {members.length === 0 ? (
        <div style={{ padding: 18, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
          No team members assigned yet. Add contributors (regulatory, operations, IT, pricing, etc.) and approvers.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {members.map(m => {
            const u = users.find(x => x.id === m.user_id);
            const isMe = m.user_id === currentUser.id;
            return (
              <div key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: 10,
                border: `1px solid ${C.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.02)',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff' }}>
                    {u?.name || u?.email || m.user_id}{isMe && <span style={{ color: C.accent, fontWeight: 500, fontSize: 10, marginLeft: 6 }}>(you)</span>}
                  </div>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>{m.role}</div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  {PROPOSAL_CAPACITIES.map(cap => {
                    const on = m.capacities.includes(cap.id);
                    return (
                      <button
                        key={cap.id}
                        onClick={() => {
                          const next = on ? m.capacities.filter(c => c !== cap.id) : [...m.capacities, cap.id];
                          if (next.length === 0) return; // must have at least one
                          onPatchMember(m, { capacities: next });
                        }}
                        title={cap.desc}
                        style={{
                          fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700,
                          letterSpacing: '0.06em', textTransform: 'uppercase',
                          padding: '4px 8px', borderRadius: 4, cursor: 'pointer',
                          border: `1px solid ${on ? C.accent : 'rgba(255,255,255,0.18)'}`,
                          background: on ? `${C.accent}22` : 'transparent',
                          color: on ? C.accent : 'rgba(255,255,255,0.5)',
                        }}
                      >{cap.label}</button>
                    );
                  })}
                </div>
                <button onClick={() => onRemoveMember(m)} style={{
                  background: 'transparent', color: 'rgba(248,113,113,0.7)', border: '1px solid rgba(248,113,113,0.4)',
                  borderRadius: 4, padding: '4px 8px', cursor: 'pointer',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.06em',
                }}>Remove</button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function AddSectionModal({ C, users, onCancel, onSubmit }) {
  const [sectionType, setSectionType] = useState('other');
  const [title, setTitle]             = useState('');
  const [authorId, setAuthorId]       = useState('');

  // When picking a type, pre-fill the title with the type's default label.
  useEffect(() => {
    if (!title) setTitle(PROPOSAL_SECTION_TYPES.find(t => t.id === sectionType)?.label || '');
  }, [sectionType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <ModalShell title="Add proposal section" onCancel={onCancel}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Type">
          <select value={sectionType} onChange={e => { setSectionType(e.target.value); setTitle(PROPOSAL_SECTION_TYPES.find(t => t.id === e.target.value)?.label || ''); }} style={modalInput(C)}>
            {PROPOSAL_SECTION_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </Field>
        <Field label="Title" required>
          <input value={title} onChange={e => setTitle(e.target.value)} style={modalInput(C)} />
        </Field>
        <Field label="Primary author">
          <select value={authorId} onChange={e => setAuthorId(e.target.value)} style={modalInput(C)}>
            <option value="">— Unassigned —</option>
            {users.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
          </select>
        </Field>
        <Field label=" " />
      </div>
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: 'Add section',
          disabled: !title.trim(),
          onClick: () => onSubmit({
            section_type: sectionType,
            title,
            primary_author_user_id: authorId || null,
          }),
        }} />
      </div>
    </ModalShell>
  );
}

function AddMemberModal({ C, users, existingMemberUserIds, onCancel, onSubmit }) {
  const eligible = users.filter(u => !existingMemberUserIds.includes(u.id));
  const [userId, setUserId]     = useState(eligible[0]?.id || '');
  const [role, setRole]         = useState('');
  const [capabilities, setCaps] = useState(['contributor']);

  const toggleCap = (id) => {
    setCaps(prev => prev.includes(id) ? (prev.length === 1 ? prev : prev.filter(c => c !== id)) : [...prev, id]);
  };

  return (
    <ModalShell title="Add proposal team member" onCancel={onCancel}>
      <Field label="User" required>
        <select value={userId} onChange={e => setUserId(e.target.value)} style={modalInput(C)}>
          {eligible.length === 0 && <option value="">No more eligible users</option>}
          {eligible.map(u => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
        </select>
      </Field>
      <div style={{ height: 10 }} />
      <Field label="Functional role">
        <input value={role} onChange={e => setRole(e.target.value)} placeholder="e.g. Regulatory, Operations, IT, Pricing, Legal" style={modalInput(C)} />
      </Field>
      <div style={{ height: 10 }} />
      <Field label="Capacities">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PROPOSAL_CAPACITIES.map(cap => {
            const on = capabilities.includes(cap.id);
            return (
              <button key={cap.id} type="button" onClick={() => toggleCap(cap.id)} style={{
                fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                letterSpacing: '0.06em', textTransform: 'uppercase',
                padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
                border: `1px solid ${on ? C.accent : 'rgba(255,255,255,0.18)'}`,
                background: on ? `${C.accent}22` : 'transparent',
                color: on ? C.accent : 'rgba(255,255,255,0.5)',
              }}>{cap.label}</button>
            );
          })}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6, fontFamily: "'Montserrat',sans-serif" }}>
          {PROPOSAL_CAPACITIES.map(c => `${c.label}: ${c.desc.toLowerCase()}`).join(' · ')}
        </div>
      </Field>
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: 'Add member',
          disabled: !userId || capabilities.length === 0,
          onClick: () => onSubmit(userId, { role, capacities: capabilities }),
        }} />
      </div>
    </ModalShell>
  );
}

function SignoffModal({ C, section, capacity, onCancel, onSubmit }) {
  const [decision, setDecision] = useState('approved');
  const [comments, setComments] = useState('');
  const capacityLabel = capacity === 'final_approver' ? 'final approver' : 'contributor';

  return (
    <ModalShell title={`Sign off — ${section.title}`} onCancel={onCancel}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 12, lineHeight: 1.5 }}>
        Recording your decision as a <strong style={{ color: '#fff' }}>{capacityLabel}</strong>. This appends to the section's sign-off history and advances workflow status.
      </div>
      <Field label="Decision">
        <div style={{ display: 'flex', gap: 8 }}>
          {[
            { id: 'approved',          label: '✓ Approve',          color: '#34d399' },
            { id: 'changes_requested', label: '↩ Request changes',  color: '#fbbf24' },
            { id: 'rejected',          label: '✗ Reject',           color: '#f87171' },
          ].map(opt => {
            const on = decision === opt.id;
            return (
              <button key={opt.id} type="button" onClick={() => setDecision(opt.id)} style={{
                flex: 1, padding: '8px 12px', borderRadius: 6, cursor: 'pointer',
                background: on ? `${opt.color}22` : 'transparent',
                border: `1px solid ${on ? opt.color : 'rgba(255,255,255,0.18)'}`,
                color: on ? opt.color : 'rgba(255,255,255,0.6)',
                fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
              }}>{opt.label}</button>
            );
          })}
        </div>
      </Field>
      <div style={{ height: 10 }} />
      <Field label="Comments (optional)">
        <textarea rows={3} value={comments} onChange={e => setComments(e.target.value)} style={{ ...modalInput(C), resize: 'vertical' }}
          placeholder={decision === 'approved' ? 'Optional — note caveats or conditions of approval' : 'What needs to change?'} />
      </Field>
      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: 'Record decision',
          onClick: () => onSubmit(decision, comments),
        }} />
      </div>
    </ModalShell>
  );
}

function DirectivesPanel({ C, directives, input, setInput, saving, drafting, sectionsCount, onAdd, onRemove, onRegenerate }) {
  const [collapsed, setCollapsed] = useState(directives.length === 0);
  useEffect(() => { if (directives.length > 0) setCollapsed(false); }, [directives.length]);

  const placeholderExamples = [
    'Use the same formatting as the BASF Barge Tracking Proposal for colors, fonts, and pricing schedule.',
    'Imperial units throughout — this is a US submission.',
    'Lead the exec summary with our safety record; pricing details come last.',
    'Don\'t mention subcontractor partnerships in the cover letter.',
  ];
  const placeholder = placeholderExamples[Math.floor((directives.length * 7919) % placeholderExamples.length)];

  return (
    <div style={{
      marginBottom: 14, padding: collapsed ? 8 : 12,
      border: `1px solid ${directives.length > 0 ? '#a78bfa55' : C.border}`,
      borderRadius: 8,
      background: directives.length > 0 ? 'rgba(167,139,250,0.04)' : 'rgba(255,255,255,0.02)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setCollapsed(!collapsed)}>
        <span style={{ fontSize: 13 }}>💬</span>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          AI directives {directives.length > 0 && <span style={{ color: '#a78bfa' }}>· {directives.length} active</span>}
        </div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
          {collapsed ? 'click to expand' : 'cross-cutting instructions every AI drafter follows'}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ color: 'rgba(255,255,255,0.5)' }}>{collapsed ? '▸' : '▾'}</span>
      </div>

      {!collapsed && (
        <div style={{ marginTop: 12 }}>
          {directives.length === 0 && (
            <div style={{
              fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.55)',
              marginBottom: 10, lineHeight: 1.55,
            }}>
              Tell the AI how to handle this whole proposal — style, formatting, references to lean on, things to avoid. Every section drafter and the Coach chat reads these as top-priority instructions.
            </div>
          )}

          {directives.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
              {directives.map(d => {
                const u = d.users || {};
                return (
                  <div key={d.id} style={{
                    padding: '8px 10px', borderRadius: 6,
                    background: 'rgba(167,139,250,0.08)',
                    border: '1px solid rgba(167,139,250,0.25)',
                    display: 'flex', alignItems: 'flex-start', gap: 10,
                  }}>
                    <span style={{ color: '#a78bfa', fontSize: 12, lineHeight: 1.4 }}>→</span>
                    <div style={{ flex: 1, fontFamily: "'Inter',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.92)', lineHeight: 1.55 }}>
                      {d.content}
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>
                        added by {u.name || u.email || '—'} · {new Date(d.created_at).toLocaleString()}
                      </div>
                    </div>
                    <button onClick={() => onRemove(d)} title="Remove directive" style={{
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: 'rgba(248,113,113,0.6)', fontFamily: "'Montserrat',sans-serif",
                      fontSize: 11, fontWeight: 700,
                    }}>✕</button>
                  </div>
                );
              })}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onAdd(); }}
              placeholder={placeholder}
              rows={2}
              style={{ ...modalInput(C), flex: 1, resize: 'vertical', fontFamily: "'Inter',sans-serif", fontSize: 12 }}
            />
            <button
              onClick={onAdd}
              disabled={!input.trim() || saving}
              style={{
                background: input.trim() ? '#a78bfa' : 'rgba(255,255,255,0.1)',
                color: input.trim() ? '#001114' : 'rgba(255,255,255,0.4)',
                border: 'none', borderRadius: 6, padding: '0 16px',
                cursor: input.trim() ? 'pointer' : 'default',
                fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800,
                letterSpacing: '0.07em', textTransform: 'uppercase',
                alignSelf: 'stretch',
              }}
            >{saving ? '…' : 'Add'}</button>
          </div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>
            Ctrl/⌘+Enter to add. These instructions flow into every AI draft on this proposal.
          </div>

          {directives.length > 0 && sectionsCount > 0 && onRegenerate && (
            <div style={{
              marginTop: 14, padding: 12, borderRadius: 6,
              border: '1px dashed rgba(167,139,250,0.5)',
              background: 'rgba(167,139,250,0.05)',
            }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.75)', lineHeight: 1.55, marginBottom: 10 }}>
                <strong style={{ color: '#a78bfa' }}>Apply these directives to existing sections?</strong>
                <br />
                Directives only influence FUTURE AI drafts. Pick how to apply them:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                <button
                  onClick={() => onRegenerate('fill_empty')}
                  disabled={drafting}
                  title="Only draft sections that are still empty. Sections you've already worked on are untouched."
                  style={{
                    background: drafting ? 'rgba(255,255,255,0.08)' : 'transparent',
                    color: drafting ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.8)',
                    border: '1px solid rgba(255,255,255,0.25)', borderRadius: 6,
                    padding: '10px 8px', cursor: drafting ? 'default' : 'pointer',
                    fontFamily: "'Montserrat',sans-serif", textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4 }}>
                    ↻ Fill empty only
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', fontWeight: 400, lineHeight: 1.4 }}>
                    Safest. Drafts only empty sections. Your edits untouched.
                  </div>
                </button>
                <button
                  onClick={() => {
                    if (!confirm('RE-STYLE every section using the directives, keeping your edits as a starting point? The AI revises your prose (preserving facts) rather than rewriting from scratch. Prior content goes into version history.')) return;
                    onRegenerate('restyle');
                  }}
                  disabled={drafting}
                  title="Keep your edited content; have the AI revise the prose to apply the new directives (tone, formatting, references). Substance is preserved; voice/structure shift."
                  style={{
                    background: drafting ? 'rgba(255,255,255,0.08)' : '#a78bfa',
                    color: drafting ? 'rgba(255,255,255,0.4)' : '#001114',
                    border: 'none', borderRadius: 6,
                    padding: '10px 8px', cursor: drafting ? 'default' : 'pointer',
                    fontFamily: "'Montserrat',sans-serif", textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4 }}>
                    ✨ Restyle (keep edits)
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.7)', fontWeight: 400, lineHeight: 1.4 }}>
                    Recommended. Keeps your work, applies directives on top.
                  </div>
                </button>
                <button
                  onClick={() => {
                    if (!confirm('REDRAFT EVERY section from scratch? Your existing content gets preserved in version history (restorable per section) but the new draft will NOT use it as a starting point.')) return;
                    onRegenerate('replace_all');
                  }}
                  disabled={drafting}
                  title="Throws away your edits and writes fresh drafts. Prior content goes into version history; restorable per-section."
                  style={{
                    background: drafting ? 'rgba(255,255,255,0.08)' : 'transparent',
                    color: drafting ? 'rgba(255,255,255,0.4)' : '#f87171',
                    border: '1px solid rgba(248,113,113,0.4)', borderRadius: 6,
                    padding: '10px 8px', cursor: drafting ? 'default' : 'pointer',
                    fontFamily: "'Montserrat',sans-serif", textAlign: 'left',
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.07em', textTransform: 'uppercase', marginBottom: 4 }}>
                    ⟲ Start from scratch
                  </div>
                  <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', fontWeight: 400, lineHeight: 1.4 }}>
                    Nuclear. Discards your prose; AI writes everything fresh.
                  </div>
                </button>
              </div>
              {drafting && (
                <div style={{ marginTop: 8, fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(167,139,250,0.85)', textAlign: 'center' }}>
                  ⟳ Working… 20–60 seconds depending on how long your sections are.
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProposalCostBreakdownModal({ C, opp, usage, onClose, onRefresh }) {
  const featureLabel = {
    proposal_section_draft:  'Section drafter',
    proposal_full_draft:     'Full-proposal generator',
    proposal_apply_fix:      'Critique apply-fix',
    proposal_critique:       'Critique mode',
    proposal_gap_radar:      'Gap radar',
    proposal_score_win:      'Win probability',
    proposal_coach_chat:     'Coach chat',
    artifact_doc_prep:       'Artifact doc prep',
  };
  return (
    <ModalShell title={`AI cost breakdown · ${opp.name}`} onCancel={onClose}>
      <div style={{
        padding: 16, borderRadius: 8, marginBottom: 16,
        background: 'linear-gradient(135deg, rgba(52,211,153,0.08), rgba(34,211,238,0.06))',
        border: '1px solid rgba(52,211,153,0.3)',
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, textAlign: 'center' }}>
          <div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>Total cost</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 26, fontWeight: 900, color: '#34d399' }}>${usage.total_cost.toFixed(4)}</div>
          </div>
          <div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>Total tokens</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 22, fontWeight: 800, color: '#22d3ee' }}>{usage.total_tokens.toLocaleString()}</div>
          </div>
          <div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)', marginBottom: 4 }}>AI calls</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 22, fontWeight: 800, color: '#fff' }}>{usage.count}</div>
          </div>
        </div>
      </div>

      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
        By feature
      </div>
      {usage.byFeature.length === 0 ? (
        <div style={{ padding: 12, color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11, textAlign: 'center' }}>No AI usage yet on this proposal.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 320, overflowY: 'auto' }}>
          {usage.byFeature.map(f => {
            const pct = usage.total_cost > 0 ? (f.cost / usage.total_cost) * 100 : 0;
            return (
              <div key={f.feature} style={{ padding: 10, borderRadius: 6, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.02)' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: '#fff', flex: 1 }}>
                    {featureLabel[f.feature] || f.feature}
                  </div>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
                    {f.calls} call{f.calls === 1 ? '' : 's'} · {f.tokens.toLocaleString()} tokens
                  </div>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 800, color: '#34d399', minWidth: 70, textAlign: 'right' }}>
                    ${f.cost.toFixed(4)}
                  </div>
                </div>
                <div style={{ height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: '#34d399' }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button onClick={onRefresh} style={{
          background: 'transparent', color: 'rgba(255,255,255,0.7)',
          border: `1px solid ${C.border}`, borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
          fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>↻ Refresh</button>
        <button onClick={onClose} style={{
          background: C.accent, color: '#001114', border: 'none', borderRadius: 6, padding: '6px 14px', cursor: 'pointer',
          fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase',
        }}>Close</button>
      </div>
    </ModalShell>
  );
}

function AllVersionsModal({ C, opp, sections, users, currentUser, onClose, onRestored, showToast }) {
  const [versions, setVersions]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filterSection, setFilterSection] = useState('all');
  const [previewVersion, setPreviewVersion] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setVersions(await loadAllSectionVersionsForOpp(opp.id));
      setLoading(false);
    })();
  }, [opp.id]);

  const SOURCE_META = {
    edit:          { label: 'Human edit',     color: '#22d3ee' },
    ai_draft:      { label: 'AI draft',       color: '#a78bfa' },
    restore:       { label: 'Restored',       color: '#fbbf24' },
    critique_fix:  { label: 'Critique fix',   color: '#f87171' },
  };

  const sectionsById = useMemo(() => {
    const m = {};
    for (const s of sections) m[s.id] = s;
    return m;
  }, [sections]);

  const filtered = filterSection === 'all'
    ? versions
    : versions.filter(v => v.section_id === filterSection);

  const restore = async (v) => {
    if (!confirm(`Restore the version from ${new Date(v.edited_at).toLocaleString()} into "${sectionsById[v.section_id]?.title || 'section'}"? Your current content for that section is saved as a new version automatically.`)) return;
    const result = await restoreProposalSectionVersion(v.section_id, v, currentUser.id);
    if (result.error) { showToast?.(`❌ Restore failed: ${result.error}`); return; }
    onRestored?.();
    // Refresh local versions list to reflect the new "restore" snapshot
    setVersions(await loadAllSectionVersionsForOpp(opp.id));
    setPreviewVersion(null);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 10500,
      display: 'flex', flexDirection: 'column', padding: 20,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        background: '#0D1B2A', border: '1px solid rgba(251,191,36,0.4)', borderRadius: 12,
        boxShadow: '0 16px 48px rgba(0,0,0,0.8)', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 18px', borderBottom: `1px solid ${C.border}`,
          background: 'rgba(251,191,36,0.05)',
        }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            ⟲ Version History · {opp.name}
          </div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
            {loading ? 'loading…' : `${versions.length} revision${versions.length === 1 ? '' : 's'} across ${sections.length} section${sections.length === 1 ? '' : 's'}`}
          </div>
          <div style={{ flex: 1 }} />
          <select value={filterSection} onChange={e => { setFilterSection(e.target.value); setPreviewVersion(null); }} style={{
            background: 'rgba(255,255,255,0.05)', color: '#fff', border: `1px solid ${C.border}`,
            borderRadius: 6, padding: '5px 10px', fontFamily: "'Montserrat',sans-serif", fontSize: 11,
          }}>
            <option value="all">All sections</option>
            {sections.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
          </select>
          <button onClick={onClose} style={{
            background: 'transparent', color: 'rgba(255,255,255,0.7)', border: `1px solid ${C.border}`,
            borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          }}>✕ Close</button>
        </div>

        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif", fontSize: 12 }}>
            Loading versions…
          </div>
        ) : versions.length === 0 ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif", fontSize: 12, gap: 8, padding: 24, textAlign: 'center' }}>
            <div>No revisions tracked yet.</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', maxWidth: 480 }}>
              Versions are captured automatically every time you save a section or the AI rewrites one. If you have edits that aren't showing here, check that migration <code style={{ background: 'rgba(255,255,255,0.08)', padding: '2px 6px', borderRadius: 3 }}>20260717_section_versions.sql</code> was applied.
            </div>
          </div>
        ) : (
          <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '340px 1fr', overflow: 'hidden' }}>
            {/* Timeline rail */}
            <div style={{ borderRight: `1px solid ${C.border}`, overflow: 'auto', padding: 8, background: 'rgba(0,0,0,0.3)' }}>
              {filtered.length === 0 ? (
                <div style={{ padding: 14, color: 'rgba(255,255,255,0.4)', fontFamily: "'Montserrat',sans-serif", fontSize: 11 }}>
                  No revisions for this section.
                </div>
              ) : (
                filtered.map(v => {
                  const sel = previewVersion?.id === v.id;
                  const editor = v.users || users.find(u => u.id === v.edited_by_user_id);
                  const meta = SOURCE_META[v.source] || SOURCE_META.edit;
                  const section = sectionsById[v.section_id];
                  return (
                    <div key={v.id} onClick={() => setPreviewVersion(v)} style={{
                      padding: 10, marginBottom: 6, borderRadius: 4,
                      border: `1px solid ${sel ? meta.color : C.border}`,
                      background: sel ? `${meta.color}10` : 'rgba(255,255,255,0.02)',
                      cursor: 'pointer',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 4 }}>
                        <span style={{
                          fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800,
                          letterSpacing: '0.07em', textTransform: 'uppercase',
                          padding: '1px 5px', borderRadius: 2,
                          background: `${meta.color}22`, color: meta.color,
                        }}>{meta.label}</span>
                        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.8)' }}>
                          {new Date(v.edited_at).toLocaleString()}
                        </div>
                      </div>
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: '#fff', marginBottom: 3 }}>
                        {section ? section.title : v.title}
                      </div>
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
                        {editor ? (editor.name || editor.email) : '—'} · {v.content?.length?.toLocaleString() || 0} chars
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Preview */}
            <div style={{ padding: 18, overflow: 'auto', background: '#fff', color: '#0D1B2A' }}>
              {previewVersion ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #E5EAF0' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 800, color: '#0D1B2A' }}>
                        {sectionsById[previewVersion.section_id]?.title || previewVersion.title}
                      </div>
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: '#506276', marginTop: 3 }}>
                        Saved {new Date(previewVersion.edited_at).toLocaleString()} · source: {previewVersion.source}
                      </div>
                    </div>
                    <button onClick={() => restore(previewVersion)} style={{
                      background: C.accent, color: '#fff', border: 'none', borderRadius: 6,
                      padding: '8px 14px', cursor: 'pointer',
                      fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800,
                      letterSpacing: '0.06em', textTransform: 'uppercase',
                    }}>↺ Restore this version</button>
                  </div>
                  <div style={{ fontFamily: "'Inter',sans-serif", fontSize: 12, color: '#0D1B2A', whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>
                    {previewVersion.content || '(empty)'}
                  </div>
                </>
              ) : (
                <div style={{ color: '#899AAB', fontFamily: "'Montserrat',sans-serif", fontSize: 13, textAlign: 'center', paddingTop: 80 }}>
                  ← Pick a revision on the left to preview and optionally restore it
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ProposalPreviewModal({ C, opp, sections, signoffs, members, users, onClose, onJumpToEditor, onExport }) {
  const [html, setHtml]       = useState('');
  const [loading, setLoading] = useState(true);
  const iframeRef             = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      // Inline images as data URLs (same path as the PDF builder) so
      // the iframe doesn't need network access to render them.
      const rawImages = await loadProposalSectionImagesByOpp(opp.id);
      const images = await Promise.all(rawImages.map(async img => {
        try {
          const url = await getProposalImageSignedUrl(img.storage_path);
          if (!url) return img;
          const resp = await fetch(url);
          const blob = await resp.blob();
          const dataUrl = await new Promise(resolve => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result);
            r.readAsDataURL(blob);
          });
          return { ...img, dataUrl };
        } catch { return img; }
      }));
      if (cancelled) return;

      // Theme: pull logo + cover as data URLs (same path as PDF builder).
      const theme = resolveTheme(opp.theme);
      const fetchAsDataUrl = async (path) => {
        if (!path) return null;
        try {
          const url = await getProposalImageSignedUrl(path);
          if (!url) return null;
          const resp = await fetch(url);
          const blob = await resp.blob();
          return await new Promise(resolve => {
            const r = new FileReader();
            r.onloadend = () => resolve(r.result);
            r.readAsDataURL(blob);
          });
        } catch { return null; }
      };
      const [logoDataUrl, coverDataUrl] = await Promise.all([
        fetchAsDataUrl(theme.logo_storage_path),
        fetchAsDataUrl(theme.cover_image_storage_path),
      ]);

      // Render the proposal HTML using the same builder the PDF
      // assembler uses — what you see here is what you get on
      // export. Review status tags off by default (clean
      // client-facing view); user can toggle them on.
      const resolvedContact = opp.primary_contact_id ? await loadContact(opp.primary_contact_id) : null;

      setHtml(buildProposalExportHtml({
        opp, sections, members, signoffs, users, images,
        generatedAt: new Date(),
        includeReviewStatus: false,
        theme, logoDataUrl, coverDataUrl,
        primaryContact: resolvedContact,
      }));
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [opp.id, sections, signoffs, members, users]);

  const jumpToSection = (sectionId) => {
    const iframe = iframeRef.current;
    if (!iframe || !iframe.contentDocument) return;
    const target = iframe.contentDocument.getElementById(`section-${sectionId}`);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const visibleSections = sections
    .filter(s => s.status !== 'rejected')
    .sort((a, b) => (a.sequence_idx - b.sequence_idx) || a.created_at.localeCompare(b.created_at));

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 10500,
      display: 'flex', flexDirection: 'column', padding: 20,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        flex: 1, display: 'flex', flexDirection: 'column',
        background: '#0D1B2A', border: '1px solid rgba(34,211,238,0.4)', borderRadius: 12,
        boxShadow: '0 16px 48px rgba(0,0,0,0.8)', overflow: 'hidden',
      }}>
        {/* Top bar */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 18px', borderBottom: `1px solid ${C.border}`,
          background: 'rgba(34,211,238,0.05)',
        }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            👁 Proposal preview · {opp.name}
          </div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)' }}>
            What you'll see in the exported PDF — review here, edit any section without leaving
          </div>
          <div style={{ flex: 1 }} />
          <button onClick={onExport} style={{
            background: C.accent, color: '#001114', border: 'none', borderRadius: 6,
            padding: '6px 14px', cursor: 'pointer',
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800,
            letterSpacing: '0.07em', textTransform: 'uppercase',
          }}>Export / Email</button>
          <button onClick={onClose} style={{
            background: 'transparent', color: 'rgba(255,255,255,0.7)', border: `1px solid ${C.border}`,
            borderRadius: 6, padding: '6px 12px', cursor: 'pointer',
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: '0.06em',
          }}>✕ Close</button>
        </div>

        {/* Body: section list rail + iframe */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Rail */}
          <div style={{
            width: 240, borderRight: `1px solid ${C.border}`, overflow: 'auto',
            padding: 12, background: 'rgba(0,0,0,0.3)',
          }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 8 }}>
              Sections
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {visibleSections.map((s, idx) => {
                const meta = SECTION_STATUS_META[s.status] || SECTION_STATUS_META.draft;
                return (
                  <div key={s.id} style={{
                    padding: '8px 10px', borderRadius: 4,
                    border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.02)',
                  }}>
                    <button onClick={() => jumpToSection(s.id)} style={{
                      width: '100%', textAlign: 'left',
                      background: 'transparent', border: 'none', cursor: 'pointer',
                      color: '#fff', padding: 0,
                      fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700,
                    }}>
                      {idx + 1}. {s.title}
                    </button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                      <span style={{
                        fontFamily: "'Montserrat',sans-serif", fontSize: 8, fontWeight: 800,
                        letterSpacing: '0.07em', textTransform: 'uppercase',
                        padding: '1px 5px', borderRadius: 2,
                        background: `${meta.color}22`, color: meta.color,
                      }}>{meta.label}</span>
                      <div style={{ flex: 1 }} />
                      <button onClick={() => onJumpToEditor(s.id)} title="Open in editor" style={{
                        background: 'transparent', border: 'none', cursor: 'pointer',
                        color: 'rgba(255,255,255,0.5)', padding: 0,
                        fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700,
                      }}>✎ Edit</button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Iframe */}
          <div style={{ flex: 1, padding: 16, overflow: 'hidden', background: '#1B2B5B' }}>
            {loading ? (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif", fontSize: 12 }}>
                Rendering preview…
              </div>
            ) : (
              <iframe
                ref={iframeRef}
                title="Proposal preview"
                srcDoc={html}
                sandbox="allow-same-origin"
                style={{
                  width: '100%', height: '100%', border: 'none', borderRadius: 6,
                  background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FullProposalDraftModal({ C, sections, drafting, onCancel, onSubmit }) {
  const visible    = sections.filter(s => s.status !== 'rejected');
  const empty      = visible.filter(s => !s.content || !s.content.trim());
  const populated  = visible.filter(s => s.content && s.content.trim());

  const [mode, setMode] = useState(populated.length === 0 ? 'fill_empty' : 'fill_empty');

  return (
    <ModalShell title="Generate full proposal with AI" onCancel={onCancel}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6, marginBottom: 14 }}>
        Drafts every section in <strong style={{ color: '#fff' }}>one coherent pass</strong>. The AI sees all section titles, your scope, notes, CRM activity, RFP, library precedents, competitors, and tone — and writes each section knowing the others, so the exec summary previews the technical approach, pricing references staffing, and past performance matches what you're actually proposing.
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14,
        padding: 12, border: `1px solid ${C.border}`, borderRadius: 6, background: 'rgba(255,255,255,0.02)',
      }}>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Empty sections</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 22, fontWeight: 800, color: '#22d3ee' }}>{empty.length}</div>
        </div>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Already drafted</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 22, fontWeight: 800, color: '#fbbf24' }}>{populated.length}</div>
        </div>
      </div>

      <Field label="What to draft">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <label style={{
            display: 'flex', gap: 10, padding: 10, borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${mode === 'fill_empty' ? C.accent : C.border}`,
            background: mode === 'fill_empty' ? `${C.accent}10` : 'transparent',
          }}>
            <input type="radio" checked={mode === 'fill_empty'} onChange={() => setMode('fill_empty')} />
            <div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff' }}>Only empty sections ({empty.length})</div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 3, lineHeight: 1.5 }}>
                Preserve your existing content. Empty sections get a fresh AI draft written straight into their content field. Sections you've already worked on stay untouched.
              </div>
            </div>
          </label>
          <label style={{
            display: 'flex', gap: 10, padding: 10, borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${mode === 'replace_all' ? '#f87171' : C.border}`,
            background: mode === 'replace_all' ? '#f8717110' : 'transparent',
            opacity: populated.length === 0 ? 0.4 : 1,
          }}>
            <input type="radio" checked={mode === 'replace_all'} onChange={() => setMode('replace_all')} disabled={populated.length === 0} />
            <div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: populated.length === 0 ? 'rgba(255,255,255,0.4)' : '#fff' }}>
                Redraft every section ({visible.length})
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 3, lineHeight: 1.5 }}>
                Replaces all existing content with a fresh coherent draft. Use this when you want a clean restart from the latest context. Your previous content is saved as a version (see history).
              </div>
            </div>
          </label>
        </div>
      </Field>

      <div style={{ marginTop: 14 }}>
        <ModalActions onCancel={onCancel} primary={{
          label: drafting ? 'Drafting full proposal…' : `✨ Generate ${mode === 'replace_all' ? visible.length : empty.length} section${(mode === 'replace_all' ? visible.length : empty.length) === 1 ? '' : 's'}`,
          disabled: drafting || (mode === 'fill_empty' ? empty.length === 0 : visible.length === 0),
          onClick: () => onSubmit(mode),
        }} />
        {drafting && (
          <div style={{ marginTop: 10, fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>
            This may take 20–60 seconds depending on proposal length. The AI is reading every piece of context.
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function AssembleProposalModal({ C, opp, sections, signoffs, members, users, exporting, sendingEmail, currentUser, onCancel, onExport, onEmail }) {
  const [includeReviewStatus, setIncludeReviewStatus] = useState(false);
  const [mode, setMode] = useState('export'); // 'export' | 'email'
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState(`Proposal — ${opp.name}${opp.client_name ? ` for ${opp.client_name}` : ''}`);
  const [emailBody, setEmailBody] = useState(
`Hi${opp.client_name ? ` ${opp.client_name} team` : ''},

Attached is our proposal for "${opp.name}". Please find the full document attached as a PDF.

Happy to walk through any section in detail — let us know what works for a follow-up.

Best,
${currentUser?.name || ''}`,
  );

  const visible = sections.filter(s => s.status !== 'rejected')
    .sort((a, b) => (a.sequence_idx - b.sequence_idx) || a.created_at.localeCompare(b.created_at));
  const counts = { final_approved: 0, contributor_approved: 0, final_review: 0, contributor_review: 0, draft: 0 };
  visible.forEach(s => { counts[s.status] = (counts[s.status] || 0) + 1; });

  const allFinal = visible.length > 0 && counts.final_approved === visible.length;
  const aiOnly   = visible.filter(s => (!s.content || !s.content.trim()) && s.ai_generated_content);
  const empty    = visible.filter(s => (!s.content || !s.content.trim()) && !s.ai_generated_content);

  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTo.trim());

  return (
    <ModalShell title={`Assemble proposal · ${opp.name}`} onCancel={onCancel}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {[{ id: 'export', label: 'Download PDF' }, { id: 'email', label: 'Send via email' }].map(t => (
          <button key={t.id} onClick={() => setMode(t.id)} style={{
            fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
            letterSpacing: '0.07em', textTransform: 'uppercase',
            padding: '6px 12px', borderRadius: 6, cursor: 'pointer',
            border: `1px solid ${mode === t.id ? C.accent : C.border}`,
            background: mode === t.id ? `${C.accent}22` : 'transparent',
            color:      mode === t.id ? C.accent           : 'rgba(255,255,255,0.6)',
          }}>{t.label}</button>
        ))}
      </div>

      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8,
        marginBottom: 14, padding: 12, border: `1px solid ${C.border}`, borderRadius: 6,
        background: 'rgba(255,255,255,0.02)',
      }}>
        <Stat C={C} label="Final approved"     value={counts.final_approved}     color="#34d399" />
        <Stat C={C} label="In review / drafted" value={counts.contributor_approved + counts.final_review + counts.contributor_review} color="#fbbf24" />
        <Stat C={C} label="Still draft"        value={counts.draft}              color="#94a3b8" />
        {aiOnly.length > 0 && (
          <Stat C={C} label="AI-only (no human content)" value={aiOnly.length} color="#a78bfa" colSpan={2} />
        )}
        {empty.length > 0 && (
          <Stat C={C} label="Empty sections" value={empty.length} color="#f87171" colSpan={2} />
        )}
      </div>

      {!allFinal && (
        <div style={{
          padding: 10, marginBottom: 14, borderRadius: 6,
          border: '1px solid rgba(251,191,36,0.4)', background: 'rgba(251,191,36,0.08)',
          fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: '#fbbf24',
        }}>
          ⚠ Not every section is final-approved yet. You can still {mode === 'email' ? 'send' : 'export'} — sections will be tagged with their current review status if you choose to include it below.
        </div>
      )}

      {mode === 'export' && (
        <>
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer',
            fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.75)',
            marginBottom: 14,
          }}>
            <input type="checkbox" checked={includeReviewStatus} onChange={e => setIncludeReviewStatus(e.target.checked)} />
            Include internal review-status tags on each section (uncheck for a clean client-facing PDF)
          </label>
          <div style={{ marginTop: 14 }}>
            <ModalActions onCancel={onCancel} primary={{
              label: exporting ? 'Exporting…' : 'Download PDF',
              disabled: exporting || visible.length === 0,
              onClick: () => onExport(includeReviewStatus),
            }} />
          </div>
        </>
      )}

      {mode === 'email' && (
        <>
          <Field label="To" required>
            <input value={emailTo} onChange={e => setEmailTo(e.target.value)}
              placeholder="recipient@client.com (comma-separate multiple)" style={modalInput(C)} />
          </Field>
          <div style={{ height: 10 }} />
          <Field label="Subject" required>
            <input value={emailSubject} onChange={e => setEmailSubject(e.target.value)} style={modalInput(C)} />
          </Field>
          <div style={{ height: 10 }} />
          <Field label="Message">
            <textarea rows={8} value={emailBody} onChange={e => setEmailBody(e.target.value)}
              style={{ ...modalInput(C), resize: 'vertical', fontFamily: "'Inter',sans-serif", fontSize: 12 }} />
          </Field>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 6 }}>
            The proposal PDF (clean, no internal review tags) will be attached automatically.
          </div>
          <div style={{ marginTop: 14 }}>
            <ModalActions onCancel={onCancel} primary={{
              label: sendingEmail ? 'Sending…' : 'Send proposal',
              disabled: sendingEmail || !validEmail || !emailSubject.trim() || visible.length === 0,
              onClick: () => onEmail({
                to: emailTo.split(',').map(s => s.trim()).filter(Boolean),
                subject: emailSubject.trim(),
                body: emailBody,
              }),
            }} />
          </div>
        </>
      )}
    </ModalShell>
  );
}

function Stat({ C, label, value, color, colSpan = 1 }) {
  return (
    <div style={{ gridColumn: `span ${colSpan}`, padding: 8 }}>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 18, fontWeight: 800, color }}>{value}</div>
    </div>
  );
}

// ─── Opportunity AI Coach ─────────────────────────────────────────
// Inline chat panel that lives inside OpportunityDetail. Per-user
// threads keyed to the opportunity (RLS: user_id = self). System
// prompt frames Claude as a Proposal Coach — reads notes + scope +
// priorities and emits win-probability + gap-radar + next-best-
// actions.
function OpportunityAiCoach({ C, opp, currentUser, linkedContext = null, showToast }) {
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(`oppCoach:${opp.id}:activeThreadId`) || null;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [showThreadMenu, setShowThreadMenu] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const skipNextReloadRef = useRef(false);
  const scrollRef = useRef(null);

  // Library scope: which subset of the org's proposal library rides
  // along with each chat turn. Persisted per-opportunity. Default is
  // OFF so the coach doesn't surprise-attach docs the user hasn't
  // explicitly opted into.
  const DEFAULT_LIB_FILTERS = { enabled: false, sectors: [], clients: [], doc_types: [] };
  const [libraryDocs, setLibraryDocs] = useState([]);
  const [libraryFilters, setLibraryFilters] = useState(() => {
    if (typeof window === 'undefined') return DEFAULT_LIB_FILTERS;
    try {
      const raw = window.localStorage.getItem(`oppCoach:${opp.id}:libraryFilters`);
      return raw ? { ...DEFAULT_LIB_FILTERS, ...JSON.parse(raw) } : DEFAULT_LIB_FILTERS;
    } catch { return DEFAULT_LIB_FILTERS; }
  });
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);

  // Pull the AI-ready slice of the org library so the picker can show
  // sector/client/doc-type options and live-count the match.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const docs = await loadProposalLibrary(currentUser.orgId);
      if (cancelled) return;
      setLibraryDocs(docs.filter(d => d.ai_enabled && (d.anthropic_file_id || d.extracted_content)));
    })();
    return () => { cancelled = true; };
  }, [currentUser.orgId]);

  // Persist filters per-opportunity.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(`oppCoach:${opp.id}:libraryFilters`, JSON.stringify(libraryFilters));
  }, [opp.id, libraryFilters]);

  const libraryMatchingDocs = useMemo(() => {
    if (!libraryFilters.enabled) return [];
    return libraryDocs.filter(d => {
      if (libraryFilters.sectors.length   && !(d.sectors   || []).some(s => libraryFilters.sectors.includes(s))) return false;
      if (libraryFilters.clients.length   && !(d.clients   || []).some(c => libraryFilters.clients.includes(c))) return false;
      if (libraryFilters.doc_types.length && !libraryFilters.doc_types.includes(d.document_type)) return false;
      return true;
    });
  }, [libraryDocs, libraryFilters]);

  const allLibSectors  = useMemo(() => [...new Set(libraryDocs.flatMap(d => d.sectors  || []))].sort(), [libraryDocs]);
  const allLibClients  = useMemo(() => [...new Set(libraryDocs.flatMap(d => d.clients  || []))].sort(), [libraryDocs]);
  const allLibDocTypes = useMemo(() => [...new Set(libraryDocs.map(d => d.document_type).filter(Boolean))].sort(), [libraryDocs]);

  const toggleArr = (arr, v) => arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v];

  const refreshThreads = useCallback(async () => {
    const list = await loadOpportunityAiThreads(opp.id);
    setThreads(list);
    return list;
  }, [opp.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await refreshThreads();
      if (cancelled) return;
      const stillExists = list.find(t => t.id === activeThreadId);
      if (!stillExists && list.length > 0) setActiveThreadId(list[0].id);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opp.id]);

  useEffect(() => {
    if (skipNextReloadRef.current) { skipNextReloadRef.current = false; return; }
    if (!activeThreadId) { setMessages([]); return; }
    let cancelled = false;
    (async () => {
      const msgs = await loadOpportunityAiMessages(opp.id, activeThreadId);
      if (!cancelled) setMessages(msgs);
    })();
    return () => { cancelled = true; };
  }, [opp.id, activeThreadId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeThreadId) window.localStorage.setItem(`oppCoach:${opp.id}:activeThreadId`, activeThreadId);
    else                window.localStorage.removeItem(`oppCoach:${opp.id}:activeThreadId`);
  }, [opp.id, activeThreadId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setSending(true);
    const optimistic = { id: `tmp-${Date.now()}`, role: 'user', content: trimmed };
    setMessages(prev => [...prev, optimistic]);
    setInput('');
    const resolvedModel = AI_MODELS['proposal_coach'] || null;
    const result = await sendOpportunityAiMessage({
      opportunityId: opp.id,
      message: trimmed,
      model: resolvedModel,
      threadId: activeThreadId,
      linkedContext,
      libraryScope: libraryFilters,
    });
    if (result.error) {
      showToast?.(`❌ Coach: ${result.error}`);
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setInput(trimmed);
    } else {
      setMessages(prev => [...prev, {
        id: `tmp-asst-${Date.now()}`,
        role: 'assistant',
        content: result.reply || '(no reply)',
        model: result.model,
      }]);
      if (result.thread_id && result.thread_id !== activeThreadId) {
        skipNextReloadRef.current = true;
        setActiveThreadId(result.thread_id);
      }
      if (result.usage) {
        logTokenUsage({
          orgId:        currentUser.orgId,
          companyName:  currentUser.company || null,
          userId:       currentUser.id,
          userName:     currentUser.name,
          feature:      'proposal_coach',
          model:        result.model,
          inputTokens:  result.usage.input_tokens  || 0,
          outputTokens: result.usage.output_tokens || 0,
        });
      }
      refreshThreads();
    }
    setSending(false);
  };

  const startNewThread = async () => {
    const t = await createOpportunityAiThread(opp.id, currentUser.id, 'New conversation');
    if (!t) { showToast?.('❌ Could not create thread'); return; }
    await refreshThreads();
    setActiveThreadId(t.id);
    setShowThreadMenu(false);
  };
  const switchThread = async (id) => {
    setShowThreadMenu(false);
    setActiveThreadId(id);
    const msgs = await loadOpportunityAiMessages(opp.id, id);
    setMessages(msgs);
  };
  const beginRename = (t) => { setRenamingId(t.id); setRenameValue(t.title); };
  const commitRename = async () => {
    const next = (renameValue || '').trim();
    if (next && next !== threads.find(t => t.id === renamingId)?.title) {
      await renameOpportunityAiThread(renamingId, next.slice(0, 200));
    }
    setRenamingId(null); setRenameValue(''); refreshThreads();
  };
  const removeThread = async (t) => {
    if (!confirm(`Delete "${t.title}"?`)) return;
    await deleteOpportunityAiThread(t.id);
    if (activeThreadId === t.id) setActiveThreadId(null);
    refreshThreads();
  };

  const activeThread = threads.find(t => t.id === activeThreadId) || null;

  return (
    <div style={{ background: 'rgba(167,139,250,0.05)', border: '1px solid rgba(167,139,250,0.3)', borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'linear-gradient(90deg,rgba(167,139,250,0.18),rgba(232,119,34,0.10))', borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 16 }}>🤝</span>
        <button onClick={() => setShowThreadMenu(v => !v)} title="Switch / manage conversations"
          style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', padding: 0 }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span>PROPOSAL COACH</span>
            <span style={{ fontSize: 9, opacity: 0.55 }}>· {activeThread?.title || 'New conversation'} ▾</span>
          </div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)' }}>
            {opp.name}{threads.length > 0 ? ` · ${threads.length} thread${threads.length === 1 ? '' : 's'}` : ''}
          </div>
        </button>
        {/* Library scope pill — sits next to the thread selector,
            tells the user at a glance whether the library is feeding
            this chat and how many docs match the current filter. */}
        {libraryDocs.length > 0 && (
          <button onClick={() => setShowLibraryPicker(v => !v)}
            title={libraryFilters.enabled
              ? `Library scope: ${libraryMatchingDocs.length} doc${libraryMatchingDocs.length === 1 ? '' : 's'} in scope. Click to refine.`
              : 'Library is OFF for this chat. Click to enable + scope.'}
            style={{
              background:   libraryFilters.enabled ? 'rgba(167,139,250,0.18)' : 'rgba(255,255,255,0.04)',
              border:      `1px solid ${libraryFilters.enabled ? 'rgba(167,139,250,0.5)' : 'rgba(255,255,255,0.15)'}`,
              color:        libraryFilters.enabled ? '#A78BFA' : 'rgba(255,255,255,0.45)',
              borderRadius: 999, padding: '3px 9px', fontSize: 9, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}>
            📚 Library {libraryFilters.enabled ? `(${libraryMatchingDocs.length}/${libraryDocs.length})` : 'off'}
          </button>
        )}
        {showThreadMenu && (
          <div style={{ position: 'absolute', top: '100%', left: 14, right: 14, marginTop: 4, background: 'rgba(13,27,42,0.99)', border: '1px solid rgba(167,139,250,0.4)', borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 10, maxHeight: 320, overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, flex: 1 }}>Conversations</span>
              <button onClick={startNewThread}
                style={{ background: 'rgba(232,119,34,0.18)', border: '1px solid rgba(232,119,34,0.45)', color: '#E87722', borderRadius: 5, padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase' }}>
                + New
              </button>
            </div>
            {threads.length === 0 ? (
              <div style={{ padding: 12, fontSize: 11, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>No conversations yet.</div>
            ) : threads.map(t => {
              const isActive = t.id === activeThreadId;
              const isRenaming = renamingId === t.id;
              const stamp = t.last_message_at
                ? new Date(t.last_message_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                : 'No messages yet';
              return (
                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderTop: `1px solid ${C.border}`, background: isActive ? 'rgba(167,139,250,0.10)' : 'transparent' }}>
                  {isRenaming ? (
                    <input value={renameValue} onChange={e => setRenameValue(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); } }}
                      autoFocus
                      style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(167,139,250,0.5)', color: '#fff', borderRadius: 5, padding: '4px 8px', fontSize: 11 }} />
                  ) : (
                    <button onClick={() => switchThread(t.id)} title={t.title}
                      style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                      <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>{stamp}</div>
                    </button>
                  )}
                  {isRenaming ? (
                    <button onClick={commitRename}
                      style={{ background: '#34D399', border: 'none', color: '#04231a', borderRadius: 4, padding: '3px 8px', fontSize: 9, fontWeight: 700, cursor: 'pointer' }}>Save</button>
                  ) : (
                    <>
                      <button onClick={() => beginRename(t)} title="Rename"
                        style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 4, padding: '3px 7px', fontSize: 9, cursor: 'pointer' }}>✎</button>
                      <button onClick={() => removeThread(t)} title="Delete"
                        style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 4, padding: '3px 7px', fontSize: 9, cursor: 'pointer' }}>×</button>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Library scope picker — slide-down panel under the header */}
      {showLibraryPicker && (
        <div style={{ borderBottom: `1px solid ${C.border}`, background: 'rgba(167,139,250,0.04)', padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)', flex: 1 }}>
              📚 Library scope
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!libraryFilters.enabled}
                onChange={e => setLibraryFilters(f => ({ ...f, enabled: e.target.checked }))}
                style={{ accentColor: '#A78BFA' }} />
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: libraryFilters.enabled ? '#A78BFA' : 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {libraryFilters.enabled ? 'On' : 'Off'}
              </span>
            </label>
            <button onClick={() => setLibraryFilters(DEFAULT_LIB_FILTERS)}
              title="Clear filters and turn library off"
              style={{ marginLeft: 8, background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.55)', borderRadius: 4, padding: '3px 8px', fontSize: 9, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              Reset
            </button>
            <button onClick={() => setShowLibraryPicker(false)}
              style={{ marginLeft: 4, background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
          </div>
          {libraryFilters.enabled && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 6 }}>
              <div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 6 }}>Sectors</div>
                {allLibSectors.length === 0 ? (
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>— none in library —</div>
                ) : allLibSectors.map(s => (
                  <label key={s} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={libraryFilters.sectors.includes(s)}
                      onChange={() => setLibraryFilters(f => ({ ...f, sectors: toggleArr(f.sectors, s) }))} />
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>{s}</span>
                  </label>
                ))}
              </div>
              <div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 6 }}>Clients</div>
                {allLibClients.length === 0 ? (
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>— none in library —</div>
                ) : allLibClients.map(c => (
                  <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={libraryFilters.clients.includes(c)}
                      onChange={() => setLibraryFilters(f => ({ ...f, clients: toggleArr(f.clients, c) }))} />
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>{c}</span>
                  </label>
                ))}
              </div>
              <div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 6 }}>Doc types</div>
                {allLibDocTypes.length === 0 ? (
                  <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>— none in library —</div>
                ) : allLibDocTypes.map(t => (
                  <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', cursor: 'pointer' }}>
                    <input type="checkbox" checked={libraryFilters.doc_types.includes(t)}
                      onChange={() => setLibraryFilters(f => ({ ...f, doc_types: toggleArr(f.doc_types, t) }))} />
                    <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.75)' }}>{t}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
          {libraryFilters.enabled && (
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 8 }}>
              <strong style={{ color: '#A78BFA' }}>{libraryMatchingDocs.length}</strong> doc{libraryMatchingDocs.length === 1 ? '' : 's'} in scope · server caps the attach at <strong>10</strong> per turn (winning + recent first).
            </div>
          )}
        </div>
      )}

      <div ref={scrollRef} style={{ padding: 14, maxHeight: 480, minHeight: 240, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.length === 0 ? (
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6, textAlign: 'center', padding: 20 }}>
            Ask the coach about this opportunity. Try: <em>"Score our win probability"</em> or <em>"What gaps should I close before submitting?"</em>
          </div>
        ) : messages.map(m => (
          <div key={m.id} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '8px 12px', borderRadius: 10, background: m.role === 'user' ? 'rgba(232,119,34,0.16)' : 'rgba(167,139,250,0.10)', border: `1px solid ${m.role === 'user' ? 'rgba(232,119,34,0.3)' : 'rgba(167,139,250,0.3)'}`, fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: '#fff', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
            {m.content}
          </div>
        ))}
        {sending && (
          <div style={{ alignSelf: 'flex-start', padding: '8px 12px', borderRadius: 10, background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.3)', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontStyle: 'italic' }}>
            thinking…
          </div>
        )}
      </div>

      <div style={{ borderTop: `1px solid ${C.border}`, padding: 10, background: 'rgba(255,255,255,0.02)', display: 'flex', gap: 6 }}>
        <textarea value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Ask the coach…" rows={2}
          style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", resize: 'none', outline: 'none' }} />
        <button onClick={send} disabled={sending || !input.trim()}
          style={{ background: sending || !input.trim() ? 'rgba(232,119,34,0.3)' : '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '0 14px', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', cursor: sending || !input.trim() ? 'not-allowed' : 'pointer' }}>
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}

function Field({ label, required = false, children }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, fontFamily: "'Montserrat',sans-serif" }}>
        {label}{required && <span style={{ color: '#E87722' }}> *</span>}
      </label>
      {children}
    </div>
  );
}
function modalInput(C) {
  return {
    background: 'rgba(255,255,255,0.05)',
    border: `1px solid ${C.border}`,
    color: '#fff',
    borderRadius: 6,
    padding: '8px 10px',
    fontSize: 12,
    fontFamily: "'Montserrat',sans-serif",
    width: '100%',
  };
}

function DealRoom({ C, currentUser, users, companies, showToast }) {
  const [deals,      setDeals]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setDeals(await loadDeals());
    setLoading(false);
  }, []);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { refresh(); }, [refresh]);

  // Seller side = internal staff (no company assignment, or marked as 40Geo).
  // Reviewer side = users tagged with another company in the Company Manager.
  const isSellerSide = !currentUser?.company || currentUser.company === '40Geo';

  // ── Create deal ────────────────────────────────────────────────
  const createDeal = async (form) => {
    const term = Math.max(1, Math.min(5, Number(form.term_years) || 3));
    // Default even-split tranche with remainder on the last year.
    const base = Math.floor(100 / term);
    const tranche_split = Array.from({ length: term }, (_, i) =>
      i === term - 1 ? 100 - base * (term - 1) : base
    );
    const draft = {
      name:             form.name,
      reviewer_company: form.reviewer_company || null,
      description:      form.description || null,
      deal_value:       Number(form.deal_value) || 10_000_000,    // company valuation
      buy_pct:          Number(form.buy_pct) || 100,
      currency:         form.currency || 'USD',
      upfront_pct:      Number(form.upfront_pct) || 50,
      term_years:       term,
      tranche_split,
      year1_target:     form.year1_target != null ? Number(form.year1_target) : null,
      year2_target:     form.year2_target != null ? Number(form.year2_target) : null,
      year3_target:     form.year3_target != null ? Number(form.year3_target) : null,
      year4_target:     form.year4_target != null ? Number(form.year4_target) : null,
      year5_target:     form.year5_target != null ? Number(form.year5_target) : null,
      org_chart:        DEFAULT_ORG_CHART,
      strategy:         DEFAULT_STRATEGY,
    };
    const saved = await saveDeal(draft, currentUser.orgId, currentUser.id);
    if (!saved) { showToast('❌ Failed to create deal'); return; }
    await bulkInsertDiligenceRequests(saved.id, DEFAULT_DILIGENCE_TEMPLATE);
    setShowCreate(false);
    await refresh();
    setSelectedId(saved.id);
    showToast(`✓ Deal created — ${DEFAULT_DILIGENCE_TEMPLATE.length} diligence items prefilled`);
  };

  // ── Detail or list ─────────────────────────────────────────────
  const selected = deals.find(d => d.id === selectedId);

  if (loading) {
    return <div style={{ padding: 24, color: 'rgba(255,255,255,0.45)' }}>Loading deals…</div>;
  }

  if (selected) {
    return (
      <DealDetail
        C={C}
        currentUser={currentUser}
        users={users}
        deal={selected}
        onBack={() => { setSelectedId(null); refresh(); }}
        showToast={showToast}
      />
    );
  }

  return (
    <div style={{ animation: 'fadeUp 0.4s ease' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 16, fontWeight: 700, color: '#fff' }}>Deal Room</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
            M&amp;A diligence, file exchange, earn-out modelling
          </div>
        </div>
        <div style={{ flex: 1 }} />
        {isSellerSide && (
          <button onClick={() => setShowCreate(true)}
            style={{ background: '#E87722', border: 'none', borderRadius: 6, color: '#fff', padding: '8px 14px', fontSize: 12, fontWeight: 700, fontFamily: "'Montserrat',sans-serif", cursor: 'pointer' }}>
            + New deal
          </button>
        )}
      </div>

      {deals.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'rgba(255,255,255,0.4)', fontSize: 13, border: `1px dashed ${C.border}`, borderRadius: 8 }}>
          No deals yet. {isSellerSide ? 'Click + New deal to start.' : 'Your reviewer access hasn’t been linked to a deal yet — ask the deal owner.'}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
          {deals.map(d => (
            <div key={d.id} onClick={() => setSelectedId(d.id)}
              style={{ padding: 16, borderRadius: 10, background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700, color: '#fff', flex: 1 }}>{d.name}</div>
                <span style={{ fontSize: 9, color: '#34D399', textTransform: 'uppercase', letterSpacing: '0.08em', background: 'rgba(52,211,153,0.1)', padding: '2px 6px', borderRadius: 3 }}>{d.status}</span>
              </div>
              {d.reviewer_company && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 6 }}>
                  Reviewer: <strong>{d.reviewer_company}</strong>
                </div>
              )}
              {/* Valuation / Buy / Purchase ONLY visible to seller-side
                  users. Reviewers (e.g. APEM) see deal metadata only —
                  the numbers live behind the Economics tab and are
                  unhidden when the relationship moves to that stage. */}
              {isSellerSide && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 12 }}>
                  <div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Valuation</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: "'Montserrat',sans-serif" }}>{fmtMoney(d.deal_value, d.currency)}</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Buy</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: "'Montserrat',sans-serif" }}>{d.buy_pct ?? 100}%</div>
                  </div>
                  <div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em', textTransform: 'uppercase' }}>Purchase</div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: '#E87722', fontFamily: "'Montserrat',sans-serif" }}>
                      {fmtMoney(Number(d.deal_value) * (Number(d.buy_pct ?? 100) / 100), d.currency)}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <DealCreateForm
          C={C}
          companies={companies}
          onCancel={() => setShowCreate(false)}
          onCreate={createDeal}
        />
      )}
    </div>
  );
}

// ── New-deal modal ───────────────────────────────────────────────
function DealCreateForm({ C, companies, onCancel, onCreate }) {
  const [draft, setDraft] = useState({
    name: 'APEM Group — Acquisition / Partial Buy',
    reviewer_company: 'APEM Group',
    description: '',
    deal_value: 10_000_000,   // company valuation
    buy_pct: 51,              // controlling stake
    currency: 'USD',
    upfront_pct: 50,
    term_years: 3,
    year1_target: 8_000_000,
    year2_target: 10_000_000,
    year3_target: 12_000_000,
    year4_target: null,
    year5_target: null,
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setDraft(d => ({ ...d, [k]: v }));
  const inp = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%' };
  const lbl = { fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4, display: 'block', fontFamily: "'Montserrat',sans-serif" };

  const submit = async () => {
    if (!draft.name.trim()) return;
    setSaving(true);
    await onCreate(draft);
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onCancel}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#0d1b2a', border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 4 }}>New deal</div>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.45)', marginBottom: 18 }}>
          Diligence template (Rashma's 8 items) will prefill automatically.
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Deal name</label>
            <input value={draft.name} onChange={e => set('name', e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>Reviewer company</label>
            <select value={draft.reviewer_company || ''} onChange={e => set('reviewer_company', e.target.value || null)} style={inp}>
              <option value="">— none —</option>
              {(companies || []).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={lbl}>Currency</label>
            <input value={draft.currency} onChange={e => set('currency', e.target.value)} style={inp} />
          </div>
          <div>
            <label style={lbl}>Company valuation</label>
            <input type="number" value={draft.deal_value} onChange={e => set('deal_value', Number(e.target.value))} style={inp} />
          </div>
          <div>
            <label style={lbl}>Buy %</label>
            <input type="number" min="1" max="100" value={draft.buy_pct} onChange={e => set('buy_pct', Number(e.target.value))} style={inp} />
          </div>
          <div style={{ gridColumn: '1 / -1', padding: '8px 10px', background: 'rgba(232,119,34,0.08)', border: '1px solid rgba(232,119,34,0.25)', borderRadius: 6, display: 'flex', alignItems: 'baseline', gap: 10 }}>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Purchase price</span>
            <span style={{ fontSize: 16, fontWeight: 800, color: '#E87722', fontFamily: "'Montserrat',sans-serif" }}>
              {fmtMoney(Number(draft.deal_value || 0) * (Number(draft.buy_pct || 0) / 100), draft.currency)}
            </span>
            <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>
              = valuation × buy %. Upfront and earn-out apply to this.
            </span>
          </div>
          <div>
            <label style={lbl}>Upfront %</label>
            <input type="number" value={draft.upfront_pct} onChange={e => set('upfront_pct', Number(e.target.value))} style={inp} />
          </div>
          <div>
            <label style={lbl}>Earn-out term (years)</label>
            <select value={draft.term_years} onChange={e => set('term_years', Number(e.target.value))} style={inp}>
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} {n === 1 ? 'year' : 'years'}</option>)}
            </select>
          </div>
          {Array.from({ length: draft.term_years }, (_, i) => i + 1).map(n => (
            <div key={n}>
              <label style={lbl}>Year {n} revenue target</label>
              <input type="number" value={draft[`year${n}_target`] ?? ''} onChange={e => set(`year${n}_target`, e.target.value === '' ? null : Number(e.target.value))} style={inp} />
            </div>
          ))}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Description (optional)</label>
            <textarea rows={3} value={draft.description} onChange={e => set('description', e.target.value)} style={{ ...inp, resize: 'vertical' }} />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <div style={{ flex: 1 }} />
          <button onClick={onCancel} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '8px 14px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
          <button onClick={submit} disabled={saving} style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>{saving ? 'Creating…' : 'Create deal'}</button>
        </div>
      </div>
    </div>
  );
}

// ── Deal detail (3-panel layout: Diligence + Earn-out + Economics) ──
function DealDetail({ C, currentUser, users, deal, onBack, showToast }) {
  const [tab, setTab] = useState('diligence'); // diligence | orgchart | strategy | earnout | economics | members
  const [requests, setRequests] = useState([]);
  const [reqLoading, setReqLoading] = useState(true);
  const [openReq, setOpenReq] = useState(null); // requestId expanded for files+comments
  const [dealState, setDealState] = useState(deal);
  const [members, setMembers] = useState([]);   // explicit deal_members rows
  const [membersLoaded, setMembersLoaded] = useState(false);

  // Seller = same org as the deal AND not tagged with this deal's
  // reviewer company. Staff (super_admin/admin) are always seller-side.
  // Mirrors dealEffectivePerms() so the Members tab + edit gates agree
  // with the perms object.
  const isStaff    = ['super_admin', 'admin'].includes(currentUser.role);
  const isReviewer = !!(currentUser.company && deal.reviewer_company
                        && currentUser.company === deal.reviewer_company);
  const isSeller   = isStaff || (!isReviewer && deal.org_id === currentUser.orgId);

  // Compute the effective tab permissions for the current user on this deal.
  const myMemberRow = members.find(m => m.user_id === currentUser.id) || null;
  const perms = dealEffectivePerms(currentUser, dealState, myMemberRow);

  // Refresh deal_members on mount / deal change. This drives both the
  // member-mgmt tab and the current user's effective perms.
  const refreshMembers = useCallback(async () => {
    setMembers(await loadDealMembers(deal.id));
    setMembersLoaded(true);
  }, [deal.id]);
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => {
    // Reset on deal switch so we don't briefly show the previous
    // deal's (potentially over-permissive) tab set.
    setMembersLoaded(false);
    setMembers([]);
    refreshMembers();
  }, [refreshMembers]);

  // Reviewers depend on the deal_members row for their effective perms,
  // so for them we MUST wait until members has loaded before rendering
  // the tab nav — otherwise they briefly see every tab while the
  // override is still in flight. Staff/sellers are unaffected by tab
  // perms so they render immediately.
  const tabsReady = isSeller || membersLoaded;

  const refreshReqs = useCallback(async () => {
    setReqLoading(true);
    setRequests(await loadDiligenceRequests(deal.id));
    setReqLoading(false);
  }, [deal.id]);

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => { refreshReqs(); }, [refreshReqs]);

  const saveReqField = async (req, field, value) => {
    const updated = { ...req, [field]: value };
    setRequests(prev => prev.map(r => r.id === req.id ? updated : r));
    const ok = await saveDiligenceRequest(updated);
    if (!ok) { showToast('❌ Save failed'); refreshReqs(); }
  };

  const addRequest = async () => {
    const text = prompt('New diligence item');
    if (!text?.trim()) return;
    const r = await saveDiligenceRequest({
      deal_id: deal.id, request: text.trim(), position: requests.length, status: 'open',
    });
    if (r) { setRequests(prev => [...prev, r]); }
  };

  const removeRequest = async (id) => {
    if (!confirm('Remove this diligence item?')) return;
    const ok = await dbDeleteDiligenceRequest(id);
    if (ok) setRequests(prev => prev.filter(r => r.id !== id));
  };

  const saveDealField = async (patch) => {
    const next = { ...dealState, ...patch };
    setDealState(next);
    // Partial UPDATE — only sends the changed fields, not the whole
    // dealState row. The full-row upsert was failing when the
    // in-memory state carried fields the DB schema doesn't have
    // (e.g. a migration not yet run). This also surfaces the real
    // Postgres error in the toast so we don't have to guess.
    const result = await updateDealFields(deal.id, patch);
    // NOTE: deliberately do NOT call onChanged() here. It refreshes the
    // parent deal list, which flips its loading flag and remounts this
    // whole detail view — resetting the active tab on every keystroke.
    // The list re-fetches on back-navigation instead. Local dealState is
    // the source of truth while we're in the detail view.
    if (result?.error) showToast(`❌ Save failed: ${result.error}`);
  };

  // Aggregate diligence progress.
  const progress = (() => {
    if (!requests.length) return { pct: 0, done: 0, total: 0 };
    const done = requests.filter(r => r.status === 'submitted' || r.status === 'accepted').length;
    return { pct: Math.round((done / requests.length) * 100), done, total: requests.length };
  })();

  return (
    <div style={{ animation: 'fadeUp 0.3s ease' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <button onClick={onBack} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '6px 10px', fontSize: 11, fontFamily: "'Montserrat',sans-serif", cursor: 'pointer' }}>
          ← All deals
        </button>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 18, fontWeight: 700, color: '#fff' }}>{deal.name}</div>
          {deal.reviewer_company && (
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>
              Reviewer: <strong>{deal.reviewer_company}</strong> · Status: {deal.status}
            </div>
          )}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Diligence</div>
          <div style={{ fontSize: 13, color: '#fff', fontWeight: 700 }}>{progress.done}/{progress.total} ({progress.pct}%)</div>
        </div>
      </div>

      {/* Tab nav + content rendered only AFTER members has loaded for
          reviewer users, so they don't see every tab flash through
          before the per-user override clamps them down. Sellers /
          staff don't depend on members for perms, so for them
          tabsReady is true immediately. */}
      {!tabsReady ? (
        <div style={{ padding: 28, textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: 12, fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Loading…
        </div>
      ) : (<>
      <div style={{ display: 'flex', gap: 4, marginBottom: 18, background: 'rgba(255,255,255,0.03)', padding: 4, borderRadius: 8, width: 'fit-content', flexWrap: 'wrap' }}>
        {[
          { id: 'diligence',    label: 'Diligence',    show: perms.diligence.view },
          { id: 'orgchart',     label: 'Org Chart',    show: perms.orgchart.view  },
          { id: 'strategy',     label: 'Strategy',     show: perms.strategy.view  },
          { id: 'earnout',      label: 'Earn-out',     show: perms.earnout.view   },
          { id: 'economics',    label: 'Economics',    show: perms.economics.view },
          { id: 'intelligence', label: 'Intelligence', show: isSeller             },
          { id: 'members',      label: 'Members',      show: isSeller             },
        ].filter(t => t.show).map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer', background: tab === t.id ? '#E87722' : 'transparent', color: tab === t.id ? '#fff' : 'rgba(255,255,255,0.4)' }}
          >{t.label}</button>
        ))}
      </div>

      {tab === 'diligence' && perms.diligence.view && (
        <DiligencePanel
          C={C}
          deal={deal}
          requests={requests}
          loading={reqLoading}
          openReq={openReq}
          setOpenReq={setOpenReq}
          users={users}
          currentUser={currentUser}
          isSeller={isSeller}
          canDownload={perms.diligence.download}
          perms={perms}
          onJumpToTab={setTab}
          saveReqField={saveReqField}
          addRequest={addRequest}
          removeRequest={removeRequest}
          showToast={showToast}
        />
      )}
      {tab === 'orgchart'  && perms.orgchart.view  && <OrgChart       C={C} deal={dealState} onSave={saveDealField} isSeller={isSeller} />}
      {tab === 'strategy'  && perms.strategy.view  && <StrategyPanel  C={C} deal={dealState} onSave={saveDealField} isSeller={isSeller} />}
      {tab === 'earnout'   && perms.earnout.view   && <EarnoutCalc    C={C} deal={dealState} onSave={saveDealField} isSeller={isSeller} />}
      {tab === 'economics' && perms.economics.view && <EconomicsEditor C={C} deal={dealState} onSave={saveDealField} isSeller={isSeller} />}
      {tab === 'intelligence' && isSeller && (
        <DealIntelligencePanel
          C={C}
          deal={dealState}
          currentUser={currentUser}
          onSaveDeal={saveDealField}
          showToast={showToast}
        />
      )}
      {tab === 'members'   && isSeller && (
        <DealMembersPanel
          C={C}
          deal={dealState}
          members={members}
          orgUsers={users}
          currentUser={currentUser}
          onSaveDeal={saveDealField}
          onMembersChanged={refreshMembers}
          showToast={showToast}
        />
      )}
      </>)}

      {/* AI chat — docked floating panel, persistent across tab changes.
          Only renders when the seller has flipped ai_chat_enabled on. */}
      {dealState.ai_chat_enabled && tabsReady && (
        <DealAiChat
          C={C}
          deal={dealState}
          requests={requests}
          currentUser={currentUser}
          orgUsers={users}
          isSeller={isSeller}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// HTML-escape helper for safely inlining user / AI text into the
// generated PDF export markup.
const _escHtml = (s) => String(s || '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

// Builds the full HTML document for a chat-export print window.
// Self-contained — no external assets, all styling inline so it
// renders identically in the new tab regardless of CSP / network.
function buildChatExportHtml({ dealName, threadTitle, userName, disclosure, generatedAt, summary, messages, citations }) {
  const dateStr = generatedAt.toLocaleString(undefined, {
    dateStyle: 'long', timeStyle: 'short',
  });
  const disclosureLabel = {
    vague:   'Vague (pre-LOI)',
    focused: 'Focused (post-LOI)',
    full:    'Full (active diligence)',
  }[disclosure] || disclosure;

  const summaryHtml = summary
    ? `<section class="summary">
         <h2>Executive Summary</h2>
         <div class="summary-body">${
           summary
             .split('\n')
             .filter(Boolean)
             .map(line => `<p>${_escHtml(line)}</p>`)
             .join('')
         }</div>
       </section>`
    : '';

  const transcriptHtml = messages
    .filter(m => m.content)
    .map(m => `
      <div class="msg ${m.role}">
        <div class="msg-role">${m.role === 'user' ? 'User' : 'AI Assistant'}</div>
        <div class="msg-body">${_escHtml(m.content).replace(/\n/g, '<br>')}</div>
      </div>
    `).join('');

  const citationsHtml = citations.length
    ? `<section class="citations">
         <h2>References</h2>
         <ol>${citations.map(c => `<li>${_escHtml(c)}</li>`).join('')}</ol>
       </section>`
    : '';

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${_escHtml(dealName)} — ${_escHtml(threadTitle)}</title>
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
      color: #0D1B2A; background: #fff; margin: 0;
      padding: 32px 40px 56px;
      font-size: 11pt; line-height: 1.55;
    }
    @page { size: Letter; margin: 0.6in; }
    .header {
      display: flex; align-items: center; gap: 18px;
      border-bottom: 2px solid #E87722;
      padding-bottom: 14px; margin-bottom: 22px;
    }
    .logo {
      width: 48px; flex-shrink: 0;
      display: flex; flex-direction: column; gap: 4px;
    }
    .logo .bar {
      height: 11px; border-radius: 3px;
      background: linear-gradient(90deg,#4A2E00,#9A6000 14%,#D4920A 28%,#FFDA44 45%,#FFE880 55%,#FFDA44 65%,#D4920A 82%,#9A6000 92%,#4A2E00);
    }
    .brand h1 {
      margin: 0;
      font-weight: 900; letter-spacing: -0.02em;
      font-size: 26pt; line-height: 1;
    }
    .brand h1 .accent { color: #E87722; }
    .brand .tag {
      font-size: 8pt; letter-spacing: 0.32em; font-weight: 700;
      color: #B98800; margin-top: 4px;
    }
    .header-meta {
      flex: 1; text-align: right; font-size: 9pt; color: #506276;
    }
    .doc-title h2 {
      font-size: 16pt; font-weight: 800; margin: 0 0 4px;
    }
    .doc-title .sub {
      font-size: 10pt; color: #506276; margin-bottom: 22px;
    }
    .meta-grid {
      display: grid; grid-template-columns: repeat(3, 1fr);
      gap: 10px 18px; margin-bottom: 26px;
      padding: 12px 14px; background: #F6F7F9;
      border: 1px solid #E2E6EC; border-radius: 6px;
      font-size: 9pt;
    }
    .meta-grid .k { color: #6B7A8C; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 700; font-size: 8pt; }
    .meta-grid .v { color: #0D1B2A; font-weight: 600; margin-top: 2px; }
    section { margin-bottom: 28px; page-break-inside: avoid; }
    section h2 {
      font-size: 12pt; font-weight: 800;
      color: #0D1B2A; text-transform: uppercase; letter-spacing: 0.1em;
      border-bottom: 1px solid #E2E6EC;
      padding-bottom: 6px; margin: 0 0 12px;
    }
    .summary-body p { margin: 0 0 6px; }
    .msg {
      margin: 12px 0; padding: 10px 14px; border-radius: 6px;
      page-break-inside: avoid;
    }
    .msg.user      { background: #FFF4EA; border: 1px solid #F2D5B8; }
    .msg.assistant { background: #F6F7F9; border: 1px solid #DDE2EA; }
    .msg-role {
      font-size: 8pt; font-weight: 800; letter-spacing: 0.1em;
      text-transform: uppercase; margin-bottom: 6px;
    }
    .msg.user .msg-role      { color: #E87722; }
    .msg.assistant .msg-role { color: #4F46E5; }
    .msg-body { font-size: 10.5pt; line-height: 1.6; white-space: pre-wrap; word-wrap: break-word; }
    .citations ol {
      margin: 0; padding-left: 20px; font-size: 10pt;
    }
    .citations li { margin-bottom: 4px; }
    .footer {
      margin-top: 36px; padding-top: 12px;
      border-top: 1px solid #E2E6EC;
      font-size: 8pt; color: #6B7A8C; text-align: center;
    }
    @media print {
      body { padding: 0; }
    }
  </style>
</head>
<body>
  <header class="header">
    <div class="logo" aria-hidden="true">
      <div class="bar"></div><div class="bar"></div><div class="bar"></div>
    </div>
    <div class="brand">
      <h1><span class="accent">40</span>GEO</h1>
      <div class="tag">NUGGETS · DEAL ROOM</div>
    </div>
    <div class="header-meta">
      <div><strong>Exported</strong> ${_escHtml(dateStr)}</div>
      <div>${_escHtml(userName || '')}</div>
    </div>
  </header>

  <div class="doc-title">
    <h2>${_escHtml(dealName)}</h2>
    <div class="sub">AI Conversation — ${_escHtml(threadTitle)}</div>
  </div>

  <div class="meta-grid">
    <div><div class="k">Disclosure Level</div><div class="v">${_escHtml(disclosureLabel)}</div></div>
    <div><div class="k">Messages</div><div class="v">${messages.length}</div></div>
    <div><div class="k">References Cited</div><div class="v">${citations.length}</div></div>
  </div>

  ${summaryHtml}

  <section>
    <h2>Conversation Transcript</h2>
    ${transcriptHtml}
  </section>

  ${citationsHtml}

  <div class="footer">
    Generated by Nuggets · 40Geo Deal Room AI · ${_escHtml(dateStr)}
  </div>
</body>
</html>`;
}

// ─── Proposal export HTML (Phase 5) ──────────────────────────────
// Builds a clean client-facing HTML document from the opportunity +
// its sections. Sections are emitted in sequence_idx order, skipping
// any with status 'rejected'. Sign-off status surfaces in an internal
// review-status footer (small, gray) so the PDF still reads as a
// professional proposal but the team can see what's still in flight.
function buildProposalExportHtml({ opp, sections, members, signoffs, users, images = [], generatedAt, includeReviewStatus = true, theme = null, logoDataUrl = null, coverDataUrl = null, primaryContact = null }) {
  // Group images by section so each section renders its own gallery
  const imagesBySection = {};
  for (const img of images) {
    if (!imagesBySection[img.section_id]) imagesBySection[img.section_id] = [];
    imagesBySection[img.section_id].push(img);
  }
  // Resolve theme defaults; anything missing falls back to 40Geo's look
  const T = theme || {};
  const themePrimary = T.primary_color || '#E87722';
  const themeFontId  = T.font_family   || 'sans';
  const themeFont    = ({
    sans:       '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    montserrat: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    inter:      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    serif:      'Georgia, Cambria, "Times New Roman", Times, serif',
  })[themeFontId] || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  const themeHeaderStyle = T.header_style || 'default';
  const themeTemplate    = T.template_style || 'standard';
  const themeCompanyName = T.company_name || '40Geo';
  const themeSubtitle    = T.subtitle || null;
  const themePreparedFor = T.prepared_for || null;
  const themePreparedBy  = T.prepared_by || null;
  const dateStr = generatedAt.toLocaleString(undefined, {
    dateStyle: 'long', timeStyle: 'short',
  });
  const visibleSections = sections
    .filter(s => s.status !== 'rejected')
    .sort((a, b) => (a.sequence_idx - b.sequence_idx) || a.created_at.localeCompare(b.created_at));

  // For each section, prefer content; fall back to ai_generated_content if
  // the contributor never pulled it in (with a small "AI-drafted" tag).
  const sectionsHtml = visibleSections.map((s, idx) => {
    const usingAi = (!s.content || !s.content.trim()) && s.ai_generated_content;
    const body    = (usingAi ? s.ai_generated_content : s.content) || '';
    const meta    = SECTION_STATUS_META[s.status] || SECTION_STATUS_META.draft;
    const author  = users.find(u => u.id === s.primary_author_user_id);
    const sectionSignoffs = signoffs.filter(so => so.section_id === s.id);
    const contribOk = sectionSignoffs.some(so => so.capacity_used === 'contributor'    && so.decision === 'approved');
    const finalOk   = sectionSignoffs.some(so => so.capacity_used === 'final_approver' && so.decision === 'approved');
    // Inline markdown — applied to escHtml'd text (so we're injecting
    // real HTML tags into already-safe text). Bold first so its **
    // doesn't get eaten by the italic pattern.
    const renderInline = (escaped) => escaped
      .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
      .replace(/(?<![*\w])\*([^*\n]+)\*(?![*\w])/g, '<em>$1</em>');

    // Block-level rendering. Each block is one of:
    //   * Subsection heading — paragraph that's just **heading**
    //   * Markdown table — | head | … | / | --- | --- | / | data | … |
    //   * Regular paragraph — with inline bold/italic + line breaks
    const renderBlock = (raw) => {
      const trimmed = raw.trim();

      // Subsection heading: a paragraph that's nothing but **Heading**
      const headingMatch = /^\*\*([^*\n]+)\*\*$/.exec(trimmed);
      if (headingMatch) {
        return `<h3 class="sect-subhead">${_escHtml(headingMatch[1].trim())}</h3>`;
      }

      const lines = raw.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines.length >= 2) {
        const looksLikeTable = lines.every(l => l.startsWith('|')) && /^\|[\s:|-]+\|?$/.test(lines[1]);
        if (looksLikeTable) {
          const splitCells = (line) => line.replace(/^\|/, '').replace(/\|$/, '').split('|').map(c => c.trim());
          const header = splitCells(lines[0]);
          const rows   = lines.slice(2).map(splitCells);
          const headHtml = `<thead><tr>${header.map(h => `<th>${renderInline(_escHtml(h))}</th>`).join('')}</tr></thead>`;
          const bodyHtml = `<tbody>${rows.map(r => `<tr>${r.map(c => `<td>${renderInline(_escHtml(c))}</td>`).join('')}</tr>`).join('')}</tbody>`;
          return `<table class="sect-table">${headHtml}${bodyHtml}</table>`;
        }
      }

      return `<p>${renderInline(_escHtml(raw)).replace(/\n/g, '<br>')}</p>`;
    };
    const paragraphs = body
      .split(/\n{2,}/)
      .map(p => p.trim())
      .filter(Boolean)
      .map(renderBlock)
      .join('');
    const statusFooter = includeReviewStatus
      ? `<div class="rev-status">
           <span class="status-pill" style="background:${meta.color}22;color:${meta.color};border-color:${meta.color}55">${_escHtml(meta.label)}</span>
           ${author    ? `<span class="rev-meta">Author: ${_escHtml(author.name || author.email)}</span>` : ''}
           ${contribOk ? `<span class="rev-meta">✓ Contributor signed</span>` : ''}
           ${finalOk   ? `<span class="rev-meta">✓ Final signed</span>`        : ''}
           ${usingAi   ? `<span class="rev-meta ai-tag">AI draft — not yet finalized by author</span>` : ''}
         </div>`
      : '';
    const sectionImages = imagesBySection[s.id] || [];
    const imagesHtml = sectionImages.length === 0 ? '' : `
      <div class="sect-images">
        ${sectionImages.map(img => `
          <figure class="sect-img">
            ${img.dataUrl ? `<img src="${img.dataUrl}" alt="${_escHtml(img.alt_text || img.filename || '')}" />` : ''}
            ${img.caption ? `<figcaption>${_escHtml(img.caption)}</figcaption>` : ''}
          </figure>
        `).join('')}
      </div>`;

    // Structured pricing tables (Phase: section-level pricing builder).
    // Each table has an optional dark header bar with the title,
    // a header row with column labels, and data rows. Multiple tables
    // per section let the user group offerings (Core / Optional /
    // Add-ons / etc).
    const pricingHtml = (Array.isArray(s.pricing_tables) && s.pricing_tables.length > 0)
      ? s.pricing_tables.map(tbl => {
          const cols = Array.isArray(tbl.columns) ? tbl.columns : [];
          const rows = Array.isArray(tbl.rows)    ? tbl.rows    : [];
          if (cols.length === 0) return '';
          const headerBar = tbl.title
            ? `<div class="pricing-bar" style="background:${themePrimary};color:#fff;">${_escHtml(tbl.title)}</div>`
            : '';
          const colsHtml = `<thead><tr>${cols.map(c => `<th>${_escHtml(c)}</th>`).join('')}</tr></thead>`;
          const rowsHtml = `<tbody>${rows.map(r =>
            `<tr>${cols.map((_, i) => `<td>${_escHtml(r[i] || '').replace(/\n/g, '<br>')}</td>`).join('')}</tr>`
          ).join('')}</tbody>`;
          return `<div class="pricing-table-wrap">${headerBar}<table class="pricing-table">${colsHtml}${rowsHtml}</table></div>`;
        }).join('')
      : '';

    return `<section class="prop-section" id="section-${_escHtml(s.id)}">
      <h2 class="sect-num">${idx + 1}. ${_escHtml(s.title)}</h2>
      ${paragraphs || '<p class="empty">(This section has not been drafted yet.)</p>'}
      ${pricingHtml}
      ${imagesHtml}
      ${statusFooter}
    </section>`;
  }).join('');

  // Cover info
  const submissionDate = generatedAt.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
  const memberRoster = members.map(m => {
    const u = users.find(x => x.id === m.user_id);
    return { name: u?.name || u?.email || 'Team member', role: m.role };
  });

  return `<!doctype html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${_escHtml(opp.name)} — Proposal</title>
  <!-- Pull Montserrat / Inter from Google Fonts when the theme asks for them.
       Local fallback chain handles offline / sandboxed PDF rendering. -->
  ${themeFontId === 'montserrat' ? '<link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap" rel="stylesheet">' : ''}
  ${themeFontId === 'inter'      ? '<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800;900&display=swap" rel="stylesheet">' : ''}
  <style>
    * { box-sizing: border-box; }
    body {
      font-family: ${themeFont};
      color: #0D1B2A; background: #fff; margin: 0;
      padding: 32px 40px 56px;
      font-size: 11pt; line-height: 1.6;
    }
    @page { size: Letter; margin: 0.6in; }
    .header {
      display: flex; align-items: center; gap: 18px;
      border-bottom: 2px solid ${themePrimary};
      padding-bottom: 14px; margin-bottom: 22px;
      ${themeHeaderStyle === 'centered' ? 'justify-content: center; text-align: center;' : ''}
      ${themeHeaderStyle === 'minimal'  ? 'border-bottom-width: 1px;' : ''}
    }
    .logo { width: 48px; flex-shrink: 0; display: flex; flex-direction: column; gap: 4px; }
    .logo .bar {
      height: 11px; border-radius: 3px;
      background: linear-gradient(90deg,#4A2E00,#9A6000 14%,#D4920A 28%,#FFDA44 45%,#FFE880 55%,#FFDA44 65%,#D4920A 82%,#9A6000 92%,#4A2E00);
    }
    .logo-img { max-width: 120px; max-height: 60px; object-fit: contain; }
    .brand h1 { margin: 0; font-weight: 900; letter-spacing: -0.02em; font-size: 26pt; line-height: 1; }
    .brand h1 .accent { color: ${themePrimary}; }
    .brand .tag { font-size: 8pt; letter-spacing: 0.32em; font-weight: 700; color: ${themePrimary}; margin-top: 4px; }
    .header-meta { flex: 1; text-align: right; font-size: 9pt; color: #506276; }
    .cover-title { margin: 40px 0 30px; text-align: center; }
    .cover-image { display: block; max-width: 100%; max-height: 280px; margin: 30px auto; object-fit: contain; }
    .cover-title h2 { font-size: 26pt; font-weight: 800; margin: 0 0 8px; color: #0D1B2A; }
    .cover-title .client { font-size: 14pt; color: #506276; font-weight: 500; }
    .cover-title .date { margin-top: 14px; font-size: 10pt; color: #899AAB; letter-spacing: 0.06em; text-transform: uppercase; }
    .cover-meta {
      display: grid; grid-template-columns: 1fr 1fr; gap: 16px;
      max-width: 520px; margin: 30px auto 50px;
      border: 1px solid #E5EAF0; padding: 18px; border-radius: 6px;
    }
    .cover-meta .k { font-size: 8pt; letter-spacing: 0.08em; text-transform: uppercase; color: #899AAB; }
    .cover-meta .v { font-size: 11pt; color: #0D1B2A; font-weight: 600; margin-top: 3px; }
    .toc { padding: 20px; border: 1px solid #E5EAF0; border-radius: 6px; margin-bottom: 36px; page-break-after: always; }
    .toc h3 { font-size: 11pt; letter-spacing: 0.1em; text-transform: uppercase; color: #506276; margin: 0 0 12px; font-weight: 700; }
    .toc ol { margin: 0; padding-left: 20px; }
    .toc li { font-size: 11pt; padding: 4px 0; color: #0D1B2A; }
    .prop-section { margin: 0 0 30px; page-break-inside: avoid; }
    .sect-images { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin: 12px 0; }
    .sect-img { margin: 0; padding: 0; page-break-inside: avoid; }
    .sect-img img { width: 100%; height: auto; border-radius: 4px; border: 1px solid #E5EAF0; display: block; }
    .sect-img figcaption { font-size: 8pt; color: #506276; margin-top: 4px; line-height: 1.4; font-style: italic; }
    .prop-section h2.sect-num {
      font-size: 16pt; font-weight: 800;
      color: #0D1B2A; margin: 0 0 12px;
      padding-bottom: 6px; border-bottom: 2px solid ${themePrimary};
    }
    .prop-section h3.sect-subhead {
      font-size: 12pt; font-weight: 700;
      color: ${themePrimary}; margin: 18px 0 6px;
      letter-spacing: 0.01em;
    }
    .prop-section p { margin: 0 0 10px; }
    .prop-section p.empty { color: #C1C8D0; font-style: italic; }
    .prop-section strong { font-weight: 700; color: #0D1B2A; }
    .prop-section em { font-style: italic; }
    .sect-table {
      width: 100%; border-collapse: collapse; margin: 12px 0;
      font-size: 10pt; page-break-inside: avoid;
    }
    .sect-table th, .sect-table td {
      border: 1px solid #E5EAF0; padding: 6px 10px; text-align: left; vertical-align: top;
    }
    .sect-table th {
      background: ${themePrimary}15; color: #0D1B2A; font-weight: 700;
      font-size: 9pt; letter-spacing: 0.04em; text-transform: uppercase;
    }
    .sect-table tr:nth-child(even) td { background: #F8FAFC; }

    /* ─── structured pricing tables ──────────────────────────────── */
    .pricing-table-wrap { margin: 14px 0; page-break-inside: avoid; }
    .pricing-bar {
      padding: 8px 14px; font-weight: 700; font-size: 11pt;
      letter-spacing: 0.02em; line-height: 1.3;
      border-radius: 3px 3px 0 0;
      text-align: center;
    }
    .pricing-table {
      width: 100%; border-collapse: collapse;
      font-size: 10pt;
      border: 1px solid #D5DCE4;
    }
    .pricing-table th {
      background: ${themePrimary}; color: #fff;
      padding: 8px 12px; text-align: center;
      font-weight: 700; font-size: 9.5pt;
      border-right: 1px solid rgba(255,255,255,0.25);
    }
    .pricing-table th:last-child { border-right: none; }
    .pricing-table td {
      padding: 10px 12px; border: 1px solid #D5DCE4;
      vertical-align: top; line-height: 1.45;
      color: #0D1B2A;
    }
    .pricing-table tr:nth-child(even) td { background: #F8FAFC; }
    .rev-status {
      margin-top: 12px; padding-top: 8px;
      border-top: 1px dashed #E5EAF0;
      font-size: 8pt; color: #899AAB;
      display: flex; gap: 10px; flex-wrap: wrap; align-items: center;
    }
    .status-pill {
      display: inline-block; padding: 2px 7px; border-radius: 3px;
      font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
      border: 1px solid;
    }
    .rev-meta { color: #899AAB; }
    .rev-meta.ai-tag { color: #B98800; font-style: italic; }
    .team-section { margin-top: 36px; padding-top: 18px; border-top: 1px solid #E5EAF0; }
    .team-section h3 { font-size: 10pt; letter-spacing: 0.1em; text-transform: uppercase; color: #506276; margin: 0 0 10px; }
    .team-list { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; font-size: 10pt; color: #0D1B2A; }
    .team-list .name { font-weight: 600; }
    .team-list .role { color: #899AAB; }
    .footer { margin-top: 40px; padding-top: 12px; border-top: 1px solid #E5EAF0; font-size: 8pt; color: #899AAB; text-align: center; }

    /* ─── cover_first template (Oxy-style) ──────────────────────── */
    /* NOTE: page-break-after on .cover-first was producing blank
       PDFs when html2pdf's 'avoid-all' mode interacted with the
       forced break. Sections-after-cover use page-break-before on
       the first section instead — same visual outcome, no blank
       page risk. */
    .cover-first { padding-top: 8px; margin-bottom: 24px; color: #0D1B2A; }
    .cover-first .cf-title { color: ${themePrimary}; font-size: 26pt; font-weight: 800; line-height: 1.15; letter-spacing: -0.01em; margin: 0 0 8px; }
    .cover-first .cf-sub   { font-size: 10pt; color: #506276; line-height: 1.5; margin: 6px 0 24px; }
    .cover-first .cf-image-frame { width: 100%; margin: 18px 0; }
    .cover-first .cf-image-frame img { width: 100%; max-height: 380px; object-fit: cover; border-radius: 4px; display: block; }
    .cover-first .cf-prepared {
      display: grid; grid-template-columns: 1fr 1fr; gap: 24px;
      margin-top: 32px; padding-top: 18px; border-top: 1px solid #E5EAF0;
    }
    .cover-first .cf-prepared h4 {
      margin: 0 0 8px; font-size: 11pt; font-weight: 700; color: #0D1B2A;
    }
    .cover-first .cf-prepared .cf-block {
      font-size: 10pt; color: #0D1B2A; line-height: 1.55; white-space: pre-wrap;
    }
    .cover-first .cf-prepared .cf-block .cf-emph { font-weight: 700; }
    .cover-first .cf-prepared .cf-by-logo img { max-width: 140px; max-height: 50px; object-fit: contain; margin-bottom: 8px; }
    .cover-first .cf-confidentiality { margin-top: 32px; font-size: 7pt; color: #899AAB; line-height: 1.5; text-align: center; padding: 0 30px; }

    /* ─── minimal template ─────────────────────────────────────── */
    .minimal-cover { padding: 24px 0 16px; border-bottom: 1px solid #E5EAF0; margin-bottom: 24px; }
    .minimal-cover h1 { font-size: 22pt; font-weight: 800; color: #0D1B2A; margin: 0 0 4px; }
    .minimal-cover .min-sub { font-size: 10pt; color: #506276; }
  </style>
</head>
<body>
  <header class="header">
    ${logoDataUrl
      ? `<img class="logo-img" src="${logoDataUrl}" alt="Logo" />`
      : `<div class="logo"><div class="bar"></div><div class="bar"></div><div class="bar"></div><div class="bar"></div></div>`}
    ${logoDataUrl ? `<div class="brand"><div class="tag">PROPOSAL</div></div>` : `<div class="brand">
      ${themeCompanyName === '40Geo'
        ? '<h1>40<span class="accent">Geo</span></h1>'
        : `<h1>${_escHtml(themeCompanyName)}</h1>`}
      <div class="tag">PROPOSAL</div>
    </div>`}
    <div class="header-meta">
      <div><strong>Generated</strong> ${_escHtml(dateStr)}</div>
    </div>
  </header>

  ${(() => {
    // Renders the cover + body content based on the picked template.
    // Wrapped in try/catch so a bug in any one variant can't return
    // an empty string (which produced a blank PDF in the wild).
    const renderStandardCover = () => `<div class="cover-title">
        <h2>${_escHtml(opp.name)}</h2>
        <div class="client">${_escHtml(opp.client_name || 'Client')}</div>
        <div class="date">${_escHtml(submissionDate)}</div>
      </div>
      ${coverDataUrl ? `<img class="cover-image" src="${coverDataUrl}" alt="Cover image" />` : ''}
      <div class="cover-meta">
        ${opp.estimated_value ? `<div><div class="k">Estimated value</div><div class="v">$${Number(opp.estimated_value).toLocaleString()} ${_escHtml(opp.currency || 'USD')}</div></div>` : ''}
        ${opp.decision_date   ? `<div><div class="k">Target decision</div><div class="v">${_escHtml(opp.decision_date)}</div></div>` : ''}
        ${opp.source          ? `<div><div class="k">Source</div><div class="v">${_escHtml(opp.source)}</div></div>` : ''}
        ${opp.proposal_tone   ? `<div><div class="k">Audience</div><div class="v">${_escHtml(opp.proposal_tone)}</div></div>` : ''}
      </div>
      ${visibleSections.length > 0 ? `
      <section class="toc">
        <h3>Contents</h3>
        <ol>
          ${visibleSections.map(s => `<li>${_escHtml(s.title)}</li>`).join('')}
        </ol>
      </section>` : ''}
      ${sectionsHtml}`;

    try {
      if (themeTemplate === 'cover_first') {
        // Oxy-style: title → subtitle → cover image → Prepared for / by → sections.
        // Build the Prepared-for block defensively so missing contact fields
        // don't blow up the whole render.
        const contactName = primaryContact
          ? (primaryContact.name || [primaryContact.first_name, primaryContact.last_name].filter(Boolean).join(' ') || '')
          : '';
        const autoPreparedFor = [
          opp.client_name || 'Client',
          contactName ? `Attn: ${contactName}${primaryContact?.title ? `, ${primaryContact.title}` : ''}` : null,
          primaryContact?.company || null,
          primaryContact?.address || null,
          primaryContact?.phone ? `Phone: ${primaryContact.phone}` : null,
          primaryContact?.email || null,
        ].filter(Boolean).join('\n');
        const preparedFor = themePreparedFor || autoPreparedFor;
        const preparedBy  = themePreparedBy || `${themeCompanyName}, LLC.${memberRoster.length > 0 ? `\n${memberRoster[0].name}` : ''}`;

        return `<div class="cover-first">
          <h1 class="cf-title">${_escHtml(opp.name)}</h1>
          ${themeSubtitle ? `<div class="cf-sub">${_escHtml(themeSubtitle)}</div>` : ''}
          ${coverDataUrl ? `<div class="cf-image-frame"><img src="${coverDataUrl}" alt="Cover image" /></div>` : ''}
          <div class="cf-prepared">
            <div>
              <h4>Prepared for:</h4>
              <div class="cf-block">${_escHtml(preparedFor)}</div>
            </div>
            <div>
              <h4>Prepared by:</h4>
              ${logoDataUrl ? `<div class="cf-by-logo"><img src="${logoDataUrl}" alt="Logo" /></div>` : ''}
              <div class="cf-block">${_escHtml(preparedBy)}</div>
            </div>
          </div>
          <div class="cf-confidentiality">
            The content of this document is the exclusive property of ${_escHtml(themeCompanyName)}, LLC. It has been provided for the purpose for which it is supplied and is not for general release or disclosure. The recipient should take all measures to ensure that the contents are only disclosed to those persons having a legitimate right to know, and that this document is not copied in whole or in part or disclosed to third parties without the express written authority of ${_escHtml(themeCompanyName)}, LLC.
          </div>
        </div>
        ${sectionsHtml}`;
      }
      if (themeTemplate === 'minimal') {
        return `<div class="minimal-cover">
          <h1>${_escHtml(opp.name)}</h1>
          <div class="min-sub">${_escHtml(opp.client_name || 'Client')} · ${_escHtml(submissionDate)}</div>
        </div>
        ${sectionsHtml}`;
      }
      return renderStandardCover();
    } catch (err) {
      // If any variant throws, fall back to the standard layout so
      // the user gets SOMETHING in their PDF instead of a blank page.
      console.error('[proposal-export] template render failed, falling back to standard:', err);
      return renderStandardCover();
    }
  })()}

  <!-- Team roster + tool attribution removed from client-facing PDF.
       Both were internal context that doesn't belong on the buyer's
       desk. Per-page footer (page numbers + optional confidentiality
       statement) is drawn directly onto each PDF page by jsPDF in
       the post-render pass. -->
</body>
</html>`;
}

// ─── Deal Room AI Chat — floating dockable panel ──────────────────
// Renders bottom-right of DealDetail when deal.ai_chat_enabled is on.
// Persistent across tab changes (lives in DealDetail, outside the tab
// content switcher). Each user has their own conversation thread per
// deal — pulled from deal_ai_messages via per-user RLS.
function DealAiChat({ C, deal, requests, currentUser, orgUsers = [], isSeller, showToast }) {
  const [open, setOpen]         = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [sending, setSending]   = useState(false);
  const [pickingFiles, setPickingFiles] = useState(false);
  const [pickedFiles,  setPickedFiles]  = useState([]); // [{id, filename}]
  const [filesByReq, setFilesByReq]     = useState(new Map());
  const [lastError, setLastError]       = useState(null);
  // Intel-docs auto-attach: when on, every AI-enabled doc on the
  // deal's Intelligence tab rides along with each chat turn. Toggle
  // off for quick / cheaper questions that don't need them.
  // Persisted per-deal in localStorage so the user's choice sticks.
  const [includeIntelDocs, setIncludeIntelDocs] = useState(() => {
    if (typeof window === 'undefined') return true;
    const raw = window.localStorage.getItem(`dealAiChat:${deal.id}:includeIntelDocs`);
    return raw === null ? true : raw === 'true';
  });
  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(`dealAiChat:${deal.id}:includeIntelDocs`, String(includeIntelDocs));
  }, [includeIntelDocs, deal.id]);
  const [intelDocCount, setIntelDocCount] = useState(0);
  // Only seller-side users have intel docs at all; for reviewers we
  // hide the toggle entirely.
  useEffect(() => {
    if (!isSeller) { setIntelDocCount(0); return; }
    let cancelled = false;
    (async () => {
      const intel = await loadDealIntel(deal.id);
      if (cancelled) return;
      const ready = (intel.documents || []).filter(d => d.ai_enabled && (d.anthropic_file_id || d.extracted_content)).length;
      setIntelDocCount(ready);
    })();
    return () => { cancelled = true; };
  }, [deal.id, isSeller]);
  // Panel dimensions are persisted to localStorage so the user's
  // preferred size sticks across navigation + sessions. Defaults
  // mirror the prior fixed values.
  const [dim, setDim] = useState(() => {
    try {
      const raw = typeof window !== 'undefined' && window.localStorage.getItem('dealAiChatDim');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed.w === 'number' && typeof parsed.h === 'number') return parsed;
      }
    } catch {}
    return { w: 420, h: 540 };
  });
  const scrollRef = useRef(null);
  const dragStateRef = useRef(null);

  // Thread state. activeThreadId is the conversation currently shown
  // and target of the next send. threads is the list shown in the
  // switcher dropdown. Persisted active thread per-deal via localStorage
  // so reopening the panel returns to the same conversation.
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(() => {
    if (typeof window === 'undefined') return null;
    return window.localStorage.getItem(`dealAiChat:${deal.id}:activeThreadId`) || null;
  });
  const [showThreadMenu, setShowThreadMenu] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  // When the server creates a thread on first send, we adopt its id
  // AFTER having already appended the assistant reply locally. The
  // useEffect that reacts to activeThreadId changes would otherwise
  // re-fetch from the DB and wipe those just-appended messages (per
  // the RLS-race issue that drove the local-append workaround). This
  // ref tells the effect to skip its next reload exactly once.
  const skipNextMessagesReloadRef = useRef(false);

  // Load threads list on mount / deal switch.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await loadDealAiThreads(deal.id);
      if (cancelled) return;
      setThreads(list);
      // If no active thread is remembered (or the remembered one was
      // deleted), pick the most recently updated one.
      const stillExists = list.find(t => t.id === activeThreadId);
      if (!stillExists && list.length > 0) {
        setActiveThreadId(list[0].id);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  // Load messages whenever active thread changes — UNLESS the change
  // was triggered by a fresh send that already populated messages
  // locally (see skipNextMessagesReloadRef).
  useEffect(() => {
    if (skipNextMessagesReloadRef.current) {
      skipNextMessagesReloadRef.current = false;
      console.log('[deal-ai-chat] message reload SKIPPED (post-send)', { threadId: activeThreadId });
      return;
    }
    if (!activeThreadId) { setMessages([]); return; }
    let cancelled = false;
    (async () => {
      const msgs = await loadDealAiMessages(deal.id, activeThreadId);
      if (cancelled) return;
      console.log('[deal-ai-chat] message reload', { threadId: activeThreadId, count: msgs.length });
      setMessages(msgs);
    })();
    return () => { cancelled = true; };
  }, [deal.id, activeThreadId]);

  // Persist active thread choice.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (activeThreadId) window.localStorage.setItem(`dealAiChat:${deal.id}:activeThreadId`, activeThreadId);
    else                window.localStorage.removeItem(`dealAiChat:${deal.id}:activeThreadId`);
  }, [deal.id, activeThreadId]);

  const refreshThreads = useCallback(async () => {
    const list = await loadDealAiThreads(deal.id);
    setThreads(list);
  }, [deal.id]);

  const startNewThread = async () => {
    const t = await createDealAiThread(deal.id, currentUser.id, 'New conversation');
    if (!t) { showToast?.('❌ Could not create new thread'); return; }
    await refreshThreads();
    setActiveThreadId(t.id);
    setShowThreadMenu(false);
  };

  const switchThread = async (id) => {
    setShowThreadMenu(false);
    // Always do an explicit DB reload on an explicit user thread
    // switch, even if the active id didn't change. The useEffect
    // can't be relied on here: it's skipped after fresh sends (via
    // skipNextMessagesReloadRef) and never fires at all when the
    // user clicks the same thread they're already on.
    setActiveThreadId(id);
    const msgs = await loadDealAiMessages(deal.id, id);
    console.log('[deal-ai-chat] switchThread loaded', { threadId: id, count: msgs.length });
    setMessages(msgs);
  };

  const beginRename = (t) => { setRenamingId(t.id); setRenameValue(t.title); };
  const commitRename = async () => {
    const next = (renameValue || '').trim();
    if (next && next !== threads.find(t => t.id === renamingId)?.title) {
      await renameDealAiThread(renamingId, next.slice(0, 200));
    }
    setRenamingId(null);
    setRenameValue('');
    refreshThreads();
  };

  const removeThread = async (t) => {
    if (!confirm(`Delete "${t.title}"? This permanently removes the conversation.`)) return;
    await deleteDealAiThread(t.id);
    if (activeThreadId === t.id) setActiveThreadId(null);
    refreshThreads();
  };

  const activeThread = threads.find(t => t.id === activeThreadId) || null;

  // Lazy-load the deal's file index when the user opens the picker for
  // the first time. Same query the Diligence panel uses.
  const refreshFiles = useCallback(async () => {
    const ids = (requests || []).map(r => r.id);
    if (!ids.length) { setFilesByReq(new Map()); return; }
    setFilesByReq(await loadDealFilesByRequest(ids));
  }, [requests]);
  useEffect(() => { if (pickingFiles) refreshFiles(); }, [pickingFiles, refreshFiles]);

  // Autoscroll to the latest message.
  useEffect(() => {
    if (open && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const trimmed = input.trim();
    if (!trimmed || sending) return;
    setSending(true);
    setLastError(null);
    const fileIds = pickedFiles.map(f => f.id);
    // Optimistically render the user's message so the panel doesn't feel laggy.
    const optimistic = { id: `tmp-${Date.now()}`, role: 'user', content: trimmed, file_ids: fileIds };
    setMessages(prev => [...prev, optimistic]);
    setInput('');
    const resolvedModel = AI_MODELS['deal_ai_chat'] || null;
    console.log('[deal-ai-chat] sending', { dealId: deal.id, msgLen: trimmed.length, fileIds, model: resolvedModel });
    const result = await sendDealAiMessage({
      dealId:           deal.id,
      message:          trimmed,
      fileIds,
      model:            resolvedModel,
      includeIntelDocs: includeIntelDocs,
      threadId:         activeThreadId,
    });
    console.log('[deal-ai-chat] result', result);
    if (result.error) {
      console.error('[deal-ai-chat] error:', result.error);
      const msg = String(result.error);
      setLastError(msg);
      showToast?.(`❌ AI: ${msg.slice(0, 120)}`);
      setMessages(prev => prev.filter(m => m.id !== optimistic.id));
      setInput(trimmed); // restore the input so the user can retry
    } else {
      // Append the assistant turn locally from the response. Don't
      // re-query the DB here — the server already persisted both
      // turns, and re-querying through RLS has been returning empty
      // in some sessions which would wipe the optimistic message and
      // make the panel look broken. The initial-load effect (on
      // panel mount) still hits the DB, so refresh on tab-switch /
      // re-open works correctly.
      setMessages(prev => [
        ...prev,
        {
          id:      `tmp-asst-${Date.now()}`,
          role:    'assistant',
          content: result.reply || '(no reply)',
          model:   result.model,
        },
      ]);
      setPickedFiles([]); // clear attachments after a successful turn
      // Sync the active thread id with whatever the server used (in
      // case it just created one for us) and refresh the thread list
      // so the auto-titled name shows up in the switcher. Skip the
      // message-load effect for this transition — we already have
      // the optimistic user message + server reply locally and a
      // re-fetch can return empty due to an RLS race.
      if (result.thread_id && result.thread_id !== activeThreadId) {
        skipNextMessagesReloadRef.current = true;
        setActiveThreadId(result.thread_id);
      }
      refreshThreads();
      // Token logging — match the pattern used by callAI.
      if (result.usage) {
        logTokenUsage({
          orgId:        currentUser.orgId,
          companyName:  currentUser.company || null,
          userId:       currentUser.id,
          userName:     currentUser.name,
          feature:      'deal_ai_chat',
          model:        result.model,
          inputTokens:  result.usage.input_tokens  || 0,
          outputTokens: result.usage.output_tokens || 0,
        });
      }
    }
    setSending(false);
  };

  const clearActiveThread = async () => {
    if (!activeThreadId) return;
    if (!confirm('Clear this conversation? Messages cannot be recovered.')) return;
    const ok = await clearDealAiMessages(deal.id, activeThreadId);
    if (ok) setMessages([]);
    else showToast?.('❌ Clear failed');
  };

  // ── Export to PDF ─────────────────────────────────────────────
  // Opens a print-styled HTML window with the 40Geo header, an
  // AI-generated executive summary, the full transcript, and a
  // References section pulled from "(from: …)" citations the model
  // emitted during the conversation. The user saves as PDF via the
  // browser's native print dialog.
  const [exporting, setExporting] = useState(false);

  // Share menu / modal state
  const [shareMenuOpen, setShareMenuOpen] = useState(false);
  const [shareMode, setShareMode]         = useState(null);   // 'user' | 'email' | null
  const [sharing, setSharing]             = useState(false);

  // Builds the chat PDF in-memory and either downloads it or returns
  // it as a Blob. mode='download' triggers the browser save dialog
  // and returns null; mode='blob' returns the PDF blob so callers can
  // attach it to an email.
  const buildThreadPdf = async (mode = 'download') => {
    if (!messages.length) { showToast?.('Nothing to export — start a conversation first.'); return null; }
    // 1. Summary
    let summary = '';
    try {
      const summaryMessages = messages
        .filter(m => m.content)
        .map(m => `${m.role === 'user' ? 'User' : 'AI'}: ${m.content}`)
        .join('\n\n')
        .slice(0, 60_000);
      const summaryResp = await callAI({
        systemPrompt: `You produce concise executive summaries of M&A deal-room conversations. Return 3-6 bullet points covering: the main topics discussed, key insights or conclusions the AI offered, and any open questions or action items. Use plain text bullet style ("• …"). No preamble. No conclusion. Just the bullets.`,
        messages: [{
          role: 'user',
          content: `Deal: ${deal.name}\nConversation thread: ${activeThread?.title || 'Untitled'}\n\nTranscript:\n\n${summaryMessages}\n\nProduce the bullet summary now.`,
        }],
        model: AI_MODELS['deal_ai_chat_summary'] || 'claude-haiku-4-5-20251001',
        feature: 'deal_ai_chat_summary',
        user: currentUser,
      });
      summary = (summaryResp?.content || [])
        .filter(c => c.type === 'text')
        .map(c => c.text)
        .join('\n')
        .trim();
    } catch (sErr) {
      console.warn('[export-pdf] summary call failed:', sErr?.message || sErr);
    }

    // 2. Citations
    const citationRe = /\(from:\s*([^)]+)\)/gi;
    const citationsSeen = new Set();
    const citations    = [];
    for (const m of messages) {
      if (m.role !== 'assistant' || !m.content) continue;
      let match;
      while ((match = citationRe.exec(m.content)) !== null) {
        const src = match[1].trim();
        if (!citationsSeen.has(src.toLowerCase())) {
          citationsSeen.add(src.toLowerCase());
          citations.push(src);
        }
      }
    }

    // 3. HTML
    const generatedAt = new Date();
    const html = buildChatExportHtml({
      dealName:    deal.name,
      threadTitle: activeThread?.title || 'Untitled conversation',
      userName:    currentUser.name,
      disclosure:  deal.disclosure_level || 'vague',
      generatedAt,
      summary,
      messages,
      citations,
    });

    // 4. Render off-screen + convert
    // Wrapper positioning: absolute + opacity:0 + z-index buried
    // instead of position:fixed at -99999px. The fixed-offscreen
    // trick was producing empty captures on recent Chrome —
    // layout engine skipped full layout for elements that far out
    // of viewport.
    const styleMatch = html.match(/<style>([\s\S]*?)<\/style>/i);
    const bodyMatch  = html.match(/<body>([\s\S]*?)<\/body>/i);
    const wrapper = document.createElement('div');
    // Canonical "hide from user, keep visible to html2canvas" pattern:
    //   position: absolute; left: -9999px (in flow, fully laid out,
    //   slightly off-screen — NOT -99999px which recent Chrome treats
    //   as "skip layout").
    //   opacity: 1 (NOT 0 — html2canvas captures whatever opacity
    //   says; 0 produces a blank/transparent capture which renders
    //   as a white PDF page).
    wrapper.style.position = 'absolute';
    wrapper.style.top = '0';
    wrapper.style.left = '-9999px';
    wrapper.style.width = '816px';
    wrapper.style.background = '#fff';
    wrapper.style.color = '#0D1B2A';
    wrapper.style.padding = '32px 40px 56px';
    wrapper.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
    wrapper.style.fontSize = '11pt';
    wrapper.style.lineHeight = '1.55';
    wrapper.style.opacity = '1';
    wrapper.style.pointerEvents = 'none';
    if (styleMatch) {
      const s = document.createElement('style');
      s.textContent = styleMatch[1];
      wrapper.appendChild(s);
    }
    const inner = document.createElement('div');
    inner.innerHTML = bodyMatch ? bodyMatch[1] : html;
    wrapper.appendChild(inner);
    document.body.appendChild(wrapper);
    void wrapper.offsetHeight;
    await new Promise(r => setTimeout(r, 50));

    try {
      const slug = (s) => String(s || '')
        .replace(/[^a-z0-9_\- ]/gi, '')
        .replace(/\s+/g, '_')
        .trim()
        .slice(0, 60) || 'chat';
      const dateTag = generatedAt.toISOString().slice(0, 10).replace(/-/g, '');
      const filename = `${slug(deal.name)}__${slug(activeThread?.title || 'thread')}__${dateTag}.pdf`;

      // Direct html2canvas + jsPDF — same path as the proposal
      // builder uses. See the long comment there for why we
      // abandoned html2pdf's wrapper.
      const wrapperH = wrapper.offsetHeight || 1056;
      const MAX_CANVAS_PX = 14000;
      const dynamicScale = wrapperH * 2 > MAX_CANVAS_PX
        ? Math.max(1, MAX_CANVAS_PX / wrapperH)
        : 2;
      console.log('[chat-export] dynamic scale = %s (wrapper height %dpx → canvas %dpx)',
        dynamicScale.toFixed(2), wrapperH, Math.round(wrapperH * dynamicScale));

      const { default: html2canvasFn } = await import('html2canvas');
      const { jsPDF: JsPDFCtor } = await import('jspdf');

      const canvas = await html2canvasFn(wrapper, {
        scale:            dynamicScale,
        useCORS:          true,
        backgroundColor:  '#ffffff',
        letterRendering:  true,
        logging:          false,
        width:            wrapper.offsetWidth  || 816,
        height:           wrapper.offsetHeight || wrapperH,
        x:                0,
        y:                0,
      });
      console.log('[chat-export] canvas dims: %dx%d', canvas?.width || 0, canvas?.height || 0);
      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        throw new Error('html2canvas produced an empty canvas');
      }

      // Composite onto opaque-white canvas — JPEG has no alpha,
      // so any transparent pixels in html2canvas's output would
      // encode as BLACK. Same fix as the proposal builder.
      const opaqueCanvas = document.createElement('canvas');
      opaqueCanvas.width  = canvas.width;
      opaqueCanvas.height = canvas.height;
      const opaqueCtx = opaqueCanvas.getContext('2d');
      opaqueCtx.fillStyle = '#ffffff';
      opaqueCtx.fillRect(0, 0, opaqueCanvas.width, opaqueCanvas.height);
      opaqueCtx.drawImage(canvas, 0, 0);

      const pdf = new JsPDFCtor({ unit: 'in', format: 'letter', orientation: 'portrait' });
      const pageW = 8.5, pageH = 11;
      const marginX = 0.5, marginY = 0.5;
      const imgW   = pageW - marginX * 2;
      const imgHTotal = opaqueCanvas.height * (imgW / opaqueCanvas.width);
      const pageContentH = pageH - marginY * 2;

      // Slice the canvas into page-sized chunks — same fix as the
      // proposal-coach PDF: adding one tall image with negative
      // y-offsets to scroll it between pages produced bleed/overlap
      // because jsPDF's cross-page clipping is unreliable.
      if (imgHTotal <= pageContentH) {
        pdf.addImage(opaqueCanvas.toDataURL('image/jpeg', 0.95), 'JPEG', marginX, marginY, imgW, imgHTotal);
      } else {
        const pxPerIn      = opaqueCanvas.width / imgW;
        const pageSliceHPx = Math.floor(pageContentH * pxPerIn);
        let   ySrcPx       = 0;
        let   isFirst      = true;
        while (ySrcPx < opaqueCanvas.height) {
          const sliceHPx = Math.min(pageSliceHPx, opaqueCanvas.height - ySrcPx);
          const sliceHIn = sliceHPx / pxPerIn;
          const slice = document.createElement('canvas');
          slice.width  = opaqueCanvas.width;
          slice.height = sliceHPx;
          const sctx = slice.getContext('2d');
          sctx.fillStyle = '#ffffff';
          sctx.fillRect(0, 0, slice.width, slice.height);
          sctx.drawImage(opaqueCanvas,
            0, ySrcPx, opaqueCanvas.width, sliceHPx,
            0, 0,      opaqueCanvas.width, sliceHPx);
          if (!isFirst) pdf.addPage();
          pdf.addImage(slice.toDataURL('image/jpeg', 0.95), 'JPEG', marginX, marginY, imgW, sliceHIn);
          ySrcPx += pageSliceHPx;
          isFirst = false;
        }
      }

      if (mode === 'blob') {
        const blob = pdf.output('blob');
        console.log('[chat-export] blob size: %d bytes', blob?.size || 0);
        if (!blob || blob.size === 0) throw new Error('PDF rendering produced an empty file');
        return { blob, filename };
      }
      pdf.save(filename);
      return { filename };
    } finally {
      document.body.removeChild(wrapper);
    }
  };

  const exportThreadToPdf = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const result = await buildThreadPdf('download');
      if (result) showToast?.('✓ Chat PDF downloaded');
    } catch (err) {
      showToast?.(`❌ Export failed: ${err.message || err}`);
    } finally {
      setExporting(false);
    }
  };

  // Share with another in-org user (clones the thread server-side).
  const submitShareWithUser = async ({ recipientUserId, note }) => {
    if (!activeThreadId) return;
    setSharing(true);
    const result = await shareDealAiThread({
      threadId:         activeThreadId,
      recipientUserId,
      note: note?.trim() || null,
    });
    setSharing(false);
    if (result.error) {
      showToast?.(`❌ Share failed: ${result.error}`);
      return;
    }
    showToast?.(`✓ Shared with ${result.recipient_name || 'user'} — ${result.message_count} message${result.message_count === 1 ? '' : 's'}`);
    setShareMode(null);
  };

  // Email the chat as a PDF attachment to a recipient address.
  const submitEmailChat = async ({ toEmail, customBody }) => {
    setSharing(true);
    try {
      const pdf = await buildThreadPdf('blob');
      if (!pdf?.blob) { setSharing(false); return; }
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result || '';
          const idx = String(result).indexOf(',');
          resolve(idx >= 0 ? String(result).slice(idx + 1) : String(result));
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdf.blob);
      });
      const subject = `${deal.name} — ${activeThread?.title || 'Deal Room AI conversation'}`;
      const bodyText = customBody?.trim()
        || `${currentUser.name || 'A teammate'} from 40Geo shared a Deal Room AI conversation with you.\n\nDeal: ${deal.name}\nThread: ${activeThread?.title || ''}\n\nThe full transcript + executive summary is attached.`;
      const html = `<div style="font-family:Helvetica,Arial,sans-serif;font-size:13px;color:#0D1B2A;line-height:1.55;">
        <p>${bodyText.replace(/\n/g, '<br>')}</p>
        <p style="color:#6B7A8C;font-size:11px;margin-top:24px;">Generated by 40Geo Nuggets · Deal Room AI</p>
      </div>`;
      const result = await dbSendEmail({
        to:      toEmail,
        subject,
        html,
        orgId:   currentUser.orgId,
        attachments: [
          { filename: pdf.filename, content: base64, contentType: 'application/pdf' },
        ],
      });
      if (result?.error)        showToast?.(`❌ Email failed: ${result.error}`);
      else if (result?.skipped) showToast?.(`⚠ Email skipped — ${result.error || 'check notification settings'}`);
      else                       showToast?.(`✓ Email sent to ${toEmail}`);
      setShareMode(null);
    } catch (err) {
      showToast?.(`❌ Email failed: ${err.message || err}`);
    } finally {
      setSharing(false);
    }
  };

  const toggleFile = (file) => {
    setPickedFiles(prev => {
      if (prev.find(p => p.id === file.id)) return prev.filter(p => p.id !== file.id);
      if (prev.length >= 6) { showToast?.('Max 6 files per message'); return prev; }
      return [...prev, { id: file.id, filename: file.filename }];
    });
  };

  // Flatten files-by-request into a single list for the picker.
  const allFiles = [];
  (requests || []).forEach(r => {
    const list = filesByReq.get(r.id) || [];
    list.forEach(f => allFiles.push({ ...f, requestLabel: r.request }));
  });

  // Resize handler: drag the top-left corner to make the panel
  // bigger going up and to the left. Pinned bottom-right (the close
  // bubble's home position) so the corner the user grabs is what
  // grows the panel toward the upper-left of the screen.
  const startResize = (e) => {
    e.preventDefault();
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startW: dim.w,
      startH: dim.h,
    };
    const onMove = (ev) => {
      const st = dragStateRef.current;
      if (!st) return;
      // Mouse moving up-and-left → larger panel.
      const nextW = st.startW + (st.startX - ev.clientX);
      const nextH = st.startH + (st.startY - ev.clientY);
      const minW = 320, minH = 360;
      const maxW = Math.max(minW, window.innerWidth  - 60);
      const maxH = Math.max(minH, window.innerHeight - 80);
      setDim({
        w: Math.max(minW, Math.min(maxW, nextW)),
        h: Math.max(minH, Math.min(maxH, nextH)),
      });
    };
    const onUp = () => {
      const final = dragStateRef.current
        ? { w: dim.w, h: dim.h }  // current state (last setDim won)
        : null;
      dragStateRef.current = null;
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup',   onUp);
      // Persist whatever the final size is (read fresh from setter).
      setDim(curr => {
        try { window.localStorage.setItem('dealAiChatDim', JSON.stringify(curr)); } catch {}
        return curr;
      });
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup',   onUp);
  };

  // ── Render ─────────────────────────────────────────────────
  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        title="Open AI chat"
        style={{ position: 'fixed', bottom: 20, right: 20, width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#A78BFA,#E87722)', border: 'none', color: '#fff', cursor: 'pointer', boxShadow: '0 8px 24px rgba(167,139,250,0.4)', fontSize: 24, zIndex: 1000 }}>
        🤝
      </button>
    );
  }

  return (
    <div style={{ position: 'fixed', bottom: 20, right: 20, width: dim.w, height: dim.h, maxWidth: 'calc(100vw - 40px)', maxHeight: 'calc(100vh - 40px)', display: 'flex', flexDirection: 'column', background: 'rgba(13,27,42,0.98)', border: '1px solid rgba(167,139,250,0.4)', borderRadius: 12, boxShadow: '0 12px 40px rgba(0,0,0,0.6)', zIndex: 1000, overflow: 'hidden' }}>
      {/* Resize grip (top-left corner) — drag up/left to make bigger */}
      <div onMouseDown={startResize}
        title="Drag to resize"
        style={{ position: 'absolute', top: 0, left: 0, width: 16, height: 16, cursor: 'nwse-resize', zIndex: 2, display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start', padding: 3 }}>
        <svg width="10" height="10" viewBox="0 0 10 10" style={{ opacity: 0.45 }}>
          <path d="M0 1 L0 0 L1 0 M0 5 L0 4 L4 4 L4 0 L5 0 M0 9 L0 8 L8 8 L8 0 L9 0"
            stroke="#A78BFA" strokeWidth="1.2" fill="none" strokeLinecap="round" />
        </svg>
      </div>

      {/* Header */}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'linear-gradient(90deg,rgba(167,139,250,0.18),rgba(232,119,34,0.12))', borderBottom: `1px solid ${C.border}` }}>
        <span style={{ fontSize: 16 }}>🤝</span>
        <button onClick={() => setShowThreadMenu(v => !v)}
          title="Switch / manage conversations"
          style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', textAlign: 'left', cursor: 'pointer', padding: 0 }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 800, color: '#fff', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {activeThread?.title || 'NEW CONVERSATION'}
            </span>
            <span style={{ fontSize: 9, opacity: 0.6 }}>▾</span>
          </div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {deal.name}{threads.length > 0 ? ` · ${threads.length} thread${threads.length === 1 ? '' : 's'}` : ''}
          </div>
        </button>

        {showThreadMenu && (
          <div style={{ position: 'absolute', top: '100%', left: 14, right: 14, marginTop: 4, background: 'rgba(13,27,42,0.99)', border: `1px solid rgba(167,139,250,0.4)`, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 10, maxHeight: 320, overflowY: 'auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', padding: '8px 10px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, flex: 1 }}>Conversations</span>
              <button onClick={startNewThread}
                style={{ background: 'rgba(232,119,34,0.18)', border: '1px solid rgba(232,119,34,0.45)', color: '#E87722', borderRadius: 5, padding: '3px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                + New
              </button>
            </div>
            {threads.length === 0 ? (
              <div style={{ padding: 12, fontSize: 11, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>No conversations yet — your first message starts one.</div>
            ) : (
              threads.map(t => {
                const isActive   = t.id === activeThreadId;
                const isRenaming = renamingId === t.id;
                const stamp = t.last_message_at
                  ? new Date(t.last_message_at).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'short' })
                  : 'No messages yet';
                return (
                  <div key={t.id}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderTop: `1px solid ${C.border}`, background: isActive ? 'rgba(167,139,250,0.10)' : 'transparent' }}>
                    {isRenaming ? (
                      <input value={renameValue}
                        onChange={e => setRenameValue(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') { setRenamingId(null); setRenameValue(''); } }}
                        autoFocus
                        style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid rgba(167,139,250,0.5)`, color: '#fff', borderRadius: 5, padding: '4px 8px', fontSize: 11, fontFamily: "'Montserrat',sans-serif" }} />
                    ) : (
                      <button onClick={() => switchThread(t.id)} title={t.title}
                        style={{ flex: 1, minWidth: 0, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: isActive ? 700 : 500, color: isActive ? '#fff' : 'rgba(255,255,255,0.8)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)' }}>{stamp}</div>
                      </button>
                    )}
                    {isRenaming ? (
                      <button onClick={commitRename}
                        style={{ background: '#34D399', border: 'none', color: '#04231a', borderRadius: 4, padding: '3px 8px', fontSize: 9, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>Save</button>
                    ) : (
                      <>
                        <button onClick={() => beginRename(t)} title="Rename"
                          style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 4, padding: '3px 7px', fontSize: 9, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>✎</button>
                        <button onClick={() => removeThread(t)} title="Delete"
                          style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 4, padding: '3px 7px', fontSize: 9, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>×</button>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
        {/* Intel-docs toggle (seller-side only). Click to flip between
            "include all AI-enabled intel docs as context every turn" and
            "skip intel docs this session". Status is persisted per-deal
            in localStorage so the choice survives reloads. */}
        {isSeller && intelDocCount > 0 && (
          <button onClick={() => setIncludeIntelDocs(v => !v)}
            title={includeIntelDocs
              ? `Including all ${intelDocCount} AI-enabled intel docs in chat context. Click to exclude.`
              : `Excluding intel docs from chat context. Click to include all ${intelDocCount}.`}
            style={{
              background: includeIntelDocs ? 'rgba(167,139,250,0.18)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${includeIntelDocs ? 'rgba(167,139,250,0.5)' : 'rgba(255,255,255,0.15)'}`,
              color: includeIntelDocs ? '#A78BFA' : 'rgba(255,255,255,0.45)',
              borderRadius: 999, padding: '3px 9px', fontSize: 9, fontWeight: 700, cursor: 'pointer',
              fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase',
              whiteSpace: 'nowrap',
            }}>
            📚 Intel {includeIntelDocs ? `on (${intelDocCount})` : 'off'}
          </button>
        )}
        {messages.length > 0 && (
          <button onClick={exportThreadToPdf} disabled={exporting}
            title="Export this conversation to a formatted PDF (summary + transcript + references)"
            style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.4)', color: '#60A5FA', borderRadius: 5, padding: '4px 9px', fontSize: 10, fontWeight: 700, cursor: exporting ? 'wait' : 'pointer', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {exporting ? 'Exporting…' : '📄 PDF'}
          </button>
        )}
        {/* Share button: in-app thread copy OR email PDF attachment */}
        {messages.length > 0 && (
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShareMenuOpen(v => !v)} disabled={sharing}
              title="Share this conversation with a teammate or by email"
              style={{ background: 'rgba(167,139,250,0.12)', border: '1px solid rgba(167,139,250,0.4)', color: '#A78BFA', borderRadius: 5, padding: '4px 9px', fontSize: 10, fontWeight: 700, cursor: sharing ? 'wait' : 'pointer', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              {sharing ? 'Sharing…' : '↗ Share'}
            </button>
            {shareMenuOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, background: 'rgba(13,27,42,0.99)', border: '1px solid rgba(167,139,250,0.4)', borderRadius: 6, boxShadow: '0 8px 24px rgba(0,0,0,0.5)', zIndex: 20, minWidth: 180, overflow: 'hidden' }}>
                <button onClick={() => { setShareMode('user'); setShareMenuOpen(false); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: '#fff', padding: '8px 12px', cursor: 'pointer', fontSize: 11, fontFamily: "'Montserrat',sans-serif" }}>
                  👥 Share with user…
                </button>
                <button onClick={() => { setShareMode('email'); setShareMenuOpen(false); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: '#fff', padding: '8px 12px', cursor: 'pointer', fontSize: 11, fontFamily: "'Montserrat',sans-serif", borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  📧 Email with PDF…
                </button>
              </div>
            )}
          </div>
        )}
        {messages.length > 0 && (
          <button onClick={clearActiveThread} title="Clear messages in this conversation (keeps the thread)"
            style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 11, fontFamily: "'Montserrat',sans-serif" }}>Clear</button>
        )}
        <button onClick={() => setOpen(false)} title="Minimize"
          style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0 }}>×</button>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 14, display: 'flex', flexDirection: 'column', gap: 10, minHeight: 120 }}>
        {messages.length === 0 ? (
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, fontFamily: "'Montserrat',sans-serif", lineHeight: 1.6, textAlign: 'center', padding: 20 }}>
            Ask me about the documents you've uploaded, the merger thesis, or how 40Geo's AI stack fits into APEM's portfolio.
            <div style={{ marginTop: 12, fontSize: 10, color: 'rgba(255,255,255,0.3)', fontStyle: 'italic' }}>
              Try: "Summarize the financials" or "Why is this merger good for APEM?"
            </div>
          </div>
        ) : (
          messages.map(m => (
            <div key={m.id} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '8px 12px', borderRadius: 10, background: m.role === 'user' ? 'rgba(232,119,34,0.16)' : 'rgba(167,139,250,0.10)', border: `1px solid ${m.role === 'user' ? 'rgba(232,119,34,0.3)' : 'rgba(167,139,250,0.3)'}`, fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: '#fff', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
              {m.content}
              {m.file_ids?.length > 0 && (
                <div style={{ marginTop: 4, fontSize: 9, color: 'rgba(255,255,255,0.45)', fontStyle: 'italic' }}>
                  📎 {m.file_ids.length} file{m.file_ids.length === 1 ? '' : 's'} attached
                </div>
              )}
            </div>
          ))
        )}
        {sending && (
          <div style={{ alignSelf: 'flex-start', padding: '8px 12px', borderRadius: 10, background: 'rgba(167,139,250,0.10)', border: '1px solid rgba(167,139,250,0.3)', color: 'rgba(255,255,255,0.5)', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontStyle: 'italic' }}>
            thinking…
          </div>
        )}
        {lastError && !sending && (
          <div style={{ alignSelf: 'stretch', padding: '8px 12px', borderRadius: 8, background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(239,68,68,0.4)', color: '#FCA5A5', fontFamily: "'Montserrat',sans-serif", fontSize: 11, lineHeight: 1.5, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <span style={{ fontSize: 14, lineHeight: 1 }}>⛔</span>
            <div style={{ flex: 1, wordBreak: 'break-word' }}>
              <div style={{ fontWeight: 700, marginBottom: 2 }}>Send failed</div>
              <div>{lastError}</div>
            </div>
            <button onClick={() => setLastError(null)}
              style={{ background: 'transparent', border: 'none', color: '#FCA5A5', cursor: 'pointer', fontSize: 14, padding: 0, lineHeight: 1 }}>×</button>
          </div>
        )}
      </div>

      {/* File picker drawer */}
      {pickingFiles && (
        <div style={{ borderTop: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.02)', maxHeight: 180, overflowY: 'auto', padding: '8px 12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700, flex: 1 }}>Attach diligence files (PDFs only)</span>
            <button onClick={() => setPickingFiles(false)}
              style={{ background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.6)', cursor: 'pointer', fontSize: 11, fontFamily: "'Montserrat',sans-serif" }}>Done</button>
          </div>
          {allFiles.length === 0 ? (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', padding: 8, fontStyle: 'italic' }}>No files uploaded yet.</div>
          ) : (
            allFiles.map(f => {
              const fn = (f.filename || '').toLowerCase();
              const isPdf  = (f.mime_type || '').toLowerCase().includes('pdf') || fn.endsWith('.pdf');
              const isSupported = isPdf || fn.endsWith('.docx') || fn.endsWith('.pptx') || /\.(txt|md|csv)$/.test(fn);
              const picked = !!pickedFiles.find(p => p.id === f.id);
              const aiReady = f.ai_enabled && (!!f.anthropic_file_id || !!f.extracted_content);
              // Only AI-prepped supported files are selectable in chat.
              // Un-prepped supported files are shown for context but
              // disabled — the seller flips 🤖 AI in the request first.
              const selectable = isSupported && aiReady;
              const reasonDisabled =
                !isSupported ? 'PDF / DOCX / PPTX / text only'
              : !aiReady     ? 'Enable 🤖 AI on this file in the diligence request first'
              : null;
              return (
                <label key={f.id}
                  title={reasonDisabled || ''}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', cursor: selectable ? 'pointer' : 'not-allowed', opacity: selectable ? 1 : 0.45 }}>
                  <input type="checkbox" checked={picked} disabled={!selectable} onChange={() => selectable && toggleFile(f)} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 11, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.filename}
                      {aiReady && <span title="AI-ready" style={{ marginLeft: 6, fontSize: 9, color: '#A78BFA', fontWeight: 700 }}>🤖</span>}
                    </div>
                    <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.requestLabel}
                      {isSupported && !aiReady && <span style={{ marginLeft: 6, color: 'rgba(255,184,48,0.8)' }}>(not AI-enabled)</span>}
                    </div>
                  </div>
                  {!isSupported && <span style={{ fontSize: 9, color: '#FFB830' }}>unsupported</span>}
                </label>
              );
            })
          )}
        </div>
      )}

      {/* Composer */}
      <div style={{ borderTop: `1px solid ${C.border}`, padding: 10, background: 'rgba(255,255,255,0.02)' }}>
        {pickedFiles.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
            {pickedFiles.map(f => (
              <span key={f.id} style={{ fontSize: 10, padding: '3px 8px', borderRadius: 999, background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.35)', color: '#60A5FA', fontFamily: "'Montserrat',sans-serif" }}>
                📎 {f.filename}
                <button onClick={() => toggleFile(f)}
                  style={{ background: 'transparent', border: 'none', color: '#60A5FA', cursor: 'pointer', marginLeft: 4, padding: 0, fontSize: 11 }}>×</button>
              </span>
            ))}
          </div>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => setPickingFiles(v => !v)}
            title="Attach diligence files"
            style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.35)', color: '#60A5FA', borderRadius: 6, padding: '0 10px', fontSize: 14, cursor: 'pointer' }}>
            📎
          </button>
          <textarea value={input} onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Ask about the deal, documents, or the merger thesis…"
            rows={2}
            style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", resize: 'none', outline: 'none' }} />
          <button onClick={send} disabled={sending || !input.trim()}
            style={{ background: sending || !input.trim() ? 'rgba(232,119,34,0.3)' : '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '0 14px', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', cursor: sending || !input.trim() ? 'not-allowed' : 'pointer' }}>
            {sending ? '…' : 'Send'}
          </button>
        </div>
      </div>

      {/* Share modals (rendered as portals-via-style so they overlay
          the chat panel without breaking its layout) */}
      {shareMode === 'user' && (
        <ShareWithUserModal
          C={C}
          currentUser={currentUser}
          orgUsers={orgUsers}
          busy={sharing}
          onCancel={() => setShareMode(null)}
          onSubmit={submitShareWithUser}
        />
      )}
      {shareMode === 'email' && (
        <EmailChatModal
          C={C}
          dealName={deal.name}
          threadTitle={activeThread?.title || 'Untitled'}
          busy={sharing}
          onCancel={() => setShareMode(null)}
          onSubmit={submitEmailChat}
        />
      )}
    </div>
  );
}

// Share-with-user modal — recipient picker filtered by company/org
// scope. Internal staff + 40Geo users see all org users; reviewer-
// company users (e.g. APEM) only see users tagged with the same
// company.
function ShareWithUserModal({ C, currentUser, orgUsers, busy, onCancel, onSubmit }) {
  const callerIsStaff    = ['super_admin', 'admin'].includes(currentUser.role);
  const callerIsReviewer = !!currentUser.company && !callerIsStaff;
  const eligible = (orgUsers || [])
    .filter(u => u.id !== currentUser.id)
    .filter(u => u.is_active !== false && (u.isActive !== false))
    .filter(u => callerIsReviewer ? (u.company === currentUser.company) : true);

  const [recipientId, setRecipientId] = useState('');
  const [note, setNote]               = useState('');

  return (
    <ModalShell title="Share conversation with a teammate" onCancel={onCancel}>
      {eligible.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, padding: '8px 0' }}>
          No eligible recipients found in your org{callerIsReviewer ? ` for "${currentUser.company}"` : ''}.
        </div>
      ) : (
        <>
          <label style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Recipient</label>
          <select value={recipientId} onChange={e => setRecipientId(e.target.value)}
            style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", marginBottom: 12 }}>
            <option value="">— Select a user —</option>
            {eligible.map(u => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}{u.company ? ` · ${u.company}` : ''}
              </option>
            ))}
          </select>
          <label style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Note (optional)</label>
          <textarea rows={3} value={note} onChange={e => setNote(e.target.value)}
            placeholder="Add a quick note for the recipient — they'll see it pinned at the top of the shared thread."
            style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", resize: 'vertical', marginBottom: 14 }} />
          <ModalActions onCancel={onCancel} primary={{
            label: busy ? 'Sharing…' : 'Share',
            disabled: busy || !recipientId,
            onClick: () => onSubmit({ recipientUserId: recipientId, note }),
          }} />
        </>
      )}
    </ModalShell>
  );
}

// Email-with-PDF modal — recipient address + optional message body.
function EmailChatModal({ C, dealName, threadTitle, busy, onCancel, onSubmit }) {
  const [toEmail, setToEmail]       = useState('');
  const [customBody, setCustomBody] = useState('');
  const validEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(toEmail.trim());
  return (
    <ModalShell title="Email chat as PDF" onCancel={onCancel}>
      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginBottom: 12, lineHeight: 1.5 }}>
        Sends a polished PDF transcript of <strong>{threadTitle}</strong> on <strong>{dealName}</strong> to a single recipient. Uses your org's configured Gmail credentials (Admin → Notifications).
      </div>
      <label style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Recipient email</label>
      <input type="email" value={toEmail} onChange={e => setToEmail(e.target.value)} placeholder="someone@example.com"
        style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", marginBottom: 12 }} />
      <label style={{ display: 'block', fontSize: 10, color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Message (optional)</label>
      <textarea rows={4} value={customBody} onChange={e => setCustomBody(e.target.value)}
        placeholder="Default template is used when blank."
        style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", resize: 'vertical', marginBottom: 14 }} />
      <ModalActions onCancel={onCancel} primary={{
        label: busy ? 'Sending…' : 'Send email',
        disabled: busy || !validEmail,
        onClick: () => onSubmit({ toEmail: toEmail.trim(), customBody }),
      }} />
    </ModalShell>
  );
}

function ModalShell({ title, onCancel, children }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 10500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={onCancel}>
      <div onClick={e => e.stopPropagation()}
        style={{ width: '100%', maxWidth: 460, background: '#0D1B2A', border: '1px solid rgba(167,139,250,0.4)', borderRadius: 12, padding: '20px 22px', boxShadow: '0 16px 48px rgba(0,0,0,0.7)' }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 800, color: '#fff', letterSpacing: '0.04em', marginBottom: 14, textTransform: 'uppercase' }}>{title}</div>
        {children}
      </div>
    </div>
  );
}
function ModalActions({ onCancel, primary }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
      <button onClick={onCancel} disabled={primary.disabled && primary.label.startsWith('Sending')}
        style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.7)', borderRadius: 6, padding: '7px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        Cancel
      </button>
      <button onClick={primary.onClick} disabled={primary.disabled}
        style={{ background: primary.disabled ? 'rgba(232,119,34,0.3)' : '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '7px 16px', fontSize: 11, fontWeight: 700, cursor: primary.disabled ? 'not-allowed' : 'pointer', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {primary.label}
      </button>
    </div>
  );
}

// Trigger a browser file save from a Blob. Signed URLs don't set
// Content-Disposition, so we materialize the bytes and use a
// download attribute on a synthetic anchor.
const triggerBlobDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

// File-safe slug for use in zip names and folder names.
const safeSlug = (s, max = 80) => (s || 'docs')
  .replace(/[^a-z0-9_\- ]/gi, '')
  .replace(/\s+/g, ' ')
  .trim()
  .slice(0, max) || 'docs';

// Zip a flat list of diligence files into a single archive and download
// it. Used by per-request "Download all" inside DiligenceRequestDetail
// and by per-row downloads in DiligencePanel.
const zipFilesAndDownload = async (files, archiveName) => {
  if (!files?.length) return { added: 0, ok: false };
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  let added = 0;
  for (const f of files) {
    const url = await dealFileDownloadUrl(f.storage_path);
    if (!url) continue;
    const resp = await fetch(url);
    zip.file(f.filename, await resp.blob());
    added++;
  }
  if (!added) return { added: 0, ok: false };
  const content = await zip.generateAsync({ type: 'blob' });
  triggerBlobDownload(content, archiveName);
  return { added, ok: true };
};

// Zip every file across every request on a deal, organized into one
// folder per request so the reviewer can navigate by question.
const zipAllDealFilesAndDownload = async (requests, filesByReq, dealName) => {
  const JSZip = (await import('jszip')).default;
  const zip = new JSZip();
  let added = 0;
  for (const r of requests) {
    const files = filesByReq.get(r.id) || [];
    if (!files.length) continue;
    const folder = zip.folder(safeSlug(r.request, 60));
    for (const f of files) {
      const url = await dealFileDownloadUrl(f.storage_path);
      if (!url) continue;
      const resp = await fetch(url);
      folder.file(f.filename, await resp.blob());
      added++;
    }
  }
  if (!added) return { added: 0, ok: false };
  const content = await zip.generateAsync({ type: 'blob' });
  triggerBlobDownload(content, `${safeSlug(dealName, 40)}_diligence.zip`);
  return { added, ok: true };
};

// Per-request configurable jump-to-tab. The seller picks the target
// from a dropdown in the request detail editor (or leaves it blank
// for document-only requests). When the row reaches a "data ready
// to review" status AND the reviewer can view that tab, a green
// "→ <Tab>" button appears in the row summary.
const DD_LINK_TARGETS = [
  { id: 'orgchart',  label: 'Org Chart' },
  { id: 'strategy',  label: 'Strategy'  },
  { id: 'earnout',   label: 'Earn-out'  },
  { id: 'economics', label: 'Economics' },
];
const DD_LINK_TARGET_LABEL = Object.fromEntries(DD_LINK_TARGETS.map(t => [t.id, t.label]));
const DD_LINKABLE_STATUSES = new Set(['submitted', 'accepted']);

function DiligencePanel({ C, deal, requests, loading, openReq, setOpenReq, users, currentUser, isSeller, canDownload = true, perms = null, onJumpToTab, saveReqField, addRequest, removeRequest, showToast }) {
  // filesByReq is loaded up-front so the row summary can show counts
  // (and the top-of-tab "Download all" knows what's available) without
  // waiting for each request row to be expanded.
  const [filesByReq, setFilesByReq] = useState(new Map());
  const [refreshTick, setRefreshTick] = useState(0);
  const [zippingAll, setZippingAll] = useState(false);
  const [zippingReqId, setZippingReqId] = useState(null);

  const requestIds = requests.map(r => r.id).join(',');
  useEffect(() => {
    if (!requests.length) { setFilesByReq(new Map()); return; }
    let cancelled = false;
    (async () => {
      const map = await loadDealFilesByRequest(requests.map(r => r.id));
      if (!cancelled) setFilesByReq(map);
    })();
    return () => { cancelled = true; };
  }, [requestIds, refreshTick]);

  const bumpFiles = useCallback(() => setRefreshTick(t => t + 1), []);

  const totalFiles = Array.from(filesByReq.values()).reduce((s, list) => s + list.length, 0);

  const downloadAll = async () => {
    if (!totalFiles) { showToast('No files uploaded yet.'); return; }
    setZippingAll(true);
    try {
      const { ok } = await zipAllDealFilesAndDownload(requests, filesByReq, deal.name);
      if (!ok) showToast('❌ Nothing to download');
    } catch (err) {
      showToast('❌ Zip failed: ' + err.message);
    } finally {
      setZippingAll(false);
    }
  };

  const downloadRequest = async (r) => {
    const files = filesByReq.get(r.id) || [];
    if (!files.length) { showToast('No files in this request yet.'); return; }
    setZippingReqId(r.id);
    try {
      const archiveName = `${safeSlug(deal.name, 40)}_${safeSlug(r.request, 60)}.zip`;
      const { ok } = await zipFilesAndDownload(files, archiveName);
      if (!ok) showToast('❌ Nothing to download');
    } catch (err) {
      showToast('❌ Download failed: ' + err.message);
    } finally {
      setZippingReqId(null);
    }
  };

  if (loading) return <div style={{ color: 'rgba(255,255,255,0.4)' }}>Loading requests…</div>;
  return (
    <div>
      {/* Tab-level toolbar: total file count + Download all */}
      {(requests.length > 0) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '10px 14px', background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 8 }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.04em' }}>
            <span style={{ color: '#fff', fontWeight: 700 }}>{totalFiles}</span> file{totalFiles === 1 ? '' : 's'} across <span style={{ color: '#fff', fontWeight: 700 }}>{requests.length}</span> request{requests.length === 1 ? '' : 's'}
          </div>
          {canDownload ? (
            <button onClick={downloadAll} disabled={!totalFiles || zippingAll}
              style={{ background: !totalFiles || zippingAll ? 'rgba(232,119,34,0.3)' : '#E87722', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 14px', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', cursor: !totalFiles || zippingAll ? 'not-allowed' : 'pointer' }}>
              {zippingAll ? 'Zipping…' : `⬇ Download all (${totalFiles})`}
            </button>
          ) : (
            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>
              🔒 View-only — downloads disabled
            </span>
          )}
        </div>
      )}

      {requests.map(r => {
        const meta   = DD_STATUS_META[r.status] || DD_STATUS_META.open;
        const isOpen = openReq === r.id;
        const fcount = (filesByReq.get(r.id) || []).length;
        const isZippingThis = zippingReqId === r.id;
        return (
          <div key={r.id} style={{ marginBottom: 10, border: `1px solid ${C.border}`, borderRadius: 8, background: 'rgba(255,255,255,0.03)' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '12px 1fr auto auto auto auto', gap: 12, alignItems: 'center', padding: '10px 14px', cursor: 'pointer' }}
                 onClick={() => setOpenReq(isOpen ? null : r.id)}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: meta.color, boxShadow: `0 0 6px ${meta.color}` }} />
              <div style={{ color: '#fff', fontSize: 13, fontFamily: "'Montserrat',sans-serif" }}>{r.request}</div>
              {/* File count + download + jump-to-tab link */}
              <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span title={`${fcount} file${fcount === 1 ? '' : 's'} uploaded`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999, fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: fcount > 0 ? '#fff' : 'rgba(255,255,255,0.4)', background: fcount > 0 ? 'rgba(96,165,250,0.18)' : 'rgba(255,255,255,0.05)', border: `1px solid ${fcount > 0 ? 'rgba(96,165,250,0.35)' : C.border}` }}>
                  📎 {fcount}
                </span>
                {canDownload && fcount > 0 && (
                  <button onClick={() => downloadRequest(r)} disabled={isZippingThis}
                    title={`Download ${fcount} file${fcount === 1 ? '' : 's'}`}
                    style={{ background: 'transparent', border: `1px solid ${C.border}`, color: isZippingThis ? 'rgba(255,255,255,0.3)' : '#E87722', borderRadius: 4, padding: '3px 8px', fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, cursor: isZippingThis ? 'wait' : 'pointer' }}>
                    {isZippingThis ? '…' : '⬇'}
                  </button>
                )}
                {(() => {
                  // Show "→ <Tab>" only when:
                  //   1. The seller has explicitly set request.link_tab for
                  //      this request (document-only requests leave it blank).
                  //   2. The request is in a status that signals data is
                  //      ready to review (submitted or accepted).
                  //   3. The current user has perms.<tab>.view (defaults to
                  //      true if perms is null — staff/sellers).
                  if (!onJumpToTab) return null;
                  if (!DD_LINKABLE_STATUSES.has(r.status)) return null;
                  const tabId = r.link_tab;
                  const label = tabId && DD_LINK_TARGET_LABEL[tabId];
                  if (!tabId || !label) return null;
                  if (perms && perms[tabId] && perms[tabId].view === false) return null;
                  return (
                    <button onClick={() => onJumpToTab(tabId)}
                      title={`Open in ${label}`}
                      style={{ background: 'rgba(52,211,153,0.1)', border: '1px solid rgba(52,211,153,0.35)', color: '#34D399', borderRadius: 4, padding: '3px 8px', fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                      → {label}
                    </button>
                  );
                })()}
              </div>
              <select value={r.status} onClick={e => e.stopPropagation()} onChange={e => saveReqField(r, 'status', e.target.value)}
                style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: meta.color, borderRadius: 4, padding: '4px 8px', fontSize: 10, fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase' }}>
                {Object.entries(DD_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={r.assignee_id || ''} onClick={e => e.stopPropagation()} onChange={e => saveReqField(r, 'assignee_id', e.target.value || null)}
                style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 4, padding: '4px 8px', fontSize: 10, maxWidth: 160 }}>
                <option value="">— assignee —</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              {isSeller ? (
                <button onClick={e => { e.stopPropagation(); removeRequest(r.id); }}
                  style={{ background: 'transparent', border: 'none', color: 'rgba(255,107,107,0.5)', cursor: 'pointer', fontSize: 14, padding: 0 }}>×</button>
              ) : <div />}
            </div>
            {isOpen && (
              <DiligenceRequestDetail
                C={C} deal={deal} request={r}
                currentUser={currentUser} users={users}
                canDownload={canDownload}
                isSeller={isSeller}
                onUpdate={saveReqField}
                onFilesChanged={bumpFiles}
                showToast={showToast}
              />
            )}
          </div>
        );
      })}
      {isSeller && (
        <button onClick={addRequest}
          style={{ background: 'transparent', border: `1px dashed ${C.border}`, color: 'rgba(255,255,255,0.5)', borderRadius: 6, padding: '8px 14px', fontSize: 11, cursor: 'pointer', marginTop: 6 }}>
          + Add a request
        </button>
      )}
    </div>
  );
}

function DiligenceRequestDetail({ C, deal, request, currentUser, users, canDownload = true, isSeller = false, onUpdate, onFilesChanged, showToast }) {
  const [notes, setNotes] = useState(request.notes || '');
  const [files, setFiles] = useState([]);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected]   = useState(new Set());  // file ids checked for bulk download
  const [deleteTarget, setDeleteTarget] = useState(null); // file pending delete-confirm
  const [downloadingId, setDownloadingId] = useState(null);
  const [zipping, setZipping]     = useState(false);
  const [preparingId, setPreparingId] = useState(null);   // file currently being AI-prepared

  // Toggle AI-readiness on a file. Server downloads from Storage and
  // uploads to Anthropic's Files API (or clears the linkage if going
  // from on → off). Optimistic UI: flip the local state first so the
  // checkbox responds instantly, then reconcile from the server result.
  const toggleFileAi = async (f) => {
    const nextEnabled = !f.ai_enabled;
    setPreparingId(f.id);
    setFiles(prev => prev.map(x => x.id === f.id ? { ...x, ai_enabled: nextEnabled, ai_prep_error: null } : x));
    const result = await setDiligenceFileAiEnabled(f.id, nextEnabled);
    if (result.error) {
      showToast?.(`❌ AI prep: ${result.error}`);
      setFiles(prev => prev.map(x => x.id === f.id ? { ...x, ai_enabled: nextEnabled, ai_prep_error: result.error } : x));
    } else {
      setFiles(prev => prev.map(x => x.id === f.id
        ? { ...x, ai_enabled: nextEnabled, anthropic_file_id: result.anthropic_file_id || null, ai_prep_error: null }
        : x));
      showToast?.(nextEnabled ? `✓ "${f.filename}" is AI-ready` : `↩ "${f.filename}" AI prep cleared`);
    }
    setPreparingId(null);
  };

  const refreshFiles    = useCallback(async () => setFiles(await loadDiligenceFiles(request.id)),    [request.id]);
  const refreshComments = useCallback(async () => setComments(await loadDiligenceComments(request.id)), [request.id]);

  useEffect(() => { refreshFiles(); refreshComments(); }, [refreshFiles, refreshComments]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const row = await uploadDiligenceFile(deal.id, request.id, file, currentUser.id);
    setUploading(false);
    e.target.value = ''; // reset picker so same file can be re-picked
    if (!row) { showToast('❌ Upload failed'); return; }
    setFiles(prev => [row, ...prev]);
    onFilesChanged?.();
  };

  const openFile = async (f) => {
    const url = await dealFileDownloadUrl(f.storage_path);
    if (url) window.open(url, '_blank', 'noopener');
    else showToast('❌ Could not generate view link');
  };

  const downloadOne = async (f) => {
    setDownloadingId(f.id);
    try {
      const url = await dealFileDownloadUrl(f.storage_path);
      if (!url) { showToast('❌ Could not generate download link'); return; }
      const resp = await fetch(url);
      triggerBlobDownload(await resp.blob(), f.filename);
    } catch (err) {
      showToast('❌ Download failed: ' + err.message);
    } finally {
      setDownloadingId(null);
    }
  };

  // Per-request zip: delegates to the module-scope helper so the panel
  // toolbar and the inline detail share the same code path.
  const downloadZip = async (list) => {
    if (!list.length) return;
    setZipping(true);
    try {
      const archiveName = `${safeSlug(deal.name, 40)}_${safeSlug(request.request, 60)}.zip`;
      const { ok } = await zipFilesAndDownload(list, archiveName);
      if (!ok) showToast('❌ Nothing to download');
    } catch (err) {
      showToast('❌ Zip failed: ' + err.message);
    } finally {
      setZipping(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const ok = await deleteDiligenceFile(deleteTarget);
    if (ok) {
      setFiles(prev => prev.filter(x => x.id !== deleteTarget.id));
      setSelected(prev => { const n = new Set(prev); n.delete(deleteTarget.id); return n; });
      onFilesChanged?.();
    } else {
      showToast('❌ Delete failed');
    }
    setDeleteTarget(null);
  };

  const toggleSel = (id) => setSelected(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const allChecked = files.length > 0 && selected.size === files.length;
  const toggleAll  = () => setSelected(allChecked ? new Set() : new Set(files.map(f => f.id)));
  const selectedFiles = files.filter(f => selected.has(f.id));

  const postComment = async () => {
    if (!newComment.trim()) return;
    const r = await addDiligenceComment(request.id, currentUser.id, newComment.trim());
    if (r) { setComments(prev => [...prev, r]); setNewComment(''); }
  };

  return (
    <>
    <div style={{ borderTop: `1px solid ${C.border}`, padding: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
      <div>
        {/* Linked tab: seller picks which detail tab this request unlocks
            when reviewed. Document-only requests leave it as None — no
            jump button appears in the row. Reviewers see the current
            value read-only for context. */}
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Linked tab</div>
        {isSeller ? (
          <select value={request.link_tab || ''}
            onChange={e => onUpdate(request, 'link_tab', e.target.value || null)}
            style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%', marginBottom: 14 }}>
            <option value="">— None (document-only) —</option>
            {DD_LINK_TARGETS.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        ) : (
          <div style={{ fontSize: 12, color: request.link_tab ? '#34D399' : 'rgba(255,255,255,0.35)', fontFamily: "'Montserrat',sans-serif", marginBottom: 14, fontStyle: request.link_tab ? 'normal' : 'italic' }}>
            {request.link_tab ? `→ ${DD_LINK_TARGET_LABEL[request.link_tab] || request.link_tab}` : 'No linked tab'}
          </div>
        )}

        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Notes</div>
        <textarea
          rows={4} value={notes}
          onChange={e => setNotes(e.target.value)}
          onBlur={() => notes !== request.notes && onUpdate(request, 'notes', notes)}
          style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: 8, fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%', resize: 'vertical' }}
        />

        {/* Files header: label + upload + download-all/selected */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '14px 0 8px' }}>
          <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Files</span>
          <div style={{ flex: 1 }} />
          {files.length > 0 && canDownload && (
            <button
              onClick={() => downloadZip(selectedFiles.length ? selectedFiles : files)}
              disabled={zipping}
              style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.4)', color: '#60A5FA', borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {zipping ? 'Zipping…' : (selectedFiles.length ? `Download ${selectedFiles.length} (zip)` : 'Download all (zip)')}
            </button>
          )}
          {!canDownload && files.length > 0 && (
            <span style={{ fontSize: 9, color: 'rgba(255,184,48,0.7)', letterSpacing:'0.08em', textTransform:'uppercase', fontWeight:700, fontFamily:"'Montserrat',sans-serif" }}>
              🔒 View-only — downloads disabled
            </span>
          )}
          {isSeller && (
            <label style={{ background: 'rgba(232,119,34,0.15)', border: '1px solid rgba(232,119,34,0.4)', color: '#E87722', borderRadius: 6, padding: '5px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {uploading ? 'Uploading…' : '+ Upload'}
              <input type="file" disabled={uploading}
                accept=".pdf,.docx,.pptx,.txt,.md,.csv,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/markdown,text/csv"
                onChange={onPick} style={{ display: 'none' }} />
            </label>
          )}
        </div>

        {files.length > 0 && (
          <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
            {/* select-all header */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', background: 'rgba(255,255,255,0.04)', borderBottom: `1px solid ${C.border}`, cursor: 'pointer', fontSize: 10, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              <input type="checkbox" checked={allChecked} onChange={toggleAll} />
              {selected.size ? `${selected.size} selected` : 'Select all'}
            </label>
            {files.map(f => {
              const fn = (f.filename || '').toLowerCase();
              const isPdf = (f.mime_type || '').toLowerCase().includes('pdf') || fn.endsWith('.pdf');
              const isAiSupported = isPdf || fn.endsWith('.docx') || fn.endsWith('.pptx') || /\.(txt|md|csv)$/.test(fn);
              const aiReady   = f.ai_enabled && (!!f.anthropic_file_id || !!f.extracted_content);
              const aiPending = preparingId === f.id;
              const aiError   = f.ai_enabled && !aiReady && !aiPending && f.ai_prep_error;
              return (
                <div key={f.id} style={{ display: 'grid', gridTemplateColumns: 'auto 1fr auto auto auto', gap: 10, alignItems: 'center', padding: '8px 12px', borderBottom: `1px solid ${C.border}`, fontSize: 12 }}>
                  <input type="checkbox" checked={selected.has(f.id)} onChange={() => toggleSel(f.id)} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</div>
                    <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 10 }}>
                      {Math.round((f.size_bytes || 0) / 1024).toLocaleString()} KB
                      {aiReady   && <span style={{ marginLeft: 8, color: '#A78BFA' }}>🤖 AI-ready</span>}
                      {aiPending && <span style={{ marginLeft: 8, color: '#FFB830' }}>🤖 preparing…</span>}
                      {aiError   && <span style={{ marginLeft: 8, color: '#EF4444' }} title={f.ai_prep_error}>🤖 prep failed</span>}
                    </div>
                  </div>
                  {/* AI enable toggle — seller-side only, supported formats */}
                  {isSeller && isAiSupported ? (
                    <label title={f.ai_enabled ? 'Disable AI access to this file' : (isPdf ? 'Upload to Anthropic Files API so the AI chat can reference it' : 'Extract text + make available to the AI chat')}
                      style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', border: `1px solid ${f.ai_enabled ? 'rgba(167,139,250,0.5)' : C.border}`, background: f.ai_enabled ? 'rgba(167,139,250,0.12)' : 'transparent', borderRadius: 5, cursor: aiPending ? 'wait' : 'pointer', fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700, color: f.ai_enabled ? '#A78BFA' : 'rgba(255,255,255,0.5)' }}>
                      <input type="checkbox" checked={!!f.ai_enabled} disabled={aiPending}
                        onChange={() => toggleFileAi(f)} style={{ accentColor: '#A78BFA' }} />
                      🤖 AI
                    </label>
                  ) : <span />}
                  <div style={{ display: 'flex', gap: 6 }}>
                    {canDownload ? (
                      <>
                        <button onClick={() => openFile(f)} title="View in browser"
                          style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.7)', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>Open</button>
                        <button onClick={() => downloadOne(f)} disabled={downloadingId === f.id} title="Download file"
                          style={{ background: 'rgba(96,165,250,0.12)', border: '1px solid rgba(96,165,250,0.35)', color: '#60A5FA', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>
                          {downloadingId === f.id ? '…' : 'Download'}
                        </button>
                      </>
                    ) : (
                      <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>view-only</span>
                    )}
                  </div>
                  {isSeller ? (
                    <button onClick={() => setDeleteTarget(f)} title="Delete file"
                      style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", fontWeight: 700 }}>Delete</button>
                  ) : <span />}
                </div>
              );
            })}
          </div>
        )}
        {files.length === 0 && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', padding: '4px 0' }}>No files yet.</div>}
      </div>

      <div>
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 6 }}>Q&amp;A</div>
        <div style={{ maxHeight: 260, overflowY: 'auto', marginBottom: 8 }}>
          {comments.map(c => {
            const u = users.find(x => x.id === c.user_id);
            return (
              <div key={c.id} style={{ marginBottom: 8, padding: 8, background: 'rgba(255,255,255,0.03)', borderRadius: 6 }}>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', fontFamily: "'Montserrat',sans-serif" }}>{u?.name || 'Unknown'} · {new Date(c.created_at).toLocaleString()}</div>
                <div style={{ fontSize: 12, color: '#fff', marginTop: 3, whiteSpace: 'pre-wrap' }}>{c.content}</div>
              </div>
            );
          })}
          {comments.length === 0 && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>No comments yet.</div>}
        </div>
        <textarea
          rows={2} value={newComment} placeholder="Ask a question, post a reply…"
          onChange={e => setNewComment(e.target.value)}
          style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: 8, fontSize: 12, width: '100%', resize: 'vertical', fontFamily: "'Montserrat',sans-serif" }}
        />
        <button onClick={postComment} disabled={!newComment.trim()}
          style={{ marginTop: 6, background: '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: newComment.trim() ? 1 : 0.4 }}>
          Post
        </button>
      </div>
    </div>

    {/* Delete-file confirmation modal */}
    {deleteTarget && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={() => setDeleteTarget(null)}>
        <div onClick={e => e.stopPropagation()} style={{ background: '#0d1b2a', border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, maxWidth: 420, width: '100%' }}>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 8 }}>Delete file?</div>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>This permanently removes:</div>
          <div style={{ fontSize: 13, color: '#fff', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', marginBottom: 18, wordBreak: 'break-all' }}>{deleteTarget.filename}</div>
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }} />
            <button onClick={() => setDeleteTarget(null)}
              style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Cancel</button>
            <button onClick={confirmDelete}
              style={{ background: '#EF4444', border: 'none', color: '#fff', borderRadius: 6, padding: '8px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Delete</button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

// ── Strategy narrative ───────────────────────────────────────────
// Seeded into every new deal. The APEM-impact section is the persuasive
// centerpiece. Tech-debt figure softened to a 10–15% range per the
// "don't let an aspirational number become contractual" guidance.
const DEFAULT_STRATEGY = {
  growth: {
    headline: 'From 15% organic to 30% YoY with focused investment',
    organicPct:  '15%',
    investedPct: '30%',
    current: 'Today 40Geo grows organically — word-of-mouth and existing relationships, minimal dedicated BD spend. Growth is constrained by attention, not by market demand.',
    invested: 'With dedicated investment from APEM, targets accelerate from 15% to 30% YoY across all three segments. The highest-leverage move is dedicated business development for adjacent maritime — current demand already outpaces our capacity to chase it.',
    vectors: [
      { name: 'Oil & Gas', organic: '15%', invested: '30%', detail: 'Continued mid-tier operator penetration with full web-app deployments (AMP, OpenIAP); expanded super-major engagements via data products (API, embedded analytics, custom dashboards). Mid-tier operators have real maritime data needs but lack internal teams — a natural fit for our full stack.' },
      { name: 'Adjacent Maritime', organic: '15%', invested: '30%', detail: 'Barge transport, terminal operators, commercial maritime. AIS-derived intelligence, port-visit history and operational data translate directly into value here — a less crowded segment than O&G, and our data infrastructure transfers cleanly.' },
      { name: 'CSA Partnership', organic: 'Steady', invested: 'Expanded', detail: 'Grow services and capabilities through existing CSA contracts and new engagements. The CSA channel extends 40Geo\'s reach without commensurate sales cost.' },
    ],
    ask: 'Two uses of funds: (1) one or more dedicated BD roles for adjacent maritime with global event/conference coverage; (2) a 40Geo-led CSA/APEM integration platform covering the full proposal-to-execution lifecycle.',
  },
  ai: {
    headline: 'AI has compressed scoping-to-deliverable by roughly 10×',
    body: 'AI is interwoven through every layer of the 40Geo stack with deliberate model flexibility — we route across Anthropic (Sonnet, Haiku), AWS Bedrock, and open-source models (BGE-M3, Titan) by fit-for-purpose and cost. No single-vendor lock-in. We have moved past chat-with-your-docs: 40Geo runs production Model Context Protocol (MCP) servers that expose our maritime tooling to any compatible AI client, and agentic systems that execute multi-step workflows autonomously across our data, our clients\' data, and the open web.',
    stacks: [
      { name: 'Stack 1 — Open-source RAG (privacy-first)', status: 'In production', detail: 'FastAPI service + background workers ingest client documents (PDF/OCR, Excel, CSV), chunk + embed via BGE-M3, store vectors in Qdrant, and serve a RAG query API. Multi-tenant isolation by company, full chat/thread history, web-search augmentation (Firecrawl), AI PDF report generation, GPU-accelerated embedding. For clients whose data sensitivity precludes commercial LLM APIs.' },
      { name: 'Stack 2 — AWS Bedrock + EKS (enterprise)', status: 'Production rollout imminent', detail: 'Nine-layer architecture: Bedrock Agents + S3 Vector storage (zero idle cost), Titan embeddings, two-tier routing (Haiku for tool selection, Sonnet for synthesis). Three action groups — web search, natural-language queries against the AMP DB (with field redaction + timeout guards), and geospatial/tabular analysis. Keycloak auth with permission-scoped access enforced before any query. Fully Terraform-managed across EKS/VPC/S3/Bedrock/SQS/IAM.' },
    ],
    mcp: [
      { name: 'AMP Data MCP Server',       detail: 'Vessel positions, port-visit history, environmental-compliance records, and operational data exposed as MCP tools with permission-scoped access. Any MCP-compatible client (Claude Desktop, Cursor, in-house agents) can query AMP in natural language without bespoke API integration. Clients bring their own LLM, 40Geo brings the data + the guardrails.' },
      { name: 'Geospatial Analysis MCP',    detail: 'pandas, geopandas, shapely, and S3-backed CSV/GeoJSON analysis exposed as MCP tools. Agents perform point-in-polygon, distance, route, and overlay analysis on the fly — no Python expertise required on the client side, no data round-tripping out of the 40Geo perimeter.' },
      { name: 'Cross-system Orchestration', detail: 'MCP bridges 40Geo platforms (AMP, OpenIAP) and external client systems. A single agent conversation can pull AMP vessel data, run a geospatial overlay, and write a finished report into a client environment — without writing custom integration code each time.' },
    ],
    agents: [
      { name: 'Incident Analysis Agent',    detail: 'Ingests live operational + sensor data, cross-references historical incidents in the AMP database, and produces a structured initial analysis with severity assessment and suggested next steps. Minutes instead of analyst-hours; the agent\'s reasoning trace is fully auditable.' },
      { name: 'Compliance Reporting Agent', detail: 'Pulls operational data on a schedule, generates the regulatory + environmental reports clients owe to agencies, and stages them for human sign-off. Converts a recurring multi-day analyst task into an oversight role.' },
      { name: 'Project Scoping Agent',      detail: 'Given a new client request, searches past similar engagements, drafts a Statement of Work with resource plan and cost estimate, and surfaces the risk factors a senior estimator would catch. Compresses scoping cycles from weeks to days — a primary driver of the 10× headline.' },
    ],
    useCases: [
      'AI-driven analysis of oil-spill events from operational and sensor data',
      'Automated water sampling and environmental compliance reporting',
      'Natural-language interrogation of vessel position, port-visit, and operational datasets',
      'Geospatial analysis of maritime survey results delivered as AI-generated reports',
      'Agentic scoping + cost estimation for new client engagements',
    ],
    differentiation: [
      'Model-agnostic — the right model is routed to each task by cost and capability; no single-vendor lock-in',
      'MCP-native — tooling exposed via Model Context Protocol so any client AI can use it without custom integration',
      'Agentic by default — multi-step autonomous workflows, not just RAG chat',
      'Privacy-flexible — open-source-only deployments for sensitive clients, Bedrock for enterprise scale',
      'Permission-enforced at the data layer — RBAC and source-path filtering before any AI call, not after',
      'Multi-tenant from inception — production-tested isolation, not a prototype retrofit',
      'Maritime domain specialization — purpose-built for AIS, metocean, geospatial and environmental data',
    ],
  },
  apem: {
    headline: '40Geo is APEM\'s AI on-ramp — internal, external, and people-side',
    intro: 'Acquiring 40Geo isn\'t buying a product — it\'s absorbing a working AI capability (MCP servers, agentic workflows, two production stacks, and the team that built them) and extending it across APEM and CSA. Five compounding value levers:',
    levers: [
      { title: 'AI on every internal app',             tag: 'Internal',        detail: 'Drop our MCP servers and agentic workflows into APEM\'s existing tools — AMP, internal portals, reporting dashboards — without forklift rebuilds. Each app gains natural-language query, automated report generation, and cross-system lookups. Integration measured in days, not quarters.' },
      { title: 'AI-augmented services to end clients', tag: 'External',        detail: 'Wrap APEM client deliverables — maritime surveys, compliance reporting, incident response, environmental analysis — with the same agentic AI we already run. Premium pricing, faster turnaround, analytical depth competitors will spend years catching up on. A new service line, not a cost-out exercise.' },
      { title: 'Multiply the team you have',           tag: 'Human resources', detail: 'AI is amplification, not replacement. One AI-augmented developer (the Uddeshya pattern at 40Geo) covers workloads that traditionally required three or four. Analysts self-serve data via natural-language MCP. Scoping cycles compress from weeks to days. APEM doesn\'t need to hire dozens of AI engineers — it absorbs a working pattern, then scales it.' },
      { title: 'Skip 2-3 years of capability build',   tag: 'Time-to-value',   detail: 'What 40Geo has shipped — MCP integration layer, agentic systems, dual production AI stacks (privacy-first and enterprise), maritime-specialised models, and the operating team — would take a comparable organisation 24-36 months to replicate from scratch. APEM\'s competitors are still hiring AI directors. APEM gets the full stack on day one.' },
      { title: 'Consolidate fragmented SaaS',          tag: 'Cost out',        detail: 'On top of the new revenue, the platform consolidates fragmented subscription tooling — data, reporting, analytics — onto 40Geo\'s owned multi-tenant base. Ends per-seat licensing creep, removes vendor lock-in, and brings AI under APEM\'s direct control rather than rented from third parties.' },
    ],
  },
};

function StrategyPanel({ C, deal, onSave, isSeller }) {
  const seed = (deal.strategy && Object.keys(deal.strategy).length) ? deal.strategy : DEFAULT_STRATEGY;
  const [s, setS] = useState(seed);
  useEffect(() => {
    setS((deal.strategy && Object.keys(deal.strategy).length) ? deal.strategy : DEFAULT_STRATEGY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);
  const [edit, setEdit] = useState(false);
  // Sub-tab: Growth or AI (AI sub-tab also hosts the APEM-impact
  // centerpiece since it's a continuation of the AI pitch).
  const [section, setSection] = useState('growth'); // 'growth' | 'ai'

  const g = s.growth || DEFAULT_STRATEGY.growth;
  const ai = s.ai || DEFAULT_STRATEGY.ai;
  const ap = s.apem || DEFAULT_STRATEGY.apem;

  const commit = (next) => { setS(next); onSave({ strategy: next }); };
  const setG  = (patch) => commit({ ...s, growth: { ...g, ...patch } });
  const setAI = (patch) => commit({ ...s, ai: { ...ai, ...patch } });
  const setAP = (patch) => commit({ ...s, apem: { ...ap, ...patch } });

  const card = { background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 };
  const eyebrow = { fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 10, fontFamily: "'Montserrat',sans-serif" };
  const ta = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: 8, fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%', resize: 'vertical', lineHeight: 1.6 };
  const ti = { ...ta, resize: 'none' };
  const prose = { fontSize: 13, color: 'rgba(255,255,255,0.8)', lineHeight: 1.7, whiteSpace: 'pre-wrap', fontFamily: "'Montserrat',sans-serif" };

  // Generic editable string-list helper (use cases, differentiation).
  const StringList = ({ items, onChange }) => (
    <div>
      {items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input value={it} onChange={e => onChange(items.map((x, j) => j === i ? e.target.value : x))} style={{ ...ti, flex: 1 }} />
          <button onClick={() => onChange(items.filter((_, j) => j !== i))} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: '#FF6B6B', borderRadius: 5, padding: '0 10px', cursor: 'pointer' }}>×</button>
        </div>
      ))}
      <button onClick={() => onChange([...items, ''])} style={{ background: 'transparent', border: `1px dashed ${C.border}`, color: 'rgba(255,255,255,0.5)', borderRadius: 5, padding: '5px 10px', fontSize: 11, cursor: 'pointer' }}>+ Add</button>
    </div>
  );

  const subTabBtn = (id, label) => (
    <button key={id} onClick={() => setSection(id)}
      style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, padding: '6px 16px', borderRadius: 6, border: 'none', cursor: 'pointer', background: section === id ? '#E87722' : 'transparent', color: section === id ? '#fff' : 'rgba(255,255,255,0.5)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
      {label}
    </button>
  );

  return (
    <div style={{ animation: 'fadeUp 0.3s ease' }}>
      {/* Sub-tabs + edit controls in one row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.03)', padding: 4, borderRadius: 8 }}>
          {subTabBtn('growth', 'Growth')}
          {subTabBtn('ai', 'AI Strategy')}
        </div>
        <div style={{ flex: 1 }} />
        {isSeller && (
          <>
            {edit && (
              <button
                onClick={() => { if (confirm('Reset the Strategy tab to the latest defaults? Your edits will be overwritten.')) commit(DEFAULT_STRATEGY); }}
                style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                Reset to default
              </button>
            )}
            <button onClick={() => setEdit(v => !v)}
              style={{ background: edit ? '#34D399' : 'transparent', border: `1px solid ${edit ? '#34D399' : C.border}`, color: edit ? '#04231a' : 'rgba(255,255,255,0.7)', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
              {edit ? '✓ Done editing' : 'Edit'}
            </button>
          </>
        )}
      </div>

      {/* ── GROWTH ─────────────────────────────────────────── */}
      {section === 'growth' && (<>
      <div style={card}>
        <div style={eyebrow}>Growth Strategy</div>
        {edit
          ? <input value={g.headline} onChange={e => setG({ headline: e.target.value })} style={{ ...ti, fontSize: 16, fontWeight: 700, marginBottom: 12 }} />
          : <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', fontFamily: "'Montserrat',sans-serif", marginBottom: 12 }}>{g.headline}</div>}

        {/* metric callout — organic% → invested% */}
        <div style={{ display: 'flex', gap: 18, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {edit ? (
              <input value={g.organicPct ?? '15%'}
                onChange={e => setG({ organicPct: e.target.value })}
                style={{ ...ti, width: 78, fontSize: 20, fontWeight: 900, color: 'rgba(255,255,255,0.85)', textAlign: 'center' }} />
            ) : (
              <span style={{ fontSize: 26, fontWeight: 900, color: 'rgba(255,255,255,0.55)', fontFamily: "'Montserrat',sans-serif" }}>{g.organicPct ?? '15%'}</span>
            )}
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>YoY organic</span>
            <span style={{ fontSize: 18, color: '#E87722' }}>→</span>
            {edit ? (
              <input value={g.investedPct ?? '30%'}
                onChange={e => setG({ investedPct: e.target.value })}
                style={{ ...ti, width: 78, fontSize: 20, fontWeight: 900, color: '#E87722', textAlign: 'center' }} />
            ) : (
              <span style={{ fontSize: 26, fontWeight: 900, color: '#E87722', fontFamily: "'Montserrat',sans-serif" }}>{g.investedPct ?? '30%'}</span>
            )}
            <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>with investment</span>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Current — organic</div>
            {edit ? <textarea rows={4} value={g.current} onChange={e => setG({ current: e.target.value })} style={ta} /> : <div style={prose}>{g.current}</div>}
          </div>
          <div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>Investment-driven</div>
            {edit ? <textarea rows={4} value={g.invested} onChange={e => setG({ invested: e.target.value })} style={ta} /> : <div style={prose}>{g.invested}</div>}
          </div>
        </div>

        {/* growth vectors */}
        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Growth vectors</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12, marginBottom: 14 }}>
          {(g.vectors || []).map((v, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
              {edit ? (
                <>
                  <input value={v.name} onChange={e => setG({ vectors: g.vectors.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} style={{ ...ti, fontWeight: 700, marginBottom: 6 }} />
                  <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <input value={v.organic} onChange={e => setG({ vectors: g.vectors.map((x, j) => j === i ? { ...x, organic: e.target.value } : x) })} style={{ ...ti }} placeholder="organic" />
                    <input value={v.invested} onChange={e => setG({ vectors: g.vectors.map((x, j) => j === i ? { ...x, invested: e.target.value } : x) })} style={{ ...ti }} placeholder="invested" />
                  </div>
                  <textarea rows={4} value={v.detail} onChange={e => setG({ vectors: g.vectors.map((x, j) => j === i ? { ...x, detail: e.target.value } : x) })} style={ta} />
                  <button onClick={() => setG({ vectors: g.vectors.filter((_, j) => j !== i) })} style={{ marginTop: 6, background: 'transparent', border: `1px solid ${C.border}`, color: '#FF6B6B', borderRadius: 5, padding: '3px 10px', fontSize: 10, cursor: 'pointer' }}>Remove</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat',sans-serif" }}>{v.name}</div>
                  <div style={{ margin: '6px 0' }}><span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>{v.organic}</span><span style={{ color: '#E87722', margin: '0 6px' }}>→</span><span style={{ fontSize: 14, color: '#E87722', fontWeight: 800 }}>{v.invested}</span></div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>{v.detail}</div>
                </>
              )}
            </div>
          ))}
          {edit && <button onClick={() => setG({ vectors: [...(g.vectors || []), { name: 'New vector', organic: '', invested: '', detail: '' }] })} style={{ background: 'transparent', border: `1px dashed ${C.border}`, color: 'rgba(255,255,255,0.5)', borderRadius: 8, padding: 12, fontSize: 11, cursor: 'pointer' }}>+ Add vector</button>}
        </div>

        <div style={{ background: 'rgba(232,119,34,0.08)', border: '1px solid rgba(232,119,34,0.25)', borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 9, color: '#E87722', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: 4, fontWeight: 700 }}>Investment ask</div>
          {edit ? <textarea rows={3} value={g.ask} onChange={e => setG({ ask: e.target.value })} style={ta} /> : <div style={{ ...prose, color: '#fff' }}>{g.ask}</div>}
        </div>
      </div>
      </>)}

      {/* ── AI CAPABILITIES ─────────────────────────────────── */}
      {section === 'ai' && (<>
      <div style={card}>
        <div style={eyebrow}>AI Capabilities & Transformation</div>
        {edit
          ? <input value={ai.headline} onChange={e => setAI({ headline: e.target.value })} style={{ ...ti, fontSize: 16, fontWeight: 700, marginBottom: 10 }} />
          : <div style={{ fontSize: 18, fontWeight: 800, color: '#fff', fontFamily: "'Montserrat',sans-serif", marginBottom: 10 }}>{ai.headline}</div>}
        {edit ? <textarea rows={4} value={ai.body} onChange={e => setAI({ body: e.target.value })} style={{ ...ta, marginBottom: 14 }} /> : <div style={{ ...prose, marginBottom: 14 }}>{ai.body}</div>}

        <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Production AI stacks</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          {(ai.stacks || []).map((st, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
              {edit ? (
                <>
                  <input value={st.name} onChange={e => setAI({ stacks: ai.stacks.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} style={{ ...ti, fontWeight: 700, marginBottom: 6 }} />
                  <input value={st.status} onChange={e => setAI({ stacks: ai.stacks.map((x, j) => j === i ? { ...x, status: e.target.value } : x) })} style={{ ...ti, marginBottom: 6 }} placeholder="status" />
                  <textarea rows={6} value={st.detail} onChange={e => setAI({ stacks: ai.stacks.map((x, j) => j === i ? { ...x, detail: e.target.value } : x) })} style={ta} />
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat',sans-serif" }}>{st.name}</div>
                  <div style={{ display: 'inline-block', margin: '6px 0', fontSize: 9, color: st.status?.toLowerCase().includes('production') && !st.status?.toLowerCase().includes('imminent') ? '#34D399' : '#FFB830', border: `1px solid ${st.status?.toLowerCase().includes('production') && !st.status?.toLowerCase().includes('imminent') ? 'rgba(52,211,153,0.4)' : 'rgba(255,184,48,0.4)'}`, borderRadius: 3, padding: '2px 7px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{st.status}</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.6)', lineHeight: 1.55 }}>{st.detail}</div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* MCP integrations */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 9, color: '#A78BFA', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>MCP integrations</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>40Geo tooling exposed via Model Context Protocol — any client AI can use it</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 14 }}>
          {(ai.mcp || []).map((m, i) => (
            <div key={i} style={{ background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 8, padding: 12 }}>
              {edit ? (
                <>
                  <input value={m.name} onChange={e => setAI({ mcp: ai.mcp.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} style={{ ...ti, fontWeight: 700, marginBottom: 6 }} />
                  <textarea rows={5} value={m.detail} onChange={e => setAI({ mcp: ai.mcp.map((x, j) => j === i ? { ...x, detail: e.target.value } : x) })} style={ta} />
                  <button onClick={() => setAI({ mcp: ai.mcp.filter((_, j) => j !== i) })} style={{ marginTop: 6, background: 'transparent', border: `1px solid ${C.border}`, color: '#FF6B6B', borderRadius: 5, padding: '3px 10px', fontSize: 10, cursor: 'pointer' }}>Remove</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat',sans-serif", marginBottom: 6 }}>{m.name}</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{m.detail}</div>
                </>
              )}
            </div>
          ))}
          {edit && <button onClick={() => setAI({ mcp: [...(ai.mcp || []), { name: 'New MCP server', detail: '' }] })} style={{ background: 'transparent', border: `1px dashed rgba(167,139,250,0.4)`, color: '#A78BFA', borderRadius: 8, padding: 12, fontSize: 11, cursor: 'pointer' }}>+ Add MCP integration</button>}
        </div>

        {/* Agentic systems */}
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 9, color: '#34D399', textTransform: 'uppercase', letterSpacing: '0.12em', fontWeight: 700 }}>Agentic systems</div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>multi-step autonomous workflows running on our own stacks today</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginBottom: 18 }}>
          {(ai.agents || []).map((a, i) => (
            <div key={i} style={{ background: 'rgba(52,211,153,0.05)', border: '1px solid rgba(52,211,153,0.25)', borderRadius: 8, padding: 12 }}>
              {edit ? (
                <>
                  <input value={a.name} onChange={e => setAI({ agents: ai.agents.map((x, j) => j === i ? { ...x, name: e.target.value } : x) })} style={{ ...ti, fontWeight: 700, marginBottom: 6 }} />
                  <textarea rows={5} value={a.detail} onChange={e => setAI({ agents: ai.agents.map((x, j) => j === i ? { ...x, detail: e.target.value } : x) })} style={ta} />
                  <button onClick={() => setAI({ agents: ai.agents.filter((_, j) => j !== i) })} style={{ marginTop: 6, background: 'transparent', border: `1px solid ${C.border}`, color: '#FF6B6B', borderRadius: 5, padding: '3px 10px', fontSize: 10, cursor: 'pointer' }}>Remove</button>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat',sans-serif", marginBottom: 6 }}>{a.name}</div>
                  <div style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.55 }}>{a.detail}</div>
                </>
              )}
            </div>
          ))}
          {edit && <button onClick={() => setAI({ agents: [...(ai.agents || []), { name: 'New agent', detail: '' }] })} style={{ background: 'transparent', border: `1px dashed rgba(52,211,153,0.4)`, color: '#34D399', borderRadius: 8, padding: 12, fontSize: 11, cursor: 'pointer' }}>+ Add agent</button>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
          <div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Real-world use cases</div>
            {edit ? <StringList items={ai.useCases || []} onChange={v => setAI({ useCases: v })} />
              : <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.8 }}>{(ai.useCases || []).map((u, i) => <li key={i}>{u}</li>)}</ul>}
          </div>
          <div>
            <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 }}>Differentiation</div>
            {edit ? <StringList items={ai.differentiation || []} onChange={v => setAI({ differentiation: v })} />
              : <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.8 }}>{(ai.differentiation || []).map((u, i) => <li key={i}>{u}</li>)}</ul>}
          </div>
        </div>
      </div>

      {/* ── APEM IMPACT — the persuasive centerpiece ─────────── */}
      <div style={{ ...card, background: 'linear-gradient(135deg, rgba(232,119,34,0.10), rgba(232,119,34,0.02))', border: '1px solid rgba(232,119,34,0.3)' }}>
        <div style={{ ...eyebrow, color: '#E87722' }}>The opportunity for APEM</div>
        {edit
          ? <input value={ap.headline} onChange={e => setAP({ headline: e.target.value })} style={{ ...ti, fontSize: 17, fontWeight: 700, marginBottom: 8 }} />
          : <div style={{ fontSize: 20, fontWeight: 800, color: '#fff', fontFamily: "'Montserrat',sans-serif", marginBottom: 8 }}>{ap.headline}</div>}
        {edit ? <textarea rows={2} value={ap.intro} onChange={e => setAP({ intro: e.target.value })} style={{ ...ta, marginBottom: 14 }} /> : <div style={{ ...prose, marginBottom: 14 }}>{ap.intro}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
          {(ap.levers || []).map((lv, i) => (
            <div key={i} style={{ background: 'rgba(13,27,42,0.6)', border: '1px solid rgba(232,119,34,0.25)', borderRadius: 8, padding: 14 }}>
              {edit ? (
                <>
                  <input value={lv.tag} onChange={e => setAP({ levers: ap.levers.map((x, j) => j === i ? { ...x, tag: e.target.value } : x) })} style={{ ...ti, marginBottom: 6 }} placeholder="tag (e.g. Cost out)" />
                  <input value={lv.title} onChange={e => setAP({ levers: ap.levers.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} style={{ ...ti, fontWeight: 700, marginBottom: 6 }} />
                  <textarea rows={5} value={lv.detail} onChange={e => setAP({ levers: ap.levers.map((x, j) => j === i ? { ...x, detail: e.target.value } : x) })} style={ta} />
                  <button onClick={() => setAP({ levers: ap.levers.filter((_, j) => j !== i) })} style={{ marginTop: 6, background: 'transparent', border: `1px solid ${C.border}`, color: '#FF6B6B', borderRadius: 5, padding: '3px 10px', fontSize: 10, cursor: 'pointer' }}>Remove</button>
                </>
              ) : (
                <>
                  <div style={{ display: 'inline-block', fontSize: 9, color: '#E87722', background: 'rgba(232,119,34,0.15)', border: '1px solid rgba(232,119,34,0.4)', borderRadius: 3, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, marginBottom: 8 }}>{lv.tag}</div>
                  <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', fontFamily: "'Montserrat',sans-serif", marginBottom: 6 }}>{lv.title}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>{lv.detail}</div>
                </>
              )}
            </div>
          ))}
          {edit && <button onClick={() => setAP({ levers: [...(ap.levers || []), { tag: '', title: 'New lever', detail: '' }] })} style={{ background: 'transparent', border: `1px dashed rgba(232,119,34,0.4)`, color: '#E87722', borderRadius: 8, padding: 14, fontSize: 11, cursor: 'pointer' }}>+ Add lever</button>}
        </div>
      </div>
      </>)}
    </div>
  );
}

// ── Org chart ────────────────────────────────────────────────────
// Seeded into every new deal. Reflects 40Geo's lean, senior-heavy team.
const DEFAULT_ORG_CHART = [
  { id: 'keith',    name: 'Keith Fraley',     title: 'CEO & Majority Owner', parent_id: null,    contractor: false, note: 'Commercial leadership, key client relationships (HWCG, oil & gas operators, strategic accounts), overall company direction.' },
  { id: 'miles',    name: 'Miles Roden',      title: 'COO & Co-Owner',       parent_id: 'keith', contractor: false, note: 'Operations, product strategy, partnerships, M&A, oversight of technical delivery.' },
  { id: 'nick',     name: 'Nicholas Enochs',  title: 'Chief Technologist',   parent_id: 'miles', contractor: false, note: 'Platform architecture and engineering leadership across OpenIAP, AMP, and Raptor.' },
  { id: 'evan',     name: 'Evan Ferguson',    title: 'Senior Developer',     parent_id: 'nick',  contractor: false, note: 'Full-stack application development and feature delivery.' },
  { id: 'uddeshya', name: 'Uddeshya Agrawal', title: 'Senior AI Developer',  parent_id: 'nick',  contractor: true,  note: '3-year contractor. AI/ML across the open-source RAG pipeline and the AWS Bedrock agent stack — continuity and deep institutional knowledge without fixed overhead.' },
];

function OrgChart({ C, deal, onSave, isSeller }) {
  const [nodes, setNodes] = useState(
    Array.isArray(deal.org_chart) && deal.org_chart.length ? deal.org_chart : DEFAULT_ORG_CHART
  );
  useEffect(() => {
    // Re-sync when switching deals. Intentional prop→state sync.
    setNodes(Array.isArray(deal.org_chart) && deal.org_chart.length ? deal.org_chart : DEFAULT_ORG_CHART);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  const [editing, setEditing] = useState(null); // node being edited (or { __new, parent_id })

  const persist = (next) => { setNodes(next); onSave({ org_chart: next }); };

  const roots    = nodes.filter(n => !n.parent_id);
  const childrenOf = (id) => nodes.filter(n => n.parent_id === id);

  const saveNode = (node) => {
    let next;
    if (node.__new) {
      const clean = { ...node, id: crypto.randomUUID() };
      delete clean.__new;
      next = [...nodes, clean];
    } else {
      next = nodes.map(n => n.id === node.id ? node : n);
    }
    persist(next);
    setEditing(null);
  };

  const deleteNode = (node) => {
    // Re-parent any reports to the deleted node's parent so the tree stays whole.
    const next = nodes
      .filter(n => n.id !== node.id)
      .map(n => n.parent_id === node.id ? { ...n, parent_id: node.parent_id } : n);
    persist(next);
    setEditing(null);
  };

  const resetDefault = () => { if (confirm('Reset the org chart to the 40Geo default?')) persist(DEFAULT_ORG_CHART); };

  const renderNode = (node) => {
    const kids = childrenOf(node.id);
    return (
      <li key={node.id}>
        <div className="oc-node">
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 13, color: '#fff' }}>
            {node.name}
            {node.contractor && <span style={{ marginLeft: 6, fontSize: 8, color: '#FFB830', border: '1px solid rgba(255,184,48,0.4)', borderRadius: 3, padding: '1px 4px', textTransform: 'uppercase', letterSpacing: '0.05em', verticalAlign: 'middle' }}>Contractor</span>}
          </div>
          <div style={{ fontSize: 11, color: '#E87722', fontFamily: "'Montserrat',sans-serif", marginTop: 2 }}>{node.title}</div>
          {node.note && <div style={{ fontSize: 9.5, color: 'rgba(255,255,255,0.45)', marginTop: 5, lineHeight: 1.4, maxWidth: 220 }}>{node.note}</div>}
          {isSeller && (
            <div style={{ display: 'flex', gap: 4, justifyContent: 'center', marginTop: 8 }}>
              <button onClick={() => setEditing(node)} style={ocBtn(C)}>Edit</button>
              <button onClick={() => setEditing({ __new: true, parent_id: node.id, name: '', title: '', note: '', contractor: false })} style={ocBtn(C)}>+ Report</button>
            </div>
          )}
        </div>
        {kids.length > 0 && <ul>{kids.map(renderNode)}</ul>}
      </li>
    );
  };

  return (
    <div>
      <style>{`
        .oc { overflow-x: auto; padding: 10px 4px 24px; text-align: center; }
        .oc ul { position: relative; padding: 22px 0 0 0; margin: 0; white-space: nowrap; }
        .oc li {
          display: inline-block; vertical-align: top; list-style: none;
          position: relative; padding: 22px 14px 0 14px; white-space: normal;
        }
        /* connectors from each child up to the shared horizontal bar */
        .oc li::before, .oc li::after {
          content: ''; position: absolute; top: 0; right: 50%;
          border-top: 1px solid rgba(255,255,255,0.22); width: 50%; height: 22px;
        }
        .oc li::after { right: auto; left: 50%; border-left: 1px solid rgba(255,255,255,0.22); }
        /* a single child needs no horizontal bar, just the vertical drop */
        .oc li:only-child::before, .oc li:only-child::after { display: none; }
        .oc li:only-child { padding-top: 22px; }
        /* trim the outer halves so the bar starts/ends at the first/last child */
        .oc li:first-child::before, .oc li:last-child::after { border: 0 none; }
        .oc li:last-child::before  { border-right: 1px solid rgba(255,255,255,0.22); }
        /* vertical drop from a parent node into its children row */
        .oc ul ul::before {
          content: ''; position: absolute; top: 0; left: 50%;
          border-left: 1px solid rgba(255,255,255,0.22); width: 0; height: 22px;
        }
        /* TOP ROW: roots have no parent — kill their connectors + top padding */
        .oc > ul { padding-top: 0; }
        .oc > ul > li { padding-top: 0; }
        .oc > ul > li::before, .oc > ul > li::after { display: none; }
        .oc-node {
          display: inline-block; text-align: center; padding: 12px 14px;
          background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px; min-width: 170px; white-space: normal;
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase' }}>Organisation structure</div>
        <div style={{ flex: 1 }} />
        {isSeller && (
          <button onClick={resetDefault} style={{ ...ocBtn(C), padding: '5px 10px' }}>Reset to default</button>
        )}
      </div>

      <div className="oc">
        <ul>{roots.map(renderNode)}</ul>
      </div>

      {editing && (
        <OrgNodeEditor
          C={C}
          node={editing}
          nodes={nodes}
          onSave={saveNode}
          onDelete={editing.__new ? null : () => deleteNode(editing)}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function ocBtn(C) {
  return { background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '3px 9px', fontSize: 9, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.04em' };
}

function OrgNodeEditor({ C, node, nodes, onSave, onDelete, onClose }) {
  const [d, setD] = useState(node);
  const set = (k, v) => setD(p => ({ ...p, [k]: v }));
  const inp = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%' };
  const lbl = { fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4, display: 'block', fontFamily: "'Montserrat',sans-serif" };
  // "Reports to" options: every node except self and self's descendants
  // (prevents cycles). For a brand-new node parent_id is preset.
  const descendants = (() => {
    if (d.__new || !d.id) return new Set();
    const set = new Set([d.id]);
    let added = true;
    while (added) {
      added = false;
      nodes.forEach(n => { if (n.parent_id && set.has(n.parent_id) && !set.has(n.id)) { set.add(n.id); added = true; } });
    }
    return set;
  })();
  const parentOptions = nodes.filter(n => !descendants.has(n.id));

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 10200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#0d1b2a', border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, maxWidth: 480, width: '100%' }}>
        <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 15, fontWeight: 700, color: '#fff', marginBottom: 16 }}>{node.__new ? 'Add person' : 'Edit person'}</div>
        <div style={{ display: 'grid', gap: 10 }}>
          <div><label style={lbl}>Name</label><input value={d.name} onChange={e => set('name', e.target.value)} style={inp} /></div>
          <div><label style={lbl}>Title</label><input value={d.title} onChange={e => set('title', e.target.value)} style={inp} /></div>
          <div>
            <label style={lbl}>Reports to</label>
            <select value={d.parent_id || ''} onChange={e => set('parent_id', e.target.value || null)} style={inp}>
              <option value="">— top of org (no manager) —</option>
              {parentOptions.map(n => <option key={n.id} value={n.id}>{n.name} · {n.title}</option>)}
            </select>
          </div>
          <div><label style={lbl}>Note</label><textarea rows={3} value={d.note || ''} onChange={e => set('note', e.target.value)} style={{ ...inp, resize: 'vertical' }} /></div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#fff', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!d.contractor} onChange={e => set('contractor', e.target.checked)} />
            Contractor (flexible-cost)
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          {onDelete && <button onClick={onDelete} style={{ background: 'transparent', border: '1px solid rgba(255,107,107,0.35)', color: '#FF6B6B', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Delete</button>}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Cancel</button>
          <button onClick={() => d.name.trim() && onSave(d)} style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Earn-out math (piecewise-linear curve, exported for reuse) ──
function earnoutMultiplier(ratioPct, floorPct, floorMult, ceilingPct, ceilingMult) {
  if (ratioPct <= 0) return 0;
  if (ratioPct < floorPct) return 0;
  if (ratioPct === floorPct) return floorMult;
  if (ratioPct < 100) {
    const t = (ratioPct - floorPct) / (100 - floorPct);
    return floorMult + t * (1 - floorMult);
  }
  if (ratioPct < ceilingPct) {
    const t = (ratioPct - 100) / (ceilingPct - 100);
    return 1 + t * (ceilingMult - 1);
  }
  return ceilingMult;
}

// ═══════════════════════════════════════════════════════════════════
// DEAL MEMBERS — manage who has access + per-user / default tab perms
// ═══════════════════════════════════════════════════════════════════
const TAB_LABEL = {
  diligence: 'Diligence',
  orgchart:  'Org Chart',
  strategy:  'Strategy',
  earnout:   'Earn-out',
  economics: 'Economics',
};

// ─── Deal Intelligence — seller-only knowledge base ───────────────
// Buyer profile + background docs + mutual contracts + URL crawler
// + disclosure-level selector. Everything here feeds the AI chat's
// system prompt server-side; reviewers never see this tab.
function DealIntelligencePanel({ C, deal, currentUser, onSaveDeal, showToast }) {
  const [intel, setIntel]     = useState({ profile: {}, documents: [], contracts: [], urls: [] });
  const [loading, setLoading] = useState(true);
  // Buyer Profile uses an explicit Save flow: local draft + dirty
  // indicator + Save button. Avoids the per-keystroke auto-save that
  // made it impossible to tell whether changes actually landed.
  const [profileDraft, setProfileDraft] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const refresh = useCallback(async () => {
    const fresh = await loadDealIntel(deal.id);
    setIntel(fresh);
    setProfileDraft(fresh.profile || {});
    setLoading(false);
  }, [deal.id]);
  useEffect(() => { refresh(); }, [refresh]);

  // Compare draft to last-saved profile (intel.profile). Dirty if any
  // tracked field differs.
  const PROFILE_FIELDS = ['buyer_name', 'buyer_industry', 'buyer_description', 'key_contacts', 'strategic_notes'];
  const profileDirty = PROFILE_FIELDS.some(k => (profileDraft[k] || '') !== (intel.profile?.[k] || ''));

  const setDraftField = (k, v) => setProfileDraft(p => ({ ...p, [k]: v }));

  const saveProfile = async () => {
    setSavingProfile(true);
    const ok = await saveDealIntelProfile(deal.id, profileDraft);
    setSavingProfile(false);
    if (ok) {
      setIntel(p => ({ ...p, profile: profileDraft }));
      setSavedAt(Date.now());
      showToast?.('✓ Buyer profile saved');
    } else {
      showToast?.('❌ Save failed');
    }
  };

  const resetProfileDraft = () => setProfileDraft(intel.profile || {});

  const DISCLOSURE = [
    { id: 'vague',   label: 'Vague (pre-LOI)',     color: '#FFB830', desc: 'High-level only — no specific figures or named accounts.' },
    { id: 'focused', label: 'Focused (post-LOI)',  color: '#60A5FA', desc: 'Share aggregated figures + named customers from attached docs.' },
    { id: 'full',    label: 'Full (diligence)',    color: '#34D399', desc: 'Transparent on every figure, customer, contract term in scope.' },
  ];

  const card    = { background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 };
  const eyebrow = { fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 12, fontFamily: "'Montserrat',sans-serif" };
  const lbl     = { fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, display: 'block' };
  const inp     = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, width: '100%', fontFamily: "'Montserrat',sans-serif" };
  const ta      = { ...inp, resize: 'vertical', lineHeight: 1.5 };

  if (loading) return <div style={{ color: 'rgba(255,255,255,0.4)', padding: 20 }}>Loading intelligence…</div>;

  return (
    <div style={{ animation: 'fadeUp 0.3s ease' }}>
      {/* Disclosure level — top of tab, prominent */}
      <div style={card}>
        <div style={eyebrow}>Disclosure level — controls how forthcoming the AI is</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          {DISCLOSURE.map(d => {
            const active = (deal.disclosure_level || 'vague') === d.id;
            return (
              <button key={d.id} onClick={() => onSaveDeal({ disclosure_level: d.id })}
                style={{ textAlign: 'left', padding: 12, borderRadius: 8, border: `1px solid ${active ? d.color : C.border}`, background: active ? `${d.color}15` : 'rgba(255,255,255,0.02)', cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: active ? d.color : '#fff', marginBottom: 4 }}>{d.label}</div>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{d.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Buyer profile */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ ...eyebrow, marginBottom: 0, flex: 1 }}>Buyer Profile</div>
          {/* Save status indicator */}
          {profileDirty ? (
            <span style={{ fontSize: 10, color: '#FFB830', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 10 }}>● Unsaved changes</span>
          ) : savedAt && (Date.now() - savedAt < 3000) ? (
            <span style={{ fontSize: 10, color: '#34D399', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 10 }}>✓ Saved</span>
          ) : null}
          <button onClick={resetProfileDraft} disabled={!profileDirty || savingProfile}
            style={{ background: 'transparent', border: `1px solid ${C.border}`, color: profileDirty ? 'rgba(255,255,255,0.65)' : 'rgba(255,255,255,0.3)', borderRadius: 6, padding: '5px 12px', fontSize: 10, fontWeight: 700, cursor: profileDirty ? 'pointer' : 'not-allowed', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase', marginRight: 6 }}>
            Revert
          </button>
          <button onClick={saveProfile} disabled={!profileDirty || savingProfile}
            style={{ background: profileDirty && !savingProfile ? '#E87722' : 'rgba(232,119,34,0.3)', border: 'none', color: '#fff', borderRadius: 6, padding: '5px 14px', fontSize: 10, fontWeight: 700, cursor: profileDirty && !savingProfile ? 'pointer' : 'not-allowed', fontFamily: "'Montserrat',sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>
            {savingProfile ? 'Saving…' : 'Save profile'}
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={lbl}>Buyer name</label>
            <input value={profileDraft.buyer_name ?? ''} onChange={e => setDraftField('buyer_name', e.target.value)} placeholder="APEM Group" style={inp} />
          </div>
          <div>
            <label style={lbl}>Industry / sector</label>
            <input value={profileDraft.buyer_industry ?? ''} onChange={e => setDraftField('buyer_industry', e.target.value)} placeholder="Environmental services" style={inp} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={lbl}>Description / what they care about</label>
            <textarea rows={4} value={profileDraft.buyer_description ?? ''} onChange={e => setDraftField('buyer_description', e.target.value)} placeholder="Public corporate description, key markets, recent press, what they value in acquisitions…" style={ta} />
          </div>
          <div>
            <label style={lbl}>Key contacts</label>
            <textarea rows={3} value={profileDraft.key_contacts ?? ''} onChange={e => setDraftField('key_contacts', e.target.value)} placeholder="Rasham Begum — Head of M&A&#10;..." style={ta} />
          </div>
          <div>
            <label style={lbl}>Strategic notes (private)</label>
            <textarea rows={3} value={profileDraft.strategic_notes ?? ''} onChange={e => setDraftField('strategic_notes', e.target.value)} placeholder="Things to remember during conversations — leverage points, red lines, walkaway numbers…" style={ta} />
          </div>
        </div>
      </div>

      {/* URLs */}
      <DealIntelUrlsSubpanel C={C} dealId={deal.id} urls={intel.urls} refresh={refresh} showToast={showToast} />

      {/* Background documents */}
      <DealIntelDocsSubpanel C={C} dealId={deal.id} docs={intel.documents} currentUser={currentUser} refresh={refresh} showToast={showToast} />

      {/* Mutual contracts */}
      <DealIntelContractsSubpanel C={C} dealId={deal.id} contracts={intel.contracts} docs={intel.documents} refresh={refresh} showToast={showToast} />
    </div>
  );
}

function DealIntelUrlsSubpanel({ C, dealId, urls, refresh, showToast }) {
  const [newUrl, setNewUrl] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [adding, setAdding] = useState(false);
  const [fetchingId, setFetchingId] = useState(null);

  const card    = { background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 };
  const eyebrow = { fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 12, fontFamily: "'Montserrat',sans-serif" };
  const inp     = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif" };

  const addUrl = async () => {
    const trimmed = newUrl.trim();
    if (!trimmed) return;
    setAdding(true);
    const result = await addDealIntelUrl(dealId, trimmed, newLabel.trim() || null);
    setAdding(false);
    if (result.error) {
      // Surface the actual cause — usually "relation does not exist"
      // (migration not run) or an RLS rejection. Generic "Add URL
      // failed" was hiding the real problem.
      const msg = String(result.error);
      showToast?.(`❌ Add URL: ${msg.slice(0, 140)}`);
      if (msg.toLowerCase().includes('relation') || msg.toLowerCase().includes('does not exist')) {
        showToast?.('⚠ Run migration 20260629_deal_intelligence.sql');
      }
      return;
    }
    const row = result.row;
    setNewUrl(''); setNewLabel('');
    await refresh();
    // Auto-fetch immediately after add.
    setFetchingId(row.id);
    const fetchResult = await fetchDealIntelUrl(row.id);
    setFetchingId(null);
    if (fetchResult.error) showToast?.(`❌ Fetch: ${fetchResult.error}`);
    else showToast?.(`✓ Fetched "${fetchResult.title || trimmed}"`);
    refresh();
  };

  const refetch = async (row) => {
    setFetchingId(row.id);
    const result = await fetchDealIntelUrl(row.id);
    setFetchingId(null);
    if (result.error) showToast?.(`❌ Fetch: ${result.error}`);
    else showToast?.(`✓ Re-fetched (${result.length?.toLocaleString() || 0} chars)`);
    refresh();
  };

  const remove = async (row) => {
    if (!confirm(`Remove "${row.label || row.url}"?`)) return;
    await deleteDealIntelUrl(row.id);
    refresh();
  };

  return (
    <div style={card}>
      <div style={eyebrow}>URLs (buyer website, news, filings)</div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Label (e.g. APEM website)" style={{ ...inp, width: 200 }} />
        <input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://example.com/about" style={{ ...inp, flex: 1 }} />
        <button onClick={addUrl} disabled={adding || !newUrl.trim()}
          style={{ background: adding ? 'rgba(232,119,34,0.3)' : '#E87722', color: '#fff', border: 'none', borderRadius: 6, padding: '0 14px', fontSize: 11, fontWeight: 700, cursor: adding ? 'wait' : 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
          {adding ? 'Adding…' : '+ Add & fetch'}
        </button>
      </div>
      {urls.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontStyle: 'italic' }}>No URLs yet. Add the buyer's website, recent press, regulatory filings.</div>
      ) : urls.map(u => {
        const fetched = !!u.fetched_at && !u.fetched_error;
        return (
          <div key={u.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center', padding: '8px 10px', borderTop: `1px solid ${C.border}` }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, color: '#fff', fontWeight: 600 }}>
                {u.label || u.fetched_title || u.url}
                {fetched   && <span style={{ marginLeft: 8, fontSize: 9, color: '#34D399' }}>✓ fetched · {u.fetched_content?.length?.toLocaleString() || 0} chars</span>}
                {u.fetched_error && <span style={{ marginLeft: 8, fontSize: 9, color: '#EF4444' }}>⛔ {u.fetched_error}</span>}
              </div>
              <a href={u.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 10, color: '#60A5FA', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{u.url}</a>
            </div>
            <button onClick={() => refetch(u)} disabled={fetchingId === u.id}
              style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.7)', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: fetchingId === u.id ? 'wait' : 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
              {fetchingId === u.id ? '…' : (fetched ? 'Re-fetch' : 'Fetch')}
            </button>
            <button onClick={() => remove(u)}
              style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Remove</button>
          </div>
        );
      })}
    </div>
  );
}

function DealIntelDocsSubpanel({ C, dealId, docs, currentUser, refresh, showToast }) {
  const [uploadProgress, setUploadProgress] = useState(null); // { done, total, current }
  const [sourceNote, setSourceNote] = useState('');
  const [preparingId, setPreparingId] = useState(null);
  const [bulkPrepping, setBulkPrepping] = useState(false);

  const card    = { background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 };
  const eyebrow = { fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 12, fontFamily: "'Montserrat',sans-serif" };

  // Multi-file upload: select N files at once, upload sequentially so
  // we don't blow up S3's concurrency. Progress chip
  // updates after each file. sourceNote is applied to every file in
  // the batch (clear/reuse between batches if you want different notes).
  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const total = files.length;
    let done = 0;
    let failures = 0;
    const note = sourceNote.trim() || null;
    setUploadProgress({ done: 0, total, current: files[0]?.name });
    for (const file of files) {
      setUploadProgress({ done, total, current: file.name });
      const row = await uploadDealIntelDocument(dealId, file, currentUser.id, note);
      if (!row) failures++;
      done++;
    }
    setUploadProgress(null);
    if (failures) showToast?.(`⚠ Uploaded ${total - failures}/${total} — ${failures} failed`);
    else          showToast?.(`✓ Uploaded ${total} doc${total === 1 ? '' : 's'}`);
    setSourceNote('');
    refresh();
  };

  const toggleAi = async (doc) => {
    const next = !doc.ai_enabled;
    setPreparingId(doc.id);
    const result = await setIntelDocumentAiEnabled(doc.id, next);
    setPreparingId(null);
    if (result.error) showToast?.(`❌ AI prep: ${result.error}`);
    else showToast?.(next ? `✓ "${doc.filename}" is AI-ready` : `↩ AI prep cleared`);
    refresh();
  };

  // Bulk-enable AI on every supported doc in the list that isn't
  // already AI-ready. Sequential so we don't hammer the Anthropic
  // Files API. Supports PDF (Files API), DOCX/PPTX (text extraction).
  const isSupportedKind = (d) => {
    const fn = (d.filename || '').toLowerCase();
    return fn.endsWith('.pdf') || fn.endsWith('.docx') || fn.endsWith('.pptx') || /\.(txt|md|csv)$/.test(fn);
  };
  const isAiReadyDoc = (d) => d.ai_enabled && (!!d.anthropic_file_id || !!d.extracted_content);
  const enableAiAll = async () => {
    const pending = docs.filter(d => isSupportedKind(d) && !isAiReadyDoc(d));
    if (!pending.length) { showToast?.('Nothing to prep — all supported docs are already AI-ready.'); return; }
    if (!confirm(`Enable AI on ${pending.length} document${pending.length === 1 ? '' : 's'}? PDFs go to Anthropic Files API; DOCX/PPTX get text-extracted. May take a minute.`)) return;
    setBulkPrepping(true);
    let ok = 0, failed = 0;
    for (const d of pending) {
      setPreparingId(d.id);
      const result = await setIntelDocumentAiEnabled(d.id, true);
      if (result.error) failed++; else ok++;
    }
    setPreparingId(null);
    setBulkPrepping(false);
    showToast?.(failed ? `⚠ Prepped ${ok}/${pending.length} — ${failed} failed` : `✓ ${ok} doc${ok === 1 ? '' : 's'} AI-ready`);
    refresh();
  };

  const remove = async (doc) => {
    if (!confirm(`Remove "${doc.filename}"?`)) return;
    await deleteDealIntelDocument(doc);
    refresh();
  };

  const uploading = !!uploadProgress;
  const pdfPendingCount = docs.filter(d => isSupportedKind(d) && !isAiReadyDoc(d)).length;

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ ...eyebrow, marginBottom: 0 }}>Background documents (research, contracts, anything about the buyer)</div>
        <div style={{ flex: 1 }} />
        {pdfPendingCount > 0 && (
          <button onClick={enableAiAll} disabled={bulkPrepping || uploading}
            title={`Upload ${pdfPendingCount} PDF${pdfPendingCount === 1 ? '' : 's'} to Anthropic Files API so the AI chat can reference them`}
            style={{ background: 'rgba(167,139,250,0.15)', border: '1px solid rgba(167,139,250,0.45)', color: '#A78BFA', borderRadius: 6, padding: '5px 12px', fontSize: 10, fontWeight: 700, cursor: bulkPrepping || uploading ? 'wait' : 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {bulkPrepping ? 'Prepping…' : `🤖 Enable AI on all (${pdfPendingCount})`}
          </button>
        )}
        <input value={sourceNote} onChange={e => setSourceNote(e.target.value)} placeholder="Optional source note (applied to whole batch)"
          style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '5px 10px', fontSize: 11, width: 240, fontFamily: "'Montserrat',sans-serif" }} />
        <label style={{ background: 'rgba(232,119,34,0.15)', border: '1px solid rgba(232,119,34,0.4)', color: '#E87722', borderRadius: 6, padding: '5px 12px', fontSize: 10, fontWeight: 700, cursor: uploading ? 'wait' : 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          {uploading
            ? `Uploading ${uploadProgress.done + 1}/${uploadProgress.total}…`
            : '+ Upload docs (PDF / DOCX / PPTX)'}
          <input type="file" multiple disabled={uploading}
            accept=".pdf,.docx,.pptx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/markdown"
            onChange={onPick} style={{ display: 'none' }} />
        </label>
      </div>
      {uploading && (
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 8, fontStyle: 'italic' }}>
          {uploadProgress.current ? `Uploading "${uploadProgress.current}"…` : 'Uploading…'}
        </div>
      )}
      {docs.length === 0 ? (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontStyle: 'italic' }}>No background documents yet. Upload buyer annual reports, press releases, market research.</div>
      ) : docs.map(d => {
        const fn = (d.filename || '').toLowerCase();
        const isPdf  = (d.mime_type || '').toLowerCase().includes('pdf') || fn.endsWith('.pdf');
        const isDocx = fn.endsWith('.docx');
        const isPptx = fn.endsWith('.pptx');
        const isText = /\.(txt|md|csv)$/.test(fn);
        const isSupported = isPdf || isDocx || isPptx || isText;
        // AI-ready when either file_id (PDF path) OR extracted_content
        // (Office/text path) is present.
        const aiReady   = d.ai_enabled && (!!d.anthropic_file_id || !!d.extracted_content);
        const aiPending = preparingId === d.id;
        return (
          <div key={d.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'center', padding: '10px 0', borderTop: `1px solid ${C.border}` }}>
            <div style={{ minWidth: 0 }}>
              {/* Headline: prefer the Claude-extracted title when present;
                  fall back to the raw filename otherwise. */}
              <div style={{ fontSize: 12, color: '#fff', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {d.extracted_title || d.filename}
                {aiReady   && <span style={{ marginLeft: 8, fontSize: 9, color: '#A78BFA', fontWeight: 700 }}>🤖 AI-ready</span>}
                {aiPending && <span style={{ marginLeft: 8, fontSize: 9, color: '#FFB830', fontWeight: 700 }}>🤖 preparing…</span>}
                {d.ai_enabled && !aiReady && !aiPending && d.ai_prep_error && <span title={d.ai_prep_error} style={{ marginLeft: 8, fontSize: 9, color: '#EF4444', fontWeight: 700 }}>🤖 prep failed</span>}
              </div>
              {/* Second line: extracted summary if we have it, else source note */}
              {d.extracted_summary && (
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 3, lineHeight: 1.4 }}>{d.extracted_summary}</div>
              )}
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 3 }}>
                {d.extracted_title ? <span style={{ marginRight: 6 }}>{d.filename} ·</span> : null}
                {Math.round((d.size_bytes || 0) / 1024).toLocaleString()} KB
                {d.source_note && <span style={{ marginLeft: 8, fontStyle: 'italic' }}>— {d.source_note}</span>}
              </div>
            </div>
            {isSupported ? (
              <label title={d.ai_enabled ? 'Disable AI access to this file' : (isPdf ? 'Upload to Anthropic Files API' : 'Extract text + make available to the AI chat')}
                style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '4px 8px', border: `1px solid ${d.ai_enabled ? 'rgba(167,139,250,0.5)' : C.border}`, background: d.ai_enabled ? 'rgba(167,139,250,0.12)' : 'transparent', borderRadius: 5, cursor: aiPending ? 'wait' : 'pointer', fontSize: 10, fontWeight: 700, color: d.ai_enabled ? '#A78BFA' : 'rgba(255,255,255,0.5)' }}>
                <input type="checkbox" checked={!!d.ai_enabled} disabled={aiPending} onChange={() => toggleAi(d)} style={{ accentColor: '#A78BFA' }} />
                🤖 AI
              </label>
            ) : <span style={{ fontSize: 9, color: '#FFB830' }}>unsupported</span>}
            <button onClick={() => remove(d)}
              style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Remove</button>
          </div>
        );
      })}
    </div>
  );
}

function DealIntelContractsSubpanel({ C, dealId, contracts, docs, refresh, showToast }) {
  const [editing, setEditing] = useState(null);
  const card    = { background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18, marginBottom: 14 };
  const eyebrow = { fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 12, fontFamily: "'Montserrat',sans-serif" };
  const inp     = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 12, width: '100%', fontFamily: "'Montserrat',sans-serif" };

  const startEdit = (c) => setEditing(c || {
    title: '', parties: '', status: 'active', summary: '', document_id: null,
  });
  const submit = async () => {
    if (!editing.title?.trim()) return;
    const row = await upsertDealIntelContract(dealId, editing);
    if (!row) { showToast?.('❌ Save failed'); return; }
    setEditing(null);
    refresh();
  };
  const remove = async (c) => {
    if (!confirm(`Remove "${c.title}"?`)) return;
    await deleteDealIntelContract(c.id);
    refresh();
  };

  return (
    <div style={card}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 12 }}>
        <div style={eyebrow}>Mutual / reference contracts</div>
        <div style={{ flex: 1 }} />
        {!editing && (
          <button onClick={() => startEdit(null)}
            style={{ background: 'rgba(96,165,250,0.15)', border: '1px solid rgba(96,165,250,0.4)', color: '#60A5FA', borderRadius: 6, padding: '5px 12px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            + Add contract
          </button>
        )}
      </div>

      {editing && (
        <div style={{ border: `1px solid rgba(96,165,250,0.4)`, borderRadius: 8, padding: 12, marginBottom: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 8 }}>
            <input value={editing.title || ''} onChange={e => setEditing({ ...editing, title: e.target.value })} placeholder="Contract title" style={inp} />
            <input value={editing.parties || ''} onChange={e => setEditing({ ...editing, parties: e.target.value })} placeholder="Parties (e.g. 40Geo + CSA)" style={inp} />
            <select value={editing.status || 'active'} onChange={e => setEditing({ ...editing, status: e.target.value })} style={inp}>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="archived">Archived</option>
            </select>
          </div>
          <textarea rows={3} value={editing.summary || ''} onChange={e => setEditing({ ...editing, summary: e.target.value })}
            placeholder="Short summary — terms, dates, why it matters to this deal"
            style={{ ...inp, marginTop: 8, resize: 'vertical' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <select value={editing.document_id || ''} onChange={e => setEditing({ ...editing, document_id: e.target.value || null })} style={{ ...inp, flex: 1 }}>
              <option value="">(no attached document)</option>
              {docs.map(d => <option key={d.id} value={d.id}>{d.filename}</option>)}
            </select>
            <button onClick={() => setEditing(null)}
              style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 6, padding: '6px 14px', fontSize: 11, cursor: 'pointer' }}>Cancel</button>
            <button onClick={submit}
              style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>Save</button>
          </div>
        </div>
      )}

      {contracts.length === 0 && !editing ? (
        <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11, fontStyle: 'italic' }}>No contracts yet. Add the 40Geo × CSA partnership, NDAs, prior agreements that are relevant context.</div>
      ) : contracts.map(c => (
        <div key={c.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 10, alignItems: 'flex-start', padding: '8px 0', borderTop: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: 12, color: '#fff', fontWeight: 700 }}>{c.title}
              {c.status && <span style={{ marginLeft: 8, fontSize: 9, padding: '2px 6px', borderRadius: 3, background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{c.status}</span>}
            </div>
            {c.parties && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>{c.parties}</div>}
            {c.summary && <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 4, lineHeight: 1.5 }}>{c.summary}</div>}
          </div>
          <button onClick={() => startEdit(c)}
            style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Edit</button>
          <button onClick={() => remove(c)}
            style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 5, padding: '4px 10px', fontSize: 10, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Remove</button>
        </div>
      ))}
    </div>
  );
}

function DealMembersPanel({ C, deal, members, orgUsers, currentUser, onSaveDeal, onMembersChanged, showToast }) {
  const [adding, setAdding]     = useState(false);
  const [pickUser, setPickUser] = useState(null);   // candidate user being configured
  const [pickPerms, setPickPerms] = useState(null); // perms draft for the candidate
  const [editingId, setEditingId] = useState(null); // existing deal_members row being edited

  // Reviewer defaults editor (lives on the deal row).
  const dflt = deal.reviewer_default_perms || defaultReviewerPerms();
  const [revDraft, setRevDraft] = useState(dflt);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setRevDraft(deal.reviewer_default_perms || defaultReviewerPerms()); }, [deal.id]);

  const saveReviewerDefaults = async () => {
    const ok = await saveReviewerDefaultPerms(deal.id, revDraft);
    if (ok) { showToast?.('✓ Reviewer defaults saved'); onSaveDeal?.({ reviewer_default_perms: revDraft }); }
    else    showToast?.('❌ Save failed');
  };

  // Members that are NOT already in deal_members (so we can add them).
  const memberIds  = new Set(members.map(m => m.user_id));
  const candidates = orgUsers.filter(u => !memberIds.has(u.id) && u.id !== currentUser.id);

  // ── Add-member flow ──────────────────────────────────────────
  const startAdd = (user) => {
    setPickUser(user);
    setPickPerms(deal.reviewer_default_perms || defaultReviewerPerms());
  };
  const cancelAdd = () => { setPickUser(null); setPickPerms(null); };
  const submitAdd = async () => {
    if (!pickUser) return;
    const ok = await upsertDealMember(deal.id, pickUser.id, 'reviewer', pickPerms);
    if (!ok) { showToast?.('❌ Add failed'); return; }
    showToast?.(`✓ ${pickUser.name} added with custom access`);
    cancelAdd(); setAdding(false);
    onMembersChanged?.();
  };

  // ── Edit-member flow ─────────────────────────────────────────
  const editing = members.find(m => m.user_id === editingId) || null;
  const [editPerms, setEditPerms] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setEditPerms(editing?.tab_perms || deal.reviewer_default_perms || defaultReviewerPerms()); }, [editingId]);

  const submitEdit = async () => {
    if (!editing) return;
    const ok = await upsertDealMember(deal.id, editing.user_id, editing.role, editPerms);
    if (!ok) { showToast?.('❌ Save failed'); return; }
    showToast?.('✓ Access updated');
    setEditingId(null);
    onMembersChanged?.();
  };
  const removeMember = async (m) => {
    const u = orgUsers.find(x => x.id === m.user_id);
    if (!confirm(`Remove ${u?.name || m.user_id} from this deal? They'll fall back to the reviewer defaults (or lose access if they're not in the reviewer company).`)) return;
    const ok = await removeDealMember(deal.id, m.user_id);
    if (!ok) { showToast?.('❌ Remove failed'); return; }
    onMembersChanged?.();
  };

  // ── Render ───────────────────────────────────────────────────
  const card    = { background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 16, marginBottom: 14 };
  const eyebrow = { fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 10, fontFamily: "'Montserrat',sans-serif" };

  return (
    <div style={{ animation: 'fadeUp 0.3s ease' }}>
      {/* Reviewer defaults card */}
      <div style={card}>
        <div style={eyebrow}>Reviewer defaults — applies to every {deal.reviewer_company || 'reviewer-company'} user without a custom override</div>
        <PermsGrid C={C} perms={revDraft} onChange={setRevDraft} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
          <button onClick={() => setRevDraft(defaultReviewerPerms())}
            style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Reset</button>
          <button onClick={saveReviewerDefaults}
            style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 5, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Save defaults</button>
        </div>
      </div>

      {/* Member list */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
          <div style={eyebrow}>Per-user overrides</div>
          <div style={{ flex: 1 }} />
          <button onClick={() => setAdding(v => !v)}
            style={{ background: adding ? 'rgba(232,119,34,0.2)' : '#E87722', border: 'none', color: '#fff', borderRadius: 6, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>
            {adding ? 'Cancel' : '+ Add member'}
          </button>
        </div>

        {adding && (
          <div style={{ background: 'rgba(232,119,34,0.06)', border: '1px solid rgba(232,119,34,0.2)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
            {!pickUser ? (
              <>
                <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Pick a user</div>
                <div style={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 6 }}>
                  {candidates.map(u => (
                    <button key={u.id} onClick={() => startAdd(u)}
                      style={{ display: 'block', width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: `1px solid ${C.border}`, padding: '8px 12px', color: '#fff', fontSize: 12, fontFamily: "'Montserrat',sans-serif", cursor: 'pointer' }}>
                      <span style={{ fontWeight: 700 }}>{u.name}</span>
                      <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: 8 }}>{u.email}</span>
                      {u.company && <span style={{ marginLeft: 8, fontSize: 9, color: '#E87722', border: '1px solid rgba(232,119,34,0.4)', borderRadius: 3, padding: '1px 6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{u.company}</span>}
                    </button>
                  ))}
                  {!candidates.length && <div style={{ padding: 12, color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>No more org users to add.</div>}
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 12, color: '#fff', marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>
                  Configure access for <strong>{pickUser.name}</strong> <span style={{ color: 'rgba(255,255,255,0.4)' }}>({pickUser.email})</span>
                </div>
                <PermsGrid C={C} perms={pickPerms} onChange={setPickPerms} />
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12, gap: 8 }}>
                  <button onClick={cancelAdd}
                    style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '6px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Cancel</button>
                  <button onClick={submitAdd}
                    style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 5, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Add member</button>
                </div>
              </>
            )}
          </div>
        )}

        {/* Existing overrides */}
        {!members.length && (
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', padding: '6px 0' }}>
            No per-user overrides yet. Reviewer-company users get the defaults above.
          </div>
        )}
        {members.map(m => {
          const u = orgUsers.find(x => x.id === m.user_id);
          const isEditing = editingId === m.user_id;
          const perms = m.tab_perms || deal.reviewer_default_perms || defaultReviewerPerms();
          return (
            <div key={m.user_id} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, marginBottom: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat',sans-serif" }}>{u?.name || m.user_id}</div>
                  <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)' }}>{u?.email || ''} · role: {m.role}</div>
                </div>
                {!isEditing && (
                  <>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      {PERM_TABS.map(tab => (
                        <span key={tab} style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3, background: perms[tab]?.view ? 'rgba(52,211,153,0.12)' : 'rgba(255,255,255,0.04)', color: perms[tab]?.view ? '#34D399' : 'rgba(255,255,255,0.3)', border: `1px solid ${perms[tab]?.view ? 'rgba(52,211,153,0.3)' : 'rgba(255,255,255,0.1)'}`, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700, textDecoration: perms[tab]?.view ? 'none' : 'line-through' }}>
                          {TAB_LABEL[tab]}
                        </span>
                      ))}
                      <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 3, background: perms.diligence?.download ? 'rgba(96,165,250,0.12)' : 'rgba(255,184,48,0.12)', color: perms.diligence?.download ? '#60A5FA' : '#FFB830', border: `1px solid ${perms.diligence?.download ? 'rgba(96,165,250,0.3)' : 'rgba(255,184,48,0.3)'}`, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>
                        {perms.diligence?.download ? '↓ download' : '🔒 view-only'}
                      </span>
                    </div>
                    <button onClick={() => setEditingId(m.user_id)}
                      style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif", textTransform: 'uppercase', letterSpacing: '0.06em' }}>Edit</button>
                    <button onClick={() => removeMember(m)}
                      style={{ background: 'rgba(255,107,107,0.1)', border: '1px solid rgba(255,107,107,0.3)', color: '#FF6B6B', borderRadius: 5, padding: '4px 10px', fontSize: 10, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Remove</button>
                  </>
                )}
              </div>
              {isEditing && (
                <div style={{ marginTop: 10 }}>
                  <PermsGrid C={C} perms={editPerms} onChange={setEditPerms} />
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10, gap: 8 }}>
                    <button onClick={() => setEditingId(null)}
                      style={{ background: 'transparent', border: `1px solid ${C.border}`, color: 'rgba(255,255,255,0.6)', borderRadius: 5, padding: '5px 12px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Cancel</button>
                    <button onClick={submitEdit}
                      style={{ background: '#E87722', border: 'none', color: '#fff', borderRadius: 5, padding: '5px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Montserrat',sans-serif" }}>Save</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Default-perms object — used when a deal hasn't been configured yet.
// Restrictive by default for reviewers (diligence + orgchart view, can
// download diligence). Admin opens up more via the Reviewer-defaults panel
// or the per-user override.
function defaultReviewerPerms() {
  return {
    diligence: { view: true,  download: true  },
    orgchart:  { view: true  },
    strategy:  { view: false },
    earnout:   { view: false },
    economics: { view: false },
  };
}

// Grid of checkboxes for the perm shape. Used in both the reviewer-default
// editor and the per-user override editor.
function PermsGrid({ C, perms, onChange }) {
  const toggle = (tab, key) => onChange({
    ...perms,
    [tab]: { ...(perms[tab] || {}), [key]: !perms?.[tab]?.[key] },
  });
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 10 }}>
      {PERM_TABS.map(tab => (
        <div key={tab} style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 6, padding: '10px 12px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#fff', fontFamily: "'Montserrat',sans-serif", marginBottom: 6 }}>{TAB_LABEL[tab]}</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!perms?.[tab]?.view} onChange={() => toggle(tab, 'view')} />
            Can view
          </label>
          {tab === 'diligence' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'rgba(255,255,255,0.7)', cursor: 'pointer', marginTop: 4 }}>
              <input type="checkbox" disabled={!perms?.diligence?.view} checked={!!perms?.[tab]?.download} onChange={() => toggle(tab, 'download')} />
              Can download
            </label>
          )}
        </div>
      ))}
    </div>
  );
}

function EarnoutCalc({ C, deal, onSave, isSeller }) {
  const [d, setD] = useState(deal);
  // Sync local draft when parent's deal prop changes (e.g. tab switch).
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setD(deal), [deal]);

  const valuation    = Number(d.deal_value) || 0;
  const buyPct       = Number(d.buy_pct ?? 100);
  const purchase     = valuation * (buyPct / 100);
  const upfront      = purchase * Number(d.upfront_pct) / 100;
  const pool         = purchase - upfront;
  const termYears    = Math.max(1, Math.min(5, Number(d.term_years) || 3));
  const targets      = Array.from({ length: termYears }, (_, i) => Number(d[`year${i + 1}_target`]) || 0);
  const actuals      = Array.from({ length: termYears }, (_, i) => Number(d[`year${i + 1}_actual`]) || 0);
  // Tranche split — use stored split if it matches term, else default to
  // even split with the remainder on the last year (so 3 yrs = 33/33/34).
  const split = (() => {
    const stored = Array.isArray(d.tranche_split) ? d.tranche_split : null;
    if (stored && stored.length === termYears) return stored;
    const base = Math.floor(100 / termYears);
    return Array.from({ length: termYears }, (_, i) => i === termYears - 1 ? 100 - base * (termYears - 1) : base);
  })();
  const floorPct     = Number(d.curve_floor_pct);
  const floorMult    = Number(d.curve_floor_mult);
  const ceilingPct   = Number(d.curve_ceiling_pct);
  const ceilingMult  = Number(d.curve_ceiling_mult);

  const perYear = targets.map((t, i) => {
    const tranche = pool * (split[i] / 100);
    const ratio   = t > 0 && actuals[i] ? (actuals[i] / t) * 100 : null;
    const mult    = ratio == null ? null : earnoutMultiplier(ratio, floorPct, floorMult, ceilingPct, ceilingMult);
    const payout  = mult == null ? null : tranche * mult;
    return { tranche, ratio, mult, payout };
  });

  const projected = perYear.reduce((s, y) => s + (y.payout || 0), 0);
  const totalIfAllFloor = pool * floorMult;
  const totalIfAllCeil  = pool * ceilingMult;

  const update = (k, v) => { const next = { ...d, [k]: v }; setD(next); onSave({ [k]: v }); };

  // ── SVG curve ───────────────────────────────────────────────
  const W = 520, H = 220, PAD = 30;
  const xOf = (pct) => PAD + (pct / 200) * (W - PAD * 2);
  const yOf = (mul) => H - PAD - (mul / 2.0) * (H - PAD * 2);
  const curvePath = (() => {
    const pts = [];
    for (let p = 0; p <= 200; p += 2) {
      const m = earnoutMultiplier(p, floorPct, floorMult, ceilingPct, ceilingMult);
      pts.push(`${pts.length === 0 ? 'M' : 'L'} ${xOf(p)} ${yOf(m)}`);
    }
    return pts.join(' ');
  })();

  const styles = {
    card:   { background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 16 },
    lbl:    { fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, display: 'block', fontFamily: "'Montserrat',sans-serif" },
    inp:    { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%' },
    kpi:    { textAlign: 'center' },
    kpiN:   { fontSize: 22, fontWeight: 800, color: '#E87722', fontFamily: "'Montserrat',sans-serif" },
    kpiL:   { fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em', marginTop: 2 },
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 18 }}>
      {/* Inputs */}
      <div style={styles.card}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 12 }}>Scenario</div>
        {Array.from({ length: termYears }, (_, i) => i + 1).map(n => {
          const k = `year${n}_actual`;
          return (
            <div key={k} style={{ marginBottom: 10 }}>
              <label style={styles.lbl}>Year {n} actual</label>
              <input type="number" disabled={!isSeller} value={d[k] ?? ''} onChange={e => update(k, e.target.value === '' ? null : Number(e.target.value))} style={styles.inp} />
            </div>
          );
        })}

        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 12, marginTop: 12, fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 12 }}>Curve anchors</div>
        {[
          ['Floor %',     'curve_floor_pct',    1],
          ['Floor mult',  'curve_floor_mult',   0.05],
          ['Ceiling %',   'curve_ceiling_pct',  1],
          ['Ceiling mult','curve_ceiling_mult', 0.05],
        ].map(([label, k, step]) => (
          <div key={k} style={{ marginBottom: 10 }}>
            <label style={styles.lbl}>{label}</label>
            <input type="number" step={step} disabled={!isSeller} value={d[k] ?? ''} onChange={e => update(k, Number(e.target.value))} style={styles.inp} />
          </div>
        ))}
      </div>

      {/* Curve + summary */}
      <div>
        <div style={styles.card}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 12 }}>Curve & performance</div>
          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: '100%', display: 'block' }}>
            {/* gridlines */}
            {[0, 50, 100, 150, 200].map(p => (
              <g key={`gx-${p}`}>
                <line x1={xOf(p)} y1={PAD} x2={xOf(p)} y2={H - PAD} stroke="rgba(255,255,255,0.07)" />
                <text x={xOf(p)} y={H - PAD + 14} fontSize="9" fill="rgba(255,255,255,0.4)" textAnchor="middle">{p}%</text>
              </g>
            ))}
            {[0, 0.5, 1.0, 1.5, 2.0].map(m => (
              <g key={`gy-${m}`}>
                <line x1={PAD} y1={yOf(m)} x2={W - PAD} y2={yOf(m)} stroke="rgba(255,255,255,0.07)" />
                <text x={PAD - 6} y={yOf(m) + 3} fontSize="9" fill="rgba(255,255,255,0.4)" textAnchor="end">{m.toFixed(1)}×</text>
              </g>
            ))}
            {/* 100% target line */}
            <line x1={xOf(100)} y1={PAD} x2={xOf(100)} y2={H - PAD} stroke="#fff" strokeOpacity="0.18" strokeDasharray="4 4" />
            <line x1={PAD} y1={yOf(1)} x2={W - PAD} y2={yOf(1)} stroke="#fff" strokeOpacity="0.18" strokeDasharray="4 4" />
            {/* curve */}
            <path d={curvePath} fill="none" stroke="#E87722" strokeWidth="2.5" />
            {/* per-year dots */}
            {perYear.map((y, i) => y.ratio == null ? null : (
              <g key={i}>
                <circle cx={xOf(Math.min(y.ratio, 200))} cy={yOf(Math.min(y.mult, 2))} r="6" fill="#34D399" stroke="#0d1b2a" strokeWidth="2" />
                <text x={xOf(Math.min(y.ratio, 200)) + 9} y={yOf(Math.min(y.mult, 2)) - 6} fontSize="10" fill="#34D399">Y{i + 1}</text>
              </g>
            ))}
          </svg>
        </div>

        {/* Top row — deal structure summary */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 12 }}>
          <div style={{ ...styles.card, ...styles.kpi }}>
            <div style={{ ...styles.kpiN, color: '#fff', fontSize: 16 }}>{fmtMoney(valuation, d.currency)}</div>
            <div style={styles.kpiL}>Valuation</div>
          </div>
          <div style={{ ...styles.card, ...styles.kpi }}>
            <div style={{ ...styles.kpiN, color: '#fff', fontSize: 16 }}>{buyPct}%</div>
            <div style={styles.kpiL}>Buy %</div>
          </div>
          <div style={{ ...styles.card, ...styles.kpi }}>
            <div style={{ ...styles.kpiN, fontSize: 18 }}>{fmtMoney(purchase, d.currency)}</div>
            <div style={styles.kpiL}>Purchase price</div>
          </div>
          <div style={{ ...styles.card, ...styles.kpi }}>
            <div style={{ ...styles.kpiN, color: '#fff', fontSize: 16 }}>{termYears} yr</div>
            <div style={styles.kpiL}>Term</div>
          </div>
        </div>

        {/* Second row — earn-out scenario outcome */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginTop: 12 }}>
          <div style={{ ...styles.card, ...styles.kpi }}>
            <div style={styles.kpiN}>{fmtMoney(upfront, d.currency)}</div>
            <div style={styles.kpiL}>Upfront</div>
          </div>
          <div style={{ ...styles.card, ...styles.kpi }}>
            <div style={styles.kpiN}>{fmtMoney(projected, d.currency)}</div>
            <div style={styles.kpiL}>Earn-out (scenario)</div>
          </div>
          <div style={{ ...styles.card, ...styles.kpi }}>
            <div style={{ ...styles.kpiN, color: '#34D399' }}>{fmtMoney(upfront + projected, d.currency)}</div>
            <div style={styles.kpiL}>Total realised</div>
          </div>
          <div style={{ ...styles.card, ...styles.kpi }}>
            <div style={{ fontSize: 11, color: '#fff', fontFamily: "'Montserrat',sans-serif" }}>
              <div>floor&nbsp;{fmtMoney(upfront + totalIfAllFloor, d.currency)}</div>
              <div style={{ marginTop: 3 }}>max&nbsp;&nbsp;&nbsp;{fmtMoney(upfront + totalIfAllCeil, d.currency)}</div>
            </div>
            <div style={styles.kpiL}>Range</div>
          </div>
        </div>

        <div style={{ ...styles.card, marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.15em', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', marginBottom: 10 }}>Per-year breakdown</div>
          <table style={{ width: '100%', fontSize: 12, color: '#ddd', borderCollapse: 'collapse', fontFamily: "'Montserrat',sans-serif" }}>
            <thead>
              <tr style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                <th style={{ textAlign: 'left',  padding: '6px 8px' }}>Year</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Target</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Actual</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Ratio</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Multiplier</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Tranche</th>
                <th style={{ textAlign: 'right', padding: '6px 8px' }}>Payout</th>
              </tr>
            </thead>
            <tbody>
              {perYear.map((y, i) => (
                <tr key={i} style={{ borderTop: `1px solid ${C.border}` }}>
                  <td style={{ padding: '6px 8px' }}>Year {i + 1}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtMoney(targets[i], d.currency)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{actuals[i] ? fmtMoney(actuals[i], d.currency) : '—'}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{y.ratio == null ? '—' : `${y.ratio.toFixed(0)}%`}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{y.mult == null ? '—' : `${y.mult.toFixed(2)}×`}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right' }}>{fmtMoney(y.tranche, d.currency)}</td>
                  <td style={{ padding: '6px 8px', textAlign: 'right', color: '#E87722', fontWeight: 700 }}>{y.payout == null ? '—' : fmtMoney(y.payout, d.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_ECON_PROJECTION = {
  base_revenue: 1750000,
  base_year:    2026,
  growth: {
    y1: { low: 0, mid: 5, high: 10 },
    y2: { low: 0, mid: 5, high: 10 },
    y3: { low: 0, mid: 5, high: 10 },
  },
};

// Compute scenario revenues at year 1/2/3. Growth rates compound
// WITHIN scenario — so mid-y2 builds on mid-y1, not on base.
function computeProjectionScenarios(proj) {
  const base = Number(proj?.base_revenue) || 0;
  const g    = proj?.growth || {};
  const path = (scenarioKey) => {
    const r1 = base * (1 + (Number(g.y1?.[scenarioKey]) || 0) / 100);
    const r2 = r1   * (1 + (Number(g.y2?.[scenarioKey]) || 0) / 100);
    const r3 = r2   * (1 + (Number(g.y3?.[scenarioKey]) || 0) / 100);
    return [r1, r2, r3];
  };
  return { low: path('low'), mid: path('mid'), high: path('high') };
}

function EconomicsEditor({ C, deal, onSave, isSeller }) {
  const [d, setD] = useState(deal);
  // Sync local draft when parent's deal prop changes (e.g. tab switch).
  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setD(deal), [deal]);

  const update = (k, v) => { const next = { ...d, [k]: v }; setD(next); onSave({ [k]: v }); };

  // Changing the term needs to re-balance the tranche split too — otherwise
  // a 5-year term keeps the stale 33/33/34 from a previous 3-year config.
  const updateTerm = (n) => {
    const term = Math.max(1, Math.min(5, Number(n)));
    const base = Math.floor(100 / term);
    const tranche_split = Array.from({ length: term }, (_, i) =>
      i === term - 1 ? 100 - base * (term - 1) : base
    );
    const next = { ...d, term_years: term, tranche_split };
    setD(next);
    onSave({ term_years: term, tranche_split });
  };

  const termYears = Math.max(1, Math.min(5, Number(d.term_years) || 3));
  const purchase  = (Number(d.deal_value) || 0) * (Number(d.buy_pct ?? 100) / 100);

  const lbl = { fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, display: 'block', fontFamily: "'Montserrat',sans-serif" };
  const inp = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '8px 10px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%' };

  return (
    <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 10, padding: 18 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {/* Identification */}
        {[
          ['name',            'Deal name',        'text'],
          ['status',          'Status',           'text'],
          ['reviewer_company','Reviewer company', 'text'],
        ].map(([k, label, type]) => (
          <div key={k}>
            <label style={lbl}>{label}</label>
            <input type={type} disabled={!isSeller} value={d[k] ?? ''}
              onChange={e => update(k, type === 'number' ? Number(e.target.value) : e.target.value)} style={inp} />
          </div>
        ))}

        {/* Deal structure */}
        <div>
          <label style={lbl}>Currency</label>
          <input type="text" disabled={!isSeller} value={d.currency ?? 'USD'} onChange={e => update('currency', e.target.value)} style={inp} />
        </div>
        <div>
          <label style={lbl}>Company valuation</label>
          <input type="number" disabled={!isSeller} value={d.deal_value ?? ''} onChange={e => update('deal_value', Number(e.target.value))} style={inp} />
        </div>
        <div>
          <label style={lbl}>Buy %</label>
          <input type="number" min="1" max="100" disabled={!isSeller} value={d.buy_pct ?? 100} onChange={e => update('buy_pct', Number(e.target.value))} style={inp} />
        </div>

        {/* Computed purchase price */}
        <div style={{ gridColumn: '1 / -1', padding: '8px 10px', background: 'rgba(232,119,34,0.08)', border: '1px solid rgba(232,119,34,0.25)', borderRadius: 6, display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Purchase price</span>
          <span style={{ fontSize: 16, fontWeight: 800, color: '#E87722', fontFamily: "'Montserrat',sans-serif" }}>
            {fmtMoney(purchase, d.currency)}
          </span>
          <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>= valuation × buy %. Upfront and earn-out scale off this.</span>
        </div>

        <div>
          <label style={lbl}>Upfront %</label>
          <input type="number" disabled={!isSeller} value={d.upfront_pct ?? ''} onChange={e => update('upfront_pct', Number(e.target.value))} style={inp} />
        </div>
        <div>
          <label style={lbl}>Earn-out term (years)</label>
          <select disabled={!isSeller} value={termYears} onChange={e => updateTerm(e.target.value)} style={inp}>
            {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n} {n === 1 ? 'year' : 'years'}</option>)}
          </select>
        </div>
        <div /> {/* spacer to keep the 3-col grid clean */}

        {/* Dynamic year targets */}
        {Array.from({ length: termYears }, (_, i) => i + 1).map(n => (
          <div key={n}>
            <label style={lbl}>Year {n} revenue target</label>
            <input type="number" disabled={!isSeller} value={d[`year${n}_target`] ?? ''}
              onChange={e => update(`year${n}_target`, e.target.value === '' ? null : Number(e.target.value))} style={inp} />
          </div>
        ))}

        <div style={{ gridColumn: '1 / -1' }}>
          <label style={lbl}>Description</label>
          <textarea rows={3} disabled={!isSeller} value={d.description ?? ''}
            onChange={e => update('description', e.target.value)} style={{ ...inp, resize: 'vertical' }} />
        </div>

        {/* AI Chat toggle — seller-only. When on, every user with deal
            access sees the floating chat panel docked across all tabs.
            Reviewers see the toggle state as read-only context. */}
        <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', background: 'rgba(167,139,250,0.06)', border: '1px solid rgba(167,139,250,0.25)', borderRadius: 8 }}>
          <span style={{ fontSize: 18 }}>🤝</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, color: '#fff' }}>AI Chat for this deal</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
              When on, a chat panel docks across every tab. Users can ask about strategy + merger thesis and attach diligence PDFs for the AI to analyze. Per-user threads (private per user).
            </div>
          </div>
          {isSeller ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={!!d.ai_chat_enabled}
                onChange={e => update('ai_chat_enabled', e.target.checked)} />
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: d.ai_chat_enabled ? '#A78BFA' : 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {d.ai_chat_enabled ? 'On' : 'Off'}
              </span>
            </label>
          ) : (
            <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: d.ai_chat_enabled ? '#A78BFA' : 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {d.ai_chat_enabled ? 'Enabled' : 'Disabled'}
            </span>
          )}
        </div>
      </div>

      <ProjectionPanel C={C} d={d} setD={setD} onSave={onSave} isSeller={isSeller} />
    </div>
  );
}

// ─── 3-year revenue projection (low / mid / high) ─────────────────
// Persisted on the deal as `economics_projection` (JSONB). The
// "Sync to earn-out" button copies the selected scenario's projected
// revenues into deal.year{1,2,3}_target so the existing earn-out
// math picks them up automatically.
function ProjectionPanel({ C, d, setD, onSave, isSeller }) {
  const proj = d.economics_projection || DEFAULT_ECON_PROJECTION;
  const updateProj = (next) => {
    setD({ ...d, economics_projection: next });
    onSave({ economics_projection: next });
  };
  const setGrowth = (yearKey, scenarioKey, pct) => {
    const next = {
      ...proj,
      growth: {
        ...proj.growth,
        [yearKey]: { ...(proj.growth?.[yearKey] || {}), [scenarioKey]: pct === '' ? 0 : Number(pct) },
      },
    };
    updateProj(next);
  };

  const scenarios = computeProjectionScenarios(proj);
  const maxVal = Math.max(...scenarios.low, ...scenarios.mid, ...scenarios.high, 1);
  const COLORS = { low: '#EF4444', mid: '#E87722', high: '#22C55E' };
  const LABELS = { low: 'Low', mid: 'Mid', high: 'High' };

  const [syncScenario, setSyncScenario] = useState('mid');
  const [syncedAt, setSyncedAt] = useState(null);
  const syncToEarnout = () => {
    const revs = scenarios[syncScenario].map(v => Math.round(v));
    const patch = { year1_target: revs[0], year2_target: revs[1], year3_target: revs[2] };
    setD({ ...d, ...patch });
    onSave(patch);
    setSyncedAt(syncScenario);
    setTimeout(() => setSyncedAt(null), 2500);
  };

  const lbl = { fontSize: 9, color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4, display: 'block', fontFamily: "'Montserrat',sans-serif" };
  const inp = { background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, color: '#fff', borderRadius: 6, padding: '6px 8px', fontSize: 12, fontFamily: "'Montserrat',sans-serif", width: '100%', textAlign: 'center' };
  const yearLabel = (i) => `Year ${i + 1} (${Number(proj.base_year || 2026) + i + 1})`;

  return (
    <div style={{ marginTop: 22, paddingTop: 18, borderTop: `1px solid ${C.border}` }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 800, color: '#fff', letterSpacing: '0.04em', textTransform: 'uppercase' }}>3-Year Revenue Projection</div>
          <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 2 }}>Low / Mid / High scenarios compound year over year from the base revenue.</div>
        </div>
      </div>

      {/* Base revenue + base year */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div>
          <label style={lbl}>Base revenue ({proj.base_year || 2026})</label>
          <input type="number" disabled={!isSeller} value={proj.base_revenue ?? 0}
            onChange={e => updateProj({ ...proj, base_revenue: e.target.value === '' ? 0 : Number(e.target.value) })}
            style={{ ...inp, textAlign: 'left' }} />
        </div>
        <div>
          <label style={lbl}>Base year</label>
          <input type="number" disabled={!isSeller} value={proj.base_year ?? 2026}
            onChange={e => updateProj({ ...proj, base_year: e.target.value === '' ? 2026 : Number(e.target.value) })}
            style={{ ...inp, textAlign: 'left' }} />
        </div>
      </div>

      {/* Growth-rate grid: 3 scenarios × 3 years */}
      <div style={{ display: 'grid', gridTemplateColumns: '90px repeat(3, 1fr)', gap: 8, alignItems: 'center', marginBottom: 18 }}>
        <div />
        {[0, 1, 2].map(i => (
          <div key={i} style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
            {yearLabel(i)}
          </div>
        ))}
        {['low', 'mid', 'high'].map(s => (
          <Fragment key={s}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: COLORS[s] }}>{LABELS[s]} %</div>
            {['y1', 'y2', 'y3'].map(y => (
              <input key={y} type="number" disabled={!isSeller} value={proj.growth?.[y]?.[s] ?? 0}
                onChange={e => setGrowth(y, s, e.target.value)} style={inp} />
            ))}
          </Fragment>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '18px 14px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, height: 200 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', gap: 8, height: 170, width: '100%' }}>
                {['low', 'mid', 'high'].map(s => {
                  const v = scenarios[s][i];
                  const h = (v / maxVal) * 100;
                  return (
                    <div key={s} title={`${LABELS[s]} · ${fmtMoney(v, d.currency)}`} style={{ width: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, justifyContent: 'flex-end', height: '100%' }}>
                      <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, color: 'rgba(255,255,255,0.5)', whiteSpace: 'nowrap' }}>{fmtMoney(v, d.currency)}</div>
                      <div style={{ width: '100%', height: `${h}%`, background: COLORS[s], borderRadius: '4px 4px 0 0', minHeight: 3, transition: 'height 0.3s' }} />
                    </div>
                  );
                })}
              </div>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>{yearLabel(i)}</div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          {['low', 'mid', 'high'].map(s => (
            <span key={s} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 12, height: 12, background: COLORS[s], borderRadius: 2 }} />
              <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{LABELS[s]}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Sync to earn-out */}
      {isSeller && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16, padding: '12px 14px', background: 'rgba(232,119,34,0.06)', border: '1px solid rgba(232,119,34,0.2)', borderRadius: 8 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, color: '#fff' }}>Sync with earn-out strategy</div>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: 'rgba(255,255,255,0.5)', marginTop: 2 }}>
              Pushes the selected scenario's revenues into Year 1–3 targets used by the earn-out tab.
            </div>
          </div>
          <select value={syncScenario} onChange={e => setSyncScenario(e.target.value)}
            style={{ ...inp, width: 110, textAlign: 'left' }}>
            <option value="low">Low</option>
            <option value="mid">Mid</option>
            <option value="high">High</option>
          </select>
          <button onClick={syncToEarnout}
            style={{ background: '#E87722', color: '#fff', border: 'none', borderRadius: 6, padding: '8px 14px', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            {syncedAt ? `✓ Synced ${LABELS[syncedAt]}` : 'Sync to earn-out'}
          </button>
        </div>
      )}
    </div>
  );
}

function CRMAnalytics({ deals, contracts }) {
  const won=deals.filter(d=>d.stage==='closed_won'), lost=deals.filter(d=>d.stage==='closed_lost'), open=deals.filter(d=>!['closed_won','closed_lost'].includes(d.stage));
  const totalWon=won.reduce((s,d)=>s+d.value,0), totalPipe=open.reduce((s,d)=>s+d.value*(d.probability/100),0), totalArr=computeArr(deals,contracts).total, wr=(won.length+lost.length)>0?Math.round(won.length/(won.length+lost.length)*100):0;
  const stageCounts=CRM_STAGES.map(s=>({...s,count:deals.filter(d=>d.stage===s.id).length,value:deals.filter(d=>d.stage===s.id).reduce((sum,d)=>sum+d.value,0)})), maxSV=Math.max(...stageCounts.map(s=>s.value),1);
  const now=new Date(), months=Array.from({length:6},(_,i)=>{const d=new Date(now.getFullYear(),now.getMonth()-5+i,1);return{label:d.toLocaleDateString('en',{month:'short'}),y:d.getFullYear(),m:d.getMonth()};});
  const mRev=months.map(m=>({...m,v:won.filter(d=>{const cd=new Date(d.wonDate||d.closeDate||d.createdAt);return cd.getMonth()===m.m&&cd.getFullYear()===m.y;}).reduce((s,d)=>s+d.value,0)})), maxM=Math.max(...mRev.map(m=>m.v),1);
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
      <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'20px 22px'}}>
        <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:16}}>Revenue Summary</div>
        {[{l:'Closed Won (Total)',v:fmtDollars(totalWon),c:'#22C55E'},{l:'Weighted Pipeline',v:fmtDollars(totalPipe),c:'#E87722'},{l:'Annual Recurring',v:fmtDollars(totalArr),c:'#60A5FA'},{l:'Win Rate',v:`${wr}%`,c:wr>=60?'#22C55E':wr>=40?'#F59E0B':'#EF4444'}].map(m=>(
          <div key={m.l} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 0',borderBottom:'1px solid rgba(255,255,255,0.05)'}}>
            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,color:'rgba(255,255,255,0.6)'}}>{m.l}</span>
            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:20,fontWeight:900,color:m.c}}>{m.v}</span>
          </div>
        ))}
      </div>
      <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'20px 22px'}}>
        <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:16}}>Monthly Closed Revenue</div>
        <div style={{display:'flex',gap:8,alignItems:'flex-end',height:130}}>
          {mRev.map(m=>(
            <div key={m.label} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:4}}>
              {m.v>0 && <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)'}}>{fmtDollars(m.v)}</div>}
              <div style={{width:'100%',background:m.v>0?'linear-gradient(180deg,#E87722,#f59840)':'rgba(255,255,255,0.06)',borderRadius:'4px 4px 0 0',height:`${Math.max((m.v/maxM)*100,m.v>0?6:3)}px`,transition:'height 0.4s'}}/>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)'}}>{m.label}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:10,padding:'20px 22px',gridColumn:'span 2'}}>
        <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase',color:'rgba(255,255,255,0.4)',marginBottom:16}}>Pipeline by Stage</div>
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {stageCounts.map(s=>(
            <div key={s.id} style={{display:'grid',gridTemplateColumns:'140px 1fr 100px',gap:12,alignItems:'center'}}>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:s.color,fontWeight:700}}>{s.label}</span>
              <div style={{height:8,background:'rgba(255,255,255,0.06)',borderRadius:4}}><div style={{height:'100%',width:`${(s.value/maxSV)*100}%`,background:s.color,borderRadius:4,opacity:0.8,transition:'width 0.5s'}}/></div>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.5)',textAlign:'right'}}>{s.count>0?`${s.count} · ${fmtDollars(s.value)}`:'—'}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// GEO MAP
// ═══════════════════════════════════════════════════════════════════
function GeoMap({ C, dark }) {
  const canvasRef = useRef(null);
  const [layers, setLayers] = useState({ parcels:true, points:true, roads:true, labels:true });
  const [coords, setCoords] = useState({ lat:37.7749, lng:-122.4194 });

  useEffect(() => {
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext("2d"); const W=canvas.width,H=canvas.height;
    ctx.clearRect(0,0,W,H);
    ctx.fillStyle="#0D1B2A"; ctx.fillRect(0,0,W,H);
    ctx.strokeStyle="rgba(255,255,255,0.04)"; ctx.lineWidth=0.5;
    for(let x=0;x<W;x+=40){ctx.beginPath();ctx.moveTo(x,0);ctx.lineTo(x,H);ctx.stroke()}
    for(let y=0;y<H;y+=40){ctx.beginPath();ctx.moveTo(0,y);ctx.lineTo(W,y);ctx.stroke()}
    if(layers.roads){[[80,120,320,140],[320,140,520,100],[160,200,500,220],[100,280,460,310],[50,350,550,330]].forEach(([x1,y1,x2,y2])=>{ctx.strokeStyle="rgba(27,43,91,0.8)";ctx.lineWidth=4;ctx.beginPath();ctx.moveTo(x1,y1);ctx.lineTo(x2,y2);ctx.stroke()})}
    if(layers.parcels){[[110,150,200,145,205,210,115,215,"#E87722"],[220,155,300,150,308,230,225,235,"#60A5FA"],[320,165,430,145,440,240,330,250,"#E87722"]].forEach(pts=>{const color=pts[8];ctx.beginPath();ctx.moveTo(pts[0],pts[1]);[[pts[2],pts[3]],[pts[4],pts[5]],[pts[6],pts[7]]].forEach(([x,y])=>ctx.lineTo(x,y));ctx.closePath();ctx.fillStyle=color+"18";ctx.fill();ctx.strokeStyle=color+"66";ctx.lineWidth=1.5;ctx.stroke()})}
    if(layers.points){[{x:160,y:180,c:"#E87722",l:"HQ"},{x:265,y:195,c:"#60A5FA",l:"Node 2"},{x:385,y:200,c:"#E87722",l:"Node 3"}].forEach(({x,y,c,l})=>{ctx.beginPath();ctx.arc(x,y,5,0,Math.PI*2);ctx.fillStyle=c;ctx.fill();ctx.beginPath();ctx.arc(x,y,9,0,Math.PI*2);ctx.strokeStyle=c+"44";ctx.lineWidth=1.5;ctx.stroke();if(layers.labels){ctx.font="10px 'Montserrat',sans-serif";ctx.fillStyle="rgba(255,255,255,0.7)";ctx.fillText(l,x+12,y+4)}})}
    ctx.strokeStyle="rgba(232,119,34,0.5)";ctx.lineWidth=1;ctx.setLineDash([4,4]);ctx.beginPath();ctx.moveTo(W/2-14,H/2);ctx.lineTo(W/2+14,H/2);ctx.stroke();ctx.beginPath();ctx.moveTo(W/2,H/2-14);ctx.lineTo(W/2,H/2+14);ctx.stroke();ctx.setLineDash([]);
  }, [dark, layers]);

  return (
    <div style={{ animation:"fadeUp 0.5s ease" }}>
      <SH C={C} title="Geo Map" sub="MapLibre-powered spatial intelligence" />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 220px", gap:14 }}>
        <div style={{ ...Card(C), padding:0, overflow:"hidden", position:"relative" }}>
          <canvas ref={canvasRef} width={580} height={420} style={{ width:"100%", height:420, cursor:"crosshair", display:"block" }}
            onMouseMove={e=>{const r=canvasRef.current?.getBoundingClientRect();if(r)setCoords({lat:+(37.7749+(e.clientY-r.top-200)*-0.0003).toFixed(5),lng:+(-122.4194+(e.clientX-r.left-290)*0.0003).toFixed(5)})}}/>
          <div style={{ position:"absolute", bottom:12, right:12, background:"rgba(13,27,42,0.9)", border:"1px solid rgba(232,119,34,0.3)", borderRadius:4, padding:"5px 10px", fontFamily:"'Montserrat',sans-serif", fontWeight:600, fontSize:10, color:"#E87722" }}>{coords.lat}°N {coords.lng}°W</div>
          <div style={{ position:"absolute", top:12, left:12, ...Badge("#E87722","rgba(232,119,34,0.12)","rgba(232,119,34,0.3)"), fontSize:9 }}>⚡ MAPLIBRE READY</div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div style={Card(C)}>
            <Eyebrow>Layers</Eyebrow>
            {Object.entries(layers).map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:600, color:v?"#fff":"rgba(255,255,255,0.3)", textTransform:"capitalize" }}>{k}</span>
                <Toggle val={v} onChange={() => setLayers(p=>({...p,[k]:!p[k]}))} C={C}/>
              </div>
            ))}
          </div>
          <div style={Card(C)}>
            <Eyebrow>CRS</Eyebrow>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700, color:"#22C55E" }}>✓ EPSG:4326</div>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.4)", marginTop:2 }}>WGS84 / Geographic</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// PLATFORM CONFIG (Admin tab)
// ═══════════════════════════════════════════════════════════════════
function PlatformConfig({ C, moduleStatuses, setModuleStatuses, showToast }) {
  const [adding,  setAdding]  = useState(false);
  const [editing, setEditing] = useState(null);
  const [newS,    setNewS]    = useState({ label:'', color:'#60A5FA' });

  const addStatus = () => {
    if (!newS.label.trim()) return;
    const id = newS.label.toLowerCase().replace(/\s+/g,'-');
    if (moduleStatuses.find(s => s.id === id)) { showToast('A status with that name already exists'); return; }
    setModuleStatuses(p => [...p, { id, label: newS.label, color: newS.color }]);
    setNewS({ label:'', color:'#60A5FA' });
    setAdding(false);
    showToast(`Status "${newS.label}" added`);
  };

  const CORE = ['live','building','planned'];

  return (
    <div>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:20 }}>
        <div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:14, color:'#fff', marginBottom:6 }}>Module Status Options</div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.35)', maxWidth:480, lineHeight:1.6 }}>
            Configure status labels used in the Strategy Lab module registry and solar system view. <strong style={{color:'rgba(255,255,255,0.5)'}}>Order determines the orbital ring</strong> — first status = closest to AMP (innermost orbit).
          </div>
        </div>
        <button onClick={() => setAdding(v=>!v)} style={{ ...BtnPrimary, padding:'8px 16px', fontSize:10, flexShrink:0, marginLeft:20 }}>+ Add Status</button>
      </div>

      {adding && (
        <div style={{ ...Card(C), marginBottom:16, display:'flex', gap:10, alignItems:'center' }}>
          <input autoFocus value={newS.label} onChange={e=>setNewS(p=>({...p,label:e.target.value}))}
            onKeyDown={e=>{ if(e.key==='Enter') addStatus(); if(e.key==='Escape') setAdding(false); }}
            placeholder="Status name (e.g. Beta, User Testing, Deprecated…)"
            style={{ ...InputStyle(C), flex:1 }} />
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.4)' }}>Color</span>
            <input type="color" value={newS.color} onChange={e=>setNewS(p=>({...p,color:e.target.value}))}
              style={{ width:36, height:36, borderRadius:4, border:'none', cursor:'pointer', padding:2 }} />
          </div>
          <button onClick={addStatus} style={{ ...BtnPrimary, padding:'8px 14px', fontSize:10 }}>Add</button>
          <button onClick={()=>setAdding(false)} style={{ ...BtnOutline(C), padding:'8px 14px', fontSize:10 }}>Cancel</button>
        </div>
      )}

      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {moduleStatuses.map((s, i) => (
          <div key={s.id} style={{ ...Card(C), display:'flex', alignItems:'center', gap:12, padding:'12px 16px', borderLeft:`3px solid ${s.color}` }}>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.3)', fontWeight:700, minWidth:18, textAlign:'right' }}>{i+1}</span>
            <div style={{ width:11, height:11, borderRadius:'50%', background:s.color, boxShadow:`0 0 8px ${s.color}88`, flexShrink:0 }} />
            {editing === s.id ? (
              <input autoFocus value={s.label}
                onChange={e => setModuleStatuses(p => p.map(x => x.id===s.id ? {...x,label:e.target.value} : x))}
                onBlur={()=>setEditing(null)}
                onKeyDown={e=>{ if(e.key==='Enter'||e.key==='Escape') setEditing(null); }}
                style={{ ...InputStyle(C), flex:1, padding:'4px 8px', fontSize:12 }} />
            ) : (
              <span onClick={()=>setEditing(s.id)} title="Click to rename"
                style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:600, color:'#fff', flex:1, cursor:'text' }}>
                {s.label}
              </span>
            )}
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.2)', fontStyle:'italic' }}>{s.id}</span>
            <input type="color" value={s.color}
              onChange={e => setModuleStatuses(p => p.map(x => x.id===s.id ? {...x,color:e.target.value} : x))}
              style={{ width:30, height:30, borderRadius:4, border:'none', cursor:'pointer', padding:2, flexShrink:0 }} />
            {CORE.includes(s.id)
              ? <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.2)', minWidth:32, textAlign:'center' }}>core</span>
              : <button onClick={()=>setModuleStatuses(p=>p.filter(x=>x.id!==s.id))}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(239,68,68,0.45)', fontSize:15, padding:'0 4px', minWidth:32, textAlign:'center' }}>✕</button>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SOLAR SYSTEM VIEW
// ═══════════════════════════════════════════════════════════════════
const SOLAR_TILT        = 0.36;
const SOLAR_RING_RADII  = [130, 178, 226, 278, 328, 375];
const SOLAR_RING_SPEEDS = [0.42, 0.30, 0.20, 0.13, 0.08, 0.05];

function SolarSystemView({ modules, moduleStatuses }) {
  const canvasRef     = useRef(null);
  const rafRef        = useRef(null);
  const hoverRef      = useRef(null);
  const planetsRef    = useRef([]);
  const [tooltip,  setTooltip]  = useState(null);
  const [selected, setSelected] = useState(null);
  const [selPlanet, setSelPlanet] = useState(null);

  const buildPlanets = useCallback(() => {
    const rings = {};
    modules.forEach(m => {
      const ri = moduleStatuses.findIndex(s => s.id === m.status);
      const ring = ri >= 0 ? ri : moduleStatuses.length;
      if (!rings[ring]) rings[ring] = [];
      rings[ring].push(m);
    });
    const result = [];
    Object.entries(rings).forEach(([ring, mods]) => {
      const r = parseInt(ring);
      mods.forEach((mod, i) => {
        const statusMeta = moduleStatuses.find(s => s.id === mod.status) || { color:'#6B7280' };
        result.push({
          ...mod,
          ring: r,
          orbitR:  SOLAR_RING_RADII[Math.min(r, SOLAR_RING_RADII.length-1)],
          speed:   SOLAR_RING_SPEEDS[Math.min(r, SOLAR_RING_SPEEDS.length-1)],
          startAngle: (2*Math.PI*i/mods.length) - Math.PI/2 + (r*0.4),
          size:    mod.type === 'platform' ? 18 : 13,
          color:   statusMeta.color,
        });
      });
    });
    return result;
  }, [modules, moduleStatuses]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    const stars = Array.from({length:160}, () => ({
      x:Math.random(), y:Math.random(),
      r:Math.random()*1.3+0.2,
      phase:Math.random()*Math.PI*2,
      spd:0.3+Math.random()*0.9,
    }));

    const ro = new ResizeObserver(() => {
      canvas.width  = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    });
    ro.observe(canvas);
    canvas.width  = canvas.offsetWidth;
    canvas.height = canvas.offsetHeight;

    let t = 0, lastNow = performance.now();

    const draw = (now) => {
      const dt = Math.min((now - lastNow)/1000, 0.05);
      lastNow = now;
      t += dt;

      const W = canvas.width, H = canvas.height;
      if (!W || !H) { rafRef.current = requestAnimationFrame(draw); return; }
      const cx = W/2, cy = H/2;

      ctx.clearRect(0,0,W,H);

      // Deep space bg
      const bg = ctx.createRadialGradient(cx,cy,0,cx,cy,Math.max(W,H)*0.75);
      bg.addColorStop(0,'rgba(18,32,75,0.65)');
      bg.addColorStop(1,'rgba(13,27,42,0)');
      ctx.fillStyle = bg;
      ctx.fillRect(0,0,W,H);

      // Stars
      stars.forEach(s => {
        const a = 0.25 + 0.45*Math.sin(t*s.spd+s.phase);
        ctx.beginPath();
        ctx.arc(s.x*W, s.y*H, s.r, 0, Math.PI*2);
        ctx.fillStyle = `rgba(255,255,255,${a})`;
        ctx.fill();
      });

      const planets = buildPlanets();

      // Compute positions
      const positions = planets.map(p => {
        const angle = p.startAngle + p.speed * t;
        return { ...p, angle,
          x: cx + p.orbitR * Math.cos(angle),
          y: cy + p.orbitR * Math.sin(angle) * SOLAR_TILT,
          depth: Math.sin(angle),
        };
      });
      planetsRef.current = positions;

      // Orbit rings
      const ringSet = new Set(planets.map(p=>p.ring));
      ringSet.forEach(ring => {
        const r = SOLAR_RING_RADII[Math.min(ring, SOLAR_RING_RADII.length-1)];
        ctx.beginPath();
        ctx.ellipse(cx,cy,r,r*SOLAR_TILT,0,0,Math.PI*2);
        ctx.strokeStyle = 'rgba(232,119,34,0.07)';
        ctx.lineWidth = 1;
        ctx.setLineDash([3,12]);
        ctx.stroke();
        ctx.setLineDash([]);
      });

      // Cross-module feature connections
      positions.forEach(from => {
        (from.features||[]).forEach(f => {
          (f.crossModules||[]).forEach(toId => {
            const to = positions.find(p=>p.id===toId);
            if (!to) return;
            ctx.beginPath();
            ctx.moveTo(from.x, from.y);
            ctx.lineTo(to.x, to.y);
            ctx.strokeStyle='rgba(232,119,34,0.15)';
            ctx.lineWidth=1;
            ctx.setLineDash([4,8]);
            ctx.stroke();
            ctx.setLineDash([]);
            // Traveling dot
            const prog = ((t*0.22) + (from.orbitR/500)) % 1;
            ctx.beginPath();
            ctx.arc(from.x+(to.x-from.x)*prog, from.y+(to.y-from.y)*prog, 2.5, 0, Math.PI*2);
            ctx.fillStyle='rgba(232,119,34,0.75)';
            ctx.fill();
          });
        });
      });

      // AMP Sun
      const sunR = 28 + 1.5*Math.sin(t*1.9);
      [2.8,1.9,1.3].forEach((mul,i) => {
        const gr=ctx.createRadialGradient(cx,cy,sunR*0.5,cx,cy,sunR*mul);
        gr.addColorStop(0,`rgba(232,119,34,${0.14/(i+1)})`);
        gr.addColorStop(1,'rgba(232,119,34,0)');
        ctx.beginPath(); ctx.arc(cx,cy,sunR*mul,0,Math.PI*2);
        ctx.fillStyle=gr; ctx.fill();
      });
      const sg=ctx.createRadialGradient(cx-sunR*0.32,cy-sunR*0.32,0,cx+sunR*0.15,cy+sunR*0.15,sunR*1.3);
      sg.addColorStop(0,'#FFF5B0'); sg.addColorStop(0.35,'#FFB830');
      sg.addColorStop(0.7,'#E87722'); sg.addColorStop(1,'#8B3E08');
      ctx.beginPath(); ctx.arc(cx,cy,sunR,0,Math.PI*2);
      ctx.fillStyle=sg; ctx.fill();
      ctx.fillStyle='rgba(255,255,255,0.92)';
      ctx.font="bold 8px 'Montserrat',sans-serif";
      ctx.textAlign='center'; ctx.textBaseline='middle';
      ctx.fillText('AMP',cx,cy);

      // Planets (back-to-front)
      const sorted=[...positions].sort((a,b)=>a.depth-b.depth);
      sorted.forEach(p => {
        const isHov = hoverRef.current===p.id;
        const isSel = selected===p.id;
        const dScale = 0.76 + 0.24*((p.depth+1)/2);
        const sz = p.size * dScale;
        const [r,g,b] = hexToRgb(p.color);

        if (isHov||isSel) {
          const gl=ctx.createRadialGradient(p.x,p.y,sz,p.x,p.y,sz*3.2);
          gl.addColorStop(0,`${p.color}2A`); gl.addColorStop(1,`${p.color}00`);
          ctx.beginPath(); ctx.arc(p.x,p.y,sz*3.2,0,Math.PI*2);
          ctx.fillStyle=gl; ctx.fill();
        }

        // Saturn ring for platform type
        if (p.type==='platform') {
          ctx.save(); ctx.translate(p.x,p.y);
          ctx.beginPath(); ctx.ellipse(0,0,sz*1.9,sz*0.52,0.25,0,Math.PI*2);
          ctx.strokeStyle=`${p.color}50`; ctx.lineWidth=2.5; ctx.stroke();
          ctx.restore();
        }

        // Sphere with lighting
        const pg=ctx.createRadialGradient(p.x-sz*0.35,p.y-sz*0.38,0,p.x+sz*0.2,p.y+sz*0.2,sz*1.5);
        pg.addColorStop(0,`rgb(${Math.min(255,r+70)},${Math.min(255,g+70)},${Math.min(255,b+70)})`);
        pg.addColorStop(0.45,p.color);
        pg.addColorStop(1,`rgb(${Math.max(0,r-55)},${Math.max(0,g-55)},${Math.max(0,b-55)})`);
        ctx.beginPath(); ctx.arc(p.x,p.y,sz,0,Math.PI*2);
        ctx.fillStyle=pg; ctx.fill();

        if (isHov||isSel) {
          ctx.strokeStyle='rgba(255,255,255,0.75)'; ctx.lineWidth=1.5; ctx.stroke();
        }

        // Feature moons
        const feats = p.features||[];
        feats.forEach((f,fi) => {
          const mAngle = (2*Math.PI*fi/feats.length) + p.speed*3.8*t;
          const mR = sz*2.5;
          const fColor = moduleStatuses.find(s=>s.id===f.status)?.color||'#60A5FA';
          const mx = p.x + mR*Math.cos(mAngle);
          const my = p.y + mR*Math.sin(mAngle)*SOLAR_TILT;
          const mSz = Math.max(2.5, sz*0.28);
          ctx.beginPath(); ctx.arc(mx,my,mSz,0,Math.PI*2);
          ctx.fillStyle=fColor; ctx.globalAlpha=0.88; ctx.fill(); ctx.globalAlpha=1;
          if ((f.crossModules||[]).length>0) {
            ctx.beginPath(); ctx.arc(mx,my,mSz+2,0,Math.PI*2);
            ctx.strokeStyle='rgba(232,119,34,0.7)'; ctx.lineWidth=1; ctx.stroke();
          }
        });

        // Label
        const labelSz = Math.max(8, 9.5*dScale);
        ctx.fillStyle = isSel||isHov ? '#fff' : 'rgba(255,255,255,0.72)';
        ctx.font=`${isSel||isHov?'bold ':''} ${labelSz}px 'Montserrat',sans-serif`;
        ctx.textAlign='center'; ctx.textBaseline='top';
        ctx.fillText(p.name, p.x, p.y+sz+6);

        if (feats.length>0) {
          ctx.fillStyle=p.color; ctx.font=`bold 7px 'Montserrat',sans-serif`;
          ctx.textAlign='left'; ctx.textBaseline='alphabetic';
          ctx.fillText(`${feats.length}`, p.x+sz*0.68, p.y-sz*0.72);
        }
      });

      rafRef.current = requestAnimationFrame(draw);
    };

    rafRef.current = requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafRef.current); ro.disconnect(); };
  }, [buildPlanets, moduleStatuses, selected]);

  const handleMouseMove = useCallback((e) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;
    const mx=e.clientX-rect.left, my=e.clientY-rect.top;
    let found = null;
    for (const p of planetsRef.current) {
      const dx=mx-p.x, dy=my-p.y;
      if (Math.sqrt(dx*dx+dy*dy) < p.size*2.8) { found=p; break; }
    }
    hoverRef.current = found?.id||null;
    setTooltip(found||null);
  }, []);

  const handleClick = useCallback(() => {
    if (!hoverRef.current) return;
    const hovId = hoverRef.current;
    setSelected(prev => {
      const next = prev === hovId ? null : hovId;
      setSelPlanet(next ? planetsRef.current.find(p => p.id === next) || null : null);
      return next;
    });
  }, []);

  return (
    <div style={{ position:'relative', width:'100%', height:'100%' }}>
      <canvas ref={canvasRef} onMouseMove={handleMouseMove} onClick={handleClick}
        style={{ width:'100%', height:'100%', cursor:tooltip?'pointer':'default', display:'block' }} />

      {/* Status legend */}
      <div style={{ position:'absolute', bottom:14, left:14, display:'flex', gap:8, flexWrap:'wrap' }}>
        {moduleStatuses.map(s=>(
          <div key={s.id} style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(13,27,42,0.85)', border:`1px solid ${s.color}2A`, borderRadius:100, padding:'3px 9px' }}>
            <div style={{ width:6, height:6, borderRadius:'50%', background:s.color }}/>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.55)', fontWeight:600 }}>{s.label}</span>
          </div>
        ))}
        <div style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(13,27,42,0.85)', border:'1px solid rgba(232,119,34,0.18)', borderRadius:100, padding:'3px 9px' }}>
          <div style={{ width:14, height:5, background:'rgba(232,119,34,0)', border:'1px dashed rgba(232,119,34,0.55)', borderRadius:2 }}/>
          <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.4)', fontWeight:600 }}>Cross-module feature</span>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:5, background:'rgba(13,27,42,0.85)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:100, padding:'3px 9px' }}>
          <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.4)' }}>Small dot = feature · ringed = platform · <strong style={{color:'rgba(232,119,34,0.7)'}}>⊙</strong> = cross-module</span>
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && !selected && (
        <div style={{ position:'absolute', bottom:55, left:'50%', transform:'translateX(-50%)',
          background:'rgba(13,27,42,0.96)', border:`1px solid ${tooltip.color}44`,
          borderRadius:8, padding:'8px 14px', pointerEvents:'none',
          fontFamily:"'Montserrat',sans-serif" }}>
          <div style={{ fontSize:11, fontWeight:700, color:'#fff' }}>{tooltip.name}</div>
          <div style={{ fontSize:9, color:'rgba(255,255,255,0.38)', marginTop:2 }}>
            {tooltip.type} · {moduleStatuses.find(s=>s.id===tooltip.status)?.label} · {(tooltip.features||[]).length} features · click for details
          </div>
        </div>
      )}

      {/* Selected detail panel */}
      {selected && selPlanet && (
        <div style={{ position:'absolute', top:14, right:14, width:230,
          background:'rgba(13,27,42,0.97)', border:`1px solid ${selPlanet.color}44`,
          borderRadius:12, padding:16, fontFamily:"'Montserrat',sans-serif" }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:10 }}>
            <div style={{ width:11,height:11,borderRadius:'50%',background:selPlanet.color,boxShadow:`0 0 8px ${selPlanet.color}` }}/>
            <span style={{ fontSize:12,fontWeight:700,color:'#fff',flex:1 }}>{selPlanet.name}</span>
            <button onClick={()=>setSelected(null)} style={{ background:'none',border:'none',color:'rgba(255,255,255,0.3)',cursor:'pointer',fontSize:14,padding:0 }}>✕</button>
          </div>
          <div style={{ fontSize:9,color:`${selPlanet.color}cc`,fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:4 }}>
            {moduleStatuses.find(s=>s.id===selPlanet.status)?.label} · {selPlanet.type}
          </div>
          <div style={{ fontSize:10,color:'rgba(255,255,255,0.42)',marginBottom:10,lineHeight:1.6 }}>{selPlanet.desc}</div>
          {(selPlanet.features||[]).length>0 && <>
            <div style={{ fontSize:9,fontWeight:700,color:'#E87722',letterSpacing:'0.15em',textTransform:'uppercase',marginBottom:6 }}>
              Features ({selPlanet.features.length})
            </div>
            {selPlanet.features.map(f=>{
              const fMeta=moduleStatuses.find(s=>s.id===f.status)||{color:'#60A5FA'};
              return (
                <div key={f.id} style={{ display:'flex',alignItems:'center',gap:7,marginBottom:5 }}>
                  <div style={{ width:5,height:5,borderRadius:'50%',background:fMeta.color,flexShrink:0 }}/>
                  <span style={{ fontSize:10,color:'rgba(255,255,255,0.75)',flex:1 }}>{f.name}</span>
                  {(f.crossModules||[]).length>0 && <span title="Connected to other modules" style={{ fontSize:10,color:'#E87722' }}>⟷</span>}
                </div>
              );
            })}
          </>}
        </div>
      )}
    </div>
  );
}

function DataFlowMap({ C, datasets, setDatasets, dataOrgs, threats = [] }) {
  const canvasRef  = useRef(null);
  const rafRef     = useRef(null);
  const nodesRef   = useRef([]);
  const partRef    = useRef([]);

  const [editMode,   setEditMode]   = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [hoverOrg,   setHoverOrg]   = useState(null);
  const [dragDs,     setDragDs]     = useState(null);
  const [addingDs,   setAddingDs]   = useState(false);
  const [newDs,      setNewDs]      = useState({ name:'', category:'Operations', source:'client', flow:'bidirectional', color:'#60A5FA', desc:'', orgs:[] });

  useEffect(() => {
    const ps = [];
    datasets.forEach(ds => {
      const orgList = ds.orgs[0]==='*' ? dataOrgs.map(o=>o.id) : ds.orgs;
      orgList.forEach(orgId => {
        const count = ds.orgs[0]==='*' ? 2 : 3;
        for (let i=0; i<count; i++) {
          const base = { dsId:ds.id, orgId, speed:0.003+Math.random()*0.002 };
          if (ds.flow==='inbound'||ds.flow==='bidirectional') ps.push({...base,t:Math.random(),dir:-1});
          if (ds.flow==='outbound'||ds.flow==='bidirectional') ps.push({...base,t:Math.random(),dir:1});
        }
      });
    });
    partRef.current = ps;
  }, [datasets, dataOrgs]);

  useEffect(() => {
    if (editMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = devicePixelRatio||1;
    const resize = () => { canvas.width=canvas.offsetWidth*dpr; canvas.height=canvas.offsetHeight*dpr; };
    const ro = new ResizeObserver(resize);
    ro.observe(canvas); resize();
    const ctx = canvas.getContext('2d');

    const draw = (ts) => {
      const W=canvas.width, H=canvas.height, t=ts*0.001;
      const sc=Math.min(W,H)/600, cx=W/2, cy=H/2, orgRing=195*sc;
      ctx.clearRect(0,0,W,H);

      // Background
      const bg=ctx.createRadialGradient(cx,cy,0,cx,cy,Math.max(W,H)*0.75);
      bg.addColorStop(0,'rgba(18,30,48,1)'); bg.addColorStop(1,'rgba(8,12,20,1)');
      ctx.fillStyle=bg; ctx.fillRect(0,0,W,H);

      // Stars
      for (let i=0;i<100;i++) {
        const sx=(i*173.5+11)%W, sy=(i*97.3+37)%H;
        const tw=0.3+0.25*Math.sin(t*1.7+i*0.9);
        ctx.beginPath(); ctx.arc(sx,sy,0.8,0,Math.PI*2);
        ctx.fillStyle=`rgba(255,255,255,${tw})`; ctx.fill();
      }

      // Org positions (slow rotation)
      const orgPos = {};
      dataOrgs.forEach((org,i) => {
        const base=(2*Math.PI*i/dataOrgs.length)-Math.PI/2;
        const a=base+t*0.012;
        orgPos[org.id]={ x:cx+orgRing*Math.cos(a), y:cy+orgRing*Math.sin(a)*0.52 };
      });

      // Connection lines
      datasets.forEach(ds => {
        const isSel=selectedId===ds.id;
        const orgList=ds.orgs[0]==='*'?dataOrgs.map(o=>o.id):ds.orgs;
        const [r,g,b]=hexToRgb(ds.color);
        const alpha=isSel?0.55:(ds.orgs[0]==='*'?0.13:0.25);
        ctx.save();
        if (ds.orgs[0]==='*') ctx.setLineDash([5,7]); else ctx.setLineDash([]);
        ctx.strokeStyle=`rgba(${r},${g},${b},${alpha})`; ctx.lineWidth=isSel?2:1;
        orgList.forEach(orgId => {
          const op=orgPos[orgId]; if (!op) return;
          ctx.beginPath(); ctx.moveTo(cx,cy); ctx.lineTo(op.x,op.y); ctx.stroke();
        });
        ctx.restore();
      });

      // Particles
      partRef.current = partRef.current.map(p => {
        const ds=datasets.find(d=>d.id===p.dsId), op=orgPos[p.orgId];
        if (!ds||!op) return p;
        let nt=p.t+p.speed*p.dir; if (nt>1) nt=0; if (nt<0) nt=1;
        const px=cx+(op.x-cx)*nt, py=cy+(op.y-cy)*nt;
        const [r,g,b]=hexToRgb(ds.color), isSel=selectedId===ds.id;
        ctx.beginPath(); ctx.arc(px,py,isSel?4:2.5,0,Math.PI*2);
        ctx.fillStyle=`rgba(${r},${g},${b},${isSel?1:0.75})`; ctx.fill();
        return {...p,t:nt};
      });

      // Org nodes
      const nodes=[];
      dataOrgs.forEach(org => {
        const op=orgPos[org.id]; if (!op) return;
        const sz=15*sc, [r,g,b]=hexToRgb(org.color), isSel=selectedId===org.id;
        const glow=ctx.createRadialGradient(op.x,op.y,0,op.x,op.y,sz*3);
        glow.addColorStop(0,`rgba(${r},${g},${b},0.3)`); glow.addColorStop(1,`rgba(${r},${g},${b},0)`);
        ctx.beginPath(); ctx.arc(op.x,op.y,sz*3,0,Math.PI*2); ctx.fillStyle=glow; ctx.fill();
        const grad=ctx.createRadialGradient(op.x-sz*0.3,op.y-sz*0.3,0,op.x,op.y,sz);
        grad.addColorStop(0,`rgba(${Math.min(r+50,255)},${Math.min(g+50,255)},${Math.min(b+50,255)},1)`);
        grad.addColorStop(1,`rgba(${r},${g},${b},0.9)`);
        ctx.beginPath(); ctx.arc(op.x,op.y,sz*(isSel?1.25:1),0,Math.PI*2); ctx.fillStyle=grad; ctx.fill();
        if (isSel) { ctx.strokeStyle=`rgba(${r},${g},${b},0.9)`; ctx.lineWidth=2; ctx.stroke(); }
        const dsCount=datasets.filter(d=>d.orgs[0]==='*'||d.orgs.includes(org.id)).length;
        ctx.fillStyle=`rgba(${r},${g},${b},0.9)`;
        ctx.beginPath(); ctx.arc(op.x+sz*0.65,op.y-sz*0.65,Math.max(5,6*sc),0,Math.PI*2); ctx.fill();
        ctx.fillStyle='#000'; ctx.font=`bold ${Math.max(6,6*sc)}px Montserrat,sans-serif`;
        ctx.textAlign='center'; ctx.textBaseline='middle';
        ctx.fillText(dsCount,op.x+sz*0.65,op.y-sz*0.65);
        ctx.font=`bold ${Math.max(8,9*sc)}px Montserrat,sans-serif`;
        ctx.fillStyle='rgba(255,255,255,0.85)'; ctx.textAlign='center'; ctx.textBaseline='top';
        ctx.fillText(org.name,op.x,op.y+sz+5);
        nodes.push({id:org.id,x:op.x,y:op.y,r:sz*1.8,type:'org',data:org});
      });

      // 40Geo hub
      const hubR=30*sc, pulse=1+0.07*Math.sin(t*2);
      const corona=ctx.createRadialGradient(cx,cy,0,cx,cy,hubR*4*pulse);
      corona.addColorStop(0,'rgba(232,119,34,0.28)'); corona.addColorStop(0.4,'rgba(232,119,34,0.07)'); corona.addColorStop(1,'rgba(232,119,34,0)');
      ctx.beginPath(); ctx.arc(cx,cy,hubR*4*pulse,0,Math.PI*2); ctx.fillStyle=corona; ctx.fill();
      const hubGrd=ctx.createRadialGradient(cx-hubR*0.3,cy-hubR*0.3,0,cx,cy,hubR);
      hubGrd.addColorStop(0,'#FFDDA0'); hubGrd.addColorStop(0.5,'#E87722'); hubGrd.addColorStop(1,'#8B3A0A');
      ctx.beginPath(); ctx.arc(cx,cy,hubR,0,Math.PI*2); ctx.fillStyle=hubGrd; ctx.fill();
      ctx.fillStyle='rgba(255,255,255,0.95)'; ctx.font=`bold ${Math.max(8,9*sc)}px Montserrat,sans-serif`;
      ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillText('40GEO AMP',cx,cy);
      ctx.font=`${Math.max(7,7.5*sc)}px Montserrat,sans-serif`; ctx.fillStyle='rgba(255,255,255,0.38)';
      ctx.fillText(`${datasets.length} datasets · ${dataOrgs.length} orgs`,cx,cy+hubR+11);

      // ── Threat overlay on hub ─────────────────────────────────────
      const activeThreats = threats.filter(th => th.status !== 'mitigated');
      if (activeThreats.length > 0) {
        const hasCrit  = activeThreats.some(th => th.severity === 'critical');
        const hasHigh  = activeThreats.some(th => th.severity === 'high');
        const tColor   = hasCrit ? '#FF3B30' : hasHigh ? '#FF9500' : '#FFD60A';
        const [tr,tg,tb] = hexToRgb(tColor);
        const tPulse   = 1 + 0.18 * Math.sin(t * 3.5);
        const tRing    = ctx.createRadialGradient(cx,cy,hubR*1.1,cx,cy,hubR*2.4*tPulse);
        tRing.addColorStop(0,`rgba(${tr},${tg},${tb},0.38)`);
        tRing.addColorStop(0.5,`rgba(${tr},${tg},${tb},0.1)`);
        tRing.addColorStop(1,`rgba(${tr},${tg},${tb},0)`);
        ctx.beginPath(); ctx.arc(cx,cy,hubR*2.4*tPulse,0,Math.PI*2);
        ctx.fillStyle=tRing; ctx.fill();
        const badgeR=Math.max(9,10*sc);
        ctx.beginPath(); ctx.arc(cx+hubR*0.72,cy-hubR*0.72,badgeR,0,Math.PI*2);
        ctx.fillStyle=tColor; ctx.fill();
        ctx.fillStyle='#000'; ctx.font=`bold ${Math.max(7,8*sc)}px Montserrat,sans-serif`;
        ctx.textAlign='center'; ctx.textBaseline='middle';
        ctx.fillText(activeThreats.length,cx+hubR*0.72,cy-hubR*0.72);
        ctx.font=`bold ${Math.max(7,7.5*sc)}px Montserrat,sans-serif`;
        ctx.fillStyle=`rgba(${tr},${tg},${tb},0.9)`;
        ctx.textAlign='center'; ctx.textBaseline='top';
        ctx.fillText(`⚠ ${activeThreats.length} THREAT${activeThreats.length!==1?'S':''} ACTIVE`,cx,cy+hubR+23);
      }

      // ── Threat overlay on hub ──────────────────────────────────────────
      const openThreats = threats.filter(th => th.status !== 'mitigated');
      if (openThreats.length > 0) {
        const hasCritical = openThreats.some(th => th.severity === 'critical');
        const hasHigh     = openThreats.some(th => th.severity === 'high');
        const threatColor = hasCritical ? '#FF3B30' : hasHigh ? '#FF9500' : '#FFD60A';
        const [tr,tg,tb]  = hexToRgb(threatColor);
        // Pulsing threat ring
        const threatPulse = 1 + 0.15 * Math.sin(t * 4);
        const threatRing  = ctx.createRadialGradient(cx, cy, hubR * 1.1, cx, cy, hubR * 2.2 * threatPulse);
        threatRing.addColorStop(0, `rgba(${tr},${tg},${tb},0.35)`);
        threatRing.addColorStop(0.5, `rgba(${tr},${tg},${tb},0.12)`);
        threatRing.addColorStop(1, `rgba(${tr},${tg},${tb},0)`);
        ctx.beginPath(); ctx.arc(cx, cy, hubR * 2.2 * threatPulse, 0, Math.PI * 2);
        ctx.fillStyle = threatRing; ctx.fill();
        // Threat count badge
        const badgeR = 11 * sc;
        ctx.beginPath(); ctx.arc(cx + hubR * 0.7, cy - hubR * 0.7, badgeR, 0, Math.PI * 2);
        ctx.fillStyle = threatColor; ctx.fill();
        ctx.fillStyle = '#000'; ctx.font = `bold ${Math.max(7, 8 * sc)}px Montserrat,sans-serif`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(openThreats.length, cx + hubR * 0.7, cy - hubR * 0.7);
        // Threat label beneath hub
        ctx.font = `bold ${Math.max(7, 7.5 * sc)}px Montserrat,sans-serif`;
        ctx.fillStyle = `rgba(${tr},${tg},${tb},0.85)`;
        ctx.textAlign = 'center'; ctx.textBaseline = 'top';
        ctx.fillText(`⚠ ${openThreats.length} THREAT${openThreats.length !== 1 ? 'S' : ''} ACTIVE`, cx, cy + hubR + 22);
      }

      nodesRef.current=nodes;
      rafRef.current=requestAnimationFrame(draw);
    };
    rafRef.current=requestAnimationFrame(draw);
    return () => { cancelAnimationFrame(rafRef.current); ro.disconnect(); };
  }, [editMode, datasets, dataOrgs, selectedId, threats]);

  const handleCanvasClick = useCallback((e) => {
    const canvas=canvasRef.current; if (!canvas) return;
    const rect=canvas.getBoundingClientRect();
    const mx=(e.clientX-rect.left)*(canvas.width/rect.width);
    const my=(e.clientY-rect.top)*(canvas.height/rect.height);
    for (const node of nodesRef.current) {
      const dx=mx-node.x, dy=my-node.y;
      if (Math.sqrt(dx*dx+dy*dy)<node.r) { setSelectedId(p=>p===node.id?null:node.id); return; }
    }
    setSelectedId(null);
  }, []);

  const addDataset = (e) => {
    e.preventDefault();
    if (!newDs.name.trim()) return;
    setDatasets(prev=>[...prev,{...newDs,id:`ds-${Date.now()}`}]);
    setNewDs({name:'',category:'Operations',source:'client',flow:'bidirectional',color:'#60A5FA',desc:'',orgs:[]});
    setAddingDs(false);
  };

  const selectedOrgData = selectedId ? dataOrgs.find(o=>o.id===selectedId) : null;
  const selectedDsData  = selectedId ? datasets.find(d=>d.id===selectedId) : null;

  if (editMode) {
    return (
      <div style={{flex:1,display:'flex',flexDirection:'column',gap:10,overflow:'hidden',minHeight:0}}>
        <div style={{display:'flex',alignItems:'center',gap:10,flexShrink:0}}>
          <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#E87722',letterSpacing:'0.1em'}}>EDIT DATA FLOWS</span>
          <span style={{fontSize:10,color:'rgba(255,255,255,0.35)',fontFamily:"'Montserrat',sans-serif"}}>Drag datasets into org buckets · ✕ to remove · toggle global for cross-org</span>
          <div style={{marginLeft:'auto',display:'flex',gap:6}}>
            <button onClick={()=>setAddingDs(p=>!p)} style={{...BtnPrimary,padding:'5px 12px',fontSize:10}}>+ Dataset</button>
            <button onClick={()=>setEditMode(false)} style={{...BtnOutline(C),padding:'5px 12px',fontSize:10}}>View Galaxy</button>
          </div>
        </div>
        {addingDs && (
          <form onSubmit={addDataset} style={{...Card(C),padding:12,display:'flex',gap:8,flexWrap:'wrap',alignItems:'flex-end',flexShrink:0}}>
            <input placeholder="Dataset name" value={newDs.name} onChange={e=>setNewDs(p=>({...p,name:e.target.value}))} style={{...InputStyle(C),width:180,marginBottom:0}} />
            <input placeholder="Category" value={newDs.category} onChange={e=>setNewDs(p=>({...p,category:e.target.value}))} style={{...InputStyle(C),width:110,marginBottom:0}} />
            <select value={newDs.flow} onChange={e=>setNewDs(p=>({...p,flow:e.target.value}))} style={{...InputStyle(C),width:130,marginBottom:0}}>
              <option value="inbound">Inbound</option><option value="outbound">Outbound</option><option value="bidirectional">Bidirectional</option>
            </select>
            <select value={newDs.source} onChange={e=>setNewDs(p=>({...p,source:e.target.value}))} style={{...InputStyle(C),width:120,marginBottom:0}}>
              {['client','external','platform','regulatory'].map(s=><option key={s} value={s}>{s}</option>)}
            </select>
            <input type="color" value={newDs.color} onChange={e=>setNewDs(p=>({...p,color:e.target.value}))} style={{width:32,height:32,padding:2,background:'none',border:'none',cursor:'pointer'}} />
            <button type="submit" style={{...BtnPrimary,padding:'7px 14px',fontSize:10}}>Add</button>
            <button type="button" onClick={()=>setAddingDs(false)} style={{...BtnOutline(C),padding:'7px 12px',fontSize:10}}>Cancel</button>
          </form>
        )}
        <div style={{flex:1,display:'flex',gap:10,overflow:'hidden',minHeight:0}}>
          <div style={{width:240,...Card(C),padding:10,overflowY:'auto',display:'flex',flexDirection:'column',gap:4}}>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.4)',letterSpacing:'0.15em',marginBottom:6}}>ALL DATASETS</div>
            {datasets.map(ds=>(
              <div key={ds.id} draggable onDragStart={()=>setDragDs(ds)} onDragEnd={()=>setDragDs(null)}
                onClick={()=>setSelectedId(p=>p===ds.id?null:ds.id)}
                style={{padding:'7px 9px',borderRadius:6,border:`1px solid ${ds.id===selectedId?ds.color:ds.color+'33'}`,
                  background:ds.id===selectedId?`${ds.color}18`:'rgba(255,255,255,0.03)',
                  cursor:'grab',display:'flex',alignItems:'center',gap:7}}>
                <div style={{width:7,height:7,borderRadius:1,background:ds.color,flexShrink:0}}/>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ds.name}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:8,color:'rgba(255,255,255,0.38)'}}>{ds.category} · {ds.flow}</div>
                </div>
                {ds.orgs[0]==='*'&&<span style={{fontSize:7,fontWeight:700,color:'#E87722',fontFamily:"'Montserrat',sans-serif"}}>GLOBAL</span>}
                <button onClick={e=>{e.stopPropagation();setDatasets(p=>p.filter(d=>d.id!==ds.id));}}
                  style={{background:'none',border:'none',color:'rgba(255,80,80,0.4)',cursor:'pointer',fontSize:11,padding:0,lineHeight:1}}>✕</button>
              </div>
            ))}
          </div>
          <div style={{flex:1,display:'grid',gridTemplateColumns:`repeat(${Math.min(dataOrgs.length,4)},1fr)`,gap:8,overflowY:'auto'}}>
            {dataOrgs.map(org=>{
              const orgDs=datasets.filter(d=>d.orgs[0]==='*'||d.orgs.includes(org.id));
              const isHov=hoverOrg===org.id;
              return (
                <div key={org.id}
                  onDragOver={e=>{e.preventDefault();setHoverOrg(org.id);}}
                  onDragLeave={()=>setHoverOrg(null)}
                  onDrop={e=>{e.preventDefault();setHoverOrg(null);if(!dragDs||dragDs.orgs[0]==='*'||dragDs.orgs.includes(org.id))return;setDatasets(prev=>prev.map(d=>d.id===dragDs.id?{...d,orgs:[...d.orgs,org.id]}:d));}}
                  style={{...Card(C),padding:10,border:`2px solid ${isHov?org.color:org.color+'22'}`,
                    background:isHov?`${org.color}12`:'rgba(255,255,255,0.02)',
                    display:'flex',flexDirection:'column',gap:5,minHeight:180}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                    <div style={{width:9,height:9,borderRadius:'50%',background:org.color,boxShadow:`0 0 6px ${org.color}`}}/>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:org.color,letterSpacing:'0.1em',textTransform:'uppercase',flex:1}}>{org.name}</span>
                    <span style={{fontSize:9,color:'rgba(255,255,255,0.3)',fontFamily:"'Montserrat',sans-serif"}}>{orgDs.length}</span>
                  </div>
                  {orgDs.map(ds=>(
                    <div key={ds.id} style={{padding:'5px 7px',borderRadius:4,background:`${ds.color}10`,border:`1px solid ${ds.color}2A`,display:'flex',alignItems:'center',gap:5}}>
                      <div style={{width:5,height:5,borderRadius:1,background:ds.color,flexShrink:0}}/>
                      <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.7)',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{ds.name}</span>
                      {ds.orgs[0]==='*'
                        ?<span style={{fontSize:7,color:'#E87722',fontWeight:700,fontFamily:"'Montserrat',sans-serif"}}>GLOBAL</span>
                        :<button onClick={()=>setDatasets(prev=>prev.map(d=>d.id===ds.id?{...d,orgs:d.orgs.filter(o=>o!==org.id)}:d))} style={{background:'none',border:'none',color:'rgba(255,80,80,0.45)',cursor:'pointer',fontSize:10,padding:0,lineHeight:1}}>✕</button>
                      }
                    </div>
                  ))}
                  {isHov&&dragDs&&!dragDs.orgs.includes(org.id)&&dragDs.orgs[0]!=='*'&&(
                    <div style={{padding:'5px 7px',borderRadius:4,border:`1px dashed ${org.color}77`,opacity:0.7}}>
                      <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.35)'}}>Drop to assign "{dragDs.name}"</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {selectedId&&selectedDsData&&(
          <div style={{...Card(C),padding:12,flexShrink:0,display:'flex',gap:12,alignItems:'flex-start'}}>
            <div style={{width:10,height:10,borderRadius:2,background:selectedDsData.color,marginTop:1,flexShrink:0}}/>
            <div style={{flex:1}}>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#fff',marginBottom:3}}>{selectedDsData.name}</div>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)'}}>{selectedDsData.desc}</div>
            </div>
            <div style={{display:'flex',gap:5,flexWrap:'wrap',justifyContent:'flex-end'}}>
              {['inbound','outbound','bidirectional'].map(f=>(
                <button key={f} onClick={()=>setDatasets(prev=>prev.map(d=>d.id===selectedDsData.id?{...d,flow:f}:d))}
                  style={{padding:'3px 8px',borderRadius:3,border:`1px solid rgba(255,255,255,${selectedDsData.flow===f?0.4:0.1})`,
                    background:selectedDsData.flow===f?'rgba(255,255,255,0.08)':'none',cursor:'pointer',
                    fontFamily:"'Montserrat',sans-serif",fontSize:8,fontWeight:700,color:selectedDsData.flow===f?'#fff':'rgba(255,255,255,0.35)',
                    textTransform:'uppercase',letterSpacing:'0.08em'}}>{f}</button>
              ))}
              <button onClick={()=>setDatasets(prev=>prev.map(d=>d.id===selectedDsData.id?{...d,orgs:['*']}:d))}
                style={{padding:'3px 8px',borderRadius:3,border:`1px solid rgba(232,119,34,${selectedDsData.orgs[0]==='*'?0.8:0.3})`,
                  background:selectedDsData.orgs[0]==='*'?'rgba(232,119,34,0.15)':'none',cursor:'pointer',
                  fontFamily:"'Montserrat',sans-serif",fontSize:8,fontWeight:700,color:selectedDsData.orgs[0]==='*'?'#E87722':'rgba(232,119,34,0.5)',
                  letterSpacing:'0.08em'}}>GLOBAL</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{position:'relative',flex:1,minHeight:0}}>
      <canvas ref={canvasRef} onClick={handleCanvasClick} style={{width:'100%',height:'100%',display:'block',cursor:'default'}}/>
      <div style={{position:'absolute',top:12,right:12,display:'flex',gap:6}}>
        <button onClick={()=>setEditMode(true)} style={{...BtnPrimary,padding:'6px 13px',fontSize:10}}>Edit Datasets</button>
      </div>
      {selectedId&&(selectedOrgData||selectedDsData)&&(
        <div style={{position:'absolute',top:12,left:12,width:220,background:'rgba(13,27,42,0.97)',borderRadius:10,padding:14,
          border:`1px solid ${((selectedOrgData||selectedDsData)?.color||'#E87722')+'44'}`,fontFamily:"'Montserrat',sans-serif"}}>
          {selectedOrgData&&<>
            <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:8}}>
              <div style={{width:10,height:10,borderRadius:'50%',background:selectedOrgData.color,boxShadow:`0 0 8px ${selectedOrgData.color}`}}/>
              <span style={{fontSize:12,fontWeight:700,color:'#fff',flex:1}}>{selectedOrgData.name}</span>
              <button onClick={()=>setSelectedId(null)} style={{background:'none',border:'none',color:'rgba(255,255,255,0.3)',cursor:'pointer',fontSize:13,padding:0}}>✕</button>
            </div>
            <div style={{fontSize:9,color:'rgba(255,255,255,0.4)',marginBottom:8}}>{datasets.filter(d=>d.orgs[0]==='*'||d.orgs.includes(selectedOrgData.id)).length} datasets</div>
            {datasets.filter(d=>d.orgs[0]==='*'||d.orgs.includes(selectedOrgData.id)).map(ds=>(
              <div key={ds.id} style={{display:'flex',alignItems:'center',gap:5,marginBottom:3}}>
                <div style={{width:5,height:5,borderRadius:1,background:ds.color}}/>
                <span style={{fontSize:9,color:'rgba(255,255,255,0.65)'}}>{ds.name}</span>
                {ds.orgs[0]==='*'&&<span style={{fontSize:7,color:'#E87722',marginLeft:'auto'}}>GLOBAL</span>}
              </div>
            ))}
          </>}
          {selectedDsData&&!selectedOrgData&&<>
            <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:8}}>
              <div style={{width:10,height:10,borderRadius:2,background:selectedDsData.color}}/>
              <span style={{fontSize:12,fontWeight:700,color:'#fff',flex:1}}>{selectedDsData.name}</span>
              <button onClick={()=>setSelectedId(null)} style={{background:'none',border:'none',color:'rgba(255,255,255,0.3)',cursor:'pointer',fontSize:13,padding:0}}>✕</button>
            </div>
            <div style={{fontSize:9,color:`${selectedDsData.color}cc`,fontWeight:700,letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:6}}>
              {selectedDsData.category} · {selectedDsData.flow} · {selectedDsData.source}
            </div>
            <div style={{fontSize:9,color:'rgba(255,255,255,0.42)',marginBottom:8,lineHeight:1.6}}>{selectedDsData.desc}</div>
            <div style={{fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.3)',marginBottom:4}}>ASSIGNED TO</div>
            {(selectedDsData.orgs[0]==='*'?dataOrgs:dataOrgs.filter(o=>selectedDsData.orgs.includes(o.id))).map(org=>(
              <div key={org.id} style={{display:'flex',alignItems:'center',gap:5,marginBottom:3}}>
                <div style={{width:5,height:5,borderRadius:'50%',background:org.color}}/>
                <span style={{fontSize:9,color:'rgba(255,255,255,0.65)'}}>{org.name}</span>
              </div>
            ))}
            {selectedDsData.orgs[0]==='*'&&<div style={{fontSize:8,color:'#E87722',fontWeight:700,marginTop:4}}>GLOBAL — CROSSES ALL ORGS</div>}
          </>}
        </div>
      )}
      <div style={{position:'absolute',bottom:12,left:12,display:'flex',gap:6,flexWrap:'wrap'}}>
        {[{label:'Inbound',color:'#3CF0FF'},{label:'Outbound',color:'#E87722'},{label:'Bidirectional',color:'#22C55E'}].map(l=>(
          <div key={l.label} style={{display:'flex',alignItems:'center',gap:5,background:'rgba(13,27,42,0.85)',border:`1px solid ${l.color}22`,borderRadius:100,padding:'3px 9px'}}>
            <div style={{width:6,height:6,borderRadius:'50%',background:l.color}}/>
            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.5)',fontWeight:600}}>{l.label}</span>
          </div>
        ))}
        <div style={{display:'flex',alignItems:'center',gap:5,background:'rgba(13,27,42,0.85)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:100,padding:'3px 9px'}}>
          <div style={{width:14,height:4,border:'1px dashed rgba(255,255,255,0.35)',borderRadius:1}}/>
          <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',fontWeight:600}}>Global / Cross-org</span>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// STRATEGY LAB
// ═══════════════════════════════════════════════════════════════════
function StrategyLab({ C, chat, chatInput, setChatInput, sendChat, chatLoading, setChat, currentUser, modules, setModules, moduleStatuses, nuggets, saveNugget, showToast, cyberThreats = [] }) {
  const endRef       = useRef(null);
  const [addingMod,  setAddingMod]  = useState(false);
  const [newMod,     setNewMod]     = useState({ name:'', status:'planned', type:'module', desc:'' });
  const [editModId,  setEditModId]  = useState(null);
  const [actions,    setActions]    = useState([]); // extracted ACTION: lines from last response

  useEffect(() => { endRef.current?.scrollIntoView({behavior:"smooth"}); }, [chat]);

  // Extract ACTION: lines from the latest assistant message
  useEffect(() => {
    const last = [...chat].reverse().find(m => m.role === 'assistant');
    if (!last) return;
    const extracted = last.content
      .split('\n')
      .filter(l => l.trim().startsWith('ACTION:'))
      .map(l => l.replace(/^ACTION:\s*/i, '').trim());
    setActions(extracted);
  }, [chat]);

  const convertToNugget = async (text) => {
    const nugget = {
      id:         Date.now(),
      title:      text.slice(0, 80),
      desc:       `From Strategy Lab AI suggestion`,
      col:        'backlog',
      priority:   'high',
      visibility: 'team',
      ownerId:    currentUser.id,
      assigneeId: null,
      company:    '',
      type:       nuggets[0]?.type || 'Task',
      tags:       ['strategy-lab'],
      createdAt:  new Date().toISOString(),
    };
    await saveNugget(nugget);
    showToast('Action converted to Nugget ✓');
    setActions(p => p.filter(a => a !== text));
  };

  const [labView,      setLabView]      = useState('registry'); // 'registry' | 'solar' | 'data'
  const [datasets,     setDatasets]     = useState(INIT_DATASETS);
  const [dataOrgs,     setDataOrgs]     = useState(INIT_DATA_ORGS);
  const [expandedMod,  setExpandedMod]  = useState(null);
  const [addingFeat,   setAddingFeat]   = useState(null); // moduleId
  const [newFeat,      setNewFeat]      = useState({ name:'', status:'planned' });

  // ── Chat history ─────────────────────────────────────────────────
  const HIST_KEY = `nuggets_strategy_chats_${currentUser.id}`;
  const LAB_CATS = [
    { id:'major-projects', label:'Major Projects', icon:'🏗️' },
    { id:'nibbles',        label:'Nibbles',        icon:'🌰' },
    { id:'architecture',   label:'Architecture',   icon:'🏛️' },
    { id:'planning',       label:'Planning',       icon:'📋' },
    { id:'general',        label:'General',        icon:'💬' },
  ];
  const loadHist  = () => { try { return JSON.parse(localStorage.getItem(HIST_KEY) || '[]'); } catch { return []; } };
  const saveHist  = (h) => localStorage.setItem(HIST_KEY, JSON.stringify(h));

  const [labHistory,      setLabHistory]      = useState(loadHist);
  const [activeChatId,    setActiveChatId]    = useState(null);
  const [activeChatCat,   setActiveChatCat]   = useState('general');
  const [rightTab,        setRightTab]        = useState('sessions'); // 'sessions' | 'prompts'
  const [showCatPicker,   setShowCatPicker]   = useState(false);

  const WELCOME_MSG = { role:"assistant", content:"Welcome to AMP Strategy Lab. I'm your platform architect advisor.\n\nI have full context on your module registry, current tasks, and AMP's multi-tenant architecture. Ask me to help plan a module integration, sequence your roadmap, design a permission model, or think through a technical trade-off.\n\nWhat are we planning today?" };

  // Auto-save current chat whenever messages change
  useEffect(() => {
    const userMsgs = chat.filter(m => m.role === 'user');
    if (!userMsgs.length) return;
    const hist = loadHist();
    const title = userMsgs[0].content.slice(0, 60) + (userMsgs[0].content.length > 60 ? '…' : '');
    const now   = new Date().toISOString();
    if (activeChatId) {
      const idx = hist.findIndex(h => h.id === activeChatId);
      if (idx >= 0) { hist[idx] = { ...hist[idx], messages: chat, updatedAt: now }; }
      else          { hist.unshift({ id: activeChatId, title, category: activeChatCat, messages: chat, createdAt: now, updatedAt: now }); }
    } else {
      const newId = `sc-${Date.now()}`;
      setActiveChatId(newId);
      hist.unshift({ id: newId, title, category: activeChatCat, messages: chat, createdAt: now, updatedAt: now });
    }
    setLabHistory([...hist]);
    saveHist(hist);
  }, [chat]); // eslint-disable-line react-hooks/exhaustive-deps

  const startNewChat = () => {
    setChat([WELCOME_MSG]);
    setActiveChatId(null);
    setActions([]);
    setShowCatPicker(false);
  };

  const loadChatSession = (item) => {
    setChat(item.messages);
    setActiveChatId(item.id);
    setActiveChatCat(item.category);
    setActions([]);
  };

  const deleteChatSession = (id, e) => {
    e.stopPropagation();
    const hist = labHistory.filter(h => h.id !== id);
    setLabHistory(hist);
    saveHist(hist);
    if (activeChatId === id) { setChat([WELCOME_MSG]); setActiveChatId(null); }
  };

  const changeCat = (catId) => {
    setActiveChatCat(catId);
    if (activeChatId) {
      const hist = labHistory.map(h => h.id === activeChatId ? { ...h, category: catId } : h);
      setLabHistory(hist);
      saveHist(hist);
    }
  };

  const addModule = () => {
    if (!newMod.name.trim()) return;
    setModules(p => [...p, { ...newMod, id: Date.now().toString(), features:[] }]);
    setNewMod({ name:'', status: moduleStatuses[moduleStatuses.length-2]?.id||'planned', type:'module', desc:'' });
    setAddingMod(false);
  };

  const addFeature = (modId) => {
    if (!newFeat.name.trim()) return;
    setModules(p => p.map(m => m.id===modId
      ? { ...m, features:[...(m.features||[]), { id:`f-${Date.now()}`, name:newFeat.name, status:newFeat.status, crossModules:[] }] }
      : m
    ));
    setNewFeat({ name:'', status:'planned' });
    setAddingFeat(null);
  };

  const updateModStatus = (id, status) => setModules(p => p.map(m => m.id===id ? {...m,status} : m));
  const deleteModule    = (id)         => setModules(p => p.filter(m => m.id!==id));
  const deleteFeature   = (modId, fId) => setModules(p => p.map(m => m.id===modId ? {...m,features:(m.features||[]).filter(f=>f.id!==fId)} : m));

  const statusCounts = moduleStatuses.map(s => ({ ...s, count: modules.filter(m=>m.status===s.id).length }));

  const STRATEGY_PROMPTS = [
    "What's the right sequence to add the Help Desk module to AMP?",
    "How should we map Nuggets roles to AMP's Group/Writer/Reader model?",
    "Design the RLS policies for cross-tenant Launchpad calls",
    "What schema changes does Tenant Portal need before we start?",
    "Compare building Help Desk on Nuggets engine vs from scratch",
    "What are the biggest risks facing our roadmap this quarter?",
  ];

  return (
    <div style={{ animation:"fadeUp 0.5s ease", height:"calc(100vh - 130px)", display:"flex", flexDirection:"column", gap:10 }}>
      {/* Header + view toggle */}
      <div style={{ display:'flex', alignItems:'center', gap:12, flexShrink:0 }}>
        <div style={{ flex:1 }}>
          <SH C={C} title="AMP Strategy Lab" sub={`AI platform advisor · ${modules.length} modules · ${currentUser.name}`} />
        </div>
        <div style={{ display:'flex', gap:4, background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.08)', borderRadius:8, padding:3, flexShrink:0 }}>
          {[['registry','Registry'],['solar','Solar System'],['data','Data Flow']].map(([id,label]) => (
            <button key={id} onClick={()=>setLabView(id)} style={{
              padding:'5px 14px', borderRadius:6, border:'none', cursor:'pointer',
              background: labView===id ? 'rgba(232,119,34,0.2)' : 'transparent',
              color: labView===id ? '#E87722' : 'rgba(255,255,255,0.4)',
              fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700,
              letterSpacing:'0.1em', transition:'all 0.15s',
            }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Status bar */}
      <div style={{ display:'flex', gap:6, flexShrink:0, flexWrap:'wrap' }}>
        {statusCounts.map(s => (
          <div key={s.id} style={{
            padding:'3px 11px', borderRadius:100,
            background:`${s.color}18`, border:`1px solid ${s.color}44`,
            fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700,
            color: s.color, letterSpacing:'0.1em',
          }}>
            {s.count} {s.label.toUpperCase()}
          </div>
        ))}
      </div>

      {/* Solar System full-height view */}
      {labView === 'solar' && (
        <div style={{ flex:1, minHeight:0 }}>
          <SolarSystemView modules={modules} moduleStatuses={moduleStatuses} />
        </div>
      )}

      {/* Data Flow Galaxy */}
      {labView === 'data' && (
        <div style={{ flex:1, minHeight:0, display:'flex', flexDirection:'column' }}>
          <DataFlowMap C={C} datasets={datasets} setDatasets={setDatasets} dataOrgs={dataOrgs} setDataOrgs={setDataOrgs} threats={cyberThreats} />
        </div>
      )}

      {/* Registry + Chat layout */}
      {labView === 'registry' && <div style={{ display:"grid", gridTemplateColumns:"240px 1fr 220px", gap:12, flex:1, minHeight:0 }}>

        {/* ── MODULE REGISTRY (left) ── */}
        <div style={{ ...Card(C), padding:0, display:'flex', flexDirection:'column', overflow:'hidden' }}>
          <div style={{ padding:'10px 12px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
            <Eyebrow style={{margin:0}}>Module Registry</Eyebrow>
            <button onClick={() => setAddingMod(v => !v)}
              style={{ background:'none', border:'none', color:C.accent, cursor:'pointer', fontSize:18, lineHeight:1, fontWeight:300 }}>+</button>
          </div>

          {addingMod && (
            <div style={{ padding:'10px 12px', borderBottom:`1px solid ${C.border}`, background:'rgba(232,119,34,0.05)', flexShrink:0 }}>
              <input value={newMod.name} onChange={e => setNewMod(p => ({...p, name:e.target.value}))}
                placeholder="Module name" style={{ ...InputStyle(C), marginBottom:6, padding:'5px 8px', fontSize:11 }} />
              <input value={newMod.desc} onChange={e => setNewMod(p => ({...p, desc:e.target.value}))}
                placeholder="Description" style={{ ...InputStyle(C), marginBottom:6, padding:'5px 8px', fontSize:11 }} />
              <div style={{ display:'flex', gap:6, marginBottom:6 }}>
                <select value={newMod.status} onChange={e => setNewMod(p => ({...p, status:e.target.value}))}
                  style={{ ...InputStyle(C), flex:1, padding:'4px 6px', fontSize:10 }}>
                  {moduleStatuses.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                <select value={newMod.type} onChange={e => setNewMod(p => ({...p, type:e.target.value}))}
                  style={{ ...InputStyle(C), flex:1, padding:'4px 6px', fontSize:10 }}>
                  <option value="module">Module</option>
                  <option value="platform">Platform</option>
                </select>
              </div>
              <div style={{ display:'flex', gap:6 }}>
                <button onClick={addModule} style={{ ...BtnPrimary, flex:1, padding:'5px', fontSize:10 }}>Add</button>
                <button onClick={() => setAddingMod(false)} style={{ ...BtnOutline(C), flex:1, padding:'5px', fontSize:10 }}>Cancel</button>
              </div>
            </div>
          )}

          <div style={{ flex:1, overflowY:'auto', padding:'6px 0' }}>
            {['platform','module'].map(type => (
              <div key={type}>
                <div style={{ padding:'6px 12px 2px', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:'rgba(255,255,255,0.25)', letterSpacing:'0.15em', textTransform:'uppercase' }}>
                  {type === 'platform' ? 'Platform' : 'Modules'}
                </div>
                {modules.filter(m => m.type === type).map(mod => {
                  const meta     = moduleStatuses.find(s => s.id===mod.status) || { color:'#6B7280' };
                  const isEdit   = editModId === mod.id;
                  const isExpand = expandedMod === mod.id;
                  const feats    = mod.features || [];
                  return (
                    <div key={mod.id} style={{ borderLeft:`2px solid ${isEdit ? meta.color : 'transparent'}`, transition:'border-color 0.15s' }}
                      onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.03)'}
                      onMouseLeave={e => e.currentTarget.style.background='transparent'}
                    >
                      <div style={{ padding:'6px 12px', display:'flex', alignItems:'center', gap:6 }}>
                        <div style={{ width:6, height:6, borderRadius:'50%', background:meta.color, flexShrink:0, boxShadow:`0 0 5px ${meta.color}88` }} />
                        <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:600, color:'rgba(255,255,255,0.85)', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                          {mod.name}
                        </span>
                        {feats.length > 0 && (
                          <button onClick={() => setExpandedMod(isExpand ? null : mod.id)}
                            style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.25)', fontSize:9, padding:'0 3px', fontFamily:"'Montserrat',sans-serif", fontWeight:700 }}>
                            {feats.length}f {isExpand ? '▲' : '▼'}
                          </button>
                        )}
                        <button onClick={() => setEditModId(isEdit ? null : mod.id)}
                          style={{ background:'none', border:'none', cursor:'pointer', color:'rgba(255,255,255,0.2)', fontSize:11, padding:2, lineHeight:1 }}>⋯</button>
                      </div>

                      {/* Status edit controls */}
                      {isEdit && (
                        <div style={{ padding:'0 12px 8px', display:'flex', gap:4, flexWrap:'wrap' }}>
                          {moduleStatuses.map(s => (
                            <button key={s.id} onClick={() => { updateModStatus(mod.id, s.id); setEditModId(null); }}
                              style={{ padding:'2px 7px', borderRadius:100, border:`1px solid ${s.color}44`, background: mod.status===s.id ? `${s.color}22` : 'transparent', color:s.color, fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, cursor:'pointer' }}>
                              {s.label}
                            </button>
                          ))}
                          <button onClick={() => { deleteModule(mod.id); setEditModId(null); }}
                            style={{ padding:'2px 7px', borderRadius:100, border:'1px solid rgba(239,68,68,0.3)', background:'transparent', color:'#EF4444', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, cursor:'pointer' }}>
                            Remove
                          </button>
                        </div>
                      )}

                      {/* Feature list (expandable) */}
                      {!isEdit && isExpand && (
                        <div style={{ paddingLeft:20, paddingRight:12, paddingBottom:6 }}>
                          {feats.map(f => {
                            const fMeta = moduleStatuses.find(s=>s.id===f.status)||{color:'#60A5FA'};
                            return (
                              <div key={f.id} style={{ display:'flex', alignItems:'center', gap:5, padding:'3px 0', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                                <div style={{ width:4, height:4, borderRadius:'50%', background:fMeta.color, flexShrink:0 }}/>
                                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.6)', flex:1 }}>{f.name}</span>
                                {(f.crossModules||[]).length > 0 && <span title="Cross-module" style={{ fontSize:9, color:'#E87722' }}>⟷</span>}
                                <button onClick={() => deleteFeature(mod.id, f.id)} style={{ background:'none', border:'none', color:'rgba(239,68,68,0.3)', cursor:'pointer', fontSize:11, padding:0, lineHeight:1 }}>×</button>
                              </div>
                            );
                          })}
                          {/* Add feature inline */}
                          {addingFeat === mod.id ? (
                            <div style={{ display:'flex', gap:4, marginTop:5 }}>
                              <input autoFocus value={newFeat.name} onChange={e=>setNewFeat(p=>({...p,name:e.target.value}))}
                                onKeyDown={e=>{ if(e.key==='Enter') addFeature(mod.id); if(e.key==='Escape') setAddingFeat(null); }}
                                placeholder="Feature name" style={{ ...InputStyle(C), flex:1, padding:'3px 6px', fontSize:10 }} />
                              <select value={newFeat.status} onChange={e=>setNewFeat(p=>({...p,status:e.target.value}))}
                                style={{ ...InputStyle(C), padding:'3px 4px', fontSize:9, width:70 }}>
                                {moduleStatuses.map(s=><option key={s.id} value={s.id}>{s.label}</option>)}
                              </select>
                              <button onClick={()=>addFeature(mod.id)} style={{ ...BtnPrimary, padding:'3px 8px', fontSize:9 }}>+</button>
                            </div>
                          ) : (
                            <button onClick={()=>setAddingFeat(mod.id)}
                              style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(232,119,34,0.5)', background:'none', border:'none', cursor:'pointer', marginTop:4, padding:0 }}>
                              + add feature
                            </button>
                          )}
                        </div>
                      )}

                      {/* Desc (collapsed, not editing) */}
                      {!isEdit && !isExpand && (
                        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.25)', margin:'-2px 0 4px 24px', lineHeight:1.4 }}>
                          {mod.desc.slice(0,55)}{mod.desc.length>55?'…':''}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* ── CLAUDE ADVISOR (center) ── */}
        <div style={{ ...Card(C), display:"flex", flexDirection:"column", overflow:"hidden", padding:0 }}>
          <div style={{ padding:"8px 12px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8, flexShrink:0, flexWrap:'wrap' }}>
            <div style={{ width:8, height:8, borderRadius:"50%", background:"#22C55E", animation:"pulse 2s ease infinite", flexShrink:0 }}/>
            <Eyebrow style={{ margin:0 }}>Claude Sonnet 4.6 — Strategy Advisor</Eyebrow>
            {/* Category badge */}
            <div style={{ position:'relative', marginLeft:'auto' }}>
              <button onClick={() => setShowCatPicker(p => !p)} style={{ background:'rgba(255,255,255,0.06)', border:`1px solid ${C.border}`, borderRadius:6, padding:'3px 10px', cursor:'pointer', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:'rgba(255,255,255,0.6)', display:'flex', alignItems:'center', gap:4 }}>
                {LAB_CATS.find(c => c.id === activeChatCat)?.icon} {LAB_CATS.find(c => c.id === activeChatCat)?.label} ▾
              </button>
              {showCatPicker && (
                <div style={{ position:'absolute', top:'100%', right:0, marginTop:4, background:'#1a2744', border:`1px solid ${C.border}`, borderRadius:8, zIndex:100, overflow:'hidden', minWidth:160 }}>
                  {LAB_CATS.map(cat => (
                    <button key={cat.id} onClick={() => { changeCat(cat.id); setShowCatPicker(false); }} style={{ width:'100%', textAlign:'left', background: activeChatCat===cat.id ? 'rgba(232,119,34,0.15)' : 'transparent', border:'none', padding:'7px 12px', cursor:'pointer', fontFamily:"'Montserrat',sans-serif", fontSize:11, color: activeChatCat===cat.id ? '#E87722' : 'rgba(255,255,255,0.7)', display:'flex', alignItems:'center', gap:8 }}>
                      {cat.icon} {cat.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={startNewChat} style={{ background:'rgba(232,119,34,0.15)', border:'1px solid rgba(232,119,34,0.3)', borderRadius:6, padding:'3px 10px', cursor:'pointer', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:'#E87722', display:'flex', alignItems:'center', gap:4, flexShrink:0 }}>
              <Plus size={10}/> New Chat
            </button>
          </div>

          <div style={{ flex:1, overflow:"auto", padding:16, display:"flex", flexDirection:"column", gap:14 }}>
            {chat.map((m,i) => (
              <div key={i} style={{ display:"flex", flexDirection:"column", alignItems:m.role==="user"?"flex-end":"flex-start" }}>
                <div style={{
                  maxWidth:"88%",
                  background: m.role==="user" ? "#1B2B5B" : 'transparent',
                  border: m.role==="user" ? "1px solid rgba(255,255,255,0.1)" : 'none',
                  borderRadius: 8, padding: m.role==="user" ? "10px 13px" : 0,
                }}>
                  <pre style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#fff", whiteSpace:"pre-wrap", wordBreak:"break-word", lineHeight:1.65, margin:0, fontWeight:400 }}>
                    {m.content}
                  </pre>
                </div>
                <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, letterSpacing:"0.15em", textTransform:"uppercase", color:m.role==="user"?"#E87722":"rgba(255,255,255,0.25)", marginTop:3 }}>
                  {m.role==="user" ? currentUser.name : "AMP · Strategy Advisor"}
                </div>
              </div>
            ))}
            {chatLoading && (
              <div style={{ display:"flex", gap:5, alignItems:"center" }}>
                {[0,0.2,0.4].map((d,i) => <div key={i} style={{ width:6,height:6,borderRadius:"50%",background:"#E87722",animation:`pulse 0.8s ease ${d}s infinite` }}/>)}
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.3)", marginLeft:4 }}>Analyzing...</span>
              </div>
            )}
            <div ref={endRef}/>
          </div>

          <div style={{ padding:"10px 14px", borderTop:`1px solid ${C.border}`, background:"#0D1B2A", display:"flex", gap:8, flexShrink:0 }}>
            <textarea value={chatInput} onChange={e=>setChatInput(e.target.value)}
              onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendChat()}}}
              placeholder="Ask about module integration, sequencing, permissions, architecture… (Enter to send)"
              rows={2}
              style={{ ...InputStyle(C), flex:1, resize:"none" }}/>
            <button onClick={sendChat} disabled={chatLoading}
              style={{ ...BtnPrimary, padding:"0 16px", opacity:chatLoading?0.5:1 }}>
              <Send size={15}/>
            </button>
          </div>
        </div>

        {/* ── RIGHT PANEL ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:10, minHeight:0, overflow:'hidden' }}>

          {/* Suggested Actions */}
          {actions.length > 0 && (
            <div style={{ ...Card(C), flexShrink:0 }}>
              <Eyebrow>AI Actions → Nuggets</Eyebrow>
              <div style={{ fontSize:10, color:'rgba(255,255,255,0.3)', fontFamily:"'Montserrat',sans-serif", marginBottom:8 }}>
                Click to convert to a tracked task
              </div>
              {actions.map((a, i) => (
                <div key={i} style={{ display:'flex', alignItems:'flex-start', gap:8, marginBottom:8 }}>
                  <div style={{ flex:1, fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.75)', lineHeight:1.5 }}>{a}</div>
                  <button onClick={() => convertToNugget(a)}
                    title="Convert to Nugget"
                    style={{ background:'rgba(232,119,34,0.12)', border:'1px solid rgba(232,119,34,0.3)', borderRadius:4, padding:'3px 7px', cursor:'pointer', color:'#E87722', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, flexShrink:0, whiteSpace:'nowrap' }}>
                    → Nugget
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Sessions / Prompts tabs */}
          <div style={{ ...Card(C), flex:1, display:'flex', flexDirection:'column', overflow:'hidden', padding:0 }}>
            {/* Tab bar */}
            <div style={{ display:'flex', borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
              {[['sessions','Sessions'],['prompts','Prompts']].map(([id,label]) => (
                <button key={id} onClick={() => setRightTab(id)} style={{ flex:1, padding:'8px 0', border:'none', borderBottom: rightTab===id ? '2px solid #E87722' : '2px solid transparent', background:'transparent', cursor:'pointer', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color: rightTab===id ? '#E87722' : 'rgba(255,255,255,0.35)', letterSpacing:'0.08em', transition:'all 0.15s' }}>
                  {label}
                </button>
              ))}
            </div>

            {/* Sessions */}
            {rightTab === 'sessions' && (
              <div style={{ flex:1, overflowY:'auto', padding:'10px 10px' }}>
                {labHistory.length === 0 && (
                  <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.2)', textAlign:'center', marginTop:20, fontStyle:'italic' }}>No saved sessions yet.<br/>Start chatting to auto-save.</p>
                )}
                {LAB_CATS.map(cat => {
                  const items = labHistory.filter(h => h.category === cat.id);
                  if (!items.length) return null;
                  return (
                    <div key={cat.id} style={{ marginBottom:14 }}>
                      <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, letterSpacing:'0.12em', color:'rgba(255,255,255,0.3)', marginBottom:5, display:'flex', alignItems:'center', gap:5 }}>
                        {cat.icon} {cat.label.toUpperCase()}
                      </div>
                      {items.map(item => (
                        <div key={item.id} onClick={() => loadChatSession(item)} style={{ display:'flex', alignItems:'flex-start', gap:6, padding:'6px 8px', borderRadius:6, marginBottom:4, cursor:'pointer', background: activeChatId===item.id ? 'rgba(232,119,34,0.12)' : 'rgba(255,255,255,0.03)', border:`1px solid ${activeChatId===item.id ? 'rgba(232,119,34,0.3)' : 'rgba(255,255,255,0.06)'}`, transition:'all 0.1s' }}
                          onMouseEnter={e => { if (activeChatId!==item.id) e.currentTarget.style.background='rgba(255,255,255,0.06)'; }}
                          onMouseLeave={e => { if (activeChatId!==item.id) e.currentTarget.style.background='rgba(255,255,255,0.03)'; }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color: activeChatId===item.id ? '#E87722' : 'rgba(255,255,255,0.75)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.4 }}>{item.title}</div>
                            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.25)', marginTop:2 }}>{new Date(item.updatedAt).toLocaleDateString()} · {item.messages.filter(m=>m.role==='user').length} msg</div>
                          </div>
                          <button onClick={(e) => deleteChatSession(item.id, e)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.2)', cursor:'pointer', padding:'2px 3px', flexShrink:0, fontSize:12, lineHeight:1 }}
                            onMouseEnter={e => e.currentTarget.style.color='#FF6B6B'}
                            onMouseLeave={e => e.currentTarget.style.color='rgba(255,255,255,0.2)'}>✕</button>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Prompts */}
            {rightTab === 'prompts' && (
              <div style={{ flex:1, overflowY:'auto', padding:'10px 10px' }}>
                {STRATEGY_PROMPTS.map((s,i) => (
                  <button key={i} onClick={() => setChatInput(s)}
                    style={{ width:"100%", textAlign:"left", background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)", borderRadius:5, padding:"8px 10px", color:"rgba(255,255,255,0.6)", fontFamily:"'Montserrat',sans-serif", fontSize:10, cursor:"pointer", marginBottom:6, lineHeight:1.5, transition:"all 0.15s ease", fontWeight:400, display:'block' }}>
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Context card */}
          <div style={{ ...Card(C), flexShrink:0 }}>
            <Eyebrow>Context Injected</Eyebrow>
            {[
              ["Modules", `${modules.length} registered`],
              ["Tasks",   `${nuggets.length} in backlog`],
              ["Model",   "claude-sonnet-4-6"],
              ["Tokens",  "2,048 max"],
            ].map(([k,v]) => (
              <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"4px 0", borderBottom:"1px solid rgba(255,255,255,0.05)" }}>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.35)" }}>{k}</span>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"#E87722", fontWeight:600 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

      </div>}

    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// DEPLOY OPS
// ═══════════════════════════════════════════════════════════════════
const DEPLOY_STAGES_INIT = [
  { name:"Lint",         emoji:"🔍", status:"done",    time:"12s" },
  { name:"Unit Tests",   emoji:"🧪", status:"done",    time:"45s" },
  { name:"Data Quality", emoji:"🛡️", status:"done",    time:"23s" },
  { name:"Build",        emoji:"🔨", status:"running", time:"..."  },
  { name:"Staging",      emoji:"🎯", status:"pending", time:""    },
  { name:"Production",   emoji:"🚀", status:"pending", time:""    },
];

function DeployOps({ C, showToast, currentUser, addAudit }) {
  const [stages,  setStages]  = useState(DEPLOY_STAGES_INIT);
  const [running, setRunning] = useState(false);
  const canProd   = can(currentUser.role, "deploy_prod");
  const canDeploy = can(currentUser.role, "deploy_trigger");

  const runPipeline = () => {
    if (!canDeploy) { showToast("No deploy permissions. 'I am serious — and don't call me a deployer.'"); return; }
    setRunning(true);
    setStages(DEPLOY_STAGES_INIT.map(s => ({...s,status:"pending",time:""})));
    let delay = 0;
    DEPLOY_STAGES_INIT.forEach((stage, i) => {
      const dur = [800,1800,900,1400,1200,1500][i];
      setTimeout(() => {
        setStages(p => p.map((s,j) => j===i ? {...s,status:"running",time:"..."} : s));
        setTimeout(() => {
          const pass = !(i===4 && Math.random()<0.2) && !(i===5 && !canProd);
          const time  = pass ? `${Math.floor(Math.random()*50+10)}s` : (i===5&&!canProd?"BLOCKED":"FAIL");
          setStages(p => p.map((s,j) => j===i ? {...s,status:pass?"done":"failed",time} : s));
          if (i === DEPLOY_STAGES_INIT.length-1) {
            setRunning(false);
            if (pass) addAudit("Production deploy succeeded","deploy");
            showToast(pass ? "🚀 DEPLOYED. Roads? We don't need roads." : i===5&&!canProd ? "⛔ Production blocked — Admin role required." : "💥 Build failed. Great Scott!");
          }
        }, dur);
      }, delay);
      delay += dur + 200;
    });
  };

  const sc = s => ({ done:"#22C55E", running:"#E87722", failed:"#EF4444", blocked:"#EF4444", pending:"rgba(255,255,255,0.15)" })[s] || "rgba(255,255,255,0.15)";

  return (
    <div style={{ animation:"fadeUp 0.5s ease" }}>
      <SH C={C} title="Deploy Ops" sub="CI/CD pipeline · RBAC enforced on production deploys" />
      {!canProd && (
        <div style={{ background:"rgba(232,119,34,0.1)", borderLeft:"3px solid #E87722", borderRadius:4, padding:"10px 16px", marginBottom:16, fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#E87722", fontWeight:600 }}>
          ⚠ Your role ({(ROLE_META[currentUser.role] || ROLE_META.viewer).label}) cannot deploy to production.
        </div>
      )}
      <div style={Card(C)}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
          <Eyebrow style={{ margin:0 }}>Pipeline — feat/claude-cache-layer</Eyebrow>
          <button onClick={runPipeline} disabled={running||!canDeploy}
            style={{ ...BtnPrimary, opacity:(!canDeploy||running)?0.5:1 }}>
            {running ? <><div style={{ width:11,height:11,border:"2px solid #fff",borderTopColor:"transparent",borderRadius:"50%",animation:"spin 0.8s linear infinite" }}/>Running...</> : <><Play size={12}/>Run Pipeline</>}
          </button>
        </div>
        <div style={{ display:"flex", alignItems:"center" }}>
          {stages.map((s,i) => (
            <div key={s.name} style={{ display:"flex", alignItems:"center", flex:1 }}>
              <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                <div style={{ width:46,height:46,borderRadius:"50%",background:`${sc(s.status)}18`,border:`2px solid ${sc(s.status)}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,transition:"all 0.4s ease" }}>
                  {s.status==="done"?"✓":s.status==="failed"?"✗":s.status==="running"?"⟳":s.emoji}
                </div>
                <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:600, color:sc(s.status), textAlign:"center", letterSpacing:"0.1em", textTransform:"uppercase" }}>{s.name}</div>
                <div style={{ fontFamily:"monospace", fontSize:9, color:"rgba(255,255,255,0.3)" }}>{s.time}</div>
              </div>
              {i<stages.length-1 && <div style={{ width:18,height:2,background:sc(s.status),flexShrink:0,transition:"background 0.4s ease" }}/>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// ═══════════════════════════════════════════════════════════════════
// MESSENGER
// ═══════════════════════════════════════════════════════════════════
function Messenger({ C, currentUser, users, showToast, autoJoinChannelId, onAutoJoinHandled, saveNugget, modules, notifyUserViaPush }) {
  const isMobile = window.innerWidth < 768;
  const [channels,      setChannels]      = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [mobShowSidebar, setMobShowSidebar] = useState(true); // mobile: show sidebar vs chat
  const [messages,      setMessages]      = useState([]);
  const [input,         setInput]         = useState("");
  const [newChName,     setNewChName]     = useState("");
  const [showNewCh,     setShowNewCh]     = useState(false);
  const [showDMPicker,  setShowDMPicker]  = useState(false);
  // Group-DM picker — multi-select of userIds → single DM channel with N members.
  const [groupDmUserIds, setGroupDmUserIds] = useState(new Set());
  // channel_id → Set<user_id>.  Populated on channel load.  Drives the
  // display name + online dot for DMs (1-on-1 AND multi-party).
  const [channelMembersById, setChannelMembersById] = useState(new Map());
  const [loadingMsgs,   setLoadingMsgs]   = useState(false);
  const [hiddenDMs,     setHiddenDMs]     = useState(new Set());
  const [onlineUsers,   setOnlineUsers]   = useState(new Set());
  const [unreadCounts,  setUnreadCounts]  = useState({});
  const [searchQuery,   setSearchQuery]   = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  // New features
  const [aiSuggestions, setAiSuggestions] = useState({});  // { msgId: {type,nuggetTitle,nuggetDesc,priority,reason} }
  const [editingId,     setEditingId]     = useState(null);
  const [editDraft,     setEditDraft]     = useState("");
  const [editedIds,     setEditedIds]     = useState(new Set());
  const [reactions,     setReactions]     = useState({});  // { msgId: { emoji: [userId,...] } }
  const [hoverMsgId,    setHoverMsgId]    = useState(null);
  const [emojiPickMsg,  setEmojiPickMsg]  = useState(null);
  const [showMentions,  setShowMentions]  = useState(false);
  const [mentionQuery,  setMentionQuery]  = useState("");
  const [mentionPos,    setMentionPos]    = useState(null);
  // Typing indicator state — Map<userId, {name, expiresAt}>.  Entries
  // expire on their own via a 4s window on top of each typing event,
  // even if the sender never sends a `stopped` (tab closed, network
  // dropped, etc.).
  const [typers,        setTypers]        = useState(new Map());
  const typingSentAt = useRef(0);       // last broadcast time (throttle)
  const typingStopTimer = useRef(null); // idle → broadcast stopped
  // ── Tier 2 ──────────────────────────────────────────────────────
  const [highlightMsgId,  setHighlightMsgId]  = useState(null);   // message to highlight after search jump
  const [threadParentId,  setThreadParentId]  = useState(null);   // open thread msg id
  const [threadMsgs,      setThreadMsgs]      = useState({});     // { parentId: [replies] }
  const [threadInput,     setThreadInput]     = useState("");
  const [pinnedIds,       setPinnedIds]       = useState({});     // { channelId: Set<msgId> }
  const [showPinned,      setShowPinned]      = useState(false);
  const [catchMeUp,       setCatchMeUp]       = useState(null);   // null | 'loading' | string
  const [incidentPrompt,  setIncidentPrompt]  = useState(null);   // { content } | null
  const [forwardMsg,      setForwardMsg]      = useState(null);   // null | { id, content, senderName }
  const [forwardTarget,   setForwardTarget]   = useState(null);   // channel to forward to
  const [bookmarks,       setBookmarks]       = useState(() => {
    try { return JSON.parse(localStorage.getItem(`nuggets_bookmarks_${currentUser.id}`) || '[]'); }
    catch { return []; }
  });
  const [showBookmarks,   setShowBookmarks]   = useState(false);
  const [attachment,      setAttachment]      = useState(null);   // { file, preview, uploading }
  const fileInputRef     = useRef(null);

  const bottomRef      = useRef(null);
  const subRef         = useRef(null);
  const msgCache       = useRef({});
  const isInitLoad     = useRef(true);
  const userMap        = useRef(new Map(users.map(u => [u.id, u])));
  const activeChRef    = useRef(null);
  const channelsRef    = useRef([]);
  const searchTimerRef   = useRef(null);
  const inputRef         = useRef(null);
  const threadInputRef   = useRef(null);
  const highlightTimerRef= useRef(null);
  const msgRefs          = useRef({});  // { msgId: domNode }

  useEffect(() => { userMap.current    = new Map(users.map(u => [u.id, u])); }, [users]);
  useEffect(() => { activeChRef.current = activeChannel; }, [activeChannel]);
  useEffect(() => { channelsRef.current = channels; },     [channels]);

  // Auto-switch to invited channel
  useEffect(() => {
    if (!autoJoinChannelId || channels.length === 0) return;
    const ch = channels.find(c => c.id === autoJoinChannelId);
    if (ch) {
      setHiddenDMs(p => { const n = new Set(p); n.delete(ch.id); return n; });
      setActiveChannel(ch);
    }
  }, [autoJoinChannelId, channels]);

  // Load channels + presence + org message sub
  useEffect(() => {
    if (!currentUser?.orgId || !currentUser?.id) return;

    const fetchChannels = () =>
      loadChannels(currentUser.orgId, currentUser.id).then(async chs => {
        if (!chs?.length) return; // don't wipe existing state on empty result
        setChannels(chs);
        setActiveChannel(prev => prev ?? (chs.find(c => c.type === "public") || null));
        // Fetch members for every channel so group-DM display names +
        // online dots can be computed without an N+1.
        const memberMap = await loadChannelMembersMap(chs.map(c => c.id));
        setChannelMembersById(memberMap);
      }).catch(e => console.warn('[Messenger] loadChannels failed:', e.message));

    fetchChannels();

    // Presence — heartbeat + poll instead of pub/sub.
    // Every 30s while the Messenger is open we bump our own
    // `users.last_seen` (via touchLastSeen) AND read back every user
    // in the org whose last_seen is within the past 2 minutes.  That
    // set drives every green dot in the UI.  Simple + reliable +
    // survives page reloads without any special coordination.
    let stopPresence = null;
    {
      const PRESENCE_WINDOW_SEC   = 120;   // "online" = seen within 2min
      const PRESENCE_INTERVAL_MS  = 30_000; // heartbeat + poll every 30s
      const tick = async () => {
        try {
          await touchLastSeen(currentUser.id);
          const ids = await loadOnlineUsers(currentUser.orgId, PRESENCE_WINDOW_SEC);
          setOnlineUsers(new Set(ids));
        } catch (e) {
          console.warn('[presence] tick failed:', e?.message || e);
        }
      };
      tick(); // fire immediately so the dots appear without a 30s wait
      const iv = setInterval(tick, PRESENCE_INTERVAL_MS);
      stopPresence = () => clearInterval(iv);
    }
    const orgSub = subscribeToOrgMessages(currentUser.orgId, (payload) => {
      const msg = payload.new;
      if (msg.user_id === currentUser.id) return;
      if (activeChRef.current?.id === msg.channel_id) return;
      setUnreadCounts(prev => ({ ...prev, [msg.channel_id]: (prev[msg.channel_id] || 0) + 1 }));
      // (DM in-app toast lives at root level so it fires from any view —
      // no need to duplicate it here.)
    }, 'messenger-unread');

    // Authoritative unread counts from the DB. Local state stays for fast
    // optimistic updates but seeds from this on every mount + whenever a
    // new channel is added.
    loadUnreadCounts(currentUser.id).then(counts => setUnreadCounts(counts));

    // Watch the channel list so new public channels or new DMs show up on
    // all the user's devices without a reload.
    const listSub = subscribeToChannelList(currentUser.orgId, currentUser.id, () => {
      fetchChannels();
      loadUnreadCounts(currentUser.id).then(counts => setUnreadCounts(counts));
    });

    // If a different device marks a channel read, zero the local unread
    // count here too so badges stay in sync.
    const memberSub = subscribeToMyChannelMembership(currentUser.id, (row) => {
      setUnreadCounts(prev => ({ ...prev, [row.channel_id]: 0 }));
    });

    return () => {
      stopPresence?.();
      orgSub?.unsubscribe();
      listSub?.unsubscribe();
      memberSub?.unsubscribe();
    };
  }, [currentUser?.orgId, currentUser?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Active channel
  useEffect(() => {
    if (!activeChannel) return;
    // Reset per-channel state
    setThreadParentId(null); setThreadInput(""); setCatchMeUp(null);
    setShowPinned(false); setIncidentPrompt(null);
    try {
      const stored = JSON.parse(localStorage.getItem(`pins-${activeChannel.id}`) || '[]');
      setPinnedIds(prev => ({ ...prev, [activeChannel.id]: new Set(stored) }));
    } catch(_) {}
    setUnreadCounts(prev => ({ ...prev, [activeChannel.id]: 0 }));
    if (msgCache.current[activeChannel.id]) {
      setMessages(msgCache.current[activeChannel.id]);
      isInitLoad.current = false;
    } else {
      setLoadingMsgs(true); isInitLoad.current = true;
    }
    loadMessages(activeChannel.id).then(msgs => {
      msgCache.current[activeChannel.id] = msgs;
      setMessages(msgs); setLoadingMsgs(false);
    });
    subRef.current?.unsubscribe();
    subRef.current = subscribeToMessages(activeChannel.id, (payload) => {
      const msg = payload.new;
      setMessages(p => {
        if (p.find(m => m.id === msg.id)) return p;
        const optIdx = p.findIndex(m => m._optimistic && m.user_id === msg.user_id && m.content === msg.content);
        let next;
        if (optIdx >= 0) { next = [...p]; next[optIdx] = msg; }
        else              { next = [...p, msg]; }
        msgCache.current[activeChannel.id] = next;
        return next;
      });
    });
    markChannelRead(activeChannel.id, currentUser.id);

    // Typing indicator subscription — replace any prior channel's.
    setTypers(new Map());
    const typingSub = subscribeToTyping(activeChannel.id, (evt) => {
      if (!evt?.user_id || evt.user_id === currentUser.id) return;
      setTypers(prev => {
        const next = new Map(prev);
        if (evt.stopped) next.delete(evt.user_id);
        else next.set(evt.user_id, { name: evt.user_name || 'Someone', expiresAt: Date.now() + 5000 });
        return next;
      });
    });

    return () => {
      subRef.current?.unsubscribe();
      typingSub?.unsubscribe();
    };
  }, [activeChannel?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sweep expired typers once per second so a ghosted sender's chip
  // disappears even if we never receive a `stopped` frame.
  useEffect(() => {
    const iv = setInterval(() => {
      setTypers(prev => {
        if (prev.size === 0) return prev;
        const now = Date.now();
        let changed = false;
        const next = new Map(prev);
        for (const [k, v] of next) {
          if (v.expiresAt < now) { next.delete(k); changed = true; }
        }
        return changed ? next : prev;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: isInitLoad.current ? "instant" : "smooth" });
    isInitLoad.current = false;
  }, [messages]);

  // Scroll to highlighted message after search jump
  useEffect(() => {
    if (!highlightMsgId) return;
    const tryScroll = (attempts = 0) => {
      const el = msgRefs.current[highlightMsgId];
      if (el) { el.scrollIntoView({ behavior:'smooth', block:'center' }); return; }
      if (attempts < 8) setTimeout(() => tryScroll(attempts + 1), 120);
    };
    tryScroll();
  }, [highlightMsgId]);

  useEffect(() => {
    clearTimeout(searchTimerRef.current);
    if (!searchQuery.trim()) { setSearchResults(null); setSearchLoading(false); return; }
    setSearchLoading(true);
    searchTimerRef.current = setTimeout(async () => {
      const ids = channelsRef.current.map(c => c.id);
      const results = await dbSearchMessages(ids, searchQuery);
      setSearchResults(results); setSearchLoading(false);
    }, 380);
    return () => clearTimeout(searchTimerRef.current);
  }, [searchQuery]);

  // ── AI message analysis ─────────────────────────────────────────
  const analyzeMessage = useCallback(async (msgId, content) => {
    if (!content || content.length < 18) return;
    try {
      const recent = messages.slice(-8).map(m => `${(userMap.current.get(m.user_id)?.name)||'?'}: ${m.content}`).join('\n');
      const modCtx = (modules||[]).slice(0,6).map(m=>m.name).join(', ');
      const data = await callAI({ systemPrompt: `You are an AI embedded in 40Geo's team chat for the AMP platform (modules: ${modCtx||'various'}). Analyze the latest message for actionable items. Return ONLY valid JSON with no extra text: {"type":"task|meeting|escalation|decision|none","nuggetTitle":"concise task title max 60 chars","nuggetDesc":"1-2 sentences","priority":"normal|high|critical","reason":"1 sentence"}. Use type "none" if no clear action item. Be conservative — only flag genuine tasks or escalations.`, messages: [{ role: 'user', content: `Recent chat:\n${recent}\n\nLatest message: "${content}"\n\nDetect actionable item.` }], feature:'messenger_detect', user:currentUser });
      const text = data.content?.[0]?.text || '{"type":"none"}';
      const m = text.match(/\{[\s\S]*?\}/);
      if (!m) return;
      const result = JSON.parse(m[0]);
      if (result.type && result.type !== 'none') {
        setAiSuggestions(prev => ({ ...prev, [msgId]: result }));
      }
    } catch(_) { /* non-critical, silent fail */ }
  }, [messages, modules, currentUser]);

  // ── AI /ai command ──────────────────────────────────────────────
  const handleAiCommand = useCallback(async (query) => {
    if (!query.trim()) return;
    const qId = `ai-q-${Date.now()}`;
    const aId = `ai-a-${Date.now()}`;
    setMessages(p => [...p,
      { id: qId, user_id: currentUser.id, content: query, created_at: new Date().toISOString(), _ai_cmd: true },
      { id: aId, user_id: '__ai__',        content: '…',   created_at: new Date().toISOString(), _ai_resp: true, _loading: true },
    ]);
    try {
      const recent = messages.slice(-8).map(m => `${(userMap.current.get(m.user_id)?.name)||'?'}: ${m.content}`).join('\n');
      const modCtx = (modules||[]).map(m=>`${m.name} [${m.status}]`).join(', ');
      const data = await callAI({ systemPrompt: `You are the AMP AI Assistant in 40Geo's team chat. AMP modules: ${modCtx}. Answer concisely (under 180 words unless code needed). When recommending actions prefix with "ACTION:".`, messages: [{ role: 'user', content: `Channel context:\n${recent}\n\nQuestion: ${query}` }], feature:'messenger_ai', user:currentUser });
      const reply = data.content?.[0]?.text || 'Unable to get response.';
      setMessages(p => p.map(m => m.id === aId ? { ...m, content: reply, _loading: false } : m));
    } catch(e) {
      setMessages(p => p.map(m => m.id === aId ? { ...m, content: `Error: ${e.message}`, _loading: false } : m));
    }
  }, [messages, modules, currentUser]);

  // ── Send ────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!input.trim() && !attachment) return;
    if (!activeChannel) return;
    const content = input.trim();
    setInput(""); setShowMentions(false);

    // Cancel the typing indicator immediately — the receiver sees a
    // new message land, no need to keep the chip up.
    clearTimeout(typingStopTimer.current);
    if (typingSentAt.current) {
      typingSentAt.current = 0;
      broadcastTyping(activeChannel.id, { stopped: true });
    }

    // Upload attachment first if present
    let attachMeta = null;
    if (attachment?.file) {
      setAttachment(a => ({ ...a, uploading: true }));
      attachMeta = await dbUploadAttachment(currentUser.orgId, activeChannel.id, attachment.file);
      setAttachment(null);
      if (!attachMeta) { showToast?.('❌ File upload failed — check S3 bucket "chat-attachments"'); return; }
    }
    // /ai command
    if (content.match(/^\/ai\s+/i)) {
      handleAiCommand(content.replace(/^\/ai\s+/i, ''));
      return;
    }
    // Build final content — append attachment marker if present
    const attachMarker = attachMeta ? `\n[attach:${JSON.stringify(attachMeta)}]` : '';
    const fullContent  = (content + attachMarker) || attachMarker.trim();

    const optId = `opt-${Date.now()}`;
    setMessages(p => {
      const next = [...p, { id: optId, channel_id: activeChannel.id, user_id: currentUser.id, content: fullContent, created_at: new Date().toISOString(), _optimistic: true }];
      msgCache.current[activeChannel.id] = next;
      return next;
    });
    let real = null;
    try {
      real = await dbSendMessage(currentUser.orgId, activeChannel.id, currentUser.id, fullContent);
    } catch (e) {
      console.error('dbSendMessage threw:', e);
    }
    let finalId = optId;
    if (real?.id) {
      finalId = real.id;
      setMessages(p => {
        const next = p.map(m => m.id === optId ? real : m);
        msgCache.current[activeChannel.id] = next;
        return next;
      });
    } else {
      // Send failed — drop the optimistic message and tell the user.
      setMessages(p => {
        const next = p.filter(m => m.id !== optId);
        msgCache.current[activeChannel.id] = next;
        return next;
      });
      setInput(fullContent); // let them edit / resend
      showToast?.('❌ Message failed to send — retry?');
      return;
    }
    // Push notify other members (fire-and-forget — never block the send path).
    notifyChannelRecipients(activeChannel, real, content).catch(() => {});
    // Check if @AI is mentioned
    if (/@ai\b/i.test(content)) {
      handleAiCommand(content.replace(/@ai\b/gi, '').trim() || content);
    } else {
      analyzeMessage(finalId, content);
    }
    // Incident keyword detection
    const INCIDENT_KW = /\b(incident|emergency|critical|mayday|crisis|breach|spill|fire|explosion)\b/i;
    if (INCIDENT_KW.test(content)) setIncidentPrompt({ content });
  };

  // Push-notify recipients of a new chat message. In-app only (no SMS/email).
  // DMs go to the one other participant; public channels go to every member
  // except the sender.
  const notifyChannelRecipients = async (channel, message, rawContent) => {
    if (!channel || !message) return;
    const preview = (rawContent || message.content || '').slice(0, 120);
    const senderName = currentUser.name || 'Someone';
    let recipientIds = [];
    if (channel.type === 'dm') {
      if (channel.otherUserId) recipientIds = [channel.otherUserId];
    } else {
      const ids = await loadChannelMemberIds(channel.id);
      recipientIds = ids.filter(id => id && id !== currentUser.id);
    }
    if (!recipientIds.length) return;
    const title = channel.type === 'dm' ? senderName : `#${channel.name}`;
    const body  = channel.type === 'dm' ? preview : `${senderName}: ${preview}`;
    await notifyUserViaPush(recipientIds, title, body, {
      kind: 'chat',
      channel_id: channel.id,
      url: `/?channel=${encodeURIComponent(channel.id)}`,
    });
  };

  // ── Edit / Delete ───────────────────────────────────────────────
  const startEdit  = (msg) => { setEditingId(msg.id); setEditDraft(msg.content); };
  const cancelEdit = () => { setEditingId(null); setEditDraft(""); };
  const saveEdit   = async () => {
    if (!editDraft.trim() || !editingId) return;
    setMessages(p => p.map(m => m.id === editingId ? { ...m, content: editDraft.trim() } : m));
    setEditedIds(p => new Set([...p, editingId]));
    cancelEdit();
    await dbEditMessage(editingId, editDraft.trim());
  };
  const handleDelete = async (msgId) => {
    setMessages(p => p.map(m => m.id === msgId ? { ...m, content: '*(message deleted)*', _deleted: true } : m));
    setAiSuggestions(p => { const n={...p}; delete n[msgId]; return n; });
    await dbDeleteMessage(msgId);
  };

  // ── Reactions ───────────────────────────────────────────────────
  const QUICK_EMOJIS = ['👍','✅','🔥','❤️','😂','🤔','🎉','👀'];
  const toggleReaction = (msgId, emoji) => {
    setReactions(prev => {
      const mr = { ...(prev[msgId] || {}) };
      const uids = new Set(mr[emoji] || []);
      if (uids.has(currentUser.id)) uids.delete(currentUser.id);
      else uids.add(currentUser.id);
      if (uids.size === 0) delete mr[emoji]; else mr[emoji] = [...uids];
      return { ...prev, [msgId]: mr };
    });
    setEmojiPickMsg(null);
  };

  // ── Pins ────────────────────────────────────────────────────────
  const togglePin = (msgId) => {
    if (!activeChannel) return;
    const chId = activeChannel.id;
    setPinnedIds(prev => {
      const chPins = new Set(prev[chId] || []);
      if (chPins.has(msgId)) chPins.delete(msgId); else chPins.add(msgId);
      try { localStorage.setItem(`pins-${chId}`, JSON.stringify([...chPins])); } catch(_) {}
      return { ...prev, [chId]: chPins };
    });
  };

  // ── Bookmark ────────────────────────────────────────────────────
  const toggleBookmark = (msg) => {
    setBookmarks(prev => {
      const exists = prev.find(b => b.id === msg.id);
      const next = exists
        ? prev.filter(b => b.id !== msg.id)
        : [...prev, { id:msg.id, content:msg.content, channelId:activeChannel?.id, channelName:activeChannel?.name||'DM', savedAt:new Date().toISOString(), senderName:getSender(msg).name }];
      try { localStorage.setItem(`nuggets_bookmarks_${currentUser.id}`, JSON.stringify(next)); } catch(_) {}
      return next;
    });
  };

  // ── Forward message ──────────────────────────────────────────────
  const executeForward = async () => {
    if (!forwardTarget || !forwardMsg) return;
    const fwdContent = `↪️ *Forwarded from ${forwardMsg.senderName}:*\n> ${forwardMsg.content.replace(/\n/g,'\n> ')}`;
    await dbSendMessage(currentUser.orgId, forwardTarget.id, currentUser.id, fwdContent);
    setForwardMsg(null); setForwardTarget(null);
    showToast(`✓ Message forwarded to #${forwardTarget.name || 'DM'}`);
  };

  // ── Thread replies ──────────────────────────────────────────────
  const sendThreadReply = () => {
    if (!threadInput.trim() || !threadParentId) return;
    const reply = {
      id: `tr-${Date.now()}`,
      user_id: currentUser.id,
      content: threadInput.trim(),
      created_at: new Date().toISOString(),
    };
    setThreadMsgs(prev => ({ ...prev, [threadParentId]: [...(prev[threadParentId]||[]), reply] }));
    setThreadInput("");
    setTimeout(() => threadInputRef.current?.focus(), 0);
  };

  // ── "Catch me up" AI summary ────────────────────────────────────
  const handleCatchMeUp = async () => {
    if (messages.length === 0) return;
    setCatchMeUp('loading');
    const recent = messages.slice(-50).map(m => {
      const s = getSender(m);
      return `${s.name} [${new Date(m.created_at).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}]: ${m.content}`;
    }).join('\n');
    try {
      const data = await callAI({ systemPrompt: 'Summarize this team chat in 5-8 concise bullet points covering key topics, decisions made, and action items. Be brief and direct.', messages: [{ role: 'user', content: `Chat log:\n${recent}` }], feature:'messenger_summary', user:currentUser });
      setCatchMeUp(data.content?.[0]?.text || 'Unable to summarize.');
    } catch(e) { setCatchMeUp(`Error: ${e.message}`); }
  };

  // ── Incident auto-channel ───────────────────────────────────────
  const handleCreateIncidentChannel = async () => {
    if (!incidentPrompt) return;
    const ts = new Date().toISOString().slice(0,16).replace('T','-').replace(':','-');
    const name = `incident-${ts}`;
    const ch = await createChannel(currentUser.orgId, currentUser.id, name,
      `Auto-created incident channel from: "${incidentPrompt.content.slice(0,80)}"`);
    if (ch) {
      setChannels(p => [...p, ch].sort((a, b) => a.name.localeCompare(b.name)));
      setActiveChannel(ch);
      showToast(`🚨 Incident channel #${name} created`);
    }
    setIncidentPrompt(null);
  };

  // ── @Mentions ───────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const val = e.target.value;
    setInput(val);

    // Typing broadcast — throttled to one hit per 2.5s while the
    // user is actively typing.  Immediate `stopped` when the input
    // goes empty, and an idle timer fires `stopped` 4s after the
    // last keystroke.
    if (activeChannel?.id) {
      if (!val.trim()) {
        clearTimeout(typingStopTimer.current);
        if (typingSentAt.current) {
          typingSentAt.current = 0;
          broadcastTyping(activeChannel.id, { stopped: true });
        }
      } else {
        const now = Date.now();
        if (now - typingSentAt.current > 2500) {
          typingSentAt.current = now;
          broadcastTyping(activeChannel.id);
        }
        clearTimeout(typingStopTimer.current);
        typingStopTimer.current = setTimeout(() => {
          if (typingSentAt.current) {
            typingSentAt.current = 0;
            broadcastTyping(activeChannel.id, { stopped: true });
          }
        }, 4000);
      }
    }

    const caret = e.target.selectionStart ?? val.length;
    const before = val.slice(0, caret);
    const atIdx = before.lastIndexOf('@');
    if (atIdx >= 0 && (atIdx === 0 || /\s/.test(before[atIdx - 1]))) {
      const q = before.slice(atIdx + 1);
      if (!q.includes(' ') && q.length <= 20) {
        setMentionQuery(q);
        setMentionPos({ start: atIdx, end: caret });
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const insertMention = (user) => {
    if (!mentionPos) return;
    const after = input.slice(mentionPos.end);
    setInput(`${input.slice(0, mentionPos.start)}@${user.name} ${after}`);
    setShowMentions(false); setMentionQuery("");
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const mentionMatches = showMentions
    ? [{ id: '__ai__', name: 'AI', initials: '🤖', accent: '#A78BFA' }, ...users.filter(u => u.id !== currentUser.id)]
        .filter(u => u.name.toLowerCase().startsWith(mentionQuery.toLowerCase())).slice(0, 6)
    : [];

  // ── Render helpers ──────────────────────────────────────────────
  const parseAttach = (content) => {
    const match = content.match(/\[attach:(\{.*?\})\]/s);
    if (!match) return { text: content, attach: null };
    try {
      const attach = JSON.parse(match[1]);
      const text   = content.replace(match[0], '').trim();
      return { text, attach };
    } catch { return { text: content, attach: null }; }
  };

  const renderContent = (content, isDeleted) => {
    if (isDeleted) return <em style={{ color:'rgba(255,255,255,0.3)', fontSize:12 }}>{content}</em>;
    if (!content) return null;
    const { text, attach } = parseAttach(content);
    const isImage = attach && /^image\//i.test(attach.type || '');
    const textEl = text ? (() => {
      const parts = text.split(/(@\S+)/g);
      return parts.map((part, i) => {
        if (part.startsWith('@')) {
          const name = part.slice(1);
          if (name.toLowerCase() === 'ai') return <span key={i} style={{ color:'#A78BFA', fontWeight:700 }}>@AI</span>;
          const isMe = name.toLowerCase() === currentUser.name.toLowerCase();
          if (isMe) return <mark key={i} style={{ background:'rgba(232,119,34,0.25)', color:'#E87722', borderRadius:3, padding:'0 2px', fontWeight:700, fontStyle:'normal' }}>{part}</mark>;
          return <span key={i} style={{ color:'rgba(255,255,255,0.9)', fontWeight:600 }}>{part}</span>;
        }
        return part;
      });
    })() : null;
    return (
      <>
        {textEl}
        {attach && (
          <div style={{ marginTop: text ? 6 : 0 }}>
            {isImage
              ? <img src={attach.url} alt={attach.name} style={{ maxWidth: 300, maxHeight: 240, borderRadius: 8, display:'block', cursor:'pointer' }} onClick={() => window.open(attach.url, '_blank')} />
              : <a href={attach.url} target="_blank" rel="noopener noreferrer"
                  style={{ display:'inline-flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.07)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:8, padding:'6px 12px', color:'#60A5FA', textDecoration:'none', fontSize:12, fontFamily:"'Montserrat',sans-serif" }}>
                  <Paperclip size={12}/> {attach.name}
                  {attach.size && <span style={{ color:'rgba(255,255,255,0.3)', fontSize:10 }}>({Math.round(attach.size/1024)}KB)</span>}
                </a>
            }
          </div>
        )}
      </>
    );
  };

  // ── Computed ─────────────────────────────────────────────────────
  const chPinnedIds    = pinnedIds[activeChannel?.id] || new Set();
  const pinnedMessages = messages.filter(m => chPinnedIds.has(m.id));
  const threadParent   = threadParentId ? messages.find(m => m.id === threadParentId) : null;
  const currentThreadMsgs = threadParentId ? (threadMsgs[threadParentId] || []) : [];

  // ── Sidebar helpers ─────────────────────────────────────────────
  const handleCreateChannel = async () => {
    if (!newChName.trim()) return;
    const ch = await createChannel(currentUser.orgId, currentUser.id, newChName);
    if (ch) {
      setChannels(p => [...p, ch].sort((a, b) => a.name.localeCompare(b.name)));
      setActiveChannel(ch); setNewChName(""); setShowNewCh(false);
    }
  };
  const handleStartDM = async (user) => {
    const ch = await getOrCreateDM(currentUser.orgId, currentUser.id, user.id);
    if (ch) {
      setChannels(p => p.find(c => c.id === ch.id) ? p : [...p, ch]);
      setChannelMembersById(prev => {
        const n = new Map(prev);
        n.set(ch.id, new Set([currentUser.id, user.id]));
        return n;
      });
      setHiddenDMs(p => { const n = new Set(p); n.delete(ch.id); return n; });
      setActiveChannel(ch);
      if (isMobile) setMobShowSidebar(false);
    }
    setShowDMPicker(false);
    setGroupDmUserIds(new Set());
  };
  // Multi-select flavour — creates or finds a group DM containing
  // the caller + every selected user.
  const handleStartGroupDM = async () => {
    const ids = [...groupDmUserIds];
    if (!ids.length) return;
    const ch = await getOrCreateGroupDM(currentUser.orgId, currentUser.id, ids);
    if (ch) {
      setChannels(p => p.find(c => c.id === ch.id) ? p : [...p, ch]);
      setChannelMembersById(prev => {
        const n = new Map(prev);
        n.set(ch.id, new Set([currentUser.id, ...ids]));
        return n;
      });
      setHiddenDMs(p => { const n = new Set(p); n.delete(ch.id); return n; });
      setActiveChannel(ch);
      if (isMobile) setMobShowSidebar(false);
    }
    setShowDMPicker(false);
    setGroupDmUserIds(new Set());
  };
  const hideDM = (e, chId) => {
    e.stopPropagation();
    setHiddenDMs(p => new Set([...p, chId]));
    if (activeChannel?.id === chId) setActiveChannel(channels.find(c => c.type === "public") || null);
  };
  // For a DM, returns the array of "other" user objects — everyone
  // in the channel except the caller.  Group DMs return >1 entry;
  // legacy 1-on-1 DMs return exactly 1.
  const getDmOthers = (ch) => {
    if (ch?.type !== 'dm') return [];
    const memberIds = channelMembersById.get(ch.id);
    if (memberIds) {
      return [...memberIds]
        .filter(id => id !== currentUser.id)
        .map(id => users.find(u => u.id === id))
        .filter(Boolean);
    }
    // Fallback for the transient window before channel_members has
    // loaded — legacy `otherUserId` field from getOrCreateDM's return.
    const u = users.find(u => u.id === ch.otherUserId);
    return u ? [u] : [];
  };
  const getDisplayName = (ch) => {
    if (ch.type !== "dm") return `#${ch.name}`;
    const others = getDmOthers(ch);
    if (others.length === 0) return "Direct Message";
    if (others.length === 1) return others[0].name;
    // Group DM — "Keith, Evan, Miles".  Cap at 4 names + "…" to keep
    // the sidebar readable.
    const names = others.map(u => u.name.split(' ')[0]);
    if (names.length <= 4) return names.join(', ');
    return names.slice(0, 3).join(', ') + `, +${names.length - 3}`;
  };
  // Legacy single-user accessor — first "other" member.  Some UI
  // paths (typing indicator, "seen" checkmark) still assume 1-on-1;
  // returning the first is the sensible degraded behaviour.
  const getDMUser  = (ch) => getDmOthers(ch)[0] || null;
  const getSender  = (msg) => msg.user_id === '__ai__'
    ? { name:'AMP AI', initials:'🤖', accent:'#A78BFA' }
    : userMap.current.get(msg.user_id) || { name:"Unknown", initials:"?", accent:"#666" };

  const publicChannels = channels.filter(c => c.type === "public");
  const dmChannels     = channels.filter(c => c.type === "dm" && !hiddenDMs.has(c.id));
  const qLower         = searchQuery.toLowerCase();
  const filteredPublic = qLower ? publicChannels.filter(c => c.name.toLowerCase().includes(qLower)) : publicChannels;
  const filteredDMs    = qLower ? dmChannels.filter(c => getDisplayName(c).toLowerCase().includes(qLower)) : dmChannels;

  const SidebarLabel = ({ children, onAdd }) => (
    <div style={{ padding:"10px 12px 4px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.35)", letterSpacing:"0.12em", textTransform:"uppercase" }}>{children}</span>
      <button onClick={onAdd} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.35)", cursor:"pointer", padding:0, fontSize:18, lineHeight:1 }}>+</button>
    </div>
  );
  const ChItem = ({ ch }) => {
    const [hov, setHov] = useState(false);
    const isActive = activeChannel?.id === ch.id;
    const unread   = unreadCounts[ch.id] || 0;
    const dmOthers = ch.type === "dm" ? getDmOthers(ch) : [];
    const isDm     = dmOthers.length > 0;
    // Group-DM presence: light the dot green if ANY other member is
    // currently online.  Single DMs keep the same behaviour they had.
    const isOnline = dmOthers.some(u => onlineUsers.has(u.id));
    return (
      <div onClick={() => { setActiveChannel(ch); if (isMobile) setMobShowSidebar(false); }} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
        style={{ padding:"5px 10px 5px 14px", cursor:"pointer", borderRadius:4, margin:"1px 6px",
          background: isActive ? "rgba(232,119,34,0.15)" : hov ? "rgba(255,255,255,0.05)" : "transparent",
          color: isActive ? C.accent : "rgba(255,255,255,0.65)",
          fontFamily:"'Montserrat',sans-serif", fontSize:12,
          fontWeight: isActive || unread > 0 ? 700 : 400,
          transition:"all 0.15s", display:"flex", alignItems:"center", gap:6 }}>
        {isDm && <div style={{ width:7, height:7, borderRadius:"50%", flexShrink:0,
          background: isOnline ? "#22C55E" : "rgba(255,255,255,0.2)",
          boxShadow: isOnline ? "0 0 5px #22C55E" : "none" }} title={dmOthers.length > 1 ? `${dmOthers.length} members` : undefined} />}
        {isDm && dmOthers.length > 1 && (
          <span style={{ fontSize:8, fontWeight:700, color:"rgba(255,255,255,0.4)", flexShrink:0 }}>{dmOthers.length}</span>
        )}
        <span style={{ flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{getDisplayName(ch)}</span>
        {unread > 0 && <div style={{ minWidth:16, height:16, borderRadius:8, background:"#E87722",
          display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:9, fontWeight:800, color:"#fff", padding:"0 4px", flexShrink:0 }}>
          {unread > 99 ? "99+" : unread}
        </div>}
        {ch.type === "dm" && hov && <button onClick={e => hideDM(e, ch.id)}
          style={{ background:"none", border:"none", color:"rgba(255,255,255,0.35)", cursor:"pointer", padding:0, lineHeight:1, flexShrink:0, display:"flex" }}>
          <X size={12} />
        </button>}
      </div>
    );
  };

  // ── RENDER ──────────────────────────────────────────────────────
  const mobInChat = isMobile && activeChannel && !mobShowSidebar;
  const mobInList = isMobile && (!activeChannel || mobShowSidebar);
  return (
    <div style={{ display:"flex", height:isMobile?"calc(100dvh - 120px)":"calc(100vh - 130px)", borderRadius:isMobile?0:8, overflow:"hidden", border:isMobile?"none":`1px solid ${C.border}`, animation:"fadeUp 0.5s ease" }}
      onClick={() => { setEmojiPickMsg(null); setShowMentions(false); }}>

      {/* SIDEBAR */}
      <div style={{ width:isMobile?"100%":220, display: isMobile && mobInChat ? "none" : "flex", background:"rgba(255,255,255,0.03)", borderRight:isMobile?"none":`1px solid ${C.border}`, flexDirection:"column", flexShrink:0 }}>
        <div style={{ padding:"14px 14px 8px", borderBottom:`1px solid ${C.border}`, flexShrink:0 }}>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:800, color:C.accent, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:8 }}>40Geo Messenger</div>
          <div style={{ display:"flex", alignItems:"center", gap:6, background:"rgba(255,255,255,0.06)", borderRadius:6, padding:"5px 9px", border:`1px solid ${searchQuery ? C.accent+"55" : "transparent"}` }}>
            <Search size={12} style={{ color:"rgba(255,255,255,0.35)", flexShrink:0 }} />
            <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search messages…"
              style={{ background:"none", border:"none", outline:"none", color:"#fff", fontFamily:"'Montserrat',sans-serif", fontSize:11, flex:1, width:0 }} />
            {searchQuery && <button onClick={() => setSearchQuery("")} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", padding:0, lineHeight:1 }}><X size={11} /></button>}
          </div>
        </div>
        <div style={{ flex:1, overflowY:"auto", paddingBottom:8 }}>
          <SidebarLabel onAdd={() => setShowNewCh(v => !v)}>Channels</SidebarLabel>
          {showNewCh && (
            <div style={{ padding:"4px 10px 8px" }}>
              <input autoFocus value={newChName} onChange={e => setNewChName(e.target.value)}
                onKeyDown={e => { if (e.key==="Enter") handleCreateChannel(); if (e.key==="Escape") setShowNewCh(false); }}
                placeholder="channel-name" style={{ ...InputStyle(C), padding:"5px 8px", fontSize:11, marginBottom:5 }} />
              <button onClick={handleCreateChannel} style={{ ...BtnPrimary, padding:"4px 10px", fontSize:10, width:"100%" }}>Create</button>
            </div>
          )}
          {filteredPublic.map(ch => <ChItem key={ch.id} ch={ch} />)}
          <SidebarLabel onAdd={() => setShowDMPicker(v => !v)}>Direct Messages</SidebarLabel>
          {showDMPicker && (
            <div style={{ padding:"2px 10px 8px" }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.4)", letterSpacing:"0.06em", marginBottom:4 }}>
                {groupDmUserIds.size === 0
                  ? 'Click a name for a 1-on-1 DM, or tick multiple to create a group DM.'
                  : `${groupDmUserIds.size} selected — click Start DM.`}
              </div>
              {users.filter(u => u.id !== currentUser.id).map(u => {
                const picked = groupDmUserIds.has(u.id);
                return (
                  <div key={u.id}
                    style={{ padding:"5px 6px", borderRadius:4, fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.7)", display:"flex", alignItems:"center", gap:7, background: picked ? 'rgba(232,119,34,0.10)' : 'transparent' }}>
                    <input
                      type="checkbox"
                      checked={picked}
                      onChange={() => setGroupDmUserIds(prev => {
                        const n = new Set(prev);
                        if (n.has(u.id)) n.delete(u.id); else n.add(u.id);
                        return n;
                      })}
                      onClick={(e) => e.stopPropagation()}
                      style={{ accentColor: C.accent, cursor:'pointer', flexShrink:0 }}
                      title="Add to group DM"
                    />
                    <div
                      onClick={() => {
                        // Row-click starts a 1-on-1 immediately unless
                        // the user is already assembling a group.
                        if (groupDmUserIds.size === 0) handleStartDM(u);
                        else {
                          setGroupDmUserIds(prev => {
                            const n = new Set(prev);
                            if (n.has(u.id)) n.delete(u.id); else n.add(u.id);
                            return n;
                          });
                        }
                      }}
                      style={{ display:'flex', alignItems:'center', gap:7, flex:1, cursor:'pointer' }}
                    >
                      <div style={{ position:"relative", flexShrink:0 }}>
                        <div style={{ width:20, height:20, borderRadius:"50%", background:u.accent||C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:700, color:"#fff" }}>{u.initials}</div>
                        {onlineUsers.has(u.id) && <div style={{ position:"absolute", bottom:-1, right:-1, width:7, height:7, borderRadius:"50%", background:"#22C55E", border:"1px solid #0D1B2A" }} />}
                      </div>
                      <span style={{ flex:1 }}>{u.name}</span>
                      {onlineUsers.has(u.id) && <span style={{ fontSize:9, color:"#22C55E" }}>online</span>}
                    </div>
                  </div>
                );
              })}
              {groupDmUserIds.size > 0 && (
                <div style={{ display:'flex', gap:6, marginTop:6 }}>
                  <button onClick={handleStartGroupDM}
                    style={{ ...BtnPrimary, padding:"5px 10px", fontSize:10, flex:1 }}>
                    Start DM ({groupDmUserIds.size})
                  </button>
                  <button onClick={() => setGroupDmUserIds(new Set())}
                    style={{ background:'transparent', border:`1px solid ${C.border}`, color:'rgba(255,255,255,0.55)', borderRadius:5, padding:"5px 10px", fontSize:10, fontFamily:"'Montserrat',sans-serif", cursor:'pointer' }}>
                    Clear
                  </button>
                </div>
              )}
            </div>
          )}
          {filteredDMs.map(ch => <ChItem key={ch.id} ch={ch} />)}
          {(() => {
            const onlineNames = [...onlineUsers]
              .filter(id => id !== currentUser.id)
              .map(id => users.find(u => u.id === id)?.name)
              .filter(Boolean);
            return (
              <div style={{ padding:"10px 14px 4px", fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.35)" }}>
                <span style={{ color:"#22C55E" }}>●</span> {onlineUsers.size} online
                {onlineNames.length > 0 && (
                  <span style={{ color:"rgba(255,255,255,0.5)", marginLeft:6 }}>
                    · {onlineNames.join(', ')}
                  </span>
                )}
              </div>
            );
          })()}
          {/* AI hint */}
          <div style={{ margin:"8px 10px 0", padding:"7px 10px", borderRadius:6, background:"rgba(167,139,250,0.06)", border:"1px solid rgba(167,139,250,0.15)" }}>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:"rgba(167,139,250,0.7)", letterSpacing:"0.1em", marginBottom:3 }}>🤖 AMP AI</div>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.35)", lineHeight:1.5 }}>
              Type <strong style={{ color:"rgba(167,139,250,0.6)" }}>/ai [question]</strong> or mention <strong style={{ color:"rgba(167,139,250,0.6)" }}>@AI</strong> in any message
            </div>
          </div>
        </div>
      </div>

      {/* MAIN */}
      <div style={{ flex:1, display: isMobile && mobInList ? "none" : "flex", flexDirection:"column", overflow:"hidden", background:C.bg }}>

        {/* Search results */}
        {searchQuery && (
          <div style={{ flex:1, overflowY:"auto", padding:"12px 18px" }}>
            {searchLoading ? (
              <div style={{ textAlign:"center", color:"rgba(255,255,255,0.25)", fontFamily:"'Montserrat',sans-serif", fontSize:12, marginTop:30 }}>Searching…</div>
            ) : searchResults === null ? (
              <div style={{ textAlign:"center", color:"rgba(255,255,255,0.2)", fontFamily:"'Montserrat',sans-serif", fontSize:12, marginTop:30 }}>Type to search messages</div>
            ) : searchResults.length === 0 ? (
              <div style={{ textAlign:"center", color:"rgba(255,255,255,0.2)", fontFamily:"'Montserrat',sans-serif", fontSize:12, marginTop:30 }}>No results for "{searchQuery}"</div>
            ) : <>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.35)", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:12 }}>
                {searchResults.length} result{searchResults.length !== 1 ? "s" : ""} for "{searchQuery}"
              </div>
              {searchResults.map(msg => {
                const sender = getSender(msg);
                const ch     = channels.find(c => c.id === msg.channel_id);
                const idx    = msg.content.toLowerCase().indexOf(searchQuery.toLowerCase());
                const hl     = idx >= 0
                  ? <>{msg.content.slice(0, idx)}<mark style={{ background:"rgba(232,119,34,0.35)", color:"#fff", borderRadius:2, padding:"0 1px" }}>{msg.content.slice(idx, idx+searchQuery.length)}</mark>{msg.content.slice(idx+searchQuery.length)}</>
                  : msg.content;
                return (
                  <div key={msg.id} onClick={() => {
                    setSearchQuery("");
                    if (ch) setActiveChannel(ch);
                    // highlight the message after channel loads
                    clearTimeout(highlightTimerRef.current);
                    setHighlightMsgId(msg.id);
                    highlightTimerRef.current = setTimeout(() => setHighlightMsgId(null), 3500);
                  }}
                    style={{ padding:"10px 12px", borderRadius:6, marginBottom:6, cursor:"pointer", background:"rgba(255,255,255,0.03)", border:`1px solid ${C.border}` }}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.07)"}
                    onMouseLeave={e=>e.currentTarget.style.background="rgba(255,255,255,0.03)"}>
                    <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:4 }}>
                      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color: msg.user_id===currentUser.id ? C.accent : "#fff" }}>{msg.user_id===currentUser.id?"You":sender.name}</span>
                      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.3)" }}>{ch?getDisplayName(ch):"?"}</span>
                      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.25)", marginLeft:"auto" }}>{new Date(msg.created_at).toLocaleString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"})}</span>
                    </div>
                    <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.75)", lineHeight:1.5 }}>{hl}</div>
                  </div>
                );
              })}
            </>}
          </div>
        )}

        {/* Normal view */}
        {!searchQuery && activeChannel && (<>
          {/* Header */}
          <div style={{ padding:isMobile?"9px 12px":"11px 18px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:isMobile?8:10, flexShrink:0 }}>
            {/* Mobile back button */}
            {isMobile && (
              <button onClick={() => setMobShowSidebar(true)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.6)", cursor:"pointer", padding:0, display:"flex", alignItems:"center" }}>
                <ChevronLeft size={20} />
              </button>
            )}
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:isMobile?13:14, color:"#fff" }}>{getDisplayName(activeChannel)}</span>
            {getDMUser(activeChannel) && (
              <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                <div style={{ width:7, height:7, borderRadius:"50%",
                  background: onlineUsers.has(getDMUser(activeChannel).id) ? "#22C55E" : "rgba(255,255,255,0.2)",
                  boxShadow:  onlineUsers.has(getDMUser(activeChannel).id) ? "0 0 5px #22C55E" : "none" }} />
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.35)" }}>
                  {onlineUsers.has(getDMUser(activeChannel).id) ? "online" : "offline"}
                </span>
              </div>
            )}
            <div style={{ flex:1 }} />
            {chPinnedIds.size > 0 && (
              <button onClick={() => setShowPinned(v => !v)}
                style={{ background: showPinned ? "rgba(255,184,48,0.12)" : "none", border:`1px solid ${showPinned?"rgba(255,184,48,0.35)":"rgba(255,255,255,0.12)"}`, color:"#FFB830", cursor:"pointer", padding:"3px 8px", borderRadius:5, fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, display:"flex", alignItems:"center", gap:4 }}>
                📌 {chPinnedIds.size}
              </button>
            )}
            {bookmarks.length > 0 && (
              <button onClick={() => setShowBookmarks(v => !v)}
                style={{ background: showBookmarks?"rgba(60,240,255,0.1)":"none", border:`1px solid ${showBookmarks?"rgba(60,240,255,0.35)":"rgba(255,255,255,0.12)"}`, color:"#3CF0FF", cursor:"pointer", padding:"3px 8px", borderRadius:5, fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, display:"flex", alignItems:"center", gap:4 }}>
                🔖 {bookmarks.length}
              </button>
            )}
            <button onClick={handleCatchMeUp} disabled={messages.length===0 || catchMeUp==='loading'}
              style={{ background:"none", border:"1px solid rgba(167,139,250,0.2)", color:"rgba(167,139,250,0.7)", cursor: messages.length===0?"not-allowed":"pointer", padding:"3px 8px", borderRadius:5, fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, display:"flex", alignItems:"center", gap:4, opacity:messages.length===0?0.4:1 }}>
              {catchMeUp==='loading' ? '⟳ Summarizing…' : '⚡ Catch me up'}
            </button>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(167,139,250,0.5)", display:"flex", alignItems:"center", gap:4 }}>
              <span>🤖</span> AI watching
            </div>
          </div>
          {/* Pinned messages panel */}
          {showPinned && pinnedMessages.length > 0 && (
            <div style={{ padding:"8px 18px", borderBottom:`1px solid ${C.border}`, background:"rgba(255,184,48,0.04)", flexShrink:0, maxHeight:180, overflowY:"auto" }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:"#FFB830", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6 }}>📌 Pinned Messages ({pinnedMessages.length})</div>
              {pinnedMessages.map(msg => { const sender = getSender(msg); return (
                <div key={msg.id} style={{ display:"flex", gap:8, alignItems:"center", marginBottom:5, padding:"5px 8px", borderRadius:5, background:"rgba(255,255,255,0.03)" }}>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color:"rgba(255,255,255,0.6)", marginRight:2 }}>{sender.name}:</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.7)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{msg.content}</span>
                  <button onClick={() => togglePin(msg.id)} title="Unpin" style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", padding:0, flexShrink:0, display:"flex" }}><X size={11}/></button>
                </div>
              ); })}
            </div>
          )}
          {/* Bookmarks panel */}
          {showBookmarks && bookmarks.length > 0 && (
            <div style={{ padding:"8px 18px", borderBottom:`1px solid ${C.border}`, background:"rgba(60,240,255,0.04)", flexShrink:0, maxHeight:200, overflowY:"auto" }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:"#3CF0FF", letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:6 }}>🔖 Bookmarks ({bookmarks.length})</div>
              {bookmarks.map(b => (
                <div key={b.id} style={{ display:"flex", gap:8, alignItems:"center", marginBottom:5, padding:"5px 8px", borderRadius:5, background:"rgba(255,255,255,0.03)" }}>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(60,240,255,0.6)", flexShrink:0 }}>#{b.channelName}</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:"rgba(255,255,255,0.55)", flexShrink:0 }}>{b.senderName}:</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.75)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{b.content}</span>
                  <button onClick={() => toggleBookmark(b)} title="Remove bookmark" style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", padding:0, flexShrink:0, display:"flex" }}><X size={11}/></button>
                </div>
              ))}
            </div>
          )}
          {/* Catch me up card */}
          {catchMeUp && catchMeUp !== 'loading' && (
            <div style={{ margin:"8px 18px 0", padding:"11px 14px", borderRadius:8, background:"rgba(167,139,250,0.07)", border:"1px solid rgba(167,139,250,0.22)", flexShrink:0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:"rgba(167,139,250,0.9)", letterSpacing:"0.1em", textTransform:"uppercase" }}>⚡ AI Channel Summary</span>
                <button onClick={() => setCatchMeUp(null)} style={{ marginLeft:"auto", background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", padding:0, display:"flex" }}><X size={12}/></button>
              </div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.75)", lineHeight:1.6, whiteSpace:"pre-wrap" }}>{catchMeUp}</div>
            </div>
          )}
          {/* Incident prompt */}
          {incidentPrompt && (
            <div style={{ margin:"6px 18px 0", padding:"9px 14px", borderRadius:8, background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.28)", display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
              <span style={{ fontSize:15 }}>🚨</span>
              <div style={{ flex:1, fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.8)" }}>Incident keyword detected — create a dedicated incident channel?</div>
              <button onClick={handleCreateIncidentChannel} style={{ ...BtnPrimary, padding:"4px 10px", fontSize:10, background:"#EF4444", flexShrink:0 }}>Create Channel</button>
              <button onClick={() => setIncidentPrompt(null)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", padding:0, display:"flex" }}><X size={12}/></button>
            </div>
          )}

          {/* Messages */}
          <div style={{ flex:1, overflowY:"auto", padding:"16px 18px 8px" }}>
            {loadingMsgs ? (
              <div style={{ textAlign:"center", color:"rgba(255,255,255,0.25)", fontFamily:"'Montserrat',sans-serif", fontSize:12, marginTop:40 }}>Loading…</div>
            ) : messages.length === 0 ? (
              <div style={{ textAlign:"center", color:"rgba(255,255,255,0.2)", fontFamily:"'Montserrat',sans-serif", fontSize:12, marginTop:40 }}>No messages yet — say something!</div>
            ) : messages.map((msg, i) => {
              const prev       = messages[i - 1];
              const showHeader = !prev || prev.user_id !== msg.user_id || (new Date(msg.created_at) - new Date(prev.created_at)) > 300000;
              const sender     = getSender(msg);
              const isMe       = msg.user_id === currentUser.id;
              const isAI       = msg.user_id === '__ai__';
              const isDeleted  = !!msg._deleted;
              const msgReacts  = reactions[msg.id] || {};
              const aiSug      = aiSuggestions[msg.id];

              return (
                <div key={msg.id}>
                  {/* Message row */}
                  <div ref={el => { if (el) msgRefs.current[msg.id] = el; }}
                    onMouseEnter={() => setHoverMsgId(msg.id)} onMouseLeave={() => setHoverMsgId(null)}
                    style={{ marginBottom: showHeader ? 10 : 2, display:"flex", gap:10, opacity: msg._optimistic ? 0.6 : 1,
                      position:"relative",
                      background: highlightMsgId===msg.id ? "rgba(232,119,34,0.14)" : isAI ? "rgba(167,139,250,0.04)" : hoverMsgId===msg.id ? "rgba(255,255,255,0.02)" : "transparent",
                      borderRadius:4, padding:"2px 4px 2px 0", margin:"0 -4px",
                      outline: highlightMsgId===msg.id ? "1px solid rgba(232,119,34,0.4)" : "none",
                      transition:"background 0.4s, outline 0.4s" }}>
                    {/* Avatar */}
                    {showHeader
                      ? <div style={{ width:34, height:34, borderRadius:"50%", background: isAI ? "#A78BFA" : sender.accent||C.accent,
                          display:"flex", alignItems:"center", justifyContent:"center", fontSize: isAI ? 16 : 11,
                          fontWeight:700, color:"#fff", flexShrink:0, marginTop:1 }}>
                          {isAI ? "🤖" : sender.initials}
                        </div>
                      : <div style={{ width:34, flexShrink:0 }} />
                    }
                    <div style={{ flex:1 }}>
                      {showHeader && (
                        <div style={{ display:"flex", gap:8, alignItems:"baseline", marginBottom:2 }}>
                          <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700,
                            color: isAI ? "#A78BFA" : isMe ? C.accent : "#fff" }}>
                            {isAI ? "AMP AI" : isMe ? "You" : sender.name}
                          </span>
                          {isAI && <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(167,139,250,0.6)", fontWeight:600, letterSpacing:"0.08em" }}>AI ASSISTANT</span>}
                          <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.3)" }}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}
                          </span>
                          {msg._ai_cmd && <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(167,139,250,0.5)", padding:"1px 5px", border:"1px solid rgba(167,139,250,0.25)", borderRadius:3 }}>/ai</span>}
                          {editedIds.has(msg.id) && <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.25)", fontStyle:"italic" }}>(edited)</span>}
                        </div>
                      )}
                      {/* Inline edit */}
                      {editingId === msg.id ? (
                        <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                          <textarea value={editDraft} onChange={e => setEditDraft(e.target.value)}
                            onKeyDown={e => { if (e.key==="Enter"&&!e.shiftKey){e.preventDefault();saveEdit();} if(e.key==="Escape") cancelEdit(); }}
                            autoFocus rows={2}
                            style={{ ...InputStyle(C), fontSize:13, resize:"none", lineHeight:1.5 }} />
                          <div style={{ display:"flex", gap:6 }}>
                            <button onClick={saveEdit} style={{ ...BtnPrimary, padding:"3px 10px", fontSize:10 }}>Save</button>
                            <button onClick={cancelEdit} style={{ ...BtnOutline(C), padding:"3px 10px", fontSize:10 }}>Cancel</button>
                            <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.25)", alignSelf:"center" }}>Esc to cancel · Enter to save</span>
                          </div>
                        </div>
                      ) : (
                        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13,
                          color: isDeleted ? "rgba(255,255,255,0.3)" : isAI ? "rgba(220,210,255,0.9)" : "rgba(255,255,255,0.88)",
                          lineHeight:1.55, wordBreak:"break-word",
                          fontStyle: isDeleted ? "italic" : "normal",
                          whiteSpace:"pre-wrap" }}>
                          {msg._loading ? <span style={{ color:"rgba(167,139,250,0.5)" }}>● ● ●</span> : renderContent(msg.content, isDeleted)}
                        </div>
                      )}
                      {/* Reactions display */}
                      {Object.keys(msgReacts).length > 0 && (
                        <div style={{ display:"flex", flexWrap:"wrap", gap:4, marginTop:4 }}>
                          {Object.entries(msgReacts).map(([emoji, uids]) => (
                            <button key={emoji} onClick={e => { e.stopPropagation(); toggleReaction(msg.id, emoji); }}
                              style={{ padding:"2px 7px", borderRadius:100, cursor:"pointer",
                                background: uids.includes(currentUser.id) ? "rgba(232,119,34,0.18)" : "rgba(255,255,255,0.06)",
                                border: `1px solid ${uids.includes(currentUser.id) ? "#E87722" : "rgba(255,255,255,0.12)"}`,
                                fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.8)", display:"flex", alignItems:"center", gap:4 }}>
                              {emoji} <span style={{ fontSize:10 }}>{uids.length}</span>
                            </button>
                          ))}
                        </div>
                      )}
                      {/* Thread reply count */}
                      {(threadMsgs[msg.id]?.length || 0) > 0 && (
                        <button onClick={() => setThreadParentId(prev => prev===msg.id?null:msg.id)}
                          style={{ marginTop:4, background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:5,
                            fontFamily:"'Montserrat',sans-serif", fontSize:11, color:C.accent, padding:0 }}>
                          💬 {threadMsgs[msg.id].length} {threadMsgs[msg.id].length===1?'reply':'replies'} in thread
                        </button>
                      )}
                      {/* Read receipt — DM last message */}
                      {activeChannel?.type==='dm' && isMe && i===messages.length-1 && !msg._optimistic && (
                        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.25)", marginTop:3 }}>
                          {onlineUsers.has(activeChannel.otherUserId) ? '✓✓ Seen' : '✓ Delivered'}
                        </div>
                      )}
                    </div>
                    {/* Hover action bar */}
                    {hoverMsgId === msg.id && !msg._optimistic && !isDeleted && editingId !== msg.id && (
                      <div onClick={e => e.stopPropagation()}
                        style={{ position:"absolute", right:4, top:-2, display:"flex", gap:2,
                          background:"rgba(18,28,45,0.97)", borderRadius:6, padding:"3px 5px",
                          border:`1px solid ${C.border}`, boxShadow:"0 2px 10px rgba(0,0,0,0.4)", zIndex:10 }}>
                        {/* Reply in thread */}
                        <button onClick={() => setThreadParentId(prev => prev===msg.id?null:msg.id)}
                          title="Reply in thread"
                          style={{ background:"none", border:"none", cursor:"pointer", padding:"2px 5px", borderRadius:4, fontSize:12,
                            color: threadParentId===msg.id ? C.accent : "rgba(255,255,255,0.5)", transition:"all 0.1s" }}
                          onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                          onMouseLeave={e=>e.currentTarget.style.background="none"}>
                          💬
                        </button>
                        {/* Emoji react */}
                        <div style={{ position:"relative" }}>
                          <button onClick={() => setEmojiPickMsg(emojiPickMsg===msg.id?null:msg.id)}
                            title="Add reaction"
                            style={{ background:"none", border:"none", cursor:"pointer", padding:"2px 5px", borderRadius:4, fontSize:13,
                              color:"rgba(255,255,255,0.5)", transition:"all 0.1s" }}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            😊
                          </button>
                          {emojiPickMsg === msg.id && (
                            <div onClick={e => e.stopPropagation()}
                              style={{ position:"absolute", bottom:"calc(100% + 4px)", right:0, background:"rgba(18,28,45,0.98)",
                                border:`1px solid ${C.border}`, borderRadius:8, padding:6, display:"flex", gap:4,
                                boxShadow:"0 4px 16px rgba(0,0,0,0.5)", zIndex:20 }}>
                              {QUICK_EMOJIS.map(e => (
                                <button key={e} onClick={() => toggleReaction(msg.id, e)}
                                  style={{ background:"none", border:"none", cursor:"pointer", fontSize:18, padding:"2px 3px", borderRadius:4, transition:"background 0.1s" }}
                                  onMouseEnter={ev=>ev.currentTarget.style.background="rgba(255,255,255,0.1)"}
                                  onMouseLeave={ev=>ev.currentTarget.style.background="none"}>
                                  {e}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Edit (own messages only) */}
                        {isMe && !isAI && (
                          <button onClick={() => startEdit(msg)} title="Edit message"
                            style={{ background:"none", border:"none", cursor:"pointer", padding:"2px 5px", borderRadius:4, fontSize:11,
                              color:"rgba(255,255,255,0.5)", fontFamily:"'Montserrat',sans-serif", transition:"all 0.1s" }}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            ✏️
                          </button>
                        )}
                        {/* Delete (own messages only) */}
                        {isMe && !isAI && (
                          <button onClick={() => handleDelete(msg.id)} title="Delete message"
                            style={{ background:"none", border:"none", cursor:"pointer", padding:"2px 5px", borderRadius:4, fontSize:11,
                              color:"rgba(255,80,80,0.5)", fontFamily:"'Montserrat',sans-serif", transition:"all 0.1s" }}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,50,50,0.1)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            🗑️
                          </button>
                        )}
                        {/* Pin */}
                        {!isAI && (
                          <button onClick={() => togglePin(msg.id)} title={chPinnedIds.has(msg.id)?"Unpin":"Pin message"}
                            style={{ background:"none", border:"none", cursor:"pointer", padding:"2px 5px", borderRadius:4, fontSize:11,
                              color: chPinnedIds.has(msg.id) ? "#FFB830" : "rgba(255,255,255,0.5)", transition:"all 0.1s" }}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            📌
                          </button>
                        )}
                        {/* Bookmark */}
                        {!isAI && (
                          <button onClick={() => toggleBookmark(msg)} title={bookmarks.find(b=>b.id===msg.id)?"Remove bookmark":"Bookmark"}
                            style={{ background:"none", border:"none", cursor:"pointer", padding:"2px 5px", borderRadius:4, fontSize:11,
                              color: bookmarks.find(b=>b.id===msg.id) ? "#3CF0FF" : "rgba(255,255,255,0.5)", transition:"all 0.1s" }}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            🔖
                          </button>
                        )}
                        {/* Forward */}
                        {!isAI && (
                          <button onClick={() => { setForwardMsg({ id:msg.id, content:msg.content, senderName:getSender(msg).name }); setForwardTarget(null); }}
                            title="Forward message"
                            style={{ background:"none", border:"none", cursor:"pointer", padding:"2px 5px", borderRadius:4, fontSize:11,
                              color:"rgba(255,255,255,0.5)", transition:"all 0.1s" }}
                            onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.1)"}
                            onMouseLeave={e=>e.currentTarget.style.background="none"}>
                            ↪️
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {/* AI Suggestion card */}
                  {aiSug && !msg._optimistic && (
                    <div onClick={e=>e.stopPropagation()} style={{ marginLeft:44, marginBottom:10, padding:"9px 12px", borderRadius:8,
                      background:"rgba(99,102,241,0.07)", border:"1px solid rgba(99,102,241,0.22)",
                      display:"flex", alignItems:"center", gap:10 }}>
                      <div style={{ fontSize:16 }}>
                        {aiSug.type==='task'?'📋':aiSug.type==='meeting'?'📅':aiSug.type==='escalation'?'⚠️':aiSug.type==='decision'?'🔖':'🤖'}
                      </div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:"rgba(167,139,250,0.85)", letterSpacing:"0.1em", marginBottom:2 }}>
                          🤖 AI DETECTED: {aiSug.type.toUpperCase()}
                        </div>
                        <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.75)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                          {aiSug.nuggetTitle}
                        </div>
                        {aiSug.reason && <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.35)", marginTop:1 }}>{aiSug.reason}</div>}
                      </div>
                      {saveNugget && (
                        <button onClick={() => {
                          saveNugget({ id:crypto.randomUUID(), title:aiSug.nuggetTitle, desc:`${aiSug.nuggetDesc||''}\n\nFrom chat: "${msg.content.slice(0,100)}"`, col:'backlog', priority:aiSug.priority||'normal', visibility:'team', ownerId:currentUser.id, assigneeId:null, createdAt:new Date().toISOString(), nuggetType:'Task' });
                          setAiSuggestions(p => { const n={...p}; delete n[msg.id]; return n; });
                          showToast('Nugget created from AI suggestion ✓');
                        }} style={{ ...BtnPrimary, padding:"4px 10px", fontSize:9, whiteSpace:"nowrap", flexShrink:0 }}>
                          → Create Nugget
                        </button>
                      )}
                      <button onClick={() => setAiSuggestions(p => { const n={...p}; delete n[msg.id]; return n; })}
                        style={{ background:"none", border:"none", color:"rgba(255,255,255,0.25)", cursor:"pointer", padding:0, flexShrink:0 }}>
                        <X size={12} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>

          {/* Input area */}
          <div style={{ padding:"8px 18px 14px", flexShrink:0, position:"relative" }}>
            {/* @mention autocomplete */}
            {showMentions && mentionMatches.length > 0 && (
              <div onClick={e=>e.stopPropagation()}
                style={{ position:"absolute", bottom:"calc(100% - 4px)", left:18,
                  background:"rgba(18,28,45,0.98)", border:`1px solid ${C.border}`,
                  borderRadius:8, overflow:"hidden", boxShadow:"0 -4px 16px rgba(0,0,0,0.4)", zIndex:20, minWidth:180 }}>
                {mentionMatches.map(u => (
                  <div key={u.id} onClick={() => insertMention(u)}
                    style={{ padding:"7px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:8,
                      fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.8)",
                      transition:"background 0.1s" }}
                    onMouseEnter={e=>e.currentTarget.style.background="rgba(255,255,255,0.08)"}
                    onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                    <div style={{ width:22, height:22, borderRadius:"50%", background:u.accent||C.accent,
                      display:"flex", alignItems:"center", justifyContent:"center", fontSize:u.id==='__ai__'?13:9, fontWeight:700, color:"#fff", flexShrink:0 }}>
                      {u.id==='__ai__'?'🤖':u.initials}
                    </div>
                    <span>{u.name}</span>
                    {u.id==='__ai__' && <span style={{ fontSize:9, color:"rgba(167,139,250,0.6)", marginLeft:"auto" }}>AI</span>}
                    {onlineUsers.has(u.id) && u.id!=='__ai__' && <span style={{ fontSize:9, color:"#22C55E", marginLeft:"auto" }}>●</span>}
                  </div>
                ))}
              </div>
            )}
            {/* Typing indicator — shows above the composer.  We cap
                the visible names at 2 and roll the rest into "N others".*/}
            {typers.size > 0 && (() => {
              const names = [...typers.values()].map(t => t.name);
              let label;
              if (names.length === 1)      label = `${names[0]} is typing…`;
              else if (names.length === 2) label = `${names[0]} and ${names[1]} are typing…`;
              else                          label = `${names[0]}, ${names[1]} and ${names.length - 2} other${names.length - 2 === 1 ? '' : 's'} are typing…`;
              return (
                <div style={{
                  padding:"2px 4px 6px", fontFamily:"'Montserrat',sans-serif",
                  fontSize:10, color:"rgba(255,255,255,0.55)",
                  display:"flex", alignItems:"center", gap:6,
                }}>
                  {/* three-dot animation via unicode braille — cheap,
                      no keyframes needed; the state itself churns
                      every second via the expiry sweep. */}
                  <span style={{ letterSpacing:2, color:C.accent }}>⠋</span>
                  <span>{label}</span>
                </div>
              );
            })()}
            {/* Attachment preview */}
            {attachment && (
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"6px 10px", background:"rgba(255,255,255,0.05)", borderRadius:8, marginBottom:6, border:`1px solid ${C.border}` }}>
                {attachment.preview
                  ? <img src={attachment.preview} alt="" style={{ width:48, height:48, objectFit:"cover", borderRadius:6 }} />
                  : <div style={{ width:48, height:48, borderRadius:6, background:"rgba(255,255,255,0.08)", display:"flex", alignItems:"center", justifyContent:"center" }}><Paperclip size={18} color="rgba(255,255,255,0.4)"/></div>
                }
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#fff", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{attachment.file.name}</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.4)" }}>{Math.round(attachment.file.size/1024)}KB</div>
                </div>
                {attachment.uploading
                  ? <span style={{ fontSize:11, color:"rgba(255,255,255,0.4)", fontFamily:"'Montserrat',sans-serif" }}>Uploading…</span>
                  : <button onClick={() => setAttachment(null)} style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", padding:4 }}><X size={14}/></button>
                }
              </div>
            )}
            <div style={{ display:"flex", gap:8, alignItems:"flex-end", background:"rgba(255,255,255,0.05)", borderRadius:8, padding:"8px 10px", border:`1px solid ${C.border}` }}>
              {/* Hidden file input */}
              <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip" style={{ display:"none" }}
                onChange={e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const preview = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;
                  setAttachment({ file, preview, uploading: false });
                  e.target.value = '';
                }} />
              <button onClick={() => fileInputRef.current?.click()} title="Attach file"
                style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", padding:"2px 4px", flexShrink:0, display:"flex", alignItems:"center" }}>
                <Paperclip size={15}/>
              </button>
              <textarea ref={inputRef} value={input} onChange={handleInputChange}
                onPaste={e => {
                  // Slack-style: paste a screenshot straight into the
                  // composer.  We only intercept when the clipboard
                  // actually carries an image blob — plain-text pastes
                  // fall through to the normal textarea behaviour.
                  const items = e.clipboardData?.items;
                  if (!items || !items.length) return;
                  for (const it of items) {
                    if (it.kind !== 'file') continue;
                    if (!it.type?.startsWith('image/')) continue;
                    const blob = it.getAsFile();
                    if (!blob) continue;
                    e.preventDefault();
                    const ext = (blob.type.split('/')[1] || 'png').split('+')[0];
                    // Use the same filename shape Slack does; makes the
                    // attachment card read naturally when the recipient
                    // sees it.  The ISO timestamp is safe inside our
                    // storage_path builder (no path-hostile chars).
                    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
                    const file  = new File([blob], `screenshot-${stamp}.${ext}`, { type: blob.type });
                    const preview = URL.createObjectURL(file);
                    setAttachment({ file, preview, uploading: false });
                    return; // one image per paste — good enough for Slack parity
                  }
                }}
                onKeyDown={e => {
                  if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
                  if (e.key==="Escape") { setShowMentions(false); setEditingId(null); }
                  if (e.key==="ArrowUp" && !input.trim()) {
                    // Edit last own message on up-arrow in empty input
                    const lastOwn = [...messages].reverse().find(m => m.user_id===currentUser.id && !m._deleted && !m._ai_cmd);
                    if (lastOwn) { startEdit(lastOwn); setInput(""); }
                  }
                }}
                placeholder={`Message ${getDisplayName(activeChannel)} · /ai [question] · @mention`}
                rows={1}
                style={{ flex:1, background:"none", border:"none", outline:"none", color:"#fff",
                  fontFamily:"'Montserrat',sans-serif", fontSize:13, resize:"none", lineHeight:1.5 }} />
              <button onClick={handleSend} disabled={!input.trim() && !attachment}
                style={{ ...BtnPrimary, padding:"5px 12px", flexShrink:0, opacity:(input.trim()||attachment)?1:0.4 }}>
                <Send size={13}/>
              </button>
            </div>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.2)", marginTop:4, paddingLeft:2 }}>
              Enter to send · Shift+Enter for new line · ↑ to edit last message
            </div>
          </div>
        </>)}

        {!searchQuery && !activeChannel && (
          <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", color:"rgba(255,255,255,0.2)", fontFamily:"'Montserrat',sans-serif", fontSize:13 }}>
            Select a channel to start messaging
          </div>
        )}
      </div>

      {/* THREAD PANEL */}
      {threadParentId && threadParent && (
        <div style={{ width:300, borderLeft:`1px solid ${C.border}`, display:"flex", flexDirection:"column", background:"rgba(255,255,255,0.015)", flexShrink:0 }}>
          {/* Header */}
          <div style={{ padding:"11px 14px", borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:13, color:"#fff" }}>Thread</span>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.3)" }}>({currentThreadMsgs.length} {currentThreadMsgs.length===1?'reply':'replies'})</span>
            <div style={{ flex:1 }} />
            <button onClick={() => { setThreadParentId(null); setThreadInput(""); }}
              style={{ background:"none", border:"none", color:"rgba(255,255,255,0.4)", cursor:"pointer", padding:0, display:"flex" }}>
              <X size={14}/>
            </button>
          </div>
          {/* Parent message */}
          <div style={{ padding:"12px 14px", borderBottom:`1px solid ${C.border}`, flexShrink:0, background:"rgba(255,255,255,0.025)" }}>
            {(() => { const s = getSender(threadParent); const isMe = threadParent.user_id===currentUser.id; return (
              <div style={{ display:"flex", gap:8, alignItems:"flex-start" }}>
                <div style={{ width:28, height:28, borderRadius:"50%", background:s.accent||C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:9, fontWeight:700, color:"#fff", flexShrink:0 }}>{s.initials}</div>
                <div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color:isMe?C.accent:"#fff", marginBottom:3 }}>{isMe?"You":s.name}</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.8)", lineHeight:1.5, wordBreak:"break-word" }}>{threadParent.content}</div>
                </div>
              </div>
            ); })()}
          </div>
          {/* Replies */}
          <div style={{ flex:1, overflowY:"auto", padding:"10px 14px" }}>
            {currentThreadMsgs.length === 0 ? (
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.2)", textAlign:"center", marginTop:20 }}>No replies yet — start the thread!</div>
            ) : currentThreadMsgs.map(reply => {
              const s = getSender(reply); const isMe = reply.user_id===currentUser.id;
              return (
                <div key={reply.id} style={{ display:"flex", gap:7, marginBottom:10, alignItems:"flex-start" }}>
                  <div style={{ width:24, height:24, borderRadius:"50%", background:s.accent||C.accent, display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:700, color:"#fff", flexShrink:0, marginTop:2 }}>{s.initials}</div>
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", gap:6, alignItems:"baseline", marginBottom:2 }}>
                      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:isMe?C.accent:"#fff" }}>{isMe?"You":s.name}</span>
                      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:"rgba(255,255,255,0.3)" }}>{new Date(reply.created_at).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'})}</span>
                    </div>
                    <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.8)", lineHeight:1.5, wordBreak:"break-word" }}>{reply.content}</div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Thread input */}
          <div style={{ padding:"8px 14px 12px", flexShrink:0, borderTop:`1px solid ${C.border}` }}>
            <div style={{ display:"flex", gap:6, background:"rgba(255,255,255,0.05)", borderRadius:6, padding:"6px 8px", border:`1px solid ${C.border}` }}>
              <textarea ref={threadInputRef} value={threadInput} onChange={e => setThreadInput(e.target.value)}
                onKeyDown={e => { if (e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendThreadReply();} }}
                placeholder="Reply in thread…" rows={1}
                style={{ flex:1, background:"none", border:"none", outline:"none", color:"#fff", fontFamily:"'Montserrat',sans-serif", fontSize:12, resize:"none", lineHeight:1.5 }} />
              <button onClick={sendThreadReply} disabled={!threadInput.trim()}
                style={{ ...BtnPrimary, padding:"3px 8px", flexShrink:0, opacity:threadInput.trim()?1:0.4 }}>
                <Send size={11}/>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LAUNCHPAD BUBBLE */}
      {activeChannel && !searchQuery && (
        <LaunchpadBubble
          user={{ id: currentUser.id, name: currentUser.name, email: currentUser.email }}
          sessionId={activeChannel.id}
          channelType={activeChannel.type}
          channelName={activeChannel.type === 'dm'
            ? (users.find(u => u.id === activeChannel.otherUserId)?.name || 'Direct Message')
            : `#${activeChannel.name}`}
          channelMembers={activeChannel.type === 'dm'
            ? users.filter(u => u.id === activeChannel.otherUserId || u.id === currentUser.id)
            : users}
          autoJoin={autoJoinChannelId === activeChannel.id}
          onAutoJoinComplete={onAutoJoinHandled}
          notifyUserViaPush={notifyUserViaPush}
        />
      )}

      {/* ── FORWARD MESSAGE MODAL ────────────────────────────── */}
      {forwardMsg && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:10000 }}
          onClick={e=>{ if(e.target===e.currentTarget){ setForwardMsg(null); setForwardTarget(null); } }}>
          <div style={{ background:'#0D1B2A', border:'1px solid rgba(232,119,34,0.25)', borderRadius:12, padding:24, width:420, display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:13, color:'#fff' }}>↪️ Forward Message</span>
              <button onClick={()=>{ setForwardMsg(null); setForwardTarget(null); }} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', cursor:'pointer' }}><X size={14}/></button>
            </div>
            <div style={{ background:'rgba(255,255,255,0.04)', borderRadius:7, padding:'8px 12px', fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.6)', lineHeight:1.5, maxHeight:80, overflowY:'auto', whiteSpace:'pre-wrap' }}>
              <span style={{ color:'rgba(255,255,255,0.35)', fontWeight:700 }}>{forwardMsg.senderName}: </span>{forwardMsg.content.slice(0,200)}{forwardMsg.content.length>200?'…':''}
            </div>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.35)', letterSpacing:'0.1em', textTransform:'uppercase' }}>Forward to</div>
            <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:220, overflowY:'auto' }}>
              {channels.filter(ch => ch.id !== activeChannel?.id).map(ch => {
                const label = ch.type === 'dm'
                  ? (users.find(u => u.id === ch.otherUserId)?.name || 'DM')
                  : `#${ch.name}`;
                return (
                  <div key={ch.id} onClick={()=>setForwardTarget(ch)}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderRadius:7, border:`1px solid ${forwardTarget?.id===ch.id?'rgba(232,119,34,0.5)':'rgba(255,255,255,0.08)'}`, background:forwardTarget?.id===ch.id?'rgba(232,119,34,0.08)':'rgba(255,255,255,0.03)', cursor:'pointer' }}>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:'#fff' }}>{label}</span>
                  </div>
                );
              })}
            </div>
            <button onClick={executeForward} disabled={!forwardTarget}
              style={{ ...BtnPrimary, justifyContent:'center', opacity:!forwardTarget?0.4:1 }}>
              <Send size={12}/> Forward
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// HELP DESK — ARIA-powered ticket management
// ═══════════════════════════════════════════════════════════════════

const HD_SAMPLE_TICKETS = [
  { id:'HD-001', title:'Cannot access AMP dashboard after password reset', description:'I reset my password but now I get a 403 Forbidden error when trying to log in. Our ops team is blocked.', customer:{name:'Sarah Chen',email:'schen@ambipar.com',company:'Ambipar-WOB'}, priority:'critical', status:'open', category:'account', created_at:new Date(Date.now()-5400000).toISOString(), sla_deadline:new Date(Date.now()+1800000).toISOString(), thread:[{from:'customer',content:'Hi, I reset my password but now I cant log into the AMP dashboard. Getting a 403 error. Our entire ops team is blocked — we have a spill response in 2 hours.',ts:new Date(Date.now()-5400000).toISOString()}], tags:['auth','urgent','ops'], csat:null, assignee:null },
  { id:'HD-002', title:'AIS vessel tracking not updating — positions frozen', description:'Vessel positions have been frozen for the past 3 hours. Live feeds appear offline.', customer:{name:'James Rickard',email:'j.rickard@acbl.com',company:'ACBL'}, priority:'high', status:'in_progress', category:'technical', created_at:new Date(Date.now()-10800000).toISOString(), sla_deadline:new Date(Date.now()+7200000).toISOString(), thread:[{from:'customer',content:'Our AIS vessel positions havent updated in 3 hours. The map is completely frozen. We need this for active barge operations on the Mississippi.',ts:new Date(Date.now()-10800000).toISOString()},{from:'agent',content:'We are investigating the AIS data feed connectivity. Our team has been notified. ETA for update: 30 minutes.',ts:new Date(Date.now()-9000000).toISOString()},{from:'customer',content:'Still seeing frozen data. Please expedite.',ts:new Date(Date.now()-3600000).toISOString()}], tags:['ais','critical-ops','data-feed'], csat:null, assignee:null },
  { id:'HD-003', title:'Billing discrepancy — overcharged on March invoice', description:'March invoice shows $4,200 but our contract rate is $3,800/month. Need credit.', customer:{name:'Laura Netto',email:'lnetto@40geo.com',company:'40Geo Internal'}, priority:'medium', status:'pending', category:'billing', created_at:new Date(Date.now()-86400000).toISOString(), sla_deadline:new Date(Date.now()+86400000).toISOString(), thread:[{from:'customer',content:'Our March invoice shows $4,200 but our contracted monthly rate is $3,800. Please issue a credit for the $400 difference.',ts:new Date(Date.now()-86400000).toISOString()},{from:'agent',content:'Thank you for flagging this. I am reviewing your contract terms and the invoice with our billing team. Will respond within 24 hours.',ts:new Date(Date.now()-82800000).toISOString()}], tags:['billing','credit','review'], csat:null, assignee:null },
  { id:'HD-004', title:'Request: HAZMAT incident report PDF export', description:'Need PDF export for HAZMAT incident reports for regulatory compliance filing.', customer:{name:'Mike Torres',email:'m.torres@woodside.com',company:'Woodside'}, priority:'medium', status:'open', category:'feature_request', created_at:new Date(Date.now()-172800000).toISOString(), sla_deadline:new Date(Date.now()+172800000).toISOString(), thread:[{from:'customer',content:'We need to export HAZMAT incident reports as PDF for our regulatory compliance filings. Currently we have to screenshot everything. This is urgent for our Q1 audit.',ts:new Date(Date.now()-172800000).toISOString()}], tags:['feature','hazmat','compliance'], csat:null, assignee:null },
  { id:'HD-005', title:'Weather alert API returning stale data', description:'Weather alerts are 6+ hours behind. Affecting safety planning decisions.', customer:{name:'Carlos Espinoza',email:'c.espinoza@ambipar.com',company:'Ambipar-WOB'}, priority:'high', status:'open', category:'technical', created_at:new Date(Date.now()-7200000).toISOString(), sla_deadline:new Date(Date.now()+3600000).toISOString(), thread:[{from:'customer',content:'The weather alert module is showing data from 6+ hours ago. We are making safety decisions based on stale information. This is a critical safety issue.',ts:new Date(Date.now()-7200000).toISOString()}], tags:['weather','api','safety'], csat:null, assignee:null },
  { id:'HD-006', title:'User cannot be assigned to HAZMAT group', description:'Trying to add new team member to the HAZMAT response group but the option is greyed out.', customer:{name:'Priya Sharma',email:'p.sharma@acbl.com',company:'ACBL'}, priority:'low', status:'resolved', category:'account', created_at:new Date(Date.now()-259200000).toISOString(), sla_deadline:new Date(Date.now()-172800000).toISOString(), resolved_at:new Date(Date.now()-86400000).toISOString(), thread:[{from:'customer',content:'We cannot add our new hire to the HAZMAT group. The option is greyed out in User Management.',ts:new Date(Date.now()-259200000).toISOString()},{from:'agent',content:'This is caused by a permission scope issue. I have reset the group permissions for your organization. Please try again now.',ts:new Date(Date.now()-172800000).toISOString()},{from:'customer',content:'That worked! Thank you.',ts:new Date(Date.now()-86400000).toISOString()}], tags:['permissions','user-mgmt','resolved'], csat:5, assignee:null },
  { id:'HD-007', title:'Logbook entries not syncing across devices', description:'Field team entries on mobile are not appearing in the web dashboard.', customer:{name:'Tom Bradley',email:'t.bradley@woodside.com',company:'Woodside'}, priority:'high', status:'in_progress', category:'technical', created_at:new Date(Date.now()-14400000).toISOString(), sla_deadline:new Date(Date.now()+10800000).toISOString(), thread:[{from:'customer',content:'Our field team is using the mobile app to add logbook entries but they are not appearing in the web dashboard for our supervisors.',ts:new Date(Date.now()-14400000).toISOString()},{from:'agent',content:'I can reproduce this. We are seeing a sync delay issue in the logbook service. Escalating to engineering now.',ts:new Date(Date.now()-10800000).toISOString()}], tags:['logbook','sync','mobile'], csat:null, assignee:null },
  { id:'HD-008', title:'Dashboard loading extremely slow — 30+ seconds', description:'AMP dashboard taking 30+ seconds to load. Productivity is severely impacted.', customer:{name:'Anna Kowalski',email:'a.kowalski@40geo.com',company:'40Geo Internal'}, priority:'medium', status:'open', category:'technical', created_at:new Date(Date.now()-3600000).toISOString(), sla_deadline:new Date(Date.now()+43200000).toISOString(), thread:[{from:'customer',content:'The AMP dashboard is taking over 30 seconds to load every time we open it. Our team is spending significant time just waiting. Browser console shows timeout errors.',ts:new Date(Date.now()-3600000).toISOString()}], tags:['performance','dashboard'], csat:null, assignee:null },
];

function HelpDesk({ C, currentUser, showToast, saveNugget }) {
  const [hdView,        setHdView]        = useState("queue");
  const [tickets,       setTickets]       = useState(HD_SAMPLE_TICKETS);
  const [activeTicket,  setActiveTicket]  = useState(null);
  const [showNew,       setShowNew]       = useState(false);
  const [showEmail,     setShowEmail]     = useState(false);
  const [ariaLoading,   setAriaLoading]   = useState(false);
  const [ariaSug,       setAriaSug]       = useState(null);
  const [replyDraft,    setReplyDraft]    = useState("");
  const [filter,        setFilter]        = useState("all");
  const [searchTerm,    setSearchTerm]    = useState("");
  const [emailInput,    setEmailInput]    = useState("");
  // New ticket form
  const [nTitle,  setNTitle]  = useState("");
  const [nDesc,   setNDesc]   = useState("");
  const [nPri,    setNPri]    = useState("medium");
  const [nCat,    setNCat]    = useState("general");
  const [nCname,  setNCname]  = useState("");
  const [nCemail, setNCemail] = useState("");
  const [nCco,    setNCco]    = useState("");

  const galaxyRef  = useRef(null);
  const galaxyAnim = useRef(null);

  const PRIORITY_COLOR = { critical:'#EF4444', high:'#F97316', medium:'#FFB830', low:'#22C55E' };
  const STATUS_COLOR   = { open:'#3CF0FF', in_progress:'#E87722', pending:'#A78BFA', resolved:'#22C55E', closed:'rgba(255,255,255,0.3)' };
  const STATUS_LABEL   = { open:'Open', in_progress:'In Progress', pending:'Pending', resolved:'Resolved', closed:'Closed' };
  const CAT_LABEL      = { billing:'💳 Billing', technical:'⚙️ Technical', account:'👤 Account', feature_request:'✨ Feature', bug:'🐛 Bug', general:'📋 General' };

  const getSlaStatus = (t) => {
    if (!t.sla_deadline || t.status==='resolved'||t.status==='closed') return 'ok';
    const rem = new Date(t.sla_deadline) - Date.now();
    return rem < 0 ? 'breached' : rem < 3600000 ? 'at_risk' : 'ok';
  };
  const slaDisplay = (t) => {
    if (!t.sla_deadline||t.status==='resolved'||t.status==='closed') return null;
    const rem = new Date(t.sla_deadline) - Date.now();
    if (rem < 0) return { text:'SLA BREACHED', color:'#EF4444' };
    const h = Math.floor(rem/3600000), m = Math.floor((rem%3600000)/60000);
    const txt = h>24?`${Math.floor(h/24)}d ${h%24}h`:h>0?`${h}h ${m}m`:`${m}m`;
    return { text:`SLA: ${txt}`, color: rem<3600000?'#FFB830':'rgba(255,255,255,0.5)' };
  };
  const timeAgoHD = (ts) => {
    if (!ts) return '';
    const m = Math.floor((Date.now()-new Date(ts))/60000);
    if (m<1) return 'just now'; if (m<60) return `${m}m ago`;
    const h=Math.floor(m/60); if (h<24) return `${h}h ago`;
    return `${Math.floor(h/24)}d ago`;
  };

  // ── Stats ───────────────────────────────────────────────────────
  const stats = {
    open:      tickets.filter(t=>t.status==='open').length,
    inProg:    tickets.filter(t=>t.status==='in_progress').length,
    slaRisk:   tickets.filter(t=>getSlaStatus(t)!=='ok').length,
    resolved:  tickets.filter(t=>t.status==='resolved').length,
    avgCsat:   (() => { const r=tickets.filter(t=>t.csat!==null); return r.length?(r.reduce((a,b)=>a+b.csat,0)/r.length).toFixed(1):null; })(),
  };

  // ── Filtered tickets ─────────────────────────────────────────────
  const filtered = tickets.filter(t => {
    if (filter==='open')       return t.status==='open';
    if (filter==='in_progress')return t.status==='in_progress';
    if (filter==='pending')    return t.status==='pending';
    if (filter==='sla_risk')   return getSlaStatus(t)!=='ok';
    if (filter==='resolved')   return t.status==='resolved';
    return true;
  }).filter(t => !searchTerm || [t.title,t.customer.name,t.id,t.customer.company]
    .some(s=>s.toLowerCase().includes(searchTerm.toLowerCase())))
    .sort((a,b) => {
      const po={critical:0,high:1,medium:2,low:3};
      const ss={open:0,in_progress:1,pending:2,resolved:3,closed:4};
      if(ss[a.status]!==ss[b.status]) return ss[a.status]-ss[b.status];
      return po[a.priority]-po[b.priority];
    });

  // ── ARIA ─────────────────────────────────────────────────────────
  const callAria = useCallback(async (ticket) => {
    if (!ticket) return;
    setAriaLoading(true); setAriaSug(null);
    try {
      const thread = ticket.thread.map(m=>`${m.from==='customer'?ticket.customer.name:'Agent (40Geo)'}: ${m.content}`).join('\n\n');
      const data = await callAI({ systemPrompt:`You are ARIA (Automated Response & Intelligence Assistant), the AI help desk agent for 40Geo's AMP platform. You assist support agents by analyzing tickets and drafting professional, empathetic responses. When you see a technical issue (AIS, weather API, permissions, logbook sync), reference our AMP platform knowledge. Return ONLY valid JSON: {"response":"full professional reply to customer (2-4 sentences)","sentiment":"positive|neutral|frustrated|angry","escalate":false,"escalate_reason":"","category":"billing|technical|account|feature_request|bug|general","priority_recommendation":"critical|high|medium|low","quick_wins":["short actionable suggestion 1","short actionable suggestion 2"]}`, messages:[{role:'user',content:`Ticket ID: ${ticket.id}\nTitle: ${ticket.title}\nPriority: ${ticket.priority}\nCategory: ${ticket.category}\nCustomer: ${ticket.customer.name} at ${ticket.customer.company}\n\nThread:\n${thread}\n\nAnalyze and provide suggested response.`}], feature:'helpdesk_aria', user:currentUser });
      const text = data.content?.[0]?.text||'{}';
      const match = text.match(/\{[\s\S]*\}/);
      if (match) setAriaSug(JSON.parse(match[0]));
    } catch(_) { setAriaSug({ response:'Unable to connect to ARIA. Please try again.', sentiment:'neutral', escalate:false, quick_wins:[] }); }
    finally { setAriaLoading(false); }
  }, [currentUser]);

  useEffect(() => {
    if (activeTicket) { callAria(activeTicket); setReplyDraft(""); }
    else setAriaSug(null);
  }, [activeTicket?.id]); // eslint-disable-line

  // ── Actions ──────────────────────────────────────────────────────
  const sendReply = (content) => {
    if (!content.trim()||!activeTicket) return;
    const msg = { from:'agent', content:content.trim(), ts:new Date().toISOString(), agentName:currentUser.name };
    const updated = { ...activeTicket, thread:[...activeTicket.thread, msg], updated_at:new Date().toISOString(), status:activeTicket.status==='open'?'in_progress':activeTicket.status };
    setTickets(p=>p.map(t=>t.id===updated.id?updated:t));
    setActiveTicket(updated); setReplyDraft(""); setAriaSug(null);
    showToast(`Reply sent to ${activeTicket.customer.name}`);
    setTimeout(()=>callAria(updated), 800);
  };

  const resolveTicket = (id, csat=4) => {
    setTickets(p=>p.map(t=>t.id===id?{...t,status:'resolved',resolved_at:new Date().toISOString(),csat}:t));
    if (activeTicket?.id===id) setActiveTicket(p=>({...p,status:'resolved',csat}));
    showToast('Ticket resolved ✓  CSAT survey queued.');
  };

  const updateStatus = (id, status) => {
    setTickets(p=>p.map(t=>t.id===id?{...t,status}:t));
    if (activeTicket?.id===id) setActiveTicket(p=>({...p,status}));
  };

  const escalateToNugget = (ticket) => {
    if (!saveNugget) return;
    saveNugget({ id:Date.now(), title:`[HD ${ticket.id}] ${ticket.title}`, desc:`Escalated from Help Desk.\nCustomer: ${ticket.customer.name} (${ticket.customer.company})\n\n${ticket.description||''}`, col:'backlog', priority:ticket.priority==='critical'?'critical':ticket.priority==='high'?'high':'normal', visibility:'team', ownerId:currentUser.id, type:'bug', createdAt:new Date().toISOString() });
    setTickets(p=>p.map(t=>t.id===ticket.id?{...t,tags:[...new Set([...t.tags,'escalated'])]}:t));
    showToast(`HD ${ticket.id} escalated → Nuggets board`);
  };

  const createTicket = () => {
    if (!nTitle.trim()) return;
    const sla = { critical:3600000, high:14400000, medium:86400000, low:259200000 }[nPri];
    const t = { id:`HD-${String(tickets.length+1).padStart(3,'0')}`, title:nTitle.trim(), description:nDesc.trim(), customer:{name:nCname||'Customer',email:nCemail||'unknown@example.com',company:nCco||'External'}, priority:nPri, status:'open', category:nCat, created_at:new Date().toISOString(), sla_deadline:new Date(Date.now()+sla).toISOString(), thread:[{from:'customer',content:nDesc||nTitle,ts:new Date().toISOString()}], tags:[], csat:null, assignee:null };
    setTickets(p=>[t,...p]); setShowNew(false); setNTitle("");setNDesc("");setNPri("medium");setNCat("general");setNCname("");setNCemail("");setNCco("");
    setActiveTicket(t); showToast(`Ticket ${t.id} created`);
  };

  const createFromEmail = () => {
    const lines = emailInput.split('\n');
    const from = lines.find(l=>l.match(/^from:/i))||'';
    const subj = lines.find(l=>l.match(/^subject:/i))||'';
    const body = lines.filter(l=>!l.match(/^(from|to|subject|date):/i)&&l.trim()).join('\n').trim();
    const fm = from.match(/([^<]+)<([^>]+)>/);
    const t = { id:`HD-${String(tickets.length+1).padStart(3,'0')}`, title:subj.replace(/^subject:\s*/i,'').trim()||'Email Inquiry', description:body, customer:{name:fm?.[1]?.trim()||from.replace(/^from:\s*/i,'')||'Email Customer',email:fm?.[2]?.trim()||'unknown@example.com',company:'External'}, priority:'medium', status:'open', category:'general', created_at:new Date().toISOString(), sla_deadline:new Date(Date.now()+86400000).toISOString(), thread:[{from:'customer',content:body||emailInput,ts:new Date().toISOString()}], tags:['email'], csat:null, assignee:null };
    setTickets(p=>[t,...p]); setShowEmail(false); setEmailInput(""); setActiveTicket(t); showToast(`Ticket ${t.id} created from email`);
  };

  // ── Galaxy canvas ─────────────────────────────────────────────────
  useEffect(() => {
    if (hdView!=='galaxy') return;
    const canvas = galaxyRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let t = 0;
    const resize = () => { canvas.width=canvas.offsetWidth; canvas.height=canvas.offsetHeight; };
    resize();
    const ro = new ResizeObserver(resize); ro.observe(canvas);

    const openTickets = tickets.filter(ts=>ts.status!=='closed');
    const rings = [0.22,0.38,0.55,0.72,0.88];
    const perRing = Math.ceil(openTickets.length/rings.length);
    const ticketNodes = openTickets.map((tk,i) => ({
      ...tk,
      ring: Math.min(Math.floor(i/perRing), rings.length-1),
      angle: (i%perRing)*(2*Math.PI/perRing) + (Math.floor(i/perRing)*0.6),
      speed: [0.008,0.006,0.004,0.003,0.002][Math.min(Math.floor(i/perRing),4)],
      color: PRIORITY_COLOR[tk.priority]||'#888',
    }));

    const particles = Array.from({length:40},()=>({ x:Math.random(), y:Math.random(), vx:(Math.random()-0.5)*0.002, vy:(Math.random()-0.5)*0.002, a:Math.random(), da:0.008+Math.random()*0.01, size:1+Math.random()*2 }));

    const draw = () => {
      const W=canvas.width, H=canvas.height, cx=W/2, cy=H/2, minDim=Math.min(W,H)*0.46;
      ctx.clearRect(0,0,W,H);
      // Background stars
      particles.forEach(p=>{ p.x+=p.vx; p.y+=p.vy; p.a+=p.da; if(p.x<0||p.x>1)p.vx*=-1; if(p.y<0||p.y>1)p.vy*=-1; ctx.globalAlpha=0.3+0.2*Math.sin(p.a); ctx.fillStyle='#ffffff'; ctx.beginPath(); ctx.arc(p.x*W,p.y*H,p.size*0.6,0,Math.PI*2); ctx.fill(); }); ctx.globalAlpha=1;
      // Hub glow
      const hubGrad = ctx.createRadialGradient(cx,cy,0,cx,cy,55);
      hubGrad.addColorStop(0,'rgba(232,119,34,0.5)'); hubGrad.addColorStop(1,'rgba(232,119,34,0)');
      ctx.fillStyle=hubGrad; ctx.beginPath(); ctx.arc(cx,cy,55,0,Math.PI*2); ctx.fill();
      // Hub ring pulse
      ctx.strokeStyle=`rgba(232,119,34,${0.3+0.2*Math.sin(t*2)})`; ctx.lineWidth=2;
      ctx.beginPath(); ctx.arc(cx,cy,45+5*Math.sin(t*2),0,Math.PI*2); ctx.stroke();
      // Hub center
      ctx.fillStyle='#E87722'; ctx.beginPath(); ctx.arc(cx,cy,22,0,Math.PI*2); ctx.fill();
      ctx.fillStyle='#fff'; ctx.font='bold 9px Montserrat,sans-serif'; ctx.textAlign='center'; ctx.textBaseline='middle'; ctx.fillText('40GEO',cx,cy);
      // Orbit rings
      rings.forEach(r=>{ ctx.strokeStyle='rgba(255,255,255,0.04)'; ctx.lineWidth=1; ctx.setLineDash([4,8]); ctx.beginPath(); ctx.arc(cx,cy,r*minDim*2.15,0,Math.PI*2); ctx.stroke(); ctx.setLineDash([]); });
      // Ticket nodes
      ticketNodes.forEach(nd=>{
        const r=rings[nd.ring]*minDim*2.15;
        const angle=nd.angle+t*nd.speed;
        const x=cx+r*Math.cos(angle), y=cy+r*Math.sin(angle);
        const sla=getSlaStatus(nd);
        const radius = nd.status==='resolved'?6:nd.priority==='critical'?12:nd.priority==='high'?10:8;
        // Glow
        if (sla==='breached'||sla==='at_risk') {
          const g=ctx.createRadialGradient(x,y,0,x,y,radius*2.5);
          g.addColorStop(0,`${nd.color}55`); g.addColorStop(1,'transparent');
          ctx.fillStyle=g; ctx.beginPath(); ctx.arc(x,y,radius*2.5,0,Math.PI*2); ctx.fill();
        }
        // Node
        ctx.fillStyle=nd.color; ctx.beginPath(); ctx.arc(x,y,radius,0,Math.PI*2); ctx.fill();
        ctx.strokeStyle='rgba(0,0,0,0.5)'; ctx.lineWidth=1.5; ctx.beginPath(); ctx.arc(x,y,radius,0,Math.PI*2); ctx.stroke();
        // SLA pulse ring on at-risk
        if (sla!=='ok') { ctx.strokeStyle=`rgba(239,68,68,${0.5+0.4*Math.sin(t*5)})`; ctx.lineWidth=1.5; ctx.beginPath(); ctx.arc(x,y,radius+4,0,Math.PI*2); ctx.stroke(); }
        // Label
        ctx.fillStyle='rgba(255,255,255,0.85)'; ctx.font='8px Montserrat,sans-serif'; ctx.textAlign='center'; ctx.textBaseline='top'; ctx.fillText(nd.id,x,y+radius+3);
      });
      // Legend
      const statuses = [{k:'critical',c:'#EF4444'},{k:'high',c:'#F97316'},{k:'medium',c:'#FFB830'},{k:'low',c:'#22C55E'}];
      statuses.forEach((s,i)=>{
        const lx=14, ly=H-60+(i*14);
        ctx.fillStyle=s.c; ctx.beginPath(); ctx.arc(lx,ly,4,0,Math.PI*2); ctx.fill();
        ctx.fillStyle='rgba(255,255,255,0.4)'; ctx.font='9px Montserrat,sans-serif'; ctx.textAlign='left'; ctx.textBaseline='middle'; ctx.fillText(s.k,lx+10,ly);
      });
      t+=0.016;
      galaxyAnim.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(galaxyAnim.current); ro.disconnect(); };
  }, [hdView, tickets]); // eslint-disable-line

  // ── Analytics canvas charts ───────────────────────────────────────
  const SlaGauge = ({ value, max=tickets.length }) => {
    const pct = max ? value/max : 0;
    const angle = Math.PI + pct*Math.PI;
    const r=60, cx=75, cy=80;
    const x = cx + r*Math.cos(angle), y = cy + r*Math.sin(angle);
    const color = pct>0.9?'#22C55E':pct>0.7?'#FFB830':'#EF4444';
    return (
      <svg width={150} height={95}>
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${cx+r} ${cy}`} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth={10} strokeLinecap="round"/>
        <path d={`M ${cx-r} ${cy} A ${r} ${r} 0 0 1 ${x} ${y}`} fill="none" stroke={color} strokeWidth={10} strokeLinecap="round"/>
        <text x={cx} y={cy-8} fill={color} fontSize={20} fontWeight={800} textAnchor="middle" fontFamily="Montserrat,sans-serif">{Math.round(pct*100)}%</text>
        <text x={cx} y={cy+8} fill="rgba(255,255,255,0.35)" fontSize={8} textAnchor="middle" fontFamily="Montserrat,sans-serif">SLA COMPLIANCE</text>
      </svg>
    );
  };

  const MiniBar = ({ data, color='#E87722', height=60 }) => {
    const max = Math.max(...data, 1);
    const W=120, barW=W/data.length-2;
    return (
      <svg width={W} height={height} style={{overflow:'visible'}}>
        {data.map((v,i)=>{
          const bh=Math.max((v/max)*(height-12),2), by=height-bh-12;
          const isLast=i===data.length-1;
          return <g key={i}>
            <rect x={i*(barW+2)} y={by} width={barW} height={bh} rx={2} fill={isLast?color:'rgba(255,255,255,0.12)'} opacity={isLast?1:0.6+0.4*(i/data.length)}/>
            {isLast&&<text x={i*(barW+2)+barW/2} y={by-4} fill={color} fontSize={8} textAnchor="middle" fontFamily="Montserrat">{v}</text>}
          </g>;
        })}
      </svg>
    );
  };

  const IB = InputStyle(C);

  // ── RENDER ───────────────────────────────────────────────────────
  return (
    <div style={{display:'flex',height:'calc(100vh - 130px)',gap:0,borderRadius:8,overflow:'hidden',border:`1px solid ${C.border}`,animation:'fadeUp 0.4s ease'}}>

      {/* ── LEFT SIDEBAR ─────────────────────────────────────── */}
      <div style={{width:220,background:'rgba(255,255,255,0.025)',borderRight:`1px solid ${C.border}`,display:'flex',flexDirection:'column',flexShrink:0}}>
        {/* Header */}
        <div style={{padding:'14px 14px 10px',borderBottom:`1px solid ${C.border}`}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
            <LifeBuoy size={16} color={C.accent}/>
            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:800,color:C.accent,letterSpacing:'0.1em',textTransform:'uppercase'}}>Help Desk</span>
            <div style={{marginLeft:'auto',display:'flex',gap:4}}>
              <button onClick={()=>setShowEmail(true)} title="Create from email" style={{background:'none',border:`1px solid ${C.border}`,borderRadius:4,color:'rgba(255,255,255,0.5)',cursor:'pointer',padding:'3px 5px',display:'flex'}}>
                <Mail size={11}/>
              </button>
              <button onClick={()=>setShowNew(true)} title="New ticket" style={{...BtnPrimary,padding:'3px 6px'}}>
                <Plus size={11}/>
              </button>
            </div>
          </div>
          {/* Search */}
          <div style={{display:'flex',alignItems:'center',gap:5,background:'rgba(255,255,255,0.05)',borderRadius:5,padding:'4px 8px',border:`1px solid ${C.border}`}}>
            <Search size={11} color="rgba(255,255,255,0.3)"/>
            <input value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} placeholder="Search tickets…" style={{background:'none',border:'none',outline:'none',color:'#fff',fontFamily:"'Montserrat',sans-serif",fontSize:10,flex:1,width:0}}/>
          </div>
        </div>
        {/* Stats pills */}
        <div style={{padding:'8px 10px',display:'grid',gridTemplateColumns:'1fr 1fr',gap:4}}>
          {[
            {k:'all',   label:'All',     val:tickets.length, color:'rgba(255,255,255,0.6)'},
            {k:'open',  label:'Open',    val:stats.open,     color:'#3CF0FF'},
            {k:'sla_risk',label:'SLA ⚠',val:stats.slaRisk,  color:'#EF4444'},
            {k:'in_progress',label:'Active',val:stats.inProg,color:'#E87722'},
          ].map(({k,label,val,color})=>(
            <button key={k} onClick={()=>setFilter(k)}
              style={{padding:'5px 6px',borderRadius:5,cursor:'pointer',background:filter===k?`${color}15`:'rgba(255,255,255,0.03)',border:`1px solid ${filter===k?color:C.border}`,textAlign:'left',display:'flex',flexDirection:'column',gap:2}}>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:14,fontWeight:800,color:filter===k?color:'rgba(255,255,255,0.7)'}}>{val}</span>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:8,color:'rgba(255,255,255,0.35)',letterSpacing:'0.08em',textTransform:'uppercase'}}>{label}</span>
            </button>
          ))}
        </div>
        {/* View toggle */}
        <div style={{padding:'0 10px 8px',display:'flex',gap:3}}>
          {[['queue','Queue'],['board','Board'],['galaxy','Galaxy'],['analytics','Analytics']].map(([v,l])=>(
            <button key={v} onClick={()=>setHdView(v)}
              style={{flex:1,padding:'4px 0',borderRadius:4,cursor:'pointer',background:hdView===v?'rgba(232,119,34,0.15)':'none',border:`1px solid ${hdView===v?C.accent:C.border}`,color:hdView===v?C.accent:'rgba(255,255,255,0.35)',fontFamily:"'Montserrat',sans-serif",fontSize:8,fontWeight:700,letterSpacing:'0.06em',textTransform:'uppercase'}}>
              {l}
            </button>
          ))}
        </div>
        <div style={{flex:1,overflowY:'auto'}}>
          {/* Queue list in sidebar */}
          {filtered.map(t=>{
            const sla=slaDisplay(t);
            const isActive=activeTicket?.id===t.id;
            return (
              <div key={t.id} onClick={()=>setActiveTicket(t)}
                style={{padding:'8px 12px',cursor:'pointer',borderLeft:`3px solid ${isActive?C.accent:PRIORITY_COLOR[t.priority]+'44'}`,background:isActive?'rgba(232,119,34,0.07)':'transparent',borderBottom:`1px solid ${C.border}`,transition:'background 0.15s'}}>
                <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:3}}>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:PRIORITY_COLOR[t.priority],fontWeight:700,letterSpacing:'0.08em'}}>{t.priority.toUpperCase()}</span>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)',marginLeft:'auto'}}>{t.id}</span>
                </div>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:isActive?'#fff':'rgba(255,255,255,0.75)',fontWeight:isActive?700:400,lineHeight:1.35,overflow:'hidden',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical'}}>{t.title}</div>
                <div style={{display:'flex',alignItems:'center',gap:5,marginTop:3}}>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:STATUS_COLOR[t.status]}}>{STATUS_LABEL[t.status]}</span>
                  {sla && <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:sla.color,marginLeft:'auto'}}>{sla.text}</span>}
                </div>
              </div>
            );
          })}
          {filtered.length===0&&<div style={{padding:'20px 14px',fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.25)',textAlign:'center'}}>No tickets</div>}
        </div>
        {/* ARIA credit */}
        <div style={{padding:'8px 12px',borderTop:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:6}}>
          <div style={{width:18,height:18,borderRadius:'50%',background:'linear-gradient(135deg,#A78BFA,#6D28D9)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,flexShrink:0}}>A</div>
          <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(167,139,250,0.7)',fontWeight:700}}>ARIA Active</span>
          <div style={{width:5,height:5,borderRadius:'50%',background:'#22C55E',boxShadow:'0 0 5px #22C55E',marginLeft:'auto'}}/>
        </div>
      </div>

      {/* ── MAIN AREA ─────────────────────────────────────────── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minWidth:0}}>

        {/* ── NEW TICKET MODAL ── */}
        {showNew&&(
          <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.7)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowNew(false)}>
            <div onClick={e=>e.stopPropagation()} style={{background:'#0D1B2A',border:`1px solid ${C.border}`,borderRadius:12,padding:24,width:480,boxShadow:'0 20px 60px rgba(0,0,0,0.6)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:18}}>
                <Plus size={16} color={C.accent}/>
                <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:800,color:'#fff'}}>New Ticket</span>
                <button onClick={()=>setShowNew(false)} style={{marginLeft:'auto',background:'none',border:'none',color:'rgba(255,255,255,0.4)',cursor:'pointer',padding:0,display:'flex'}}><X size={14}/></button>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
                <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',display:'block',marginBottom:3}}>CUSTOMER NAME</label><input value={nCname} onChange={e=>setNCname(e.target.value)} placeholder="Jane Smith" style={{...IB,width:'100%',boxSizing:'border-box'}}/></div>
                <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',display:'block',marginBottom:3}}>COMPANY</label><input value={nCco} onChange={e=>setNCco(e.target.value)} placeholder="Ambipar-WOB" style={{...IB,width:'100%',boxSizing:'border-box'}}/></div>
                <div style={{gridColumn:'1/-1'}}><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',display:'block',marginBottom:3}}>EMAIL</label><input value={nCemail} onChange={e=>setNCemail(e.target.value)} placeholder="jane@company.com" style={{...IB,width:'100%',boxSizing:'border-box'}}/></div>
                <div style={{gridColumn:'1/-1'}}><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',display:'block',marginBottom:3}}>TITLE *</label><input value={nTitle} onChange={e=>setNTitle(e.target.value)} placeholder="Brief description of the issue" style={{...IB,width:'100%',boxSizing:'border-box'}}/></div>
                <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',display:'block',marginBottom:3}}>PRIORITY</label><select value={nPri} onChange={e=>setNPri(e.target.value)} style={{...IB,width:'100%'}}><option value="critical">🔴 Critical</option><option value="high">🟠 High</option><option value="medium">🟡 Medium</option><option value="low">🟢 Low</option></select></div>
                <div><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',display:'block',marginBottom:3}}>CATEGORY</label><select value={nCat} onChange={e=>setNCat(e.target.value)} style={{...IB,width:'100%'}}>{Object.entries(CAT_LABEL).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
                <div style={{gridColumn:'1/-1'}}><label style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',display:'block',marginBottom:3}}>DESCRIPTION</label><textarea value={nDesc} onChange={e=>setNDesc(e.target.value)} placeholder="Detailed description…" rows={3} style={{...IB,width:'100%',boxSizing:'border-box',resize:'none'}}/></div>
              </div>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button onClick={()=>setShowNew(false)} style={{...BtnOutline(C),padding:'7px 14px',fontSize:11}}>Cancel</button>
                <button onClick={createTicket} disabled={!nTitle.trim()} style={{...BtnPrimary,padding:'7px 14px',fontSize:11,opacity:nTitle.trim()?1:0.4}}>Create Ticket</button>
              </div>
            </div>
          </div>
        )}

        {/* ── EMAIL MODAL ── */}
        {showEmail&&(
          <div style={{position:'absolute',inset:0,background:'rgba(0,0,0,0.7)',zIndex:50,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShowEmail(false)}>
            <div onClick={e=>e.stopPropagation()} style={{background:'#0D1B2A',border:`1px solid ${C.border}`,borderRadius:12,padding:24,width:520,boxShadow:'0 20px 60px rgba(0,0,0,0.6)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                <Mail size={15} color={C.accent}/>
                <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:800,color:'#fff'}}>Create Ticket from Email</span>
                <button onClick={()=>setShowEmail(false)} style={{marginLeft:'auto',background:'none',border:'none',color:'rgba(255,255,255,0.4)',cursor:'pointer',padding:0,display:'flex'}}><X size={14}/></button>
              </div>
              <p style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.4)',marginBottom:12}}>Paste the raw email content below. ARIA will parse sender, subject, and body automatically.</p>
              <textarea value={emailInput} onChange={e=>setEmailInput(e.target.value)} placeholder={`From: Sarah Chen <schen@ambipar.com>\nTo: support@40geo.com\nSubject: Cannot access AMP dashboard\nDate: Mon, 30 Mar 2026 09:14:00\n\nHi, I'm having trouble accessing the AMP dashboard after my password reset. Getting a 403 error.`} rows={10} style={{...IB,width:'100%',boxSizing:'border-box',resize:'none',fontFamily:'monospace',fontSize:11,marginBottom:12}}/>
              <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
                <button onClick={()=>setShowEmail(false)} style={{...BtnOutline(C),padding:'7px 14px',fontSize:11}}>Cancel</button>
                <button onClick={createFromEmail} disabled={!emailInput.trim()} style={{...BtnPrimary,padding:'7px 14px',fontSize:11,opacity:emailInput.trim()?1:0.4}}>Parse &amp; Create</button>
              </div>
            </div>
          </div>
        )}

        {/* ── QUEUE VIEW ── */}
        {hdView==='queue'&&!activeTicket&&(
          <div style={{flex:1,overflowY:'auto',padding:18}}>
            <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14}}>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:800,color:'#fff'}}>Ticket Queue</span>
              <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.3)'}}>{filtered.length} ticket{filtered.length!==1?'s':''}</span>
              <div style={{marginLeft:'auto',display:'flex',gap:6}}>
                <button onClick={()=>setShowEmail(true)} style={{...BtnOutline(C),padding:'4px 10px',fontSize:10,display:'flex',alignItems:'center',gap:5}}><Mail size={11}/> From Email</button>
                <button onClick={()=>setShowNew(true)} style={{...BtnPrimary,padding:'4px 10px',fontSize:10,display:'flex',alignItems:'center',gap:5}}><Plus size={11}/> New Ticket</button>
              </div>
            </div>
            {/* Column headers */}
            <div style={{display:'grid',gridTemplateColumns:'90px 1fr 110px 90px 120px 100px',gap:8,padding:'0 12px 8px',fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.3)',letterSpacing:'0.1em',textTransform:'uppercase'}}>
              <span>ID</span><span>ISSUE</span><span>CUSTOMER</span><span>PRIORITY</span><span>STATUS / SLA</span><span>UPDATED</span>
            </div>
            {filtered.map(t=>{
              const sla=slaDisplay(t);
              return (
                <div key={t.id} onClick={()=>setActiveTicket(t)}
                  style={{display:'grid',gridTemplateColumns:'90px 1fr 110px 90px 120px 100px',gap:8,padding:'10px 12px',marginBottom:4,borderRadius:7,background:'rgba(255,255,255,0.025)',border:`1px solid ${C.border}`,cursor:'pointer',transition:'all 0.15s',alignItems:'center'}}
                  onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.05)';e.currentTarget.style.borderColor=PRIORITY_COLOR[t.priority]+'66';}}
                  onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.025)';e.currentTarget.style.borderColor=C.border;}}>
                  <div style={{display:'flex',flexDirection:'column',gap:2}}>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:800,color:PRIORITY_COLOR[t.priority]}}>{t.id}</span>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:8,color:'rgba(255,255,255,0.3)'}}>{CAT_LABEL[t.category]}</span>
                  </div>
                  <div>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.9)',lineHeight:1.4,marginBottom:2}}>{t.title}</div>
                    <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                      {t.tags.map(tag=><span key={tag} style={{fontFamily:"'Montserrat',sans-serif",fontSize:8,color:'rgba(255,255,255,0.35)',padding:'1px 5px',borderRadius:3,background:'rgba(255,255,255,0.06)'}}>{tag}</span>)}
                    </div>
                  </div>
                  <div>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.8)'}}>{t.customer.name}</div>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.35)'}}>{t.customer.company}</div>
                  </div>
                  <div style={{display:'flex',alignItems:'center',gap:4}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:PRIORITY_COLOR[t.priority],boxShadow:`0 0 5px ${PRIORITY_COLOR[t.priority]}`}}/>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:PRIORITY_COLOR[t.priority],fontWeight:700,textTransform:'capitalize'}}>{t.priority}</span>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:3}}>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:STATUS_COLOR[t.status],fontWeight:700}}>{STATUS_LABEL[t.status]}</span>
                    {sla&&<span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:sla.color}}>{sla.text}</span>}
                  </div>
                  <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)'}}>{timeAgoHD(t.updated_at||t.created_at)}</span>
                </div>
              );
            })}
            {filtered.length===0&&<div style={{textAlign:'center',padding:'40px 0',fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.2)'}}>No tickets match your filter</div>}
          </div>
        )}

        {/* ── BOARD VIEW ── */}
        {hdView==='board'&&!activeTicket&&(
          <div style={{flex:1,overflowX:'auto',overflowY:'hidden',padding:18,display:'flex',gap:14,alignItems:'flex-start'}}>
            {['open','in_progress','pending','resolved'].map(col=>{
              const colTickets=tickets.filter(t=>t.status===col);
              return (
                <div key={col} style={{width:220,flexShrink:0,display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                    <div style={{width:8,height:8,borderRadius:'50%',background:STATUS_COLOR[col]}}/>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:800,color:STATUS_COLOR[col],textTransform:'uppercase',letterSpacing:'0.1em'}}>{STATUS_LABEL[col]}</span>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.3)',marginLeft:'auto'}}>{colTickets.length}</span>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:6,minHeight:60,padding:'4px 0'}}>
                    {colTickets.map(t=>{
                      const sla=slaDisplay(t);
                      return (
                        <div key={t.id} onClick={()=>setActiveTicket(t)}
                          style={{padding:'10px 11px',borderRadius:8,background:'rgba(255,255,255,0.04)',border:`1px solid ${PRIORITY_COLOR[t.priority]}44`,cursor:'pointer',transition:'all 0.15s'}}
                          onMouseEnter={e=>{e.currentTarget.style.background='rgba(255,255,255,0.08)';e.currentTarget.style.transform='translateY(-1px)';}}
                          onMouseLeave={e=>{e.currentTarget.style.background='rgba(255,255,255,0.04)';e.currentTarget.style.transform='';}}>
                          <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:5}}>
                            <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:PRIORITY_COLOR[t.priority]}}>{t.id}</span>
                            <div style={{width:6,height:6,borderRadius:'50%',background:PRIORITY_COLOR[t.priority],marginLeft:'auto'}}/>
                          </div>
                          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.85)',lineHeight:1.4,marginBottom:6}}>{t.title}</div>
                          <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)'}}>{t.customer.company}</div>
                          {sla&&<div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:sla.color,marginTop:4}}>{sla.text}</div>}
                          {t.csat&&<div style={{marginTop:4}}>{'⭐'.repeat(t.csat)}</div>}
                        </div>
                      );
                    })}
                    {colTickets.length===0&&<div style={{padding:'16px 8px',fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.15)',textAlign:'center',border:`1px dashed ${C.border}`,borderRadius:6}}>Empty</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── GALAXY VIEW ── */}
        {hdView==='galaxy'&&!activeTicket&&(
          <div style={{flex:1,position:'relative'}}>
            <canvas ref={galaxyRef} style={{width:'100%',height:'100%',display:'block'}}/>
            <div style={{position:'absolute',top:16,right:16,background:'rgba(13,27,42,0.9)',border:`1px solid ${C.border}`,borderRadius:8,padding:'10px 14px'}}>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:800,color:C.accent,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.1em'}}>Ticket Galaxy</div>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',lineHeight:1.7}}>
                {[['🔴','Critical — 1h SLA'],['🟠','High — 4h SLA'],['🟡','Medium — 24h SLA'],['🟢','Low — 72h SLA']].map(([e,l])=><div key={l}>{e} {l}</div>)}
                <div style={{marginTop:4,color:'rgba(239,68,68,0.7)'}}>Pulsing = SLA at risk</div>
              </div>
            </div>
            <div style={{position:'absolute',top:16,left:16,background:'rgba(13,27,42,0.85)',border:`1px solid ${C.border}`,borderRadius:8,padding:'8px 12px'}}>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)'}}>Click a ticket in the sidebar to open it</div>
            </div>
          </div>
        )}

        {/* ── ANALYTICS VIEW ── */}
        {hdView==='analytics'&&!activeTicket&&(
          <div style={{flex:1,overflowY:'auto',padding:18}}>
            <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:13,fontWeight:800,color:'#fff',marginBottom:14}}>Help Desk Analytics</div>
            {/* Top KPI row */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:12,marginBottom:18}}>
              {[
                {label:'Total Tickets',    val:tickets.length,              icon:'🎫', color:C.accent},
                {label:'Open',             val:stats.open,                  icon:'📬', color:'#3CF0FF'},
                {label:'SLA At Risk',      val:stats.slaRisk,               icon:'⏱️', color:'#EF4444'},
                {label:'Avg CSAT',         val:stats.avgCsat?`${stats.avgCsat}★`:'—',icon:'⭐',color:'#FFB830'},
              ].map(({label,val,icon,color})=>(
                <div key={label} style={{padding:'14px 16px',borderRadius:8,background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`}}>
                  <div style={{fontSize:20,marginBottom:6}}>{icon}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:22,fontWeight:800,color}}>{val}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',letterSpacing:'0.1em',textTransform:'uppercase'}}>{label}</div>
                </div>
              ))}
            </div>
            {/* Charts row */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12,marginBottom:18}}>
              {/* SLA Compliance Gauge */}
              <div style={{padding:'16px',borderRadius:8,background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`,display:'flex',flexDirection:'column',alignItems:'center'}}>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:'rgba(255,255,255,0.5)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:8}}>SLA Compliance</div>
                <SlaGauge value={tickets.filter(t=>getSlaStatus(t)==='ok').length}/>
              </div>
              {/* Ticket volume 7-day */}
              <div style={{padding:'16px',borderRadius:8,background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`}}>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:'rgba(255,255,255,0.5)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:12}}>7-Day Volume</div>
                <MiniBar data={[3,5,2,7,4,6,tickets.filter(t=>new Date(t.created_at)>new Date(Date.now()-86400000)).length||stats.open]} color={C.accent} height={80}/>
                <div style={{display:'flex',justifyContent:'space-between',marginTop:4,fontFamily:"'Montserrat',sans-serif",fontSize:8,color:'rgba(255,255,255,0.25)'}}>
                  {['Mon','Tue','Wed','Thu','Fri','Sat','Today'].map(d=><span key={d}>{d}</span>)}
                </div>
              </div>
              {/* Category breakdown */}
              <div style={{padding:'16px',borderRadius:8,background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`}}>
                <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:'rgba(255,255,255,0.5)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:10}}>By Category</div>
                {Object.entries(CAT_LABEL).map(([k,label])=>{
                  const cnt=tickets.filter(t=>t.category===k).length;
                  const pct=tickets.length?cnt/tickets.length:0;
                  return (
                    <div key={k} style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                      <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.6)',width:70,flexShrink:0}}>{label}</span>
                      <div style={{flex:1,height:5,borderRadius:3,background:'rgba(255,255,255,0.07)',overflow:'hidden'}}>
                        <div style={{width:`${pct*100}%`,height:'100%',background:C.accent,borderRadius:3,transition:'width 0.6s'}}/>
                      </div>
                      <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)',width:14,textAlign:'right'}}>{cnt}</span>
                    </div>
                  );
                })}
              </div>
            </div>
            {/* ARIA Performance */}
            <div style={{padding:'16px',borderRadius:8,background:'rgba(167,139,250,0.05)',border:'1px solid rgba(167,139,250,0.2)',marginBottom:12}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
                <div style={{width:28,height:28,borderRadius:'50%',background:'linear-gradient(135deg,#A78BFA,#6D28D9)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:13}}>A</div>
                <div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:800,color:'rgba(167,139,250,0.9)'}}>ARIA Performance</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.35)'}}>Automated Response & Intelligence Assistant</div>
                </div>
              </div>
              <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10}}>
                {[
                  {label:'Suggestions Generated',val:'24',color:'#A78BFA'},
                  {label:'Responses Used by Agents',val:'18',color:'#22C55E'},
                  {label:'Avg Suggestion Time',val:'2.3s',color:'#3CF0FF'},
                  {label:'Escalations Flagged',val:'4',color:'#FFB830'},
                ].map(({label,val,color})=>(
                  <div key={label} style={{padding:'10px',borderRadius:6,background:'rgba(255,255,255,0.03)',textAlign:'center'}}>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:18,fontWeight:800,color,marginBottom:3}}>{val}</div>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:8,color:'rgba(255,255,255,0.35)',lineHeight:1.3}}>{label}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Top customers */}
            <div style={{padding:'16px',borderRadius:8,background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`}}>
              <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:'rgba(255,255,255,0.5)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:10}}>Top Customers by Volume</div>
              {Object.entries(tickets.reduce((acc,t)=>({...acc,[t.customer.company]:(acc[t.customer.company]||0)+1}),{}))
                .sort(([,a],[,b])=>b-a).slice(0,5)
                .map(([co,cnt])=>(
                  <div key={co} style={{display:'flex',alignItems:'center',gap:8,marginBottom:6,padding:'6px 8px',borderRadius:5,background:'rgba(255,255,255,0.02)'}}>
                    <div style={{width:24,height:24,borderRadius:'50%',background:C.accent+'33',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:800,color:C.accent}}>{co[0]}</div>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.8)',flex:1}}>{co}</span>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:C.accent}}>{cnt} ticket{cnt!==1?'s':''}</span>
                  </div>
                ))}
            </div>
          </div>
        )}

        {/* ── TICKET DETAIL ── */}
        {activeTicket&&(
          <div style={{flex:1,display:'flex',overflow:'hidden'}}>
            {/* Conversation */}
            <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',borderRight:`1px solid ${C.border}`}}>
              {/* Detail header */}
              <div style={{padding:'11px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                <button onClick={()=>setActiveTicket(null)} style={{background:'none',border:'none',color:'rgba(255,255,255,0.4)',cursor:'pointer',padding:0,display:'flex'}}><ChevronLeft size={16}/></button>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:700,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{activeTicket.title}</div>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginTop:2}}>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:PRIORITY_COLOR[activeTicket.priority],fontWeight:700}}>{activeTicket.id} · {activeTicket.priority.toUpperCase()}</span>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:STATUS_COLOR[activeTicket.status]}}>{STATUS_LABEL[activeTicket.status]}</span>
                    {slaDisplay(activeTicket)&&<span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:slaDisplay(activeTicket).color}}>{slaDisplay(activeTicket).text}</span>}
                  </div>
                </div>
                {/* Status selector */}
                <select value={activeTicket.status} onChange={e=>updateStatus(activeTicket.id,e.target.value)}
                  style={{...IB,padding:'4px 8px',fontSize:10,width:'auto'}}>
                  {Object.entries(STATUS_LABEL).map(([k,v])=><option key={k} value={k}>{v}</option>)}
                </select>
                <button onClick={()=>escalateToNugget(activeTicket)} title="Escalate to Nuggets"
                  style={{...BtnOutline(C),padding:'4px 8px',fontSize:10,display:'flex',alignItems:'center',gap:4}}>
                  <ArrowUpRight size={11}/> Escalate
                </button>
                {activeTicket.status!=='resolved'&&(
                  <button onClick={()=>resolveTicket(activeTicket.id,5)}
                    style={{...BtnPrimary,padding:'4px 8px',fontSize:10,background:'#22C55E',display:'flex',alignItems:'center',gap:4}}>
                    <CheckCircle size={11}/> Resolve
                  </button>
                )}
              </div>
              {/* Customer info bar */}
              <div style={{padding:'8px 16px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:12,background:'rgba(255,255,255,0.015)',flexShrink:0}}>
                <div style={{width:28,height:28,borderRadius:'50%',background:C.accent+'33',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:800,color:C.accent,flexShrink:0}}>{activeTicket.customer.name.split(' ').map(n=>n[0]).join('').slice(0,2)}</div>
                <div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,fontWeight:700,color:'#fff'}}>{activeTicket.customer.name}</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.4)'}}>{activeTicket.customer.email} · {activeTicket.customer.company}</div>
                </div>
                <div style={{marginLeft:'auto',display:'flex',gap:6}}>
                  {activeTicket.tags.map(tag=><span key={tag} style={{fontFamily:"'Montserrat',sans-serif",fontSize:8,padding:'2px 6px',borderRadius:3,background:'rgba(255,255,255,0.07)',color:'rgba(255,255,255,0.45)'}}>{tag}</span>)}
                </div>
              </div>
              {/* Thread */}
              <div style={{flex:1,overflowY:'auto',padding:'14px 16px'}}>
                {activeTicket.thread.map((msg,i)=>{
                  const isAgent=msg.from==='agent';
                  return (
                    <div key={i} style={{marginBottom:14,display:'flex',gap:10,alignItems:'flex-start',flexDirection:isAgent?'row-reverse':'row'}}>
                      <div style={{width:30,height:30,borderRadius:'50%',flexShrink:0,background:isAgent?C.accent+'33':'rgba(255,255,255,0.08)',display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:800,color:isAgent?C.accent:'rgba(255,255,255,0.6)'}}>
                        {isAgent?currentUser.initials:activeTicket.customer.name.split(' ').map(n=>n[0]).join('').slice(0,2)}
                      </div>
                      <div style={{maxWidth:'75%'}}>
                        <div style={{display:'flex',alignItems:'baseline',gap:6,marginBottom:4,flexDirection:isAgent?'row-reverse':'row'}}>
                          <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,fontWeight:700,color:isAgent?C.accent:'#fff'}}>{isAgent?(msg.agentName||'Agent (40Geo)'):activeTicket.customer.name}</span>
                          <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.3)'}}>{timeAgoHD(msg.ts)}</span>
                        </div>
                        <div style={{padding:'10px 13px',borderRadius:isAgent?'12px 4px 12px 12px':'4px 12px 12px 12px',background:isAgent?'rgba(232,119,34,0.12)':'rgba(255,255,255,0.06)',border:`1px solid ${isAgent?'rgba(232,119,34,0.2)':C.border}`,fontFamily:"'Montserrat',sans-serif",fontSize:12,color:'rgba(255,255,255,0.85)',lineHeight:1.55,wordBreak:'break-word'}}>
                          {msg.content}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {activeTicket.status==='resolved'&&(
                  <div style={{textAlign:'center',padding:'12px 0',fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'#22C55E'}}>✓ Ticket resolved · CSAT: {'⭐'.repeat(activeTicket.csat||4)}</div>
                )}
              </div>
              {/* Reply input */}
              {activeTicket.status!=='resolved'&&activeTicket.status!=='closed'&&(
                <div style={{padding:'10px 16px 14px',flexShrink:0,borderTop:`1px solid ${C.border}`}}>
                  {ariaSug?.response&&!ariaLoading&&(
                    <div style={{marginBottom:8,padding:'8px 10px',borderRadius:6,background:'rgba(167,139,250,0.07)',border:'1px solid rgba(167,139,250,0.2)',display:'flex',alignItems:'flex-start',gap:8}}>
                      <span style={{fontSize:14,flexShrink:0}}>A</span>
                      <div style={{flex:1,fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.7)',lineHeight:1.5}}>{ariaSug.response}</div>
                      <button onClick={()=>setReplyDraft(ariaSug.response)} style={{...BtnOutline(C),padding:'3px 8px',fontSize:9,flexShrink:0,whiteSpace:'nowrap'}}>Use</button>
                    </div>
                  )}
                  <div style={{display:'flex',gap:8,alignItems:'flex-end',background:'rgba(255,255,255,0.05)',borderRadius:8,padding:'8px 10px',border:`1px solid ${C.border}`}}>
                    <textarea value={replyDraft} onChange={e=>setReplyDraft(e.target.value)}
                      onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();sendReply(replyDraft);}}}
                      placeholder="Write a reply… (Enter to send, Shift+Enter for new line)" rows={2}
                      style={{flex:1,background:'none',border:'none',outline:'none',color:'#fff',fontFamily:"'Montserrat',sans-serif",fontSize:12,resize:'none',lineHeight:1.5}}/>
                    <button onClick={()=>sendReply(replyDraft)} disabled={!replyDraft.trim()}
                      style={{...BtnPrimary,padding:'6px 12px',flexShrink:0,opacity:replyDraft.trim()?1:0.4}}>
                      <Send size={12}/>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* ── ARIA SIDEBAR ── */}
            <div style={{width:280,display:'flex',flexDirection:'column',background:'rgba(167,139,250,0.03)',overflow:'hidden'}}>
              {/* ARIA header */}
              <div style={{padding:'12px 14px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                <div style={{width:32,height:32,borderRadius:'50%',background:'linear-gradient(135deg,#A78BFA,#6D28D9)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,boxShadow:'0 0 12px rgba(167,139,250,0.4)',position:'relative'}}>
                  A
                  {ariaLoading&&<div style={{position:'absolute',bottom:-2,right:-2,width:10,height:10,borderRadius:'50%',background:'#22C55E',border:'2px solid #0D1B2A',animation:'pulse 1s infinite'}}/>}
                </div>
                <div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:800,color:'rgba(167,139,250,0.9)'}}>ARIA</div>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:8,color:'rgba(255,255,255,0.35)',letterSpacing:'0.1em'}}>AI RESPONSE ASSISTANT</div>
                </div>
                <button onClick={()=>callAria(activeTicket)} disabled={ariaLoading} title="Refresh ARIA analysis"
                  style={{marginLeft:'auto',background:'none',border:'none',color:'rgba(167,139,250,0.5)',cursor:'pointer',padding:0,display:'flex',opacity:ariaLoading?0.4:1}}>
                  <RefreshCw size={13} style={{animation:ariaLoading?'spin 1s linear infinite':''}}/>
                </button>
              </div>
              <div style={{flex:1,overflowY:'auto',padding:'12px 14px',display:'flex',flexDirection:'column',gap:10}}>
                {ariaLoading&&(
                  <div style={{padding:'14px',borderRadius:8,background:'rgba(167,139,250,0.07)',border:'1px solid rgba(167,139,250,0.15)',display:'flex',gap:8,alignItems:'center'}}>
                    <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(167,139,250,0.7)'}}>ARIA is analyzing</span>
                    <span style={{color:'rgba(167,139,250,0.5)',letterSpacing:3}}>● ● ●</span>
                  </div>
                )}
                {ariaSug&&!ariaLoading&&<>
                  {/* Sentiment */}
                  <div style={{padding:'10px 12px',borderRadius:8,background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`}}>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.35)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:5}}>Customer Sentiment</div>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <span style={{fontSize:18}}>{ariaSug.sentiment==='positive'?'😊':ariaSug.sentiment==='frustrated'?'😤':ariaSug.sentiment==='angry'?'😡':'😐'}</span>
                      <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:12,fontWeight:700,color:{positive:'#22C55E',neutral:'rgba(255,255,255,0.7)',frustrated:'#FFB830',angry:'#EF4444'}[ariaSug.sentiment]||'#fff',textTransform:'capitalize'}}>{ariaSug.sentiment||'neutral'}</span>
                    </div>
                  </div>
                  {/* Suggested response */}
                  <div style={{padding:'10px 12px',borderRadius:8,background:'rgba(167,139,250,0.05)',border:'1px solid rgba(167,139,250,0.2)'}}>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'rgba(167,139,250,0.7)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:6}}>Suggested Response</div>
                    <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:11,color:'rgba(255,255,255,0.75)',lineHeight:1.55,marginBottom:8}}>{ariaSug.response}</div>
                    <button onClick={()=>setReplyDraft(ariaSug.response)} style={{...BtnPrimary,padding:'4px 10px',fontSize:10,width:'100%'}}>Use This Response</button>
                  </div>
                  {/* Quick wins */}
                  {ariaSug.quick_wins?.length>0&&(
                    <div style={{padding:'10px 12px',borderRadius:8,background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`}}>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.35)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:6}}>⚡ Quick Actions</div>
                      {ariaSug.quick_wins.map((qw,i)=>(
                        <div key={i} style={{display:'flex',gap:6,marginBottom:4,alignItems:'flex-start'}}>
                          <span style={{color:C.accent,flexShrink:0,marginTop:1}}>→</span>
                          <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.65)',lineHeight:1.4}}>{qw}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* Escalate warning */}
                  {ariaSug.escalate&&(
                    <div style={{padding:'10px 12px',borderRadius:8,background:'rgba(239,68,68,0.08)',border:'1px solid rgba(239,68,68,0.25)'}}>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'#EF4444',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:4}}>⚠ ARIA Recommends Escalation</div>
                      <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:10,color:'rgba(255,255,255,0.65)',lineHeight:1.4,marginBottom:8}}>{ariaSug.escalate_reason}</div>
                      <button onClick={()=>escalateToNugget(activeTicket)} style={{...BtnPrimary,padding:'4px 10px',fontSize:10,background:'#EF4444',width:'100%',display:'flex',alignItems:'center',gap:5,justifyContent:'center'}}><ArrowUpRight size={11}/> Escalate to Nuggets</button>
                    </div>
                  )}
                </>}
                {/* Ticket metadata */}
                <div style={{padding:'10px 12px',borderRadius:8,background:'rgba(255,255,255,0.03)',border:`1px solid ${C.border}`}}>
                  <div style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,fontWeight:700,color:'rgba(255,255,255,0.35)',letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:8}}>Ticket Details</div>
                  {[
                    ['Category',CAT_LABEL[activeTicket.category]||activeTicket.category],
                    ['Created',timeAgoHD(activeTicket.created_at)],
                    ['Messages',`${activeTicket.thread.length}`],
                    ['Replies',`${activeTicket.thread.filter(m=>m.from==='agent').length} agent · ${activeTicket.thread.filter(m=>m.from==='customer').length} customer`],
                  ].map(([k,v])=>(
                    <div key={k} style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                      <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.35)'}}>{k}</span>
                      <span style={{fontFamily:"'Montserrat',sans-serif",fontSize:9,color:'rgba(255,255,255,0.7)'}}>{v}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SLACK BRIDGE (kept for reference — replaced by Messenger)
// ═══════════════════════════════════════════════════════════════════
function SlackBridge({ C, currentUser, showToast, addAudit, canDo }) {
  const [channel,    setChannel]    = useState("C02");
  const [msg,        setMsg]        = useState("");
  const [feed,       setFeed]       = useState([
    { ch:"#40geo-deploys", user:"JW",  text:"✅ feat/geocoder-v2 deployed to prod", ts:"2h ago"  },
    { ch:"#40geo-nuggets", user:"AR",  text:"📌 GEO-042 moved → In Progress",       ts:"14m ago" },
    { ch:"#40geo-alerts",  user:"SYS", text:"🚨 CRITICAL: GEO-042 needs review",    ts:"1h ago"  },
  ]);
  const [autoConfig, setAutoConfig] = useState({ on_deploy:true, on_critical:true, on_nugget_create:false, on_nugget_move:false });

  const post = () => {
    if (!msg.trim() || !canDo("slack_post")) return;
    const ch = SLACK_CHANNELS.find(c => c.id===channel);
    setFeed(p => [{ ch:ch.name, user:currentUser.initials, text:msg, ts:"just now" }, ...p]);
    addAudit(`Posted to Slack ${ch.name}`, "system");
    showToast(`💬 Posted to ${ch.name}.`);
    setMsg("");
  };

  return (
    <div style={{ animation:"fadeUp 0.5s ease" }}>
      <SH C={C} title="Slack Bridge" sub="Bolt.js integration · post to channels · configure auto-events" />
      <div style={{ background:"rgba(232,119,34,0.08)", borderLeft:"3px solid #E87722", borderRadius:4, padding:"10px 16px", marginBottom:16, fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"rgba(255,255,255,0.7)" }}>
        💡 Slack Bot lives in <code style={{ background:"rgba(255,255,255,0.1)", padding:"1px 5px", borderRadius:2, fontSize:11 }}>apps/slack-bot/</code> — set <code style={{ background:"rgba(255,255,255,0.1)", padding:"1px 5px", borderRadius:2, fontSize:11 }}>SLACK_BOT_TOKEN</code> in <code style={{ background:"rgba(255,255,255,0.1)", padding:"1px 5px", borderRadius:2, fontSize:11 }}>.env</code>
      </div>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 280px", gap:14 }}>
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          {canDo("slack_post") && (
            <div style={Card(C)}>
              <Eyebrow>Post to Slack</Eyebrow>
              <select value={channel} onChange={e=>setChannel(e.target.value)} style={{ ...InputStyle(C), marginBottom:10 }}>
                {SLACK_CHANNELS.map(c => <option key={c.id} value={c.id}>{c.name} — {c.topic}</option>)}
              </select>
              <textarea value={msg} onChange={e=>setMsg(e.target.value)} onKeyDown={e=>e.key==="Enter"&&!e.shiftKey&&(e.preventDefault(),post())} rows={3} placeholder="Message... (Enter to post)" style={{ ...InputStyle(C), resize:"none", marginBottom:12 }}/>
              <button onClick={post} style={{ ...BtnPrimary, background:"#4A154B" }}>
                <MessageSquare size={13}/> Post to Slack
              </button>
            </div>
          )}
          <div style={Card(C)}>
            <Eyebrow>Channel Feed</Eyebrow>
            {feed.map((f,i) => (
              <div key={i} style={{ display:"flex", gap:10, padding:"9px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ width:26, height:26, borderRadius:4, background:"#4A154B", display:"flex", alignItems:"center", justifyContent:"center", fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:"#fff", flexShrink:0 }}>{f.user.slice(0,2)}</div>
                <div style={{ flex:1 }}>
                  <div style={{ display:"flex", gap:8, marginBottom:3 }}>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:"#9c5ab8" }}>{f.ch}</span>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.3)" }}>{f.ts}</span>
                  </div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:"#fff" }}>{f.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div style={Card(C)}>
            <Eyebrow>Auto Events → Slack</Eyebrow>
            {[["on_deploy","Post on deploy","#40geo-deploys"],["on_critical","Alert on critical","#40geo-alerts"],["on_nugget_create","New nugget","#40geo-nuggets"],["on_nugget_move","Board moves","#40geo-nuggets"]].map(([k,label,ch]) => (
              <div key={k} style={{ padding:"9px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:600, color:"#fff" }}>{label}</span>
                  <Toggle val={autoConfig[k]} onChange={() => setAutoConfig(p=>({...p,[k]:!p[k]}))} C={C}/>
                </div>
                <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.3)" }}>{ch}</div>
              </div>
            ))}
          </div>
          <div style={Card(C)}>
            <Eyebrow>Slash Commands</Eyebrow>
            {[["/geo nuggets","List open nuggets"],["/geo deploy","Trigger pipeline"],["/geo status","System status"]].map(([cmd,desc]) => (
              <div key={cmd} style={{ padding:"6px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                <div style={{ ...Badge("#E87722","rgba(232,119,34,0.1)","rgba(232,119,34,0.25)"), display:"inline-block", marginBottom:2 }}>{cmd}</div>
                <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:"rgba(255,255,255,0.4)" }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// NOTES — Stream-of-thought note-taker with AI, voice & mini-nuggets
// ═══════════════════════════════════════════════════════════════════

const NOTE_CATS = [
  { id:'stream',   label:'Stream of Thought', color:'#60A5FA', emoji:'🌊' },
  { id:'meeting',  label:'Meeting',           color:'#34D399', emoji:'🤝' },
  { id:'idea',     label:'Pie in the Sky',    color:'#A78BFA', emoji:'💡' },
  { id:'ai',       label:'AI Enhancement',    color:'#E87722', emoji:'🤖' },
  { id:'action',   label:'Action Item',       color:'#EF4444', emoji:'⚡' },
  { id:'research', label:'Research',          color:'#FFB830', emoji:'🔍' },
  { id:'prompt',   label:'Prompt / Code',     color:'#F59E0B', emoji:'🧠' },
  { id:'client',   label:'Client Note',       color:'#3CF0FF', emoji:'👤' },
  { id:'vendor',   label:'Vendor Note',       color:'#FF6B6B', emoji:'🏢' },
];

const NOTE_CRM_CONTACTS = [
  'ACBL','Ambipar-WOB','Woodside Energy','40Geo Internal',
  'Noble Crowser','DeepOcean','Subsea 7','TechnipFMC','Fugro',
  'Shell','BP','Chevron','ExxonMobil','Total','Equinor',
];

const INIT_NOTES = [
  {
    id:'sample-note-1', title:'AMP Integration Strategy',
    content:`Meeting notes — AMP platform integration planning\n\nKey points discussed:\n- Need to map nuggets schema → AMP companies table\n- Auth IDs must be linked properly across both systems\n- Use org_settings.features as source of truth for feature flags\n- Staging environment needed before prod rollout\n\nQuestions for next call:\n- Timeline for AMP API availability?\n- Who owns the data migration script?`,
    category:'meeting', crmLink:'ACBL', tags:['integration','AMP','architecture'],
    miniNuggets:[
      {id:'mn-s1',text:'Set up staging environment for AMP integration',status:'pending'},
      {id:'mn-s2',text:'Document API contracts between Nuggets and AMP',status:'pending'},
    ],
    createdAt:new Date(Date.now()-86400000).toISOString(), updatedAt:new Date(Date.now()-86400000).toISOString(),
  },
  {
    id:'sample-note-2', title:'Pie in the Sky — AI Voice Field Ops',
    content:`What if field operators could dictate incident reports via voice and AI auto-fills the entire form?\n\nIdeas:\n- Mobile-first voice UI for HAZMAT incidents\n- AI cross-references similar past incidents automatically\n- Auto-generates regulatory filing draft\n- Push to relevant stakeholders immediately via Launchpad\n\nThis would save field teams 40+ minutes per incident report and reduce data entry errors significantly.`,
    category:'idea', crmLink:'', tags:['ai','mobile','field-ops'],
    miniNuggets:[
      {id:'mn-s3',text:'Build voice-to-form AI prototype for HAZMAT incident reports',status:'pending'},
    ],
    createdAt:new Date(Date.now()-172800000).toISOString(), updatedAt:new Date(Date.now()-172800000).toISOString(),
  },
  {
    id:'sample-note-3', title:'Claude Enhancement Ideas for Nuggets',
    content:`Prompts to explore with Claude Code:\n\n1. Add "smart grouping" to the nuggets board — AI clusters related tickets by topic\n2. Auto-generate release notes from resolved nuggets each sprint\n3. Predict sprint velocity based on historical data and team capacity\n4. Weekly AI summary of all open critical items emailed to admin\n5. Auto-triage inbound Help Desk tickets using similar past resolutions\n\nThese could all be background scheduled tasks triggered by cron.`,
    category:'prompt', crmLink:'', tags:['claude','automation','features'],
    miniNuggets:[
      {id:'mn-s4',text:'Build AI smart-grouping feature for nuggets board',status:'pending'},
      {id:'mn-s5',text:'Auto-generate release notes from resolved nuggets',status:'pending'},
      {id:'mn-s6',text:'Weekly AI critical-items summary email to admins',status:'pending'},
    ],
    createdAt:new Date(Date.now()-3600000).toISOString(), updatedAt:new Date(Date.now()-3600000).toISOString(),
  },
];

function NotesModule({ C, currentUser, showToast, saveNugget, setChatInput, setMainView, users }) {
  const storageKey = `nuggets_notes_${currentUser.id}`;

  // Coerce a single note into a safe shape.  Guards against JSONB
  // fields (`tags`, `miniNuggets`) being stored as `{}` instead of
  // `[]` — which is how the "Taxonomics Data" note broke the whole
  // module on 2026-07-08.  Applied on state init and on every fresh
  // note that lands from the DB.
  const safeNote = (n) => (!n ? n : ({
    ...n,
    tags:        Array.isArray(n.tags)        ? n.tags        : [],
    miniNuggets: Array.isArray(n.miniNuggets) ? n.miniNuggets : [],
  }));
  const safeNotes = (arr) => (Array.isArray(arr) ? arr.map(safeNote) : []);

  const [notes, setNotes]               = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(storageKey) || 'null');
      const arr = Array.isArray(raw) && raw.length > 0 ? raw : INIT_NOTES;
      return safeNotes(arr);
    } catch { return safeNotes(INIT_NOTES); }
  });
  const _first = notes[0] || null;
  const [activeId,      setActiveId]     = useState(_first?.id || null);
  const [content,       setContent]      = useState(_first?.content || '');
  const [category,      setCategory]     = useState(_first?.category || 'stream');
  const [crmLink,       setCrmLink]      = useState(_first?.crmLink || '');
  const [tags,          setTags]         = useState(Array.isArray(_first?.tags) ? _first.tags : []);
  const [tagInput,      setTagInput]     = useState('');
  const [searchQuery,   setSearchQuery]  = useState('');
  const [filterCat,     setFilterCat]    = useState('all');
  const [recording,     setRecording]    = useState(false);
  const [aiLoading,     setAiLoading]    = useState(false);
  const [suggLoading,   setSuggLoading]  = useState(false);
  const [saveStatus,    setSaveStatus]   = useState('saved');
  const [showMsgModal,  setShowMsgModal] = useState(false);
  const [msgContent,    setMsgContent]   = useState('');
  const [channels,      setChannels]     = useState([]);
  const [selectedCh,    setSelectedCh]   = useState(null);
  const [sendLoading,   setSendLoading]  = useState(false);
  const [crmSugg,       setCrmSugg]      = useState([]);
  const [showCrmDD,     setShowCrmDD]    = useState(false);
  const [spellLoading,  setSpellLoading]  = useState(false);
  const [spellResult,   setSpellResult]   = useState(null); // null | { corrected, summary }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // null | noteId to confirm
  const recognitionRef  = useRef(null);
  const saveTimer       = useRef(null);
  const textareaRef     = useRef(null);

  // Clear spell result when content changes
  useEffect(() => { setSpellResult(null); }, [content]);

  const activeNote = notes.find(n => n.id === activeId) || null;

  // persist to localStorage + optionally sync one note to the DB
  const persist = (updated, noteToSync = null) => {
    try { localStorage.setItem(storageKey, JSON.stringify(updated)); } catch(_) {}
    if (noteToSync) dbSaveNote(noteToSync, currentUser.orgId, currentUser.id);
  };

  const selectNote = (note) => {
    clearTimeout(saveTimer.current);
    setActiveId(note.id);
    setContent(note.content);
    setCategory(note.category || 'stream');
    setCrmLink(note.crmLink || '');
    setTags(Array.isArray(note.tags) ? note.tags : []);
    setSaveStatus('saved');
  };

  // On mount: merge DB + localStorage — never discard notes from either side
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const dbNotes = await dbLoadNotes(currentUser.id);
      if (cancelled) return;
      if (dbNotes === null) {
        console.warn('[notes-effect] dbLoadNotes returned null — keeping localStorage state (' + notes.length + ' notes)');
        return; // DB error — keep localStorage as-is
      }

      // Merge: build a map by id, prefer whichever copy has the newer updatedAt
      const localNotes = notes.filter(n => !n.id?.startsWith('sample-'));
      const merged = new Map();
      [...localNotes, ...dbNotes].forEach(n => {
        const existing = merged.get(n.id);
        if (!existing || new Date(n.updatedAt || 0) >= new Date(existing.updatedAt || 0)) {
          merged.set(n.id, n);
        }
      });
      const allNotes = [...merged.values()].sort((a, b) =>
        new Date(b.updatedAt || b.createdAt || 0) - new Date(a.updatedAt || a.createdAt || 0)
      );
      console.log('[notes-effect] db=' + dbNotes.length + ' local=' + localNotes.length + ' merged=' + allNotes.length);

      if (allNotes.length === 0) return;

      // Push any local-only or newer-local notes up to DB
      const dbIds = new Set(dbNotes.map(n => n.id));
      const toSync = allNotes.filter(n => {
        if (!dbIds.has(n.id)) return true; // local-only
        const dbVer = dbNotes.find(d => d.id === n.id);
        return new Date(n.updatedAt || 0) > new Date(dbVer?.updatedAt || 0); // local is newer
      });
      if (toSync.length > 0) {
        await Promise.all(toSync.map(n => dbSaveNote(n, currentUser.orgId, currentUser.id)));
      }

      const safe = safeNotes(allNotes);
      setNotes(safe);
      try { localStorage.setItem(storageKey, JSON.stringify(safe)); } catch(_) {}
      selectNote(safe[0]);
    })();
    return () => { cancelled = true; };
  }, []); // eslint-disable-line

  const applyAutoSave = (newContent, newCat, newCrm, newTags) => {
    setSaveStatus('unsaved');
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      setSaveStatus('saving');
      setNotes(prev => {
        const title = (newContent.split('\n')[0] || 'Untitled').slice(0, 60);
        let updatedNote = null;
        const updated = prev.map(n => {
          if (n.id !== activeId) return n;
          updatedNote = { ...n, title, content: newContent, category: newCat, crmLink: newCrm, tags: newTags, updatedAt: new Date().toISOString() };
          return updatedNote;
        });
        persist(updated, updatedNote);
        return updated;
      });
      setSaveStatus('saved');
    }, 1100);
  };

  const newNote = () => {
    const note = { id:crypto.randomUUID(), title:'New Note', content:'', category:'stream', crmLink:'', tags:[], miniNuggets:[], createdAt:new Date().toISOString(), updatedAt:new Date().toISOString() };
    setNotes(prev => { const u = [note, ...prev]; persist(u, note); return u; });
    selectNote(note);
    setTimeout(() => textareaRef.current?.focus(), 80);
  };

  // ── Voice ──────────────────────────────────────────────────────────
  const toggleVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { showToast('Voice input requires Chrome browser'); return; }
    if (recording) { recognitionRef.current?.stop(); setRecording(false); return; }
    const r = new SR();
    r.continuous = true; r.interimResults = true; r.lang = 'en-US';
    let finalBuf = '';
    r.onresult = (e) => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalBuf += e.results[i][0].transcript + ' ';
      }
      if (finalBuf) {
        setContent(prev => {
          const next = prev + (prev && !prev.endsWith('\n') ? '\n' : '') + finalBuf;
          finalBuf = '';
          applyAutoSave(next, category, crmLink, tags);
          return next;
        });
      }
    };
    r.onerror = () => setRecording(false);
    r.onend = () => setRecording(false);
    r.start();
    recognitionRef.current = r;
    setRecording(true);
  };

  // ── AI: Continue thought ──────────────────────────────────────────
  const suggestContinuation = async () => {
    if (!content.trim() || suggLoading) return;
    setSuggLoading(true);
    try {
      const res = await callAI({
        feature:'notes_suggest', user:currentUser,
        model:'claude-haiku-4-5-20251001',
        systemPrompt:'You are a thought-completion assistant. Continue the user\'s note naturally — 2-4 sentences, match their tone, no preamble or meta-commentary. Just continue the thought.',
        messages:[{ role:'user', content:`Continue this note:\n\n${content.slice(-800)}` }],
      });
      const sug = res.content?.[0]?.text || '';
      if (sug) {
        const next = content + '\n' + sug.trim();
        setContent(next);
        applyAutoSave(next, category, crmLink, tags);
        showToast('✓ Thought continued');
      }
    } catch { showToast('AI suggestion failed'); }
    setSuggLoading(false);
  };

  // ── AI: Extract mini-nuggets ──────────────────────────────────────
  const extractMiniNuggets = async () => {
    if (!content.trim() || aiLoading) return;
    setAiLoading(true);
    try {
      const res = await callAI({
        feature:'notes_extract', user:currentUser,
        model:'claude-haiku-4-5-20251001',
        systemPrompt:'Extract actionable items and key ideas from the note. Return ONLY a JSON array of strings (max 8 items, max 15 words each). Be specific. If nothing actionable, return []. No markdown, just raw JSON array.',
        messages:[{ role:'user', content:content }],
      });
      const raw = res.content?.[0]?.text?.trim() || '[]';
      const items = JSON.parse(raw.replace(/```json|```/g,'').trim());
      const newMNs = items.map(text => ({ id:crypto.randomUUID(), text, status:'not_started' }));
      setNotes(prev => {
        let updatedNote = null;
        const updated = prev.map(n => {
          if (n.id !== activeId) return n;
          updatedNote = { ...n, miniNuggets:[...(n.miniNuggets||[]), ...newMNs] };
          return updatedNote;
        });
        persist(updated, updatedNote);
        return updated;
      });
      showToast(`✓ ${newMNs.length} mini-nuggets extracted`);
    } catch { showToast('AI extraction failed'); }
    setAiLoading(false);
  };

  // ── AI: Auto-detect category ──────────────────────────────────────
  const autoCategory = async () => {
    if (!content.trim()) return;
    try {
      const res = await callAI({
        feature:'notes_categorize', user:currentUser,
        model:'claude-haiku-4-5-20251001',
        systemPrompt:'Classify this note. Reply with ONLY one of: stream, meeting, idea, ai, action, research, prompt, client, vendor',
        messages:[{ role:'user', content:content.slice(0,400) }],
      });
      const det = res.content?.[0]?.text?.trim().toLowerCase();
      const found = NOTE_CATS.find(c => c.id === det);
      if (found) { setCategory(found.id); applyAutoSave(content, found.id, crmLink, tags); showToast(`✓ Category → ${found.label}`); }
    } catch(_) {}
  };

  // ── Mini-nugget actions ───────────────────────────────────────────
  const updateMN = (mnId, status) => {
    setNotes(prev => {
      let updatedNote = null;
      const updated = prev.map(n => {
        if (n.id !== activeId) return n;
        updatedNote = { ...n, miniNuggets:(n.miniNuggets||[]).map(m => m.id === mnId ? {...m,status} : m) };
        return updatedNote;
      });
      persist(updated, updatedNote);
      return updated;
    });
  };

  const promoteMN = (mn) => {
    saveNugget({
      id:crypto.randomUUID(), title:mn.text,
      desc:`Promoted from note: "${activeNote?.title}" — ${new Date().toLocaleDateString()}`,
      priority:'medium', col:'todo', ownerId:currentUser.id,
      tags, visibility:'team', sharedWith:[], company:crmLink||'',
      nuggetType:'Feature', source:'notes', createdAt:new Date().toISOString(),
    });
    updateMN(mn.id,'promoted');
    showToast('✓ Promoted to Nuggets board');
  };

  const sendMNToLab = (mn) => {
    setChatInput(mn.text);
    setMainView('lab');
    showToast('✓ Sent to Strategy Lab');
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); showToast('✓ Copied'); }
    catch { showToast('Copy failed'); }
  };

  // ── AI: Spell-check ───────────────────────────────────────────────
  const checkSpelling = async () => {
    if (!content.trim() || spellLoading) return;
    setSpellLoading(true);
    try {
      const res = await callAI({
        feature:'notes_spell', user:currentUser,
        model:'claude-haiku-4-5-20251001',
        systemPrompt:'You are a spelling and grammar checker. Check the provided text for spelling and grammar errors. Respond with ONLY a valid JSON object — no explanation, no markdown fences. Format: {"corrected":"<full corrected text>","summary":"<short summary of fixes>"} if there are errors, or {"corrected":null,"summary":"No errors found"} if the text is correct.',
        messages:[{ role:'user', content:`Check this text:\n\n${content}` }],
      });
      const raw = res.content?.[0]?.text?.trim() || '{}';
      // Strip any accidental markdown fences
      const cleaned = raw.replace(/^```(?:json)?\n?/,'').replace(/\n?```$/,'').trim();
      let result;
      try {
        result = JSON.parse(cleaned);
      } catch {
        // If model returned plain text instead of JSON, treat it as no errors
        result = { corrected: null, summary: 'No errors found' };
      }
      setSpellResult(result);
      if (!result.corrected) showToast('✓ No spelling errors found');
    } catch(err) {
      console.error('Spell check error:', err);
      const msg = err?.message || String(err);
      if (msg.toLowerCase().includes('overload') || msg.includes('529')) {
        showToast('AI is busy — try again in a moment');
      } else {
        showToast(`Spell check failed: ${msg.slice(0,60)}`);
      }
    }
    setSpellLoading(false);
  };

  const fixAll = () => {
    if (!spellResult?.corrected) return;
    const next = spellResult.corrected;
    setContent(next);
    applyAutoSave(next, category, crmLink, tags);
    setSpellResult(null);
    showToast('✓ All corrections applied');
  };

  // ── Delete note (actual) ──────────────────────────────────────────
  const confirmDeleteNote = (id) => {
    setNotes(prev => {
      const u = prev.filter(n => n.id !== id);
      persist(u);
      if (activeId === id && u.length > 0) selectNote(u[0]);
      else if (u.length === 0) { setActiveId(null); setContent(''); }
      return u;
    });
    dbDeleteNote(id);
    setDeleteConfirm(null);
    showToast('Note deleted');
  };

  // ── Send note/mini-nugget to Messenger ───────────────────────────
  const openMsgModal = async (text) => {
    setMsgContent(text);
    setShowMsgModal(true);
    const chs = await loadChannels(currentUser.orgId, currentUser.id);
    setChannels(chs);
  };

  const sendToMessenger = async () => {
    if (!selectedCh || !msgContent.trim()) return;
    setSendLoading(true);
    try {
      let targetChannelId = selectedCh.id;
      if (selectedCh._isDM) {
        const dm = await getOrCreateDM(currentUser.orgId, currentUser.id, selectedCh._userId);
        targetChannelId = dm?.id;
      }
      if (!targetChannelId) { showToast('Could not resolve channel'); setSendLoading(false); return; }
      await dbSendMessage(currentUser.orgId, targetChannelId, currentUser.id, `📝 *Note from ${currentUser.name}:*\n\n${msgContent}`);
      setShowMsgModal(false);
      setSelectedCh(null);
      showToast('✓ Note sent to Messenger');
    } catch { showToast('Send failed'); }
    setSendLoading(false);
  };

  // ── CRM autocomplete ──────────────────────────────────────────────
  const handleCrmInput = (val) => {
    setCrmLink(val);
    applyAutoSave(content, category, val, tags);
    if (val.length > 0) {
      const s = NOTE_CRM_CONTACTS.filter(c => c.toLowerCase().includes(val.toLowerCase())).slice(0,6);
      setCrmSugg(s); setShowCrmDD(s.length > 0);
    } else { setShowCrmDD(false); }
  };

  // ── Filtered list ─────────────────────────────────────────────────
  const filteredNotes = notes.filter(n => {
    const mq = !searchQuery || n.title.toLowerCase().includes(searchQuery.toLowerCase()) || n.content.toLowerCase().includes(searchQuery.toLowerCase());
    const mc = filterCat === 'all' || n.category === filterCat;
    return mq && mc;
  });

  const catMeta = id => NOTE_CATS.find(c => c.id === id) || NOTE_CATS[0];
  // eslint-disable-next-line react-hooks/purity -- "X ago" formatter; recomputed per render is intended
  const relTime = iso => { const d=Date.now()-new Date(iso).getTime(),m=Math.floor(d/60000); if(m<1)return'just now';if(m<60)return`${m}m ago`;const h=Math.floor(m/60);if(h<24)return`${h}h ago`;return`${Math.floor(h/24)}d ago`; };

  const MN_STATUS_META = {
    not_started: { label:'Not Started', color:'rgba(255,255,255,0.3)', bg:'rgba(255,255,255,0.05)', next:'in_progress' },
    in_progress:  { label:'In Progress', color:'#FFB830',              bg:'rgba(255,184,48,0.1)',   next:'completed' },
    completed:    { label:'Completed',   color:'#34D399',              bg:'rgba(52,211,153,0.1)',   next:'not_started' },
  };
  const activeMNs = (activeNote?.miniNuggets||[]).filter(m => m.status==='not_started'||m.status==='in_progress'||m.status==='pending');
  const doneMNs   = (activeNote?.miniNuggets||[]).filter(m => m.status==='completed'||m.status==='promoted'||m.status==='dismissed'||m.status==='sent');
  // legacy alias so existing data with status:'pending' shows in active list
  const pendingMNs = activeMNs;

  return (
    <div style={{ display:'flex', height:'100%', gap:0, animation:'fadeUp 0.4s ease' }}>

      {/* ── LEFT: Notes list ────────────────────────────────────── */}
      <div style={{ width:240, flexShrink:0, display:'flex', flexDirection:'column', borderRight:`1px solid ${C.border}`, background:'rgba(255,255,255,0.02)' }}>
        <div style={{ padding:'14px 12px 10px', borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:'flex', gap:6, marginBottom:8 }}>
            <div style={{ flex:1, display:'flex', alignItems:'center', gap:6, background:'rgba(255,255,255,0.06)', borderRadius:6, padding:'5px 8px', border:`1px solid ${C.border}` }}>
              <Search size={11} style={{ color:'rgba(255,255,255,0.3)', flexShrink:0 }} />
              <input value={searchQuery} onChange={e=>setSearchQuery(e.target.value)} placeholder="Search notes…"
                style={{ flex:1, background:'none', border:'none', outline:'none', color:'#fff', fontFamily:"'Montserrat',sans-serif", fontSize:11 }} />
            </div>
            <button onClick={newNote} style={{ ...BtnPrimary, padding:'5px 8px', flexShrink:0 }} title="New note"><Plus size={13}/></button>
          </div>
          <select value={filterCat} onChange={e=>setFilterCat(e.target.value)}
            style={{ width:'100%', background:'rgba(255,255,255,0.06)', border:`1px solid ${C.border}`, borderRadius:5, padding:'4px 6px', color:'rgba(255,255,255,0.7)', fontFamily:"'Montserrat',sans-serif", fontSize:10, cursor:'pointer' }}>
            <option value="all">All categories</option>
            {NOTE_CATS.map(c=><option key={c.id} value={c.id}>{c.emoji} {c.label}</option>)}
          </select>
        </div>

        <div style={{ flex:1, overflowY:'auto' }}>
          {filteredNotes.length === 0 && (
            <div style={{ padding:20, textAlign:'center', color:'rgba(255,255,255,0.25)', fontFamily:"'Montserrat',sans-serif", fontSize:11 }}>
              No notes yet.<br/>Hit + to start.
            </div>
          )}
          {filteredNotes.map(note => {
            const cm = catMeta(note.category);
            const isActive = note.id === activeId;
            return (
              <div key={note.id} onClick={()=>selectNote(note)} style={{ padding:'10px 12px', cursor:'pointer', borderLeft:`3px solid ${isActive?cm.color:'transparent'}`, background:isActive?'rgba(255,255,255,0.05)':'transparent', borderBottom:`1px solid ${C.border}`, transition:'all 0.15s', position:'relative' }}>
                <div style={{ display:'flex', alignItems:'center', gap:5, marginBottom:3 }}>
                  <span style={{ fontSize:11 }}>{cm.emoji}</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:11, color:'#fff', flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{note.title}</span>
                </div>
                <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.35)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {note.content.replace(/\n/g,' ').slice(0,50)}
                </div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginTop:4 }}>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:cm.color, textTransform:'uppercase', letterSpacing:'0.06em' }}>{cm.label}</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.25)' }}>{relTime(note.updatedAt)}</span>
                </div>
                {isActive && (
                  <button onClick={e=>{e.stopPropagation();setDeleteConfirm(note.id);}} style={{ position:'absolute', top:8, right:6, background:'none', border:'none', color:'rgba(255,255,255,0.2)', cursor:'pointer', padding:2 }} title="Delete note"><Trash2 size={10}/></button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── CENTER: Editor ──────────────────────────────────────── */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' }}>
        {!activeNote ? (
          <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column', gap:14, color:'rgba(255,255,255,0.2)' }}>
            <PenLine size={36} />
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13 }}>Select a note or create a new one</div>
            <button onClick={newNote} style={BtnPrimary}><Plus size={13}/> New Note</button>
          </div>
        ) : (
          <>
            {/* Editor top bar */}
            <div style={{ padding:'10px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
              {/* Category pills */}
              <div style={{ flex:1, display:'flex', gap:5, flexWrap:'wrap' }}>
                {NOTE_CATS.map(c => (
                  <button key={c.id} onClick={()=>{ setCategory(c.id); applyAutoSave(content,c.id,crmLink,tags); }}
                    style={{ padding:'3px 9px', borderRadius:20, border:`1px solid ${category===c.id?c.color:'rgba(255,255,255,0.1)'}`, background:category===c.id?`${c.color}20`:'transparent', color:category===c.id?c.color:'rgba(255,255,255,0.35)', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, cursor:'pointer', letterSpacing:'0.05em', transition:'all 0.15s', whiteSpace:'nowrap' }}>
                    {c.emoji} {c.label}
                  </button>
                ))}
              </div>
              <button onClick={autoCategory} title="AI: auto-detect category" style={{ background:'none', border:'none', color:'rgba(255,255,255,0.3)', cursor:'pointer', display:'flex', alignItems:'center', gap:4 }}>
                <Wand2 size={13}/>
              </button>
            </div>

            {/* CRM link + tags bar */}
            <div style={{ padding:'7px 16px', borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:10, flexShrink:0 }}>
              <Tag size={11} style={{ color:'rgba(255,255,255,0.3)', flexShrink:0 }} />
              <div style={{ position:'relative', flex:'0 0 180px' }}>
                <input value={crmLink} onChange={e=>handleCrmInput(e.target.value)} onFocus={()=>crmLink&&setShowCrmDD(true)} onBlur={()=>setTimeout(()=>setShowCrmDD(false),150)}
                  placeholder="Link client / vendor…"
                  style={{ width:'100%', background:'transparent', border:'none', outline:'none', color: crmLink?'#3CF0FF':'rgba(255,255,255,0.3)', fontFamily:"'Montserrat',sans-serif", fontSize:11, boxSizing:'border-box' }}/>
                {showCrmDD && (
                  <div style={{ position:'absolute', top:'100%', left:0, zIndex:50, background:'#1B2B5B', border:`1px solid ${C.border}`, borderRadius:6, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.4)', minWidth:180 }}>
                    {crmSugg.map(s=>(
                      <div key={s} onClick={()=>{ setCrmLink(s); setShowCrmDD(false); applyAutoSave(content,category,s,tags); }}
                        style={{ padding:'7px 12px', cursor:'pointer', fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'#fff', borderBottom:`1px solid rgba(255,255,255,0.06)` }}
                        onMouseEnter={e=>e.target.style.background='rgba(232,119,34,0.12)'}
                        onMouseLeave={e=>e.target.style.background='transparent'}>
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ width:1, height:14, background:'rgba(255,255,255,0.1)', flexShrink:0 }}/>
              <div style={{ display:'flex', gap:4, flex:1, flexWrap:'wrap', alignItems:'center' }}>
                {tags.map(t => (
                  <span key={t} style={{ background:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.12)', borderRadius:10, padding:'2px 7px', fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.6)', display:'flex', alignItems:'center', gap:4 }}>
                    #{t}
                    <button onClick={()=>{ const nt=tags.filter(x=>x!==t); setTags(nt); applyAutoSave(content,category,crmLink,nt); }} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.3)', cursor:'pointer', padding:0, lineHeight:1 }}>×</button>
                  </span>
                ))}
                <input value={tagInput} onChange={e=>setTagInput(e.target.value)}
                  onKeyDown={e=>{ if((e.key==='Enter'||e.key===',')&&tagInput.trim()){ const nt=[...new Set([...tags,tagInput.trim()])]; setTags(nt); applyAutoSave(content,category,crmLink,nt); setTagInput(''); e.preventDefault(); } }}
                  placeholder="+ tag…"
                  style={{ background:'none', border:'none', outline:'none', color:'rgba(255,255,255,0.4)', fontFamily:"'Montserrat',sans-serif", fontSize:10, width:60 }}/>
              </div>
            </div>

            {/* Textarea */}
            <textarea ref={textareaRef} value={content}
              onChange={e=>{ setContent(e.target.value); applyAutoSave(e.target.value,category,crmLink,tags); }}
              placeholder={`Start writing… or tap 🎙 to dictate\n\nStream your thoughts here — meetings, ideas, prompts, anything.\nPress Ctrl+Enter to extract mini-nuggets with AI.`}
              onKeyDown={e=>{ if(e.ctrlKey&&e.key==='Enter'){e.preventDefault();extractMiniNuggets();} }}
              style={{ flex:1, resize:'none', background:'transparent', border:'none', outline:'none', color:'rgba(255,255,255,0.88)', fontFamily:"'Montserrat',sans-serif", fontSize:13, lineHeight:1.75, padding:'16px 18px', width:'100%', boxSizing:'border-box' }}/>

            {/* Bottom action bar */}
            <div style={{ padding:'8px 16px', borderTop:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:8, flexShrink:0 }}>
              {/* Voice */}
              <button onClick={toggleVoice}
                style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:6, border:`1px solid ${recording?'#EF4444':'rgba(255,255,255,0.15)'}`, background:recording?'rgba(239,68,68,0.1)':'transparent', color:recording?'#EF4444':'rgba(255,255,255,0.4)', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, cursor:'pointer', letterSpacing:'0.08em', transition:'all 0.15s' }}>
                {recording ? <><MicOff size={12}/> Stop</> : <><Mic size={12}/> Dictate</>}
              </button>
              {recording && <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'#EF4444', animation:'pulse 1s ease infinite' }}>● LIVE</div>}

              {/* AI: continue thought */}
              <button onClick={suggestContinuation} disabled={suggLoading} title="AI: continue my thought"
                style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:6, border:'1px solid rgba(232,119,34,0.3)', background:'rgba(232,119,34,0.08)', color:'#E87722', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, cursor:'pointer', letterSpacing:'0.08em', opacity:suggLoading?0.5:1 }}>
                <Sparkles size={11}/> {suggLoading?'Thinking…':'Continue'}
              </button>

              {/* AI: extract mini-nuggets */}
              <button onClick={extractMiniNuggets} disabled={aiLoading} title="Ctrl+Enter — Extract action items with AI"
                style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:6, border:'1px solid rgba(96,165,250,0.3)', background:'rgba(96,165,250,0.08)', color:'#60A5FA', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, cursor:'pointer', letterSpacing:'0.08em', opacity:aiLoading?0.5:1 }}>
                <Zap size={11}/> {aiLoading?'Extracting…':'Extract Nuggets'}
              </button>

              {/* AI: spell-check */}
              <button onClick={checkSpelling} disabled={spellLoading}
                style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:6, border:'1px solid rgba(52,211,153,0.3)', background:'rgba(52,211,153,0.07)', color:'#34D399', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, cursor:'pointer', letterSpacing:'0.08em', opacity:spellLoading?0.5:1 }}>
                <CheckCircle size={11}/> {spellLoading?'Checking…':'Check Spelling'}
              </button>
              {spellResult?.corrected && (
                <button onClick={fixAll}
                  style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:6, border:'1px solid rgba(52,211,153,0.6)', background:'rgba(52,211,153,0.18)', color:'#34D399', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:800, cursor:'pointer', letterSpacing:'0.08em', animation:'pulse 1.5s ease infinite' }}
                  title={spellResult.summary}>
                  ✓ Fix All
                </button>
              )}

              {/* Send to Messenger */}
              <button onClick={()=>openMsgModal(content)}
                style={{ display:'flex', alignItems:'center', gap:5, padding:'6px 12px', borderRadius:6, border:'1px solid rgba(255,255,255,0.1)', background:'transparent', color:'rgba(255,255,255,0.4)', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, cursor:'pointer', letterSpacing:'0.08em' }}>
                <MessageSquare size={11}/> Send
              </button>

              <div style={{ flex:1 }}/>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:saveStatus==='saved'?'rgba(52,211,153,0.6)':saveStatus==='saving'?'#E87722':'rgba(255,255,255,0.25)', letterSpacing:'0.1em', textTransform:'uppercase' }}>
                {saveStatus==='saved'?'✓ Saved':saveStatus==='saving'?'Saving…':'● Unsaved'}
              </span>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.2)' }}>{content.length} chars</span>
            </div>
          </>
        )}
      </div>

      {/* ── RIGHT: Mini-Nuggets panel ───────────────────────────── */}
      <div style={{ width:286, flexShrink:0, display:'flex', flexDirection:'column', borderLeft:`1px solid ${C.border}`, background:'rgba(255,255,255,0.02)' }}>
        <div style={{ padding:'12px 14px 10px', borderBottom:`1px solid ${C.border}` }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:2 }}>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:11, letterSpacing:'0.15em', textTransform:'uppercase', color:'#E87722' }}>Mini-Nuggets</span>
            <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.3)' }}>{activeMNs.length} active</span>
          </div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.3)', lineHeight:1.5 }}>
            AI-extracted actions — promote, send, or copy
          </div>
        </div>

        <div style={{ flex:1, overflowY:'auto', padding:'10px 10px' }}>
          {!activeNote && (
            <div style={{ padding:20, textAlign:'center', color:'rgba(255,255,255,0.2)', fontFamily:"'Montserrat',sans-serif", fontSize:11 }}>Select a note to see mini-nuggets</div>
          )}
          {activeNote && pendingMNs.length === 0 && doneMNs.length === 0 && (
            <div style={{ padding:'20px 10px', textAlign:'center', color:'rgba(255,255,255,0.2)', fontFamily:"'Montserrat',sans-serif", fontSize:11, lineHeight:1.6 }}>
              No mini-nuggets yet.<br/>
              Click <span style={{ color:'#60A5FA' }}>Extract Nuggets</span> or<br/>
              press <span style={{ color:'rgba(255,255,255,0.4)' }}>Ctrl+Enter</span>
            </div>
          )}

          {activeMNs.map(mn => {
            const smeta = MN_STATUS_META[mn.status] || MN_STATUS_META.not_started;
            return (
            <div key={mn.id} style={{ background:'rgba(255,255,255,0.04)', border:`1px solid rgba(255,255,255,0.08)`, borderLeft:`2px solid ${smeta.color}`, borderRadius:8, padding:'10px 10px', marginBottom:8 }}>
              {/* Status badge + cycle button */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
                <button onClick={()=>updateMN(mn.id, smeta.next)}
                  style={{ display:'flex', alignItems:'center', gap:4, padding:'2px 8px', borderRadius:20, border:`1px solid ${smeta.color}`, background:smeta.bg, color:smeta.color, fontFamily:"'Montserrat',sans-serif", fontSize:8, fontWeight:700, cursor:'pointer', letterSpacing:'0.08em', textTransform:'uppercase' }}>
                  {mn.status==='in_progress'?'⏳':mn.status==='completed'?'✓':'○'} {smeta.label}
                </button>
              </div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.85)', lineHeight:1.5, marginBottom:8 }}>{mn.text}</div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:4 }}>
                <button onClick={()=>promoteMN(mn)} title="Create a Nugget task from this"
                  style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 0', borderRadius:5, border:'1px solid rgba(232,119,34,0.35)', background:'rgba(232,119,34,0.08)', color:'#E87722', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, cursor:'pointer', justifyContent:'center', letterSpacing:'0.06em' }}>
                  <Layers size={9}/> Nugget
                </button>
                <button onClick={()=>sendMNToLab(mn)} title="Send to Strategy Lab as prompt"
                  style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 0', borderRadius:5, border:'1px solid rgba(96,165,250,0.35)', background:'rgba(96,165,250,0.08)', color:'#60A5FA', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, cursor:'pointer', justifyContent:'center', letterSpacing:'0.06em' }}>
                  <Brain size={9}/> Strategy Lab
                </button>
                <button onClick={()=>openMsgModal(mn.text)} title="Send to a Messenger channel"
                  style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 0', borderRadius:5, border:'1px solid rgba(255,255,255,0.12)', background:'transparent', color:'rgba(255,255,255,0.4)', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, cursor:'pointer', justifyContent:'center', letterSpacing:'0.06em' }}>
                  <MessageSquare size={9}/> Messenger
                </button>
                <button onClick={()=>copyText(mn.text)} title="Copy to clipboard"
                  style={{ display:'flex', alignItems:'center', gap:4, padding:'4px 0', borderRadius:5, border:'1px solid rgba(255,255,255,0.12)', background:'transparent', color:'rgba(255,255,255,0.4)', fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, cursor:'pointer', justifyContent:'center', letterSpacing:'0.06em' }}>
                  <Copy size={9}/> Copy
                </button>
              </div>
              <button onClick={()=>updateMN(mn.id,'dismissed')}
                style={{ marginTop:5, width:'100%', background:'none', border:'none', color:'rgba(255,255,255,0.2)', fontSize:9, fontFamily:"'Montserrat',sans-serif", cursor:'pointer', textAlign:'center' }}>
                dismiss
              </button>
            </div>
            );
          })}

          {doneMNs.length > 0 && (
            <div style={{ marginTop:10 }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, letterSpacing:'0.12em', textTransform:'uppercase', color:'rgba(255,255,255,0.2)', marginBottom:6 }}>Done / Dismissed</div>
              {doneMNs.map(mn => {
                const doneColor = mn.status==='promoted'?'#E87722':mn.status==='sent'?'#60A5FA':mn.status==='completed'?'#34D399':'#6B7280';
                const doneLabel = mn.status==='promoted'?'Promoted':mn.status==='sent'?'Sent':mn.status==='completed'?'Completed':'Dismissed';
                return (
                  <div key={mn.id} style={{ padding:'7px 10px', marginBottom:5, borderRadius:6, background:'transparent', border:'1px solid rgba(255,255,255,0.05)', opacity:0.55 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                      <CheckCircle size={10} style={{ color:doneColor, flexShrink:0 }}/>
                      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:'rgba(255,255,255,0.45)', textDecoration:'line-through', flex:1 }}>{mn.text}</span>
                      <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:8, color:doneColor, flexShrink:0, letterSpacing:'0.05em' }}>{doneLabel}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── DELETE CONFIRMATION MODAL ───────────────────────────── */}
      {deleteConfirm && (() => {
        const noteToDelete = notes.find(n => n.id === deleteConfirm);
        const hasMiniNuggets = (noteToDelete?.miniNuggets || []).some(m => m.status === 'promoted' || m.status === 'sent');
        return (
          <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300 }} onClick={e=>{ if(e.target===e.currentTarget) setDeleteConfirm(null); }}>
            <div style={{ background:'#0D1B2A', border:'1px solid rgba(239,68,68,0.3)', borderRadius:12, padding:28, width:380, display:'flex', flexDirection:'column', gap:16 }}>
              <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                <div style={{ width:36, height:36, borderRadius:'50%', background:'rgba(239,68,68,0.15)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                  <Trash2 size={16} color="#EF4444"/>
                </div>
                <div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:14, color:'#fff' }}>Delete Note?</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>"{noteToDelete?.title}"</div>
                </div>
              </div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:'rgba(255,255,255,0.65)', lineHeight:1.6 }}>
                This note will be permanently deleted. This action cannot be undone.
              </div>
              {hasMiniNuggets && (
                <div style={{ background:'rgba(52,211,153,0.08)', border:'1px solid rgba(52,211,153,0.25)', borderRadius:8, padding:'10px 12px' }}>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'#34D399', lineHeight:1.6 }}>
                    Mini-nuggets promoted to the Nugget Board or Strategy Lab are <strong>preserved</strong> and will not be deleted.
                  </div>
                </div>
              )}
              <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
                <button onClick={()=>setDeleteConfirm(null)}
                  style={{ ...BtnOutline(C), padding:'8px 18px' }}>
                  Cancel
                </button>
                <button onClick={()=>confirmDeleteNote(deleteConfirm)}
                  style={{ display:'flex', alignItems:'center', gap:6, padding:'8px 18px', borderRadius:6, border:'1px solid rgba(239,68,68,0.5)', background:'rgba(239,68,68,0.15)', color:'#EF4444', fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, cursor:'pointer', letterSpacing:'0.06em' }}>
                  <Trash2 size={12}/> Delete Note
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── SEND TO MESSENGER MODAL ─────────────────────────────── */}
      {showMsgModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.6)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:200 }} onClick={e=>{ if(e.target===e.currentTarget)setShowMsgModal(false); }}>
          <div style={{ background:'#0D1B2A', border:'1px solid rgba(255,255,255,0.1)', borderRadius:12, padding:24, width:400, maxHeight:'80vh', display:'flex', flexDirection:'column', gap:14 }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:14, color:'#fff' }}>Send to Messenger</span>
              <button onClick={()=>setShowMsgModal(false)} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', cursor:'pointer' }}><X size={16}/></button>
            </div>

            {/* Preview */}
            <div style={{ background:'rgba(255,255,255,0.04)', borderRadius:8, padding:'10px 12px', maxHeight:100, overflowY:'auto' }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.6)', lineHeight:1.5, whiteSpace:'pre-wrap' }}>{msgContent.slice(0,300)}{msgContent.length>300?'…':''}</div>
            </div>

            {/* Channels */}
            <div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, letterSpacing:'0.1em', textTransform:'uppercase', color:'rgba(255,255,255,0.4)', marginBottom:8 }}>Choose channel or DM</div>
              <div style={{ display:'flex', flexDirection:'column', gap:5, maxHeight:240, overflowY:'auto' }}>
                {channels.length === 0 && <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,255,255,0.3)', padding:'10px 0' }}>Loading channels…</div>}
                {channels.filter(ch=>ch.type==='public').map(ch => (
                  <div key={ch.id} onClick={()=>setSelectedCh(ch)}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderRadius:7, border:`1px solid ${selectedCh?.id===ch.id?'#E87722':'rgba(255,255,255,0.08)'}`, background:selectedCh?.id===ch.id?'rgba(232,119,34,0.08)':'rgba(255,255,255,0.03)', cursor:'pointer' }}>
                    <MessageSquare size={11} style={{ color:'rgba(255,255,255,0.4)' }}/>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:'#fff' }}>#{ch.name}</span>
                  </div>
                ))}
                {users.filter(u=>u.id!==currentUser.id).map(u => (
                  <div key={u.id} onClick={()=>setSelectedCh({ ...u, _isDM:true, _userId:u.id, id:`dm-${u.id}` })}
                    style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 12px', borderRadius:7, border:`1px solid ${selectedCh?.id===`dm-${u.id}`?'#60A5FA':'rgba(255,255,255,0.08)'}`, background:selectedCh?.id===`dm-${u.id}`?'rgba(96,165,250,0.08)':'rgba(255,255,255,0.03)', cursor:'pointer' }}>
                    <div style={{ width:20, height:20, borderRadius:'50%', background:u.accent||'#E87722', display:'flex', alignItems:'center', justifyContent:'center', fontSize:8, fontWeight:800, color:'#fff', flexShrink:0 }}>{u.initials}</div>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:'#fff' }}>{u.name}</span>
                    <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:'rgba(255,255,255,0.3)', marginLeft:'auto' }}>DM</span>
                  </div>
                ))}
              </div>
            </div>

            <button onClick={sendToMessenger} disabled={!selectedCh||sendLoading}
              style={{ ...BtnPrimary, justifyContent:'center', opacity:(!selectedCh||sendLoading)?0.4:1 }}>
              <Send size={12}/> {sendLoading?'Sending…':'Send Message'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// SETTINGS
// ═══════════════════════════════════════════════════════════════════
function AppSettings({ C, dark, setDark, currentUser, pushEnabled, pushLoading, onEnablePush, onDisablePush }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installable,    setInstallable]    = useState(false);
  const [installed,      setInstalled]      = useState(false);

  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setDeferredPrompt(e); setInstallable(true); };
    window.addEventListener('beforeinstallprompt', handler);
    // Check if already running as PWA
    if (window.matchMedia('(display-mode: standalone)').matches) setInstalled(true);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') { setInstalled(true); setInstallable(false); }
    setDeferredPrompt(null);
  };

  const pushSupported = 'Notification' in window && 'PushManager' in window && 'serviceWorker' in navigator;

  return (
    <div style={{ animation:"fadeUp 0.5s ease" }}>
      <SH C={C} title="Settings" sub="Theme · Your Google SSO session · Mobile app · Tech stack" />
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
        <div style={Card(C)}>
          <Eyebrow>Your Session</Eyebrow>
          {[["Name",currentUser.name],["Email",currentUser.email],["Role",(ROLE_META[currentUser.role] || ROLE_META.viewer).label],["Auth","Google OAuth 2.0"],["Token","Google ID Token (JWT)"]].map(([k,v]) => (
            <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"8px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.4)", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.1em" }}>{k}</span>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:k==="Role"?(ROLE_META[currentUser.role] || ROLE_META.viewer).color:"#fff", fontWeight:600 }}>{v}</span>
            </div>
          ))}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div style={Card(C)}>
            <Eyebrow>Display</Eyebrow>
            <div style={{ display:"flex", gap:8, marginBottom:4 }}>
              {["dark","light"].map(t => (
                <div key={t} onClick={() => setDark(t==="dark")}
                  style={{ flex:1, background:dark===(t==="dark")?"rgba(232,119,34,0.12)":"rgba(255,255,255,0.04)", border:`2px solid ${dark===(t==="dark")?"#E87722":"rgba(255,255,255,0.08)"}`, borderRadius:6, padding:14, cursor:"pointer", textAlign:"center", transition:"all 0.2s ease" }}>
                  <div style={{ fontSize:22 }}>{t==="dark"?"🌙":"☀️"}</div>
                  <div style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:10, letterSpacing:"0.2em", textTransform:"uppercase", color:dark===(t==="dark")?"#E87722":"rgba(255,255,255,0.4)", marginTop:6 }}>{t}</div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Mobile App / PWA Card ── */}
          <div style={Card(C)}>
            <Eyebrow>Mobile App (PWA)</Eyebrow>

            {/* Install to home screen */}
            <div style={{ marginBottom:12 }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.5)", marginBottom:6, lineHeight:1.5 }}>
                {installed
                  ? "✓ Running as installed app"
                  : "Add to your Android home screen for a native-app experience — works offline, no app store needed."}
              </div>
              {!installed && (
                installable
                  ? <button onClick={handleInstall} style={{ ...BtnPrimary, width:"100%", justifyContent:"center" }}>
                      <Smartphone size={13} /> Add to Home Screen
                    </button>
                  : <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.3)", background:"rgba(255,255,255,0.04)", borderRadius:6, padding:"8px 10px" }}>
                      On Android Chrome: tap the <strong style={{ color:"rgba(255,255,255,0.5)" }}>⋮ menu → Add to Home screen</strong>
                    </div>
              )}
            </div>

            {/* Push notifications */}
            <div style={{ borderTop:"1px solid rgba(255,255,255,0.06)", paddingTop:10 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:6 }}>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em", color:"rgba(255,255,255,0.5)" }}>
                  Push Notifications
                </span>
                <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color: pushEnabled ? "#34D399" : "rgba(255,255,255,0.3)" }}>
                  {pushEnabled ? "ON" : "OFF"}
                </span>
              </div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.4)", marginBottom:8, lineHeight:1.5 }}>
                Get notified of inbound Launchpad calls even when the app is in the background.
              </div>
              {!pushSupported
                ? <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.25)" }}>Not supported in this browser</div>
                : pushEnabled
                  ? <button onClick={onDisablePush} disabled={pushLoading}
                      style={{ ...BtnOutline(C), width:"100%", justifyContent:"center", opacity:pushLoading?0.5:1 }}>
                      {pushLoading ? "..." : "Disable Notifications"}
                    </button>
                  : <button onClick={onEnablePush} disabled={pushLoading}
                      style={{ ...BtnPrimary, width:"100%", justifyContent:"center", opacity:pushLoading?0.5:1 }}>
                      {pushLoading ? "..." : "Enable Notifications"}
                    </button>
              }
            </div>
          </div>
        </div>
      </div>

      {/* Tech Stack */}
      <div style={{ ...Card(C), marginTop:14 }}>
        <Eyebrow>Tech Stack</Eyebrow>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"0 40px" }}>
          {[["Frontend","React 19 + Vite"],["Auth","Keycloak (OIDC)"],["Database","AWS RDS Postgres"],["Realtime","SSE + LISTEN/NOTIFY"],["AI","Claude Fable 5 · Sonnet 4.6 · Haiku 4.5"],["Push","Web Push API + VAPID"],["Map","MapLibre GL JS"],["PWA","Service Worker + Manifest"]].map(([k,v]) => (
            <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"5px 0", borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"rgba(255,255,255,0.4)" }}>{k}</span>
              <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:"#E87722", fontWeight:600 }}>{v}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Cyber Security — threat seeds + dashboard
// ─────────────────────────────────────────────────────────────────────────────

const THREAT_SEEDS = {
  messenger: [
    { severity:'critical', category:'Phishing',     title:'Credential harvesting link shared in #general',            desc:'User shared a URL matching known phishing domain. 4 team members clicked before detection. OAuth tokens may be compromised.', assets:['#general','auth-service','user-accounts'] },
    { severity:'high',     category:'Auth Anomaly', title:'Login from unrecognized geolocation — RO/Bucharest',       desc:'Successful auth from IP not previously associated with this account. Country: Romania. Session active for 14 min.', assets:['auth-service','session-store'] },
    { severity:'medium',   category:'Data Exfil',   title:'47MB archive sent via Direct Message',                     desc:'Large zip attachment transmitted in DM thread. Contents unverified. Recipient is external contact.', assets:['file-storage','dm-channel'] },
    { severity:'low',      category:'Config',       title:'Message retention policy not configured',                  desc:'No data retention rules set. Messages and attachments accumulate indefinitely. Recommend 90-day policy.', assets:['storage-policy'] },
  ],
  geo_map: [
    { severity:'critical', category:'API Exposure', title:'Mapbox API key detected in public JS bundle',              desc:'Tile API token found in unminified client bundle accessible without auth. Rotate key immediately. Potential abuse window: 6+ days.', assets:['mapbox-api','tile-server','cdn'] },
    { severity:'high',     category:'Location Data',title:'Vessel position data queried outside authorized boundary', desc:'AIS query returned coordinates 340nm outside contractual data scope. Possible misconfiguration or unauthorized data pull.', assets:['ais-feed','geo-api','rls-policy'] },
    { severity:'medium',   category:'Rate Anomaly', title:'Tile server request spike — 340% above baseline',          desc:'Single IP origin driving abnormal tile request volume. Possible scraping, DoS probe, or misconfigured client.', assets:['tile-cache','cdn-edge'] },
    { severity:'low',      category:'Stale Creds',  title:'GeoJSON feed using deprecated auth header',               desc:'Weather data feed still uses legacy API key auth. Migrate to bearer token before deprecation on 2026-06-01.', assets:['weather-feed','noaa-api'] },
  ],
  deploy_ops: [
    { severity:'critical', category:'Secret Leak',  title:'Service role key committed to git — 3 commits ago',       desc:'Service-role API key found in commit a74614e. Key must be rotated immediately. Check for replay attacks against admin endpoints.', assets:['git-repo','db-admin','service-role-key'] },
    { severity:'high',     category:'CVE',          title:'CVE-2024-4577 — CVSS 9.8 in dependency tree',            desc:'Critical injection vulnerability in indirect dependency. Production build affected. Patch available — update package-lock.json.', assets:['package-lock','node-modules','prod-build'] },
    { severity:'high',     category:'CI/CD',        title:'GitHub Actions workflow has write access to all branches', desc:'Deployment workflow granted excess repository permissions. Single compromise could push to main. Apply least-privilege scoping.', assets:['github-actions','ci-pipeline','main-branch'] },
    { severity:'medium',   category:'Config Drift', title:'CORS wildcard origin active on production API',           desc:'Access-Control-Allow-Origin: * detected on /api/v2 in production. Staging config promoted without review.', assets:['api-gateway','cors-policy'] },
    { severity:'low',      category:'Hygiene',      title:'12 npm packages with low-severity advisories',            desc:'Non-critical advisories in dev dependencies. No production exposure identified. Batch update recommended.', assets:['node-modules'] },
  ],
  help_desk: [
    { severity:'high',     category:'Credential Stuffing', title:'17 password reset attempts across 9 accounts in 12 min', desc:'Distributed reset requests from 6 IPs targeting valid usernames. Consistent with automated credential stuffing toolkit.', assets:['auth-service','email-gateway','user-accounts'] },
    { severity:'high',     category:'XSS Attempt',  title:'Script tag injected in ticket body — blocked',            desc:'Stored XSS attempt in ticket #HLP-441. Input sanitization blocked execution. Review sanitizer coverage for edge cases.', assets:['helpdesk-ui','ticket-db','sanitizer'] },
    { severity:'medium',   category:'Privilege Req',title:'Org member requested super_admin role via ticket',        desc:'Support ticket #HLP-449 claims urgent admin access needed. No change management record found. Pending manual verification.', assets:['role-service','admin-panel'] },
    { severity:'low',      category:'Recon',        title:'Help desk form probed for user enumeration',              desc:'Sequential user ID requests on password reset form. Rate limiting active. 23 probes logged from single IP.', assets:['reset-form','rate-limiter'] },
  ],
  crm: [
    { severity:'high',     category:'Data Access',  title:'Bulk CRM export — 2,400 customer records',               desc:'User exported full contact database. Export volume is 18x above 90-day average for this account. Verify business justification.', assets:['crm-db','export-service','customer-pii'] },
    { severity:'high',     category:'PII Exposure', title:'Customer emails exposed in URL query params',             desc:'CRM filter route passing plaintext email as GET param. Cached in browser history, proxy logs, and server access logs.', assets:['crm-api','browser-cache','proxy-logs'] },
    { severity:'medium',   category:'Access Pattern',title:'CRM accessed at 2:14 AM from unrecognized mobile device',desc:'Out-of-hours access with new device fingerprint. IP geolocates to VPN exit node. May be legitimate remote work.', assets:['crm-mobile','session-store'] },
    { severity:'low',      category:'Config',       title:'CRM API keys not rotated in 180+ days',                  desc:'API tokens for CRM integrations last rotated 184 days ago. Best practice is 90-day rotation.', assets:['api-keys','integrations'] },
  ],
};

function seedThreats(orgId, features = {}) {
  const threats = [];
  let idx = 0;
  ['messenger','geo_map','deploy_ops','help_desk','crm'].forEach(mod => {
    if (!features[mod]) return;
    (THREAT_SEEDS[mod] || []).forEach((seed, i) => {
      const daysAgo  = (idx * 17 + i * 11) % 12;
      const hoursAgo = (idx * 7  + i * 5)  % 24;
      const det = new Date(Date.now() - (daysAgo * 86400000 + hoursAgo * 3600000));
      threats.push({
        id: `${orgId}-${mod}-${i}`,
        orgId, module: mod,
        severity:   seed.severity,
        category:   seed.category,
        title:      seed.title,
        desc:       seed.desc,
        assets:     seed.assets || [],
        status:     daysAgo > 8 ? 'mitigated' : daysAgo > 4 ? 'investigating' : 'open',
        detectedAt: det.toISOString(),
        updatedAt:  new Date(det.getTime() + idx * 3600000).toISOString(),
      });
      idx++;
    });
  });
  return threats;
}

function CyberDashboard({ C, currentUser, orgSettings, showToast, users, threats, setThreats }) {
  const features = orgSettings?.features || {};

  const [filter,        setFilter]        = useState('all');
  const [sevFilter,     setSevFilter]     = useState('all');
  const [selected,      setSelected]      = useState(null);
  const [summaryLoad,   setSummaryLoad]   = useState(false);
  const [summary,       setSummary]       = useState('');
  const [emailModal,    setEmailModal]    = useState(false);
  const [emailTarget,   setEmailTarget]   = useState('');
  const [emailLoading,  setEmailLoading]  = useState(false);

  useEffect(() => {
    if (!threats.length && currentUser?.orgId) {
      setThreats(seedThreats(currentUser.orgId, features));
    }
    // Only seed on org change; `threats`/`features` intentionally excluded to
    // avoid re-seeding after the user mitigates threats or toggles features.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.orgId]);

  const SEV_ORDER = ['critical','high','medium','low','info'];
  const SEV_META  = {
    critical: { color:'#FF3B30', bg:'rgba(255,59,48,0.14)',   label:'Critical' },
    high:     { color:'#FF9500', bg:'rgba(255,149,0,0.14)',   label:'High' },
    medium:   { color:'#FFD60A', bg:'rgba(255,214,10,0.11)',  label:'Medium' },
    low:      { color:'#34D399', bg:'rgba(52,211,153,0.11)',  label:'Low' },
    info:     { color:'#60A5FA', bg:'rgba(96,165,250,0.1)',   label:'Info' },
  };
  const STATUS_META = {
    open:          { color:'#FF3B30', label:'Open' },
    investigating: { color:'#FF9500', label:'Investigating' },
    mitigated:     { color:'#34D399', label:'Mitigated' },
  };
  const MOD_LABEL = { messenger:'Messenger', geo_map:'Geo Map', deploy_ops:'Deploy Ops', help_desk:'Help Desk', crm:'CRM' };

  const updateStatus = (id, status) => {
    setThreats(prev => prev.map(t => t.id === id ? { ...t, status, updatedAt: new Date().toISOString() } : t));
    setSelected(prev => prev?.id === id ? { ...prev, status } : prev);
  };

  const open       = threats.filter(t => t.status !== 'mitigated');
  const riskScore  = Math.min(100, open.reduce((a, t) => a + ({ critical:40, high:20, medium:8, low:3, info:1 }[t.severity] || 0), 0));
  const riskColor  = riskScore >= 70 ? '#FF3B30' : riskScore >= 40 ? '#FF9500' : riskScore >= 15 ? '#FFD60A' : '#34D399';
  const riskLabel  = riskScore >= 70 ? 'CRITICAL' : riskScore >= 40 ? 'HIGH' : riskScore >= 15 ? 'ELEVATED' : 'LOW';
  const sevCounts  = SEV_ORDER.reduce((a, s) => ({ ...a, [s]: open.filter(t => t.severity === s).length }), {});

  const filtered = threats
    .filter(t => filter === 'all' || t.status === filter)
    .filter(t => sevFilter === 'all' || t.severity === sevFilter)
    .sort((a, b) => SEV_ORDER.indexOf(a.severity) - SEV_ORDER.indexOf(b.severity) || new Date(b.detectedAt) - new Date(a.detectedAt));

  const generateSummary = async () => {
    setSummaryLoad(true);
    try {
      const lines = open.map(t => `[${t.severity.toUpperCase()}] ${t.category}: ${t.title} (${MOD_LABEL[t.module]||t.module}, ${t.status})`).join('\n');
      const res = await callAI({
        feature: 'cyber_assessment', user: currentUser,
        systemPrompt: `You are a cybersecurity analyst for 40Geo's AMP platform. Write a concise executive threat summary (4-6 sentences, prose) covering: overall risk posture, the most critical items requiring immediate action, and recommended next steps. Be specific about threat names and modules affected. Do not use bullet points.`,
        messages: [{ role:'user', content:`Active threats:\n\n${lines}\n\nRisk score: ${riskScore}/100 (${riskLabel})` }],
      });
      setSummary(res?.content?.[0]?.text || '');
    } catch (e) { showToast?.('Summary failed: ' + e.message); }
    setSummaryLoad(false);
  };

  const sendReport = async () => {
    if (!emailTarget) { showToast?.('Select a recipient'); return; }
    const rec = users.find(u => u.id === emailTarget);
    if (!rec?.email) { showToast?.('Recipient has no email'); return; }
    setEmailLoading(true);
    const sevRows = SEV_ORDER.filter(s => sevCounts[s] > 0).map(s =>
      `<tr><td style="padding:7px 12px;color:${SEV_META[s].color};font-weight:700;font-size:11px;text-transform:uppercase">${s}</td><td style="padding:7px 12px;font-size:22px;font-weight:800;color:${SEV_META[s].color}">${sevCounts[s]}</td></tr>`).join('');
    const tRows = open.slice(0,20).map(t =>
      `<tr><td style="padding:6px 12px;font-size:11px;color:${SEV_META[t.severity]?.color};font-weight:700;text-transform:uppercase">${t.severity}</td><td style="padding:6px 12px;font-size:12px;color:#fff">${t.title}</td><td style="padding:6px 12px;font-size:11px;color:rgba(255,255,255,0.5)">${MOD_LABEL[t.module]||t.module}</td><td style="padding:6px 12px;font-size:11px;color:${STATUS_META[t.status]?.color};font-weight:700;text-transform:uppercase">${t.status}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>40Geo Cyber Report</title><style>*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Segoe UI',Arial,sans-serif;background:#0d1b2a;color:#e0e0e0;padding:0}.page{max-width:900px;margin:0 auto;padding:40px 48px}.header{border-bottom:3px solid ${riskColor};padding-bottom:20px;margin-bottom:32px;display:flex;justify-content:space-between;align-items:flex-end}.logo{font-size:26px;font-weight:800;color:#E87722}h2{font-size:11px;font-weight:700;letter-spacing:.15em;color:rgba(255,255,255,.35);text-transform:uppercase;margin-bottom:12px;margin-top:28px}table{width:100%;border-collapse:collapse;background:rgba(255,255,255,.03);border:1px solid rgba(255,255,255,.08);border-radius:8px;overflow:hidden}th{background:rgba(255,255,255,.06);padding:8px 12px;text-align:left;font-size:10px;font-weight:700;letter-spacing:.1em;color:rgba(255,255,255,.3);text-transform:uppercase}tr:nth-child(even){background:rgba(255,255,255,.02)}.risk{background:rgba(255,255,255,.04);border:2px solid ${riskColor};border-radius:12px;padding:20px 28px;margin-bottom:8px;display:flex;align-items:center;gap:28px}.rscore{font-size:52px;font-weight:900;color:${riskColor};line-height:1}.rlabel{font-size:13px;font-weight:700;color:${riskColor};letter-spacing:.15em;text-transform:uppercase}.sumbox{background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:10px;padding:20px 24px;margin-top:8px;font-size:14px;line-height:1.75;color:#ddd}.footer{margin-top:40px;padding-top:16px;border-top:1px solid rgba(255,255,255,.08);font-size:10px;color:rgba(255,255,255,.2);display:flex;justify-content:space-between}</style></head><body><div class="page"><div class="header"><div><div class="logo">40Geo <span style="color:#fff;font-weight:400">Nuggets</span></div><div style="font-size:12px;color:rgba(255,255,255,.35);margin-top:4px">Cyber Security Threat Report</div></div><div style="background:rgba(255,255,255,.08);border:1px solid rgba(255,255,255,.15);border-radius:6px;padding:6px 14px;font-size:13px;color:rgba(255,255,255,.7);font-weight:600">${new Date().toISOString().slice(0,10)}</div></div><div class="risk"><div class="rscore">${riskScore}</div><div><div class="rlabel">${riskLabel} RISK</div><div style="font-size:12px;color:rgba(255,255,255,.4);margin-top:4px">${open.length} active threat${open.length!==1?'s':''} · score out of 100</div></div></div>${summary?`<h2>Executive Summary</h2><div class="sumbox">${summary.replace(/\n/g,'<br>')}</div>`:''}<h2>Severity Breakdown</h2><table><thead><tr><th>Severity</th><th>Open</th></tr></thead><tbody>${sevRows}</tbody></table><h2>Active Threats</h2><table><thead><tr><th>Sev</th><th>Threat</th><th>Module</th><th>Status</th></tr></thead><tbody>${tRows}</tbody></table><div class="footer"><span>40Geo Nuggets · Cyber Security Module</span><span>${new Date().toLocaleString('en-US',{timeZone:'America/Chicago'})} CT</span></div></div></body></html>`;
    try {
      const result = await dbSendEmail({ to: rec.email, subject: `40Geo Cyber Security Report — ${new Date().toISOString().slice(0,10)}`, html, orgId: currentUser.orgId });
      if (result?.success) {
        showToast?.(`Report sent to ${rec.name || rec.email}`);
        setEmailModal(false); setEmailTarget('');
      } else {
        showToast?.('Email failed: ' + (result?.error || 'unknown error'));
      }
    } catch (e) { showToast?.('Email failed: ' + e.message); }
    setEmailLoading(false);
  };

  const DC  = { background: C.cardBg, borderRadius: 12, padding: 16, border: `1px solid ${C.border}` };
  const fmt = (iso) => { const diff = Date.now() - new Date(iso); return diff < 3600000 ? `${Math.floor(diff/60000)}m ago` : diff < 86400000 ? `${Math.floor(diff/3600000)}h ago` : `${Math.floor(diff/86400000)}d ago`; };

  return (
    <div style={{ padding: 20, maxWidth: 1400, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <h2 style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:800, fontSize:22, color:C.text, margin:0 }}>Cyber Security</h2>
          <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:C.subtext, margin:'4px 0 0' }}>
            Real-time threat monitoring · {Object.keys(features).filter(k => features[k] && THREAT_SEEDS[k]).length} modules tracked
          </p>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button onClick={() => { setThreats(seedThreats(currentUser.orgId, features)); setSummary(''); showToast?.('Threat feed refreshed'); }}
            style={{ background:'rgba(255,255,255,0.06)', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 16px', color:C.subtext, fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700, cursor:'pointer' }}>
            ↺ Refresh
          </button>
          <button onClick={() => setEmailModal(true)}
            style={{ background:'rgba(255,255,255,0.06)', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 16px', color:C.subtext, fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700, cursor:'pointer' }}>
            ✉ Send Report
          </button>
        </div>
      </div>

      {/* Risk score + severity stats */}
      <div style={{ display:'grid', gridTemplateColumns:'220px 1fr', gap:12 }}>
        <div style={{ ...DC, background:`linear-gradient(135deg,${riskColor}18,${riskColor}06)`, border:`1px solid ${riskColor}44`, textAlign:'center', padding:'22px 16px' }}>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, letterSpacing:'0.15em', color:riskColor, textTransform:'uppercase', marginBottom:8 }}>Risk Score</div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:58, fontWeight:900, color:riskColor, lineHeight:1 }}>{riskScore}</div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color:riskColor, letterSpacing:'0.2em', textTransform:'uppercase', marginTop:4 }}>{riskLabel}</div>
          <div style={{ marginTop:12, height:6, borderRadius:3, background:'rgba(255,255,255,0.08)', overflow:'hidden' }}>
            <div style={{ height:'100%', width:`${riskScore}%`, background:riskColor, borderRadius:3, transition:'width 1s ease' }} />
          </div>
          <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:C.subtext, marginTop:6 }}>{open.length} active · {threats.filter(t=>t.status==='mitigated').length} mitigated</div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10 }}>
          {['critical','high','medium','low'].map(s => (
            <div key={s} onClick={() => setSevFilter(sevFilter === s ? 'all' : s)}
              style={{ ...DC, textAlign:'center', cursor:'pointer', border:`1px solid ${sevFilter===s ? SEV_META[s].color : C.border}`, background:sevFilter===s ? SEV_META[s].bg : 'rgba(255,255,255,0.03)', transition:'all 0.2s' }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:32, fontWeight:900, color:SEV_META[s].color }}>{sevCounts[s]||0}</div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:SEV_META[s].color, textTransform:'uppercase', letterSpacing:'0.12em', marginTop:2 }}>{SEV_META[s].label}</div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:C.subtext, marginTop:2 }}>open</div>
            </div>
          ))}
        </div>
      </div>

      {/* Module breakdown */}
      <div style={{ display:'flex', gap:10, overflowX:'auto', paddingBottom:2 }}>
        {Object.entries(MOD_LABEL).filter(([k]) => features[k]).map(([mod, label]) => {
          const mt     = threats.filter(t => t.module === mod && t.status !== 'mitigated');
          const topSev = SEV_ORDER.find(s => mt.some(t => t.severity === s));
          const meta   = SEV_META[topSev] || { color:'#34D399', label:'Secure' };
          return (
            <div key={mod} style={{ flexShrink:0, minWidth:120, ...DC, textAlign:'center', cursor:'pointer', borderColor:topSev?`${meta.color}55`:'rgba(52,211,153,0.3)', background:topSev?meta.bg:'rgba(52,211,153,0.05)' }}>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:20, fontWeight:800, color:meta.color }}>{mt.length||'✓'}</div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:C.text, marginTop:2 }}>{label}</div>
              <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:meta.color, textTransform:'uppercase', letterSpacing:'0.1em', marginTop:2 }}>{topSev||'secure'}</div>
            </div>
          );
        })}
      </div>

      {/* AI Assessment */}
      <div style={{ ...DC, background:'linear-gradient(135deg,rgba(255,59,48,0.06),rgba(123,47,255,0.06))', border:'1px solid rgba(255,59,48,0.2)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:13, fontWeight:700, color:C.text }}>✦ AI Threat Assessment</div>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:C.subtext, marginTop:2 }}>Claude analyzes active threats and generates an executive risk summary</div>
          </div>
          <button onClick={generateSummary} disabled={summaryLoad||!open.length}
            style={{ background:summaryLoad?'rgba(255,255,255,0.08)':'rgba(255,59,48,0.15)', border:'1px solid rgba(255,59,48,0.4)', borderRadius:8, padding:'9px 20px', color:'#FF3B30', fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:700, cursor:'pointer', flexShrink:0, opacity:(!open.length||summaryLoad)?0.5:1 }}>
            {summaryLoad ? '✦ Analyzing…' : '✦ Assess Threats'}
          </button>
        </div>
        {summary && (
          <div style={{ marginTop:14, fontFamily:"'Montserrat',sans-serif", fontSize:13, color:C.text, lineHeight:1.75, background:'rgba(0,0,0,0.2)', borderRadius:8, padding:'14px 16px', border:'1px solid rgba(255,59,48,0.15)' }}>
            {summary}
          </div>
        )}
      </div>

      {/* Threat feed */}
      <div style={{ ...DC }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' }}>
          <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, fontWeight:700, color:C.subtext, letterSpacing:'0.12em', textTransform:'uppercase', marginRight:4 }}>Threat Feed</span>
          {['all','open','investigating','mitigated'].map(f => (
            <button key={f} onClick={() => setFilter(f)}
              style={{ background:filter===f?'rgba(232,119,34,0.15)':'rgba(255,255,255,0.05)', border:`1px solid ${filter===f?'rgba(232,119,34,0.5)':C.border}`, borderRadius:6, padding:'4px 12px', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:filter===f?'#E87722':C.subtext, cursor:'pointer', textTransform:'uppercase', letterSpacing:'0.08em' }}>
              {f==='all'?`All (${threats.length})`:`${f} (${threats.filter(t=>t.status===f).length})`}
            </button>
          ))}
          {sevFilter !== 'all' && (
            <button onClick={() => setSevFilter('all')} style={{ background:SEV_META[sevFilter]?.bg, border:`1px solid ${SEV_META[sevFilter]?.color}55`, borderRadius:6, padding:'4px 10px', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:SEV_META[sevFilter]?.color, cursor:'pointer' }}>
              {sevFilter} ✕
            </button>
          )}
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
          {filtered.map(t => {
            const sm  = SEV_META[t.severity]  || SEV_META.info;
            const stm = STATUS_META[t.status] || STATUS_META.open;
            const sel = selected?.id === t.id;
            return (
              <div key={t.id}>
                <div onClick={() => setSelected(sel ? null : t)}
                  style={{ display:'grid', gridTemplateColumns:'88px 1fr 100px 110px 110px 72px', gap:8, alignItems:'center', padding:'9px 12px', borderRadius:sel?'8px 8px 0 0':8, border:`1px solid ${sel?sm.color+'55':'rgba(255,255,255,0.05)'}`, background:sel?sm.bg:'rgba(255,255,255,0.02)', cursor:'pointer', transition:'all 0.15s' }}>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:800, color:sm.color, background:sm.bg, borderRadius:4, padding:'3px 7px', textTransform:'uppercase', letterSpacing:'0.08em', textAlign:'center' }}>{t.severity}</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, fontWeight:600, color:C.text, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.title}</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:C.subtext }}>{MOD_LABEL[t.module]||t.module}</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:C.subtext }}>{t.category}</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, fontWeight:700, color:stm.color, textTransform:'uppercase', letterSpacing:'0.08em' }}>{stm.label}</span>
                  <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:10, color:C.subtext, textAlign:'right' }}>{fmt(t.detectedAt)}</span>
                </div>
                {sel && (
                  <div style={{ padding:'14px 16px', background:'rgba(0,0,0,0.25)', borderRadius:'0 0 8px 8px', border:`1px solid ${sm.color}33`, borderTop:'none', marginBottom:2 }}>
                    <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:C.text, lineHeight:1.75, marginBottom:12 }}>{t.desc}</p>
                    {t.assets?.length > 0 && (
                      <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginBottom:12, alignItems:'center' }}>
                        <span style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, color:C.subtext, fontWeight:700, letterSpacing:'0.1em' }}>AFFECTED:</span>
                        {t.assets.map(a => <span key={a} style={{ fontFamily:"'Montserrat',sans-serif", fontSize:9, background:'rgba(255,255,255,0.08)', borderRadius:4, padding:'2px 7px', color:C.subtext }}>{a}</span>)}
                      </div>
                    )}
                    <div style={{ display:'flex', gap:8 }}>
                      {['open','investigating','mitigated'].map(s => (
                        <button key={s} onClick={() => updateStatus(t.id, s)}
                          style={{ background:t.status===s?STATUS_META[s].color+'22':'rgba(255,255,255,0.05)', border:`1px solid ${t.status===s?STATUS_META[s].color+'66':C.border}`, borderRadius:6, padding:'5px 14px', fontFamily:"'Montserrat',sans-serif", fontSize:10, fontWeight:700, color:t.status===s?STATUS_META[s].color:C.subtext, cursor:'pointer', textTransform:'uppercase', letterSpacing:'0.08em' }}>
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          {!filtered.length && (
            <p style={{ fontFamily:"'Montserrat',sans-serif", fontSize:12, color:'rgba(255,255,255,0.2)', fontStyle:'italic', textAlign:'center', padding:'20px 0' }}>No threats match the current filter</p>
          )}
        </div>
      </div>

      {/* Email modal */}
      {emailModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div style={{ background:C.cardBg, borderRadius:14, padding:28, width:420, border:`1px solid ${C.border}` }}>
            <h3 style={{ fontFamily:"'Montserrat',sans-serif", fontWeight:700, fontSize:16, color:C.text, margin:'0 0 16px' }}>Send Cyber Security Report</h3>
            <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:C.subtext, marginBottom:6, fontWeight:600 }}>RECIPIENT</div>
            <select value={emailTarget} onChange={e=>setEmailTarget(e.target.value)} style={{ width:'100%', background:'rgba(255,255,255,0.06)', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', color:C.text, fontFamily:"'Montserrat',sans-serif", fontSize:13, marginBottom:14, outline:'none' }}>
              <option value="">Select recipient…</option>
              {users.filter(u=>u.email).map(u=><option key={u.id} value={u.id}>{u.name||u.email} ({u.email})</option>)}
            </select>
            {!summary && <div style={{ fontFamily:"'Montserrat',sans-serif", fontSize:11, color:'rgba(255,149,0,0.8)', marginBottom:12 }}>Tip: Run "✦ Assess Threats" first to include an AI summary in the report.</div>}
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={sendReport} disabled={emailLoading||!emailTarget} style={{ flex:1, background:'rgba(255,59,48,0.2)', border:'1px solid rgba(255,59,48,0.5)', borderRadius:8, padding:'10px 0', color:'#FF3B30', fontFamily:"'Montserrat',sans-serif", fontSize:13, fontWeight:700, cursor:'pointer', opacity:(!emailTarget||emailLoading)?0.5:1 }}>
                {emailLoading ? 'Sending…' : 'Send Report'}
              </button>
              <button onClick={() => setEmailModal(false)} style={{ flex:1, background:'rgba(255,255,255,0.06)', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 0', color:C.subtext, fontFamily:"'Montserrat',sans-serif", fontSize:13, cursor:'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DevNotebook — 40Geo super_admin only
// ─────────────────────────────────────────────────────────────────────────────
function DevNotebook({ C, currentUser, users, showToast, nuggets = [], audit = [] }) {
  const DN_KEY    = `nuggets_devnotebook_${currentUser.id}`;
  const todayKey  = () => new Date().toISOString().slice(0, 10);

  const ENTRY_TYPES = [
    { id: 'feature',  label: 'Feature',  color: '#34D399' },
    { id: 'fix',      label: 'Fix',      color: '#FF6B6B' },
    { id: 'change',   label: 'Change',   color: '#FFB830' },
    { id: 'refactor', label: 'Refactor', color: '#7B2FFF' },
    { id: 'note',     label: 'Note',     color: 'rgba(255,255,255,0.5)' },
  ];

  const loadNotebook = () => { try { return JSON.parse(localStorage.getItem(DN_KEY) || '{}'); } catch { return {}; } };

  const [notebook,      setNotebook]      = useState(loadNotebook);
  const [selectedDate,  setSelectedDate]  = useState(todayKey());
  const [newEntry,      setNewEntry]      = useState('');
  const [entryType,     setEntryType]     = useState('feature');
  const [autoLoading,   setAutoLoading]   = useState(false);
  const [summaryLoading,setSummaryLoading]= useState(false);
  const [importText,    setImportText]    = useState('');
  const [importLoading, setImportLoading] = useState(false);
  const [showImport,    setShowImport]    = useState(false);
  const [emailModal,    setEmailModal]    = useState(false);
  const [emailTarget,   setEmailTarget]   = useState('');
  const [emailLoading,  setEmailLoading]  = useState(false);
  const [customMsg,     setCustomMsg]     = useState('');
  const [githubLoading, setGithubLoading] = useState(false);

  const saveNb = (nb) => { setNotebook(nb); localStorage.setItem(DN_KEY, JSON.stringify(nb)); };
  const dayData = notebook[selectedDate] || { entries: [], summary: '' };

  // Kanban activity for selected date
  const today = todayKey();
  const createdToday  = nuggets.filter(n => n.createdAt?.startsWith(today));
  const doneNuggets   = nuggets.filter(n => n.col === 'done' || n.col === 'review');
  const inProgress    = nuggets.filter(n => n.col === 'in-progress');

  const addEntry = (type, text) => {
    if (!text?.trim()) return;
    const entry = { id: Date.now() + Math.random(), type: type || entryType, text: text.trim(), ts: new Date().toISOString() };
    const updated = { ...notebook, [selectedDate]: { ...dayData, entries: [...dayData.entries, entry] } };
    saveNb(updated);
    setNewEntry('');
  };

  const removeEntry = (id) => {
    const updated = { ...notebook, [selectedDate]: { ...dayData, entries: dayData.entries.filter(e => e.id !== id) } };
    saveNb(updated);
  };

  // ── Auto-fill: Claude analyzes today's activity ──────────────────
  const autoFill = async () => {
    setAutoLoading(true);
    try {
      // Gather context: nuggets, audit, strategy lab sessions from today
      const labSessions = (() => {
        try {
          const hist = JSON.parse(localStorage.getItem(`nuggets_strategy_chats_${currentUser.id}`) || '[]');
          return hist.filter(s => s.updatedAt?.startsWith(today)).map(s => `Session: "${s.title}" (${s.category})`).join('\n');
        } catch { return ''; }
      })();

      const context = [
        `DATE: ${selectedDate}`,
        `\nKANBAN — Created today (${createdToday.length}):`,
        createdToday.map(n => `  • [${n.nuggetType}] ${n.title} → col: ${n.col}, priority: ${n.priority}`).join('\n') || '  (none)',
        `\nKANBAN — Done/Review (${doneNuggets.length} total):`,
        doneNuggets.slice(0, 20).map(n => `  • ${n.title}`).join('\n') || '  (none)',
        `\nKANBAN — In Progress (${inProgress.length}):`,
        inProgress.map(n => `  • ${n.title}`).join('\n') || '  (none)',
        `\nSESSION AUDIT LOG (${audit.length} entries):`,
        audit.slice(0, 40).map(a => `  • [${a.type}] ${a.action}`).join('\n') || '  (none)',
        labSessions ? `\nSTRATEGY LAB SESSIONS TODAY:\n${labSessions}` : '',
        `\nEXISTING NOTEBOOK ENTRIES FOR TODAY (${dayData.entries.length}):`,
        dayData.entries.map(e => `  • [${e.type}] ${e.text}`).join('\n') || '  (none)',
      ].join('\n');

      const res = await callAI({
        feature: 'devnotebook',
        user: currentUser,
        systemPrompt: `You are the 40Geo technical scribe. Analyze the development activity provided and generate a structured list of dev notebook entries for today.

Rules:
- Each entry should be a concise technical statement (max 120 chars)
- Categorize accurately: feature (new capability), fix (bug resolved), change (behavior modified), refactor (code quality), note (observation/decision)
- Focus on WHAT was built/changed/fixed, not HOW
- Do NOT duplicate entries already in the notebook
- Return ONLY a valid JSON array. No markdown, no explanation. Example:
[{"type":"feature","text":"Added real-time nugget sync via LISTEN/NOTIFY"},{"type":"fix","text":"Fixed forward message showing Unknown sender name"}]`,
        messages: [{ role: 'user', content: context }],
      });

      const raw = res?.content?.[0]?.text || '[]';
      // Extract JSON from response (Claude sometimes wraps in backticks)
      const jsonMatch = raw.match(/\[[\s\S]*\]/);
      if (!jsonMatch) throw new Error('No JSON array in response');
      const generated = JSON.parse(jsonMatch[0]);
      if (!Array.isArray(generated) || !generated.length) { showToast?.('No new entries generated'); setAutoLoading(false); return; }

      // Merge: skip duplicates based on text similarity
      const existing = dayData.entries.map(e => e.text.toLowerCase());
      const fresh = generated.filter(g => !existing.some(ex => ex.includes(g.text.slice(0, 30).toLowerCase())));
      if (!fresh.length) { showToast?.('All entries already exist in today\'s log'); setAutoLoading(false); return; }

      const newEntries = fresh.map(g => ({ id: Date.now() + Math.random(), type: g.type || 'note', text: g.text, ts: new Date().toISOString(), ai: true }));
      const updated = { ...notebook, [selectedDate]: { ...dayData, entries: [...dayData.entries, ...newEntries] } };
      saveNb(updated);
      showToast?.(`✦ Claude added ${fresh.length} entries to today's log`);
    } catch (e) {
      showToast?.('Auto-fill failed: ' + e.message);
    }
    setAutoLoading(false);
  };

  // ── Import: paste git log / session notes → Claude categorizes ───
  const importNotes = async () => {
    if (!importText.trim()) return;
    setImportLoading(true);
    try {
      const existing = dayData.entries.map(e => e.text.toLowerCase());
      const res = await callAI({
        feature: 'devnotebook',
        user: currentUser,
        systemPrompt: `You are a technical scribe for 40Geo. Parse the pasted text (git commit log, session notes, or any development summary) and convert it into structured dev notebook entries.

Rules:
- Each entry = one concise technical statement (max 120 chars)
- Classify accurately: feature (new capability added), fix (bug resolved), change (behavior/UI modified), refactor (code structure improved), note (decision/observation)
- Expand terse git messages into readable descriptions where needed
- Do NOT duplicate concepts already covered
- Return ONLY a valid JSON array. No markdown fences, no explanation.
Example: [{"type":"feature","text":"Added real-time nugget sync via SSE"},{"type":"fix","text":"Fixed messenger active status not updating on join/leave"}]`,
        messages: [{ role: 'user', content: `Parse this into dev notebook entries:\n\n${importText}` }],
      });
      const raw = res?.content?.[0]?.text || '[]';
      const jsonMatch = raw.match(/\[[\s\S]*\]/);
      if (!jsonMatch) throw new Error('No JSON returned');
      const generated = JSON.parse(jsonMatch[0]);
      if (!Array.isArray(generated) || !generated.length) { showToast?.('No entries extracted'); setImportLoading(false); return; }
      const fresh = generated.filter(g => !existing.some(ex => ex.includes(g.text.slice(0, 30).toLowerCase())));
      if (!fresh.length) { showToast?.('All entries already logged'); setImportLoading(false); return; }
      const newEntries = fresh.map(g => ({ id: Date.now() + Math.random(), type: g.type || 'note', text: g.text, ts: new Date().toISOString(), ai: true }));
      const updated = { ...notebook, [selectedDate]: { ...dayData, entries: [...dayData.entries, ...newEntries] } };
      saveNb(updated);
      showToast?.(`✦ Imported ${fresh.length} entries from pasted text`);
      setImportText(''); setShowImport(false);
    } catch (e) { showToast?.('Import failed: ' + e.message); }
    setImportLoading(false);
  };

  // ── Fetch today's GitHub commits and auto-categorize ─────────────
  const fetchFromGitHub = async () => {
    setGithubLoading(true);
    try {
      const since = new Date(); since.setHours(0, 0, 0, 0);
      const resp = await fetch(
        `https://api.github.com/repos/mlrtexas/nuggets/commits?since=${since.toISOString()}&per_page=50`,
        { headers: { Accept: 'application/vnd.github.v3+json' } }
      );
      if (!resp.ok) throw new Error(`GitHub API returned ${resp.status}`);
      const commits = await resp.json();
      if (!commits.length) { showToast?.('No commits pushed today yet'); setGithubLoading(false); return; }

      const gitLog = commits.reverse().map(c => `${c.sha.slice(0,7)} ${c.commit.message.split('\n')[0]}`).join('\n');
      const kanbanCtx = [
        `Done/Review: ${doneNuggets.map(n => n.title).join(', ') || 'none'}`,
        `In Progress: ${inProgress.map(n => n.title).join(', ') || 'none'}`,
      ].join('\n');

      const res = await callAI({
        feature: 'devnotebook',
        user: currentUser,
        systemPrompt: `You are the 40Geo technical scribe. Parse the git commit log and convert each commit into a structured dev notebook entry.
Rules:
- Each entry = one concise technical statement (max 120 chars)
- Classify: feature (new capability), fix (bug resolved), change (behavior modified), refactor (code quality), note (decision)
- Expand terse git messages into readable descriptions
- Return ONLY a valid JSON array. No markdown, no explanation.
Example: [{"type":"feature","text":"Added real-time nugget sync via SSE + LISTEN/NOTIFY"}]`,
        messages: [{ role: 'user', content: `Git log for today:\n${gitLog}\n\nKanban context:\n${kanbanCtx}` }],
      });

      const raw = res?.content?.[0]?.text || '[]';
      const jsonMatch = raw.match(/\[[\s\S]*\]/);
      if (!jsonMatch) throw new Error('No JSON returned from Claude');
      const generated = JSON.parse(jsonMatch[0]);
      if (!Array.isArray(generated) || !generated.length) { showToast?.('No entries extracted from commits'); setGithubLoading(false); return; }

      const existing = dayData.entries.map(e => e.text.toLowerCase());
      const fresh = generated.filter(g => !existing.some(ex => ex.includes(g.text.slice(0, 30).toLowerCase())));
      if (!fresh.length) { showToast?.('All commits already logged'); setGithubLoading(false); return; }

      const newEntries = fresh.map(g => ({ id: Date.now() + Math.random(), type: g.type || 'note', text: g.text, ts: new Date().toISOString(), ai: true, source: 'github' }));
      const updated = { ...notebook, [selectedDate]: { ...dayData, entries: [...dayData.entries, ...newEntries] } };
      saveNb(updated);
      showToast?.(`✦ Fetched ${commits.length} commit${commits.length !== 1 ? 's' : ''} → added ${fresh.length} entr${fresh.length !== 1 ? 'ies' : 'y'}`);
    } catch (e) {
      showToast?.('GitHub fetch failed: ' + e.message);
    }
    setGithubLoading(false);
  };

  // ── Generate narrative summary ────────────────────────────────────
  const generateSummary = async () => {
    if (!dayData.entries.length) { showToast?.('No entries to summarize'); return; }
    setSummaryLoading(true);
    try {
      const list = dayData.entries.map(e => `[${e.type.toUpperCase()}] ${e.text}`).join('\n');
      const kanbanCtx = [
        doneNuggets.length  ? `${doneNuggets.length} task(s) in done/review` : '',
        inProgress.length   ? `${inProgress.length} task(s) in progress` : '',
        createdToday.length ? `${createdToday.length} new task(s) created today` : '',
      ].filter(Boolean).join(', ');

      const res = await callAI({
        feature: 'devnotebook',
        user: currentUser,
        systemPrompt: `You are a technical writer producing a professional daily development report for 40Geo. Write a concise executive summary (4-6 sentences) covering what was built, fixed, and changed. Include a brief note on Kanban progress. Be specific and technical. Do not use bullet points — write in prose.`,
        messages: [{ role: 'user', content: `Dev entries:\n${list}\n\nKanban status: ${kanbanCtx || 'no data'}` }],
      });
      const summary = res?.content?.[0]?.text || 'No summary generated.';
      const updated = { ...notebook, [selectedDate]: { ...dayData, summary } };
      saveNb(updated);
      showToast?.('Summary generated');
    } catch (e) { showToast?.('Summary failed: ' + e.message); }
    setSummaryLoading(false);
  };

  // ── Report HTML ───────────────────────────────────────────────────
  const buildReportHtml = (date, data) => {
    const typeColor = (t) => ({ feature:'#34D399', fix:'#FF6B6B', change:'#FFB830', refactor:'#7B2FFF', note:'#aaa' })[t] || '#aaa';
    const entryRows = (data.entries || []).map(e =>
      `<tr><td style="padding:7px 12px;color:${typeColor(e.type)};font-weight:700;font-size:11px;white-space:nowrap;text-transform:uppercase">${e.type}</td><td style="padding:7px 12px;font-size:13px">${e.text}${e.ai ? ' <span style="font-size:10px;color:#E87722;opacity:0.6">✦ AI</span>' : ''}</td></tr>`
    ).join('');

    const doneRows = doneNuggets.slice(0, 20).map(n =>
      `<tr><td style="padding:6px 12px;font-size:12px">${n.title}</td><td style="padding:6px 12px;font-size:11px;color:#888;text-transform:uppercase">${n.priority}</td><td style="padding:6px 12px;font-size:11px;color:${n.col==='done'?'#34D399':'#FFB830'};font-weight:700">${n.col.toUpperCase()}</td></tr>`
    ).join('');

    const inProgressRows = inProgress.map(n =>
      `<tr><td style="padding:6px 12px;font-size:12px">${n.title}</td><td style="padding:6px 12px;font-size:11px;color:#888;text-transform:uppercase">${n.priority}</td></tr>`
    ).join('');

    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>40Geo Dev Report — ${date}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:'Segoe UI',Arial,sans-serif;background:#0d1b2a;color:#e0e0e0;padding:0}
.page{max-width:900px;margin:0 auto;padding:40px 48px}
.header{border-bottom:3px solid #E87722;padding-bottom:20px;margin-bottom:32px;display:flex;justify-content:space-between;align-items:flex-end}
.logo{font-size:26px;font-weight:800;color:#E87722;letter-spacing:-0.5px}
.logo span{color:#fff;font-weight:400}
.date-badge{background:rgba(232,119,34,0.15);border:1px solid rgba(232,119,34,0.4);border-radius:6px;padding:6px 14px;font-size:13px;color:#E87722;font-weight:600}
h2{font-size:11px;font-weight:700;letter-spacing:0.15em;color:rgba(255,255,255,0.35);text-transform:uppercase;margin-bottom:12px;margin-top:28px}
table{width:100%;border-collapse:collapse;border-radius:8px;overflow:hidden;background:rgba(255,255,255,0.03);border:1px solid rgba(255,255,255,0.08)}
th{background:rgba(255,255,255,0.06);padding:8px 12px;text-align:left;font-size:10px;font-weight:700;letter-spacing:0.1em;color:rgba(255,255,255,0.3);text-transform:uppercase}
tr:nth-child(even){background:rgba(255,255,255,0.02)}
.summary-box{background:rgba(232,119,34,0.08);border:1px solid rgba(232,119,34,0.25);border-radius:10px;padding:20px 24px;margin-top:8px;font-size:14px;line-height:1.75;color:#ddd}
.stats{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-bottom:8px}
.stat{background:rgba(255,255,255,0.04);border:1px solid rgba(255,255,255,0.08);border-radius:8px;padding:14px 16px;text-align:center}
.stat-num{font-size:28px;font-weight:800;color:#E87722}
.stat-label{font-size:10px;color:rgba(255,255,255,0.4);text-transform:uppercase;letter-spacing:0.1em;margin-top:2px}
.print-btn{background:#E87722;color:#fff;border:none;padding:10px 28px;border-radius:7px;font-size:13px;font-weight:700;cursor:pointer;margin-bottom:28px;letter-spacing:0.05em}
.footer{margin-top:40px;padding-top:16px;border-top:1px solid rgba(255,255,255,0.08);font-size:10px;color:rgba(255,255,255,0.2);display:flex;justify-content:space-between}
@media print{
  .print-btn{display:none}
  body{background:#fff;color:#000}
  .page{padding:24px 32px}
  .logo{color:#E87722}
  table{border:1px solid #ddd}
  th{background:#f5f5f5;color:#555}
  .summary-box{background:#fff8f0;border-color:#E87722;color:#333}
  .stat{background:#f9f9f9;border-color:#ddd}
  .stat-num{color:#E87722}
  .footer{color:#999}
}
</style></head>
<body><div class="page">
<button class="print-btn" onclick="window.print()">🖨 Print / Save as PDF</button>
<div class="header">
  <div><div class="logo">40Geo <span>Nuggets</span></div><div style="font-size:12px;color:rgba(255,255,255,0.35);margin-top:4px">Daily Development Report</div></div>
  <div class="date-badge">${date}</div>
</div>

<div class="stats">
  <div class="stat"><div class="stat-num">${(data.entries||[]).length}</div><div class="stat-label">Dev Entries</div></div>
  <div class="stat"><div class="stat-num">${doneNuggets.length}</div><div class="stat-label">Done / Review</div></div>
  <div class="stat"><div class="stat-num">${inProgress.length}</div><div class="stat-label">In Progress</div></div>
</div>

${data.summary ? `<h2>Executive Summary</h2><div class="summary-box">${data.summary.replace(/\n/g,'<br>')}</div>` : ''}

<h2>Development Log</h2>
<table><thead><tr><th>Type</th><th>Description</th></tr></thead><tbody>${entryRows || '<tr><td colspan="2" style="padding:12px;color:rgba(255,255,255,0.2);text-align:center">No entries</td></tr>'}</tbody></table>

${doneRows ? `<h2>Kanban — Completed &amp; In Review</h2>
<table><thead><tr><th>Task</th><th>Priority</th><th>Status</th></tr></thead><tbody>${doneRows}</tbody></table>` : ''}

${inProgressRows ? `<h2>Kanban — In Progress</h2>
<table><thead><tr><th>Task</th><th>Priority</th></tr></thead><tbody>${inProgressRows}</tbody></table>` : ''}

<div class="footer"><span>Generated by 40Geo Nuggets · Claude AI</span><span>${new Date().toLocaleString()}</span></div>
</div></body></html>`;
  };

  const exportPDF = () => {
    const w = window.open('', '_blank');
    if (!w) { showToast?.('Pop-up blocked — allow pop-ups for this site'); return; }
    w.document.write(buildReportHtml(selectedDate, dayData));
    w.document.close();
  };

  const sendEmail = async () => {
    if (!emailTarget) { showToast?.('Select a recipient'); return; }
    const recipient = users.find(u => u.id === emailTarget);
    if (!recipient?.email) { showToast?.('Recipient has no email'); return; }
    setEmailLoading(true);
    try {
      const html = buildReportHtml(selectedDate, dayData) +
        (customMsg ? `<div style="margin:24px 48px;padding:16px 20px;background:rgba(255,255,255,0.04);border-radius:8px;font-size:13px;color:#ccc"><strong style="color:#E87722">Note from ${currentUser.name}:</strong><br><br>${customMsg}</div>` : '');
      const result = await dbSendEmail({ to: recipient.email, subject: `40Geo Dev Report — ${selectedDate}`, html, orgId: currentUser.orgId });
      if (result?.success) {
        showToast?.(`Report sent to ${recipient.name || recipient.email}`);
        setEmailModal(false); setCustomMsg(''); setEmailTarget('');
      } else {
        showToast?.('Email failed: ' + (result?.error || 'unknown error'));
      }
    } catch (e) { showToast?.('Email failed: ' + e.message); }
    setEmailLoading(false);
  };

  const sortedDates = Object.keys(notebook).sort((a, b) => b.localeCompare(a));
  if (!sortedDates.includes(todayKey())) sortedDates.unshift(todayKey());

  const DnCard = { background: C.cardBg, borderRadius: 12, padding: 20, border: `1px solid ${C.border}` };

  return (
    <div style={{ padding: 20, maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <h2 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 800, fontSize: 22, color: C.text, margin: 0 }}>Dev Notebook</h2>
          <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: C.subtext, margin: '4px 0 0' }}>AI-populated daily development log · {nuggets.length} nuggets tracked</p>
        </div>
        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={exportPDF} disabled={!dayData.entries.length} style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', color: C.subtext, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: !dayData.entries.length ? 0.4 : 1 }}>
            🖨 Print Report
          </button>
          <button onClick={() => setEmailModal(true)} disabled={!dayData.entries.length} style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 16px', color: C.subtext, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: !dayData.entries.length ? 0.4 : 1 }}>
            ✉ Send Report
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 16 }}>
        {/* Left: date list */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {sortedDates.map(d => (
            <button key={d} onClick={() => setSelectedDate(d)} style={{
              background: selectedDate === d ? 'rgba(232,119,34,0.15)' : 'rgba(255,255,255,0.04)',
              border: `1px solid ${selectedDate === d ? 'rgba(232,119,34,0.5)' : C.border}`,
              borderRadius: 8, padding: '8px 12px', cursor: 'pointer', textAlign: 'left',
              fontFamily: "'Montserrat',sans-serif", fontSize: 12,
              color: selectedDate === d ? '#E87722' : C.subtext,
            }}>
              {d === todayKey() ? '📅 Today' : d}
              {(notebook[d]?.entries?.length > 0) && (
                <span style={{ marginLeft: 6, background: 'rgba(232,119,34,0.3)', borderRadius: 10, padding: '1px 6px', fontSize: 10, color: '#E87722' }}>
                  {notebook[d].entries.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Right: main content */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Kanban snapshot */}
          {selectedDate === todayKey() && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
              {[
                { label: 'Done / Review', count: doneNuggets.length, color: '#34D399' },
                { label: 'In Progress',   count: inProgress.length,  color: '#FFB830' },
                { label: 'Created Today', count: createdToday.length, color: '#E87722' },
              ].map(s => (
                <div key={s.label} style={{ ...DnCard, padding: '14px 18px', textAlign: 'center' }}>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 28, fontWeight: 800, color: s.color }}>{s.count}</div>
                  <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 10, color: C.subtext, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Auto-fill by Claude */}
          <div style={{ ...DnCard, background: 'linear-gradient(135deg, rgba(232,119,34,0.08), rgba(123,47,255,0.08))', border: '1px solid rgba(232,119,34,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 14, fontWeight: 700, color: C.text }}>✦ Claude Auto-Fill</div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: C.subtext, marginTop: 3 }}>Analyzes Kanban, audit log, and Strategy Lab sessions to populate today's entries</div>
              </div>
              <button onClick={autoFill} disabled={autoLoading} style={{ background: autoLoading ? 'rgba(255,255,255,0.08)' : '#E87722', border: 'none', borderRadius: 8, padding: '10px 22px', color: '#fff', fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, cursor: 'pointer', flexShrink: 0, opacity: autoLoading ? 0.6 : 1, transition: 'all 0.2s' }}>
                {autoLoading ? '✦ Analyzing…' : '✦ Auto-Fill Today'}
              </button>
            </div>
          </div>

          {/* Fetch from GitHub */}
          <div style={{ ...DnCard, background: 'rgba(255,255,255,0.02)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: C.text }}>🔄 Fetch Today from GitHub</div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: C.subtext, marginTop: 2 }}>Pulls today's commits from mlrtexas/nuggets → Claude categorizes automatically</div>
              </div>
              <button onClick={fetchFromGitHub} disabled={githubLoading} style={{ background: githubLoading ? 'rgba(255,255,255,0.06)' : 'rgba(52,211,153,0.12)', border: '1px solid rgba(52,211,153,0.3)', borderRadius: 7, padding: '8px 18px', color: '#34D399', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer', flexShrink: 0, opacity: githubLoading ? 0.6 : 1, whiteSpace: 'nowrap' }}>
                {githubLoading ? '⏳ Fetching…' : '🔄 Fetch Commits'}
              </button>
            </div>
          </div>

          {/* Import from git log / session notes */}
          <div style={{ ...DnCard, background: 'rgba(255,255,255,0.02)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, color: C.text }}>📋 Import Session Notes</div>
                <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: C.subtext, marginTop: 2 }}>Paste a git log, Claude Code summary, or any session notes — Claude will categorize them</div>
              </div>
              <button onClick={() => setShowImport(p => !p)} style={{ background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, borderRadius: 7, padding: '6px 14px', color: C.subtext, fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                {showImport ? 'Hide' : 'Paste Text'}
              </button>
            </div>
            {showImport && (
              <div style={{ marginTop: 12 }}>
                <textarea
                  value={importText}
                  onChange={e => setImportText(e.target.value)}
                  rows={8}
                  placeholder={`Paste anything here — git log, Claude Code session summary, bullet list of changes...\n\nExample:\nec0ec3c Add PWA support — installable Android app\na74614e Add DevNotebook, file attachments, forward fix\n5f738a1 Real-time nugget sync via SSE`}
                  style={{ width: '100%', background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 12px', color: C.text, fontFamily: 'monospace', fontSize: 12, resize: 'vertical', outline: 'none', boxSizing: 'border-box', lineHeight: 1.6 }}
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8, justifyContent: 'flex-end' }}>
                  <button onClick={() => { setImportText(''); setShowImport(false); }} style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 7, padding: '7px 16px', color: C.subtext, fontFamily: "'Montserrat',sans-serif", fontSize: 12, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={importNotes} disabled={importLoading || !importText.trim()} style={{ background: importLoading ? 'rgba(255,255,255,0.08)' : 'rgba(232,119,34,0.85)', border: 'none', borderRadius: 7, padding: '7px 20px', color: '#fff', fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer', opacity: (!importText.trim() || importLoading) ? 0.5 : 1 }}>
                    {importLoading ? '✦ Processing…' : '✦ Import & Categorize'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Entries list */}
          <div style={{ ...DnCard }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
              <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: C.subtext, fontWeight: 600 }}>ENTRIES — {selectedDate} ({dayData.entries.length})</div>
              <button onClick={generateSummary} disabled={summaryLoading || !dayData.entries.length} style={{ background: summaryLoading ? 'rgba(255,255,255,0.06)' : 'rgba(123,47,255,0.15)', border: '1px solid rgba(123,47,255,0.35)', borderRadius: 7, padding: '4px 13px', color: '#A78BFA', fontFamily: "'Montserrat',sans-serif", fontSize: 11, fontWeight: 700, cursor: 'pointer', opacity: !dayData.entries.length ? 0.4 : 1 }}>
                {summaryLoading ? 'Writing…' : '✦ Write Summary'}
              </button>
            </div>

            {dayData.entries.length === 0 ? (
              <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 12, color: 'rgba(255,255,255,0.2)', fontStyle: 'italic', textAlign: 'center', padding: '20px 0' }}>
                Click "Auto-Fill Today" to let Claude analyze your session, or add entries manually below.
              </p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {dayData.entries.map(e => {
                  const meta = ENTRY_TYPES.find(t => t.id === e.type) || ENTRY_TYPES[4];
                  return (
                    <div key={e.id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 12px', background: 'rgba(255,255,255,0.03)', borderRadius: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
                      <span style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 9, fontWeight: 700, color: meta.color, background: meta.color + '22', borderRadius: 4, padding: '3px 8px', whiteSpace: 'nowrap', marginTop: 2, letterSpacing: '0.1em' }}>{meta.label.toUpperCase()}</span>
                      <span style={{ flex: 1, fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: C.text, lineHeight: 1.5 }}>{e.text}</span>
                      {e.ai && <span style={{ fontSize: 9, color: 'rgba(232,119,34,0.5)', fontFamily: "'Montserrat',sans-serif", marginTop: 3, flexShrink: 0 }}>✦ AI</span>}
                      <button onClick={() => removeEntry(e.id)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', cursor: 'pointer', fontSize: 14, padding: '0 2px', flexShrink: 0 }}
                        onMouseEnter={ev => ev.currentTarget.style.color='#FF6B6B'}
                        onMouseLeave={ev => ev.currentTarget.style.color='rgba(255,255,255,0.2)'}>✕</button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* AI Summary */}
            {dayData.summary && (
              <div style={{ marginTop: 16, fontFamily: "'Montserrat',sans-serif", fontSize: 13, color: C.text, lineHeight: 1.7, background: 'rgba(123,47,255,0.07)', borderRadius: 8, padding: '14px 16px', border: '1px solid rgba(123,47,255,0.2)' }}>
                <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: '#A78BFA', marginBottom: 8 }}>AI SUMMARY</div>
                {dayData.summary}
              </div>
            )}

            {/* Manual add */}
            <div style={{ display: 'flex', gap: 6, marginTop: 16, paddingTop: 14, borderTop: `1px solid ${C.border}`, flexWrap: 'wrap' }}>
              {ENTRY_TYPES.map(t => (
                <button key={t.id} onClick={() => setEntryType(t.id)} style={{
                  background: entryType === t.id ? t.color + '22' : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${entryType === t.id ? t.color + '66' : 'transparent'}`,
                  borderRadius: 6, padding: '3px 9px', cursor: 'pointer',
                  fontFamily: "'Montserrat',sans-serif", fontSize: 10, fontWeight: 700,
                  color: entryType === t.id ? t.color : C.subtext,
                }}>{t.label}</button>
              ))}
              <div style={{ display: 'flex', gap: 6, flex: 1, minWidth: 0, marginTop: 8, width: '100%' }}>
                <input value={newEntry} onChange={e => setNewEntry(e.target.value)} onKeyDown={e => e.key === 'Enter' && addEntry(entryType, newEntry)}
                  placeholder="Add entry manually…"
                  style={{ flex: 1, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 12px', color: C.text, fontFamily: "'Montserrat',sans-serif", fontSize: 12, outline: 'none' }} />
                <button onClick={() => addEntry(entryType, newEntry)} style={{ background: 'rgba(255,255,255,0.08)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '7px 16px', color: C.text, fontFamily: "'Montserrat',sans-serif", fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Add</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {emailModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: C.cardBg, borderRadius: 14, padding: 28, width: 440, border: `1px solid ${C.border}` }}>
            <h3 style={{ fontFamily: "'Montserrat',sans-serif", fontWeight: 700, fontSize: 16, color: C.text, margin: '0 0 16px' }}>Send Dev Report</h3>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: C.subtext, marginBottom: 6, fontWeight: 600 }}>RECIPIENT</div>
            <select value={emailTarget} onChange={e => setEmailTarget(e.target.value)} style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontFamily: "'Montserrat',sans-serif", fontSize: 13, marginBottom: 14, outline: 'none' }}>
              <option value="">Select recipient…</option>
              {users.filter(u => u.email).map(u => <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>)}
            </select>
            <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 11, color: C.subtext, marginBottom: 6, fontWeight: 600 }}>PERSONAL NOTE (OPTIONAL)</div>
            <textarea value={customMsg} onChange={e => setCustomMsg(e.target.value)} rows={3} placeholder="Add a note to accompany the report…" style={{ width: '100%', background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', color: C.text, fontFamily: "'Montserrat',sans-serif", fontSize: 13, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={sendEmail} disabled={emailLoading || !emailTarget} style={{ flex: 1, background: '#E87722', border: 'none', borderRadius: 8, padding: '10px 0', color: '#fff', fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: (!emailTarget || emailLoading) ? 0.5 : 1 }}>
                {emailLoading ? 'Sending…' : 'Send Report'}
              </button>
              <button onClick={() => setEmailModal(false)} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 0', color: C.subtext, fontFamily: "'Montserrat',sans-serif", fontSize: 13, cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
